import { createContext, useContext, useState, useEffect, ReactNode } from "react";


export type Locale = "en" | "sv" | "no" | "de" | "da" | "fi";

const translations: Record<Locale, Record<string, string>> = {
  en: {
    "offline.message": "You are offline — saved data is shown from cache",
    "offline.syncing": "Syncing data...",
    "offline.synced": "All synced!",
    "confirm.deleteTitle": "Are you sure?",
    "confirm.deleteDesc": "This action cannot be undone.",
    "confirm.cancel": "Cancel",
    "confirm.delete": "Delete",
    "confirm.deleteTrip": "Delete this trip?",
    "confirm.deleteTripDesc": "This will permanently delete the trip and all its data. This action cannot be undone.",
    "confirm.deletePost": "Delete this post?",
    "confirm.deletePostDesc": "This post will be permanently removed.",
    "confirm.deleteBudget": "Delete this budget?",
    "confirm.deleteBudgetDesc": "This will permanently delete the budget and all its expenses.",
    "confirm.deleteNotification": "Delete this notification?",
    "confirm.deleteNotificationDesc": "The notification will be permanently removed.",
    "confirm.deleteMaterial": "Delete this material?",
    "confirm.deleteMaterialDesc": "The material will be permanently removed from the project.",
    "confirm.deleteTask": "Delete this task?",
    "confirm.deleteTaskDesc": "The task will be permanently removed from the project.",
    "confirm.deleteFile": "Delete this file?",
    "confirm.deleteFileDesc": "The file will be permanently removed.",
    "confirm.deleteMessage": "Delete this message?",
    "confirm.deleteMessageDesc": "The message will be permanently removed.",
    "confirm.deleteUpdate": "Delete this update?",
    "confirm.deleteUpdateDesc": "The update will be permanently removed.",
    "confirm.deleteCamping": "Delete this camping permanently?",
    "confirm.deleteCampingDesc": "This will permanently delete the camping spot.",
    "confirm.unpublishTrip": "Unpublish this trip?",
    "confirm.unpublishTripDesc": "The trip will no longer be visible to others.",
    "confirm.endTrip": "End this trip?",
    "confirm.endTripDesc": "The trip will be marked as completed.",
    "nav.explore": "Find Campings",
    "nav.map": "Explore Map",
    "nav.trips": "Plan a Trip",
    "nav.favorites": "Saved Places",
    "nav.login": "Log In",
    "nav.signup": "Get Started Free",
    "nav.logout": "Log Out",
    "nav.bottom.trips": "New Trip",
    "nav.bottom.explore": "Explore",
    "nav.bottom.map": "Map",
    "nav.bottom.saved": "Saved",
    "nav.bottom.profile": "Profile",
    "nav.bottom.login": "Log In",
    "nav.bottom.signup": "Sign Up",
    "nav.bottom.home": "Home",
    "nav.bottom.newTrip": "New Trip",
    "nav.bottom.myTrips": "My Trips",
    "nav.checklists": "Checklists",
    "nav.travelGuides": "Travel Guides",
    "nav.menuMine": "Mine",
    "nav.menuCreate": "Create",
    "nav.settings": "Settings",
    "footer.integrationTerms": "Integration Terms",
    "dash.greeting.morning": "Good morning",
    "dash.greeting.day": "Hi",
    "dash.greeting.afternoon": "Good afternoon",
    "dash.greeting.evening": "Good evening",
    "dash.greeting.night": "Good night",
    "dash.subtitle.active": "Your trip is in progress – here's your status",
    "dash.subtitle.planning": "You have trips to prepare",
    "dash.subtitle.completed": "Well done! Here's your trip summary",
    "dash.subtitle.empty": "What would you like to do today?",
    "dash.paused": "Paused",
    "dash.ongoing": "Trip in progress",
    "dash.total": "Total",
    "dash.stops": "Stops",
    "dash.done": "Done",
    "dash.nextDest": "Next destination",
    "dash.continueTrip": "Continue trip",
    "dash.openActive": "Open active trip",
    "dash.journal": "Journal",
    "dash.budget": "Budget",
    "dash.findPlace": "Find place",
    "dash.upcoming": "Upcoming trip",
    "dash.today": "Today!",
    "dash.tomorrow": "Tomorrow",
    "dash.daysLeft": "{n} days left",
    "dash.continuePlan": "Continue planning",
    "dash.start": "Start",
    "dash.morePlanned": "More planned trips",
    "dash.findCamping": "Find camping",
    "dash.findCampingSub": "2,800+ places",
    "dash.guides": "Travel guides",
    "dash.guidesSub": "21+ countries",
    "dash.checklists": "Checklists",
    "dash.checklistsSub": "Pack smart",
    "dash.wishlist": "Wishlist",
    "dash.vehicle": "Vehicle",
    "dash.projects": "Projects",
    "dash.community": "Community",
    "dash.popularDest": "Popular destinations",
    "dash.seeAll": "See all",
    "dash.planNew": "Plan new trip",
    "dash.planNewSub": "Step-by-step wizard",
    "dash.season.spring": "Spring adventures",
    "dash.season.summer": "Summer on the road",
    "dash.season.autumn": "Autumn road trips",
    "dash.season.winter": "Winter escapes",
    "dash.tripDone": "Trip completed",
    "dash.visited": "Visited",
    "dash.seeSummary": "See summary",
    "dash.planNewOld": "Plan new trip",
    "dash.emptyTitle": "Time to plan your first trip!",
    "dash.emptyDesc": "Create a trip and add stops, budget and checklists.",
    "dash.createTrip": "Create trip",
    "dash.stopsCount": "{count} stops",
    "dash.visitedCount": "{count} visited",
    "dash.onboarding.step1": "Plan a trip",
    "dash.onboarding.step1Desc": "Create your first road trip with stops and dates",
    "dash.onboarding.step2": "Save dream destinations",
    "dash.onboarding.step2Desc": "Build your wishlist of places to visit",
    "dash.onboarding.step3": "Explore camping spots",
    "dash.onboarding.step3Desc": "Browse 2,800+ campsites across Europe",
    "dash.onboarding.stepVehicle": "Set up your vehicle",
    "dash.onboarding.stepVehicleDesc": "Add your motorhome, car or campervan",
    "dash.onboarding.stepProject": "Start a project",
    "dash.onboarding.stepProjectDesc": "Track renovations, upgrades and builds",
    "dash.emptyDescReturning": "Plan your first road trip — your saved data is ready to use!",
    "dash.recentProjects": "Your projects",
    "dash.popularGuides": "Popular guides",
    "dash.noProjects": "No projects yet",
    "dash.draft": "Draft",
    "dash.continueDraft": "Continue planning",
    "dash.untitled": "Unnamed trip",
    "dash.nextStep": "Next step",
    "dash.nextStepStops": "Add your first stop",
    "dash.nextStepDate": "Choose travel dates",
    "dash.nextStepChecklist": "Complete the checklist",
    "dash.prepTitle": "Prepare for your trip",
    "dash.prepBudget": "Set a budget so you stay on track",
    "dash.prepStops": "Add more stops to your route",
    "dash.prepChecklist": "Review your packing checklist",
    "dash.packed": "packed",
    "dash.incompleteTitle": "Finish planning",
    "dash.incompleteDate": "Add a departure date",
    "dash.incompleteStops": "Add stops to your route",
    "dash.postTripTitle": "What's next?",
    "dash.postTripShare": "Share your trip with friends",
    "dash.postTripReview": "Rate the places you visited",
    "dash.postTripJournal": "Write about your experience",
    "dash.postTripMsg": "You traveled {km} km and visited {stops} places — great adventure!",
    "nav.myTrips": "My Trips",
    "nav.submit": "Add a Place",
    "nav.help": "How to use the app",
    "nav.compare": "Compare Campings",
    "nav.guides": "Country Guides",
    "nav.contact": "Contact Us",
    "help.title": "How to use the app",
    "search.startTyping": "Start typing to search",
    "search.startTypingDesc": "Search by place name, city, category, or destination type",
    "search.clickForDetails": "Click a card for details",
    "search.searchHint": "Search by name, category, city, or region. Smart search finds the right place for you.",
    
    "search.foundOnGoogle": "extra suggestions from Google",
    "search.addHint": "Tap a card to add it to Vantastika",
    "search.fromGoogle": "From Google",
    "search.addToVantastika": "Add to Vantastika",
    "search.googleAdding": "Adding...",
    "search.googleAddedShort": "Submitted for review",
    "search.googleAdded": "Place submitted",
    "search.googleAddedDesc": "Thanks! It will be visible after admin review.",
    "search.googleAlreadyExists": "Already in Vantastika",
    "search.googleAlreadyExistsDesc": "We're taking you to the existing page.",
    "search.googleAddError": "Could not add the place. Try again.",
    "search.nearbyFound": "campings near you",
    "search.within25km": "within 25 km",
    "search.noNearby": "No campings nearby",
    "search.noNearbyDesc": "Try the map to explore further away.",
    "search.openMap": "Open map",
    "search.findingLocation": "Finding your location...",
    "search.findingLocationDesc": "We use this to show campings nearby.",
    "search.locationDeniedHint": "Location access is denied. Allow it to see campings nearby.",
    "search.enableLocationHint": "Allow location access to see the closest campings.",
    "search.allowLocation": "Allow location",
    "search.topRated": "Top rated campings",
    "help.whatIs.title": "What is Vantastika?",
    "help.whatIs.body": "Your travel companion for motorhome, campervan and car trips across Europe. Find campsites, plan routes and keep track of your budget — all in one place.",
    "help.startTrip.title": "Start a trip",
    "help.startTrip.body": "Tap Trip Planner, give your trip a name, add your stops and dates. The app calculates distances and fuel costs for you.",
    "help.search.title": "Search for places",
    "help.search.body": "Use Explore or the search page to find campsites, pitches and service stations near any location in Europe.",
    "help.map.title": "Use the map",
    "help.map.body": "Open Explore to see the map. Search for a city or use your current location. Tap markers for details and directions.",
    "help.addStop.title": "Add a stop to your trip",
    "help.addStop.body": "When viewing a campsite, tap 'Add to trip' to include it in your route. You can reorder and edit stops anytime.",
    "help.share.title": "Share & get inspired",
    "help.share.body": "Make your trip public so others can see your route. Browse shared trips from the community for ideas.",
    "help.favorites.title": "Save favorites",
    "help.favorites.body": "Tap the heart icon on any campsite to save it. Organize favorites into collections for easy access later.",
    "hero.title1": "Plan your entire trip",
    "hero.title2": "in one place.",
    "hero.subtitle": "Plan your trip, add stops, find places on the map and track your budget — without switching between apps.",
    "hero.searchPlaceholder": "Where do you want to go?",
    "hero.search": "Search",
    "hero.ctaPlan": "Plan my trip — free",
    "hero.ctaExplore": "Find Campings",
    "hero.ctaSubmit": "Know a Great Spot?",
    "hero.valueProps": "Everything in one place — no more switching between apps",
    "hero.diffTitle": "Why Vantastika?",
    "hero.diff1": "Trip planning, stops and budget in one flow",
    "hero.diff2": "Scan receipts and track expenses automatically",
    "hero.diff3": "Add stops directly from the map",
    "hero.diff4": "For motorhomes, cars and road trips",
    "features.find": "Find places on the map",
    "features.findDesc": "Campings, attractions, restaurants and more stops — straight into your plan",
    "features.save": "Save Favorites",
    "features.saveDesc": "Keep track of your must-visit spots",
    "features.plan": "Plan your trip",
    "features.planDesc": "Create a route, add stops and build your trip step by step",
    "features.budget": "Track your budget",
    "features.budgetDesc": "Log expenses, scan receipts and see where the money goes",
    "features.europe": "All of Europe",
    "features.europeDesc": "Thousands of verified spots across Europe",
    "featured.title": "Popular Campings",
    "featured.viewAll": "View all →",
    "search.title": "Explore Campings",
    "search.placeholder": "Search campings...",
    "search.country": "Country",
    "search.allCountries": "All countries",
    "search.filters": "Filters",
    "search.clearAll": "Clear all",
    "search.minPrice": "Min price",
    "search.maxPrice": "Max price",
    "search.region": "Region",
    "search.regionPlaceholder": "e.g. Dalarna",
    "search.noResults": "No campings found",
    "search.adjustFilters": "Try adjusting your filters",
    "search.found": "campings found",
    "filter.electricity": "Electricity hookup",
    "filter.freshWater": "Fresh water",
    "filter.greyWater": "Grey water disposal",
    "filter.toilet": "Toilet cassette disposal",
    "filter.shower": "Shower",
    "filter.wc": "WC",
    "filter.petFriendly": "Pet friendly",
    "filter.openAllYear": "Open all year",
    "filter.familyFriendly": "Family friendly",
    "filter.largeMotorhomes": "Large motorhomes",
    "filter.nearWater": "Near lake/beach",
    "filter.spaWellness": "Spa/wellness nearby",
    "filter.wifi": "WiFi",
    "filter.laundry": "Laundry",
    "filter.restaurant": "Restaurant",
    "detail.about": "About",
    "detail.amenities": "Amenities",
    "detail.contact": "Contact",
    "detail.website": "Website",
    "detail.checkIn": "Check-in",
    "detail.checkOut": "Check-out",
    "detail.location": "Location",
    "detail.openInMaps": "Open in Google Maps →",
    "detail.mapComingSoon": "Map coming soon",
    "detail.back": "Back",
    "detail.save": "Save",
    "detail.saved": "Saved",
    "detail.perNight": "per night",
    "detail.reviews": "Reviews",
    "detail.writeReview": "Write a review",
    "detail.noReviews": "No reviews yet",
    "detail.submitReview": "Submit review",
    "detail.yourRating": "Your rating",
    "detail.yourComment": "Your comment (optional)",
    "detail.loginToReview": "Log in to write a review",
    "detail.notFound": "Camping not found",
    "detail.backToSearch": "Back to search",
    "favorites.title": "My Favorites",
    "favorites.saveYour": "Save your favorite campings",
    "favorites.signUpPrompt": "Sign up or log in to save campings to your favorites.",
    "favorites.empty": "No favorites yet",
    "favorites.emptyDesc": "Start exploring and save campings you love",
    "favorites.explore": "Explore Campings",
    "favorites.emptyCollection": "No favorites in this collection",
    "wishlist.title": "Wishlist",
    "wishlist.save": "Save to wishlist",
    "wishlist.saved": "Saved to wishlist!",
    "wishlist.alreadySaved": "Already in wishlist",
    // My Map
    "myMap.title": "My travel map",
    "myMap.loginMsg": "Log in to build your own travel map with saved, visited and manually placed places.",
    "myMap.placesShown": "places shown",
    "myMap.addPin": "Add pin",
    "myMap.newPin": "New pin",
    "myMap.editPin": "Edit pin",
    "myMap.name": "Name",
    "myMap.namePlaceholder": "e.g. Grandma's cabin",
    "myMap.statusLabel": "Status",
    "myMap.status.wantToVisit": "Want to visit",
    "myMap.status.planned": "Planned",
    "myMap.status.visited": "Visited",
    "myMap.status.favorite": "Favorite",
    "myMap.category": "Category",
    "myMap.categoryPlaceholder": "e.g. Nature reserve, Restaurant…",
    "myMap.note": "Note",
    "myMap.notePlaceholder": "What makes this place special?",
    "myMap.optional": "optional",
    "myMap.linkTrip": "Link to trip",
    "myMap.noTrip": "No link",
    "myMap.untitledTrip": "Untitled trip",
    "myMap.activeTripHint": "active",
    "myMap.activeTripAutoLinked": "Suggested automatically because you have an active trip.",
    "myMap.savePin": "Save pin",
    "myMap.saveChanges": "Save changes",
    "myMap.confirmDelete": "Remove this pin?",
    "myMap.dragHint": "Drag the pin on the map to move it.",
    "myMap.savedToast": "Pin saved",
    "myMap.updatedToast": "Pin updated",
    "myMap.deletedToast": "Pin removed",
    "myMap.movedToast": "Position updated",
    "myMap.layer.wishlist": "Wishlist",
    "myMap.layer.visited": "Visited",
    "myMap.layer.manual": "Your pins",
    "myMap.longPressTitle": "Press and hold on the map",
    "myMap.longPressDesc": "Hold your finger or mouse down on a spot for 0.5 seconds to drop your own pin there.",
    "myMap.mapFailed": "Map could not be loaded. Try reloading the page.",
    "myMap.searchPlaceholder": "Search place, camping, address…",
    "myMap.clearSearch": "Clear search",
    // SaveGateSheet
    "saveGate.wishlist.savedFor": "We saved the place",
    "saveGate.trip.savedFor": "We saved your trip",
    "saveGate.place.savedFor": "We saved",
    "saveGate.holdsFor24h": "We'll keep it for 24 hours — create an account so it never disappears.",
    "saveGate.wishlist.benefit1": "Sync between phone & computer",
    "saveGate.wishlist.benefit2": "Get notified when the price drops",
    "saveGate.wishlist.benefit3": "Share with your travel buddy",
    "saveGate.trip.benefit1": "Activate offline mode on the road",
    "saveGate.trip.benefit2": "Smart navigation adapted to your vehicle",
    "saveGate.trip.benefit3": "Share live location with family",
    "saveGate.place.benefit1": "Save your place permanently",
    "saveGate.place.benefit2": "Get tips for nearby places",
    "saveGate.place.benefit3": "Available on every device",
    "saveGate.socialProof": "Nordic travellers use Vantastika",
    "saveGate.createAccount15s": "Create account in 15 sec",
    "saveGate.alreadyMember": "Already a member?",
    "saveGate.logIn": "Log in",
    "saveGate.continueGuest": "Continue as guest — saved 24 h",
    "saveGate.noCardNeeded": "Free. No card needed.",
    // GuestRescueSheet
    "guestRescue.title": "You have {count} saved items at risk",
    "guestRescue.expiresIn": "Expires in {time}",
    "guestRescue.expired": "Expired — create an account to restore",
    "guestRescue.trip": "Trip",
    "guestRescue.wishlist": "Wishlist",
    "guestRescue.rescueCta": "Rescue my items — create account",
    "guestRescue.footnote": "Free forever. No credit card.",
    "saveGate.hoursShort": "h",
    "saveGate.minutesShort": "min",
    "guestRescue.toastTitle": "Tip: Create an account",
    "guestRescue.toastBody": "Save on every device so you never lose your trips.",
    "guestRescue.toastCta": "Do it now",
    "nav.bottom.guestSaves": "{count} saved",
    "wishlist.loginMsg": "Log in to save places you want to visit.",
    "wishlist.empty": "Your wishlist is empty",
    "wishlist.emptyDesc": "Save places you dream of visiting and plan trips from them later.",
    "wishlist.add": "Add place",
    "wishlist.searchPlace": "Search for a place to save",
    "wishlist.searchPlaceholder": "City, country, attraction, camping...",
    "wishlist.myNote": "My note",
    "wishlist.notePlaceholder": "Write a note about this place...",
    "wishlist.addNote": "Tap to add a note...",
    "wishlist.saveNote": "Save",
    "wishlist.nearby": "Nearby",
    "wishlist.nearbyAttractions": "Attractions",
    "wishlist.nearbyAccommodation": "Accommodation",
    "wishlist.nearbyHotels": "Hotels",
    "wishlist.nearbyRestaurants": "Restaurants",
    "wishlist.noNearby": "No nearby places found in our database yet.",
    "wishlist.planTrip": "Plan trip here",
    "wishlist.planMultiple": "Plan trip",
    "wishlist.deleteTitle": "Remove from wishlist?",
    "wishlist.deleteDesc": "This place will be removed from your wishlist.",
    "wishlist.deletedToast": "Place removed",
    "wishlist.restoredToast": "Place restored",
    "wishlist.undo": "Undo",
    "wishlist.selectedCount": "selected",
    "wishlist.pickFromWishlist": "Choose from wishlist",
    "wishlist.loading": "Loading...",
    "wishlist.emptyPicker": "Your wishlist is empty. Save places first!",
    "wishlist.addSelected": "Add selected",
    "wishlist.fromWishlist": "From wishlist",
    "wishlist.planWithPlaces": "Plan trip with selected places",
    "wishlist.planWithPlacesDesc": "Choose order mode and assign a role to each place.",
    "wishlist.orderMode": "Route order",
    "wishlist.myOrder": "Keep my order",
    "wishlist.myOrderDesc": "Use the exact order you chose",
    "wishlist.smartOrder": "Optimize route",
    "wishlist.smartOrderDesc": "Find the smartest order with less detour",
    "wishlist.assignRoles": "What role does each place have?",
    "wishlist.role_destination": "Destination",
    "wishlist.role_stop": "Stop",
    "wishlist.role_attraction": "Attraction",
    "wishlist.role_overnight": "Overnight",
    "wishlist.startPlanning": "Start planning",
    "wishlist.places": "places",
    "notifications.emptyTitle": "No notifications yet",
    "notifications.emptyDesc": "When something happens, you'll see it here",
    "notifications.title": "Notifications",
    "notifications.unreadCount": "unread",
    "notifications.noUnread": "No unread notifications",
    "notifications.markAll": "Mark all",
    "notifications.markRead": "Mark as read",
    "notifications.show": "Show",
    "notifications.delete": "Delete",
    "notifications.goToInbox": "Go to inbox & messages",
    "notifications.allRead": "All marked as read",
    "trips.title": "My Trips",
    "trips.plan": "Plan Your Trips",
    "trips.loginPrompt": "Log in to create and manage your camping trips.",
    "trips.newTrip": "New Trip",
    "trips.createTrip": "Create a New Trip",
    "trips.tripTitle": "Trip title",
    "trips.startDate": "Start date",
    "trips.endDate": "End date",
    "trips.notes": "Notes for the trip...",
    "trips.create": "Create Trip",
    "trips.empty": "No trips yet",
    "trips.emptyDesc": "Create your first trip and start adding camping stops",
    "trips.stops": "Stops",
    "trips.noStops": "No stops added yet. Save campings to your favorites, then add them here.",
    "trips.created": "Trip created!",
    "trips.failed": "Failed to create trip",
    "trips.partialSave": "Trip partially saved",
    "trips.partialDesc": "Some parts could not be saved. Go to My Trips to edit.",
    "trips.unknownPlace": "Unknown place",
    "trips.ideaLoaded": "Your trip idea has been loaded into the planner below.",
    "trips.newInspiration": "New inspiration",
    "trips.requireAuthSave": "You've spent time planning this trip. Create an account and everything is saved automatically.",
    "trips.daysAbbrev": "days",
    "trips.perDay": "/day",
    "trips.socialProof": "travelers plan here",
    "trips.freeLabel": "100% free",
    "trips.ideaCreating": "Creating your trip idea...",
    "trips.ideaBuilding": "We're building a complete trip plan for {destination} with stops, attractions and campings.",
    "trips.ideaTitle": "Trip idea: {title}",
    "trips.campingsNearby": "Campings & pitches nearby",
    "trips.restoreWelcome": "Welcome back!",
    "trips.restoreSaving": "We're saving your trip \"{title}\" automatically...",
    "trips.restoreFailed": "Could not restore the trip",
    "trips.restoreFailedDesc": "The saved trip data was incomplete. Plan a new trip.",
    "trips.restoreErrorDesc": "Something went wrong. Plan a new trip.",
    "trips.partialStopsFailed": "Trip saved but some stops failed",
    "trips.partialStopsDesc": "Go to My Trips and edit the trip to add missing stops.",
    "contact.sendFailed": "Could not send",
    "contact.tryAgain": "Try again later.",
    "contact.sent": "Message sent!",
    "contact.writeMessage": "Write a message",
    "contact.rateLimited": "Wait a moment before sending again",
    "weather.fetchError": "Could not fetch weather data. Try again later.",
    "cost.fetchFailed": "Could not fetch cost estimate",
    "cost.adults": "adults",
    "cost.children": "children",
    "cost.person": "person",
    "cost.persons": "persons",
    "wizard.nextStepDesc": "What would you like to do next?",
    "wizard.addBudgetCta": "Set a budget for this trip",
    "wizard.addMoreStopsCta": "Add more stops & campings",
    "wizard.viewMyTrips": "View my trips",
    "myTrips.chatUnavailable": "Trip chat is not available",
    "myTrips.chatClosed": "The chat closes when the trip is completed.",
    "myTrips.chatOnlyShared": "Chat is only available in shared trips with travel companions.",
    "contact.subject": "Subject",
    "contact.subjectPlaceholder": "Briefly describe what this is about",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Tell us more in detail...",
    "contact.creating": "Creating...",
    "contact.sendTicket": "Send ticket",
    "contact.allTickets": "All tickets",
    "contact.waitingReply": "Vantastika is waiting for your reply",
    "contact.resolvedLabel": "Resolved",
    "contact.closedNotice": "This ticket is closed. Create a new ticket if you need more help.",
    "contact.you": "You",
    "contact.replyPlaceholder": "Write your reply...",
    "contact.sending": "Sending...",
    "contact.reply": "Reply",
    "places.yourPosition": "Your position",
    "places.unnamedPlace": "Unnamed place",
    "places.myPosition": "My position",
    "map.listView": "List view",
    "map.mapView": "Map view",
    "shared.joinedTrip": "You have joined the trip!",
    "shared.joinedDesc": "You can now view and edit the trip together with the travel group.",
    "shared.couldNotJoin": "Could not join",
    "shared.tryAgainLater": "Try again later.",
    "shared.searchStartPlace": "Search for your start location...",
    "shared.savingCopy": "Saving...",
    "shared.saveAsCopy": "Save as your own copy",
    "camping.blurBenefitDesc": "See full description and amenities",
    "camping.blurBenefitPrice": "View prices and price history",
    "camping.blurBenefitReviews": "Read reviews from other travelers",
    "camping.blurBenefitMap": "Open map and navigate directly",
    "login.title": "Welcome back",
    "login.subtitle": "Log in to your Vantastika account",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Log In",
    "login.loading": "Logging in...",
    "login.failed": "Login failed",
    "login.noAccount": "Don't have an account?",
    "login.signUp": "Sign up",
    "login.forgotPassword": "Forgot password?",
    "login.or": "OR",
    "login.verificationFailed": "Verification failed. Try again.",
    "login.captchaFailed": "CAPTCHA verification failed. Try again.",
    "login.tooManyAttempts": "Too many login attempts. Wait a moment.",
    "login.wrongCredentials": "Incorrect email or password.",
    "social.continueGoogle": "Continue with Google",
    "social.continueApple": "Continue with Apple",
    "social.loginFailed": "Login failed",
    "social.unknownError": "Unknown error",
    "forgot.title": "Forgot password",
    "forgot.checkInbox": "Check your inbox!",
    "forgot.enterEmail": "Enter your email and we'll send a reset link.",
    "forgot.sentTo": "We sent an email to",
    "forgot.checkSpam": "with a link to reset your password. Check spam if you can't find it.",
    "forgot.backToLogin": "Back to login",
    "forgot.emailLabel": "Email address",
    "forgot.emailPlaceholder": "your@email.com",
    "forgot.sending": "Sending...",
    "forgot.sendLink": "Send reset link",
    "forgot.couldNotSend": "Could not send reset email",
    "forgot.enterEmailFirst": "Enter your email address",
    "reset.title": "New password",
    "reset.subtitle": "Choose a new password for your account.",
    "reset.newPassword": "New password",
    "reset.confirmPassword": "Confirm password",
    "reset.placeholder": "At least 8 characters, letter + number",
    "reset.tooShort": "Password must be at least 8 characters",
    "reset.noMatch": "Passwords don't match",
    "reset.updating": "Updating...",
    "reset.update": "Update password",
    "reset.couldNotUpdate": "Could not update password",
    "reset.success": "Password updated!",
    "reset.successDesc": "Your password has been changed successfully.",
    "reset.goToProfile": "Go to my profile",
    "reset.invalidLink": "Invalid link",
    "reset.invalidLinkDesc": "The reset link is invalid or has expired. Request a new one from the login page.",
    "signup.title": "Create an account",
    "signup.subtitle": "Save campings and start planning trips for free",
    "signup.name": "Display name",
    "signup.email": "Email",
    "signup.password": "Password",
    "signup.gdpr": "I agree to the privacy policy and consent to the processing of my personal data in accordance with GDPR.",
    "signup.submit": "Sign Up",
    "signup.loading": "Creating account...",
    "signup.failed": "Sign up failed",
    "signup.success": "Account created!",
    "signup.checkEmail": "Check your email to confirm your account.",
    "signup.acceptPrivacy": "Please accept the privacy policy",
    "signup.hasAccount": "Already have an account?",
    "signup.logIn": "Log in",
    "signup.heroTitle": "Plan your next trip in 60 seconds",
    "signup.heroSubtitle": "Create a free account and unlock all of Vantastika — no ads, no credit card.",
    "signup.benefit1": "Save your trips forever",
    "signup.benefit2": "Get camping suggestions along the way",
    "signup.benefit3": "Join the community",
    "signup.benefit4": "Free forever — no card required",
    "signup.titleShort": "Create your account",
    "signup.subtitleShort": "Takes 10 seconds. No credit card needed.",
    "signup.submitShort": "Create free account",
    "signup.miniConsent": "I accept the",
    "signup.ageMini": "I am at least 16 years old.",
    "signup.trustLine": "🔒 We never share your email. Cancel anytime.",
    "signup.freeForever": "Free forever",
    "signup.confirmPasswordPlaceholder": "Repeat your password",
    "footer.desc": "Plan your trip, find the right spots and travel with full control. Motorhome, van or tent.",
    "footer.explore": "Explore",
    "footer.searchCampings": "Search Campings",
    "footer.account": "Account",
    "footer.legal": "Legal",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.rights": "All rights reserved.",
    "footer.followInstagram": "Follow Vantastika on Instagram",
    "footer.followFacebook": "Follow Vantastika on Facebook",
    "footer.followUs": "Follow us",
    "geo.myLocation": "My Location",
    "geo.nearMe": "Near me",
    "geo.permissionDenied": "Location permission denied",
    "geo.detecting": "Detecting location...",
    // Trip planner
    "trip.editTrip": "Edit trip",
    "trip.tripTitle": "Trip title",
    "trip.destination": "Destination (search address...)",
    "trip.startDate": "Start date",
    "trip.consumption": "Consumption (l/100km)",
    "trip.consumptionEv": "Consumption (kWh/100km)",
    "trip.fuelPrice": "Diesel price",
    "trip.fuelPriceBensin": "Petrol price",
    "trip.fuelPriceEv": "Electricity price",
    "trip.fuelPriceUnitEv": "/kWh",
    "trip.vehicleType": "Vehicle type",
    "trip.vehicleDiesel": "Diesel",
    "trip.vehicleBensin": "Petrol",
    "trip.vehicleEv": "Electric",
    "trip.travelMode": "Travel mode",
    "trip.travelModeCar": "Car",
    "trip.travelModeMotorhome": "Motorhome",
    "trip.travelModeMotorcycle": "Motorcycle",
    "trip.travelModeDesc": "AI adapts routes and tips based on your vehicle",
    "trip.priceCurrency": "Price currency (the currency you enter the price in)",
    "trip.saveChanges": "Save changes",
    "trip.calculatingRoute": "Calculating route...",
    "trip.driveTime": "drive time",
    "trip.estimatedFuelCost": "Estimated fuel cost:",
    "trip.estimatedDiesel": "Estimated diesel cost:",
    "trip.estimatedChargeCost": "Estimated charging cost:",
    "trip.openGoogleMaps": "Open in Google Maps",
    "trip.tankSize": "Tank size (l)",
    "trip.batterySize": "Battery (kWh)",
    "trip.rangeAnalysis": "Range analysis",
    "trip.rangePerFill": "Range per fill",
    "trip.chargingStops": "Charging stops",
    "trip.fuelStops": "Refueling stops",
    "trip.avgLeg": "Avg. per leg",
    "trip.costPerCharge": "Cost per charge",
    "trip.costPerFill": "Cost per fill",
    "trip.evStopNote": "You'll need to charge approximately {stops} times, roughly every {km} km.",
    "trip.fuelStopNote": "You'll need to refuel approximately {stops} times, roughly every {km} km.",
    "trip.noStopsNeeded": "Your range covers the entire trip without stopping!",
    "trip.routeAlternatives": "Route alternatives",
    "trip.route": "Route",
    "trip.addMin2Stops": "Add at least 2 stops for fuel calculation",
    "trip.addStop": "Add stop",
    "trip.newStop": "New stop",
    "trip.searchPlace": "Search place or address...",
    "trip.notesOptional": "Notes (optional)",
    "trip.saving": "Saving...",
    "trip.add": "Add",
    "trip.cancel": "Cancel",
    "trip.dragToReorder": "Drag and drop to reorder",
    "trip.navigate": "Navigate here",
    "trip.startNavigation": "Start navigation",
    "trip.uploading": "Uploading...",
    "trip.writeExperience": "Write about your experience here...",
    "trip.save": "Save",
    "trip.tripExperience": "Trip experience",
    "trip.edit": "Edit",
    "trip.write": "Write",
    "trip.describeTripExperience": "Describe your trip, what you experienced, tips for others...",
    "trip.tripUpdated": "Trip updated!",
    "trip.linkCopied": "Link copied!",
    "trip.shareLinkDesc": "Share the link with your friends.",
    "trip.couldNotShare": "Could not share",
    "trip.imageUploaded": "Image uploaded!",
    "trip.couldNotUpload": "Could not upload image",
    "trip.experienceSaved": "Experience saved!",
    "trip.stopAdded": "Stop added!",
    "trip.couldNotAddStop": "Could not add stop",
    "trip.orderUpdated": "Order updated!",
    "trip.fuelCalc": "Fuel calculation (diesel)",
    "trip.currency": "Currency",
    "trip.stopType": "Stop type",
    "trip.stopTypeVisit": "Short visit",
    "trip.stopTypeOvernight": "Overnight",
    "trip.stopTypeDestination": "Final destination",
    "trip.nightsCount": "Nights",
    "trip.editStop": "Edit stop",
    "trip.optimizeRoute": "Optimize route",
    "trip.outboundLeg": "Outbound",
    "trip.returnLeg": "Return trip",
    "trip.returnLegDesc": "Return journey home",
    "trip.addReturnStop": "Add stop on return",
    "trip.optimizeReturn": "Optimize return",
    "trip.returnOptimized": "Return route optimized!",
    "trip.noReturnStops": "No return stops yet. Add stops for your journey home.",
    "trip.markAsReturn": "Mark as return",
    "trip.markAsOutbound": "Mark as outbound",
    "trip.routeOptimized": "Route optimized!",
    "optimize.title": "Optimize route order",
    "optimize.description": "Compare different route alternatives and choose the one that suits your trip best.",
    "optimize.analyzing": "Analyzing route alternatives…",
    "optimize.currentOrder": "Current order",
    "optimize.currentDesc": "Your current stop order as planned.",
    "optimize.geographic": "Most logical route",
    "optimize.geographicDesc": "Stops sorted for natural geographic flow toward destination.",
    "optimize.shortest": "Shortest route",
    "optimize.shortestDesc": "Nearest-neighbor sorting to minimize total driving distance.",
    "optimize.dayOptimized": "Day-trip optimized",
    "optimize.dayOptimizedDesc": "Organized around overnight stops for logical day stages.",
    "optimize.recommended": "Recommended",
    "optimize.stopOrder": "Stop order",
    "optimize.start": "Start",
    "optimize.end": "Destination",
    "optimize.overnights": "overnights",
    "optimize.shorter": "shorter",
    "optimize.longer": "longer",
    "optimize.saves": "saves",
    "optimize.useThisRoute": "Use this route",
    "optimize.cancel": "Cancel",
    // Travel Tips
    "tip.notFound": "Trip not found",
    "tip.notFoundDesc": "It may no longer be available.",
    "tip.back": "Back",
    "tip.traveler": "Traveler",
    "tip.sharedTrip": "Shared trip",
    "tip.planThisTrip": "Plan this trip",
    "tip.aboutTrip": "About this trip",
    "tip.aiGenerated": "AI-generated trip presentation",
    "tip.budget": "Budget",
    "tip.stops": "Stops",
    "tip.places": "places",
    "tip.duration": "Duration",
    "tip.days": "days",
    "tip.consumption": "Consumption",
    "tip.goodToKnow": "Good to know before you go",
    "tip.bestSeason": "Best season",
    "tip.currency": "Currency",
    "tip.speed": "Speed",
    "tip.emergency": "Emergency",
    "tip.campingRules": "Camping rules",
    "tip.route": "The route",
    "tip.endOfTrip": "End of trip",
    "tip.wantSameRoute": "Want to take the same route?",
    "tip.cloneDesc": "We'll create a brand new trip for you with the same stops. You can then customize it — change the start, add stops and plan your budget.",
    "tip.needAccount": "You need an account to save the trip",
    "tip.tripCreated": "Trip created! 🚐 Now you can customize it.",
    "tip.couldNotCreate": "Could not create trip",
    "tip.home": "Home",
    "tip.travelTips": "Travel tips",
    "tip.startPoint": "Start point",
    "tip.endPoint": "End point",
    "tip.travelGuide": "Travel guide",
    "tip.hide": "Hide",
    "tip.sharedThisTrip": "shared this trip",
    "tip.explore": "Explore",
    "tip.inspirationTitle": "Inspiration from travelers",
    "tip.inspirationDesc": "Discover routes shared by other travelers",
    "tip.tipHidden": "Travel tip hidden from front page",
    "tip.couldNotHide": "Could not hide travel tip",
    "tip.publishAsTip": "Publish as travel tip",
    "tip.publishDesc": "Show your trip on the front page as inspiration for others",
    "tip.creatingAi": "Creating AI trip presentation... ✨",
    "tip.creatingAiDesc": "This may take a moment.",
    "tip.publishedWithAi": "Trip published with AI guide! 🌍✨",
    "tip.published": "Trip is now visible to everyone! 🌍",
    "tip.unpublished": "Trip is now private",
    "tip.publishedNoAi": "Trip published! (AI guide could not be created)",
    "tip.routeHighlights": "Tips along this route",
    "tip.routeHighlightsDesc": "Recommended places and highlights for your trip",
    "tip.openInMaps": "Open in Maps",
    "tip.saveMoney": "Save money",
    "tip.fuelEstimate": "Fuel cost",
    "tip.campingCost": "Camping",
    "tip.night": "night",
    "tip.yourDescription": "Your description (optional)",
    "tip.yourDescriptionPlaceholder": "Share what makes this route special, tips for others, or why you liked it...",
    // Submit
    "submit.loginRequired": "Login Required",
    "submit.loginDesc": "You need to be logged in to register a place.",
    "submit.title": "Register a Place",
    "submit.subtitle": "Share your favorite camping spot with the community",
    "submit.reviewNote": "Registered places will be reviewed before being published.",
    "submit.basicInfo": "Basic Information",
    "submit.name": "Name",
    "submit.type": "Type",
    "submit.country": "Country",
    "submit.region": "Region",
    "submit.city": "City",
    "submit.description": "Description",
    "submit.shortDescription": "Short description",
    "submit.fullDescription": "Full description",
    "submit.priceSchedule": "Price & Schedule",
    "submit.freeSpot": "This is a free spot",
    "submit.pricePerNight": "Price per night (€)",
    "submit.imageContact": "Photos & Contact",
    "submit.imageUrl": "Image URL",
    "submit.phone": "Phone",
    "submit.email": "Email",
    "submit.website": "Website",
    "submit.address": "Address",
    "submit.fullAddress": "Full address",
    "submit.searchAddress": "Search address...",
    "submit.amenities": "Amenities",
    "submit.photos": "Photos",
    "submit.photosDesc": "Upload up to 5 photos",
    "submit.submit": "Register Place",
    "submit.submitting": "Registering...",
    "submit.success": "Registered!",
    "submit.successDesc": "Your place will be reviewed by an admin.",
    "submit.missingFields": "Missing fields",
    "submit.missingFieldsDesc": "Name, country and region are required.",
    "submit.ctaTitle": "Know a great spot?",
    "submit.ctaDesc": "Register a camping or stellplatz",
    // Home page
    "home.featuresTrustTitle": "Verified places",
    "home.featuresTrustDesc": "Real campings and pitches with prices, reviews and everything you need before you go.",
    "home.featuresRoutesTitle": "Smart routes",
    "home.featuresRoutesDesc": "See the whole trip in one view. Stops, driving times, fuel cost and alternative roads right on the map.",
    "home.featuresBudgetTitle": "Travel budget",
    "home.featuresBudgetDesc": "Set a budget, log your spending and see exactly where the money goes. Category by category.",
    "home.featuresChecklistTitle": "Checklist",
    "home.featuresChecklistDesc": "Checklists tailored to your trip type. Gas, water, passport, charger. All in one place.",
    "home.featuresAITitle": "AI assistant",
    "home.featuresAIDesc": "Ask Vannie anything about your trip. Routes, rules, tips and local knowledge. Always available.",
    "home.sectionTitle": "See how it works",
    "home.sectionSubtitle": "From the first idea to your trip on the road. Everything in one place.",
    "home.trustPlaces": "2 400+ places",
    "home.trustEurope": "All of Europe",
    "home.videoOverlay1": "Plan your route. Find the best places. Hit the road.",
    "home.videoOverlay2": "Everything you need for your road trip.",
    "home.lifestyleTitle": "Your trip. Your way.",
    "home.lifestyleSubtitle": "Routes, stops, budget and packing list. Ready in seconds.",
    "home.lifestyleCta": "Start planning",
    "home.vannieTitle": "Meet Vannie. Your personal travel guide.",
    "home.vannieDesc": "Ask a question, get a straight answer. Best places, local tips, route suggestions and rules by country. Vannie knows. And it costs nothing.",
    "home.vannieCta": "Chat with Vannie",
    "home.vannieCtaSignup": "Join and chat with Vannie",
    "home.vannieRoutes": "Route planning",
    "home.vannieCamping": "Camping tips",
    "home.vannieRules": "Local rules",
    "home.vannieBudget": "Budget help",
    "nav.services": "Service & SOS",
    "nav.games": "Roadtrip Games",
    "nav.vannie": "Vannie",
    "nav.community": "Community",
    "nav.profile": "My profile",
    // Camping card
    "card.free": "FREE",
    "card.night": "night",
    "card.loginForPrice": "Login for price",
    // Quick categories
    "cat.campings": "Campings",
    "cat.stellplatz": "Stellplatz",
    "cat.freeSpots": "Free Spots",
    // Map
    "map.noStopsToShow": "No stops with coordinates to show",
    // Wizard
    "wizard.whereToGo": "Where do you want to go?",
    "wizard.searchDestination": "Search destination (e.g. Sanremo, Italy)...",
    "wizard.startPoint": "Where are you starting from?",
    "wizard.whenTravel": "When are you traveling?",
    "wizard.myLocation": "My location",
    "wizard.chooseManually": "Choose manually",
    "wizard.searchStartPoint": "Search start point...",
    "wizard.locationDetected": "Location detected",
    "wizard.start": "Start",
    "wizard.destination": "Destination",
    "wizard.yourStops": "Your stops",
    "wizard.stop": "Stop",
    "wizard.addedAsStop": "added as stop!",
    "campingDetector.title": "Looks like a camping!",
    "campingDetector.aiDetected": "AI detected",
    "campingDetector.description": "Want to add this place to Vantastika so other travelers can find it too?",
    "campingDetector.pendingNote": "It will be reviewed by an admin before going public.",
    "campingDetector.confidence": "match",
    "campingDetector.typeCamping": "Camping",
    "campingDetector.typeStellplatz": "Motorhome stop",
    "campingDetector.typeFreeParking": "Free parking",
    "campingDetector.typeLodging": "Accommodation",
    "campingDetector.notNow": "Not now",
    "campingDetector.registerCta": "Add to Vantastika",
    "campingDetector.registering": "Adding…",
    "campingDetector.registeredTitle": "Thanks for the contribution!",
    "campingDetector.registeredDesc": "The place was sent for review and will be published once approved.",
    "campingDetector.alreadyExists": "Already in Vantastika",
    "campingDetector.alreadyExistsDesc": "This place is already listed.",
    "campingDetector.couldNotRegister": "Could not register the place",
    "campingDetector.loginRequired": "Login required",
    "campingDetector.loginRequiredDesc": "Sign in to contribute new places.",
    "campingDetector.cannotRegister": "Cannot register",
    "campingDetector.missingPlaceId": "Missing place identifier from Google.",
    "stopType.visit": "Visit",
    "stopType.overnight": "Overnight",
    "stopType.destination": "Destination",
    "stopType.nights": "Nights",
    "stopType.stayHours": "Stay (h)",
    "wizard.suggestedStops": "Campings along the route",
    "wizard.findingCampings": "Finding campings along the route...",
    "wizard.noCampingsNearRoute": "No campings found near this route",
    "wizard.saveTrip": "Save my trip",
    "wizard.stepPlanningMode": "Planning",
    "wizard.stepDrivingHours": "Driving",
    "wizard.stepTravelStyle": "Travel Style",
    "wizard.stepCountryInfo": "Countries",
    "wizard.howToPlan": "How do you want to plan your trip?",
    "wizard.howToPlanDesc": "Choose the option that suits you best.",
    "wizard.planModeKnow": "I know where I'm going",
    "wizard.planModeKnowDesc": "I have a destination and want to plan the route.",
    "wizard.planModeHelp": "Help me find a trip",
    "wizard.planModeHelpDesc": "I want inspiration and AI suggestions for trips.",
    "wizard.planModeOptimize": "Optimize my route smartly",
    "wizard.planModeOptimizeDesc": "I know roughly where I'm going but want the best stages and stops.",
    "wizard.stepDetails": "Details",
    "wizard.stepTrip": "Trip",
    "wizard.stepPreferences": "Preferences",
    "wizard.dailyDrivingTitle": "How long do you want to drive per day?",
    "wizard.dailyDrivingDesc": "We adapt the stages based on your choice.",
    "wizard.recommended": "Recommended",
    "wizard.travelStyleTitle": "How do you want to travel?",
    "wizard.travelStyleDesc": "We adapt the route and suggestions to your travel style.",
    "wizard.styleFastest": "Fastest route",
    "wizard.styleFastestDesc": "Prioritize shortest driving time via highways.",
    "wizard.styleSafe": "Safe and relaxed",
    "wizard.styleSafeDesc": "Calmer roads with reasonable daily driving.",
    "wizard.styleScenic": "Scenic route",
    "wizard.styleScenicDesc": "Prioritize beautiful roads and views.",
    "wizard.styleCamping": "Camping-friendly route",
    "wizard.styleCampingDesc": "Route with good access to campsites.",
    "wizard.styleBudget": "Budget-friendly trip",
    "wizard.styleBudgetDesc": "Avoid tolls and expensive stops.",
    "wizard.styleFamily": "Family-friendly trip",
    "wizard.styleFamilyDesc": "Shorter stages with kid-friendly stops.",
    "wizard.selectTravelStyle": "Select a travel style",
    "wizard.longDrivingWarning": "Longer daily driving can affect road safety. We recommend max 4-5 hours per day.",
    "wizard.skipOvernightHere": "Skip overnight – keep driving",
    "wizard.resetStages": "Reset stages",
    "wizard.passThrough": "Pass-through",
    "wizard.noOvernightShort": "No overnight stay",
    "wizard.mergedDayTitle": "You're driving through without an overnight stay",
    "wizard.mergedDayDesc": "This day is merged with the next. Short breaks for rest and refueling are recommended.",
    "wizard.undoSkipOvernight": "Undo – stay overnight here",
    "wizard.selectPlanningMode": "Choose how you want to plan",
    "wizard.accommodationTitle": "Accommodation suggestions",
    "wizard.accommodationDescCamping": "Showing campsites and motorhome stops along your route.",
    "wizard.accommodationDescCar": "Showing hotels and stays near your destination.",
    "wizard.campingRules": "Camping rules",
    "wizard.countriesInRouteOrder": "Countries shown in route order",
    "wizard.countryInfoDesc": "Practical information for each country you pass through.",
    "wizard.countryInfoTitle": "Countries along your route",
    "wizard.startCountry": "Start country",
    "wizard.transitCountry": "Transit country",
    "wizard.destinationCountry": "Destination",
    "wizard.ferryAlternatives": "Ferry alternatives",
    "wizard.ferryDuration": "Duration",
    "wizard.ferryRoute": "Route",
    "wizard.ferryRelevance": "Why relevant",
    "wizard.driving": "Driving",
    "wizard.drivingRules": "Driving rules",
    "wizard.fromRoute": "Along route",
    "wizard.fuelInfo": "Fuel",
    "wizard.importantForMotorhome": "important for motorhome",
    "wizard.infoItems": "info items",
    "wizard.loadingCountryInfo": "Loading country information...",
    "wizard.motorhomeMultiCountryTip": "With a motorhome, rules can vary significantly between countries. Check environmental zones, vignettes and permitted dimensions before departure.",
    "wizard.noCountryInfo": "No country information available. Enter a route first.",
    "wizard.noResultsForStage": "No results for this stage",
    "wizard.oneWay": "One way",
    "wizard.optimizeRoute": "Optimize stop order",
    "wizard.searchingStage": "Searching for stage",
    "wizard.selectDestForAccommodation": "Select a destination first to see accommodation suggestions.",
    "wizard.shortTripNoOvernight": "Short trip – no overnight stay needed.",
    "wizard.skipStep": "Skip this step",
    "wizard.from": "From",
    "wizard.to": "To",
    "wizard.selectedAccommodation": "Selected accommodation",
    "wizard.changeAccommodation": "Change",
    "wizard.selected": "Selected",
    "wizard.selectAccommodation": "Select",
    "wizard.noAccommodationYet": "No accommodation selected yet. You can choose in the next step.",
    "wizard.viewOnMap": "View on map",
    "common.close": "Close",
    "wizard.speedLimits": "Speed limits",
    "wizard.stages": "stages",
    "wizard.tolls": "Tolls & vignettes",
    "wizard.wildCamping": "Wild camping",
    "wizard.drivingHoursPerDay": "Driving hours per day",
    "wizard.countriesOnRoute": "Countries along the route",
    "wizard.emergency": "Emergency number",
    "wizard.longDrivingTitle": "Long daily stage",
    "wizard.safeDrivingNote": "Good choice! Calmer daily stages make for a safer and more enjoyable trip with time to explore along the way.",
    "wizard.stepRoute": "Route",
    "wizard.stepReturn": "Return",
    "wizard.stepAccommodation": "Accommodation",
    "wizard.stepVehicle": "Vehicle",
    "wizard.stepOverview": "Overview",
    "wizard.next": "Next",
    "wizard.back": "Back",
    "wizard.tripTypeLabel": "Trip type",
    "wizard.accommodationDesc": "Here are accommodation suggestions based on your route and travel mode. Add them as stops or skip this step.",
    "wizard.showingCampingsAlongRoute": "Showing camping spots along your route",
    "wizard.showingHotelsNearDest": "Showing hotels near your destination",
    "wizard.showingAccommodationNearDest": "Showing accommodation near your route",
    "wizard.searchingAlongRoute": "Searching along your route...",
    "wizard.expandingSearch": "Expanding search area...",
    "wizard.tryingAlternatives": "Trying alternative accommodation...",
    "wizard.noAccommodationFound": "No accommodation found near your route. You can skip this step and add stops manually later.",
    "wizard.returnTripDesc": "Choose how you want to travel back.",
    "wizard.sameRouteDesc": "Drive back the same way",
    "wizard.scenicDesc": "Alternative scenic route",
    "wizard.fastestDesc": "Quickest route back",
    "wizard.routeSummary": "Route",
    "wizard.selectDestFirst": "Select a destination first",
    "wizard.selectStartFirst": "Select a start point first",
    "wizard.fuelType": "Fuel type",
    "wizard.estimatedFuelCostLabel": "Estimated fuel cost",
    "wizard.showPricesIn": "Show prices in",
    "wizard.perNight": "night",
    "wizard.finalDay": "Final destination",
    "wizard.overnight": "Overnight",
    "wizard.viewAllAccommodations": "View all",
    "wizard.accommodationNear": "Stays near",
    "wizard.tapToChooseAccommodation": "Tap to choose accommodation.",
    "wizard.accommodationMissingHint": "You planned an overnight here but no accommodation is selected.",
    "wizard.accommodationMissingTitle": "Accommodation missing",
    "wizard.accommodationMissingSubtitle": "Tap to choose accommodation for each day.",
    "wizard.detectedAccommodationTitle": "Looks like accommodation",
    "wizard.detectedAccommodationDesc": "looks like accommodation. How would you like to mark it?",
    "wizard.overnightHint": "You sleep here",
    "wizard.visitHint": "Just passing through or visiting",
    "wizard.destinationHint": "Trip's final destination",
    "wizard.moreDetails": "More details",
    "wizard.firstTimeHint": "Start by searching for your destination. Then choose vehicle, dates and stops — the route is calculated automatically!",
    "wizard.saving": "Saving...",
    "wizard.budgetOptional": "Set a budget (optional)",
    "wizard.budgetPlaceholder": "e.g. 15000",
    "wizard.fuelAutoAdded": "Estimated fuel cost added to budget",
    "wizard.planNewTrip": "Plan a new trip",
    "wizard.myTrips": "My saved trips",
    "wizard.planTrip": "Plan trip",
    "wizard.or": "or",
    "wizard.popularDestinations": "Popular",
    "wizard.describeTrip": "Describe your trip – we plan everything for you",
    "wizard.describeSubtext": "Write freely what you want to do and we'll build the suggestion",
    "wizard.backToManual": "Back to manual search",
    "wizard.selectSuggestion": "Select a suggestion from the list to continue.",
    "wizard.analyzingTrip": "Analyzing your trip…",
    "wizard.createPlan": "Create travel plan",
    "wizard.suggestedTrip": "Suggested trip",
    "wizard.places": "places",
    "wizard.startOver": "Start over",
    "wizard.noDestFound": "No destinations found",
    "wizard.noDestFoundDesc": "Try describing your trip in more detail.",
    "wizard.couldNotParse": "Could not interpret the trip",
    "wizard.couldNotParseDesc": "Try again with a clearer description.",
    "wizard.tooManyRequests": "Too many requests",
    "wizard.tooManyRequestsDesc": "Wait a moment and try again.",
    "wizard.aiCreditOut": "AI credits exhausted",
    "wizard.aiCreditOutDesc": "Contact support for more information.",
    "wizard.tripSaved": "Trip saved!",
    "wizard.tripSavedDesc": "Go to My Trips to see it.",
    "wizard.couldNotReachVannie": "Could not reach Vannie right now.",
    "wizard.describePlaceholder": "Describe your trip in your own words — we create a complete travel plan for you.",
    "wizard.chars": "characters",
    "wizard.destinationConfirmed": "Destination selected",
    "wizard.locating": "Locating coordinates...",
    "wizard.destType": "Destination",
    "wizard.visitType": "Visit",
    "wizard.aiSuggestTitle": "AI Travel Suggestion",
    "wizard.aiSuggestOpen": "Describe your trip – we plan everything for you",
    "wizard.aiSuggestPlaceholder": "E.g. 'I want to go to the beach for 5 days' or 'Cozy mountain trip with hiking in the Alps'",
    "wizard.vannieThinking": "Vannie is thinking...",
    "wizard.suggestTrip": "Suggest trip",
    "wizard.suggestedStopsLabel": "Suggested stops",
    "wizard.useThisSuggestion": "Use this suggestion",
    "wizard.couldNotGenerate": "Could not generate suggestion",
    "wizard.days": "days",
    "wizard.perDay": "/day",
    "wizard.roundtrip": "Round trip",
    "wizard.oneway": "One way",
    "wizard.departure": "Departure",
    "wizard.return": "Return",
    "wizard.selectDate": "Select date",
    "wizard.departureDateOptional": "Departure date (optional)",
    "wizard.day": "day",
    "wizard.dayLabel": "Day {n}",
    "wizard.roundtripLabel": "Round trip",
    "wizard.estimatedTravelTime": "Estimated travel time",
    "wizard.total": "total",
    "wizard.stopsLabel": "stops",
    "wizard.fetchingReturnTips": "Getting return route tips…",
    "wizard.returnTips": "Return route tips",
    "wizard.roundtripShort": "round trip",
    "wizard.driveTime": "Drive time",
    "wizard.chargeCost": "Charge cost",
    "wizard.fuelCost": "Fuel cost",
    "wizard.distance": "Distance",
    "wizard.chooseRoute": "Choose this route",
    "wizard.ferry": "Ferry",
    "wizard.maxDrivingHours": "Max driving hours per day",
    "wizard.longDriveWarning": "Long driving time",
    "wizard.longDriveDesc": "We recommend max 4 hours for safe and enjoyable driving.",
    "wizard.drivingDaysCalc": "{hours}h/day = ~{days} driving days",
    "wizard.drivingDaysRoundtrip": "days round trip",
    "wizard.vehicleSettings": "Vehicle & fuel settings",
    "wizard.scenic": "Scenic",
    "wizard.fastest": "Fastest",
    "wizard.balanced": "Balanced",
    "wizard.sameRoute": "Same route",
    "wizard.returnTrip": "Return trip",
    "wizard.homecoming": "Arrival home",
    "wizard.optimizationSummary": "Route optimized",
    "wizard.stageOverview": "Daily stages",
    "wizard.overnightIn": "Overnight near",
    "wizard.arrivalAt": "Arrival at",
    "wizard.returnRouteOption": "Return trip",
    "wizard.hours": "h",
    "wizard.addedAsOvernight": "added as overnight stop!",
    "wizard.addedAsRestStop": "added as rest stop!",
    "wizard.stopAddedFromMap": "Stop added from map!",
    "wizard.updatedStop": "Updated stop",
    "wizard.stopPositionUpdated": "Stop position updated",
    "wizard.newStop": "New stop",
    "wizard.optimizedDesc": "Stops reordered for shortest total distance",
    "wizard.newOrder": "New order",
    "wizard.orderChanged": "Order changed from original",
    "wizard.routeOptimized": "Route optimized!",
    "wizard.nameError": "Could not generate name suggestions",
    "wizard.mapInstructions": "Click on the map to add stops, drag to move them",
    "wizard.nameTrip": "Name your trip",
    "wizard.optional": "optional",
    "wizard.namePlaceholder": "e.g. Summer in the Alps",
    "wizard.aiSuggest": "AI suggest",
    "wizard.signupAndSave": "Sign up & save trip",
    "wizard.useTrip": "Use this trip",
    "wizard.dragToReorder": "Drag to reorder",
    "wizard.prefillTip": "Pre-filled from a Vantastika tip",
    "wizard.prefillGuide": "Pre-filled from a travel guide",
    "budget.title": "Travel Budget",
    "budget.tab": "Budget",
    "budget.setBudget": "Set budget",
    "budget.totalBudget": "Total budget (kr)",
    "budget.spent": "Spent",
    "budget.remaining": "Remaining",
    "budget.used": "used",
    "budget.topCategory": "Biggest category",
    "budget.addExpense": "Add expense",
    "budget.amount": "Amount",
    "budget.description": "Description (optional)",
    "budget.save": "Save",
    "budget.cancel": "Cancel",
    "budget.expenses": "Expenses",
    "budget.breakdown": "Category breakdown",
    "budget.expenseAdded": "Expense added!",
    "budget.expenseUpdated": "Expense updated!",
    "budget.expenseFailed": "Could not save expense",
    "budget.autoLabel": "Auto-generated",
    "budget.editFuelCalc": "Edit",
    "budget.fuelCalcTitle": "Fuel cost calculation",
    "budget.fuelPriceLabel": "Price/liter",
    "budget.fuelConsumptionLabel": "l/100km",
    "budget.fuelDistanceLabel": "Distance (km)",
    "budget.fuelCalcResult": "Estimated fuel cost:",
    "budget.fuelSave": "Update fuel cost",
    "budget.fuelUpdated": "Fuel cost updated!",
    "budget.budgetSaved": "Budget saved!",
    "budget.getAiTips": "Get budget tips from Vannie",
    "budget.aiTipsTitle": "Vannie's budget tips",
    "budget.noTips": "No tips available right now.",
    "budget.tipsFailed": "Could not load tips",
    "budget.close": "Close",
    "budget.cat.fuel": "Fuel",
    "budget.cat.camping": "Camping/Pitch",
    "budget.cat.food": "Food & Drink",
    "budget.cat.tolls": "Tolls/Ferries",
    "budget.cat.activities": "Activities",
    "budget.cat.shopping": "Shopping",
    "budget.cat.other": "Other",
    "budget.budgetLabel": "Budget",
    "budget.edit": "Edit",
    "budget.backToTrip": "Back to trip",
    "budget.estimatedFuel": "Estimated fuel",
    "budget.estimateOnly": "Estimate only",
    "budget.autoCosts": "Automatic costs",
    "budget.manualExpenses": "Your expenses",
    "budget.pageDesc": "Manage your budgets and expenses",
    "budget.loginRequired": "Log in to use the budget feature",
    "budget.subtitle": "Your budgets and expenses",
    "budget.pageSubtitle": "Plan your travel budget, add expenses and track how much you have left.",
    "budget.newBudget": "New budget",
    "budget.newExpense": "New expense",
    "budget.tabActive": "Active",
    "budget.tabAll": "All",
    "budget.tabExpenses": "Unlinked expenses",
    "budget.tabUnlinked": "Unlinked",
    "budget.emptyTitle": "No budgets yet",
    "budget.emptyDesc": "Create your first budget to get a better overview of your trip costs.",
    "budget.emptyStateTitle": "No budget created yet",
    "budget.emptyStateDesc": "Create your first travel budget to get a better overview of costs for accommodation, food, fuel and activities.",
    "budget.createBudget": "Create budget",
    "budget.createFirstBudget": "Create first budget",
    "budget.chipAccommodation": "Accommodation",
    "budget.chipFood": "Food",
    "budget.chipFuel": "Fuel",
    "budget.chipActivities": "Activities",
    "budget.chipShopping": "Shopping",
    "budget.unlinkedLabel": "Unlinked expenses",
    "budget.unlinkedHint": "These expenses are not linked to any budget.",
    "budget.more": "more",
    "budget.noActiveTitle": "No active budgets",
    "budget.noActiveDesc": "Create a budget or activate an existing one.",
    "budget.overviewTitle": "Budget overview",
    "budget.statusGood": "On track",
    "budget.statusWarning": "Approaching limit",
    "budget.statusOver": "Over budget",
    "budget.pctUsed": "used",
    "budget.totalBudgetCard": "Total budget",
    "budget.spentCard": "Spent",
    "budget.remainingCard": "Remaining",
    "budget.paused": "Paused",
    "budget.completed": "completed",
    "budget.noExpenses": "No unlinked expenses",
    "budget.noExpensesDesc": "Expenses without a linked budget will show up here",
    // Budget dialogs
    "budget.setBudgetTitle": "Monthly budget",
    "budget.setBudgetBtn": "Set budget",
    "budget.setBudgetSaved": "Budget limit saved ✓",
    "budget.month": "Month",
    "budget.budgetAmount": "Budget",
    "budget.amountPlaceholder": "e.g. 15000",
    "budget.saving": "Saving...",
    "budget.saveBudgetLimit": "Save budget limit",
    "budget.createBudgetTitle": "Create budget",
    "budget.budgetName": "Budget name",
    "budget.budgetNamePlaceholder": "e.g. Summer vacation 2026",
    "budget.budgetTypeLabel": "Budget type",
    "budget.totalBudgetLabel": "Total budget",
    "budget.currencyLabel": "Currency",
    "budget.startDateLabel": "Start date",
    "budget.endDateLabel": "End date",
    "budget.colorLabel": "Color",
    "budget.selectColor": "Select color",
    "budget.descriptionLabel": "Description",
    "budget.descriptionPlaceholder": "Optional note...",
    "budget.linkTrip": "Link to trip (optional)",
    "budget.noTripSelected": "No trip selected",
    "budget.noTrip": "No trip",
    "budget.fillNameAndAmount": "Enter a name and valid amount",
    "budget.budgetCreated": "Budget created ✓",
    "budget.couldNotSave": "Could not save",
    "budget.creating": "Creating...",
    "budget.addExpenseTitle": "Add expense",
    "budget.scanWithAi": "Scan receipt with AI",
    "budget.amountLabel": "Amount",
    "budget.dateLabel": "Date",
    "budget.titleLabel": "Title",
    "budget.titlePlaceholder": "What did you buy?",
    "budget.categoryLabel": "Category",
    "budget.paymentMethod": "Payment method",
    "budget.selectPayment": "Select...",
    "budget.linkBudget": "Link to budget (optional)",
    "budget.linkBudgetDirect": "Link to budget",
    "budget.noBudgetSelected": "No budget selected",
    "budget.noBudget": "No budget",
    "budget.noteLabel": "Note",
    "budget.notePlaceholder": "Optional note...",
    "budget.receiptLabel": "Receipt",
    "budget.receiptRemove": "Remove",
    "budget.receiptUpload": "Take photo or upload receipt",
    "budget.fileTooLarge": "File is too large",
    "budget.fileTooLargeDesc": "Max 15 MB",
    "budget.fillAmountAndTitle": "Enter a valid amount and title",
    "budget.expenseSaved": "Expense saved ✓",
    "budget.converted": "Converted:",
    "budget.editBudgetTitle": "Edit budget",
    "budget.budgetUpdated": "Budget updated ✓",
    "budget.saveChanges": "Save changes",
    "budget.editExpenseTitle": "Edit expense",
    "budget.scanTitle": "Smart receipt scan",
    "budget.scanCapture": "Take photo or upload receipt",
    "budget.scanCaptureDesc": "AI analyzes your receipt line by line",
    "budget.scanTakePhoto": "Take photo",
    "budget.scanUpload": "Upload",
    "budget.scanAnalyzing": "Analyzing receipt...",
    "budget.scanAnalyzingDesc": "AI is reading items, amounts and categories",
    "budget.scanFailed": "Could not analyze receipt",
    "budget.scanConfidenceHigh": "High confidence",
    "budget.scanConfidenceMedium": "Medium confidence — review fields",
    "budget.scanConfidenceLow": "Low confidence — check all fields",
    "budget.scanCandidates": "Possible amounts:",
    "budget.scanItemsPerCategory": "Items per category",
    "budget.scanMixedCategories": "Mixed categories",
    "budget.scanSaveHow": "How do you want to save?",
    "budget.scanSingleExpense": "One expense",
    "budget.scanSplitExpenses": "Split",
    "budget.scanSplitDesc": "Creates {n} separate expenses — one per category.",
    "budget.scanMainCategory": "Main category",
    "budget.scanShowRaw": "Show raw receipt text",
    "budget.scanHideRaw": "Hide raw receipt text",
    "budget.scanCancel": "Cancel",
    "budget.scanSaveExpense": "Save expense",
    "budget.scanSaveExpenses": "Save {n} expenses",
    "budget.scanSavedMultiple": "{n} expenses saved from receipt ✓",
    "budget.scanSavedSingle": "Expense saved from receipt ✓",
    "budget.confidenceMedium": "Medium confidence",
    "budget.confidenceLow": "Uncertain — please check",
    // Budget detail
    "budget.statusPaused": "Paused",
    "budget.statusCompleted": "Completed",
    "budget.budgetLabel2": "Budget",
    "budget.spentLabel": "Spent",
    "budget.remainingLabel": "Remaining",
    "budget.overBy": "Over by",
    "budget.usedPercent": "used",
    "budget.expenseCount": "expenses",
    "budget.mixedCurrenciesNote": "Expenses in {currencies} — converted to {target}",
    "budget.deleteConfirm": "Are you sure you want to delete this budget?",
    "budget.statusActivated": "Budget activated",
    "budget.statusPausedToast": "Budget paused",
    "budget.statusCompletedToast": "Budget completed",
    "budget.perCategory": "Per category",
    "budget.biggestExpense": "Biggest expense",
    "budget.expenseListTitle": "Expenses",
    "budget.noExpensesYet": "No expenses yet",
    "budget.noExpensesYetDesc": "Tap \"Add expense\" to get started",
    // Budget overview
    "budget.totalSpent": "Total spent",
    "budget.thisMonth": "This month",
    "budget.budgeted": "Budgeted",
    "budget.warnings": "Warnings",
    "summary.title": "Trip Cost Summary",
    "summary.titleOngoing": "Cost Overview",
    "summary.over": "Over budget",
    "summary.saved": "Saved",
    "summary.dailyAvg": "Per day",
    "summary.overBudget": "You went over budget on this trip",
    "summary.underBudget": "You stayed within budget — great job!",
    "summary.breakdown": "Spending Breakdown",
    "summary.categoryDetails": "Category Details",
    "fuelLog.title": "Fuel Log",
    "fuelLog.addFuel": "Add refueling",
    "fuelLog.liters": "Liters",
    "fuelLog.cost": "Cost",
    "fuelLog.date": "Date",
    "fuelLog.odometer": "Odometer (km)",
    "fuelLog.note": "Note",
    "fuelLog.optional": "Optional",
    "fuelLog.save": "Save",
    "fuelLog.added": "Refueling logged!",
    "fuelLog.addFailed": "Could not save refueling",
    "fuelLog.invalidInput": "Enter both liters and cost",
    "fuelSummary.title": "Fuel Summary",
    "fuelSummary.totalLiters": "Total liters",
    "fuelSummary.totalCost": "Fuel cost",
    "fuelSummary.avgPrice": "Avg price/liter",
    "fuelSummary.consumption": "Consumption",
    "fuelSummary.costPerKm": "Cost/km",
    "fuelSummary.costPerMil": "Cost/mil",
    "fuelSummary.noDistance": "Add route distance to see consumption and cost per km.",
    "fuelSummary.basedOn": "Based on {n} refueling(s)",
    "checklist.tab": "Checklist",
    "journal.tab": "Journal",
    "authGate.title": "Save your trip and continue whenever you want",
    "authGate.description": "Create a free account to keep your trip, your stops and your budget — so you can pick up where you left off.",
    "authGate.signup": "Create account and save",
    "authGate.login": "Log in",
    "authGate.ctaSection": "Everything You Need for Your Trip",
    "authGate.ctaPlaces": "Your stops and route — saved and synced",
    "authGate.ctaRoutes": "Continue planning on any device",
    "authGate.ctaBudget": "Budget and receipts — always accessible",
    "authGate.ctaChecklist": "Checklists that follow your trip",
    "authGate.ctaAI": "AI assistant that knows every road",
    "authGate.ctaButton": "Create account and save",
    "authGate.saveTripMsg": "Save your trip and continue later",
    "authGate.saveBudgetMsg": "Save your budget and track expenses",
    "authGate.saveReceiptMsg": "Keep your receipts across devices",
    "authGate.saveFavoriteMsg": "Save your favorite places in one list",
    "authGate.planRouteMsg": "Continue where you left off",
    "authGate.savePlaceMsg": "Save this place to your collection",
    "authGate.useBudgetMsg": "Track your budget — create a free account",
    "authGate.useChecklistMsg": "Use checklists — create a free account",
    "authGate.or": "or",
    "authGate.hasAccount": "Already have an account?",
    "gate.whyJoin": "WHY CREATE AN ACCOUNT?",
    "gate.benefit1": "Keep your trip, stops and budget safe",
    "gate.benefit2": "Continue planning on any device",
    "gate.benefit3": "Full budget tracking with receipt scanning",
    "gate.benefit4": "AI travel assistant included for free",
    "gate.benefit5": "Access community tips and reviews",
    "gate.benefit6": "Checklists tailored to your vehicle",
    "gate.freeForever": "Free to get started",
    "welcome.title": "Welcome to Vantastika!",
    "welcome.titleName": "Welcome, {name}!",
    "welcome.desc": "Everything you need to plan, travel and remember your trips is right here.",
    "welcome.feature1": "Plan routes, add stops and track budget and fuel",
    "welcome.feature2": "Vannie, your AI travel assistant, helps you find the best routes",
    "welcome.feature3": "Save favorite places, share trips with friends",
    "welcome.cta": "Plan my first trip",
    "welcome.dismiss": "Explore on my own",
    "welcome.close": "Close",
    "gate.noCard": "Free to get started. No credit card required.",
    "gate.createAccount": "Create free account",
    "gate.alreadyMember": "Already have an account? Log in",
    "gate.seeMore": "Want to see more?",
    "gate.registerToSee": "Create a free account to access all features and places.",
    "gate.searchTitle": "Explore all camping spots",
    "gate.searchDesc": "Register for free to browse all verified places, save your favorites and read reviews from the community.",
    "gate.servicesTitle": "Service & Emergency",
    "gate.servicesDesc": "Create an account to find workshops, charging stations, water points and emergency services near you.",
    "gate.tripsTitle": "Trip Planner",
    "gate.tripsDesc": "Register for free to plan unlimited trips with smart routes, fuel costs and budget tracking.",
    "gate.submitTitle": "Add a place",
    "gate.submitDesc": "Register for free to share your favorite spots with the community.",
    "gate.tipsTitle": "Road Tips",
    "gate.tipsDesc": "Create an account to read and share travel tips from real travelers.",
    "roadTips.title": "Road Tips",
    "roadTips.share": "Share a Tip",
    "roadTips.allCountries": "All countries",
    "roadTips.formTitle": "Share your road tip",
    "roadTips.labelTitle": "Title *",
    "roadTips.placeholderTitle": "e.g. Best route through the Alps",
    "roadTips.labelType": "Type",
    "roadTips.labelCountry": "Country",
    "roadTips.select": "Select",
    "roadTips.labelTip": "Your tip *",
    "roadTips.placeholderTip": "Share your experience...",
    "roadTips.labelImage": "Image URL (optional)",
    "roadTips.posting": "Posting...",
    "roadTips.post": "Post Tip",
    "roadTips.shared": "Tip shared!",
    "roadTips.loginToShare": "Log in to share your own road tips.",
    "roadTips.noTips": "No road tips yet. Be the first to share!",
    "roadTips.type.general": "General Tip",
    "roadTips.type.route": "Route Recommendation",
    "roadTips.type.parking": "Parking Tip",
    "roadTips.type.food": "Food & Shopping",
    "roadTips.type.warning": "Warning / Heads Up",
    "roadTips.type.service": "Service Point",
    "community.title": "Community",
    "community.subtitle": "Share experiences, tips and inspiration with other travelers",
    "community.newPost": "New post",
    "community.createPost": "Create new post",
    "community.all": "All",
    "community.anonymous": "Anonymous",
    "community.noPostsYet": "No posts yet",
    "community.beFirst": "Be the first to start a discussion!",
    "community.createFirst": "Create the first post",
    "community.loadMore": "Load more posts",
    "community.loading": "Loading…",
    "community.back": "Back",
    "community.comments": "Comments",
    "community.noComments": "No comments yet — be the first!",
    "community.commentPlaceholder": "Write a comment...",
    "community.titlePlaceholder": "Title",
    "community.contentPlaceholder": "What would you like to discuss?",
    "community.addImage": "Add image (optional)",
    "community.publishing": "Publishing...",
    "community.publish": "Publish post",
    "community.toast.postCreated": "Post created!",
    "community.toast.postPublished": "Your post has been published.",
    "community.toast.postCreateError": "Could not create post.",
    "community.toast.commentError": "Could not create comment.",
    "community.toast.postDeleted": "Post deleted",
    "community.toast.error": "Error",
    "community.confirmDelete": "Are you sure you want to delete this post?",
    "community.cat.general": "General",
    "community.cat.tips": "Tips & Tricks",
    "community.cat.routes": "Routes & Places",
    "community.cat.gear": "Gear",
    "community.cat.help": "Help & Questions",
    "community.cat.photos": "Photos & Stories",
    "community.type.question": "Question",
    "community.type.tip": "Tip",
    "community.type.discussion": "Discussion",
    "community.type.routes": "Routes & Places",
    "community.type.gear": "Gear",
    "community.type.photos": "Photos",
    "community.type.help": "Help",
    "community.type.question.desc": "Ask the community a question",
    "community.type.tip.desc": "Share a great tip",
    "community.type.discussion.desc": "Start a discussion",
    "community.type.routes.desc": "Share favorite places",
    "community.type.gear.desc": "Gear & accessories",
    "community.type.photos.desc": "Share your trip",
    "community.type.help.desc": "Ask for help",
    "community.awaitingAnswer": "Awaiting answer",
    "community.selectType": "Choose post type",
    "community.createPostTitle": "Create post",
    "community.changeType": "Change type",
    "community.questionPlaceholder": "Write your question here...",
    "community.questionContentPlaceholder": "Describe your question in detail so others can help...",
    "community.communityRules": "Community rules",
    "community.communityRulesText": "You may not post nudity, sexual content, advertisements, sales, spam, threats or harassment. All posts and images may be reviewed automatically before publishing.",
    "games.seoTitle": "Roadtrip Games – Vantastika",
    "games.seoDesc": "Play quiz, bingo and more games during your roadtrip",
    "demo.phase1Eyebrow": "Too many choices",
    "demo.phase1Title": "See the problem",
    "demo.phase1Text": "The map is full of places and it's hard to know what's actually worth the stop.",
    "demo.phase2Eyebrow": "Clean planning",
    "demo.phase2Title": "Only verified places",
    "demo.phase2Text": "Vantastika filters the noise and makes planning clear, fast and safe.",
    "demo.phase3Eyebrow": "Complete roadtrip",
    "demo.phase3Title": "Your route, ready to go",
    "demo.phase3Text": "See the full route, key stops and a trip that feels done before you start the engine.",
    "demo.stepStart": "Start your trip",
    "demo.stepVerified": "Verified stops",
    "demo.stepArrive": "Arrive at destination",
    "demo.planSmart": "Plan smart",
    "demo.budget": "Budget",
    "demo.budgetUsed": "used",
    "demo.roadtripPreview": "Roadtrip preview",
    "service.title": "Service & Emergency",
    "service.workshop": "Workshop",
    "service.gasStation": "Gas Station",
    "service.water": "Water",
    "service.charging": "Charging",
    "service.waste": "Waste",
    "service.hospital": "Hospital",
    "service.police": "Police",
    "service.grocery": "Grocery",
    "service.emergency": "Emergency",
    "service.exitEmergency": "Exit SOS",
    "service.emergencyMode": "Emergency Mode",
    "service.callEmergency": "Call",
    "service.emergencyNote": "112 works in all EU countries",
    "service.locating": "Finding your location...",
    "service.locationNeeded": "Location needed",
    "service.enableLocation": "Allow location access to find nearby services",
    "service.retry": "Try again",
    "service.searching": "Searching nearby...",
    "service.noResults": "No services found nearby",
    "service.navigate": "Navigate",
    "service.open": "Open",
    "service.closed": "Closed",
    "service.fetchError": "Could not load services",
    "service.useGPS": "Use GPS",
    "service.searchPlaceholder": "Search anything... Tesla, Hymer, camping gear, pharmacy",
    "service.manual": "Manual",
    "service.searchResultsFor": "Results for",
    // Social proof & misc
    "social.places": "Places",
    "social.tripsPlanned": "Trips planned",
    "social.members": "Members",
    "social.reviews": "Reviews",
    "social.popularRoutes": "Popular routes from other travelers",
    "social.traveler": "Traveler",
    "social.stops": "stops",
    // Error boundary
    "error.somethingWrong": "Something went wrong",
    "error.autoRepairFailed": "We tried to repair automatically but it didn't work. Try reloading the page.",
    "error.tryAgain": "Try again",
    "error.reloadPage": "Reload page",
    // Accessibility
    "a11y.decreaseText": "Decrease text size",
    "a11y.increaseText": "Increase text size",
    "a11y.decreaseTooltip": "Smaller text",
    "a11y.increaseTooltip": "Larger text",
    // Navigation aria
    "aria.close": "Close",
    "aria.openVannie": "Open Vannie",
    "aria.previous": "Previous",
    "aria.next": "Next",
    "aria.openMenu": "Open menu",
    "aria.closeMenu": "Close menu",
    "aria.step": "Step",
    // Onboarding
    "onboarding.step": "Step {current} of {total}",
    "onboarding.back": "Back",
    "onboarding.skip": "Skip",
    "onboarding.planTrip": "Plan your trip",
    "onboarding.planTripDesc": "Tell us where you want to go and we'll help you build your trip — route, stops and budget in one place.",
    "onboarding.createFirstTrip": "Build my first trip",
    "onboarding.findPlaces": "Find places on the map",
    "onboarding.findPlacesDesc": "Campings, attractions and restaurants — add stops directly from the map into your trip.",
    "onboarding.searchCampings": "Explore the map",
    "onboarding.budgetChecklist": "Track budget and expenses",
    "onboarding.budgetChecklistDesc": "Log what you spend, scan receipts and keep track of your trip budget — all in one place.",
    "onboarding.createTrip": "Start my budget",
    "onboarding.askVannie": "Meet Vannie",
    "onboarding.askVannieDesc": "Your personal travel assistant. Get tips, route ideas and local rules — included for free.",
    "onboarding.doneStart": "Let's go!",
    // Compare page
    "compare.title": "Compare Campings",
    "compare.subtitle": "Choose up to {max} campings to compare side by side.",
    "compare.add": "Add",
    "compare.searchPlaceholder": "Search camping...",
    "compare.property": "Property",
    "compare.rating": "Rating",
    "compare.pricePerNight": "Price/night",
    "compare.free": "Free",
    "compare.type": "Type",
    "compare.addMore": "Add at least one more camping to compare.",
    "compare.selectCampings": "Select campings to compare",
    "compare.clickAdd": "Click \"Add\" above to get started.",
    "compare.electricity": "Electricity",
    "compare.freshWater": "Fresh water",
    "compare.shower": "Shower",
    "compare.pets": "Pets",
    "compare.greyWater": "Grey water",
    "compare.toiletCassette": "Toilet cassette",
    "compare.wc": "WC",
    "compare.openAllYear": "Open all year",
    "compare.familyFriendly": "Family friendly",
    "compare.largeMotorhomes": "Large motorhomes",
    "compare.nearWater": "Near water",
    "compare.spaWellness": "Spa/Wellness",
    // Friends nearby
    "friends.nearby": "Travel companions nearby",
    "friends.sharingOn": "Sharing on",
    "friends.sharingOff": "Sharing off",
    "friends.enableSharing": "Enable location sharing to see people you share trips with",
    "friends.onlyCollaborators": "Only those you collaborate with on a trip can see your position",
    "friends.noOneSharing": "No travel companions are sharing their location right now",
    "friends.anonymous": "Anonymous",
    "friends.away": "away",
    "friends.unknownDistance": "Unknown distance",
    // Spots
    "spot.loginToNavigate": "Log in to open navigation",
    "spot.navigateHere": "Navigate here",
    // Incident report
    "incident.title": "Report Incident",
    "incident.button": "Report Incident",
    "incident.stepOf": "Step {current} of {total}",
    "incident.saving": "Saving...",
    "incident.step1": "Type of incident",
    "incident.step2": "Time and location",
    "incident.step3": "Vehicle details",
    "incident.step4": "What happened",
    "incident.step5": "Other party",
    "incident.step6": "Summary & PDF",
    "incident.type.collision": "Collision",
    "incident.type.theft": "Theft",
    "incident.type.vandalism": "Vandalism",
    "incident.type.breakdown": "Breakdown",
    "incident.type.weather": "Weather damage",
    "incident.type.other": "Other",
    "incident.date": "Date",
    "incident.time": "Time",
    "incident.location": "Location",
    "incident.locationPlaceholder": "Address or place name",
    "incident.locationHint": "Be as specific as possible — street, city, country",
    "incident.vehicleType": "Vehicle type",
    "incident.vehicleTypePlaceholder": "e.g. Motorhome, Campervan, Car",
    "incident.registration": "Registration number",
    "incident.vehicleDesc": "Vehicle description",
    "incident.vehicleDescPlaceholder": "Color, make, model, year",
    "incident.whatHappened": "What happened?",
    "incident.descriptionPlaceholder": "Describe the incident clearly and calmly",
    "incident.additionalNotes": "Additional notes",
    "incident.notesPlaceholder": "Witnesses, weather, road conditions...",
    "incident.otherPartyHint": "Fill in if another party was involved. Skip if not applicable.",
    "incident.otherName": "Name",
    "incident.otherPhone": "Phone number",
    "incident.otherVehicle": "Vehicle",
    "incident.otherReg": "Registration number",
    "incident.otherInsurance": "Insurance company",
    "incident.incidentType": "Incident type",
    "incident.summary": "Report summary",
    "incident.downloadPdf": "Save & Download PDF",
    "incident.saveOnly": "Save without PDF",
    "incident.wizardTitle": "Damage Report",
    "incident.step0.title": "Important information",
    "incident.step0.heading": "This is a damage report — not an insurance claim",
    "incident.step0.body1": "With this tool you create a structured report that helps you collect correct information during an incident.",
    "incident.step0.body2": "You are responsible for reviewing and submitting the report to your insurance company.",
    "incident.step0.gdprTitle": "Data protection (GDPR)",
    "incident.step0.gdprBody1": "All information is securely stored in your account. The data is not shared with any third party automatically.",
    "incident.step0.gdprBody2": "You can delete an incident report and all associated data at any time.",
    "incident.step0.accept": "I understand — start",
    "incident.step1.title": "Type of incident",
    "incident.step2.title": "Date, time & location",
    "incident.step3.title": "Your vehicle",
    "incident.step4.title": "Other party's vehicle",
    "incident.step5.title": "Drivers & owners",
    "incident.step6.title": "Insurance",
    "incident.step7.title": "Damage description",
    "incident.step8.title": "Images",
    "incident.step9.title": "Summary",
    "incident.stepOf2": "Step {current} of {total}",
    "incident.selectCountry": "Select country...",
    "incident.searchCountry": "Search country...",
    "incident.searchInsurance": "Search or type manually...",
    "incident.regNum": "Registration number",
    "incident.regCountry": "Registration country",
    "incident.makeModel": "Make / Model",
    "incident.driverIsOwner": "Driver = Owner",
    "incident.yes": "Yes",
    "incident.no": "No",
    "incident.ownerName": "Owner's name",
    "incident.ownerPhone": "Owner's phone",
    "incident.ownerEmail": "Owner's email",
    "incident.otherDriverName": "Driver's name",
    "incident.otherDriverPhone": "Driver's phone",
    "incident.yourInsurance": "Your insurance",
    "incident.opponentInsurance": "Other party's insurance",
    "incident.insuranceCompany": "Insurance company",
    "incident.policyNum": "Policy number",
    "incident.policyNumHint": "Found on your insurance certificate",
    "incident.damageWhat": "What happened?",
    "incident.damageWhatPlaceholder": "Describe what occurred",
    "incident.damageHow": "How did it happen?",
    "incident.damageHowPlaceholder": "Describe circumstances, speed, visibility, etc.",
    "incident.damageParts": "Which parts were damaged?",
    "incident.damagePartsPlaceholder": "Which parts of the vehicle were damaged?",
    "incident.damageOther": "Other details",
    "incident.damageOtherPlaceholder": "Witnesses, weather, road conditions...",
    "incident.notesLabel": "Notes",
    "incident.notesPlaceholder2": "Additional notes",
    "incident.imagesOptional": "Images are optional but recommended",
    "incident.imagesHint": "Images can support the damage claims and facilitate processing by the insurance company.",
    "incident.takePhoto": "Take photo",
    "incident.uploadBtn": "Upload",
    "incident.uploading": "Uploading...",
    "incident.saveDraftFirst": "The report must be saved before you can upload images.",
    "incident.saveDraftBtn": "Save draft first",
    "incident.imagesSecure": "Images are securely stored in your account and are not shared automatically. You can delete them at any time. JPG and PNG only (max 5 MB).",
    "incident.notFilled": "Not filled in",
    "incident.notSpecified": "Not specified",
    "incident.notDescribed": "Not described",
    "incident.change": "Edit",
    "incident.summaryTitle": "Summary",
    "incident.disclaimerShort": "You are responsible for reviewing and submitting the report to your insurance company.",
    "incident.generatePdf": "Generating PDF...",
    "incident.savePdf": "Save & download PDF",
    "incident.saveNoPdf": "Save without PDF",
    "incident.reportSaved": "Incident report saved",
    "incident.couldNotSave": "Could not save report",
    "incident.invalidFile": "Invalid file",
    "incident.uploadFailed": "Upload failed",
    "incident.imagesAttached": "{count} image(s) attached",
    "incident.timeAndPlace": "Time & place",
    "incident.yourVehicle": "Your vehicle",
    "incident.opponent": "Other party",
    "incident.insurance": "Insurance",
    "incident.damages": "Damages",
    "incident.images": "Images",
    "incident.val.selectType": "Select incident type",
    "incident.val.dateRequired": "Date is required",
    "incident.val.locationRequired": "Location is required",
    "incident.val.regRequired": "Registration number is required",
    "incident.val.regCountryRequired": "Registration country is required",
    "incident.val.vehicleTypeRequired": "Vehicle type is required",
    "incident.val.ownerNameRequired": "Owner's name is required",
    "incident.val.describeWhat": "Describe what happened",
    "incident.val.describeHow": "Describe how it happened",
    "incident.val.typeMissing": "Incident type missing",
    "incident.val.regMissing": "Registration number missing",
    "incident.val.damageMissing": "Damage description missing",
    "incident.opponentVehicle": "Other party's vehicle",
    "incident.yourDriver": "Your details",
    "incident.opponentDriver": "Other party's details",
    "incident.pdf.title": "Damage Report",
    "incident.pdf.disclaimer": "This document is a damage report and not an official insurance claim. You are responsible for reviewing and submitting the report to your insurance company.",
    "incident.pdf.generated": "Generated",
    "incident.pdf.trip": "Trip",
    "incident.pdf.type": "Type",
    "incident.pdf.date": "Date",
    "incident.pdf.time": "Time",
    "incident.pdf.location": "Location",
    "incident.pdf.vehicleType": "Vehicle type",
    "incident.pdf.regNum": "Registration number",
    "incident.pdf.regCountry": "Registration country",
    "incident.pdf.makeModel": "Make / Model",
    "incident.pdf.driverIsOwner": "Driver = Owner",
    "incident.pdf.ownerName": "Owner's name",
    "incident.pdf.ownerPhone": "Owner's phone",
    "incident.pdf.ownerEmail": "Owner's email",
    "incident.pdf.driver": "Driver",
    "incident.pdf.phone": "Phone",
    "incident.pdf.insuranceCompany": "Insurance company",
    "incident.pdf.policyNum": "Policy number",
    "incident.pdf.whatHappened": "What happened",
    "incident.pdf.howHappened": "How it happened",
    "incident.pdf.damagedParts": "Damaged parts",
    "incident.pdf.otherDetails": "Other details",
    "incident.pdf.notes": "Notes",
    "incident.pdf.images": "Images",
    "incident.pdf.imagesNote": "{count} image(s) attached. Images are available in your Vantastika account.",
    "incident.pdf.footer": "This document is a damage report created with Vantastika and does not constitute an official insurance claim.",
    "incident.pdf.footerReview": "You are responsible for verifying the information and submitting it to your insurance company.",
    "incident.pdf.yourVehicle": "Your vehicle",
    "incident.pdf.opponentVehicle": "Other party's vehicle",
    "incident.pdf.damageDesc": "Damage description",
    "incident.pdf.incident": "Incident",
    "notFound.title": "Page not found",
    "notFound.description": "The page you were looking for doesn't seem to exist. It may have been moved or removed.",
    "notFound.home": "Go to home",
    "notFound.back": "Go back",
    "incident.generating": "Generating PDF...",
    "incident.saved": "Incident report saved",
    "incident.saveFailed": "Could not save report",
    "incident.back": "Back",
    "incident.next": "Next",
    "incident.cancel": "Cancel",
    "incident.pdfTitle": "Incident Report",
    "incident.pdfGenerated": "Generated",
    "incident.pdfDisclaimer": "This is a personal record. It is not an official insurance claim form.",
    "incident.drafts": "Draft reports",
    "incident.noDrafts": "No draft reports",
    "incident.continueDraft": "Continue",
    "incident.noReports": "No incident reports yet",
    "incident.deleted": "Report deleted",
    "incident.draft": "Draft",
    "incident.completed": "Completed",
    "incident.myReports": "My incident reports",
    "incident.myReportsSubtitle": "Your saved and draft reports",
    "incident.vtype.motorhome": "Motorhome",
    "incident.vtype.campervan": "Campervan",
    "incident.vtype.car": "Car",
    "incident.vtype.motorcycle": "Motorcycle",
    "incident.vtype.trailer": "Trailer",
    "incident.vtype.other": "Other",
    // Quick actions
    "quick.hello": "Hi {name}! 👋",
    "quick.whatToDo": "What would you like to do today?",
    "quick.continueTrip": "Continue trip",
    "quick.activeTrip": "Active trip",
    "quick.newTrip": "New trip",
    "quick.planNext": "Plan your next adventure",
    "quick.upcomingTrip": "Upcoming trip",
    "quick.findCamping": "Find camping",
    "quick.searchFilter": "Search & filter",
    "quick.favorites": "Favorites",
    "quick.saved": "{count} saved",
    "quick.community": "Community",
    "quick.tipsDiscussions": "Tips & discussions",
    // GDPR
    "gdpr.title": "We care about your privacy",
    "gdpr.description": "This site uses only essential and functional cookies (authentication, language preference, country setting, and cookie consent). No tracking, advertising, or third-party analytics cookies are used.",
    "gdpr.readPolicy": "Read our privacy policy",
    "gdpr.accept": "Got it",
    "gdpr.decline": "Got it",
    // Install
    "install.title": "Install Vantastika",
    "install.description": "Add to your home screen for quick access — works like an app!",
    "install.installButton": "Install app",
    "install.showHow": "Show how",
    "install.understood": "Got it",
    "install.iosSteps": "Follow these steps in Safari:",
    "install.iosTapShare": "Tap the",
    "install.iosShareLocation": "At the bottom of Safari",
    "install.iosAddHome": "Choose \"Add to Home Screen\"",
    "install.iosScrollDown": "Scroll down in the menu if needed",
    "install.iosTapAdd": "Tap \"Add\"",
    "install.androidSteps": "Follow these steps:",
    "install.androidTapMenu": "Tap the",
    "install.androidMenuLocation": "Top right of the browser",
    "install.androidInstallApp": "Choose \"Install app\"",
    "install.androidOrAddHome": "Or \"Add to home screen\"",
    "home.trustFree": "100% free",
    // Trip management
    "myTrips.title": "My Trips",
    "myTrips.loginTitle": "Log in to see your trips",
    "myTrips.loginDesc": "Your saved trips will appear here after logging in.",
    "myTrips.loginButton": "Log in",
    "myTrips.pausedStatus": "Paused",
    "myTrips.activeStatus": "Active trip",
    "myTrips.pausedSubtitle": "Your trip is paused",
    "myTrips.activeSubtitle": "You have an active trip",
    "myTrips.tripsCount": "{count} trips saved",
    "myTrips.tripCount": "{count} trip saved",
    "myTrips.newTrip": "New trip",
    "myTrips.resumeTrip": "Resume trip",
    "myTrips.continueTrip": "Continue trip",
    "myTrips.resume": "Resume",
    "myTrips.pause": "Pause",
    "myTrips.endTrip": "End trip",
    "myTrips.end": "End",
    "myTrips.progress": "{pct}% completed",
    "myTrips.stops": "{count} stops",
    "myTrips.nextStop": "Next stop:",
    "myTrips.openActiveTrip": "Open active trip",
    "myTrips.noTrips": "No saved trips yet",
    "myTrips.noTripsDesc": "Create your first trip in the planner and it will appear here.",
    "myTrips.planFirst": "Plan your first trip",
    "myTrips.upcomingTrips": "Upcoming trips",
    "myTrips.sharedWithMe": "Shared with me",
    "myTrips.invitedBy": "Invited by",
    "myTrips.completedTrips": "Completed trips",
    "myTrips.startTrip": "Start trip",
    "myTrips.budget": "Budget",
    "myTrips.checklist": "Checklist",
    "myTrips.stoppedPromptTitle": "You seem to have stopped",
    "myTrips.stoppedPromptDesc": "Do you want to continue or take a break?",
    "myTrips.arrivedAt": "Arrived at {name}!",
    "myTrips.arrivedDesc": "Destination reached — well done!",
    "myTrips.stopRemoved": "Stop removed",
    "myTrips.close": "Close",
    "myTrips.alreadyActiveTrip": "You already have an active trip",
    "myTrips.endActiveFirst": "End it first.",
    "myTrips.tripStarted": "Trip started!",
    "myTrips.tripStartedDesc": "GPS tracking and journal are now active.",
    "myTrips.tripPaused": "Trip paused",
    "myTrips.tripPausedDesc": "You can continue at any time.",
    "myTrips.tripResumed": "Trip resumed!",
    "myTrips.tripResumedDesc": "GPS tracking is active again.",
    "myTrips.tripEnded": "Trip ended!",
    "myTrips.tripEndedDesc": "Your trip has been saved.",
    "myTrips.tripReactivated": "Trip reactivated!",
    "myTrips.revertedToPlanned": "Reverted to planned",
    "myTrips.revertedToPlannedDesc": "The trip is back as planned. All data is intact.",
    "myTrips.missingStops": "Trip has no stops",
    "myTrips.missingStopsDesc": "Add at least one stop before starting.",
    "myTrips.missingTitle": "Trip has no title",
    "myTrips.missingTitleDesc": "Give the trip a name before starting.",
    "trip.stopAddedFromMap": "Stop added from map",
    "trip.stopPositionUpdated": "Stop position updated",
    "trip.routeOptimizedDesc": "Stops have been sorted for shortest driving distance.",
    "places.verificationThanks": "Thanks for the verification!",
    "places.verificationThanksDesc": "You help us keep the information accurate.",
    "friends.requestAccepted": "Friend request accepted!",
    "friends.requestSent": "Friend request sent!",
    "myTrips.checklistReminder": "Don't forget to go through the checklist before departure",
    // NextTripPrepCard
    "myTrips.today": "Today",
    "myTrips.tomorrow": "Tomorrow",
    "myTrips.inDays": "In {days} days",
    "myTrips.nextTrip": "Next trip",
    "myTrips.drafts": "Drafts",
    "myTrips.readyToStart": "Ready to start",
    "myTrips.continuePlanning": "Continue planning",
    "myTrips.editTrip": "Edit",
    "myTrips.openTrip": "Open trip",
    "myTrips.viewSummary": "View summary",
    "myTrips.duplicate": "Duplicate",
    "myTrips.archive": "Archive",
    "myTrips.activeTrips": "Active trip",
    "myTrips.showTrip": "Show trip",
    "myTrips.liveView": "Live view",
    "myTrips.details": "Details",
    "myTrips.revertToPlanned": "Revert to planned",
    "myTrips.reactivateTrip": "Reactivate trip",
    "myTrips.loadingLive": "Loading live view...",
    "myTrips.showCompleted": "Show completed trips",
    "myTrips.hideCompleted": "Hide completed trips",
    "nav.menuApp": "App",
    "nav.menuOther": "Other",
    "nav.installApp": "Install app",
    // TripToolsBar
    "tools.title": "Travel tools",
    "tools.checklist": "Checklist",
    "tools.budget": "Budget",
    "tools.incident": "Incident",
    // TripOverviewSummary
    "tripSummary.distance": "Distance",
    "tripSummary.driveTime": "Drive time",
    "tripSummary.days": "Days",
    "tripSummary.stops": "Stops",
    "tripSummary.fuel": "Fuel",
    "tripSummary.budget": "Budget",
    "tripSummary.route": "Route",
    "tripSummary.stop": "Stop {n}",
    // TripCountryGuideLink
    "tripGuideLink.title": "Guide for {country}",
    "tripGuideLink.camping": "Camping",
    "tripGuideLink.driving": "Driving",
    "tripGuideLink.safety": "Safety",
    // ArrivalDetector
    "arrival.continueToNext": "Continue to {name}",
    // BottomNav (keys defined at top of en block)
    // TravelStats
    "stats.trips": "Trips",
    "stats.stops": "Stops",
    "stats.nights": "Nights",
    "stats.countries": "Countries",
    "stats.reviews": "Reviews",
    "stats.friends": "Friends",
    // BadgeDisplay
    "badge.travel": "Travel",
    "badge.exploration": "Exploration",
    "badge.community": "Community",
    "badge.milestone": "Milestones",
    "badge.creative": "Creative",
    "badge.engagement": "Engagement",
    "badge.special": "Special",
    // TripChecklist
    "checklist.title": "Checklists",
    "checklist.subtitle": "Trip-linked and standalone checklists",
    "checklist.loginRequired": "Log in to see your checklists",
    "checklist.loginHint": "Your checklists will appear here after logging in.",
    "checklist.backToAll": "Back to all checklists",
    "checklist.trip": "Trip",
    "checklist.general": "General packlist",
    "checklist.generalSection": "Standalone checklists",
    "checklist.generalPacklist": "General packlist",
    "checklist.done": "done",
    "checklist.noChecklistYet": "No checklist yet — tap to create",
    "checklist.activeTrips": "Active & upcoming trips",
    "checklist.completedTrips": "Completed trips",
    "checklist.noTrips": "No trips yet. Create a trip to link a checklist.",
    "checklist.planTrip": "Plan trip",
    "checklist.allDone": "All done! 🎉",
    "checklist.remaining": "{count} items left",
    "checklist.progress": "{done} of {total} done",
    "checklist.addCustom": "Custom item",
    "checklist.addTemplate": "Add template",
    "checklist.reset": "Reset",
    "checklist.created": "Checklist created! ✅",
    "checklist.createFailed": "Could not create checklist",
    "checklist.resetDone": "Checklist has been reset",
    "checklist.resetFailed": "Could not reset",
    "checklist.itemAdded": "Item added!",
    "checklist.itemAddFailed": "Could not add item",
    "checklist.inputPlaceholder": "Describe what you want to add...",
    "checklist.add": "Add",
    "checklist.cancel": "Cancel",
    "checklist.noChecklist": "No checklist yet for {type}.",
    "checklist.selectTemplate": "Select template:",
    "checklist.or": "or",
    "checklist.aiPacklist": "AI-customized packlist",
    "checklist.standardChecklist": "Standard checklist",
    "checklist.aiAdapted": "AI list is adapted for {dest}",
    "checklist.andSeason": " and season",
    "checklist.aiCreated": "🤖 AI packlist created!",
    "checklist.allExist": "All items already exist in your list",
    "checklist.newItemsAdded": "{count} new items added!",
    "checklist.addExtraPacking": "Add extra packing:",
    // TripFloatingBar
    "float.navigate": "Navigate",
    "float.save": "Save",
    "float.saving": "Saving...",
    // Profile
    "profile.save": "Save changes",
    "profile.saving": "Saving...",
    // SharedTrip
    "shared.saveToMyTrips": "Save to my trips",
    "shared.saving": "Saving...",
    "shared.privacyNote": "Start and end points have been removed for privacy. Add your own start location.",
    "shared.addYourStart": "Add your start location",
    "shared.templateNote": "This trip is shared as inspiration. Destinations and stops are included — add your own start and end points.",
    // Edit sections
    "trip.editOutbound": "Outbound",
    "trip.editReturn": "Return journey",
    "trip.editMainInfo": "Trip details",
    "vehicle.motorhome": "Motorhome",
    "vehicle.campervan": "Campervan",
    "vehicle.caravan": "Caravan",
    "vehicle.car": "Car",
    "vehicle.motorcycle": "Motorcycle",
    "vehicle.selectType": "Vehicle type",
    "vehicle.nameLabel": "Vehicle name (optional)",
    "vehicle.namePlaceholder": "e.g. Fiat Ducato 2020",
    "vehicle.dimensions": "Vehicle dimensions",
    "vehicle.dimensionsExplain": "Used for smarter route planning — avoid low bridges, narrow passages and weight restrictions.",
    "vehicle.height": "Height",
    "vehicle.width": "Width",
    "vehicle.length": "Length",
    "vehicle.weight": "Weight",
    "vehicle.disclaimer": "Route warnings based on vehicle dimensions are advisory guidance. Always verify restrictions on-site. Full restriction data coverage is being expanded.",
    "vehicle.profileComplete": "Vehicle profile complete — ready for smart routing",
    "vehicle.save": "Save vehicle profile",
    "vehicle.saving": "Saving...",
    "vehicle.saved": "Vehicle profile saved!",
    "vehicle.saveFailed": "Could not save vehicle profile",
    "vehicle.profileTitle": "Vehicle Profile",
    "vehicle.profileSubtitle": "For smarter, safer route planning",
    "myTrips.viewRoute": "View Route",
    "myTrips.playTrip": "Play trip",
    "myTrips.needMoreStops": "Add at least 2 stops with coordinates to play the trip animation.",
    "tripReplay.stops": "stops",
    "tripReplay.days": "days",
    "tripReplay.completed": "Trip complete!",
    "tripReplay.replay": "Replay",
    "tripReplay.share": "Share",
    "routeOverview.title": "Route Overview",
    "routeOverview.totalDistance": "Total distance",
    "routeOverview.driveTime": "Drive time",
    "routeOverview.stops": "Stops",
    "routeOverview.overnights": "Overnights",
    "routeOverview.fuelEstimate": "Estimated fuel",
    "routeOverview.itinerary": "Itinerary",
    "routeOverview.nights": "nights",
    "routeOverview.start": "Start",
    "routeOverview.destination": "Destination",
    "routeOverview.visit": "Visit",
    "why.title": "Why choose Vantastika?",
    "why.subtitle": "Everything you need for your road trip — completely free, no card required.",
    "why.smartPlanning": "Smart trip planning",
    "why.smartPlanningDesc": "Create routes with stops, fuel calculation and time planning in one flow.",
    "why.aiAssistant": "AI travel assistant",
    "why.aiAssistantDesc": "Describe your dream trip in your own words — our AI builds the entire plan for you.",
    "why.campingsEurope": "Campings across Europe",
    "why.campingsEuropeDesc": "Find, compare and filter campsites and pitches in 21 countries.",
    "why.travelGuides": "Inspiring travel guides",
    "why.travelGuidesDesc": "Ready-made themed routes with history, stops and tips — ready to drive.",
    "why.budget": "Budget & expenses",
    "why.budgetDesc": "Track costs, fuel and daily budget during your trip.",
    "why.shareFriends": "Share with friends",
    "why.shareFriendsDesc": "Invite travel companions and plan together in real time.",
    "why.checklists": "Smart checklists",
    "why.checklistsDesc": "Custom packing lists based on your vehicle type and trip.",
    "why.sos": "SOS & safety kit",
    "why.sosDesc": "Emergency sharing, incident reports and quick access to help numbers.",
    "why.badges": "Levels & badges",
    "why.badgesDesc": "Collect achievements, climb levels and challenge yourself as a traveler.",
    "why.cta": "Create free account",
    "why.ctaSub": "No card needed · Ready in 30 seconds",
    // Games module
    "games.title": "Roadtrip Games",
    "games.subtitle": "Make the trip more fun 🎮",
    "games.played": "Played",
    "games.totalPoints": "Total points",
    "games.challengeFriend": "Challenge a friend",
    "games.selectCountry": "Select country",
    "games.loginPrompt": "Log in to save game history",
    "games.loginPromptSub": "Your scores, favorites and leaderboard position are saved",
    "games.onTrip": "Trip in progress – games adapted for {country}",
    "games.duringTrip": "During the trip",
    "games.duringTripSub": "Perfect for passengers – {n} game types",
    "games.atStop": "At stops & breaks",
    "games.atStopSub": "When you pause or explore – {n} game types",
    "games.camping": "Camping & evening games",
    "games.campingSub": "Longer games for evenings and camping life – {n} game types",
    "games.tabs.games": "Games",
    "games.tabs.community": "Community",
    "games.tabs.challenges": "Challenges",
    "games.tabs.badges": "Badges",
    "games.tabs.history": "History",
    "games.favorites": "Favorite games",
    "games.favoritesEmpty": "Heart a game to save it here",
    "games.recommended": "Recommended",
    "game.quiz.name": "Roadtrip Quiz",
    "game.quiz.desc": "Test your knowledge about countries, roads and camping",
    "game.bingo.name": "Roadtrip Bingo",
    "game.bingo.desc": "Spot things along the road and mark your bingo card",
    "game.guess_place.name": "Guess the Place",
    "game.guess_place.desc": "Can you guess which place we're describing?",
    "game.would_you_rather.name": "Would You Rather",
    "game.would_you_rather.desc": "Choose between tricky travel dilemmas",
    "game.trivia_battle.name": "Trivia Battle",
    "game.trivia_battle.desc": "Compete against other travelers in real time",
    "game.photo_challenge.name": "Photo Challenge",
    "game.photo_challenge.desc": "Creative photo tasks at your stop",
    "game.story.name": "Story Game",
    "game.story.desc": "Create a travel story together",
    "game.checkpoint.name": "Checkpoint",
    "game.checkpoint.desc": "Explore the area with fun missions",
    "game.evening_game.name": "Evening Game",
    "game.evening_game.desc": "Cozy group games for the evening",
    "game.scenario.name": "Scenario Game",
    "game.scenario.desc": "What would you do? Travel dilemmas",
    "game.intro.tagline.quiz": "Test your knowledge!",
    "game.intro.tagline.bingo": "Eyes on the road!",
    "game.intro.tagline.guess_place": "Can you guess it?",
    "game.intro.tagline.would_you_rather": "Make your choice!",
    "game.intro.tagline.trivia_battle": "Challenge accepted!",
    "game.intro.tagline.photo_challenge": "Capture the moment!",
    "game.intro.tagline.story": "Once upon a trip...",
    "game.intro.tagline.checkpoint": "Explore & discover!",
    "game.intro.tagline.evening_game": "Time to unwind!",
    "game.intro.tagline.scenario": "What would you do?",
    "game.difficulty": "Choose difficulty",
    "game.difficulty.easy": "Easy",
    "game.difficulty.medium": "Medium",
    "game.difficulty.hard": "Hard",
    "game.difficulty.easyDesc": "Perfect for beginners",
    "game.difficulty.mediumDesc": "A fair challenge",
    "game.difficulty.hardDesc": "For real experts",
    "game.question": "Question",
    "game.points": "points",
    "game.finished": "Game over!",
    "game.score.amazing": "Incredible! You're a master! 🏆",
    "game.score.great": "Fantastic! 🎉",
    "game.score.good": "Well done! 👍",
    "game.score.ok": "Not bad! Try again! 💪",
    "game.score.low": "Practice makes perfect! 💪",
    "game.bestStreak": "Best streak: {n} in a row!",
    "game.back": "Back",
    "game.paused": "Game paused",
    "game.exit.title": "Leave game?",
    "game.exit.description": "Your progress will be lost if you quit.",
    "game.exit.continue": "Continue playing",
    "game.exit.pause": "Pause game",
    "game.exit.quit": "Quit game",
    "game.story.finished": "Story complete!",
    "game.story.chapters": "chapters completed",
    "game.story.chapter": "Chapter",
    "game.story.new": "New story",
    "game.story.whatDo": "What do you do?",
    "game.scenario.finished": "Scenario complete!",
    "game.scenario.steps": "steps taken",
    "game.scenario.step": "Step",
    "game.scenario.new": "New scenario",
    "game.scenario.whatDo": "What do you do?",
    "game.scenario.intense": "Intense!",
    "game.newRound": "New round",
    "game.seeResults": "See results",
    "game.nextQuestion": "Next question",
    "game.playNow": "Play now",
    "game.players": "players",
    "game.minutes": "min",
    "game.safety.title": "Safety first!",
    "game.safety.desc": "This game is meant for passengers, not the driver. Confirm that you are a passenger.",
    "game.safety.confirm": "I am a passenger",
    "game.safety.later": "Play later",
    "game.version": "{country} version",
    "game.hero.title": "Make the trip fun",
    "game.hero.game_types": "game types",
    "game.hero.questions": "questions",
    "game.hero.countries": "countries",
    "game.challenge_friend": "Challenge a friend",
    "game.trip_active": "Trip in progress",
    "game.login_prompt": "Log in to save game history",
    "game.login_prompt_desc": "Your scores, favorites and leaderboard position are saved",
    "game.tab.games": "Games",
    "game.tab.challenges": "Challenges",
    "game.tab.community": "Community",
    "game.tab.collect": "Collect",
    "game.tab.history": "History",
    "game.section.on_the_road": "On the road",
    "game.section.at_stop": "At stops & rest",
    "game.section.camping": "Camping & evening",
    "game.favorites": "Favorite games",
    "game.no_favorites": "Heart a game to save it here",
    "game.wyr.allDone": "All questions done!",
    "game.wyr.questionsAnswered": "questions answered",
    "game.wyr.title": "Would You Rather",
    "game.wyr.wouldYouRather": "Would you rather...",
    "game.wyr.or": "or",
    "game.wyr.done": "Done!",
    "game.recommended.title": "Recommended for you",
    "game.weekly.title": "Weekly challenges",
    "game.weekly.done": "Done",
    "game.bingo.found": "found",
    "game.bingo.subtitle": "Spot things along the road",
    "game.bingo.spotted": "Spotted!",
    "game.bingo.instructions": "Tap things you see along the road. Bingo = row, column or diagonal!",
    "game.guess.finished": "Guess the Place done!",
    "game.guess.fewerClues": "Fewer clues = more points!",
    "game.guess.title": "Guess the Place",
    "game.guess.place": "Place",
    "game.guess.clues": "Clues",
    "game.guess.showNext": "Show next clue",
    "game.guess.placeholder": "Type your answer...",
    "game.guess.guess": "Guess",
    "game.guess.correct": "Correct!",
    "game.guess.wrong": "Wrong!",
    "game.guess.correctAnswer": "Correct answer",
    "game.guess.nextPlace": "Next place",
    "game.playAgain": "Play again",
    "game.evening.finished": "Evening game done!",
    "game.evening.wellDone": "Great job with the whole group!",
    "game.evening.round": "Round",
    "game.evening.done": "Done!",
    "game.evening.nextRound": "Next round",
    "game.evening.trivia": "Trivia",
    "game.evening.creative": "Creative",
    "game.evening.debate": "Debate",
    "game.evening.memory": "Memory",
    "game.evening.drawing": "Drawing",
    "game.checkpoint.finished": "Checkpoint done!",
    "game.checkpoint.challengesDone": "challenges completed",
    "game.checkpoint.newChallenges": "New challenges",
    "game.checkpoint.subtitle": "Explore the location with challenges",
    "game.checkpoint.finish": "Finish checkpoint",
    "game.safety.warning": "Traffic safety always comes first. Never drive and play at the same time.",
    "game.safety.cancel": "Cancel",
    "game.leaderboard.title": "Leaderboard – this week",
    "game.leaderboard.games": "games",
    "game.leaderboard.you": "You",
    "game.leaderboard.yourPosition": "Your position",
    "game.history.empty": "No games played yet",
    "game.history.emptyDesc": "Start your first game and see your history here!",
    "game.history.played": "Played",
    "game.history.gameTypes": "Game types",
    "game.history.countries": "Countries",
    "game.history.avgScore": "Average score",
    "game.history.inProgress": "In progress",
    "game.history.continue": "Continue playing",
    "game.badges.title": "Badges",
    "game.quiz.fast": "Fast",
    "game.quiz.perfect": "Perfect!",
    "game.audience.label": "Who's playing?",
    "game.audience.kids": "Kids",
    "game.audience.kids_desc": "Fun and easy games for children",
    "game.audience.family": "Family",
    "game.audience.family_desc": "Games for the whole family",
    "game.audience.adult": "Adults",
    "game.audience.adult_desc": "Games for adults",
    "game.audience.couple": "Couple",
    "game.audience.couple_desc": "Romantic games for two",
    "game.audience.friends": "Friends",
    "game.audience.friends_desc": "Fun and competitive with friends",
    "game.daily.title": "Daily & weekly challenges",
    "game.daily.today": "Today",
    "game.daily.this_week": "This week",
    "game.daily.this_month": "This month",
    "game.daily.quiz_challenge": "Daily quiz challenge",
    "game.daily.quiz_challenge_desc": "Score 7+ on today's quiz",
    "game.daily.bingo_quick": "Quick bingo",
    "game.daily.bingo_quick_desc": "Find 5 things on the bingo board",
    "game.daily.guess_master": "Guess master",
    "game.daily.guess_master_desc": "Guess 3 places correctly",
    "game.daily.scenario_star": "Scenario star",
    "game.daily.scenario_star_desc": "Complete a scenario round",
    "game.daily.photo_snap": "Photo snap",
    "game.daily.photo_snap_desc": "Complete 3 photo challenges",
    "game.daily.story_teller": "Storyteller",
    "game.daily.story_teller_desc": "Complete a story adventure",
    "game.daily.trivia_duel": "Trivia duel",
    "game.daily.trivia_duel_desc": "Score 5+ in trivia battle",
    "game.weekly.quiz_master": "Weekly quiz master",
    "game.weekly.quiz_master_desc": "Score 9+ on any quiz this week",
    "game.weekly.bingo_champion": "Bingo champion",
    "game.weekly.bingo_champion_desc": "Complete a full bingo board",
    "game.weekly.explorer": "Weekly explorer",
    "game.weekly.explorer_desc": "Complete 4 checkpoint challenges",
    "game.weekly.photo_pro": "Photo pro",
    "game.weekly.photo_pro_desc": "Complete 6 photo challenges",
    "game.weekly.evening_host": "Evening host",
    "game.weekly.evening_host_desc": "Host an evening game session",
    "game.monthly.country_champion": "Country champion",
    "game.monthly.country_champion_desc": "Perfect score on a country quiz",
    "game.monthly.all_rounder": "All-rounder",
    "game.monthly.all_rounder_desc": "Play 5 different game types",
    "game.monthly.road_legend": "Road legend",
    "game.monthly.road_legend_desc": "Complete 3 story adventures",
    "game.season.spring": "Spring games",
    "game.season.summer": "Summer games",
    "game.season.autumn": "Autumn games",
    "game.season.winter": "Winter games",
    "game.seasonal.limited": "Seasonal",
    "game.seasonal.spring_awakening": "Spring awakening quiz",
    "game.seasonal.spring_awakening_desc": "Test your spring knowledge",
    "game.seasonal.easter_trip": "Easter roadtrip bingo",
    "game.seasonal.easter_trip_desc": "Spot Easter on the road",
    "game.seasonal.spring_photo": "Spring photo challenge",
    "game.seasonal.spring_photo_desc": "Capture the beauty of spring",
    "game.seasonal.summer_roadtrip": "Summer roadtrip quiz",
    "game.seasonal.summer_roadtrip_desc": "Sun, beaches and summer roads",
    "game.seasonal.beach_bingo": "Beach & coast bingo",
    "game.seasonal.beach_bingo_desc": "Find summer things along the coast",
    "game.seasonal.midsummer": "Midsummer challenge",
    "game.seasonal.midsummer_desc": "Celebrate midsummer with challenges",
    "game.seasonal.camping_nights": "Summer camping nights",
    "game.seasonal.camping_nights_desc": "Perfect for warm camping evenings",
    "game.seasonal.autumn_colors": "Autumn colors photo",
    "game.seasonal.autumn_colors_desc": "Capture autumn's golden hues",
    "game.seasonal.harvest_quiz": "Harvest season quiz",
    "game.seasonal.harvest_quiz_desc": "Quiz about autumn and harvest",
    "game.seasonal.fog_mystery": "Foggy mystery story",
    "game.seasonal.fog_mystery_desc": "A mysterious autumn adventure",
    "game.seasonal.winter_roads": "Winter roads quiz",
    "game.seasonal.winter_roads_desc": "Test your winter driving knowledge",
    "game.seasonal.christmas_markets": "Christmas market explorer",
    "game.seasonal.christmas_markets_desc": "Guess the famous Christmas markets",
    "game.seasonal.cozy_evening": "Cozy winter evening",
    "game.seasonal.cozy_evening_desc": "Perfect game for cold evenings",
    "game.seasonal.northern_lights": "Northern lights story",
    "game.seasonal.northern_lights_desc": "An adventure under the aurora",
    "game.level.label": "Level",
    "game.level.beginner": "Beginner",
    "game.level.explorer": "Explorer",
    "game.level.adventurer": "Adventurer",
    "game.level.traveler": "Traveler",
    "game.level.road_master": "Road Master",
    "game.level.pro": "Roadtrip Pro",
    "game.level.navigator": "Navigator",
    "game.level.euro_champ": "Euro Champion",
    "game.level.legendary": "Legendary",
    "game.level.legend": "Roadtrip Legend",
    "game.level.next": "Next",
    "game.level.games_played": "played",
    "game.collectibles.title": "Country collectibles",
    "game.collectibles.countries": "countries",
    "country.sweden": "Sweden",
    "country.norway": "Norway",
    "country.germany": "Germany",
    "country.italy": "Italy",
    "country.france": "France",
    "country.spain": "Spain",
    "game.region.norrland": "Norrland",
    "game.region.svealand": "Svealand",
    "game.region.gotaland": "Götaland",
    "game.region.fjords": "Fjords",
    "game.region.nordnorge": "Northern Norway",
    "game.region.sorlandet": "Southern Norway",
    "game.region.bayern": "Bavaria",
    "game.region.norddeutschland": "Northern Germany",
    "game.region.rheinland": "Rhineland",
    "game.region.norditalien": "Northern Italy",
    "game.region.toscana": "Tuscany",
    "game.region.syditalien": "Southern Italy",
    "game.region.ildefrance": "Île-de-France",
    "game.region.provence": "Provence",
    "game.region.bretagne": "Brittany",
    "game.region.andalucia": "Andalusia",
    "game.region.catalonia": "Catalonia",
    "game.region.nordspanien": "Northern Spain",
    // Nav extras
    "nav.bottom.budget": "Budget",
    "nav.installTitle": "Install Vantastika",
    "nav.installIOS1": "Tap the Share button (bottom of Safari)",
    "nav.installIOS2": "Choose \"Add to Home Screen\"",
    "nav.installIOS3": "Tap \"Add\"",
    "nav.installAndroid1": "Tap the menu icon (⋮ top right)",
    "nav.installAndroid2": "Choose \"Install app\" or \"Add to Home Screen\"",
    "nav.installUnderstood": "Got it",
    // Trip status confirm
    "tripStatus.startTitle": "Do you want to start the trip now?",
    "tripStatus.startDesc": "The trip will be set as ongoing and GPS tracking is activated.",
    "tripStatus.startConfirm": "Yes, start",
    "tripStatus.endTitle": "Do you really want to end the trip?",
    "tripStatus.endDesc": "You can resume it later if you change your mind.",
    "tripStatus.endConfirm": "Yes, end",
    "tripStatus.pauseTitle": "Do you want to pause the trip?",
    "tripStatus.pauseDesc": "You can continue the trip anytime.",
    "tripStatus.pauseConfirm": "Yes, pause",
    "tripStatus.resumeTitle": "Do you want to resume the trip?",
    "tripStatus.resumeDesc": "The trip continues where you paused.",
    "tripStatus.resumeConfirm": "Yes, continue",
    "tripStatus.revertTitle": "Revert to planned trip?",
    "tripStatus.revertDesc": "All trip information is kept — only the status changes back to planned.",
    "tripStatus.revertConfirm": "Yes, revert",
    "tripStatus.reactivateTitle": "Resume the ended trip?",
    "tripStatus.reactivateDesc": "The trip will be set as ongoing again. All data is kept.",
    "tripStatus.reactivateConfirm": "Yes, resume",
    "tripStatus.trip": "Trip",
    "tripStatus.cancel": "Cancel",
    // AddToTripDialog
    "addToTrip.title": "Add to trip",
    "addToTrip.loginPrompt": "Log in to add places to your trips.",
    "addToTrip.login": "Log in",
    "addToTrip.createAccount": "Create account",
    "addToTrip.howUsePlace": "How do you want to use this place?",
    "addToTrip.chooseTrip": "Choose trip",
    "addToTrip.startPoint": "Start point",
    "addToTrip.startPointDesc": "The trip starts here",
    "addToTrip.destination": "Destination",
    "addToTrip.destinationDesc": "I want to travel here",
    "addToTrip.stop": "Stop along the way",
    "addToTrip.stopDesc": "A stop during the trip",
    "addToTrip.noTrips": "You have no planned trips yet",
    "addToTrip.noTripsDesc": "Create a new trip and add the place directly.",
    "addToTrip.stops": "stops",
    "addToTrip.unnamed": "Unnamed trip",
    "addToTrip.addedAs": "has been added as",
    "addToTrip.roleStart": "start point",
    "addToTrip.roleDestination": "destination",
    "addToTrip.roleStop": "stop",
    "addToTrip.couldNotAdd": "Could not add the place",
    "addToTrip.createNew": "Create new trip with this place",
    "optimize.bannerTitle": "AI found a shorter route",
    "optimize.bannerDesc": "Save approx.",
    "optimize.bannerByReordering": "by reordering your stops.",
    "optimize.viewAlternatives": "View alternatives",
    "optimize.dismiss": "Not now",
    "optimize.fuelSaved": "in fuel",
    // CampingPreviewDialog
    "camping.notFound": "Place could not be found.",
    "camping.approxPrice": "approx.",
    "camping.perNight": "/ night (approximate)",
    "camping.free": "Free",
    "camping.typeStellplatz": "Stellplatz",
    "camping.typeCamping": "Camping",
    "camping.amenities": "Amenities",
    "camping.checkIn": "In",
    "camping.checkOut": "Out",
    "camping.visitWebsite": "Visit website",
    "camping.addToTrip": "Add to trip",
    "camping.navigate": "Navigate",
    "camping.viewPage": "View page",
    "camping.amenityElectricity": "Electricity",
    "camping.amenityFreshWater": "Fresh water",
    "camping.amenityGreyWater": "Grey water",
    "camping.amenityToilet": "Toilet",
    "camping.amenityShower": "Shower",
    "camping.amenityWC": "WC",
    "camping.amenityPets": "Pets",
    "camping.amenityAllYear": "Open all year",
    "camping.amenityFamily": "Family-friendly",
    "camping.amenityLarge": "Large motorhomes",
    "camping.amenityWater": "Near water",
    "camping.amenitySpa": "Spa",
    "camping.amenityWifi": "WiFi",
    "camping.amenityLaundry": "Laundry",
    "camping.amenityRestaurant": "Restaurant",
    // TripShareDialog
    "share.inviteToSharedTrip": "Invite to shared trip",
    "share.inviteDesc": "The recipient joins the same trip. All changes sync in real-time — budget, checklist, stops and notes.",
    "share.inviteViaApp": "Invite via app...",
    "share.copyLink": "Copy invite link",
    "share.copied": "Copied!",
    "share.shareAsTip": "Share as travel tip",
    "share.tipDesc": "The recipient gets their own copy as inspiration. Changes don't affect your trip.",
    "share.createAiGuide": "Create AI travel guide",
    "share.generatingAi": "Generating AI travel guide... ✨",
    "share.aiGuideCreated": "AI travel guide created! ✨",
    "share.couldNotCreateAi": "Could not create AI guide",
    "share.aiGuideActive": "AI travel guide active",
    "share.tabShare": "Share",
    "share.tabTeam": "Travel group",
    "share.tabComments": "Comments",
    "share.tabActivity": "Activity",
    "share.inviteByUsername": "Invite by username or share an invite link:",
    "share.usernamePlaceholder": "Username to invite...",
    "share.inviteSent": "Invite sent!",
    "share.userNotFound": "User not found. Enter exact username.",
    "share.cannotInviteSelf": "You cannot invite yourself.",
    "share.alreadyInvited": "User is already invited.",
    "share.couldNotInvite": "Could not invite",
    "share.noMembers": "No group members yet. Invite someone to plan together!",
    "share.editor": "Editor",
    "share.removedFromGroup": "Removed from travel group",
    "share.noComments": "No comments yet.",
    "share.unknown": "Unknown",
    "share.commentPlaceholder": "Write a comment...",
    "share.send": "Send",
    "share.noActivity": "No activity yet.",
    "share.actionInvited": "invited",
    "share.actionCommented": "commented",
    "share.actionAddedStop": "added stop:",
    "share.actionUpdatedStop": "updated stop:",
    "share.actionRemovedStop": "removed stop:",
    "share.actionUpdatedTrip": "updated the trip",
    // SignupPage
    "signup.guestVsMember": "Guest vs Member",
    "signup.seeWhatYouGet": "See what you get as a member",
    "signup.freeNoCard": "100% free — no credit card needed",
    "signup.premiumSoon": "Premium coming soon with even more features",
    "signup.username": "Username",
    "signup.usernamePlaceholder": "e.g. vanansen",
    "signup.displayName": "Display name",
    "signup.displayNamePlaceholder": "Your name shown to others",
    "signup.confirmPassword": "Confirm password",
    "signup.passwordPlaceholder": "At least 8 characters, letter + number",
    "signup.passwordsNoMatch": "Passwords don't match",
    "signup.selectCountry": "Select your country",
    "signup.whatVehicle": "What do you travel with?",
    "signup.ageWarning": "You must be at least 16 years old to create an account on Vantastika. If you are under 18, a guardian's consent is required.",
    "signup.termsConsent": "I accept the",
    "signup.termsLink": "terms of service",
    "signup.andThe": "and the",
    "signup.integrationTermsLink": "integration terms",
    "signup.ageConfirm": "and confirm that I am at least 16 years old.",
    "signup.gdprConsent": "I have read and accept the",
    "signup.privacyLink": "privacy policy (GDPR)",
    "signup.or": "or",
    "signup.verificationFailed": "Verification failed. Try again.",
    "signup.captchaFailed": "CAPTCHA verification failed. Try again.",
    "signup.tooManyAttempts": "Too many attempts. Wait a moment.",
    "signup.acceptTerms": "You must accept the terms",
    "signup.selectCountryError": "Select your country",
    "signup.enterDisplayName": "Enter a display name",
    "signup.enterUsername": "Enter a username",
    "signup.accountCreated": "Account created!",
    "signup.welcomeMsg": "Welcome to Vantastika!",
    "signup.checkDetails": "Check your details and try again.",
    "signup.showPassword": "Show password",
    "signup.hidePassword": "Hide password",
    // Signup vehicle options
    "signupVehicle.motorhome": "🚐 Motorhome",
    "signupVehicle.motorhomeDesc": "I travel by motorhome",
    "signupVehicle.car": "🚗 Car",
    "signupVehicle.carDesc": "I travel by car",
    "signupVehicle.motorcycle": "🏍️ Motorcycle",
    "signupVehicle.motorcycleDesc": "I ride a motorcycle",
    "signupVehicle.campervan": "🚌 Campervan",
    "signupVehicle.campervanDesc": "I have a campervan",
    "signupVehicle.rental": "🔑 Rental vehicle",
    "signupVehicle.rentalDesc": "I rent when needed",
    "signupVehicle.other": "🌍 Other / Unsure",
    "signupVehicle.otherDesc": "Flexible traveler",
    // ProfilePage
    "profile.yourProfile": "Your profile",
    "profile.since": "Since",
    "profile.quickLinks": "Quick links",
    "profile.myTrips": "My trips",
    "profile.checklists": "Checklists",
    "profile.savedPlaces": "Saved places",
    "profile.planning": "Planning",
    "profile.saved": "Saved",
    "vehicle.projects": "Projects",
    "vehicle.viewAll": "View all",
    "vehicle.noProjects": "No projects linked to {name} yet.",
    "vehicle.createProject": "Create project",
    "wishlist.startExploring": "Start exploring",
    "profile.notifications": "Notifications",
    "profile.travelOverview": "Travel overview",
    "profile.levelAndProfile": "Travel profile & level",
    "profile.progress": "Progress",
    "profile.total": "Total",
    "profile.badges": "Badges",
    "profile.challenges": "Challenges",
    "profile.activeChallenges": "Active challenges",
    "profile.viewAllProgress": "View all progress",
    "profile.incidentReports": "Incident reports",
    "profile.newReport": "New report",
    "profile.social": "Social",
    "profile.inbox": "Inbox",
    "profile.mySubmissions": "My submissions",
    "profile.accountSettings": "Account & settings",
    "profile.accountSettingsDesc": "Profile, theme and password",
    "profile.email": "Email",
    "profile.displayName": "Display name",
    "profile.displayNamePlaceholder": "Your name",
    "profile.username": "Username",
    "profile.usernamePlaceholder": "@username",
    "profile.country": "Country",
    "profile.countryPlaceholder": "Sweden",
    "profile.appearance": "Appearance",
    "profile.themeLight": "Light",
    "profile.themeDark": "Dark",
    "profile.themeSystem": "System",
    "profile.themeLightDesc": "Always light theme",
    "profile.themeDarkDesc": "Always dark theme",
    "profile.themeSystemDesc": "Follows device",
    "profile.changePassword": "Change password",
    "profile.currentPassword": "Current password",
    "profile.newPassword": "New password",
    "profile.newPasswordPlaceholder": "At least 8 characters",
    "profile.confirmPasswordLabel": "Confirm",
    "profile.changePasswordBtn": "Change password",
    "profile.changingPassword": "Changing...",
    "profile.wrongCurrentPassword": "Wrong current password",
    "profile.passwordChanged": "Password changed!",
    "profile.passwordMinLength": "Password must be at least 8 characters",
    "profile.passwordsMismatch": "Passwords don't match",
    "profile.deleteAccount": "Delete account",
    "profile.permanentDeletion": "Permanent deletion",
    "profile.deleteAccountDesc": "When deleting your account, the following is permanently removed: profile, trips, stops, favorites, checklists, forum posts, reviews, images, friend requests, messages, incident reports and notifications.",
    "profile.deleteAccountNote": "What may remain: Anonymized audit logs (not linked to your account), the shared badges catalog, and any data we are legally required to keep (e.g. accounting laws, max 7 years).",
    "profile.deleteMyAccount": "Delete my account",
    "profile.deleteConfirmTitle": "Delete account permanently",
    "profile.deleteConfirmDesc": "Are you sure? The following is permanently deleted:",
    "profile.deleteConfirmList": "• Profile, settings and avatar\n• Trips, stops, budget and checklists\n• Favorites, collections and saved places\n• Incident reports and uploaded images\n• Forum posts, comments and likes\n• Friend requests and messages\n• Notifications and place verifications",
    "profile.deleteConfirmNote": "Anonymized audit logs and the shared badges catalog are not affected. Data we are legally required to keep may remain up to 7 years.",
    "profile.cannotUndo": "Cannot be undone.",
    "profile.deleting": "Deleting...",
    "profile.yesDeleteAccount": "Yes, delete my account",
    "profile.accountDeleted": "Account has been deleted",
    "profile.accountDeletedDesc": "All your data has been removed.",
    "profile.couldNotDelete": "Could not delete account",
    "profile.profileSaved": "Profile saved!",
    "profile.couldNotSave": "Could not save",
    "profile.profilePicUpdated": "Profile picture updated!",
    "profile.uploading": "Uploading...",
    "profile.fileTooLarge": "File is too large",
    "profile.fileTooLargeDesc": "Max 2 MB",
    "profile.imageRejected": "Image rejected",
    "profile.myVehicle": "My vehicle",
    "profile.enterCurrentPassword": "Enter your current password",
    "profile.couldNotChangePassword": "Could not change password",
    "profile.vehicleMotorhome": "Motorhome",
    "profile.vehicleCar": "Car",
    "profile.vehicleMotorcycle": "MC",
    "profile.vehicleCampervan": "Campervan",
    "profile.vehicleRental": "Rental",
    "profile.vehicleOther": "Other",
    "profile.activeNow": "Active now",
    "profile.today": "Today!",
    "profile.inDays": "In {n} days",
    "profile.newNotification": "new notification",
    "profile.newNotifications": "new notifications",
    "profile.showAll": "Show all",
    "profile.inspection": "Inspection",
    "profile.gasInspection": "Gas inspection",
    "profile.service": "Service",
    "profile.expired": "Expired!",
    "profile.noDateRegistered": "No date registered",
    "profile.noServiceRegistered": "No service registered",
    "profile.missing": "Missing",
    "profile.insurance": "Insurance",
    "profile.noInsuranceRegistered": "No insurance registered",
    "profile.regNumberMissing": "Registration number missing",
    "profile.addForSmartData": "Add for smart vehicle data",
    "profile.addRegNumber": "Add reg. no.",
    "profile.statusGood": "Good",
    "profile.statusSoon": "Soon",
    "profile.statusAction": "Action",
    "profile.edit": "Edit",
    "profile.addService": "Add service",
    "profile.addVehicleDesc": "Connect your vehicle for smart planning, reminders and personalised content",
    "profile.addVehicleBtn": "Add vehicle",
    "profile.validUntil": "Valid until {date}",
    // Scan receipt
    "scan.title": "Smart receipt scan",
    "scan.takePhoto": "Take photo",
    "scan.upload": "Upload",
    "scan.photoOrUpload": "Take a photo or upload a receipt",
    "scan.aiAnalyzes": "AI analyzes the receipt line by line",
    "scan.analyzing": "Analyzing receipt...",
    "scan.analyzingDesc": "AI reads items, amounts and categories",
    "scan.fileTooLarge": "File is too large",
    "scan.fileTooLargeMax": "Max 15 MB",
    "scan.analysisFailed": "Analysis failed",
    "scan.couldNotAnalyze": "Could not analyze the receipt",
    "scan.amount": "Amount",
    "scan.currency": "Currency",
    "scan.possibleAmounts": "Possible amounts:",
    "scan.converted": "Converted:",
    "scan.date": "Date",
    "scan.titleLabel": "Title",
    "scan.itemsPerCategory": "Items per category",
    "scan.mixedCategories": "Mixed categories",
    "scan.howToSave": "How do you want to save?",
    "scan.oneExpense": "One expense",
    "scan.splitUp": "Split up",
    "scan.splitDesc": "Creates {n} separate expenses — one per category.",
    "scan.mainCategory": "Main category",
    "scan.linkToBudget": "Link to budget",
    "scan.noBudgetSelected": "No budget selected",
    "scan.noBudget": "No budget",
    "scan.linkToTrip": "Link to trip",
    "scan.noTripSelected": "No trip selected",
    "scan.noTrip": "No trip",
    "scan.note": "Note",
    "scan.hideRawText": "Hide raw receipt text",
    "scan.showRawText": "Show raw receipt text",
    "scan.cancel": "Cancel",
    "scan.saving": "Saving...",
    "scan.saveExpense": "Save expense",
    "scan.saveExpenses": "Save {n} expenses",
    "scan.expenseSaved": "Expense saved from receipt ✓",
    "scan.expensesSaved": "{n} expenses saved from receipt ✓",
    "scan.fillAmountAndTitle": "Enter amount and title",
    "scan.couldNotSave": "Could not save",
    "scan.confidenceHigh": "High confidence",
    "scan.confidenceMedium": "Medium confidence — review fields",
    "scan.confidenceLow": "Low confidence — check all fields",
    "scan.dotMedium": "Medium confidence",
    "scan.dotLow": "Uncertain — check",
    "scan.close": "Close",
    "scan.paid": "Paid:",
    "scan.vat": "VAT:",
    "scan.receipt": "Receipt",
    // Camera permissions
    "camera.prePermissionHint": "Camera access is needed to scan receipts",
    "camera.deniedError": "Camera access was denied. Enable it in your browser or phone settings.",
    "camera.notFoundError": "No camera found on this device.",
    "camera.inUseError": "The camera is being used by another app.",
    "camera.genericError": "An unexpected error occurred while accessing the camera.",
    "camera.openSettings": "Show instructions",
    "camera.androidSettingsHint": "Go to Settings → Apps → Your browser → Permissions → Camera",
    "camera.iosSettingsHint": "Go to Settings → Privacy & Security → Camera → Your browser",
    "camera.desktopSettingsHint": "Click the lock icon in the address bar and enable camera access.",
    // Location permissions
    "location.detecting": "Getting your location…",
    "location.unsupportedTitle": "Location not supported",
    "location.unsupportedDesc": "Your device or browser does not support location sharing. Try a different browser.",
    "location.deniedTitle": "Location access denied",
    "location.deniedDesc": "You have denied location access. To use this feature, enable location sharing in your browser or phone settings.",
    "location.tryAgain": "Try again",
    "location.openSettings": "Show instructions",
    "location.androidSettingsHint": "Go to Settings → Apps → Your browser → Permissions → Location",
    "location.iosSettingsHint": "Go to Settings → Privacy & Security → Location Services → Your browser",
    "location.desktopSettingsHint": "Click the lock icon in the address bar and enable location access.",
    "location.promptDesc": "We need your location to show relevant results nearby.",
    "location.allowAccess": "Allow location access",
    "location.fetchErrorTitle": "Could not get location",
    "location.timeoutDesc": "It took too long to get your location. Make sure GPS is enabled.",
    "location.unavailableDesc": "Your location could not be determined right now. Try again in a moment.",
    "location.errorTitle": "Location error",
    "location.errorDesc": "Something went wrong while getting your location.",
    // Season greetings
    "season.spring": "Spring",
    "season.springGreeting": "Spring is here — time to plan!",
    "season.summer": "Summer",
    "season.summerGreeting": "Summer awaits — which road will you take?",
    "season.autumn": "Autumn",
    "season.autumnGreeting": "Autumn colors are calling!",
    "season.winter": "Winter",
    "season.winterGreeting": "Winter is magical — plan a snow trip!",
    "map.searchPlaceholder": "Search city, camping, address...",
    "map.searching": "Searching...",
    "map.searchingSuggestions": "Searching for suggestions…",
    "map.noResults": "No results found",
    "map.noResultsHint": "Try a city, camping, address or place name",
    "map.exploreEurope": "Explore Europe",
    "map.exploreDesc": "Search for a place to discover campings, rest stops and attractions",
    "map.exploreDescLong": "Search for a place to discover campings, restaurants, attractions and more",
    "map.resultsNear": "Results near",
    "map.noCampingsFound": "No campings or rest stops found nearby.",
    "map.mapErrorBanner": "The map could not be loaded. You can still search and explore places in list view.",
    "map.nearYou": "Places near you",
    "map.places": "places",
    "map.placesNearYou": "places near you",
    "map.campingsNearby": "Campings & rest stops nearby",
    "map.explore": "Explore",
    "map.closePanel": "Close panel",
    "map.showResults": "Show results",
    "map.navigate": "Navigate",
    "map.addToTrip": "Add to trip",
    "map.open": "Open",
    "map.closed": "Closed",
    "map.free": "Free",
    "map.camping": "Camping",
    "map.stellplatz": "Rest stop",
    "map.dumpStation": "Dump station",
    "map.navigateHit": "Navigate here",
    "map.place": "Place",
    "map.restaurant": "Restaurant",
    "map.supermarket": "Grocery",
    "map.attraction": "Attraction",
    "map.parking": "Parking",
    "map.fuelStation": "Gas station",
    "map.museum": "Museum",
    "map.nature": "Nature",
    "map.sights": "Attractions",
    "map.grocery": "Grocery",
    "map.perNight": "/night",
    "submit.addressAutoHint": "Search the address first — country, region and city are filled in automatically.",
    "submit.autoDetected": "Country, region and city have been filled in automatically!",
    "submit.placeExists": "Place already exists",
    "submit.placeExistsDesc": "A place named \"{name}\" is already registered.",
    "submit.nearbyExists": "Nearby place already exists",
    "submit.nearbyExistsDesc": "\"{name}\" is already registered at the same location.",
    "submit.imageRejected": "Image rejected",
    "submit.mainImage": "Main image",
    "submit.contactInfo": "Contact information",
    "service.heroDesc": "Find workshops, fuel, water, hospitals and more — wherever you are in Europe",
    "service.close": "Close",
    "service.clearSearch": "Clear",
    "service.search": "Search",
    "service.positionSharing": "Position sharing",
    "service.positionSharingDesc": "Share your GPS position or activate SOS",
    "service.connectionError": "Check your connection and try again",
    "service.tryAgain": "Try again",
    "service.noResultsHint": "Try searching for something else or switch category",
    "service.resultsNearYou": "near you",
    "budget.noPeriod": "No period",
    "budget.exceededBy": "Exceeded by",
    "budget.expenseDeleted": "Expense deleted",
    "budget.undo": "Undo",
    "budget.receipt": "Receipt",
    "budget.trip": "Trip",
    "whatsNew.title": "What's New",
    "whatsNew.subtitle": "Latest updates and features",
    "whatsNew.seoTitle": "What's New – Vantastika",
    "whatsNew.seoDesc": "See the latest updates and news in Vantastika.",
    "whatsNew.all": "All",
    "whatsNew.noUpdates": "No updates right now",
    "whatsNew.type.new_feature": "New feature",
    "whatsNew.type.improvement": "Improvement",
    "whatsNew.type.bugfix": "Bug fix",
    "whatsNew.type.important": "Important",
    "whatsNew.type.maintenance": "Maintenance",
    "whatsNew.readMore": "Read more",
    "search.benefit1": "See all campings and pitches",
    "search.benefit2": "Filter by amenities and price",
    "search.benefit3": "Read reviews and see ratings",
    "search.smartPlaceholder": "Search places, campings, pitches, cities...",
    "search.recommendedFor": "Recommended for",
    "search.typeCategory": "Category",
    "search.typeRegion": "Area",
    "search.typeCountry": "Country",
    "search.typePlace": "Place",
    "search.noResultsHint": "Try a different search term or remove some filters",
    "search.browseByCountry": "Browse by country",
    "travelGuides.badge": "Travel Guides",
    "travelGuides.heroTitle1": "Discover Europe's best ",
    "travelGuides.heroTitle2": "routes",
    "travelGuides.heroSubtitle": "Detailed guides with history, highlights and practical info. Turn inspiration into a real trip with one click.",
    "travelGuides.popular": "Popular destinations",
    "travelGuides.moreCountries": "More countries",
    "travelGuides.guidesCount": "{n} guides",
    "travelGuides.guidesSingular": "1 guide",
    "travelGuides.explore": "Explore",
    "countryGuide.titlePrefix": "Travel Guide",
    "countryGuide.allGuides": "All travel guides",
    "countryGuide.seoDesc": "Complete motorhome travel guide for",
    "countryGuide.overview": "Overview",
    "countryGuide.campingRules": "Camping & Overnight Stays",
    "countryGuide.campingRegulations": "Camping Rules",
    "countryGuide.wildCamping": "Wild Camping",
    "countryGuide.campingTips": "Camping Tips",
    "countryGuide.bestSeason": "Best Season",
    "countryGuide.drivingInfo": "Driving & Roads",
    "countryGuide.trafficRules": "Traffic Rules",
    "countryGuide.speedLimits": "Speed Limits",
    "countryGuide.tollsVignettes": "Tolls & Vignettes",
    "countryGuide.currency": "Currency",
    "countryGuide.fuel": "Fuel",
    "countryGuide.emergency": "Emergency",
    "countryGuide.language": "Language",
    "countryGuide.bestPlaces": "Best Places to Visit",
    "countryGuide.safetyTips": "Safety Tips",
    "countryGuide.practicalTips": "Practical Tips",
    "countryGuide.routeGuides": "Route Guides",
    "countryGuide.guide": "Guide",
    "countryGuide.readGuide": "Read guide",
    "countryGuide.searchCampings": "Search campings",
    "countryGuide.showOnMap": "Show on map",
    "countryGuide.planTrip": "Plan trip",
    "countryGuide.planTripHere": "Plan trip here",
    "countryGuide.campings": "Campings",
    "guideDetail.days": "days",
    "guideDetail.history": "History & Background",
    "guideDetail.routeInfo": "Route Information",
    "guideDetail.startPoint": "Start Point",
    "guideDetail.endPoint": "End Point",
    "guideDetail.distance": "Distance",
    "guideDetail.direction": "Direction",
    "guideDetail.passesThrough": "Passes through",
    "guideDetail.overview": "Overview",
    "guideDetail.highlights": "Highlights",
    "guideDetail.practical": "Practical",
    "guideDetail.bestSeason": "Best Season",
    "guideDetail.vehicleType": "Vehicle Type",
    "guideDetail.motorhome": "Motorhome",
    "guideDetail.car": "Car",
    "guideDetail.motorcycle": "Motorcycle",
    "guideDetail.campervan": "Campervan",
    "guideDetail.warnings": "Things to Consider",
    "guideDetail.practicalTips": "Practical Tips",
    "guideDetail.whyVisit": "Why visit: ",
    "guideDetail.readyToGo": "Ready to go?",
    "guideDetail.readyToGoDesc": "Turn this guide into a real trip in the trip planner. Customize stops, number of days and return journey as you wish.",
    "guideDetail.planThisTrip": "Plan this trip",
    "guideDetail.refreshGuide": "Refresh guide",
    "guideDetail.generating": "Creating detailed travel guide with AI...",
    "guideDetail.loading": "Loading travel guide...",
    "guideDetail.premiumBenefit1": "Read the full travel guide with stops and tips",
    "guideDetail.premiumBenefit2": "See detailed route information",
    "guideDetail.premiumBenefit3": "Turn the guide into a real trip",
    "destination.motorhome": "Motorhome in",
    "destination.planTrip": "Plan your motorhome trip to",
    "destination.planNow": "Plan your trip now",
    "destination.viewGuides": "View travel guides",
    "destination.whatWeOffer": "What Vantastika offers",
    "destination.featureRoutes": "Route planning",
    "destination.featureRoutesDesc": "Plan your route with stops, distances and driving times.",
    "destination.featureCamping": "Campsites & pitches",
    "destination.featureCampingDesc": "Find the best campsites and motorhome pitches.",
    "destination.featureRules": "Driving rules",
    "destination.featureRulesDesc": "Speed limits, tolls and local regulations.",
    "destination.featureSafety": "Safety & SOS",
    "destination.featureSafetyDesc": "Emergency numbers, roadside assistance and safety tips.",
    "destination.popularRoutes": "Popular routes in",
    "destination.readyToGo": "Ready for the trip?",
    "destination.ctaDesc": "Create a free account and start planning your road trip today.",
    "destination.getStarted": "Get started free",
    "destination.findCampings": "Find campsites",
    "share.shareOnInstagram": "Share on Instagram",
    "share.shareOnFacebook": "Share on Facebook",
    "share.linkCopied": "Link copied!",
    "share.linkCopiedDesc": "Paste it wherever you like.",
    "ig.headline": "Plan your trip smarter with Vantastika",
    "ig.subheadline": "Route, budget, and trip planning in one place for road trips, motorhomes, and car holidays.",
    "ig.ctaStart": "Get started free",
    "ig.ctaOpen": "Open Vantastika",
    "ig.ctaDemo": "See how it works",
    "ig.demoTitle": "This is how easy trip planning is",
    "ig.benefit1.title": "Plan your full route",
    "ig.benefit1.desc": "Create your route with stops, distances, and times.",
    "ig.benefit2.title": "Keep track of the budget",
    "ig.benefit2.desc": "See what the trip costs — before and during.",
    "ig.benefit3.title": "Better overview",
    "ig.benefit3.desc": "Everything in one place, before and during the trip.",
    "ig.free": "Free. No credit card required.",
    "projects.title": "Projects",
    "projects.myProjects": "My Projects",
    "projects.subtitle": "Plan and track your builds and renovations",
    "projects.pageDesc": "Manage your motorhome projects, renovations, and builds",
    "projects.loginRequired": "Log in to manage your projects",
    "projects.create": "New project",
    "projects.createTitle": "Create project",
    "projects.createFirst": "Create your first project",
    "projects.creating": "Creating...",
    "projects.emptyTitle": "No projects yet",
    "projects.emptyDesc": "Create a project to organize your builds, renovations, and upgrades with materials lists, task tracking, and budget management.",
    "projects.name": "Project name",
    "projects.namePlaceholder": "e.g. Battery conversion",
    "projects.description": "Description",
    "projects.descPlaceholder": "What is this project about?",
    "projects.categoryLabel": "Category",
    "projects.priorityLabel": "Priority",
    "projects.startDate": "Start date",
    "projects.endDate": "End date",
    "projects.budget": "Budget",
    "projects.actualCost": "Actual cost",
    "projects.notes": "Notes",
    "projects.notesPlaceholder": "Add notes...",
    "projects.save": "Save",
    "projects.edit": "Edit",
    "projects.cancel": "Cancel",
    "projects.add": "Add",
    "projects.share": "Share",
    "projects.notFound": "Project not found",
    "projects.backToProjects": "Back to projects",
    "projects.status.not_started": "Not started",
    "projects.status.in_progress": "In progress",
    "projects.status.completed": "Completed",
    "projects.priority.low": "Low",
    "projects.priority.medium": "Medium",
    "projects.priority.high": "High",
    "projects.priority.urgent": "Urgent",
    "projects.category.battery": "Battery",
    "projects.category.solar": "Solar panel",
    "projects.category.bathroom": "Bathroom",
    "projects.category.kitchen": "Kitchen",
    "projects.category.water_system": "Water system",
    "projects.category.electrical": "Electrical",
    "projects.category.interior": "Interior",
    "projects.category.exterior": "Exterior",
    "projects.category.mechanical": "Mechanical",
    "projects.category.other": "Other",
    "projects.tab.overview": "Overview",
    "projects.tab.materials": "Materials",
    "projects.tab.tasks": "Tasks",
    "projects.tab.files": "Files",
    "projects.tab.history": "History",
    "projects.tasksCompleted": "Tasks completed",
    "projects.materialsBought": "Materials bought",
    "projects.budgetOverview": "Budget overview",
    "projects.materialsCost": "Materials cost",
    "projects.spent": "Spent",
    "projects.remaining": "Remaining",
    "projects.total": "Total",
    "projects.bought": "Bought",
    "projects.addMaterial": "Add material",
    "projects.materialName": "Product name",
    "projects.quantity": "Quantity",
    "projects.price": "Price",
    "projects.storeName": "Store",
    "projects.storeUrl": "Link (URL)",
    "projects.noMaterials": "No materials added yet",
    "projects.addTask": "Add task",
    "projects.taskTitle": "Task title",
    "projects.taskDesc": "Description",
    "projects.noTasks": "No tasks added yet",
    "projects.completedAt": "Completed",
    "projects.status_label": "Status",
    "projects.filter.all": "All",
    "projects.filter.bought": "Bought",
    "projects.filter.not_bought": "Not bought",
    "projects.filter.pending": "Pending",
    "projects.filter.done": "Done",
    "projects.uploadFile": "Upload file",
    "projects.uploading": "Uploading...",
    "projects.maxFileSize": "Max 15 MB",
    "projects.fileTooLarge": "File is too large (max 15 MB)",
    "projects.fileUploaded": "File uploaded",
    "projects.uploadError": "Upload failed",
    "projects.noFiles": "No files added yet",
    "projects.noHistory": "No activity yet",
    "projects.shareProject": "Share project",
    "projects.generateLink": "Generate share link",
    "projects.linkGenerated": "Share link created",
    "projects.exportPdf": "Export as PDF",
    "projects.pdfFull": "Full project",
    "projects.pdfMaterials": "Materials only",
    "projects.pdfTasks": "Tasks only",
    "projects.pdfSummary": "Summary",
    "projects.deleteProject": "Delete project",
    "projects.confirmDeleteTitle": "Delete project?",
    "projects.confirmDeleteDesc": "This will permanently delete this project and all its materials, tasks, files, and history. This action cannot be undone.",
    "projects.deleted": "Project deleted",
    "projects.vehicleLabel": "Vehicle",
    "projects.linkedVehicle": "Linked vehicle",
    "projects.linkVehicle": "Link vehicle",
    "projects.noVehicle": "No vehicle linked",
    "projects.selectVehicle": "Select vehicle",
    "projects.noVehicleOption": "None",
    "projects.duplicate": "Duplicate",
    "projects.editMaterial": "Edit material",
    "projects.editTask": "Edit task",
    "projects.saveMaterial": "Save material",
    "projects.saveTask": "Save task",
    "projects.swipeToDelete": "Swipe to delete",
    "projects.moreActions": "More actions",
    "projects.editProject": "Edit project",
    "upload.validating": "Validating...",
    "upload.processing": "Processing image...",
    "upload.converting": "Converting HEIC...",
    "upload.resizing": "Resizing...",
    "upload.compressing": "Compressing...",
    "upload.thumbnailing": "Creating thumbnail...",
    "upload.almostDone": "Almost done...",
    "upload.uploading": "Uploading...",
    "upload.done": "Done!",
    "upload.failed": "Upload failed",
    "upload.fileTooLarge": "File is too large",
    "upload.imageRejected": "Image rejected",
  },
  sv: {
    "offline.message": "Du är offline — sparade resor visas från cachen",
    "offline.syncing": "Synkar data...",
    "offline.synced": "Allt synkat!",
    "confirm.deleteTitle": "Vill du verkligen ta bort detta?",
    "confirm.deleteDesc": "Den här åtgärden kan inte ångras.",
    "confirm.cancel": "Avbryt",
    "confirm.delete": "Ta bort",
    "confirm.deleteTrip": "Ta bort denna resa?",
    "confirm.deleteTripDesc": "Resan och all dess data tas bort permanent. Åtgärden kan inte ångras.",
    "confirm.deletePost": "Ta bort detta inlägg?",
    "confirm.deletePostDesc": "Inlägget tas bort permanent.",
    "confirm.deleteBudget": "Ta bort denna budget?",
    "confirm.deleteBudgetDesc": "Budgeten och alla dess utgifter tas bort permanent.",
    "confirm.deleteNotification": "Ta bort denna notis?",
    "confirm.deleteNotificationDesc": "Notisen tas bort permanent.",
    "confirm.deleteMaterial": "Ta bort detta material?",
    "confirm.deleteMaterialDesc": "Materialet tas bort permanent från projektet.",
    "confirm.deleteTask": "Ta bort denna uppgift?",
    "confirm.deleteTaskDesc": "Uppgiften tas bort permanent från projektet.",
    "confirm.deleteFile": "Ta bort denna fil?",
    "confirm.deleteFileDesc": "Filen tas bort permanent.",
    "confirm.deleteMessage": "Ta bort detta meddelande?",
    "confirm.deleteMessageDesc": "Meddelandet tas bort permanent.",
    "confirm.deleteUpdate": "Ta bort denna uppdatering?",
    "confirm.deleteUpdateDesc": "Uppdateringen tas bort permanent.",
    "confirm.deleteCamping": "Ta bort denna campingplats permanent?",
    "confirm.deleteCampingDesc": "Campingplatsen tas bort permanent.",
    "confirm.unpublishTrip": "Avpublicera denna resa?",
    "confirm.unpublishTripDesc": "Resan blir inte längre synlig för andra.",
    "confirm.endTrip": "Avsluta denna resa?",
    "confirm.endTripDesc": "Resan markeras som avslutad.",
    "nav.explore": "Hitta camping",
    "nav.map": "Utforska kartan",
    "nav.trips": "Planera resa",
    "nav.favorites": "Sparade platser",
    "nav.login": "Logga in",
    "nav.signup": "Kom igång gratis",
    "nav.logout": "Logga ut",
    "nav.bottom.trips": "Ny resa",
    "nav.bottom.explore": "Camping",
    "nav.bottom.map": "Karta",
    "nav.bottom.saved": "Sparat",
    "nav.bottom.profile": "Profil",
    "nav.bottom.login": "Logga in",
    "nav.bottom.signup": "Registrera",
    "nav.bottom.home": "Hem",
    "nav.bottom.newTrip": "Ny resa",
    "nav.bottom.myTrips": "Mina resor",
    "nav.checklists": "Checklistor",
    "nav.travelGuides": "Reseguider",
    "nav.menuMine": "Mina",
    "nav.menuCreate": "Skapa / Bidra",
    "nav.settings": "Inställningar",
    "footer.integrationTerms": "Integrationsvillkor",
    "dash.greeting.morning": "God morgon",
    "dash.greeting.day": "Hej",
    "dash.greeting.afternoon": "God eftermiddag",
    "dash.greeting.evening": "God kväll",
    "dash.greeting.night": "God natt",
    "dash.subtitle.active": "Din resa pågår – här är din status",
    "dash.subtitle.planning": "Du har resor att förbereda",
    "dash.subtitle.completed": "Bra jobbat! Här är din reseöversikt",
    "dash.subtitle.empty": "Vad vill du göra idag?",
    "dash.paused": "Pausad",
    "dash.ongoing": "Pågående resa",
    "dash.total": "Totalt",
    "dash.stops": "Stopp",
    "dash.done": "Klart",
    "dash.nextDest": "Nästa destination",
    "dash.continueTrip": "Fortsätt resa",
    "dash.openActive": "Öppna aktiv resa",
    "dash.journal": "Journal",
    "dash.budget": "Budget",
    "dash.findPlace": "Hitta plats",
    "dash.upcoming": "Kommande resa",
    "dash.today": "Idag!",
    "dash.tomorrow": "Imorgon",
    "dash.daysLeft": "{n} dagar kvar",
    "dash.continuePlan": "Fortsätt planera",
    "dash.start": "Starta",
    "dash.morePlanned": "Fler planerade resor",
    "dash.findCamping": "Hitta camping",
    "dash.findCampingSub": "2 800+ platser",
    "dash.guides": "Reseguider",
    "dash.guidesSub": "21+ länder",
    "dash.checklists": "Checklistor",
    "dash.checklistsSub": "Packa smart",
    "dash.wishlist": "Önskelista",
    "dash.vehicle": "Fordon",
    "dash.projects": "Projekt",
    "dash.community": "Community",
    "dash.popularDest": "Populära destinationer",
    "dash.seeAll": "Visa alla",
    "dash.planNew": "Planera ny resa",
    "dash.planNewSub": "Steg-för-steg wizard",
    "dash.season.spring": "Våräventyr",
    "dash.season.summer": "Sommar på vägen",
    "dash.season.autumn": "Höstens roadtrips",
    "dash.season.winter": "Vinteräventyr",
    "dash.tripDone": "Resa avslutad",
    "dash.visited": "Besökt",
    "dash.seeSummary": "Se sammanfattning",
    "dash.planNewOld": "Planera ny resa",
    "dash.emptyTitle": "Dags att planera din första resa!",
    "dash.emptyDesc": "Skapa en resa och lägg till stopp, budget och checklistor.",
    "dash.createTrip": "Skapa resa",
    "dash.stopsCount": "{count} stopp",
    "dash.visitedCount": "{count} besökta",
    "dash.onboarding.step1": "Planera en resa",
    "dash.onboarding.step1Desc": "Skapa din första roadtrip med stopp och datum",
    "dash.onboarding.step2": "Spara drömresmål",
    "dash.onboarding.step2Desc": "Bygg din önskelista med platser att besöka",
    "dash.onboarding.step3": "Utforska campingplatser",
    "dash.onboarding.step3Desc": "Bläddra bland 2 800+ campingar i Europa",
    "dash.onboarding.stepVehicle": "Ställ in ditt fordon",
    "dash.onboarding.stepVehicleDesc": "Lägg till din husbil, bil eller campingbuss",
    "dash.onboarding.stepProject": "Starta ett projekt",
    "dash.onboarding.stepProjectDesc": "Följ renoveringar, uppgraderingar och byggen",
    "dash.emptyDescReturning": "Planera din första resa – din sparade data är redo att användas!",
    "dash.recentProjects": "Dina projekt",
    "dash.popularGuides": "Populära guider",
    "dash.noProjects": "Inga projekt ännu",
    "dash.draft": "Utkast",
    "dash.continueDraft": "Fortsätt planera",
    "dash.untitled": "Namnlös resa",
    "dash.nextStep": "Nästa steg",
    "dash.nextStepStops": "Lägg till ditt första stopp",
    "dash.nextStepDate": "Välj resdatum",
    "dash.nextStepChecklist": "Slutför checklistan",
    "dash.prepTitle": "Förbered din resa",
    "dash.prepBudget": "Sätt en budget så du håller koll",
    "dash.prepStops": "Lägg till fler stopp längs rutten",
    "dash.prepChecklist": "Gå igenom din packlista",
    "dash.packed": "packat",
    "dash.incompleteTitle": "Slutför planeringen",
    "dash.incompleteDate": "Lägg till ett avresedatum",
    "dash.incompleteStops": "Lägg till stopp på din rutt",
    "dash.postTripTitle": "Vad händer nu?",
    "dash.postTripShare": "Dela din resa med vänner",
    "dash.postTripReview": "Betygsätt platserna du besökte",
    "dash.postTripJournal": "Skriv om dina upplevelser",
    "dash.postTripMsg": "Du reste {km} km och besökte {stops} platser — vilken resa!",
    "nav.myTrips": "Mina resor",
    "nav.submit": "Lägg till plats",
    "nav.help": "Så använder du appen",
    "nav.compare": "Jämför campingar",
    "nav.guides": "Landsguider",
    "nav.contact": "Kontakta oss",
    "help.title": "Så använder du appen",
    "search.startTyping": "Börja skriva för att söka",
    "search.startTypingDesc": "Sök på platsnamn, stad, kategori eller typ av destination",
    "search.clickForDetails": "Klicka på ett kort för detaljer",
    "search.searchHint": "Sök på namn, kategori, stad eller region. Intelligent sökning hittar rätt plats åt dig.",
    "search.foundOnGoogle": "extra förslag från Google",
    "search.addHint": "Tryck på ett kort för att lägga till i Vantastika",
    "search.fromGoogle": "Från Google",
    "search.addToVantastika": "Lägg till i Vantastika",
    "search.googleAdding": "Lägger till...",
    "search.googleAddedShort": "Skickad för granskning",
    "search.googleAdded": "Plats inskickad",
    "search.googleAddedDesc": "Tack! Den blir synlig efter admin-granskning.",
    "search.googleAlreadyExists": "Finns redan i Vantastika",
    "search.googleAlreadyExistsDesc": "Vi tar dig till den befintliga sidan.",
    "search.googleAddError": "Kunde inte lägga till platsen. Försök igen.",
    "search.nearbyFound": "campingar nära dig",
    "search.within25km": "inom 25 km",
    "search.noNearby": "Inga campingar i närheten",
    "search.noNearbyDesc": "Prova kartan för att utforska längre bort.",
    "search.openMap": "Öppna kartan",
    "search.findingLocation": "Hittar din position...",
    "search.findingLocationDesc": "Vi använder den för att visa campingar nära dig.",
    "search.locationDeniedHint": "Platsåtkomst är nekad. Tillåt den för att se campingar nära dig.",
    "search.enableLocationHint": "Tillåt platsåtkomst för att se närmaste campingar.",
    "search.allowLocation": "Tillåt plats",
    "search.topRated": "Topprankade campingar",
    "help.whatIs.title": "Vad är Vantastika?",
    "help.whatIs.body": "Din resecompis för husbil, campervan och bilresor i Europa. Hitta campingplatser, planera rutter och håll koll på budgeten — allt på ett ställe.",
    "help.startTrip.title": "Starta en resa",
    "help.startTrip.body": "Tryck på Reseplaneraren, ge din resa ett namn, lägg till stopp och datum. Appen räknar ut avstånd och bränslekostnader åt dig.",
    "help.search.title": "Sök efter platser",
    "help.search.body": "Använd Utforska eller söksidan för att hitta campingar, ställplatser och servicestationer nära vilken plats som helst i Europa.",
    "help.map.title": "Använd kartan",
    "help.map.body": "Öppna Utforska för att se kartan. Sök efter en stad eller använd din position. Tryck på markörer för detaljer och vägbeskrivning.",
    "help.addStop.title": "Lägg till ett stopp",
    "help.addStop.body": "När du tittar på en camping, tryck 'Lägg till i resa' för att lägga till den i din rutt. Du kan ändra ordning och redigera stopp när som helst.",
    "help.share.title": "Dela och bli inspirerad",
    "help.share.body": "Gör din resa publik så andra kan se din rutt. Bläddra bland delade resor från communityn för inspiration.",
    "help.favorites.title": "Spara favoriter",
    "help.favorites.body": "Tryck på hjärtat på valfri camping för att spara den. Organisera favoriter i samlingar för enkel åtkomst senare.",
    "hero.title1": "Planera hela din resa",
    "hero.title2": "på ett ställe.",
    "hero.subtitle": "Planera resan, lägg till stopp, hitta platser på kartan och håll koll på budgeten – utan att hoppa mellan flera olika appar.",
    "hero.searchPlaceholder": "Vart vill du resa?",
    "hero.search": "Sök",
    "hero.ctaPlan": "Planera min resa gratis",
    "hero.ctaExplore": "Hitta campingar",
    "hero.ctaSubmit": "Tipsa om en plats",
    "hero.valueProps": "Allt på ett ställe — slipp hoppa mellan flera appar",
    "hero.diffTitle": "Varför Vantastika?",
    "hero.diff1": "Reseplanering, stopp och budget i ett flöde",
    "hero.diff2": "Skanna kvitton och följ utgifterna automatiskt",
    "hero.diff3": "Lägg till stopp direkt från kartan",
    "hero.diff4": "För husbil, bil och roadtrip",
    "features.find": "Lägg till platser från kartan",
    "features.findDesc": "Campingar, sevärdheter, restauranger och fler stopp direkt i planeringen",
    "features.save": "Spara favoriter",
    "features.saveDesc": "Ha koll på dina bästa platser",
    "features.plan": "Planera hela resan",
    "features.planDesc": "Skapa rutt, lägg till stopp och bygg resan steg för steg",
    "features.budget": "Håll koll på budgeten",
    "features.budgetDesc": "Registrera utgifter, skanna kvitton och få överblick över resans kostnader",
    "features.europe": "Hela Europa",
    "features.europeDesc": "Tusentals platser du kan lita på, i hela Europa",
    "featured.title": "Populära Campingar",
    "featured.viewAll": "Visa alla →",
    "search.title": "Utforska Campingar",
    "search.placeholder": "Sök campingar...",
    "search.country": "Land",
    "search.allCountries": "Alla länder",
    "search.filters": "Filter",
    "search.clearAll": "Rensa alla",
    "search.minPrice": "Min pris",
    "search.maxPrice": "Max pris",
    "search.region": "Region",
    "search.regionPlaceholder": "t.ex. Dalarna",
    "search.noResults": "Inga campingar hittades",
    "search.adjustFilters": "Prova att justera dina filter",
    "search.found": "campingar hittades",
    "filter.electricity": "Elanslutning",
    "filter.freshWater": "Färskvatten",
    "filter.greyWater": "Gråvattenavlopp",
    "filter.toilet": "Tömning toalettkassett",
    "filter.shower": "Dusch",
    "filter.wc": "WC",
    "filter.petFriendly": "Husdjursvänlig",
    "filter.openAllYear": "Öppet hela året",
    "filter.familyFriendly": "Familjevänlig",
    "filter.largeMotorhomes": "Stora husbilar",
    "filter.nearWater": "Nära sjö/strand",
    "filter.spaWellness": "Spa/wellness",
    "filter.wifi": "WiFi",
    "filter.laundry": "Tvättstuga",
    "filter.restaurant": "Restaurang",
    "detail.about": "Om",
    "detail.amenities": "Bekvämligheter",
    "detail.contact": "Kontakt",
    "detail.website": "Webbsida",
    "detail.checkIn": "Incheckning",
    "detail.checkOut": "Utcheckning",
    "detail.location": "Plats",
    "detail.openInMaps": "Öppna i Google Maps →",
    "detail.mapComingSoon": "Karta kommer snart",
    "detail.back": "Tillbaka",
    "detail.save": "Spara",
    "detail.saved": "Sparad",
    "detail.perNight": "per natt",
    "detail.reviews": "Recensioner",
    "detail.writeReview": "Skriv en recension",
    "detail.noReviews": "Inga recensioner än",
    "detail.submitReview": "Skicka recension",
    "detail.yourRating": "Ditt betyg",
    "detail.yourComment": "Din kommentar (valfritt)",
    "detail.loginToReview": "Logga in för att skriva en recension",
    "detail.notFound": "Campingen hittades inte",
    "detail.backToSearch": "Tillbaka till sökning",
    "favorites.title": "Mina Favoriter",
    "favorites.saveYour": "Spara dina favoritcampingar",
    "favorites.signUpPrompt": "Registrera dig eller logga in för att spara campingar.",
    "favorites.empty": "Inga favoriter ännu",
    "favorites.emptyDesc": "Börja utforska och spara campingar du gillar",
    "favorites.explore": "Utforska Campingar",
    "favorites.emptyCollection": "Inga favoriter i denna samling",
    "wishlist.title": "Önskelista",
    "wishlist.save": "Spara i önskelista",
    "wishlist.saved": "Sparad i önskelistan!",
    "wishlist.alreadySaved": "Redan i önskelistan",
    // My Map (manuella nålar + lager)
    "myMap.title": "Min resekarta",
    "myMap.loginMsg": "Logga in för att bygga din egen resekarta med sparade, besökta och manuellt lagda platser.",
    "myMap.placesShown": "platser visas",
    "myMap.addPin": "Lägg nål",
    "myMap.newPin": "Ny nål",
    "myMap.editPin": "Redigera nål",
    "myMap.name": "Namn",
    "myMap.namePlaceholder": "T.ex. Mormorsstugan",
    "myMap.statusLabel": "Status",
    "myMap.status.wantToVisit": "Vill besöka",
    "myMap.status.planned": "Planerad",
    "myMap.status.visited": "Besökt",
    "myMap.status.favorite": "Favorit",
    "myMap.category": "Kategori",
    "myMap.categoryPlaceholder": "T.ex. Naturreservat, Restaurang…",
    "myMap.note": "Anteckning",
    "myMap.notePlaceholder": "Vad gör platsen speciell?",
    "myMap.optional": "valfritt",
    "myMap.linkTrip": "Koppla till resa",
    "myMap.noTrip": "Ingen koppling",
    "myMap.untitledTrip": "Resa utan titel",
    "myMap.activeTripHint": "aktiv",
    "myMap.activeTripAutoLinked": "Föreslås automatiskt eftersom du har en aktiv resa.",
    "myMap.savePin": "Spara nål",
    "myMap.saveChanges": "Spara ändringar",
    "myMap.confirmDelete": "Ta bort denna nål?",
    "myMap.dragHint": "Dra nålen på kartan för att flytta den.",
    "myMap.savedToast": "Nål sparad",
    "myMap.updatedToast": "Nål uppdaterad",
    "myMap.deletedToast": "Nål borttagen",
    "myMap.movedToast": "Position uppdaterad",
    "myMap.layer.wishlist": "Önskelista",
    "myMap.layer.visited": "Besökta",
    "myMap.layer.manual": "Egna nålar",
    "myMap.longPressTitle": "Tryck och håll på kartan",
    "myMap.longPressDesc": "Håll fingret eller musen nedtryckt på en plats i 0,5 sekunder för att lägga en egen nål där.",
    "myMap.mapFailed": "Kartan kunde inte laddas. Försök ladda om sidan.",
    "myMap.searchPlaceholder": "Sök plats, camping, adress…",
    "myMap.clearSearch": "Rensa sökning",
    // SaveGateSheet
    "saveGate.wishlist.savedFor": "Vi har sparat platsen",
    "saveGate.trip.savedFor": "Vi har sparat resan",
    "saveGate.place.savedFor": "Vi har sparat",
    "saveGate.holdsFor24h": "Vi sparar den i 24 timmar — skapa ett konto så att den aldrig försvinner.",
    "saveGate.wishlist.benefit1": "Synka mellan telefon & dator",
    "saveGate.wishlist.benefit2": "Få notis när priset sjunker",
    "saveGate.wishlist.benefit3": "Dela med resekompis",
    "saveGate.trip.benefit1": "Aktivera offline-läge på vägen",
    "saveGate.trip.benefit2": "Smart navigation som anpassas till ditt fordon",
    "saveGate.trip.benefit3": "Dela live-position med familjen",
    "saveGate.place.benefit1": "Spara din plats permanent",
    "saveGate.place.benefit2": "Få tips på platser i närheten",
    "saveGate.place.benefit3": "Tillgängligt på alla enheter",
    "saveGate.socialProof": "resenärer i Norden använder Vantastika",
    "saveGate.createAccount15s": "Skapa konto på 15 sek",
    "saveGate.alreadyMember": "Redan medlem?",
    "saveGate.logIn": "Logga in",
    "saveGate.continueGuest": "Fortsätt som gäst — sparas i 24 h",
    "saveGate.noCardNeeded": "Gratis. Inget kort behövs.",
    // GuestRescueSheet
    "guestRescue.title": "Du har {count} sparade saker som riskerar att försvinna",
    "guestRescue.expiresIn": "Försvinner om {time}",
    "guestRescue.expired": "Utgånget — skapa konto för att återställa",
    "guestRescue.trip": "Resa",
    "guestRescue.wishlist": "Önskelista",
    "guestRescue.rescueCta": "Rädda mina sparade saker — skapa konto",
    "guestRescue.footnote": "Gratis för alltid. Inget betalkort.",
    "saveGate.hoursShort": "h",
    "saveGate.minutesShort": "min",
    "guestRescue.toastTitle": "Tips: Skapa konto",
    "guestRescue.toastBody": "Spara på alla enheter och tappa aldrig dina resor.",
    "guestRescue.toastCta": "Gör det nu",
    "nav.bottom.guestSaves": "{count} sparat",
    "wishlist.loginMsg": "Logga in för att spara platser du vill besöka.",
    "wishlist.empty": "Din önskelista är tom",
    "wishlist.emptyDesc": "Spara platser du drömmer om att besöka och planera resor utifrån dem senare.",
    "wishlist.add": "Lägg till plats",
    "wishlist.searchPlace": "Sök efter en plats att spara",
    "wishlist.searchPlaceholder": "Stad, land, sevärdhet, camping...",
    "wishlist.myNote": "Min anteckning",
    "wishlist.notePlaceholder": "Skriv en anteckning om den här platsen...",
    "wishlist.addNote": "Tryck för att lägga till anteckning...",
    "wishlist.saveNote": "Spara",
    "wishlist.nearby": "I närheten",
    "wishlist.nearbyAttractions": "Sevärdheter",
    "wishlist.nearbyAccommodation": "Boende",
    "wishlist.nearbyHotels": "Hotell",
    "wishlist.nearbyRestaurants": "Restauranger",
    "wishlist.noNearby": "Inga närliggande platser hittades i vår databas ännu.",
    "wishlist.planTrip": "Planera resa hit",
    "wishlist.planMultiple": "Planera resa",
    "wishlist.deleteTitle": "Ta bort från önskelistan?",
    "wishlist.deleteDesc": "Platsen tas bort från din önskelista.",
    "wishlist.deletedToast": "Platsen togs bort",
    "wishlist.restoredToast": "Platsen återställdes",
    "wishlist.undo": "Ångra",
    "wishlist.selectedCount": "valda",
    "wishlist.pickFromWishlist": "Välj från önskelista",
    "wishlist.loading": "Laddar...",
    "wishlist.emptyPicker": "Din önskelista är tom. Spara platser först!",
    "wishlist.addSelected": "Lägg till valda",
    "wishlist.fromWishlist": "Från önskelista",
    "wishlist.planWithPlaces": "Planera resa med valda platser",
    "wishlist.planWithPlacesDesc": "Välj ordning och tilldela varje plats en roll.",
    "wishlist.orderMode": "Ruttordning",
    "wishlist.myOrder": "Planera i min ordning",
    "wishlist.myOrderDesc": "Behåll exakt den ordning du valt",
    "wishlist.smartOrder": "Optimera resan smart",
    "wishlist.smartOrderDesc": "Hitta smartaste ordningen med mindre omväg",
    "wishlist.assignRoles": "Vilken roll har varje plats?",
    "wishlist.role_destination": "Huvuddestination",
    "wishlist.role_stop": "Mellanstop",
    "wishlist.role_attraction": "Sevärdhet",
    "wishlist.role_overnight": "Övernattning",
    "wishlist.startPlanning": "Börja planera",
    "wishlist.places": "platser",
    "notifications.emptyTitle": "Inga notiser ännu",
    "notifications.emptyDesc": "När något händer ser du det här",
    "notifications.title": "Notiser",
    "notifications.unreadCount": "olästa",
    "notifications.noUnread": "Inga olästa notiser",
    "notifications.markAll": "Markera alla",
    "notifications.markRead": "Markera som läst",
    "notifications.show": "Visa",
    "notifications.delete": "Ta bort",
    "notifications.goToInbox": "Gå till inkorg & meddelanden",
    "notifications.allRead": "Alla markerade som lästa",
    "trips.title": "Mina Resor",
    "trips.plan": "Planera Dina Resor",
    "trips.loginPrompt": "Logga in för att skapa och hantera dina campingresor.",
    "trips.newTrip": "Ny Resa",
    "trips.createTrip": "Skapa en Ny Resa",
    "trips.tripTitle": "Resans titel",
    "trips.startDate": "Startdatum",
    "trips.endDate": "Slutdatum",
    "trips.notes": "Anteckningar för resan...",
    "trips.create": "Skapa Resa",
    "trips.empty": "Inga resor ännu",
    "trips.emptyDesc": "Skapa din första resa och börja lägga till campingstopp",
    "trips.stops": "Stopp",
    "trips.noStops": "Inga stopp tillagda ännu.",
    "trips.created": "Resa skapad!",
    "trips.failed": "Kunde inte skapa resa",
    "trips.partialSave": "Resan sparades delvis",
    "trips.partialDesc": "Vissa delar kunde inte sparas. Gå till Mina resor för att redigera.",
    "trips.unknownPlace": "Okänd plats",
    "trips.ideaLoaded": "Din reseidé har laddats in i planeraren nedan.",
    "trips.newInspiration": "Ny inspiration",
    "trips.requireAuthSave": "Du har lagt ner tid på att planera den här resan. Skapa ett konto så sparas allt automatiskt.",
    "trips.daysAbbrev": "dagar",
    "trips.perDay": "/dag",
    "trips.socialProof": "resenärer planerar här",
    "trips.freeLabel": "100% gratis",
    "trips.ideaCreating": "Skapar din reseidé...",
    "trips.ideaBuilding": "Vi bygger en komplett reseplan för {destination} med stopp, sevärdheter och campingar.",
    "trips.ideaTitle": "Reseidé: {title}",
    "trips.campingsNearby": "Campingar & ställplatser i området",
    "trips.restoreWelcome": "Välkommen tillbaka!",
    "trips.restoreSaving": "Vi sparar din resa \"{title}\" automatiskt...",
    "trips.restoreFailed": "Kunde inte återställa resan",
    "trips.restoreFailedDesc": "Den sparade resedatan var ofullständig. Planera en ny resa.",
    "trips.restoreErrorDesc": "Något gick fel. Planera en ny resa.",
    "trips.partialStopsFailed": "Resan sparades men vissa stopp misslyckades",
    "trips.partialStopsDesc": "Gå till Mina resor och redigera resan för att lägga till stopp som saknas.",
    "contact.sendFailed": "Kunde inte skicka",
    "contact.tryAgain": "Försök igen senare.",
    "contact.sent": "Meddelande skickat!",
    "contact.writeMessage": "Skriv ett meddelande",
    "contact.rateLimited": "Vänta en stund innan du skickar igen",
    "weather.fetchError": "Kunde inte hämta väderdata. Försök igen senare.",
    "cost.fetchFailed": "Kunde inte hämta kostnadsuppskattning",
    "cost.adults": "vuxna",
    "cost.children": "barn",
    "cost.person": "person",
    "cost.persons": "personer",
    "wizard.nextStepDesc": "Vad vill du göra härnäst?",
    "wizard.addBudgetCta": "Sätt en budget för resan",
    "wizard.addMoreStopsCta": "Lägg till fler stopp & campingar",
    "wizard.viewMyTrips": "Visa mina resor",
    "myTrips.chatUnavailable": "Resechatten är inte tillgänglig",
    "myTrips.chatClosed": "Chatten stängs när resan är avslutad.",
    "myTrips.chatOnlyShared": "Chatten finns bara i gemensamma resor med resesällskap.",
    "contact.subject": "Ämne",
    "contact.subjectPlaceholder": "Beskriv kort vad ärendet gäller",
    "contact.message": "Meddelande",
    "contact.messagePlaceholder": "Berätta mer i detalj...",
    "contact.creating": "Skapar...",
    "contact.sendTicket": "Skicka ärende",
    "contact.allTickets": "Alla ärenden",
    "contact.waitingReply": "Vantastika väntar på ditt svar",
    "contact.resolvedLabel": "Löst",
    "contact.closedNotice": "Detta ärende är stängt. Skapa ett nytt ärende om du behöver mer hjälp.",
    "contact.you": "Du",
    "contact.replyPlaceholder": "Skriv ditt svar...",
    "contact.sending": "Skickar...",
    "contact.reply": "Svara",
    "places.yourPosition": "Din position",
    "places.unnamedPlace": "Namnlös plats",
    "places.myPosition": "Min position",
    "map.listView": "Listvy",
    "map.mapView": "Kartvy",
    "shared.joinedTrip": "Du har gått med i resan!",
    "shared.joinedDesc": "Du kan nu se och redigera resan tillsammans med resesällskapet.",
    "shared.couldNotJoin": "Kunde inte gå med",
    "shared.tryAgainLater": "Försök igen senare.",
    "shared.searchStartPlace": "Sök din startplats...",
    "shared.savingCopy": "Sparar...",
    "shared.saveAsCopy": "Spara som egen kopia",
    "camping.blurBenefitDesc": "Se fullständig beskrivning och bekvämligheter",
    "camping.blurBenefitPrice": "Visa priser och prishistorik",
    "camping.blurBenefitReviews": "Läs omdömen från andra resenärer",
    "camping.blurBenefitMap": "Öppna karta och navigera direkt",
    "login.title": "Välkommen tillbaka",
    "login.subtitle": "Logga in på ditt Vantastika-konto",
    "login.email": "E-post",
    "login.password": "Lösenord",
    "login.submit": "Logga In",
    "login.loading": "Loggar in...",
    "login.failed": "Inloggning misslyckades",
    "login.noAccount": "Har du inget konto?",
    "login.signUp": "Registrera dig",
    "login.forgotPassword": "Glömt lösenord?",
    "login.or": "ELLER",
    "login.verificationFailed": "Verifieringen misslyckades. Försök igen.",
    "login.captchaFailed": "CAPTCHA-verifiering misslyckades. Försök igen.",
    "login.tooManyAttempts": "För många inloggningsförsök. Vänta en stund.",
    "login.wrongCredentials": "Felaktig e-post eller lösenord.",
    "social.continueGoogle": "Fortsätt med Google",
    "social.continueApple": "Fortsätt med Apple",
    "social.loginFailed": "Inloggningen misslyckades",
    "social.unknownError": "Okänt fel",
    "forgot.title": "Glömt lösenord",
    "forgot.checkInbox": "Kolla din inkorg!",
    "forgot.enterEmail": "Ange din e-postadress så skickar vi en återställningslänk.",
    "forgot.sentTo": "Vi har skickat ett mail till",
    "forgot.checkSpam": "med en länk för att återställa ditt lösenord. Kolla din skräppost om du inte hittar det.",
    "forgot.backToLogin": "Tillbaka till inloggning",
    "forgot.emailLabel": "E-postadress",
    "forgot.emailPlaceholder": "din@epost.se",
    "forgot.sending": "Skickar...",
    "forgot.sendLink": "Skicka återställningslänk",
    "forgot.couldNotSend": "Kunde inte skicka återställningsmail",
    "forgot.enterEmailFirst": "Ange din e-postadress",
    "reset.title": "Nytt lösenord",
    "reset.subtitle": "Välj ett nytt lösenord för ditt konto.",
    "reset.newPassword": "Nytt lösenord",
    "reset.confirmPassword": "Bekräfta lösenord",
    "reset.placeholder": "Minst 8 tecken, bokstav + siffra",
    "reset.tooShort": "Lösenordet måste vara minst 8 tecken",
    "reset.noMatch": "Lösenorden matchar inte",
    "reset.updating": "Uppdaterar...",
    "reset.update": "Uppdatera lösenord",
    "reset.couldNotUpdate": "Kunde inte uppdatera lösenord",
    "reset.success": "Lösenord uppdaterat!",
    "reset.successDesc": "Ditt lösenord har ändrats framgångsrikt.",
    "reset.goToProfile": "Gå till min profil",
    "reset.invalidLink": "Ogiltig länk",
    "reset.invalidLinkDesc": "Återställningslänken är ogiltig eller har gått ut. Begär en ny från inloggningssidan.",
    "signup.title": "Skapa ett konto",
    "signup.subtitle": "Spara campingar och planera resor helt gratis",
    "signup.name": "Visningsnamn",
    "signup.email": "E-post",
    "signup.password": "Lösenord",
    "signup.gdpr": "Jag godkänner integritetspolicyn och samtycker till behandlingen av mina personuppgifter i enlighet med GDPR.",
    "signup.submit": "Registrera",
    "signup.loading": "Skapar konto...",
    "signup.failed": "Registrering misslyckades",
    "signup.success": "Konto skapat!",
    "signup.checkEmail": "Kontrollera din e-post för att bekräfta ditt konto.",
    "signup.acceptPrivacy": "Godkänn integritetspolicyn",
    "signup.hasAccount": "Har du redan ett konto?",
    "signup.logIn": "Logga in",
    "signup.heroTitle": "Planera din nästa resa på 60 sekunder",
    "signup.heroSubtitle": "Skapa ett kostnadsfritt konto och få tillgång till hela Vantastika – inga annonser, inget kort.",
    "signup.benefit1": "Spara dina rutter för alltid",
    "signup.benefit2": "Boendeförslag längs vägen",
    "signup.benefit3": "Ta del av communityn",
    "signup.benefit4": "Helt gratis – inget kort krävs",
    "signup.titleShort": "Skapa ditt konto",
    "signup.subtitleShort": "Tar 10 sekunder. Inget kort behövs.",
    "signup.submitShort": "Skapa konto gratis",
    "signup.miniConsent": "Jag godkänner",
    "signup.ageMini": "Jag är minst 16 år.",
    "signup.trustLine": "🔒 Vi delar aldrig din e-post. Avsluta när som helst.",
    "signup.freeForever": "Gratis för alltid",
    "signup.confirmPasswordPlaceholder": "Upprepa ditt lösenord",
    "footer.desc": "Planera din resa, hitta rätt platser och res med full kontroll. Husbil, van eller tält.",
    "footer.explore": "Utforska",
    "footer.searchCampings": "Sök Campingar",
    "footer.account": "Konto",
    "footer.legal": "Juridisk",
    "footer.privacy": "Integritetspolicy",
    "footer.terms": "Användarvillkor",
    "footer.rights": "Alla rättigheter förbehållna.",
    "footer.followInstagram": "Följ Vantastika på Instagram",
    "footer.followFacebook": "Följ Vantastika på Facebook",
    "footer.followUs": "Följ oss",
    "geo.myLocation": "Min plats",
    "geo.nearMe": "Nära mig",
    "geo.permissionDenied": "Platsåtkomst nekad",
    "geo.detecting": "Identifierar plats...",
    // Trip planner
    "trip.editTrip": "Redigera resa",
    "trip.tripTitle": "Resans titel",
    "trip.destination": "Destination (sök adress...)",
    "trip.startDate": "Startdatum",
    "trip.consumption": "Förbrukning (l/100km)",
    "trip.consumptionEv": "Förbrukning (kWh/100km)",
    "trip.fuelPrice": "Dieselpris",
    "trip.fuelPriceBensin": "Bensinpris",
    "trip.fuelPriceEv": "Elpris",
    "trip.fuelPriceUnitEv": "/kWh",
    "trip.vehicleType": "Fordonstyp",
    "trip.vehicleDiesel": "Diesel",
    "trip.vehicleBensin": "Bensin",
    "trip.vehicleEv": "Elbil",
    "trip.travelMode": "Färdmedel",
    "trip.travelModeCar": "Bil",
    "trip.travelModeMotorhome": "Husbil",
    "trip.travelModeMotorcycle": "Motorcykel",
    "trip.travelModeDesc": "AI anpassar rutter och tips baserat på ditt fordon",
    "trip.priceCurrency": "Prisvaluta (valutan du anger priset i)",
    "trip.saveChanges": "Spara ändringar",
    "trip.calculatingRoute": "Beräknar rutt...",
    "trip.driveTime": "körtid",
    "trip.estimatedFuelCost": "Uppskattad bränslekostnad:",
    "trip.estimatedDiesel": "Uppskattad dieselkostnad:",
    "trip.estimatedChargeCost": "Uppskattad laddkostnad:",
    "trip.openGoogleMaps": "Öppna i Google Maps",
    "trip.tankSize": "Tankvolym (l)",
    "trip.batterySize": "Batteri (kWh)",
    "trip.rangeAnalysis": "Räckviddsanalys",
    "trip.rangePerFill": "Räckvidd per tank",
    "trip.chargingStops": "Laddningsstopp",
    "trip.fuelStops": "Tankstopp",
    "trip.avgLeg": "Snitt per sträcka",
    "trip.costPerCharge": "Kostnad per laddning",
    "trip.costPerFill": "Kostnad per tankning",
    "trip.evStopNote": "Du behöver ladda ungefär {stops} gånger, ungefär var {km}:e km.",
    "trip.fuelStopNote": "Du behöver tanka ungefär {stops} gånger, ungefär var {km}:e km.",
    "trip.noStopsNeeded": "Din räckvidd täcker hela resan utan stopp!",
    "trip.routeAlternatives": "Ruttalternativ",
    "trip.route": "Rutt",
    "trip.addMin2Stops": "Lägg till minst 2 stopp för bränsleberäkning",
    "trip.addStop": "Lägg till stopp",
    "trip.newStop": "Nytt stopp",
    "trip.searchPlace": "Sök plats eller adress...",
    "trip.notesOptional": "Anteckningar (valfritt)",
    "trip.saving": "Sparar...",
    "trip.add": "Lägg till",
    "trip.cancel": "Avbryt",
    "trip.dragToReorder": "Dra och släpp för att ändra ordning",
    "trip.navigate": "Navigera hit",
    "trip.startNavigation": "Starta navigering",
    "trip.uploading": "Laddar upp...",
    "trip.writeExperience": "Skriv om din upplevelse här...",
    "trip.save": "Spara",
    "trip.tripExperience": "Reseupplevelse",
    "trip.edit": "Redigera",
    "trip.write": "Skriv",
    "trip.describeTripExperience": "Beskriv din resa, vad du upplevde, tips till andra...",
    "trip.tripUpdated": "Resa uppdaterad!",
    "trip.linkCopied": "Länk kopierad!",
    "trip.shareLinkDesc": "Dela länken med dina vänner.",
    "trip.couldNotShare": "Kunde inte dela",
    "trip.imageUploaded": "Bild uppladdad!",
    "trip.couldNotUpload": "Kunde inte ladda upp bild",
    "trip.experienceSaved": "Upplevelse sparad!",
    "trip.stopAdded": "Stopp tillagt!",
    "trip.couldNotAddStop": "Kunde inte lägga till stopp",
    "trip.orderUpdated": "Ordning uppdaterad!",
    "trip.fuelCalc": "Bränsleberäkning (diesel)",
    "trip.currency": "Valuta",
    "trip.stopType": "Stopptyp",
    "trip.stopTypeVisit": "Kort besök",
    "trip.stopTypeOvernight": "Övernattning",
    "trip.stopTypeDestination": "Slutdestination",
    "trip.nightsCount": "Antal nätter",
    "trip.editStop": "Redigera stopp",
    "trip.optimizeRoute": "Optimera rutt",
    "trip.outboundLeg": "Utresa",
    "trip.returnLeg": "Hemresa",
    "trip.returnLegDesc": "Returresan hem",
    "trip.addReturnStop": "Lägg till stopp på hemresan",
    "trip.optimizeReturn": "Optimera hemresan",
    "trip.returnOptimized": "Hemresan har optimerats!",
    "trip.noReturnStops": "Inga returstopp ännu. Lägg till stopp för hemresan.",
    "trip.markAsReturn": "Markera som hemresa",
    "trip.markAsOutbound": "Markera som utresa",
    "trip.routeOptimized": "Rutten har optimerats!",
    "optimize.title": "Optimera stoppordning",
    "optimize.description": "Jämför olika ruttalternativ och välj det som passar din resa bäst.",
    "optimize.analyzing": "Analyserar ruttalternativ…",
    "optimize.currentOrder": "Nuvarande ordning",
    "optimize.currentDesc": "Din nuvarande stoppordning som planerad.",
    "optimize.geographic": "Mest logiska rutt",
    "optimize.geographicDesc": "Stopp sorterade för naturligt geografiskt flöde mot destinationen.",
    "optimize.shortest": "Kortaste rutten",
    "optimize.shortestDesc": "Närmaste-granne-sortering för att minimera total körsträcka.",
    "optimize.dayOptimized": "Dagsetapp-optimerad",
    "optimize.dayOptimizedDesc": "Organiserad kring övernattningsstopp för logiska dagsetapper.",
    "optimize.recommended": "Rekommenderad",
    "optimize.stopOrder": "Stoppordning",
    "optimize.start": "Start",
    "optimize.end": "Destination",
    "optimize.overnights": "övernattningar",
    "optimize.shorter": "kortare",
    "optimize.longer": "längre",
    "optimize.saves": "sparar",
    "optimize.useThisRoute": "Använd denna rutt",
    "optimize.cancel": "Avbryt",
    "tip.notFound": "Resan hittades inte",
    "tip.notFoundDesc": "Den kanske inte längre är tillgänglig.",
    "tip.back": "Tillbaka",
    "tip.traveler": "Resenär",
    "tip.sharedTrip": "Delad resa",
    "tip.planThisTrip": "Planera denna resa",
    "tip.aboutTrip": "Om denna resa",
    "tip.aiGenerated": "AI-genererad resepresentation",
    "tip.budget": "Budget",
    "tip.stops": "Stopp",
    "tip.places": "platser",
    "tip.duration": "Längd",
    "tip.days": "dagar",
    "tip.consumption": "Förbrukning",
    "tip.goodToKnow": "Bra att veta innan du åker",
    "tip.bestSeason": "Bästa säsong",
    "tip.currency": "Valuta",
    "tip.speed": "Hastighet",
    "tip.emergency": "Nödnummer",
    "tip.campingRules": "Campingregler",
    "tip.route": "Rutten",
    "tip.endOfTrip": "Resans slut",
    "tip.wantSameRoute": "Vill du åka samma rutt?",
    "tip.cloneDesc": "Vi skapar en helt ny resa åt dig med samma stopp. Du kan sedan anpassa den efter dina egna önskemål — ändra start, lägga till stopp och planera budget.",
    "tip.needAccount": "Du behöver ett konto för att spara resan",
    "tip.tripCreated": "Resa skapad! 🚐 Nu kan du anpassa den.",
    "tip.couldNotCreate": "Kunde inte skapa resan",
    "tip.home": "Hem",
    "tip.travelTips": "Resetips",
    "tip.startPoint": "Startpunkt",
    "tip.endPoint": "Slutpunkt",
    "tip.travelGuide": "Reseguide",
    "tip.hide": "Dölj",
    "tip.sharedThisTrip": "delade denna resa",
    "tip.explore": "Utforska",
    "tip.inspirationTitle": "Inspiration från resenärer",
    "tip.inspirationDesc": "Upptäck rutter delade av andra resenärer",
    "tip.tipHidden": "Resetipset har dolts från startsidan",
    "tip.couldNotHide": "Kunde inte dölja resetipset",
    "tip.publishAsTip": "Publicera som resetips",
    "tip.publishDesc": "Visa din resa på startsidan som inspiration för andra",
    "tip.creatingAi": "Skapar AI-resepresentation... ✨",
    "tip.creatingAiDesc": "Detta kan ta en stund.",
    "tip.publishedWithAi": "Resan är nu publicerad med AI-guide! 🌍✨",
    "tip.published": "Resan är nu synlig för alla! 🌍",
    "tip.unpublished": "Resan är nu privat",
    "tip.publishedNoAi": "Resan är publicerad! (AI-guide kunde inte skapas)",
    "tip.routeHighlights": "Tips längs rutten",
    "tip.routeHighlightsDesc": "Rekommenderade platser och höjdpunkter för din resa",
    "tip.openInMaps": "Öppna i kartor",
    "tip.saveMoney": "Spara pengar",
    "tip.fuelEstimate": "Bränslekostnad",
    "tip.campingCost": "Camping",
    "tip.night": "natt",
    "tip.yourDescription": "Din beskrivning (valfritt)",
    "tip.yourDescriptionPlaceholder": "Berätta vad som gör denna rutt speciell, tips till andra, eller varför du gillade den...",
    // Submit
    "submit.loginRequired": "Inloggning krävs",
    "submit.loginDesc": "Du måste vara inloggad för att registrera en plats.",
    "submit.title": "Registrera en plats",
    "submit.subtitle": "Dela din favoritplats med gemenskapen",
    "submit.reviewNote": "Registrerade platser granskas innan publicering.",
    "submit.basicInfo": "Grundläggande information",
    "submit.name": "Namn",
    "submit.type": "Typ",
    "submit.country": "Land",
    "submit.region": "Region",
    "submit.city": "Stad",
    "submit.description": "Beskrivning",
    "submit.shortDescription": "Kort beskrivning",
    "submit.fullDescription": "Fullständig beskrivning",
    "submit.priceSchedule": "Pris & Schema",
    "submit.freeSpot": "Detta är en gratis plats",
    "submit.pricePerNight": "Pris per natt (€)",
    "submit.imageContact": "Bilder & Kontakt",
    "submit.imageUrl": "Bild-URL",
    "submit.phone": "Telefon",
    "submit.email": "E-post",
    "submit.website": "Webbsida",
    "submit.address": "Adress",
    "submit.fullAddress": "Fullständig adress",
    "submit.searchAddress": "Sök adress...",
    "submit.amenities": "Bekvämligheter",
    "submit.photos": "Bilder",
    "submit.photosDesc": "Ladda upp max 5 bilder",
    "submit.submit": "Registrera plats",
    "submit.submitting": "Registrerar...",
    "submit.success": "Registrerad!",
    "submit.successDesc": "Din plats kommer att granskas av en admin.",
    "submit.missingFields": "Saknade fält",
    "submit.missingFieldsDesc": "Namn, land och region krävs.",
    "submit.ctaTitle": "Vet du en bra plats?",
    "submit.ctaDesc": "Tipsa oss om en camping eller ställplats",
    "home.featuresTrustTitle": "Verifierade platser",
    "home.featuresTrustDesc": "Riktiga campingar och ställplatser med priser, recensioner och allt du behöver veta innan du åker.",
    "home.featuresRoutesTitle": "Smarta rutter",
    "home.featuresRoutesDesc": "Se hela resan i en vy. Stopp, körtider, bränslekostnad och alternativa vägar direkt på kartan.",
    "home.featuresBudgetTitle": "Resebudget",
    "home.featuresBudgetDesc": "Sätt en budget, logga vad du spenderar och se exakt vart pengarna tar vägen. Kategori för kategori.",
    "home.featuresChecklistTitle": "Checklista",
    "home.featuresChecklistDesc": "Checklistor anpassade efter din restyp. Gasol, vatten, pass, laddare. Allt på ett ställe.",
    "home.featuresAITitle": "AI-assistent",
    "home.featuresAIDesc": "Fråga Vannie vad som helst om din resa. Rutter, regler, tips och lokal kunskap. Alltid tillgänglig.",
    "home.sectionTitle": "Se hur det fungerar",
    "home.sectionSubtitle": "Från första idén till resan på väg. Allt på ett ställe.",
    "home.trustPlaces": "2 400+ platser",
    "home.trustEurope": "Hela Europa",
    "home.videoOverlay1": "Planera din rutt. Hitta de bästa platserna. Ge dig ut.",
    "home.videoOverlay2": "Allt du behöver för den perfekta roadtrippen.",
    "home.lifestyleTitle": "Din resa. Dina regler.",
    "home.lifestyleSubtitle": "Rutter, stopp, budget och packlista. Klart på sekunder.",
    "home.lifestyleCta": "Börja planera",
    "home.vannieTitle": "Möt Vannie. Din personliga reseguide.",
    "home.vannieDesc": "Ställ en fråga, få ett rakt svar. Bästa platserna, lokala tips, ruttförslag och regler per land. Vannie vet. Och det kostar ingenting.",
    "home.vannieCta": "Chatta med Vannie",
    "home.vannieCtaSignup": "Bli medlem och chatta med Vannie",
    "home.vannieRoutes": "Ruttplanering",
    "home.vannieCamping": "Campingtips",
    "home.vannieRules": "Lokala regler",
    "home.vannieBudget": "Budgethjälp",
    "nav.services": "Service & SOS",
    "nav.games": "Roadtripspel",
    "nav.vannie": "Vannie",
    "nav.community": "Community",
    "nav.profile": "Min profil",
    // Camping card
    "card.free": "GRATIS",
    "card.night": "natt",
    "card.loginForPrice": "Logga in för pris",
    // Quick categories
    "cat.campings": "Campingar",
    "cat.stellplatz": "Ställplats",
    "cat.freeSpots": "Gratis platser",
    // Map
    "map.noStopsToShow": "Inga stopp med koordinater att visa",
    // Wizard
    "wizard.whereToGo": "Vart vill du åka?",
    "wizard.searchDestination": "Ange destination eller stad…",
    "wizard.startPoint": "Var startar du ifrån?",
    "wizard.finalDay": "Slutmål",
    "wizard.overnight": "Övernattning",
    "wizard.viewAllAccommodations": "Visa alla",
    "wizard.accommodationNear": "Boende nära",
    "wizard.tapToChooseAccommodation": "Välj boende nedan.",
    "wizard.accommodationMissingHint": "Du har planerat en övernattning här men inget boende valt.",
    "wizard.accommodationMissingTitle": "Boende saknas",
    "wizard.accommodationMissingSubtitle": "Tryck för att välja boende för varje dag.",
    "wizard.detectedAccommodationTitle": "Verkar vara ett boende",
    "wizard.detectedAccommodationDesc": "ser ut att vara ett boende. Hur vill du markera det?",
    "wizard.overnightHint": "Du sover här",
    "wizard.visitHint": "Bara genomfart eller besök",
    "wizard.destinationHint": "Resans slutmål",
    "wizard.optimizeOrder": "Optimera ordning",
    "wizard.whenTravel": "När reser du?",
    "wizard.myLocation": "Min plats",
    "wizard.chooseManually": "Välj manuellt",
    "wizard.searchStartPoint": "Sök startpunkt...",
    "wizard.locationDetected": "Plats hittad",
    "wizard.start": "Start",
    "wizard.destination": "Destination",
    "wizard.yourStops": "Dina stopp",
    "wizard.stop": "Stopp",
    "wizard.addedAsStop": "tillagd som stopp!",
    "campingDetector.title": "Det här ser ut som en camping!",
    "campingDetector.aiDetected": "AI upptäckte",
    "campingDetector.description": "Vill du lägga till platsen i Vantastika så andra resenärer kan hitta den?",
    "campingDetector.pendingNote": "Platsen granskas av en admin innan den blir publik.",
    "campingDetector.confidence": "träff",
    "campingDetector.typeCamping": "Camping",
    "campingDetector.typeStellplatz": "Ställplats",
    "campingDetector.typeFreeParking": "Gratisparkering",
    "campingDetector.typeLodging": "Boende",
    "campingDetector.notNow": "Inte nu",
    "campingDetector.registerCta": "Lägg till i Vantastika",
    "campingDetector.registering": "Lägger till…",
    "campingDetector.registeredTitle": "Tack för bidraget!",
    "campingDetector.registeredDesc": "Platsen är inskickad och publiceras efter granskning.",
    "campingDetector.alreadyExists": "Finns redan i Vantastika",
    "campingDetector.alreadyExistsDesc": "Den här platsen är redan registrerad.",
    "campingDetector.couldNotRegister": "Kunde inte registrera platsen",
    "campingDetector.loginRequired": "Inloggning krävs",
    "campingDetector.loginRequiredDesc": "Logga in för att bidra med nya platser.",
    "campingDetector.cannotRegister": "Kan inte registrera",
    "campingDetector.missingPlaceId": "Saknar plats-ID från Google.",
    "stopType.visit": "Besök",
    "stopType.overnight": "Övernattning",
    "stopType.destination": "Slutmål",
    "stopType.nights": "Nätter",
    "stopType.stayHours": "Stannar (h)",
    "wizard.suggestedStops": "Campingar längs rutten",
    "wizard.findingCampings": "Hittar campingar längs rutten...",
    "wizard.noCampingsNearRoute": "Inga campingar hittades nära denna rutt",
    "wizard.saveTrip": "Skapa min resa",
    "wizard.stepPlanningMode": "Planering",
    "wizard.stepDrivingHours": "Körning",
    "wizard.stepTravelStyle": "Resestil",
    "wizard.stepCountryInfo": "Länder",
    "wizard.howToPlan": "Hur vill du planera din resa?",
    "wizard.howToPlanDesc": "Välj det alternativ som passar dig bäst.",
    "wizard.planModeKnow": "Jag vet vart jag ska",
    "wizard.planModeKnowDesc": "Jag har en destination klar och vill planera rutten dit.",
    "wizard.planModeHelp": "Hjälp mig hitta en resa",
    "wizard.planModeHelpDesc": "Jag vill ha inspiration och AI-förslag på resor.",
    "wizard.planModeOptimize": "Optimera min rutt smart",
    "wizard.planModeOptimizeDesc": "Jag vet ungefär vart jag ska men vill ha bästa etapper och stopp.",
    "wizard.stepDetails": "Detaljer",
    "wizard.stepTrip": "Resa",
    "wizard.stepPreferences": "Preferenser",
    "wizard.dailyDrivingTitle": "Hur länge vill du köra per dag?",
    "wizard.dailyDrivingDesc": "Vi anpassar etapperna efter ditt val.",
    "wizard.recommended": "Rekommenderat",
    "wizard.travelStyleTitle": "Hur vill du resa?",
    "wizard.travelStyleDesc": "Vi anpassar rutten och förslagen efter din resestil.",
    "wizard.styleFastest": "Snabbaste vägen",
    "wizard.styleFastestDesc": "Prioritera kortast körtid via motorväg.",
    "wizard.styleSafe": "Trygg och lugn resa",
    "wizard.styleSafeDesc": "Lugnare vägar med rimlig daglig körning.",
    "wizard.styleScenic": "Naturskön väg",
    "wizard.styleScenicDesc": "Prioritera vackra vägar och utsikter.",
    "wizard.styleCamping": "Campingvänlig rutt",
    "wizard.styleCampingDesc": "Rutt med bra tillgång till campingplatser.",
    "wizard.styleBudget": "Budgetvänlig resa",
    "wizard.styleBudgetDesc": "Undvik vägavgifter och dyra stopp.",
    "wizard.styleFamily": "Barnvänlig resa",
    "wizard.styleFamilyDesc": "Kortare etapper med barnanpassade stopp.",
    "wizard.selectTravelStyle": "Välj en resestil",
    "wizard.longDrivingWarning": "Längre körning per dag kan påverka trafiksäkerheten negativt. Vi rekommenderar max 4–5 timmar per dag.",
    "wizard.skipOvernightHere": "Hoppa över övernattning – fortsätt köra",
    "wizard.resetStages": "Återställ etapper",
    "wizard.passThrough": "Genomresa",
    "wizard.noOvernightShort": "Ingen övernattning",
    "wizard.mergedDayTitle": "Du kör genom utan att övernatta",
    "wizard.mergedDayDesc": "Den här dagen är sammanslagen med nästa. Korta stopp för paus och tankning rekommenderas.",
    "wizard.undoSkipOvernight": "Ångra – övernatta här igen",
    "wizard.selectPlanningMode": "Välj hur du vill planera",
    "wizard.accommodationTitle": "Boendeförslag",
    "wizard.accommodationDescCamping": "Visar ställplatser och campingar längs din rutt.",
    "wizard.accommodationDescCar": "Visar hotell och boenden nära din destination.",
    "wizard.campingRules": "Campingregler",
    "wizard.countriesInRouteOrder": "Länder visas i ruttordning",
    "wizard.countryInfoDesc": "Praktisk information för varje land du passerar.",
    "wizard.countryInfoTitle": "Länder längs din rutt",
    "wizard.startCountry": "Startland",
    "wizard.transitCountry": "Transitland",
    "wizard.destinationCountry": "Destination",
    "wizard.ferryAlternatives": "Färjealternativ till bilväg",
    "wizard.ferryDuration": "Restid",
    "wizard.ferryRoute": "Sträcka",
    "wizard.ferryRelevance": "Varför relevant",
    "wizard.driving": "Körning",
    "wizard.drivingRules": "Körregler",
    "wizard.fromRoute": "Längs rutten",
    "wizard.fuelInfo": "Bränsle",
    "wizard.importantForMotorhome": "viktigt för husbil",
    "wizard.infoItems": "infopunkter",
    "wizard.loadingCountryInfo": "Hämtar landinformation...",
    "wizard.motorhomeMultiCountryTip": "Med husbil kan regler skilja sig kraftigt mellan länder. Kontrollera miljözoner, vinjetter och tillåtna mått innan avfärd.",
    "wizard.noCountryInfo": "Ingen landinformation tillgänglig. Ange rutt först.",
    "wizard.noResultsForStage": "Inga resultat för denna etapp",
    "wizard.oneWay": "Enkel resa",
    "wizard.optimizeRoute": "Optimera stoppordning",
    "wizard.searchingStage": "Söker för etapp",
    "wizard.selectDestForAccommodation": "Välj destination först för boendeförslag.",
    "wizard.shortTripNoOvernight": "Kort resa – ingen övernattning behövs.",
    "wizard.skipStep": "Hoppa över detta steg",
    "wizard.from": "Från",
    "wizard.to": "Till",
    "wizard.selectedAccommodation": "Valt boende",
    "wizard.changeAccommodation": "Byt",
    "wizard.selected": "Vald",
    "wizard.selectAccommodation": "Välj",
    "wizard.noAccommodationYet": "Inget boende valt ännu. Du kan välja i nästa steg.",
    "wizard.viewOnMap": "Visa på karta",
    "common.close": "Stäng",
    "wizard.speedLimits": "Hastighetsgränser",
    "wizard.stages": "etapper",
    "wizard.tolls": "Vägavgifter & vinjetter",
    "wizard.wildCamping": "Vildcamping",
    "wizard.drivingHoursPerDay": "Körtid per dag",
    "wizard.countriesOnRoute": "Länder längs rutten",
    "wizard.emergency": "Nödnummer",
    "wizard.longDrivingTitle": "Lång dagsetapp",
    "wizard.safeDrivingNote": "Bra val! Lugnare dagsetapper ger en tryggare och mer njutbar resa med tid att utforska längs vägen.",
    "wizard.stepRoute": "Utresa",
    "wizard.stepReturn": "Hemresa",
    "wizard.stepAccommodation": "Boende",
    "wizard.stepVehicle": "Fordon",
    "wizard.stepOverview": "Översikt",
    "wizard.next": "Nästa",
    "wizard.back": "Tillbaka",
    "wizard.tripTypeLabel": "Restyp",
    "wizard.accommodationDesc": "Här är boendeförslag baserade på din rutt och ditt färdsätt. Lägg till som stopp eller hoppa över.",
    "wizard.showingCampingsAlongRoute": "Visar ställplatser längs din rutt",
    "wizard.showingHotelsNearDest": "Visar hotell nära din destination",
    "wizard.showingAccommodationNearDest": "Visar boende längs din rutt",
    "wizard.searchingAlongRoute": "Söker längs din rutt...",
    "wizard.expandingSearch": "Utökar sökområdet...",
    "wizard.tryingAlternatives": "Provar alternativa boenden...",
    "wizard.noAccommodationFound": "Inga boenden hittades nära din rutt. Du kan hoppa över detta steg och lägga till stopp manuellt senare.",
    "wizard.returnTripDesc": "Välj hur du vill resa tillbaka.",
    "wizard.sameRouteDesc": "Kör tillbaka samma väg",
    "wizard.scenicDesc": "Alternativ scenisk väg",
    "wizard.fastestDesc": "Snabbaste vägen hem",
    "wizard.routeSummary": "Rutt",
    "wizard.selectDestFirst": "Välj destination först",
    "wizard.selectStartFirst": "Välj startplats först",
    "wizard.fuelType": "Bränsletyp",
    "wizard.estimatedFuelCostLabel": "Uppskattad bränslekostnad",
    "wizard.showPricesIn": "Visa priser i",
    "wizard.perNight": "natt",
    "wizard.moreDetails": "Fler detaljer",
    "wizard.firstTimeHint": "Börja med att söka efter ditt resmål. Välj sedan fordon, datum och stopp — rutten beräknas automatiskt!",
    "wizard.saving": "Sparar...",
    "wizard.budgetOptional": "Ange budget (valfritt)",
    "wizard.budgetPlaceholder": "t.ex. 15000",
    "wizard.fuelAutoAdded": "Uppskattad bränslekostnad tillagd i budgeten",
    "wizard.planNewTrip": "Skapa min resa",
    "wizard.myTrips": "Mina sparade resor",
    "wizard.planTrip": "Planera resa",
    "wizard.or": "eller",
    "wizard.popularDestinations": "Populärt",
    "wizard.describeTrip": "Beskriv din resa – vi planerar allt åt dig",
    "wizard.describeSubtext": "Skriv fritt vad du vill göra så bygger vi förslaget",
    "wizard.backToManual": "Tillbaka till manuell sökning",
    "wizard.selectSuggestion": "Välj ett förslag från listan för att gå vidare.",
    "wizard.analyzingTrip": "Analyserar din resa…",
    "wizard.createPlan": "Skapa reseplan",
    "wizard.suggestedTrip": "Föreslagen resa",
    "wizard.places": "platser",
    "wizard.startOver": "Börja om",
    "wizard.noDestFound": "Inga destinationer hittades",
    "wizard.noDestFoundDesc": "Försök beskriva din resa mer detaljerat.",
    "wizard.couldNotParse": "Kunde inte tolka resan",
    "wizard.couldNotParseDesc": "Försök igen med en tydligare beskrivning.",
    "wizard.tooManyRequests": "För många förfrågningar",
    "wizard.tooManyRequestsDesc": "Vänta en stund och försök igen.",
    "wizard.aiCreditOut": "AI-kredit slut",
    "wizard.aiCreditOutDesc": "Kontakta support för mer information.",
    "wizard.tripSaved": "Resan sparad!",
    "wizard.tripSavedDesc": "Gå till Mina resor för att se den.",
    "wizard.couldNotReachVannie": "Kunde inte nå Vannie just nu.",
    "wizard.describePlaceholder": "Beskriv din resa med egna ord — vi skapar en komplett reseplan åt dig.",
    "wizard.chars": "tecken",
    "wizard.destinationConfirmed": "Destination vald",
    "wizard.locating": "Söker koordinater...",
    "wizard.destType": "Mål",
    "wizard.visitType": "Besök",
    "wizard.aiSuggestTitle": "AI Resförslag",
    "wizard.aiSuggestOpen": "Beskriv din resa – vi planerar allt åt dig",
    "wizard.aiSuggestPlaceholder": "T.ex. 'Jag vill till stranden i 5 dagar' eller 'Mysig bergsresa med vandring i alperna'",
    "wizard.vannieThinking": "Vannie tänker...",
    "wizard.suggestTrip": "Föreslå resa",
    "wizard.suggestedStopsLabel": "Föreslagna stopp",
    "wizard.useThisSuggestion": "Använd detta förslag",
    "wizard.couldNotGenerate": "Kunde inte generera förslag",
    "wizard.days": "dagar",
    "wizard.perDay": "/dag",
    "wizard.roundtrip": "Tur & retur",
    "wizard.oneway": "Enkel resa",
    "wizard.departure": "Avresa",
    "wizard.return": "Hemresa",
    "wizard.selectDate": "Välj datum",
    "wizard.departureDateOptional": "Avresedatum (valfritt)",
    "wizard.day": "dag",
    "wizard.dayLabel": "Dag {n}",
    "wizard.roundtripLabel": "Tur & retur",
    "wizard.estimatedTravelTime": "Uppskattad restid",
    "wizard.total": "totalt",
    "wizard.stopsLabel": "stopp",
    "wizard.fetchingReturnTips": "Hämtar tips för hemresan…",
    "wizard.returnTips": "Tips för hemresan",
    "wizard.roundtripShort": "t&r",
    "wizard.driveTime": "Körtid",
    "wizard.chargeCost": "Laddkostnad",
    "wizard.fuelCost": "Bränslekostnad",
    
    "wizard.distance": "Sträcka",
    "wizard.chooseRoute": "Välj denna rutt",
    "wizard.ferry": "Färja",
    "wizard.maxDrivingHours": "Max körtid per dag",
    "wizard.longDriveWarning": "Lång körtid",
    "wizard.longDriveDesc": "Vi rekommenderar max 4 timmar för säker och bekväm körning.",
    "wizard.drivingDaysCalc": "{hours}h/dag = ~{days} kördagar",
    "wizard.drivingDaysRoundtrip": "dagar tur & retur",
    "wizard.vehicleSettings": "Fordon & bränsle",
    "wizard.scenic": "Naturskönt",
    "wizard.fastest": "Snabbast",
    "wizard.balanced": "Balanserat",
    "wizard.sameRoute": "Samma väg",
    "wizard.returnTrip": "Hemresa",
    "wizard.homecoming": "Hemkomst",
    "wizard.optimizationSummary": "Rutt optimerad",
    "wizard.stageOverview": "Dagsetapper",
    "wizard.overnightIn": "Övernattning nära",
    "wizard.arrivalAt": "Framme vid",
    "wizard.returnRouteOption": "Hemresa",
    "wizard.hours": "h",
    "wizard.addedAsOvernight": "tillagd som övernattning!",
    "wizard.addedAsRestStop": "tillagd som raststopp!",
    "wizard.stopAddedFromMap": "Stopp tillagt från kartan!",
    "wizard.updatedStop": "Uppdaterat stopp",
    "wizard.stopPositionUpdated": "Stoppets position uppdaterad",
    "wizard.newStop": "Nytt stopp",
    "wizard.optimizedDesc": "Stoppen omsorterade för kortast totalavstånd",
    "wizard.newOrder": "Ny ordning",
    "wizard.orderChanged": "Ordningen ändrad från original",
    "wizard.routeOptimized": "Rutten optimerad!",
    "wizard.nameError": "Kunde inte generera namnförslag",
    "wizard.mapInstructions": "Klicka på kartan för att lägga till stopp, dra för att flytta",
    "wizard.nameTrip": "Namnge din resa",
    "wizard.optional": "valfritt",
    "wizard.namePlaceholder": "t.ex. Sommar i Alperna",
    "wizard.aiSuggest": "AI-förslag",
    "wizard.signupAndSave": "Skapa konto & spara",
    "wizard.useTrip": "Använd denna resa",
    "wizard.dragToReorder": "Dra för att ändra ordning",
    "wizard.prefillTip": "Förifyllt från ett Vantastika-tips",
    "wizard.prefillGuide": "Förifyllt från en reseguide",
    "budget.title": "Resebudget",
    "budget.tab": "Budget",
    "budget.setBudget": "Ange budget",
    "budget.totalBudget": "Total budget (kr)",
    "budget.spent": "Spenderat",
    "budget.remaining": "Återstår",
    "budget.used": "använt",
    "budget.topCategory": "Dyraste kategori",
    "budget.addExpense": "Lägg till utgift",
    "budget.amount": "Belopp",
    "budget.description": "Beskrivning (valfritt)",
    "budget.save": "Spara",
    "budget.cancel": "Avbryt",
    "budget.expenses": "Utgifter",
    "budget.breakdown": "Kategorifördelning",
    "budget.expenseAdded": "Utgift tillagd!",
    "budget.expenseUpdated": "Utgift uppdaterad!",
    "budget.expenseFailed": "Kunde inte spara utgift",
    "budget.autoLabel": "Automatiskt beräknad",
    "budget.editFuelCalc": "Redigera",
    "budget.fuelCalcTitle": "Bränsleberäkning",
    "budget.fuelPriceLabel": "Pris/liter",
    "budget.fuelConsumptionLabel": "l/100km",
    "budget.fuelDistanceLabel": "Sträcka (km)",
    "budget.fuelCalcResult": "Uppskattad bränslekostnad:",
    "budget.fuelSave": "Uppdatera bränslekostnad",
    "budget.fuelUpdated": "Bränslekostnad uppdaterad!",
    "budget.budgetSaved": "Budget sparad!",
    "budget.getAiTips": "Få budgettips från Vannie",
    "budget.aiTipsTitle": "Vannies budgettips",
    "budget.noTips": "Inga tips tillgängliga just nu.",
    "budget.tipsFailed": "Kunde inte hämta tips",
    "budget.close": "Stäng",
    "budget.cat.fuel": "Bränsle",
    "budget.cat.camping": "Camping/Ställplats",
    "budget.cat.food": "Mat & dryck",
    "budget.cat.tolls": "Vägavgifter/Färjor",
    "budget.cat.activities": "Aktiviteter",
    "budget.cat.shopping": "Shopping",
    "budget.cat.other": "Övrigt",
    "budget.budgetLabel": "Budget",
    "budget.edit": "Ändra",
    "budget.backToTrip": "Tillbaka till resan",
    "budget.estimatedFuel": "Uppskattad bränslekostnad",
    "budget.estimateOnly": "Endast uppskattning",
    "budget.autoCosts": "Automatiska kostnader",
    "budget.manualExpenses": "Dina utgifter",
    "budget.pageDesc": "Hantera dina budgetar och utgifter",
    "budget.loginRequired": "Logga in för att använda budgetfunktionen",
    "budget.subtitle": "Dina budgetar och utgifter",
    "budget.pageSubtitle": "Planera din resebudget, lägg till utgifter och följ hur mycket du har kvar.",
    "budget.newBudget": "Ny budget",
    "budget.newExpense": "Ny utgift",
    "budget.tabActive": "Aktiva",
    "budget.tabAll": "Alla",
    "budget.tabExpenses": "Ej kopplade utgifter",
    "budget.tabUnlinked": "Ej kopplade",
    "budget.emptyTitle": "Inga budgetar ännu",
    "budget.emptyDesc": "Skapa din första budget för att få bättre koll på resans kostnader.",
    "budget.emptyStateTitle": "Ingen budget skapad ännu",
    "budget.emptyStateDesc": "Skapa din första resebudget för att få bättre koll på kostnader för boende, mat, bränsle och aktiviteter.",
    "budget.createBudget": "Skapa budget",
    "budget.createFirstBudget": "Skapa första budget",
    "budget.chipAccommodation": "Boende",
    "budget.chipFood": "Mat",
    "budget.chipFuel": "Bränsle",
    "budget.chipActivities": "Aktiviteter",
    "budget.chipShopping": "Shopping",
    "budget.unlinkedLabel": "Ej kopplade utgifter",
    "budget.unlinkedHint": "Dessa utgifter är inte kopplade till någon budget.",
    "budget.more": "till",
    "budget.noActiveTitle": "Inga aktiva budgetar",
    "budget.noActiveDesc": "Skapa en budget eller aktivera en befintlig.",
    "budget.overviewTitle": "Budgetöversikt",
    "budget.statusGood": "Bra läge",
    "budget.statusWarning": "Närmar sig gränsen",
    "budget.statusOver": "Över budget",
    "budget.pctUsed": "använt",
    "budget.totalBudgetCard": "Total budget",
    "budget.spentCard": "Spenderat",
    "budget.remainingCard": "Kvar",
    "budget.paused": "Pausade",
    "budget.completed": "avslutad(e)",
    "budget.noExpenses": "Inga ej kopplade utgifter",
    "budget.noExpensesDesc": "Utgifter som inte är kopplade till en budget visas här",
    "budget.setBudgetTitle": "Månadsbudget",
    "budget.setBudgetBtn": "Sätt budget",
    "budget.setBudgetSaved": "Budgetgräns sparad ✓",
    "budget.month": "Månad",
    "budget.budgetAmount": "Budget",
    "budget.amountPlaceholder": "t.ex. 15000",
    "budget.saving": "Sparar...",
    "budget.saveBudgetLimit": "Spara budgetgräns",
    "budget.createBudgetTitle": "Skapa budget",
    "budget.budgetName": "Budgetnamn",
    "budget.budgetNamePlaceholder": "t.ex. Sommarsemester 2026",
    "budget.budgetTypeLabel": "Budgettyp",
    "budget.totalBudgetLabel": "Total budget",
    "budget.currencyLabel": "Valuta",
    "budget.startDateLabel": "Startdatum",
    "budget.endDateLabel": "Slutdatum",
    "budget.colorLabel": "Färg",
    "budget.selectColor": "Välj färg",
    "budget.descriptionLabel": "Beskrivning",
    "budget.descriptionPlaceholder": "Valfri anteckning...",
    "budget.linkTrip": "Koppla till resa (valfritt)",
    "budget.noTripSelected": "Ingen resa vald",
    "budget.noTrip": "Ingen resa",
    "budget.fillNameAndAmount": "Fyll i namn och giltigt belopp",
    "budget.budgetCreated": "Budget skapad ✓",
    "budget.couldNotSave": "Kunde inte spara",
    "budget.creating": "Skapar...",
    "budget.addExpenseTitle": "Lägg till utgift",
    "budget.scanWithAi": "Skanna kvitto med AI",
    "budget.amountLabel": "Belopp",
    "budget.dateLabel": "Datum",
    "budget.titleLabel": "Rubrik",
    "budget.titlePlaceholder": "Vad köpte du?",
    "budget.categoryLabel": "Kategori",
    "budget.paymentMethod": "Betalningssätt",
    "budget.selectPayment": "Välj...",
    "budget.linkBudget": "Koppla till budget (valfritt)",
    "budget.linkBudgetDirect": "Koppla till budget",
    "budget.noBudgetSelected": "Ingen budget vald",
    "budget.noBudget": "Ingen budget",
    "budget.noteLabel": "Anteckning",
    "budget.notePlaceholder": "Valfri anteckning...",
    "budget.receiptLabel": "Kvitto",
    "budget.receiptRemove": "Ta bort",
    "budget.receiptUpload": "Fotografera eller ladda upp kvitto",
    "budget.fileTooLarge": "Filen är för stor",
    "budget.fileTooLargeDesc": "Max 15 MB",
    "budget.fillAmountAndTitle": "Fyll i giltigt belopp och rubrik",
    "budget.expenseSaved": "Utgift sparad ✓",
    "budget.converted": "Omräknat:",
    "budget.editBudgetTitle": "Redigera budget",
    "budget.budgetUpdated": "Budget uppdaterad ✓",
    "budget.saveChanges": "Spara ändringar",
    "budget.editExpenseTitle": "Redigera utgift",
    "budget.scanTitle": "Smart kvittoskanning",
    "budget.scanCapture": "Fotografera eller ladda upp kvitto",
    "budget.scanCaptureDesc": "AI:n analyserar kvittot rad för rad",
    "budget.scanTakePhoto": "Ta foto",
    "budget.scanUpload": "Ladda upp",
    "budget.scanAnalyzing": "Analyserar kvittot...",
    "budget.scanAnalyzingDesc": "AI:n läser av artiklar, belopp och kategorier",
    "budget.scanFailed": "Kunde inte analysera kvittot",
    "budget.scanConfidenceHigh": "Hög säkerhet",
    "budget.scanConfidenceMedium": "Medelhög säkerhet — granska fälten",
    "budget.scanConfidenceLow": "Låg säkerhet — kontrollera alla fält",
    "budget.scanCandidates": "Möjliga belopp:",
    "budget.scanItemsPerCategory": "Artiklar per kategori",
    "budget.scanMixedCategories": "Blandade kategorier",
    "budget.scanSaveHow": "Hur vill du spara?",
    "budget.scanSingleExpense": "En utgift",
    "budget.scanSplitExpenses": "Dela upp",
    "budget.scanSplitDesc": "Skapar {n} separata utgifter — en per kategori.",
    "budget.scanMainCategory": "Huvudkategori",
    "budget.scanShowRaw": "Visa rå kvittotext",
    "budget.scanHideRaw": "Dölj rå kvittotext",
    "budget.scanCancel": "Avbryt",
    "budget.scanSaveExpense": "Spara utgift",
    "budget.scanSaveExpenses": "Spara {n} utgifter",
    "budget.scanSavedMultiple": "{n} utgifter sparade från kvitto ✓",
    "budget.scanSavedSingle": "Utgift sparad från kvitto ✓",
    "budget.confidenceMedium": "Medelsäker",
    "budget.confidenceLow": "Osäker — kontrollera",
    "budget.statusPaused": "Pausad",
    "budget.statusCompleted": "Avslutad",
    "budget.spentLabel": "Spenderat",
    "budget.remainingLabel": "Kvar",
    "budget.overBy": "Överskriden med",
    "budget.usedPercent": "använd",
    "budget.expenseCount": "utgifter",
    "budget.mixedCurrenciesNote": "Utgifter i {currencies} – omräknat till {target}",
    "budget.deleteConfirm": "Vill du verkligen ta bort denna budget?",
    "budget.statusActivated": "Budget aktiverad",
    "budget.statusPausedToast": "Budget pausad",
    "budget.statusCompletedToast": "Budget avslutad",
    "budget.perCategory": "Per kategori",
    "budget.biggestExpense": "Störst utgift",
    "budget.expenseListTitle": "Utgifter",
    "budget.noExpensesYet": "Inga utgifter ännu",
    "budget.noExpensesYetDesc": "Tryck på \"Lägg till utgift\" för att börja",
    "budget.totalSpent": "Totalt spenderat",
    "budget.thisMonth": "Denna månad",
    "budget.budgeted": "Budgeterat",
    "budget.warnings": "Varningar",
    "summary.title": "Resekostnadssammanfattning",
    "summary.titleOngoing": "Kostnadsöversikt",
    "summary.over": "Över budget",
    "summary.saved": "Sparat",
    "summary.dailyAvg": "Per dag",
    "summary.overBudget": "Du gick över budget på denna resa",
    "summary.underBudget": "Du höll dig inom budget — bra jobbat!",
    "summary.breakdown": "Utgiftsfördelning",
    "summary.categoryDetails": "Kategoriöversikt",
    "fuelLog.title": "Bränslelogg",
    "fuelLog.addFuel": "Lägg till tankning",
    "fuelLog.liters": "Liter",
    "fuelLog.cost": "Kostnad",
    "fuelLog.date": "Datum",
    "fuelLog.odometer": "Mätarställning (km)",
    "fuelLog.note": "Anteckning",
    "fuelLog.optional": "Valfritt",
    "fuelLog.save": "Spara",
    "fuelLog.added": "Tankning registrerad!",
    "fuelLog.addFailed": "Kunde inte spara tankning",
    "fuelLog.invalidInput": "Ange både liter och kostnad",
    "fuelSummary.title": "Bränslesammanfattning",
    "fuelSummary.totalLiters": "Totalt liter",
    "fuelSummary.totalCost": "Bränslekostnad",
    "fuelSummary.avgPrice": "Snittpris/liter",
    "fuelSummary.consumption": "Förbrukning",
    "fuelSummary.costPerKm": "Kostnad/km",
    "fuelSummary.costPerMil": "Kostnad/mil",
    "fuelSummary.noDistance": "Lägg till ruttavstånd för att se förbrukning och kostnad per km.",
    "fuelSummary.basedOn": "Baserat på {n} tankning(ar)",
    "checklist.tab": "Checklista",
    "journal.tab": "Journal",
    "authGate.title": "Spara din resa och fortsätt när du vill",
    "authGate.description": "Skapa ett konto för att spara din resa, dina stopp och din budget så att du kan fortsätta senare.",
    "authGate.signup": "Skapa konto och spara",
    "authGate.login": "Logga in",
    "authGate.ctaSection": "Allt du behöver för din resa",
    "authGate.ctaPlaces": "Dina stopp och din rutt — sparade och synkade",
    "authGate.ctaRoutes": "Fortsätt planeringen på vilken enhet som helst",
    "authGate.ctaBudget": "Budget och kvitton — alltid tillgängliga",
    "authGate.ctaChecklist": "Checklistor som följer din resa",
    "authGate.ctaAI": "AI-assistent som kan varje väg",
    "authGate.ctaButton": "Skapa konto och spara",
    "authGate.saveTripMsg": "Spara din resa och fortsätt senare",
    "authGate.saveBudgetMsg": "Spara din budget och följ utgifterna",
    "authGate.saveReceiptMsg": "Spara kvitton på alla enheter",
    "authGate.planRouteMsg": "Fortsätt där du slutade",
    "authGate.savePlaceMsg": "Spara platsen i din samling",
    "authGate.saveFavoriteMsg": "Spara dina favoritplatser i en lista",
    "authGate.useBudgetMsg": "Håll koll på din budget — skapa ett gratis konto",
    "authGate.useChecklistMsg": "Använd checklistor — skapa ett gratis konto",
    "authGate.or": "eller",
    "authGate.hasAccount": "Har du redan konto?",
    "gate.whyJoin": "VARFÖR SKAPA KONTO?",
    "gate.benefit1": "Behåll din resa, dina stopp och din budget",
    "gate.benefit2": "Fortsätt planeringen på alla enheter",
    "gate.benefit3": "Full budgetkontroll med kvittoskanning",
    "gate.benefit4": "AI-reseassistent ingår gratis",
    "gate.benefit5": "Tillgång till community-tips och recensioner",
    "gate.benefit6": "Checklistor anpassade efter ditt fordon",
    "gate.freeForever": "Gratis att komma igång",
    "welcome.title": "Välkommen till Vantastika!",
    "welcome.titleName": "Välkommen, {name}!",
    "welcome.desc": "Allt du behöver för att planera, resa och minnas dina resor finns här.",
    "welcome.feature1": "Planera rutter, lägg till stopp och håll koll på budget och bränsle",
    "welcome.feature2": "Vannie, din AI-reseassistent, hjälper dig hitta de bästa rutterna",
    "welcome.feature3": "Spara favoritplatser, dela resor med vänner",
    "welcome.cta": "Planera min första resa",
    "welcome.dismiss": "Utforska själv",
    "welcome.close": "Stäng",
    "gate.noCard": "Gratis att komma igång. Inget kreditkort krävs.",
    "gate.createAccount": "Skapa gratis konto",
    "gate.alreadyMember": "Har du redan ett konto? Logga in",
    "gate.seeMore": "Vill du se mer?",
    "gate.registerToSee": "Skapa ett gratis konto för att få tillgång till alla funktioner och platser.",
    "gate.searchTitle": "Utforska alla campingplatser",
    "gate.searchDesc": "Registrera dig gratis för att bläddra bland alla verifierade platser, spara favoriter och läsa recensioner.",
    "gate.servicesTitle": "Service & nödhjälp",
    "gate.servicesDesc": "Skapa konto för att hitta verkstäder, laddstationer, vattenpåfyllning och akuttjänster nära dig.",
    "gate.tripsTitle": "Reseplanerare",
    "gate.tripsDesc": "Registrera dig gratis för att planera obegränsat med smarta rutter, bränslekostnader och budgetkontroll.",
    "gate.submitTitle": "Lägg till en plats",
    "gate.submitDesc": "Registrera dig gratis för att dela dina favoritplatser med communityn.",
    "gate.tipsTitle": "Resetips",
    "gate.tipsDesc": "Skapa konto för att läsa och dela resetips från riktiga resenärer.",
    "roadTips.title": "Resetips",
    "roadTips.share": "Dela ett tips",
    "roadTips.allCountries": "Alla länder",
    "roadTips.formTitle": "Dela ditt resetips",
    "roadTips.labelTitle": "Rubrik *",
    "roadTips.placeholderTitle": "t.ex. Bästa vägen genom Alperna",
    "roadTips.labelType": "Typ",
    "roadTips.labelCountry": "Land",
    "roadTips.select": "Välj",
    "roadTips.labelTip": "Ditt tips *",
    "roadTips.placeholderTip": "Dela din upplevelse...",
    "roadTips.labelImage": "Bild-URL (valfritt)",
    "roadTips.posting": "Publicerar...",
    "roadTips.post": "Publicera tips",
    "roadTips.shared": "Tips delat!",
    "roadTips.loginToShare": "Logga in för att dela dina egna resetips.",
    "roadTips.noTips": "Inga resetips ännu. Bli den första att dela!",
    "roadTips.type.general": "Allmänt tips",
    "roadTips.type.route": "Ruttrekommendation",
    "roadTips.type.parking": "Parkeringstips",
    "roadTips.type.food": "Mat & Shopping",
    "roadTips.type.warning": "Varning / Heads up",
    "roadTips.type.service": "Servicepunkt",
    "community.title": "Community",
    "community.subtitle": "Dela upplevelser, tips och inspiration med andra resenärer",
    "community.newPost": "Nytt inlägg",
    "community.createPost": "Skapa nytt inlägg",
    "community.all": "Alla",
    "community.anonymous": "Anonym",
    "community.noPostsYet": "Inga inlägg ännu",
    "community.beFirst": "Bli den första att starta en diskussion!",
    "community.loadMore": "Ladda fler inlägg",
    "community.loading": "Laddar…",
    "community.createFirst": "Skapa första inlägget",
    "community.back": "Tillbaka",
    "community.comments": "Kommentarer",
    "community.noComments": "Inga kommentarer ännu — bli den första!",
    "community.commentPlaceholder": "Skriv en kommentar...",
    "community.titlePlaceholder": "Rubrik",
    "community.contentPlaceholder": "Vad vill du diskutera?",
    "community.addImage": "Lägg till bild (valfritt)",
    "community.publishing": "Publicerar...",
    "community.publish": "Publicera inlägg",
    "community.toast.postPublished": "Ditt inlägg har publicerats.",
    "community.toast.postCreateError": "Kunde inte skapa inlägget.",
    "community.toast.commentError": "Kunde inte skapa kommentaren.",
    "community.toast.postDeleted": "Inlägg borttaget",
    "community.toast.error": "Fel",
    "community.confirmDelete": "Är du säker på att du vill ta bort detta inlägg?",
    "community.cat.general": "Allmänt",
    "community.cat.tips": "Tips & Tricks",
    "community.cat.routes": "Rutter & Platser",
    "community.cat.gear": "Utrustning",
    "community.cat.help": "Hjälp & Frågor",
    "community.cat.photos": "Bilder & Reseberättelser",
    "community.type.question": "Fråga",
    "community.type.tip": "Tips",
    "community.type.discussion": "Diskussion",
    "community.type.routes": "Rutter & platser",
    "community.type.gear": "Utrustning",
    "community.type.photos": "Bilder",
    "community.type.help": "Hjälp",
    "community.type.question.desc": "Ställ en fråga till communityn",
    "community.type.tip.desc": "Dela ett bra tips",
    "community.type.discussion.desc": "Starta en diskussion",
    "community.type.routes.desc": "Dela favoritplatser",
    "community.type.gear.desc": "Utrustning & tillbehör",
    "community.type.photos.desc": "Dela din resa",
    "community.type.help.desc": "Be om hjälp",
    "community.awaitingAnswer": "Väntar på svar",
    "community.selectType": "Välj typ av inlägg",
    "community.createPostTitle": "Skapa inlägg",
    "community.changeType": "Byt typ",
    "community.questionPlaceholder": "Skriv din fråga här...",
    "community.questionContentPlaceholder": "Beskriv din fråga i detalj så att andra kan hjälpa dig...",
    "community.communityRules": "Communityregler",
    "community.communityRulesText": "Du får inte publicera nakenbilder, sexuellt innehåll, reklam, annonser, försäljning, spam, hot eller trakasserier. Alla inlägg och bilder kan granskas automatiskt innan publicering.",
    "games.seoTitle": "Roadtrip-spel – Vantastika",
    "games.seoDesc": "Spela quiz, bingo och fler spel under din roadtrip",
    "demo.phase1Eyebrow": "För många val",
    "demo.phase1Title": "Se problemet direkt",
    "demo.phase1Text": "Kartan är full av platser och det är svårt att veta vad som faktiskt är värt stoppet.",
    "demo.phase2Eyebrow": "Ren planering",
    "demo.phase2Title": "Välj bara verifierade platser",
    "demo.phase2Text": "Vantastika filtrerar bort bruset och gör planeringen tydlig, snabb och trygg.",
    "demo.phase3Eyebrow": "Färdig roadtrip",
    "demo.phase3Title": "Din rutt, redo att köra",
    "demo.phase3Text": "Se hela rutten, viktiga stopp och en resa som känns klar innan du ens har startat motorn.",
    "demo.stepStart": "Starta din resa",
    "demo.stepVerified": "Verifierade stopp",
    "demo.stepArrive": "Framme vid målet",
    "demo.planSmart": "Planera smart",
    "demo.budget": "Budget",
    "demo.budgetUsed": "använt",
    "demo.roadtripPreview": "Resförhandsvisning",
    "service.title": "Service & Nödhjälp",
    "service.workshop": "Verkstad",
    "service.gasStation": "Bensinstation",
    "service.water": "Vatten",
    "service.charging": "Laddning",
    "service.waste": "Tömning",
    "service.hospital": "Sjukhus",
    "service.police": "Polis",
    "service.grocery": "Matbutik",
    "service.emergency": "Nödläge",
    "service.exitEmergency": "Stäng SOS",
    "service.emergencyMode": "Nödläge",
    "service.callEmergency": "Ring",
    "service.emergencyNote": "112 fungerar i alla EU-länder",
    "service.locating": "Söker din position...",
    "service.locationNeeded": "Plats behövs",
    "service.enableLocation": "Tillåt platsåtkomst för att hitta tjänster nära dig",
    "service.retry": "Försök igen",
    "service.searching": "Söker i närheten...",
    "service.noResults": "Inga tjänster hittades i närheten",
    "service.navigate": "Navigera",
    "service.open": "Öppet",
    "service.closed": "Stängt",
    "service.fetchError": "Kunde inte hämta tjänster",
    "service.useGPS": "Använd GPS",
    "service.searchPlaceholder": "Sök vad som helst... Tesla, Hymer, campingutrustning, apotek",
    "service.manual": "Manuell",
    "service.searchResultsFor": "Resultat för",
    "social.places": "Platser",
    "social.tripsPlanned": "Resor planerade",
    "social.members": "Medlemmar",
    "social.reviews": "Recensioner",
    "social.popularRoutes": "Populära rutter från andra resenärer",
    "social.traveler": "Resenär",
    "social.stops": "stopp",
    "error.somethingWrong": "Något gick fel",
    "error.autoRepairFailed": "Vi försökte reparera automatiskt men det lyckades inte. Prova att ladda om sidan.",
    "error.tryAgain": "Försök igen",
    "error.reloadPage": "Ladda om sidan",
    "a11y.decreaseText": "Minska textstorlek",
    "a11y.increaseText": "Öka textstorlek",
    "a11y.decreaseTooltip": "Mindre text",
    "a11y.increaseTooltip": "Större text",
    "aria.close": "Stäng",
    "aria.openVannie": "Öppna Vannie",
    "aria.previous": "Föregående",
    "aria.next": "Nästa",
    "aria.openMenu": "Öppna meny",
    "aria.closeMenu": "Stäng meny",
    "aria.step": "Steg",
    "onboarding.step": "Steg {current} av {total}",
    "onboarding.back": "Tillbaka",
    "onboarding.skip": "Hoppa över",
    "onboarding.planTrip": "Planera din resa",
    "onboarding.planTripDesc": "Berätta vart du vill resa så hjälper vi dig bygga din resa — rutt, stopp och budget på ett ställe.",
    "onboarding.createFirstTrip": "Bygg min första resa",
    "onboarding.findPlaces": "Hitta platser på kartan",
    "onboarding.findPlacesDesc": "Campingar, sevärdheter och restauranger — lägg till stopp direkt från kartan i din resa.",
    "onboarding.searchCampings": "Utforska kartan",
    "onboarding.budgetChecklist": "Håll koll på utgifterna",
    "onboarding.budgetChecklistDesc": "Registrera vad du spenderar, skanna kvitton och följ din resebudget — allt på ett ställe.",
    "onboarding.createTrip": "Starta min budget",
    "onboarding.askVannie": "Möt Vannie",
    "onboarding.askVannieDesc": "Din personliga reseassistent. Få tips, ruttförslag och lokala regler — ingår gratis.",
    "onboarding.doneStart": "Nu kör vi!",
    "compare.title": "Jämför campingar",
    "compare.subtitle": "Välj upp till {max} campingar att jämföra sida vid sida.",
    "compare.add": "Lägg till",
    "compare.searchPlaceholder": "Sök camping...",
    "compare.property": "Egenskap",
    "compare.rating": "Betyg",
    "compare.pricePerNight": "Pris/natt",
    "compare.free": "Gratis",
    "compare.type": "Typ",
    "compare.addMore": "Lägg till minst en camping till för att jämföra.",
    "compare.selectCampings": "Välj campingar att jämföra",
    "compare.clickAdd": "Klicka \"Lägg till\" ovan för att komma igång.",
    "compare.electricity": "El",
    "compare.freshWater": "Färskvatten",
    "compare.shower": "Dusch",
    "compare.pets": "Husdjur",
    "compare.greyWater": "Gråvatten",
    "compare.toiletCassette": "Toalettkassett",
    "compare.wc": "WC",
    "compare.openAllYear": "Helårsöppet",
    "compare.familyFriendly": "Familjevänligt",
    "compare.largeMotorhomes": "Stora husbilar",
    "compare.nearWater": "Nära vatten",
    "compare.spaWellness": "Spa/Wellness",
    "friends.nearby": "Resesällskap i närheten",
    "friends.sharingOn": "Delning på",
    "friends.sharingOff": "Delning av",
    "friends.enableSharing": "Aktivera platsdelning för att se personer du delar resor med",
    "friends.onlyCollaborators": "Bara de du samarbetar med på en resa kan se din position",
    "friends.noOneSharing": "Inga resesällskap delar sin position just nu",
    "friends.anonymous": "Anonym",
    "friends.away": "bort",
    "friends.unknownDistance": "Okänd distans",
    "spot.loginToNavigate": "Logga in för att öppna navigation",
    "spot.navigateHere": "Navigera hit",
    // Incident report
    "incident.title": "Rapportera incident",
    "incident.button": "Rapportera incident",
    "incident.stepOf": "Steg {current} av {total}",
    "incident.saving": "Sparar...",
    "incident.step1": "Typ av incident",
    "incident.step2": "Tid och plats",
    "incident.step3": "Fordonsuppgifter",
    "incident.step4": "Vad hände",
    "incident.step5": "Motpart",
    "incident.step6": "Sammanfattning & PDF",
    "incident.type.collision": "Kollision",
    "incident.type.theft": "Stöld",
    "incident.type.vandalism": "Skadegörelse",
    "incident.type.breakdown": "Motorstopp",
    "incident.type.weather": "Väderskada",
    "incident.type.other": "Övrigt",
    "incident.date": "Datum",
    "incident.time": "Tid",
    "incident.location": "Plats",
    "incident.locationPlaceholder": "Adress eller platsnamn",
    "incident.locationHint": "Var så specifik som möjligt — gata, stad, land",
    "incident.vehicleType": "Fordonstyp",
    "incident.vehicleTypePlaceholder": "t.ex. Husbil, Husvagn, Bil",
    "incident.registration": "Registreringsnummer",
    "incident.vehicleDesc": "Fordonsbeskrivning",
    "incident.vehicleDescPlaceholder": "Färg, märke, modell, årsmodell",
    "incident.whatHappened": "Vad hände?",
    "incident.descriptionPlaceholder": "Beskriv händelsen lugnt och tydligt",
    "incident.additionalNotes": "Övriga anteckningar",
    "incident.notesPlaceholder": "Vittnen, väder, vägförhållanden...",
    "incident.otherPartyHint": "Fyll i om en motpart var inblandad. Hoppa över om det inte gäller.",
    "incident.otherName": "Namn",
    "incident.otherPhone": "Telefonnummer",
    "incident.otherVehicle": "Fordon",
    "incident.otherReg": "Registreringsnummer",
    "incident.otherInsurance": "Försäkringsbolag",
    "incident.incidentType": "Typ av incident",
    "incident.summary": "Sammanfattning",
    "incident.downloadPdf": "Spara & ladda ner PDF",
    "incident.saveOnly": "Spara utan PDF",
    "incident.wizardTitle": "Skadeunderlag",
    "incident.step0.title": "Viktig information",
    "incident.step0.heading": "Detta är ett skadeunderlag — inte en skadeanmälan",
    "incident.step0.body1": "Med detta verktyg skapar du ett strukturerat underlag som hjälper dig samla korrekt information vid en incident.",
    "incident.step0.body2": "Du ansvarar själv för att granska och skicka rapporten till ditt försäkringsbolag.",
    "incident.step0.gdprTitle": "Dataskydd (GDPR)",
    "incident.step0.gdprBody1": "All information sparas säkert i ditt konto. Uppgifterna delas inte automatiskt med någon tredje part.",
    "incident.step0.gdprBody2": "Du kan när som helst radera en incidentrapport och all tillhörande data.",
    "incident.step0.accept": "Jag förstår — starta",
    "incident.step1.title": "Typ av incident",
    "incident.step2.title": "Datum, tid & plats",
    "incident.step3.title": "Ditt fordon",
    "incident.step4.title": "Motpartens fordon",
    "incident.step5.title": "Förare & ägare",
    "incident.step6.title": "Försäkring",
    "incident.step7.title": "Skadebeskrivning",
    "incident.step8.title": "Bilder",
    "incident.step9.title": "Sammanfattning",
    "incident.stepOf2": "Steg {current} av {total}",
    "incident.selectCountry": "Välj land...",
    "incident.searchCountry": "Sök land...",
    "incident.searchInsurance": "Sök eller skriv manuellt...",
    "incident.regNum": "Registreringsnummer",
    "incident.regCountry": "Registreringsland",
    "incident.makeModel": "Märke / Modell",
    "incident.driverIsOwner": "Förare = Ägare",
    "incident.yes": "Ja",
    "incident.no": "Nej",
    "incident.ownerName": "Ägarens namn",
    "incident.ownerPhone": "Ägarens telefon",
    "incident.ownerEmail": "Ägarens e-post",
    "incident.otherDriverName": "Förarens namn",
    "incident.otherDriverPhone": "Förarens telefon",
    "incident.yourInsurance": "Din försäkring",
    "incident.opponentInsurance": "Motpartens försäkring",
    "incident.insuranceCompany": "Försäkringsbolag",
    "incident.policyNum": "Försäkringsnummer / Polisnummer",
    "incident.policyNumHint": "Anges på försäkringsbrevet",
    "incident.damageWhat": "Vad hände?",
    "incident.damageWhatPlaceholder": "Beskriv vad som inträffade",
    "incident.damageHow": "Hur hände det?",
    "incident.damageHowPlaceholder": "Beskriv omständigheterna, hastighet, sikt, etc.",
    "incident.damageParts": "Vilka skador uppstod?",
    "incident.damagePartsPlaceholder": "Vilka delar av fordonet skadades?",
    "incident.damageOther": "Övriga detaljer",
    "incident.damageOtherPlaceholder": "Vittnen, väder, vägförhållanden...",
    "incident.notesLabel": "Anteckningar",
    "incident.notesPlaceholder2": "Övriga noteringar",
    "incident.imagesOptional": "Bilder är valfria men rekommenderas",
    "incident.imagesHint": "Bilder kan styrka skadorna och underlätta handläggningen hos försäkringsbolaget.",
    "incident.takePhoto": "Ta bild",
    "incident.uploadBtn": "Ladda upp",
    "incident.uploading": "Laddar upp...",
    "incident.saveDraftFirst": "Rapporten måste sparas innan du kan ladda upp bilder.",
    "incident.saveDraftBtn": "Spara utkast först",
    "incident.imagesSecure": "Bilder sparas säkert i ditt konto och delas inte automatiskt. Du kan när som helst radera dem. Endast JPG och PNG (max 5 MB).",
    "incident.notFilled": "Ej ifyllt",
    "incident.notSpecified": "Ej angiven",
    "incident.notDescribed": "Ej beskrivet",
    "incident.change": "Ändra",
    "incident.summaryTitle": "Sammanfattning",
    "incident.disclaimerShort": "Du ansvarar själv för att granska och skicka rapporten till ditt försäkringsbolag.",
    "incident.generatePdf": "Genererar PDF...",
    "incident.savePdf": "Spara & ladda ner PDF",
    "incident.saveNoPdf": "Spara utan PDF",
    "incident.reportSaved": "Incidentrapport sparad",
    "incident.couldNotSave": "Kunde inte spara rapport",
    "incident.invalidFile": "Ogiltig fil",
    "incident.uploadFailed": "Uppladdning misslyckades",
    "incident.imagesAttached": "{count} bild(er) bifogade",
    "incident.timeAndPlace": "Tid & plats",
    "incident.yourVehicle": "Ditt fordon",
    "incident.opponent": "Motpart",
    "incident.insurance": "Försäkring",
    "incident.damages": "Skador",
    "incident.images": "Bilder",
    "incident.val.selectType": "Välj typ av incident",
    "incident.val.dateRequired": "Datum krävs",
    "incident.val.locationRequired": "Plats krävs",
    "incident.val.regRequired": "Registreringsnummer krävs",
    "incident.val.regCountryRequired": "Registreringsland krävs",
    "incident.val.vehicleTypeRequired": "Fordonstyp krävs",
    "incident.val.ownerNameRequired": "Ägarens namn krävs",
    "incident.val.describeWhat": "Beskriv vad som hände",
    "incident.val.describeHow": "Beskriv hur det hände",
    "incident.val.typeMissing": "Incidenttyp saknas",
    "incident.val.regMissing": "Registreringsnummer saknas",
    "incident.val.damageMissing": "Skadebeskrivning saknas",
    "incident.opponentVehicle": "Motpartens fordon",
    "incident.yourDriver": "Dina uppgifter",
    "incident.opponentDriver": "Motpartens uppgifter",
    "incident.pdf.title": "Skadeunderlag",
    "incident.pdf.disclaimer": "Detta dokument är ett skadeunderlag och inte en officiell skadeanmälan. Du ansvarar själv för att granska och skicka rapporten till ditt försäkringsbolag.",
    "incident.pdf.generated": "Genererad",
    "incident.pdf.trip": "Resa",
    "incident.pdf.type": "Typ",
    "incident.pdf.date": "Datum",
    "incident.pdf.time": "Tid",
    "incident.pdf.location": "Plats",
    "incident.pdf.vehicleType": "Fordonstyp",
    "incident.pdf.regNum": "Registreringsnummer",
    "incident.pdf.regCountry": "Registreringsland",
    "incident.pdf.makeModel": "Märke / Modell",
    "incident.pdf.driverIsOwner": "Förare = Ägare",
    "incident.pdf.ownerName": "Ägarens namn",
    "incident.pdf.ownerPhone": "Ägarens telefon",
    "incident.pdf.ownerEmail": "Ägarens e-post",
    "incident.pdf.driver": "Förare",
    "incident.pdf.phone": "Telefon",
    "incident.pdf.insuranceCompany": "Försäkringsbolag",
    "incident.pdf.policyNum": "Försäkringsnummer",
    "incident.pdf.whatHappened": "Vad hände",
    "incident.pdf.howHappened": "Hur hände det",
    "incident.pdf.damagedParts": "Skadade delar",
    "incident.pdf.otherDetails": "Övriga detaljer",
    "incident.pdf.notes": "Anteckningar",
    "incident.pdf.images": "Bilder",
    "incident.pdf.imagesNote": "{count} bild(er) bifogade. Bilderna finns i ditt Vantastika-konto.",
    "incident.pdf.footer": "Detta dokument är ett skadeunderlag framtaget med Vantastika och utgör inte en officiell skadeanmälan.",
    "incident.pdf.footerReview": "Du ansvarar själv för att kontrollera uppgifterna och skicka dem till ditt försäkringsbolag.",
    "incident.pdf.yourVehicle": "Ditt fordon",
    "incident.pdf.opponentVehicle": "Motpartens fordon",
    "incident.pdf.damageDesc": "Skadebeskrivning",
    "incident.pdf.incident": "Incident",
    "notFound.title": "Sidan kunde inte hittas",
    "notFound.description": "Vägen du letade efter verkar inte finnas. Kanske har den flyttats eller tagits bort.",
    "notFound.home": "Till startsidan",
    "notFound.back": "Gå tillbaka",
    "incident.generating": "Skapar PDF...",
    "incident.saved": "Incidentrapport sparad",
    "incident.saveFailed": "Kunde inte spara rapporten",
    "incident.back": "Tillbaka",
    "incident.next": "Nästa",
    "incident.cancel": "Avbryt",
    "incident.pdfTitle": "Incidentrapport",
    "incident.pdfGenerated": "Genererad",
    "incident.pdfDisclaimer": "Detta är en personlig dokumentation. Det är inte en officiell försäkringsanmälan.",
    "incident.drafts": "Pågående rapporter",
    "incident.noDrafts": "Inga pågående rapporter",
    "incident.continueDraft": "Fortsätt",
    "incident.noReports": "Inga incidentrapporter ännu",
    "incident.deleted": "Rapport borttagen",
    "incident.draft": "Utkast",
    "incident.completed": "Klar",
    "incident.myReports": "Mina incidentrapporter",
    "incident.myReportsSubtitle": "Dina sparade och påbörjade rapporter",
    "incident.vtype.motorhome": "Husbil",
    "incident.vtype.campervan": "Campingbuss",
    "incident.vtype.car": "Bil",
    "incident.vtype.motorcycle": "MC",
    "incident.vtype.trailer": "Släp",
    "incident.vtype.other": "Annat",
    // Quick actions
    "quick.hello": "Hej {name}! 👋",
    "quick.whatToDo": "Vad vill du göra idag?",
    "quick.continueTrip": "Fortsätt resan",
    "quick.activeTrip": "Pågående resa",
    "quick.newTrip": "Ny resa",
    "quick.planNext": "Planera ditt nästa äventyr",
    "quick.upcomingTrip": "Kommande resa",
    "quick.findCamping": "Hitta camping",
    "quick.searchFilter": "Sök & filtrera",
    "quick.favorites": "Favoriter",
    "quick.saved": "{count} sparade",
    "quick.community": "Community",
    "quick.tipsDiscussions": "Tips & diskussioner",
    // GDPR
    "gdpr.title": "Vi värnar om din integritet",
    "gdpr.description": "Denna sida använder enbart nödvändiga och funktionella cookies (autentisering, språkval, landsval och cookie-samtycke). Inga spårnings-, reklam- eller tredjepartsanalyscookies används.",
    "gdpr.readPolicy": "Läs vår integritetspolicy",
    "gdpr.accept": "Uppfattat",
    "gdpr.decline": "Uppfattat",
    // Install
    "install.title": "Installera Vantastika",
    "install.description": "Lägg till på hemskärmen för snabb åtkomst — fungerar som en app!",
    "install.installButton": "Installera appen",
    "install.showHow": "Visa hur",
    "install.understood": "Jag förstår",
    "install.iosSteps": "Följ dessa steg i Safari:",
    "install.iosTapShare": "Tryck på",
    "install.iosShareLocation": "Längst ner i Safari",
    "install.iosAddHome": "Välj \"Lägg till på hemskärmen\"",
    "install.iosScrollDown": "Scrolla ner i menyn om det behövs",
    "install.iosTapAdd": "Tryck på \"Lägg till\"",
    "install.androidSteps": "Följ dessa steg:",
    "install.androidTapMenu": "Tryck på",
    "install.androidMenuLocation": "Uppe till höger i webbläsaren",
    "install.androidInstallApp": "Välj \"Installera app\"",
    "install.androidOrAddHome": "Eller \"Lägg till på startskärmen\"",
    "home.trustFree": "100% gratis",
    // Trip management
    "myTrips.title": "Mina resor",
    "myTrips.loginTitle": "Logga in för att se dina resor",
    "myTrips.loginDesc": "Dina sparade resor visas här efter inloggning.",
    "myTrips.loginButton": "Logga in",
    "myTrips.pausedStatus": "Pausad",
    "myTrips.activeStatus": "Pågående resa",
    "myTrips.pausedSubtitle": "Din resa är pausad",
    "myTrips.activeSubtitle": "Du har en pågående resa",
    "myTrips.tripsCount": "{count} resor sparade",
    "myTrips.tripCount": "{count} resa sparad",
    "myTrips.newTrip": "Ny resa",
    "myTrips.resumeTrip": "Återuppta resa",
    "myTrips.continueTrip": "Fortsätt resa",
    "myTrips.resume": "Återuppta",
    "myTrips.pause": "Pausa",
    "myTrips.endTrip": "Avsluta resa",
    "myTrips.end": "Avsluta",
    "myTrips.progress": "{pct}% avklarat",
    "myTrips.stops": "{count} stopp",
    "myTrips.nextStop": "Nästa stopp:",
    "myTrips.openActiveTrip": "Öppna aktiv resa",
    "myTrips.noTrips": "Inga sparade resor ännu",
    "myTrips.noTripsDesc": "Skapa din första resa i reseplaneraren så visas den här.",
    "myTrips.planFirst": "Planera din första resa",
    "myTrips.upcomingTrips": "Kommande resor",
    "myTrips.sharedWithMe": "Delade med mig",
    "myTrips.invitedBy": "Inbjuden av",
    "myTrips.completedTrips": "Avslutade resor",
    "myTrips.startTrip": "Starta resa",
    "myTrips.budget": "Budget",
    "myTrips.checklist": "Checklista",
    "myTrips.stoppedPromptTitle": "Du verkar ha stannat",
    "myTrips.stoppedPromptDesc": "Vill du fortsätta resan eller ta en paus?",
    "myTrips.arrivedAt": "Framme vid {name}!",
    "myTrips.arrivedDesc": "Destination nådd – bra jobbat!",
    "myTrips.stopRemoved": "Stopp borttaget",
    "myTrips.close": "Stäng",
    "myTrips.alreadyActiveTrip": "Du har redan en aktiv resa",
    "myTrips.endActiveFirst": "Avsluta den först.",
    "myTrips.tripStarted": "Resan startad!",
    "myTrips.tripStartedDesc": "GPS-spårning och journal är nu aktiva.",
    "myTrips.tripPaused": "Resa pausad",
    "myTrips.tripPausedDesc": "Du kan fortsätta när som helst.",
    "myTrips.tripResumed": "Resan fortsätter!",
    "myTrips.tripResumedDesc": "GPS-spårning är aktiv igen.",
    "myTrips.tripEnded": "Resan avslutad!",
    "myTrips.tripEndedDesc": "Din resa har sparats.",
    "myTrips.tripReactivated": "Resan återaktiverad!",
    "myTrips.revertedToPlanned": "Återställd till planerad",
    "myTrips.revertedToPlannedDesc": "Resan är tillbaka som planerad. All data finns kvar.",
    "myTrips.missingStops": "Resan saknar stopp",
    "myTrips.missingStopsDesc": "Lägg till minst ett stopp innan du startar.",
    "myTrips.missingTitle": "Resan saknar titel",
    "myTrips.missingTitleDesc": "Ge resan ett namn innan du startar.",
    "trip.stopAddedFromMap": "Stopp tillagt från kartan",
    "trip.stopPositionUpdated": "Stoppets position uppdaterad",
    "trip.routeOptimizedDesc": "Stoppen har sorterats för kortaste körsträcka.",
    "places.verificationThanks": "Tack för bekräftelsen!",
    "places.verificationThanksDesc": "Du hjälper oss att hålla informationen korrekt.",
    "friends.requestAccepted": "Vänförfrågan accepterad!",
    "friends.requestSent": "Vänförfrågan skickad!",
    "myTrips.checklistReminder": "Glöm inte att gå igenom checklistan innan avresa",
    "myTrips.today": "Idag",
    "myTrips.tomorrow": "Imorgon",
    "myTrips.inDays": "Om {days} dagar",
    "myTrips.nextTrip": "Nästa resa",
    "myTrips.drafts": "Utkast",
    "myTrips.readyToStart": "Redo att starta",
    "myTrips.continuePlanning": "Fortsätt planera",
    "myTrips.editTrip": "Redigera",
    "myTrips.openTrip": "Öppna resa",
    "myTrips.viewSummary": "Visa sammanfattning",
    "myTrips.duplicate": "Duplicera",
    "myTrips.archive": "Arkivera",
    "myTrips.activeTrips": "Pågående resa",
    "myTrips.showTrip": "Visa resa",
    "myTrips.liveView": "Live-vy",
    "myTrips.details": "Detaljer",
    "myTrips.revertToPlanned": "Återställ till planerad",
    "myTrips.reactivateTrip": "Återuppta resa",
    "myTrips.loadingLive": "Laddar live-vy...",
    "myTrips.showCompleted": "Visa avslutade resor",
    "myTrips.hideCompleted": "Dölj avslutade resor",
    "nav.menuApp": "App",
    "nav.menuOther": "Övrigt",
    "nav.installApp": "Installera appen",
    "tools.title": "Reseverktyg",
    "tools.checklist": "Checklista",
    "tools.budget": "Budget",
    "tools.incident": "Incident",
    "tripSummary.distance": "Sträcka",
    "tripSummary.driveTime": "Körtid",
    "tripSummary.days": "Dagar",
    "tripSummary.stops": "Stopp",
    "tripSummary.fuel": "Bränsle",
    "tripSummary.budget": "Budget",
    "tripSummary.route": "Rutt",
    "tripSummary.stop": "Stopp {n}",
    "tripGuideLink.title": "Guide för {country}",
    "tripGuideLink.camping": "Camping",
    "tripGuideLink.driving": "Körning",
    "tripGuideLink.safety": "Säkerhet",
    "arrival.continueToNext": "Fortsätt till {name}",
    // BottomNav (keys defined at top of sv block)
    // TravelStats
    "stats.trips": "Resor",
    "stats.stops": "Stopp",
    "stats.nights": "Nätter",
    "stats.countries": "Länder",
    "stats.reviews": "Recensioner",
    "stats.friends": "Vänner",
    // BadgeDisplay
    "badge.travel": "Resor",
    "badge.exploration": "Utforskning",
    "badge.community": "Community",
    "badge.milestone": "Milstolpar",
    "badge.creative": "Kreativt",
    "badge.engagement": "Engagemang",
    "badge.special": "Special",
    // TripChecklist
    "checklist.title": "Checklistor",
    "checklist.subtitle": "Resekopplade och fristående checklistor",
    "checklist.loginRequired": "Logga in för att se dina checklistor",
    "checklist.loginHint": "Dina checklistor visas här efter inloggning.",
    "checklist.backToAll": "Tillbaka till alla checklistor",
    "checklist.trip": "Resa",
    "checklist.general": "Fristående checklista",
    "checklist.generalSection": "Fristående checklistor",
    "checklist.generalPacklist": "Allmän packlista",
    "checklist.done": "klart",
    "checklist.noChecklistYet": "Ingen checklista — tryck för att skapa",
    "checklist.activeTrips": "Aktiva & kommande resor",
    "checklist.completedTrips": "Avslutade resor",
    "checklist.noTrips": "Inga resor ännu. Skapa en resa för att koppla en checklista.",
    "checklist.planTrip": "Planera resa",
    "checklist.allDone": "Allt klart! 🎉",
    "checklist.remaining": "{count} punkter kvar",
    "checklist.progress": "{done} av {total} klart",
    "checklist.addCustom": "Egen punkt",
    "checklist.addTemplate": "Lägg till mall",
    "checklist.reset": "Återställ",
    "checklist.created": "Checklista skapad! ✅",
    "checklist.createFailed": "Kunde inte skapa checklista",
    "checklist.resetDone": "Checklistan har återställts",
    "checklist.resetFailed": "Kunde inte återställa",
    "checklist.itemAdded": "Punkt tillagd!",
    "checklist.itemAddFailed": "Kunde inte lägga till",
    "checklist.inputPlaceholder": "Beskriv vad du vill lägga till...",
    "checklist.add": "Lägg till",
    "checklist.cancel": "Avbryt",
    "checklist.noChecklist": "Ingen checklista ännu för {type}.",
    "checklist.selectTemplate": "Välj mall:",
    "checklist.or": "eller",
    "checklist.aiPacklist": "AI-anpassad packlista",
    "checklist.standardChecklist": "Standard-checklista",
    "checklist.aiAdapted": "AI-listan anpassas efter {dest}",
    "checklist.andSeason": " och årstid",
    "checklist.aiCreated": "🤖 AI-packlista skapad!",
    "checklist.allExist": "Alla punkter finns redan i din lista",
    "checklist.newItemsAdded": "{count} nya punkter tillagda!",
    "checklist.addExtraPacking": "Lägg till extra packning:",
    // TripFloatingBar
    "float.navigate": "Navigera",
    "float.save": "Spara",
    "float.saving": "Sparar...",
    // Profile
    "profile.save": "Spara ändringar",
    "profile.saving": "Sparar...",
    // SharedTrip
    "shared.saveToMyTrips": "Spara i mina resor",
    "shared.saving": "Sparar...",
    "shared.privacyNote": "Start- och slutpunkter har tagits bort av integritetsskäl. Lägg till din egen startplats.",
    "shared.addYourStart": "Lägg till din startplats",
    "shared.templateNote": "Denna resa delas som inspiration. Destinationer och stopp ingår — lägg till egna start- och slutpunkter.",
    // Edit sections
    "trip.editOutbound": "Utresa",
    "trip.editReturn": "Hemresa",
    "trip.editMainInfo": "Resedetaljer",
    "vehicle.motorhome": "Husbil",
    "vehicle.campervan": "Campingbuss",
    "vehicle.caravan": "Husvagn",
    "vehicle.car": "Bil",
    "vehicle.motorcycle": "MC",
    "vehicle.selectType": "Fordonstyp",
    "vehicle.nameLabel": "Fordonsnamn (valfritt)",
    "vehicle.namePlaceholder": "t.ex. Fiat Ducato 2020",
    "vehicle.dimensions": "Fordonsmått",
    "vehicle.dimensionsExplain": "Används för smartare ruttplanering — undvik låga broar, smala passager och viktbegränsningar.",
    "vehicle.height": "Höjd",
    "vehicle.width": "Bredd",
    "vehicle.length": "Längd",
    "vehicle.weight": "Totalvikt",
    "vehicle.disclaimer": "Ruttvarningar baserade på fordonsmått är vägledande. Kontrollera alltid begränsningar på plats. Full restriktionsdatatäckning byggs ut löpande.",
    "vehicle.profileComplete": "Fordonsprofil komplett — redo för smart ruttplanering",
    "vehicle.save": "Spara fordonsprofil",
    "vehicle.saving": "Sparar...",
    "vehicle.saved": "Fordonsprofil sparad!",
    "vehicle.saveFailed": "Kunde inte spara fordonsprofil",
    "vehicle.profileTitle": "Fordonsprofil",
    "vehicle.profileSubtitle": "För smartare och säkrare ruttplanering",
    "myTrips.viewRoute": "Visa rutt",
    "myTrips.playTrip": "Spela upp resa",
    "myTrips.needMoreStops": "Lägg till minst 2 stopp med koordinater för att spela upp reseanimationen.",
    "tripReplay.stops": "stopp",
    "tripReplay.days": "dagar",
    "tripReplay.completed": "Resan avslutad!",
    "tripReplay.replay": "Spela igen",
    "tripReplay.share": "Dela",
    "routeOverview.title": "Ruttöversikt",
    "routeOverview.totalDistance": "Total sträcka",
    "routeOverview.driveTime": "Körtid",
    "routeOverview.stops": "Stopp",
    "routeOverview.overnights": "Övernattningar",
    "routeOverview.fuelEstimate": "Uppskattad bränslekostnad",
    "routeOverview.itinerary": "Resplan",
    "routeOverview.nights": "nätter",
    "routeOverview.start": "Start",
    "routeOverview.destination": "Slutmål",
    "routeOverview.visit": "Besök",
    "why.title": "Varför välja Vantastika?",
    "why.subtitle": "Allt du behöver för din roadtrip — helt gratis, utan kort.",
    "why.smartPlanning": "Smart reseplanering",
    "why.smartPlanningDesc": "Skapa rutter med stopp, bränsleberäkning och tidsplanering i ett enda flöde.",
    "why.aiAssistant": "AI-reseassistent",
    "why.aiAssistantDesc": "Beskriv din drömresa med egna ord — vår AI bygger hela planen åt dig.",
    "why.campingsEurope": "Campingar i hela Europa",
    "why.campingsEuropeDesc": "Hitta, jämför och filtrera campingplatser och ställplatser i 21 länder.",
    "why.travelGuides": "Inspirerande reseguider",
    "why.travelGuidesDesc": "Färdiga temarutter med historia, stopp och tips — redo att köra.",
    "why.budget": "Budget & utgifter",
    "why.budgetDesc": "Håll koll på kostnader, bränsle och daglig budget under resan.",
    "why.shareFriends": "Dela med vänner",
    "why.shareFriendsDesc": "Bjud in resekompisar och planera tillsammans i realtid.",
    "why.checklists": "Smarta checklistor",
    "why.checklistsDesc": "Anpassade packlistor baserat på din fordonstyp och resa.",
    "why.sos": "SOS & trygghetskit",
    "why.sosDesc": "Nöddelning, incidentrapport och snabb tillgång till hjälpnummer.",
    "why.badges": "Nivåer & badges",
    "why.badgesDesc": "Samla utmärkelser, klättra i nivåer och utmana dig själv som resenär.",
    "why.cta": "Skapa gratis konto",
    "why.ctaSub": "Inget kort behövs · Klart på 30 sekunder",
    // Games module
    "games.title": "Roadtrip Games",
    "games.subtitle": "Gör resan roligare 🎮",
    "games.played": "Spelade",
    "games.totalPoints": "Total poäng",
    "games.challengeFriend": "Utmana en vän",
    "games.selectCountry": "Välj land",
    "games.loginPrompt": "Logga in för att spara spelhistorik",
    "games.loginPromptSub": "Dina poäng, favoriter och plats på topplistan sparas",
    "games.onTrip": "Resa pågår – spel anpassade för {country}",
    "games.duringTrip": "Under resan",
    "games.duringTripSub": "Perfekt för passagerare – {n} speltyper",
    "games.atStop": "Vid stopp & rast",
    "games.atStopSub": "När ni pausar eller utforskar – {n} speltyper",
    "games.camping": "Camping & kvällsspel",
    "games.campingSub": "Längre spel för kvällar och campingliv – {n} speltyper",
    "games.tabs.games": "Spel",
    "games.tabs.community": "Community",
    "games.tabs.challenges": "Utmaning",
    "games.tabs.badges": "Badges",
    "games.tabs.history": "Historik",
    "games.favorites": "Favoritspel",
    "games.favoritesEmpty": "Hjärta ett spel för att spara det här",
    "games.recommended": "Rekommenderat",
    "game.quiz.name": "Roadtrip Quiz",
    "game.quiz.desc": "Testa dina kunskaper om länder, vägar och camping",
    "game.bingo.name": "Roadtrip Bingo",
    "game.bingo.desc": "Spana efter saker längs vägen och markera din bingobricka",
    "game.guess_place.name": "Gissa Platsen",
    "game.guess_place.desc": "Kan du gissa vilken plats vi beskriver?",
    "game.would_you_rather.name": "Hellre Då",
    "game.would_you_rather.desc": "Välj mellan knepiga resedilemman",
    "game.trivia_battle.name": "Trivia Battle",
    "game.trivia_battle.desc": "Tävla mot andra resenärer i realtid",
    "game.photo_challenge.name": "Fotoutmaning",
    "game.photo_challenge.desc": "Kreativa fotouppdrag vid ditt stopp",
    "game.story.name": "Berättelsespel",
    "game.story.desc": "Skapa en reseberättelse tillsammans",
    "game.checkpoint.name": "Checkpoint",
    "game.checkpoint.desc": "Utforska området med roliga uppdrag",
    "game.evening_game.name": "Kvällsspel",
    "game.evening_game.desc": "Mysiga gruppspel för kvällen",
    "game.scenario.name": "Scenariospel",
    "game.scenario.desc": "Vad skulle du göra? Resedilemman",
    "game.intro.tagline.quiz": "Testa dina kunskaper!",
    "game.intro.tagline.bingo": "Håll utkik!",
    "game.intro.tagline.guess_place": "Kan du gissa?",
    "game.intro.tagline.would_you_rather": "Gör ditt val!",
    "game.intro.tagline.trivia_battle": "Utmaningen börjar!",
    "game.intro.tagline.photo_challenge": "Fånga ögonblicket!",
    "game.intro.tagline.story": "Det var en gång en resa...",
    "game.intro.tagline.checkpoint": "Utforska & upptäck!",
    "game.intro.tagline.evening_game": "Dags att koppla av!",
    "game.intro.tagline.scenario": "Vad skulle du göra?",
    "game.difficulty": "Välj svårighetsgrad",
    "game.difficulty.easy": "Lätt",
    "game.difficulty.medium": "Medel",
    "game.difficulty.hard": "Svår",
    "game.difficulty.easyDesc": "Perfekt för nybörjare",
    "game.difficulty.mediumDesc": "Lagom utmaning",
    "game.difficulty.hardDesc": "För riktiga experter",
    "game.question": "Fråga",
    "game.points": "poäng",
    "game.finished": "Quiz klart!",
    "game.score.amazing": "Otroligt! Du är en mästare! 🏆",
    "game.score.great": "Fantastiskt! 🎉",
    "game.score.good": "Bra jobbat! 👍",
    "game.score.ok": "Inte illa! Prova igen! 💪",
    "game.score.low": "Övning ger färdighet! 💪",
    "game.bestStreak": "Bästa streak: {n} rätt i rad!",
    "game.back": "Tillbaka",
    "game.paused": "Spelet pausat",
    "game.exit.title": "Lämna spelet?",
    "game.exit.description": "Dina framsteg försvinner om du avslutar.",
    "game.exit.continue": "Fortsätt spela",
    "game.exit.pause": "Pausa spelet",
    "game.exit.quit": "Avsluta spelet",
    "game.story.finished": "Berättelsen klar!",
    "game.story.chapters": "kapitel avslutade",
    "game.story.chapter": "Kapitel",
    "game.story.new": "Ny historia",
    "game.story.whatDo": "Vad gör ni?",
    "game.scenario.finished": "Scenariot klart!",
    "game.scenario.steps": "steg genomförda",
    "game.scenario.step": "Steg",
    "game.scenario.new": "Nytt scenario",
    "game.scenario.whatDo": "Vad gör ni?",
    "game.scenario.intense": "Intensivt!",
    "game.newRound": "Ny runda",
    "game.seeResults": "Se resultat",
    "game.nextQuestion": "Nästa fråga",
    "game.playNow": "Spela nu",
    "game.players": "spelare",
    "game.minutes": "min",
    "game.safety.title": "Säkerheten först!",
    "game.safety.desc": "Det här spelet är avsett för passagerare, inte föraren. Bekräfta att du är passagerare.",
    "game.safety.confirm": "Jag är passagerare",
    "game.safety.later": "Spela senare",
    "game.version": "{country}-version",
    "game.hero.title": "Gör resan roligare",
    "game.hero.game_types": "speltyper",
    "game.hero.questions": "frågor",
    "game.hero.countries": "länder",
    "game.challenge_friend": "Utmana en vän",
    "game.trip_active": "Resa pågår",
    "game.login_prompt": "Logga in för att spara spelhistorik",
    "game.login_prompt_desc": "Dina poäng, favoriter och plats på topplistan sparas",
    "game.tab.games": "Spel",
    "game.tab.challenges": "Utmaningar",
    "game.tab.community": "Community",
    "game.tab.collect": "Samla",
    "game.tab.history": "Historik",
    "game.section.on_the_road": "Under resan",
    "game.section.at_stop": "Vid stopp & rast",
    "game.section.camping": "Camping & kvällsspel",
    "game.favorites": "Favoritspel",
    "game.no_favorites": "Hjärta ett spel för att spara det här",
    "game.wyr.allDone": "Alla frågor klara!",
    "game.wyr.questionsAnswered": "frågor besvarade",
    "game.wyr.title": "Hellre Då",
    "game.wyr.wouldYouRather": "Skulle du hellre...",
    "game.wyr.or": "eller",
    "game.wyr.done": "Klar!",
    "game.recommended.title": "Rekommenderat för dig",
    "game.weekly.title": "Veckans utmaningar",
    "game.weekly.done": "Klar",
    "game.bingo.found": "hittade",
    "game.bingo.subtitle": "Hitta saker längs vägen",
    "game.bingo.spotted": "Hittad!",
    "game.bingo.instructions": "Tryck på saker du ser längs vägen. Bingo = rad, kolumn eller diagonal!",
    "game.guess.finished": "Gissa Platsen klar!",
    "game.guess.fewerClues": "Färre ledtrådar = fler poäng!",
    "game.guess.title": "Gissa Platsen",
    "game.guess.place": "Plats",
    "game.guess.clues": "Ledtrådar",
    "game.guess.showNext": "Visa nästa ledtråd",
    "game.guess.placeholder": "Skriv ditt svar...",
    "game.guess.guess": "Gissa",
    "game.guess.correct": "Rätt!",
    "game.guess.wrong": "Fel!",
    "game.guess.correctAnswer": "Rätt svar",
    "game.guess.nextPlace": "Nästa plats",
    "game.playAgain": "Spela igen",
    "game.evening.finished": "Kvällsspel klart!",
    "game.evening.wellDone": "Bra jobbat med hela sällskapet!",
    "game.evening.round": "Runda",
    "game.evening.done": "Klart!",
    "game.evening.nextRound": "Nästa runda",
    "game.evening.trivia": "Trivia",
    "game.evening.creative": "Kreativ",
    "game.evening.debate": "Debatt",
    "game.evening.memory": "Minne",
    "game.evening.drawing": "Teckna",
    "game.checkpoint.finished": "Checkpoint klar!",
    "game.checkpoint.challengesDone": "utmaningar avklarade",
    "game.checkpoint.newChallenges": "Nya utmaningar",
    "game.checkpoint.subtitle": "Utforska platsen med utmaningar",
    "game.checkpoint.finish": "Avsluta checkpoint",
    "game.safety.warning": "Trafiksäkerhet går alltid först. Kör aldrig och spela samtidigt.",
    "game.safety.cancel": "Avbryt",
    "game.leaderboard.title": "Topplista – denna vecka",
    "game.leaderboard.games": "spel",
    "game.leaderboard.you": "Du",
    "game.leaderboard.yourPosition": "Din position",
    "game.history.empty": "Inga spelade spel ännu",
    "game.history.emptyDesc": "Starta ditt första spel och se din historik här!",
    "game.history.played": "Spelade",
    "game.history.gameTypes": "Speltyper",
    "game.history.countries": "Länder",
    "game.history.avgScore": "Genomsnittlig poäng",
    "game.history.inProgress": "Pågående",
    "game.history.continue": "Fortsätt spela",
    "game.badges.title": "Badges",
    "game.quiz.fast": "Snabba",
    "game.quiz.perfect": "Perfekt!",
    "game.audience.label": "Vem spelar?",
    "game.audience.kids": "Barn",
    "game.audience.kids_desc": "Roliga och enkla spel för barn",
    "game.audience.family": "Familj",
    "game.audience.family_desc": "Spel för hela familjen",
    "game.audience.adult": "Vuxna",
    "game.audience.adult_desc": "Spel för vuxna",
    "game.audience.couple": "Par",
    "game.audience.couple_desc": "Romantiska spel för två",
    "game.audience.friends": "Vänner",
    "game.audience.friends_desc": "Roligt och tävlingsinriktat med vänner",
    "game.daily.title": "Dagliga & veckovisa utmaningar",
    "game.daily.today": "Idag",
    "game.daily.this_week": "Denna vecka",
    "game.daily.this_month": "Denna månad",
    "game.daily.quiz_challenge": "Dagens quiz-utmaning",
    "game.daily.quiz_challenge_desc": "Få 7+ på dagens quiz",
    "game.daily.bingo_quick": "Snabb bingo",
    "game.daily.bingo_quick_desc": "Hitta 5 saker på bingobrädet",
    "game.daily.guess_master": "Gissningmästare",
    "game.daily.guess_master_desc": "Gissa rätt på 3 platser",
    "game.daily.scenario_star": "Scenariostjärna",
    "game.daily.scenario_star_desc": "Klara en scenariorunda",
    "game.daily.photo_snap": "Fotosnabben",
    "game.daily.photo_snap_desc": "Klara 3 fotoutmaningar",
    "game.daily.story_teller": "Berättaren",
    "game.daily.story_teller_desc": "Klara en berättelse",
    "game.daily.trivia_duel": "Triviaduellen",
    "game.daily.trivia_duel_desc": "Få 5+ i trivia battle",
    "game.weekly.quiz_master": "Veckans quizmästare",
    "game.weekly.quiz_master_desc": "Få 9+ på valfritt quiz denna vecka",
    "game.weekly.bingo_champion": "Bingomästare",
    "game.weekly.bingo_champion_desc": "Fyll ett helt bingobräde",
    "game.weekly.explorer": "Veckans utforskare",
    "game.weekly.explorer_desc": "Klara 4 checkpoint-utmaningar",
    "game.weekly.photo_pro": "Fotoproff",
    "game.weekly.photo_pro_desc": "Klara 6 fotoutmaningar",
    "game.weekly.evening_host": "Kvällsvärd",
    "game.weekly.evening_host_desc": "Håll i ett kvällsspel",
    "game.monthly.country_champion": "Landsmästare",
    "game.monthly.country_champion_desc": "Perfekt poäng på ett landsquiz",
    "game.monthly.all_rounder": "Allroundern",
    "game.monthly.all_rounder_desc": "Spela 5 olika speltyper",
    "game.monthly.road_legend": "Väglegend",
    "game.monthly.road_legend_desc": "Klara 3 berättelser",
    "game.season.spring": "Vårens spel",
    "game.season.summer": "Sommarens spel",
    "game.season.autumn": "Höstens spel",
    "game.season.winter": "Vinterns spel",
    "game.seasonal.limited": "Säsong",
    "game.seasonal.spring_awakening": "Vårens quiz",
    "game.seasonal.spring_awakening_desc": "Testa dina vårkunskaper",
    "game.seasonal.easter_trip": "Påsk-roadtrip bingo",
    "game.seasonal.easter_trip_desc": "Hitta påsken längs vägen",
    "game.seasonal.spring_photo": "Vår-fotoutmaning",
    "game.seasonal.spring_photo_desc": "Fånga vårens skönhet",
    "game.seasonal.summer_roadtrip": "Sommar-roadtrip quiz",
    "game.seasonal.summer_roadtrip_desc": "Sol, stränder och sommarvägar",
    "game.seasonal.beach_bingo": "Strand & kust-bingo",
    "game.seasonal.beach_bingo_desc": "Hitta sommarsaker vid kusten",
    "game.seasonal.midsummer": "Midsommarutmaning",
    "game.seasonal.midsummer_desc": "Fira midsommar med utmaningar",
    "game.seasonal.camping_nights": "Sommarcampingkvällar",
    "game.seasonal.camping_nights_desc": "Perfekt för varma campingkvällar",
    "game.seasonal.autumn_colors": "Höstfärger-foto",
    "game.seasonal.autumn_colors_desc": "Fånga höstens gyllene färger",
    "game.seasonal.harvest_quiz": "Skördetidens quiz",
    "game.seasonal.harvest_quiz_desc": "Quiz om höst och skörd",
    "game.seasonal.fog_mystery": "Dimmigt mysterium",
    "game.seasonal.fog_mystery_desc": "Ett mystiskt höstäventyr",
    "game.seasonal.winter_roads": "Vintervägar-quiz",
    "game.seasonal.winter_roads_desc": "Testa vinterkörkunskaper",
    "game.seasonal.christmas_markets": "Julmarknadsutforskaren",
    "game.seasonal.christmas_markets_desc": "Gissa berömda julmarknader",
    "game.seasonal.cozy_evening": "Mysig vinterkväll",
    "game.seasonal.cozy_evening_desc": "Perfekt spel för kalla kvällar",
    "game.seasonal.northern_lights": "Norrsken-berättelse",
    "game.seasonal.northern_lights_desc": "Ett äventyr under norrskenet",
    "game.level.label": "Nivå",
    "game.level.beginner": "Nybörjare",
    "game.level.explorer": "Utforskare",
    "game.level.adventurer": "Äventyrare",
    "game.level.traveler": "Resenär",
    "game.level.road_master": "Vägmästare",
    "game.level.pro": "Roadtrip Pro",
    "game.level.navigator": "Kartläsare",
    "game.level.euro_champ": "Europamästare",
    "game.level.legendary": "Legendarisk",
    "game.level.legend": "Roadtrip Legend",
    "game.level.next": "Nästa",
    "game.level.games_played": "spelade",
    "game.collectibles.title": "Landssamling",
    "game.collectibles.countries": "länder",
    "country.sweden": "Sverige",
    "country.norway": "Norge",
    "country.germany": "Tyskland",
    "country.italy": "Italien",
    "country.france": "Frankrike",
    "country.spain": "Spanien",
    "game.region.norrland": "Norrland",
    "game.region.svealand": "Svealand",
    "game.region.gotaland": "Götaland",
    "game.region.fjords": "Fjordarna",
    "game.region.nordnorge": "Nordnorge",
    "game.region.sorlandet": "Sørlandet",
    "game.region.bayern": "Bayern",
    "game.region.norddeutschland": "Nordtyskland",
    "game.region.rheinland": "Rhenlandet",
    "game.region.norditalien": "Norditalien",
    "game.region.toscana": "Toscana",
    "game.region.syditalien": "Syditalien",
    "game.region.ildefrance": "Île-de-France",
    "game.region.provence": "Provence",
    "game.region.bretagne": "Bretagne",
    "game.region.andalucia": "Andalusien",
    "game.region.catalonia": "Katalonien",
    "game.region.nordspanien": "Nordspanien",
    // Nav extras
    "nav.bottom.budget": "Budget",
    "nav.installTitle": "Installera Vantastika",
    "nav.installIOS1": "Tryck på Dela-knappen (längst ner i Safari)",
    "nav.installIOS2": "Välj \"Lägg till på hemskärmen\"",
    "nav.installIOS3": "Tryck på \"Lägg till\"",
    "nav.installAndroid1": "Tryck på menyikonen (⋮ uppe till höger)",
    "nav.installAndroid2": "Välj \"Installera app\" eller \"Lägg till på startskärmen\"",
    "nav.installUnderstood": "Jag förstår",
    // Trip status confirm
    "tripStatus.startTitle": "Vill du starta resan nu?",
    "tripStatus.startDesc": "Resan sätts som pågående och GPS-spårning aktiveras.",
    "tripStatus.startConfirm": "Ja, starta",
    "tripStatus.endTitle": "Vill du verkligen avsluta resan?",
    "tripStatus.endDesc": "Du kan återuppta den senare om du ändrar dig.",
    "tripStatus.endConfirm": "Ja, avsluta",
    "tripStatus.pauseTitle": "Vill du pausa resan?",
    "tripStatus.pauseDesc": "Du kan fortsätta resan när som helst.",
    "tripStatus.pauseConfirm": "Ja, pausa",
    "tripStatus.resumeTitle": "Vill du återuppta resan?",
    "tripStatus.resumeDesc": "Resan fortsätter där du pausade.",
    "tripStatus.resumeConfirm": "Ja, fortsätt",
    "tripStatus.revertTitle": "Vill du återställa till planerad resa?",
    "tripStatus.revertDesc": "All reseinformation sparas kvar — bara statusen ändras tillbaka till planerad.",
    "tripStatus.revertConfirm": "Ja, återställ",
    "tripStatus.reactivateTitle": "Vill du återuppta den avslutade resan?",
    "tripStatus.reactivateDesc": "Resan sätts som pågående igen. All data finns kvar.",
    "tripStatus.reactivateConfirm": "Ja, återuppta",
    "tripStatus.trip": "Resa",
    "tripStatus.cancel": "Avbryt",
    // AddToTripDialog
    "addToTrip.title": "Lägg till i resa",
    "addToTrip.loginPrompt": "Logga in för att lägga till platser i dina resor.",
    "addToTrip.login": "Logga in",
    "addToTrip.createAccount": "Skapa konto",
    "addToTrip.howUsePlace": "Hur vill du använda platsen?",
    "addToTrip.chooseTrip": "Välj resa",
    "addToTrip.startPoint": "Startpunkt",
    "addToTrip.startPointDesc": "Resan börjar härifrån",
    "addToTrip.destination": "Destination",
    "addToTrip.destinationDesc": "Jag vill resa hit",
    "addToTrip.stop": "Stopp på vägen",
    "addToTrip.stopDesc": "Mellanlandning under resan",
    "addToTrip.noTrips": "Du har ingen planerad resa ännu",
    "addToTrip.noTripsDesc": "Skapa en ny resa och lägg till platsen direkt.",
    "addToTrip.stops": "stopp",
    "addToTrip.unnamed": "Namnlös resa",
    "addToTrip.addedAs": "har lagts till som",
    "addToTrip.roleStart": "startpunkt",
    "addToTrip.roleDestination": "destination",
    "addToTrip.roleStop": "stopp",
    "addToTrip.couldNotAdd": "Kunde inte lägga till platsen",
    "addToTrip.createNew": "Skapa ny resa med denna plats",
    "optimize.bannerTitle": "AI hittade en kortare rutt",
    "optimize.bannerDesc": "Spara ca",
    "optimize.bannerByReordering": "genom att ändra ordningen på dina stopp.",
    "optimize.viewAlternatives": "Visa alternativ",
    "optimize.dismiss": "Inte nu",
    "optimize.fuelSaved": "i bränsle",
    // CampingPreviewDialog
    "camping.notFound": "Platsen kunde inte hittas.",
    "camping.approxPrice": "ca.",
    "camping.perNight": "/ natt (ungefärligt)",
    "camping.free": "Gratis",
    "camping.typeStellplatz": "Ställplats",
    "camping.typeCamping": "Camping",
    "camping.amenities": "Bekvämligheter",
    "camping.checkIn": "In",
    "camping.checkOut": "Ut",
    "camping.visitWebsite": "Besök webbplats",
    "camping.addToTrip": "Lägg till i resa",
    "camping.navigate": "Navigera",
    "camping.viewPage": "Visa sida",
    "camping.amenityElectricity": "El",
    "camping.amenityFreshWater": "Färskvatten",
    "camping.amenityGreyWater": "Gråvatten",
    "camping.amenityToilet": "Toalett",
    "camping.amenityShower": "Dusch",
    "camping.amenityWC": "WC",
    "camping.amenityPets": "Husdjur",
    "camping.amenityAllYear": "Helårsöppet",
    "camping.amenityFamily": "Barnvänlig",
    "camping.amenityLarge": "Stora husbilar",
    "camping.amenityWater": "Nära vatten",
    "camping.amenitySpa": "Spa",
    "camping.amenityWifi": "WiFi",
    "camping.amenityLaundry": "Tvätt",
    "camping.amenityRestaurant": "Restaurang",
    // TripShareDialog
    "share.inviteToSharedTrip": "Bjud in till gemensam resa",
    "share.inviteDesc": "Mottagaren blir deltagare i samma resa. Alla ändringar synkas i realtid — budget, checklista, stopp och anteckningar.",
    "share.inviteViaApp": "Bjud in via app...",
    "share.copyLink": "Kopiera inbjudningslänk",
    "share.copied": "Kopierad!",
    "share.shareAsTip": "Dela som resetips",
    "share.tipDesc": "Mottagaren får en egen kopia som inspiration. Ändringar påverkar inte din resa.",
    "share.createAiGuide": "Skapa AI-reseguide",
    "share.generatingAi": "Genererar AI-reseguide... ✨",
    "share.aiGuideCreated": "AI-reseguide skapad! ✨",
    "share.couldNotCreateAi": "Kunde inte skapa AI-guide",
    "share.aiGuideActive": "AI-reseguide aktiv",
    "share.tabShare": "Dela",
    "share.tabTeam": "Sällskap",
    "share.tabComments": "Kommentarer",
    "share.tabActivity": "Aktivitet",
    "share.inviteByUsername": "Bjud in via användarnamn eller dela en inbjudningslänk:",
    "share.usernamePlaceholder": "Användarnamn att bjuda in...",
    "share.inviteSent": "Inbjudan skickad!",
    "share.userNotFound": "Användaren hittades inte. Ange exakt användarnamn.",
    "share.cannotInviteSelf": "Du kan inte bjuda in dig själv.",
    "share.alreadyInvited": "Användaren är redan inbjuden.",
    "share.couldNotInvite": "Kunde inte bjuda in",
    "share.noMembers": "Inga sällskapsmedlemmar ännu. Bjud in någon för att planera tillsammans!",
    "share.editor": "Redigerare",
    "share.removedFromGroup": "Borttagen från sällskapet",
    "share.noComments": "Inga kommentarer ännu.",
    "share.unknown": "Okänd",
    "share.commentPlaceholder": "Skriv en kommentar...",
    "share.send": "Skicka",
    "share.noActivity": "Ingen aktivitet ännu.",
    "share.actionInvited": "bjöd in",
    "share.actionCommented": "kommenterade",
    "share.actionAddedStop": "la till stopp:",
    "share.actionUpdatedStop": "uppdaterade stopp:",
    "share.actionRemovedStop": "tog bort stopp:",
    "share.actionUpdatedTrip": "uppdaterade resan",
    // SignupPage
    "signup.guestVsMember": "Gäst vs Medlem",
    "signup.seeWhatYouGet": "Se vad du får som medlem",
    "signup.freeNoCard": "100% gratis — inget kort behövs",
    "signup.premiumSoon": "Premium kommer snart med ännu mer funktioner",
    "signup.username": "Användarnamn",
    "signup.usernamePlaceholder": "t.ex. vanansen",
    "signup.displayName": "Visningsnamn",
    "signup.displayNamePlaceholder": "Ditt namn som visas för andra",
    "signup.confirmPassword": "Bekräfta lösenord",
    "signup.passwordPlaceholder": "Minst 8 tecken, bokstav + siffra",
    "signup.passwordsNoMatch": "Lösenorden matchar inte",
    "signup.selectCountry": "Välj ditt land",
    "signup.whatVehicle": "Vad reser du med?",
    "signup.ageWarning": "Du måste vara minst 16 år för att skapa ett konto på Vantastika. Om du är under 18 år krävs vårdnadshavares godkännande.",
    "signup.termsConsent": "Jag godkänner",
    "signup.termsLink": "användarvillkoren",
    "signup.andThe": "och",
    "signup.integrationTermsLink": "integrationsvillkoren",
    "signup.ageConfirm": "och intygar att jag är minst 16 år.",
    "signup.gdprConsent": "Jag har läst och godkänner",
    "signup.privacyLink": "integritetspolicyn (GDPR)",
    "signup.or": "eller",
    "signup.verificationFailed": "Verifieringen misslyckades. Försök igen.",
    "signup.captchaFailed": "CAPTCHA-verifiering misslyckades. Försök igen.",
    "signup.tooManyAttempts": "För många försök. Vänta en stund.",
    "signup.acceptTerms": "Du måste godkänna villkoren",
    "signup.selectCountryError": "Välj ditt land",
    "signup.enterDisplayName": "Ange ett visningsnamn",
    "signup.enterUsername": "Ange ett användarnamn",
    "signup.accountCreated": "Konto skapat!",
    "signup.welcomeMsg": "Välkommen till Vantastika!",
    "signup.checkDetails": "Kontrollera uppgifterna och försök igen.",
    "signup.showPassword": "Visa lösenord",
    "signup.hidePassword": "Dölj lösenord",
    // Signup vehicle options
    "signupVehicle.motorhome": "🚐 Husbil",
    "signupVehicle.motorhomeDesc": "Jag reser med husbil",
    "signupVehicle.car": "🚗 Bil",
    "signupVehicle.carDesc": "Jag reser med personbil",
    "signupVehicle.motorcycle": "🏍️ Motorcykel",
    "signupVehicle.motorcycleDesc": "Jag kör motorcykel",
    "signupVehicle.campervan": "🚌 Campingbuss",
    "signupVehicle.campervanDesc": "Jag har campingbuss",
    "signupVehicle.rental": "🔑 Hyr fordon",
    "signupVehicle.rentalDesc": "Jag hyr vid behov",
    "signupVehicle.other": "🌍 Annat / Vet ej",
    "signupVehicle.otherDesc": "Flexibel resenär",
    // ProfilePage
    "profile.yourProfile": "Din profil",
    "profile.since": "Sedan",
    "profile.quickLinks": "Snabbval",
    "profile.myTrips": "Mina resor",
    "profile.checklists": "Checklistor",
    "profile.savedPlaces": "Sparade platser",
    "profile.planning": "Planering",
    "profile.saved": "Sparat",
    "vehicle.projects": "Projekt",
    "vehicle.viewAll": "Visa alla",
    "vehicle.noProjects": "Inga projekt kopplade till {name} ännu.",
    "vehicle.createProject": "Skapa projekt",
    "wishlist.startExploring": "Börja utforska",
    "profile.notifications": "Notiser",
    "profile.travelOverview": "Reseöversikt",
    "profile.levelAndProfile": "Reseprofil & nivå",
    "profile.progress": "Framsteg",
    "profile.total": "Totalt",
    "profile.badges": "Badges",
    "profile.challenges": "Utmaningar",
    "profile.activeChallenges": "Aktiva utmaningar",
    "profile.viewAllProgress": "Visa alla framsteg",
    "profile.incidentReports": "Incidentrapporter",
    "profile.newReport": "Ny rapport",
    "profile.social": "Socialt",
    "profile.inbox": "Inkorg",
    "profile.mySubmissions": "Mina bidrag",
    "profile.accountSettings": "Konto & inställningar",
    "profile.accountSettingsDesc": "Profil, tema och lösenord",
    "profile.email": "E-post",
    "profile.displayName": "Visningsnamn",
    "profile.displayNamePlaceholder": "Ditt namn",
    "profile.username": "Användarnamn",
    "profile.usernamePlaceholder": "@användarnamn",
    "profile.country": "Land",
    "profile.countryPlaceholder": "Sverige",
    "profile.appearance": "Utseende",
    "profile.themeLight": "Ljust",
    "profile.themeDark": "Mörkt",
    "profile.themeSystem": "System",
    "profile.themeLightDesc": "Alltid ljust tema",
    "profile.themeDarkDesc": "Alltid mörkt tema",
    "profile.themeSystemDesc": "Följer enheten",
    "profile.changePassword": "Ändra lösenord",
    "profile.currentPassword": "Nuvarande lösenord",
    "profile.newPassword": "Nytt lösenord",
    "profile.newPasswordPlaceholder": "Minst 8 tecken",
    "profile.confirmPasswordLabel": "Bekräfta",
    "profile.changePasswordBtn": "Ändra lösenord",
    "profile.changingPassword": "Ändrar...",
    "profile.wrongCurrentPassword": "Fel nuvarande lösenord",
    "profile.passwordChanged": "Lösenord ändrat!",
    "profile.passwordMinLength": "Lösenordet måste vara minst 8 tecken",
    "profile.passwordsMismatch": "Lösenorden matchar inte",
    "profile.deleteAccount": "Radera konto",
    "profile.permanentDeletion": "Permanent radering",
    "profile.deleteAccountDesc": "Vid kontoradering tas följande bort permanent: profil, resor, stopp, favoriter, checklistor, foruminlägg, recensioner, bilder (avatar, resor, forum, incidenter), vänförfrågningar, meddelanden, incidentrapporter och notifikationer.",
    "profile.deleteAccountNote": "Vad som kan finnas kvar: Anonymiserade poster i granskningsloggen (utan koppling till ditt konto), badges-katalogen (gemensam för alla) och eventuella uppgifter vi enligt lag måste behålla (t.ex. bokföringslagen, max 7 år).",
    "profile.deleteMyAccount": "Radera mitt konto",
    "profile.deleteConfirmTitle": "Radera konto permanent",
    "profile.deleteConfirmDesc": "Är du säker? Följande raderas permanent:",
    "profile.deleteConfirmList": "• Profil, inställningar och avatar\n• Resor, stopp, budget och checklistor\n• Favoriter, samlingar och sparade platser\n• Incidentrapporter och uppladdade bilder\n• Foruminlägg, kommentarer och gillanden\n• Vänförfrågningar och meddelanden\n• Notifikationer och platsverifieringar",
    "profile.deleteConfirmNote": "Anonymiserade granskningsloggar och den gemensamma badges-katalogen påverkas inte. Uppgifter som vi enligt lag måste behålla kan finnas kvar i upp till 7 år.",
    "profile.cannotUndo": "Kan inte ångras.",
    "profile.deleting": "Raderar...",
    "profile.yesDeleteAccount": "Ja, radera mitt konto",
    "profile.accountDeleted": "Kontot har raderats",
    "profile.accountDeletedDesc": "All din data har tagits bort.",
    "profile.couldNotDelete": "Kunde inte radera kontot",
    "profile.profileSaved": "Profil sparad!",
    "profile.couldNotSave": "Kunde inte spara",
    "profile.profilePicUpdated": "Profilbild uppdaterad!",
    "profile.uploading": "Laddar upp...",
    "profile.fileTooLarge": "Filen är för stor",
    "profile.fileTooLargeDesc": "Max 2 MB",
    "profile.imageRejected": "Bilden nekades",
    "profile.myVehicle": "Mitt fordon",
    "profile.enterCurrentPassword": "Ange ditt nuvarande lösenord",
    "profile.couldNotChangePassword": "Kunde inte ändra lösenord",
    "profile.vehicleMotorhome": "Husbil",
    "profile.vehicleCar": "Bil",
    "profile.vehicleMotorcycle": "MC",
    "profile.vehicleCampervan": "Campingbuss",
    "profile.vehicleRental": "Hyrfordon",
    "profile.vehicleOther": "Annat",
    "profile.activeNow": "Aktivt just nu",
    "profile.today": "Idag!",
    "profile.inDays": "Om {n} dagar",
    "profile.newNotification": "ny avisering",
    "profile.newNotifications": "nya aviseringar",
    "profile.showAll": "Visa alla",
    "profile.inspection": "Besiktning",
    "profile.gasInspection": "Gasolkontroll",
    "profile.service": "Service",
    "profile.expired": "Förfallen!",
    "profile.noDateRegistered": "Inget datum registrerat",
    "profile.noServiceRegistered": "Ingen service registrerad",
    "profile.missing": "Saknas",
    "profile.insurance": "Försäkring",
    "profile.noInsuranceRegistered": "Ingen försäkring registrerad",
    "profile.regNumberMissing": "Registreringsnummer saknas",
    "profile.addForSmartData": "Lägg till för smart fordonsdata",
    "profile.addRegNumber": "Lägg till regnr",
    "profile.statusGood": "Bra",
    "profile.statusSoon": "Snart",
    "profile.statusAction": "Åtgärd",
    "profile.edit": "Redigera",
    "profile.addService": "Lägg till service",
    "profile.addVehicleDesc": "Koppla ditt fordon för smart planering, påminnelser och anpassat innehåll",
    "profile.addVehicleBtn": "Lägg till fordon",
    "profile.validUntil": "Giltig till {date}",
    // Scan receipt
    "scan.title": "Smart kvittoskanning",
    "scan.takePhoto": "Ta foto",
    "scan.upload": "Ladda upp",
    "scan.photoOrUpload": "Fotografera eller ladda upp kvitto",
    "scan.aiAnalyzes": "AI:n analyserar kvittot rad för rad",
    "scan.analyzing": "Analyserar kvittot...",
    "scan.analyzingDesc": "AI:n läser av artiklar, belopp och kategorier",
    "scan.fileTooLarge": "Filen är för stor",
    "scan.fileTooLargeMax": "Max 15 MB",
    "scan.analysisFailed": "Analys misslyckades",
    "scan.couldNotAnalyze": "Kunde inte analysera kvittot",
    "scan.amount": "Belopp",
    "scan.currency": "Valuta",
    "scan.possibleAmounts": "Möjliga belopp:",
    "scan.converted": "Omräknat:",
    "scan.date": "Datum",
    "scan.titleLabel": "Rubrik",
    "scan.itemsPerCategory": "Artiklar per kategori",
    "scan.mixedCategories": "Blandade kategorier",
    "scan.howToSave": "Hur vill du spara?",
    "scan.oneExpense": "En utgift",
    "scan.splitUp": "Dela upp",
    "scan.splitDesc": "Skapar {n} separata utgifter — en per kategori.",
    "scan.mainCategory": "Huvudkategori",
    "scan.linkToBudget": "Koppla till budget",
    "scan.noBudgetSelected": "Ingen budget vald",
    "scan.noBudget": "Ingen budget",
    "scan.linkToTrip": "Koppla till resa",
    "scan.noTripSelected": "Ingen resa vald",
    "scan.noTrip": "Ingen resa",
    "scan.note": "Anteckning",
    "scan.hideRawText": "Dölj rå kvittotext",
    "scan.showRawText": "Visa rå kvittotext",
    "scan.cancel": "Cancel",
    "scan.saving": "Sparar...",
    "scan.saveExpense": "Spara utgift",
    "scan.saveExpenses": "Spara {n} utgifter",
    "scan.expenseSaved": "Utgift sparad från kvitto ✓",
    "scan.expensesSaved": "{n} utgifter sparade från kvitto ✓",
    "scan.fillAmountAndTitle": "Fyll i belopp och rubrik",
    "scan.couldNotSave": "Kunde inte spara",
    "scan.confidenceHigh": "Hög säkerhet",
    "scan.confidenceMedium": "Medelhög säkerhet — granska fälten",
    "scan.confidenceLow": "Låg säkerhet — kontrollera alla fält",
    "scan.dotMedium": "Medelsäker",
    "scan.dotLow": "Osäker — kontrollera",
    "scan.close": "Stäng",
    "scan.paid": "Betalt:",
    "scan.vat": "moms:",
    "scan.receipt": "Kvitto",
    // Camera permissions
    "camera.prePermissionHint": "Kameratillgång behövs för att skanna kvitton",
    "camera.deniedError": "Kameratillgång nekades. Aktivera den i webbläsarens eller telefonens inställningar.",
    "camera.notFoundError": "Ingen kamera hittades på denna enhet.",
    "camera.inUseError": "Kameran används av en annan app.",
    "camera.genericError": "Ett oväntat fel inträffade vid åtkomst till kameran.",
    "camera.openSettings": "Visa instruktioner",
    "camera.androidSettingsHint": "Gå till Inställningar → Appar → Din webbläsare → Behörigheter → Kamera",
    "camera.iosSettingsHint": "Gå till Inställningar → Integritet och säkerhet → Kamera → Din webbläsare",
    "camera.desktopSettingsHint": "Klicka på låsikonen i adressfältet och aktivera kameraåtkomst.",
    // Location permissions
    "location.detecting": "Hämtar din position…",
    "location.unsupportedTitle": "Platsdelning stöds inte",
    "location.unsupportedDesc": "Din enhet eller webbläsare stöder inte platsdelning. Testa en annan webbläsare.",
    "location.deniedTitle": "Platsåtkomst nekad",
    "location.deniedDesc": "Du har nekat platsåtkomst. För att använda denna funktion behöver du aktivera platsdelning i dina webbläsar- eller telefoninställningar.",
    "location.tryAgain": "Försök igen",
    "location.openSettings": "Visa instruktioner",
    "location.androidSettingsHint": "Gå till Inställningar → Appar → Din webbläsare → Behörigheter → Plats",
    "location.iosSettingsHint": "Gå till Inställningar → Integritet och säkerhet → Platstjänster → Din webbläsare",
    "location.desktopSettingsHint": "Klicka på låsikonen i adressfältet och aktivera platsåtkomst.",
    "location.promptDesc": "Vi behöver din position för att visa relevanta resultat i närheten.",
    "location.allowAccess": "Tillåt platsåtkomst",
    "location.fetchErrorTitle": "Kunde inte hämta position",
    "location.timeoutDesc": "Det tog för lång tid att hämta din position. Kontrollera att GPS är aktiverat.",
    "location.unavailableDesc": "Din position kunde inte bestämmas just nu. Försök igen om en stund.",
    "location.errorTitle": "Platsfel",
    "location.errorDesc": "Något gick fel vid hämtning av din position.",
    // Season greetings
    "season.spring": "Vår",
    "season.springGreeting": "Våren är här – dags att planera!",
    "season.summer": "Sommar",
    "season.summerGreeting": "Sommaren väntar – vilken väg tar du?",
    "season.autumn": "Höst",
    "season.autumnGreeting": "Höstens färger kallar!",
    "season.winter": "Vinter",
    "season.winterGreeting": "Vintern är magisk – planera en snöresa!",
    "map.searchPlaceholder": "Sök stad, camping, adress...",
    "map.searching": "Söker...",
    "map.searchingSuggestions": "Söker platsförslag…",
    "map.noResults": "Inga träffar hittades",
    "map.noResultsHint": "Prova stad, camping, adress eller platsnamn",
    "map.exploreEurope": "Utforska Europa",
    "map.exploreDesc": "Sök efter en plats för att upptäcka campingar, ställplatser och sevärdheter",
    "map.exploreDescLong": "Sök efter en plats för att upptäcka campingar, restauranger, sevärdheter och mer",
    "map.resultsNear": "Resultat nära",
    "map.noCampingsFound": "Inga campingar eller ställplatser hittades i närheten.",
    "map.mapErrorBanner": "Kartan kunde inte laddas. Du kan fortfarande söka och utforska platser i listvyn.",
    "map.nearYou": "Platser nära dig",
    "map.places": "platser",
    "map.placesNearYou": "platser nära dig",
    "map.campingsNearby": "Campingar & ställplatser i närheten",
    "map.explore": "Utforska",
    "map.closePanel": "Stäng panel",
    "map.showResults": "Visa resultat",
    "map.navigate": "Navigera",
    "map.addToTrip": "Lägg till i resa",
    "map.open": "Öppet",
    "map.closed": "Stängt",
    "map.free": "Gratis",
    "map.camping": "Camping",
    "map.stellplatz": "Ställplats",
    "map.dumpStation": "Tömningsstation",
    "map.navigateHit": "Navigera hit",
    "map.place": "Plats",
    "map.restaurant": "Restaurang",
    "map.supermarket": "Matbutik",
    "map.attraction": "Sevärdhet",
    "map.parking": "Parkering",
    "map.fuelStation": "Bensinstation",
    "map.museum": "Museum",
    "map.nature": "Natur",
    "map.sights": "Sevärdheter",
    "map.grocery": "Matbutik",
    "map.perNight": "/natt",
    "submit.addressAutoHint": "Sök adressen först — land, region och stad fylls i automatiskt.",
    "submit.autoDetected": "Land, region och stad har fyllts i automatiskt!",
    "submit.placeExists": "Platsen finns redan",
    "submit.placeExistsDesc": "En plats med namnet \"{name}\" finns redan registrerad.",
    "submit.nearbyExists": "Plats i närheten finns redan",
    "submit.nearbyExistsDesc": "\"{name}\" finns redan registrerad på samma plats.",
    "submit.imageRejected": "Bild nekades",
    "submit.mainImage": "Huvudbild",
    "submit.contactInfo": "Kontaktuppgifter",
    "service.heroDesc": "Hitta verkstäder, tankning, vatten, sjukhus och mer — var du än befinner dig i Europa",
    "service.close": "Stäng",
    "service.clearSearch": "Rensa",
    "service.search": "Sök",
    "service.positionSharing": "Positionsdelning",
    "service.positionSharingDesc": "Dela din GPS-position eller aktivera SOS",
    "service.connectionError": "Kontrollera din anslutning och försök igen",
    "service.tryAgain": "Försök igen",
    "service.noResultsHint": "Testa att söka på något annat eller byt kategori",
    "service.resultsNearYou": "nära dig",
    "budget.noPeriod": "Ingen period",
    "budget.exceededBy": "Överskriden med",
    "budget.expenseDeleted": "Utgift borttagen",
    "budget.undo": "Ångra",
    "budget.receipt": "Kvitto",
    "budget.trip": "Resa",
    "whatsNew.title": "Vad är nytt",
    "whatsNew.subtitle": "Senaste uppdateringar och funktioner",
    "whatsNew.seoTitle": "Vad är nytt – Vantastika",
    "whatsNew.seoDesc": "Se senaste uppdateringarna och nyheterna i Vantastika.",
    "whatsNew.all": "Alla",
    "whatsNew.noUpdates": "Inga uppdateringar just nu",
    "whatsNew.type.new_feature": "Ny funktion",
    "whatsNew.type.improvement": "Förbättring",
    "whatsNew.type.bugfix": "Buggfix",
    "whatsNew.type.important": "Viktigt",
    "whatsNew.type.maintenance": "Drift",
    "whatsNew.readMore": "Läs mer",
    "search.benefit1": "Se alla campingar och ställplatser",
    "search.benefit2": "Filtrera på bekvämligheter och pris",
    "search.benefit3": "Läs omdömen och se betyg",
    "search.smartPlaceholder": "Sök platser, campingar, ställplatser, städer...",
    "search.recommendedFor": "Rekommenderat för",
    "search.typeCategory": "Kategori",
    "search.typeRegion": "Område",
    "search.typeCountry": "Land",
    "search.typePlace": "Plats",
    "search.noResultsHint": "Prova ett annat sökord eller ta bort några filter",
    "search.browseByCountry": "Bläddra efter land",
    "travelGuides.badge": "Reseguider",
    "travelGuides.heroTitle1": "Upptäck Europas bästa ",
    "travelGuides.heroTitle2": "resvägar",
    "travelGuides.heroSubtitle": "Detaljerade guider med historia, höjdpunkter och praktisk information. Omvandla inspiration till en riktig resa med ett klick.",
    "travelGuides.popular": "Populära resmål",
    "travelGuides.moreCountries": "Fler länder",
    "travelGuides.guidesCount": "{n} guider",
    "travelGuides.guidesSingular": "1 guide",
    "travelGuides.explore": "Utforska",
    "countryGuide.titlePrefix": "Reseguide",
    "countryGuide.allGuides": "Alla reseguider",
    "countryGuide.seoDesc": "Komplett reseguide för husbil i",
    "countryGuide.overview": "Översikt",
    "countryGuide.campingRules": "Camping & Övernattning",
    "countryGuide.campingRegulations": "Campingregler",
    "countryGuide.wildCamping": "Vildcamping",
    "countryGuide.campingTips": "Campingtips",
    "countryGuide.bestSeason": "Bästa säsongen",
    "countryGuide.drivingInfo": "Körning & Vägar",
    "countryGuide.trafficRules": "Trafikregler",
    "countryGuide.speedLimits": "Hastighetsbegränsningar",
    "countryGuide.tollsVignettes": "Tullar & Vinjetter",
    "countryGuide.currency": "Valuta",
    "countryGuide.fuel": "Bränsle",
    "countryGuide.emergency": "Nödnummer",
    "countryGuide.language": "Språk",
    "countryGuide.bestPlaces": "Bästa platserna att besöka",
    "countryGuide.safetyTips": "Säkerhetstips",
    "countryGuide.practicalTips": "Praktiska tips",
    "countryGuide.routeGuides": "Ruttguider",
    "countryGuide.guide": "Guide",
    "countryGuide.readGuide": "Läs guide",
    "countryGuide.searchCampings": "Sök campingar",
    "countryGuide.showOnMap": "Visa på karta",
    "countryGuide.planTrip": "Planera resa",
    "countryGuide.planTripHere": "Planera resa hit",
    "countryGuide.campings": "Campingar",
    "guideDetail.days": "dagar",
    "guideDetail.history": "Historia & bakgrund",
    "guideDetail.routeInfo": "Ruttinformation",
    "guideDetail.startPoint": "Startpunkt",
    "guideDetail.endPoint": "Slutpunkt",
    "guideDetail.distance": "Avstånd",
    "guideDetail.direction": "Riktning",
    "guideDetail.passesThrough": "Passerar genom",
    "guideDetail.overview": "Översikt",
    "guideDetail.highlights": "Höjdpunkter",
    "guideDetail.practical": "Praktiskt",
    "guideDetail.bestSeason": "Bästa säsongen",
    "guideDetail.vehicleType": "Fordonstyp",
    "guideDetail.motorhome": "Husbil",
    "guideDetail.car": "Bil",
    "guideDetail.motorcycle": "Motorcykel",
    "guideDetail.campervan": "Campervan",
    "guideDetail.warnings": "Att tänka på",
    "guideDetail.practicalTips": "Praktiska tips",
    "guideDetail.whyVisit": "Varför besöka: ",
    "guideDetail.readyToGo": "Redo att ge dig iväg?",
    "guideDetail.readyToGoDesc": "Omvandla denna guide till en riktig resa i reseplaneraren. Anpassa stopp, antal dagar och returresa efter dina önskemål.",
    "guideDetail.planThisTrip": "Planera denna resa",
    "guideDetail.refreshGuide": "Uppdatera guide",
    "guideDetail.generating": "Skapar detaljerad reseguide med AI...",
    "guideDetail.loading": "Laddar reseguide...",
    "guideDetail.premiumBenefit1": "Läs hela reseguiden med stopp och tips",
    "guideDetail.premiumBenefit2": "Se detaljerad ruttinformation",
    "guideDetail.premiumBenefit3": "Omvandla guiden till en riktig resa",
    "destination.motorhome": "Husbil i",
    "destination.planTrip": "Planera din husbilsresa till",
    "destination.planNow": "Planera din resa nu",
    "destination.viewGuides": "Se reseguider",
    "destination.whatWeOffer": "Vad Vantastika erbjuder",
    "destination.featureRoutes": "Ruttplanering",
    "destination.featureRoutesDesc": "Planera din rutt med stopp, avstånd och körtider.",
    "destination.featureCamping": "Campingar & ställplatser",
    "destination.featureCampingDesc": "Hitta de bästa campingarna och ställplatserna.",
    "destination.featureRules": "Körregler",
    "destination.featureRulesDesc": "Hastighetsbegränsningar, vägtullar och lokala regler.",
    "destination.featureSafety": "Säkerhet & SOS",
    "destination.featureSafetyDesc": "Nödnummer, vägassistans och säkerhetstips.",
    "destination.popularRoutes": "Populära rutter i",
    "destination.readyToGo": "Redo för resan?",
    "destination.ctaDesc": "Skapa ett gratis konto och börja planera din roadtrip idag.",
    "destination.getStarted": "Kom igång gratis",
    "destination.findCampings": "Hitta campingar",
    "share.shareOnInstagram": "Dela på Instagram",
    "share.shareOnFacebook": "Dela på Facebook",
    "share.linkCopied": "Länk kopierad!",
    "share.linkCopiedDesc": "Klistra in var du vill.",
    "ig.headline": "Planera din resa smartare med Vantastika",
    "ig.subheadline": "Rutt, budget och reseplanering på ett ställe för roadtrips, husbil och bilsemester.",
    "ig.ctaStart": "Kom igång gratis",
    "ig.ctaOpen": "Öppna Vantastika",
    "ig.ctaDemo": "Se hur det fungerar",
    "ig.demoTitle": "Så enkelt planerar du din resa",
    "ig.benefit1.title": "Planera hela resrutten",
    "ig.benefit1.desc": "Skapa din rutt med stopp, avstånd och tider.",
    "ig.benefit2.title": "Håll koll på resebudgeten",
    "ig.benefit2.desc": "Se vad resan kostar — före och under.",
    "ig.benefit3.title": "Bättre överblick",
    "ig.benefit3.desc": "Allt samlat på ett ställe, före och under resan.",
    "ig.free": "Gratis. Inget kreditkort krävs.",
    "projects.title": "Projekt",
    "projects.myProjects": "Mina projekt",
    "projects.subtitle": "Planera och följ upp dina byggen och renoveringar",
    "projects.pageDesc": "Hantera dina husbilsprojekt, renoveringar och byggen",
    "projects.loginRequired": "Logga in för att hantera dina projekt",
    "projects.create": "Nytt projekt",
    "projects.createTitle": "Skapa projekt",
    "projects.createFirst": "Skapa ditt första projekt",
    "projects.creating": "Skapar...",
    "projects.emptyTitle": "Inga projekt ännu",
    "projects.emptyDesc": "Skapa ett projekt för att organisera dina byggen, renoveringar och uppgraderingar med materiallistor, uppgifter och budget.",
    "projects.name": "Projektnamn",
    "projects.namePlaceholder": "t.ex. Batterikonvertering",
    "projects.description": "Beskrivning",
    "projects.descPlaceholder": "Vad handlar projektet om?",
    "projects.categoryLabel": "Kategori",
    "projects.priorityLabel": "Prioritet",
    "projects.startDate": "Startdatum",
    "projects.endDate": "Slutdatum",
    "projects.budget": "Budget",
    "projects.actualCost": "Faktisk kostnad",
    "projects.notes": "Anteckningar",
    "projects.notesPlaceholder": "Lägg till anteckningar...",
    "projects.save": "Spara",
    "projects.edit": "Redigera",
    "projects.cancel": "Avbryt",
    "projects.add": "Lägg till",
    "projects.share": "Dela",
    "projects.notFound": "Projektet hittades inte",
    "projects.backToProjects": "Tillbaka till projekt",
    "projects.status.not_started": "Inte påbörjat",
    "projects.status.in_progress": "Pågår",
    "projects.status.completed": "Klart",
    "projects.priority.low": "Låg",
    "projects.priority.medium": "Mellan",
    "projects.priority.high": "Hög",
    "projects.priority.urgent": "Akut",
    "projects.category.battery": "Batteri",
    "projects.category.solar": "Solpanel",
    "projects.category.bathroom": "Badrum",
    "projects.category.kitchen": "Kök",
    "projects.category.water_system": "Vattensystem",
    "projects.category.electrical": "El",
    "projects.category.interior": "Interiör",
    "projects.category.exterior": "Exteriör",
    "projects.category.mechanical": "Mekanik",
    "projects.category.other": "Övrigt",
    "projects.tab.overview": "Översikt",
    "projects.tab.materials": "Material",
    "projects.tab.tasks": "Uppgifter",
    "projects.tab.files": "Filer",
    "projects.tab.history": "Historik",
    "projects.tasksCompleted": "Uppgifter klara",
    "projects.materialsBought": "Material köpt",
    "projects.budgetOverview": "Budgetöversikt",
    "projects.materialsCost": "Materialkostnad",
    "projects.spent": "Spenderat",
    "projects.remaining": "Återstår",
    "projects.total": "Totalt",
    "projects.bought": "Köpt",
    "projects.addMaterial": "Lägg till material",
    "projects.materialName": "Produktnamn",
    "projects.quantity": "Antal",
    "projects.price": "Pris",
    "projects.storeName": "Butik",
    "projects.storeUrl": "Länk (URL)",
    "projects.noMaterials": "Inga material tillagda ännu",
    "projects.addTask": "Lägg till uppgift",
    "projects.taskTitle": "Uppgiftstitel",
    "projects.taskDesc": "Beskrivning",
    "projects.noTasks": "Inga uppgifter tillagda ännu",
    "projects.completedAt": "Slutfört",
    "projects.status_label": "Status",
    "projects.filter.all": "Alla",
    "projects.filter.bought": "Köpt",
    "projects.filter.not_bought": "Inte köpt",
    "projects.filter.pending": "Återstår",
    "projects.filter.done": "Klart",
    "projects.uploadFile": "Ladda upp fil",
    "projects.uploading": "Laddar upp...",
    "projects.maxFileSize": "Max 15 MB",
    "projects.fileTooLarge": "Filen är för stor (max 15 MB)",
    "projects.fileUploaded": "Fil uppladdad",
    "projects.uploadError": "Uppladdningen misslyckades",
    "projects.noFiles": "Inga filer tillagda ännu",
    "projects.noHistory": "Ingen aktivitet ännu",
    "projects.shareProject": "Dela projekt",
    "projects.generateLink": "Skapa delningslänk",
    "projects.linkGenerated": "Delningslänk skapad",
    "projects.exportPdf": "Exportera som PDF",
    "projects.pdfFull": "Hela projektet",
    "projects.pdfMaterials": "Bara material",
    "projects.pdfTasks": "Bara uppgifter",
    "projects.pdfSummary": "Sammanfattning",
    "projects.deleteProject": "Ta bort projekt",
    "projects.confirmDeleteTitle": "Ta bort projekt?",
    "projects.confirmDeleteDesc": "Detta tar permanent bort projektet och allt dess material, uppgifter, filer och historik. Åtgärden kan inte ångras.",
    "projects.deleted": "Projekt borttaget",
    "projects.vehicleLabel": "Fordon",
    "projects.linkedVehicle": "Kopplat fordon",
    "projects.linkVehicle": "Koppla fordon",
    "projects.noVehicle": "Inget fordon kopplat",
    "projects.selectVehicle": "Välj fordon",
    "projects.noVehicleOption": "Inget",
    "projects.duplicate": "Duplicera",
    "projects.editMaterial": "Redigera material",
    "projects.editTask": "Redigera uppgift",
    "projects.saveMaterial": "Spara material",
    "projects.saveTask": "Spara uppgift",
    "projects.swipeToDelete": "Svep för att ta bort",
    "projects.moreActions": "Fler åtgärder",
    "projects.editProject": "Redigera projekt",
    "upload.validating": "Validerar...",
    "upload.processing": "Bearbetar bild...",
    "upload.converting": "Konverterar HEIC...",
    "upload.resizing": "Ändrar storlek...",
    "upload.compressing": "Komprimerar...",
    "upload.thumbnailing": "Skapar miniatyr...",
    "upload.almostDone": "Nästan klart...",
    "upload.uploading": "Laddar upp...",
    "upload.done": "Klart!",
    "upload.failed": "Uppladdningen misslyckades",
    "upload.fileTooLarge": "Filen är för stor",
    "upload.imageRejected": "Bilden nekades",
  },
  no: {
    "offline.message": "Du er offline — lagrede data vises fra cache",
    "offline.syncing": "Synkroniserer data...",
    "offline.synced": "Alt synkronisert!",
    "confirm.deleteTitle": "Er du sikker?",
    "confirm.deleteDesc": "Denne handlingen kan ikke angres.",
    "confirm.cancel": "Avbryt",
    "confirm.delete": "Slett",
    "confirm.deleteTrip": "Slette denne turen?",
    "confirm.deleteTripDesc": "Turen og alle data slettes permanent.",
    "confirm.deletePost": "Slette dette innlegget?",
    "confirm.deletePostDesc": "Innlegget slettes permanent.",
    "confirm.deleteBudget": "Slette dette budsjettet?",
    "confirm.deleteBudgetDesc": "Budsjettet og alle utgifter slettes permanent.",
    "confirm.deleteNotification": "Slette denne varslingen?",
    "confirm.deleteNotificationDesc": "Varslingen slettes permanent.",
    "confirm.deleteMaterial": "Slette dette materialet?",
    "confirm.deleteMaterialDesc": "Materialet slettes permanent fra prosjektet.",
    "confirm.deleteTask": "Slette denne oppgaven?",
    "confirm.deleteTaskDesc": "Oppgaven slettes permanent fra prosjektet.",
    "confirm.deleteFile": "Slette denne filen?",
    "confirm.deleteFileDesc": "Filen slettes permanent.",
    "confirm.deleteMessage": "Slette denne meldingen?",
    "confirm.deleteMessageDesc": "Meldingen slettes permanent.",
    "confirm.deleteUpdate": "Slette denne oppdateringen?",
    "confirm.deleteUpdateDesc": "Oppdateringen slettes permanent.",
    "confirm.deleteCamping": "Slette denne campingplassen permanent?",
    "confirm.deleteCampingDesc": "Campingplassen slettes permanent.",
    "confirm.unpublishTrip": "Avpublisere denne turen?",
    "confirm.unpublishTripDesc": "Turen vil ikke lenger være synlig for andre.",
    "confirm.endTrip": "Avslutte denne turen?",
    "confirm.endTripDesc": "Turen markeres som fullført.",
    "nav.explore": "Campingplasser",
    "nav.map": "Utforsk",
    "nav.trips": "Reiseplanlegger",
    "nav.favorites": "Favoritter",
    "nav.login": "Logg inn",
    "nav.signup": "Registrer",
    "nav.logout": "Logg ut",
    "nav.bottom.trips": "Ny tur",
    "nav.bottom.explore": "Utforsk",
    "nav.bottom.map": "Kart",
    "nav.bottom.saved": "Lagret",
    "nav.bottom.profile": "Profil",
    "nav.bottom.login": "Logg inn",
    "nav.bottom.signup": "Registrer",
    "nav.bottom.home": "Hjem",
    "nav.bottom.newTrip": "Ny tur",
    "nav.bottom.myTrips": "Mine turer",
    "nav.checklists": "Sjekklister",
    "nav.travelGuides": "Reiseguider",
    "nav.menuMine": "Mine",
    "nav.menuCreate": "Opprett",
    "nav.menuApp": "App",
    "nav.menuOther": "Annet",
    "nav.installApp": "Installer appen",
    "nav.settings": "Innstillinger",
    "myTrips.showTrip": "Vis tur",
    "myTrips.liveView": "Live-visning",
    "myTrips.details": "Detaljer",
    "myTrips.revertToPlanned": "Tilbakestill til planlagt",
    "myTrips.reactivateTrip": "Gjenoppta tur",
    "myTrips.loadingLive": "Laster live-visning...",
    "footer.integrationTerms": "Integrasjonsvilkår",
    "dash.greeting.morning": "God morgen",
    "dash.greeting.day": "Hei",
    "dash.greeting.afternoon": "God ettermiddag",
    "dash.greeting.evening": "God kveld",
    "dash.greeting.night": "God natt",
    "dash.subtitle.active": "Reisen din pågår – her er din status",
    "dash.subtitle.planning": "Du har reiser å forberede",
    "dash.subtitle.completed": "Bra jobbet! Her er reiseoversikten din",
    "dash.subtitle.empty": "Hva vil du gjøre i dag?",
    "dash.paused": "Pauset",
    "dash.ongoing": "Pågående reise",
    "dash.total": "Totalt",
    "dash.stops": "Stopp",
    "dash.done": "Ferdig",
    "dash.nextDest": "Neste destinasjon",
    "dash.continueTrip": "Fortsett reisen",
    "dash.openActive": "Åpne aktiv reise",
    "dash.journal": "Journal",
    "dash.budget": "Budsjett",
    "dash.findPlace": "Finn sted",
    "dash.upcoming": "Kommende reise",
    "dash.today": "I dag!",
    "dash.tomorrow": "I morgen",
    "dash.daysLeft": "{n} dager igjen",
    "dash.continuePlan": "Fortsett planlegging",
    "dash.start": "Start",
    "dash.morePlanned": "Flere planlagte reiser",
    "dash.findCamping": "Finn camping",
    "dash.findCampingSub": "2 800+ steder",
    "dash.guides": "Reiseguider",
    "dash.guidesSub": "21+ land",
    "dash.checklists": "Sjekklister",
    "dash.checklistsSub": "Pakk smart",
    "dash.wishlist": "Ønskeliste",
    "dash.vehicle": "Kjøretøy",
    "dash.projects": "Prosjekter",
    "dash.community": "Community",
    "dash.popularDest": "Populære destinasjoner",
    "dash.seeAll": "Se alle",
    "dash.planNew": "Planlegg ny reise",
    "dash.planNewSub": "Steg-for-steg veiviser",
    "dash.season.spring": "Våreventyr",
    "dash.season.summer": "Sommer på veien",
    "dash.season.autumn": "Høstens roadtrips",
    "dash.season.winter": "Vintereventyr",
    "dash.tripDone": "Reise avsluttet",
    "dash.visited": "Besøkt",
    "dash.seeSummary": "Se sammendrag",
    "dash.planNewOld": "Planlegg ny reise",
    "dash.emptyTitle": "På tide å planlegge din første reise!",
    "dash.emptyDesc": "Opprett en reise og legg til stopp, budsjett og sjekklister.",
    "dash.createTrip": "Opprett reise",
    "dash.stopsCount": "{count} stopp",
    "dash.visitedCount": "{count} besøkt",
    "dash.onboarding.step1": "Planlegg en reise",
    "dash.onboarding.step1Desc": "Lag din første roadtrip med stopp og datoer",
    "dash.onboarding.step2": "Lagre drømmemål",
    "dash.onboarding.step2Desc": "Bygg ønskelisten din med steder å besøke",
    "dash.onboarding.step3": "Utforsk campingplasser",
    "dash.onboarding.step3Desc": "Bla gjennom 2 800+ campingplasser i Europa",
    "dash.onboarding.stepVehicle": "Sett opp kjøretøyet ditt",
    "dash.onboarding.stepVehicleDesc": "Legg til bobilen, bilen eller campingbussen din",
    "dash.onboarding.stepProject": "Start et prosjekt",
    "dash.onboarding.stepProjectDesc": "Følg renoveringer, oppgraderinger og byggeprosjekter",
    "dash.emptyDescReturning": "Planlegg din første tur – de lagrede dataene dine er klare til bruk!",
    "dash.recentProjects": "Dine prosjekter",
    "dash.popularGuides": "Populære guider",
    "dash.noProjects": "Ingen prosjekter ennå",
    "dash.draft": "Utkast",
    "dash.continueDraft": "Fortsett planlegging",
    "dash.untitled": "Navnløs reise",
    "dash.nextStep": "Neste steg",
    "dash.nextStepStops": "Legg til ditt første stopp",
    "dash.nextStepDate": "Velg reisedato",
    "dash.nextStepChecklist": "Fullfør sjekklisten",
    "dash.prepTitle": "Forbered reisen din",
    "dash.prepBudget": "Sett et budsjett for å holde oversikten",
    "dash.prepStops": "Legg til flere stopp langs ruten",
    "dash.prepChecklist": "Sjekk pakkelisten din",
    "dash.packed": "pakket",
    "dash.incompleteTitle": "Fullfør planleggingen",
    "dash.incompleteDate": "Legg til en avreisedato",
    "dash.incompleteStops": "Legg til stopp på ruten din",
    "dash.postTripTitle": "Hva skjer nå?",
    "dash.postTripShare": "Del reisen din med venner",
    "dash.postTripReview": "Vurder stedene du besøkte",
    "dash.postTripJournal": "Skriv om opplevelsene dine",
    "dash.postTripMsg": "Du reiste {km} km og besøkte {stops} steder — flott eventyr!",
    "nav.myTrips": "Mine reiser",
    "nav.submit": "Send inn sted",
    "nav.help": "Slik bruker du appen",
    "help.title": "Slik bruker du appen",
    "help.whatIs.title": "Hva er Vantastika?",
    "help.whatIs.body": "Din reisekompis for bobil, campingbil og bilturer i Europa. Finn campingplasser, planlegg ruter og hold styr på budsjettet — alt på ett sted.",
    "help.startTrip.title": "Start en tur",
    "help.startTrip.body": "Trykk på Turplanleggeren, gi turen et navn, legg til stopp og datoer. Appen beregner avstander og drivstoffkostnader for deg.",
    "help.search.title": "Søk etter steder",
    "help.search.body": "Bruk Utforsk eller søkesiden for å finne campingplasser og servicestasjoner nær ethvert sted i Europa.",
    "help.map.title": "Bruk kartet",
    "help.map.body": "Åpne Utforsk for å se kartet. Søk etter en by eller bruk din posisjon. Trykk på markører for detaljer og veibeskrivelse.",
    "help.addStop.title": "Legg til et stopp",
    "help.addStop.body": "Når du ser på en campingplass, trykk 'Legg til i tur' for å inkludere den i ruten din.",
    "help.share.title": "Del og bli inspirert",
    "help.share.body": "Gjør turen din offentlig så andre kan se ruten din. Bla gjennom delte turer for inspirasjon.",
    "help.favorites.title": "Lagre favoritter",
    "help.favorites.body": "Trykk på hjertet på en campingplass for å lagre den. Organiser favoritter i samlinger.",
    "hero.title1": "Planlegg hele reisen",
    "hero.title2": "på ett sted.",
    "hero.subtitle": "Verifiserte steder, smarte ruter, budsjettkontroll, sjekklister og AI-hjelp.",
    "hero.searchPlaceholder": "Hvor vil du reise?",
    "hero.search": "Søk",
    "hero.ctaPlan": "Planlegg reisen",
    "hero.ctaExplore": "Finn campinger",
    "hero.ctaSubmit": "Tips om et sted",
    "features.find": "Finn campinger",
    "features.findDesc": "Ekte campingplasser med priser, anmeldelser og alt du trenger å vite",
    "features.save": "Lagre favoritter",
    "features.saveDesc": "Hold oversikt over dine beste steder",
    "features.plan": "Planlegg reiser",
    "features.planDesc": "Ruter, stopp og drivstoffkostnader. Klart på sekunder",
    "features.europe": "Hele Europa",
    "features.europeDesc": "Tusenvis av steder du kan stole på, i hele Europa",
    "featured.title": "Utvalgte Campinger",
    "featured.viewAll": "Vis alle →",
    "search.title": "Utforsk Campinger",
    "search.placeholder": "Søk campinger...",
    "search.country": "Land",
    "search.allCountries": "Alle land",
    "search.filters": "Filtre",
    "search.clearAll": "Fjern alle",
    "search.minPrice": "Min pris",
    "search.maxPrice": "Maks pris",
    "search.region": "Region",
    "search.regionPlaceholder": "f.eks. Nordland",
    "search.noResults": "Ingen campinger funnet",
    "search.adjustFilters": "Prøv å justere filtrene",
    "search.found": "campinger funnet",
    "filter.electricity": "Strømtilkobling",
    "filter.freshWater": "Ferskvann",
    "filter.greyWater": "Gråvannsavløp",
    "filter.toilet": "Tømming toalettkassett",
    "filter.shower": "Dusj",
    "filter.wc": "WC",
    "filter.petFriendly": "Dyrevennlig",
    "filter.openAllYear": "Åpent hele året",
    "filter.familyFriendly": "Familievennlig",
    "filter.largeMotorhomes": "Store bobiler",
    "filter.nearWater": "Nær sjø/strand",
    "filter.spaWellness": "Spa/wellness",
    "filter.wifi": "WiFi",
    "filter.laundry": "Vaskeri",
    "filter.restaurant": "Restaurant",
    "detail.about": "Om",
    "detail.amenities": "Fasiliteter",
    "detail.contact": "Kontakt",
    "detail.website": "Nettside",
    "detail.checkIn": "Innsjekking",
    "detail.checkOut": "Utsjekking",
    "detail.location": "Plassering",
    "detail.openInMaps": "Åpne i Google Maps →",
    "detail.mapComingSoon": "Kart kommer snart",
    "detail.back": "Tilbake",
    "detail.save": "Lagre",
    "detail.saved": "Lagret",
    "detail.perNight": "per natt",
    "detail.reviews": "Anmeldelser",
    "detail.writeReview": "Skriv en anmeldelse",
    "detail.noReviews": "Ingen anmeldelser ennå",
    "detail.submitReview": "Send anmeldelse",
    "detail.yourRating": "Din vurdering",
    "detail.yourComment": "Din kommentar (valgfritt)",
    "detail.loginToReview": "Logg inn for å skrive en anmeldelse",
    "detail.notFound": "Campingen ble ikke funnet",
    "detail.backToSearch": "Tilbake til søk",
    "favorites.title": "Mine Favoritter",
    "favorites.saveYour": "Lagre favorittcampingene dine",
    "favorites.signUpPrompt": "Registrer deg eller logg inn for å lagre campinger.",
    "favorites.empty": "Ingen favoritter ennå",
    "favorites.emptyDesc": "Begynn å utforske og lagre campinger du liker",
    "favorites.explore": "Utforsk Campinger",
    "favorites.emptyCollection": "Ingen favoritter i denne samlingen",
    "wishlist.title": "Ønskeliste",
    "wishlist.save": "Lagre i ønskeliste",
    "wishlist.saved": "Lagret i ønskelisten!",
    "wishlist.alreadySaved": "Allerede i ønskelisten",
    // SaveGateSheet
    "saveGate.wishlist.savedFor": "Vi har lagret stedet",
    "saveGate.trip.savedFor": "Vi har lagret turen",
    "saveGate.place.savedFor": "Vi har lagret",
    "saveGate.holdsFor24h": "Vi tar vare på det i 24 timer — opprett en konto så det aldri forsvinner.",
    "saveGate.wishlist.benefit1": "Synkroniser mellom telefon og datamaskin",
    "saveGate.wishlist.benefit2": "Få varsel når prisen synker",
    "saveGate.wishlist.benefit3": "Del med reisekompis",
    "saveGate.trip.benefit1": "Aktiver offline-modus på veien",
    "saveGate.trip.benefit2": "Smart navigasjon tilpasset kjøretøyet",
    "saveGate.trip.benefit3": "Del live-posisjon med familien",
    "saveGate.place.benefit1": "Lagre stedet ditt permanent",
    "saveGate.place.benefit2": "Få tips om steder i nærheten",
    "saveGate.place.benefit3": "Tilgjengelig på alle enheter",
    "saveGate.socialProof": "reisende i Norden bruker Vantastika",
    "saveGate.createAccount15s": "Opprett konto på 15 sek",
    "saveGate.alreadyMember": "Allerede medlem?",
    "saveGate.logIn": "Logg inn",
    "saveGate.continueGuest": "Fortsett som gjest — lagres 24 t",
    "saveGate.noCardNeeded": "Gratis. Ingen kort kreves.",
    // GuestRescueSheet
    "guestRescue.title": "Du har {count} lagrede ting i fare",
    "guestRescue.expiresIn": "Utløper om {time}",
    "guestRescue.expired": "Utløpt — opprett konto for å gjenopprette",
    "guestRescue.trip": "Tur",
    "guestRescue.wishlist": "Ønskeliste",
    "guestRescue.rescueCta": "Redd tingene mine — opprett konto",
    "guestRescue.footnote": "Gratis for alltid. Ingen betalingskort.",
    "saveGate.hoursShort": "t",
    "saveGate.minutesShort": "min",
    "guestRescue.toastTitle": "Tips: Opprett en konto",
    "guestRescue.toastBody": "Lagre på alle enheter, så du aldri mister turene dine.",
    "guestRescue.toastCta": "Gjør det nå",
    "nav.bottom.guestSaves": "{count} lagret",
    "wishlist.loginMsg": "Logg inn for å lagre steder du vil besøke.",
    "wishlist.empty": "Ønskelisten din er tom",
    "wishlist.emptyDesc": "Lagre steder du drømmer om å besøke og planlegg reiser fra dem senere.",
    "wishlist.add": "Legg til sted",
    "wishlist.searchPlace": "Søk etter et sted å lagre",
    "wishlist.searchPlaceholder": "By, land, attraksjon, camping...",
    "wishlist.myNote": "Mitt notat",
    "wishlist.notePlaceholder": "Skriv et notat om dette stedet...",
    "wishlist.addNote": "Trykk for å legge til notat...",
    "wishlist.saveNote": "Lagre",
    "wishlist.nearby": "I nærheten",
    "wishlist.nearbyAttractions": "Attraksjoner",
    "wishlist.nearbyAccommodation": "Overnatting",
    "wishlist.nearbyRestaurants": "Restauranter",
    "wishlist.noNearby": "Ingen nærliggende steder funnet i databasen vår ennå.",
    "wishlist.planTrip": "Planlegg reise hit",
    "wishlist.planMultiple": "Planlegg reise",
    "wishlist.deleteTitle": "Fjerne fra ønskelisten?",
    "wishlist.deleteDesc": "Stedet fjernes fra ønskelisten din.",
    "wishlist.selectedCount": "valgt",
    "wishlist.pickFromWishlist": "Velg fra ønskeliste",
    "wishlist.loading": "Laster...",
    "wishlist.emptyPicker": "Ønskelisten er tom. Lagre steder først!",
    "wishlist.addSelected": "Legg til valgte",
    "wishlist.fromWishlist": "Fra ønskeliste",
    "wishlist.planWithPlaces": "Planlegg reise med valgte steder",
    "wishlist.planWithPlacesDesc": "Velg rekkefølge og tildel hver plass en rolle.",
    "wishlist.orderMode": "Ruterekkefølge",
    "wishlist.myOrder": "Behold min rekkefølge",
    "wishlist.myOrderDesc": "Bruk nøyaktig rekkefølgen du valgte",
    "wishlist.smartOrder": "Optimaliser ruten",
    "wishlist.smartOrderDesc": "Finn smarteste rekkefølgen med mindre omvei",
    "wishlist.assignRoles": "Hvilken rolle har hvert sted?",
    "wishlist.role_destination": "Destinasjon",
    "wishlist.role_stop": "Stopp",
    "wishlist.role_attraction": "Attraksjon",
    "wishlist.role_overnight": "Overnatting",
    "wishlist.startPlanning": "Start planlegging",
    "wishlist.places": "steder",
    "notifications.emptyTitle": "Ingen varsler ennå",
    "notifications.emptyDesc": "Når noe skjer, ser du det her",
    "notifications.title": "Varsler",
    "notifications.unreadCount": "uleste",
    "notifications.noUnread": "Ingen uleste varsler",
    "notifications.markAll": "Merk alle",
    "notifications.markRead": "Merk som lest",
    "notifications.show": "Vis",
    "notifications.delete": "Slett",
    "notifications.goToInbox": "Gå til innboks og meldinger",
    "notifications.allRead": "Alle merket som lest",
    "trips.title": "Mine Reiser",
    "trips.plan": "Planlegg Reisene Dine",
    "trips.loginPrompt": "Logg inn for å opprette og administrere campingreisene dine.",
    "trips.newTrip": "Ny Reise",
    "trips.createTrip": "Opprett en Ny Reise",
    "trips.tripTitle": "Reisetittel",
    "trips.startDate": "Startdato",
    "trips.endDate": "Sluttdato",
    "trips.notes": "Notater for reisen...",
    "trips.create": "Opprett Reise",
    "trips.empty": "Ingen reiser ennå",
    "trips.emptyDesc": "Opprett din første reise",
    "trips.stops": "Stopp",
    "trips.noStops": "Ingen stopp lagt til ennå.",
    "trips.created": "Reise opprettet!",
    "trips.failed": "Kunne ikke opprette reise",
    "trips.partialSave": "Turen ble delvis lagret",
    "trips.partialDesc": "Noen deler kunne ikke lagres. Gå til Mine reiser for å redigere.",
    "trips.unknownPlace": "Ukjent sted",
    "trips.ideaLoaded": "Reiseideen din er lastet inn i planleggeren nedenfor.",
    "trips.newInspiration": "Ny inspirasjon",
    "trips.requireAuthSave": "Du har brukt tid på å planlegge denne turen. Opprett en konto så lagres alt automatisk.",
    "trips.daysAbbrev": "dager",
    "trips.perDay": "/dag",
    "trips.socialProof": "reisende planlegger her",
    "trips.freeLabel": "100% gratis",
    "trips.ideaCreating": "Lager reiseideen din...",
    "trips.ideaBuilding": "Vi bygger en komplett reiseplan for {destination} med stopp, severdigheter og campingplasser.",
    "trips.ideaTitle": "Reiseidé: {title}",
    "trips.campingsNearby": "Campingplasser og plasser i nærheten",
    "trips.restoreWelcome": "Velkommen tilbake!",
    "trips.restoreSaving": "Vi lagrer turen din \"{title}\" automatisk...",
    "trips.restoreFailed": "Kunne ikke gjenopprette turen",
    "trips.restoreFailedDesc": "De lagrede turdata var ufullstendige. Planlegg en ny tur.",
    "trips.restoreErrorDesc": "Noe gikk galt. Planlegg en ny tur.",
    "trips.partialStopsFailed": "Turen ble lagret, men noen stopp mislyktes",
    "trips.partialStopsDesc": "Gå til Mine reiser og rediger turen for å legge til manglende stopp.",
    "contact.sendFailed": "Kunne ikke sende",
    "contact.tryAgain": "Prøv igjen senere.",
    "contact.sent": "Melding sendt!",
    "contact.writeMessage": "Skriv en melding",
    "contact.rateLimited": "Vent litt før du sender igjen",
    "weather.fetchError": "Kunne ikke hente værdata. Prøv igjen senere.",
    "cost.fetchFailed": "Kunne ikke hente kostnadsestimat",
    "cost.adults": "voksne",
    "cost.children": "barn",
    "cost.person": "person",
    "cost.persons": "personer",
    "wizard.nextStepDesc": "Hva vil du gjøre videre?",
    "wizard.addBudgetCta": "Sett et budsjett for turen",
    "wizard.addMoreStopsCta": "Legg til flere stopp og campingplasser",
    "wizard.viewMyTrips": "Se mine reiser",
    "myTrips.chatUnavailable": "Resechatten er ikke tilgjengelig",
    "myTrips.chatClosed": "Chatten stenges når reisen er fullført.",
    "myTrips.chatOnlyShared": "Chat er kun tilgjengelig i delte reiser med reisefølge.",
    "contact.subject": "Emne",
    "contact.subjectPlaceholder": "Beskriv kort hva saken gjelder",
    "contact.message": "Melding",
    "contact.messagePlaceholder": "Fortell oss mer i detalj...",
    "contact.creating": "Oppretter...",
    "contact.sendTicket": "Send henvendelse",
    "contact.allTickets": "Alle henvendelser",
    "contact.waitingReply": "Vantastika venter på ditt svar",
    "contact.resolvedLabel": "Løst",
    "contact.closedNotice": "Denne saken er lukket. Opprett en ny henvendelse hvis du trenger mer hjelp.",
    "contact.you": "Du",
    "contact.replyPlaceholder": "Skriv svaret ditt...",
    "contact.sending": "Sender...",
    "contact.reply": "Svar",
    "places.yourPosition": "Din posisjon",
    "places.unnamedPlace": "Navnløst sted",
    "places.myPosition": "Min posisjon",
    "map.listView": "Listevisning",
    "map.mapView": "Kartvisning",
    "shared.joinedTrip": "Du har blitt med på turen!",
    "shared.joinedDesc": "Du kan nå se og redigere turen sammen med reisefølget.",
    "shared.couldNotJoin": "Kunne ikke bli med",
    "shared.tryAgainLater": "Prøv igjen senere.",
    "shared.searchStartPlace": "Søk etter startstedet ditt...",
    "shared.savingCopy": "Lagrer...",
    "shared.saveAsCopy": "Lagre som egen kopi",
    "camping.blurBenefitDesc": "Se full beskrivelse og fasiliteter",
    "camping.blurBenefitPrice": "Vis priser og prishistorikk",
    "camping.blurBenefitReviews": "Les anmeldelser fra andre reisende",
    "camping.blurBenefitMap": "Åpne kart og naviger direkte",
    "login.title": "Velkommen tilbake",
    "login.subtitle": "Logg inn på Vantastika-kontoen din",
    "login.email": "E-post",
    "login.password": "Passord",
    "login.submit": "Logg Inn",
    "login.loading": "Logger inn...",
    "login.failed": "Innlogging mislyktes",
    "login.noAccount": "Har du ingen konto?",
    "login.signUp": "Registrer deg",
    "login.forgotPassword": "Glemt passord?",
    "login.or": "ELLER",
    "login.verificationFailed": "Verifiseringen mislyktes. Prøv igjen.",
    "login.captchaFailed": "CAPTCHA-verifisering mislyktes. Prøv igjen.",
    "login.tooManyAttempts": "For mange innloggingsforsøk. Vent litt.",
    "login.wrongCredentials": "Feil e-post eller passord.",
    "social.continueGoogle": "Fortsett med Google",
    "social.continueApple": "Fortsett med Apple",
    "social.loginFailed": "Innlogging mislyktes",
    "social.unknownError": "Ukjent feil",
    "forgot.title": "Glemt passord",
    "forgot.checkInbox": "Sjekk innboksen din!",
    "forgot.enterEmail": "Skriv inn e-postadressen din, så sender vi en tilbakestillingslenke.",
    "forgot.sentTo": "Vi har sendt en e-post til",
    "forgot.checkSpam": "med en lenke for å tilbakestille passordet ditt. Sjekk søppelpost om du ikke finner det.",
    "forgot.backToLogin": "Tilbake til innlogging",
    "forgot.emailLabel": "E-postadresse",
    "forgot.emailPlaceholder": "din@epost.no",
    "forgot.sending": "Sender...",
    "forgot.sendLink": "Send tilbakestillingslenke",
    "forgot.couldNotSend": "Kunne ikke sende tilbakestillings-e-post",
    "forgot.enterEmailFirst": "Skriv inn e-postadressen din",
    "reset.title": "Nytt passord",
    "reset.subtitle": "Velg et nytt passord for kontoen din.",
    "reset.newPassword": "Nytt passord",
    "reset.confirmPassword": "Bekreft passord",
    "reset.placeholder": "Minst 8 tegn, bokstav + tall",
    "reset.tooShort": "Passordet må være minst 8 tegn",
    "reset.noMatch": "Passordene stemmer ikke",
    "reset.updating": "Oppdaterer...",
    "reset.update": "Oppdater passord",
    "reset.couldNotUpdate": "Kunne ikke oppdatere passord",
    "reset.success": "Passord oppdatert!",
    "reset.successDesc": "Passordet ditt er endret.",
    "reset.goToProfile": "Gå til profilen min",
    "reset.invalidLink": "Ugyldig lenke",
    "reset.invalidLinkDesc": "Tilbakestillingslenken er ugyldig eller utløpt. Be om en ny fra innloggingssiden.",
    "signup.title": "Opprett en konto",
    "signup.subtitle": "Begynn å lagre campinger og planlegge reiser",
    "signup.name": "Visningsnavn",
    "signup.email": "E-post",
    "signup.password": "Passord",
    "signup.gdpr": "Jeg godtar personvernreglene og samtykker til behandling av mine personopplysninger i samsvar med GDPR.",
    "signup.submit": "Registrer",
    "signup.loading": "Oppretter konto...",
    "signup.failed": "Registrering mislyktes",
    "signup.success": "Konto opprettet!",
    "signup.checkEmail": "Sjekk e-posten din for å bekrefte kontoen.",
    "signup.acceptPrivacy": "Godta personvernreglene",
    "signup.hasAccount": "Har du allerede en konto?",
    "signup.logIn": "Logg inn",
    "signup.heroTitle": "Planlegg neste tur på 60 sekunder",
    "signup.heroSubtitle": "Opprett en gratis konto og få tilgang til hele Vantastika – ingen annonser, ingen kort.",
    "signup.benefit1": "Lagre rutene dine for alltid",
    "signup.benefit2": "Campingforslag langs veien",
    "signup.benefit3": "Bli en del av community",
    "signup.benefit4": "Helt gratis – kort er ikke nødvendig",
    "signup.titleShort": "Opprett kontoen din",
    "signup.subtitleShort": "Tar 10 sekunder. Kort trengs ikke.",
    "signup.submitShort": "Opprett gratis konto",
    "signup.miniConsent": "Jeg godtar",
    "signup.ageMini": "Jeg er minst 16 år.",
    "signup.trustLine": "🔒 Vi deler aldri e-posten din. Avslutt når som helst.",
    "signup.freeForever": "Gratis for alltid",
    "signup.confirmPasswordPlaceholder": "Gjenta passordet ditt",
    "footer.desc": "Planlegg turen, finn de rette stedene og reis med full kontroll. Bobil, van eller telt.",
    "footer.explore": "Utforsk",
    "footer.searchCampings": "Søk Campinger",
    "footer.account": "Konto",
    "footer.legal": "Juridisk",
    "footer.privacy": "Personvernerklæring",
    "footer.terms": "Vilkår for bruk",
    "footer.rights": "Alle rettigheter forbeholdt.",
    "footer.followInstagram": "Følg Vantastika på Instagram",
    "footer.followFacebook": "Følg Vantastika på Facebook",
    "footer.followUs": "Følg oss",
    "geo.myLocation": "Min plassering",
    "geo.nearMe": "Nær meg",
    "geo.permissionDenied": "Stedstilgang nektet",
    "geo.detecting": "Finner plassering...",
    "trip.editTrip": "Rediger reise",
    "trip.tripTitle": "Reisetittel",
    "trip.destination": "Destinasjon (søk adresse...)",
    "trip.startDate": "Startdato",
    "trip.consumption": "Forbruk (l/100km)",
    "trip.consumptionEv": "Forbruk (kWh/100km)",
    "trip.fuelPrice": "Dieselpris",
    "trip.fuelPriceBensin": "Bensinpris",
    "trip.fuelPriceEv": "Strømpris",
    "trip.fuelPriceUnitEv": "/kWh",
    "trip.vehicleType": "Kjøretøytype",
    "trip.vehicleDiesel": "Diesel",
    "trip.vehicleBensin": "Bensin",
    "trip.vehicleEv": "Elbil",
    "trip.travelMode": "Reisemåte",
    "trip.travelModeCar": "Bil",
    "trip.travelModeMotorhome": "Bobil",
    "trip.travelModeMotorcycle": "Motorsykkel",
    "trip.travelModeDesc": "AI tilpasser ruter og tips basert på kjøretøyet ditt",
    "trip.priceCurrency": "Prisvaluta",
    "trip.saveChanges": "Lagre endringer",
    "trip.calculatingRoute": "Beregner rute...",
    "trip.driveTime": "kjøretid",
    "trip.estimatedFuelCost": "Estimert drivstoffkostnad:",
    "trip.estimatedDiesel": "Estimert dieselkostnad:",
    "trip.estimatedChargeCost": "Estimert ladekostnad:",
    "trip.openGoogleMaps": "Åpne i Google Maps",
    "trip.tankSize": "Tankvolum (l)",
    "trip.batterySize": "Batteri (kWh)",
    "trip.rangeAnalysis": "Rekkeviddeanalyse",
    "trip.rangePerFill": "Rekkevidde per fylling",
    "trip.chargingStops": "Ladestopp",
    "trip.fuelStops": "Tankstopp",
    "trip.avgLeg": "Snitt per etappe",
    "trip.costPerCharge": "Kostnad per lading",
    "trip.costPerFill": "Kostnad per fylling",
    "trip.evStopNote": "Du må lade omtrent {stops} ganger, omtrent hver {km} km.",
    "trip.fuelStopNote": "Du må fylle omtrent {stops} ganger, omtrent hver {km} km.",
    "trip.noStopsNeeded": "Rekkevidden din dekker hele turen uten stopp!",
    "trip.routeAlternatives": "Rutealternativer",
    "trip.route": "Rute",
    "trip.addMin2Stops": "Legg til minst 2 stopp for drivstoffberegning",
    "trip.addStop": "Legg til stopp",
    "trip.newStop": "Nytt stopp",
    "trip.searchPlace": "Søk sted eller adresse...",
    "trip.notesOptional": "Notater (valgfritt)",
    "trip.saving": "Lagrer...",
    "trip.add": "Legg til",
    "trip.cancel": "Avbryt",
    "trip.dragToReorder": "Dra og slipp for å endre rekkefølge",
    "trip.navigate": "Naviger hit",
    "trip.startNavigation": "Start navigasjon",
    "trip.uploading": "Laster opp...",
    "trip.writeExperience": "Skriv om opplevelsen din her...",
    "trip.save": "Lagre",
    "trip.tripExperience": "Reiseopplevelse",
    "trip.edit": "Rediger",
    "trip.write": "Skriv",
    "trip.describeTripExperience": "Beskriv reisen din, hva du opplevde, tips til andre...",
    "trip.tripUpdated": "Reise oppdatert!",
    "trip.linkCopied": "Lenke kopiert!",
    "trip.shareLinkDesc": "Del lenken med vennene dine.",
    "trip.couldNotShare": "Kunne ikke dele",
    "trip.imageUploaded": "Bilde lastet opp!",
    "trip.couldNotUpload": "Kunne ikke laste opp bilde",
    "trip.experienceSaved": "Opplevelse lagret!",
    "trip.stopAdded": "Stopp lagt til!",
    "trip.couldNotAddStop": "Kunne ikke legge til stopp",
    "trip.orderUpdated": "Rekkefølge oppdatert!",
    "trip.fuelCalc": "Drivstoffberegning (diesel)",
    "trip.currency": "Valuta",
    "tip.notFound": "Reisen ble ikke funnet",
    "tip.notFoundDesc": "Den er kanskje ikke lenger tilgjengelig.",
    "tip.back": "Tilbake",
    "tip.traveler": "Reisende",
    "tip.sharedTrip": "Delt reise",
    "tip.planThisTrip": "Planlegg denne reisen",
    "tip.aboutTrip": "Om denne reisen",
    "tip.aiGenerated": "AI-generert reisepresentasjon",
    "tip.budget": "Budsjett",
    "tip.stops": "Stopp",
    "tip.places": "steder",
    "tip.duration": "Varighet",
    "tip.days": "dager",
    "tip.consumption": "Forbruk",
    "tip.goodToKnow": "Greit å vite før du drar",
    "tip.bestSeason": "Beste sesong",
    "tip.currency": "Valuta",
    "tip.speed": "Hastighet",
    "tip.emergency": "Nødnummer",
    "tip.campingRules": "Campingregler",
    "tip.route": "Ruten",
    "tip.endOfTrip": "Slutten av reisen",
    "tip.wantSameRoute": "Vil du kjøre samme rute?",
    "tip.cloneDesc": "Vi lager en helt ny reise for deg med de samme stoppene. Du kan tilpasse den etterpå — endre start, legge til stopp og planlegge budsjett.",
    "tip.needAccount": "Du trenger en konto for å lagre reisen",
    "tip.tripCreated": "Reise opprettet! 🚐 Nå kan du tilpasse den.",
    "tip.couldNotCreate": "Kunne ikke opprette reise",
    "tip.home": "Hjem",
    "tip.travelTips": "Reisetips",
    "tip.startPoint": "Startpunkt",
    "tip.endPoint": "Sluttpunkt",
    "tip.travelGuide": "Reiseguide",
    "tip.hide": "Skjul",
    "tip.sharedThisTrip": "delte denne reisen",
    "tip.explore": "Utforsk",
    "tip.inspirationTitle": "Inspirasjon fra reisende",
    "tip.inspirationDesc": "Oppdag ruter delt av andre reisende",
    "tip.tipHidden": "Reisetipset er skjult fra forsiden",
    "tip.couldNotHide": "Kunne ikke skjule reisetipset",
    "tip.publishAsTip": "Publiser som reisetips",
    "tip.publishDesc": "Vis reisen din på forsiden som inspirasjon for andre",
    "tip.creatingAi": "Lager AI-reisepresentasjon... ✨",
    "tip.creatingAiDesc": "Dette kan ta litt tid.",
    "tip.publishedWithAi": "Reisen er publisert med AI-guide! 🌍✨",
    "tip.published": "Reisen er nå synlig for alle! 🌍",
    "tip.unpublished": "Reisen er nå privat",
    "tip.publishedNoAi": "Reisen er publisert! (AI-guide kunne ikke opprettes)",
    "authGate.title": "Lås opp hele Vantastika",
    "authGate.description": "Opprett en konto for å planlegge reiser, lagre steder, bruke budsjett og sjekklister.",
    "authGate.signup": "Opprett konto",
    "authGate.login": "Logg inn",
    "authGate.or": "eller",
    "authGate.hasAccount": "Har allerede konto?",
    "authGate.ctaPlaces": "Verifiserte steder i hele Europa",
    "authGate.ctaRoutes": "Smarte ruter med drivstoffkostnader",
    "authGate.ctaBudget": "Full budsjettkontroll",
    "authGate.ctaChecklist": "Pakklister for alle reisetyper",
    "authGate.ctaAI": "AI-assistent som kjenner hver vei",
    "authGate.saveTripMsg": "Lagre reisen din – opprett konto",
    "authGate.planRouteMsg": "Planlegg ruten din – logg inn",
    "authGate.savePlaceMsg": "Lagre stedet – opprett konto",
    "gate.freeForever": "Gratis å komme i gang",
    "submit.loginDesc": "Du må være logget inn for å sende inn en campingplass.",
    "submit.title": "Send inn en Campingplass",
    "submit.subtitle": "Del favorittstedet ditt med fellesskapet",
    "submit.reviewNote": "Innsendte steder blir gjennomgått før publisering.",
    "submit.basicInfo": "Grunnleggende informasjon",
    "submit.name": "Navn",
    "submit.type": "Type",
    "submit.country": "Land",
    "submit.region": "Region",
    "submit.city": "By",
    "submit.description": "Beskrivelse",
    "submit.shortDescription": "Kort beskrivelse",
    "submit.fullDescription": "Full beskrivelse",
    "submit.priceSchedule": "Pris & Tidsplan",
    "submit.freeSpot": "Dette er et gratis sted",
    "submit.pricePerNight": "Pris per natt (€)",
    "submit.imageContact": "Bilde & Kontakt",
    "submit.imageUrl": "Bilde-URL",
    "submit.phone": "Telefon",
    "submit.email": "E-post",
    "submit.website": "Nettside",
    "submit.address": "Adresse",
    "submit.fullAddress": "Full adresse",
    "submit.searchAddress": "Søk adresse...",
    "submit.amenities": "Fasiliteter",
    "submit.submit": "Send inn campingplass",
    "submit.submitting": "Sender inn...",
    "submit.success": "Innsendt!",
    "submit.successDesc": "Campingplassen din vil bli gjennomgått av en admin.",
    "submit.missingFields": "Manglende felt",
    "submit.missingFieldsDesc": "Navn, land og region er påkrevd.",
    "submit.ctaTitle": "Kjenner du et flott sted?",
    "submit.ctaDesc": "Hjelp fellesskapet ved å legge til en campingplass",
    "home.featuresTrustTitle": "Steder du kan stole på",
    "home.featuresTrustDesc": "Ekte campingplasser med priser, anmeldelser og alt du trenger å vite.",
    "home.featuresRoutesTitle": "Ruter som fungerer",
    "home.featuresRoutesDesc": "Se hele turen i ett bilde. Stopp, kjøretider og drivstoffkostnader rett på kartet.",
    "home.featuresBudgetTitle": "Full kontroll på pengene",
    "home.featuresBudgetDesc": "Sett et budsjett, logg utgifter og se nøyaktig hvor pengene går.",
    "home.featuresChecklistTitle": "Glem aldri noe",
    "home.featuresChecklistDesc": "Sjekklister tilpasset din turtype. Gass, vann, pass, lader. Alt på ett sted.",
    "home.featuresAITitle": "AI-assistent",
    "home.featuresAIDesc": "Spør Vannie om hva som helst. Ruter, regler, tips og lokal kunnskap. Alltid tilgjengelig.",
    "home.sectionTitle": "Alt du trenger for hele turen",
    "home.sectionSubtitle": "Fra første idé til siste mil. Vantastika gir deg kontrollen.",
    "home.trustPlaces": "2 400+ steder",
    "home.trustEurope": "Hele Europa",
    "home.videoOverlay1": "Planlegg ruten din. Finn de beste stedene. Dra ut.",
    "home.videoOverlay2": "Alt du trenger for den perfekte roadtrippen.",
    "home.lifestyleTitle": "Din tur. Dine regler.",
    "home.lifestyleSubtitle": "Ruter, stopp, budsjett og pakkeliste. Klart på sekunder.",
    "home.lifestyleCta": "Begynn å planlegge",
    "home.vannieTitle": "Møt Vannie. Din personlige reiseguide.",
    "home.vannieDesc": "Still et spørsmål, få et rett svar. Beste stedene, lokale tips, ruteforslag og regler per land.",
    "home.vannieCta": "Chat med Vannie",
    "home.vannieCtaSignup": "Bli medlem og chat med Vannie",
    "home.vannieRoutes": "Ruteplanlegging",
    "home.vannieCamping": "Campingtips",
    "home.vannieRules": "Lokale regler",
    "home.vannieBudget": "Budsjetthjelp",
    "nav.guides": "Landguider",
    "nav.services": "Service & SOS",
    "nav.games": "Roadtrip-spill",
    "nav.vannie": "Vannie",
    "nav.community": "Fellesskap",
    "nav.profile": "Min profil",
    "card.free": "GRATIS",
    "card.night": "natt",
    "card.loginForPrice": "Logg inn for pris",
    "cat.campings": "Campinger",
    "cat.stellplatz": "Bobilplass",
    "cat.freeSpots": "Gratis steder",
    "map.noStopsToShow": "Ingen stopp med koordinater å vise",
    "wizard.whereToGo": "Hvor vil du reise?",
    "wizard.searchDestination": "Søk destinasjon (f.eks. Sanremo, Italia)...",
    "wizard.startPoint": "Hvor starter du fra?",
    "wizard.myLocation": "Min posisjon",
    "wizard.chooseManually": "Velg manuelt",
    "wizard.searchStartPoint": "Søk startpunkt...",
    "wizard.locationDetected": "Posisjon funnet",
    "wizard.start": "Start",
    "wizard.destination": "Destinasjon",
    "wizard.yourStops": "Dine stopp",
    "wizard.stop": "Stopp",
    "wizard.addedAsStop": "lagt til som stopp!",
    "campingDetector.title": "Dette ser ut som en camping!",
    "campingDetector.aiDetected": "AI oppdaget",
    "campingDetector.description": "Vil du legge stedet til i Vantastika så andre reisende kan finne det?",
    "campingDetector.pendingNote": "Stedet gjennomgås av en admin før det blir offentlig.",
    "campingDetector.confidence": "treff",
    "campingDetector.typeCamping": "Camping",
    "campingDetector.typeStellplatz": "Bobilplass",
    "campingDetector.typeFreeParking": "Gratis parkering",
    "campingDetector.typeLodging": "Overnatting",
    "campingDetector.notNow": "Ikke nå",
    "campingDetector.registerCta": "Legg til i Vantastika",
    "campingDetector.registering": "Legger til…",
    "campingDetector.registeredTitle": "Takk for bidraget!",
    "campingDetector.registeredDesc": "Stedet er sendt inn og publiseres etter gjennomgang.",
    "campingDetector.alreadyExists": "Finnes allerede i Vantastika",
    "campingDetector.alreadyExistsDesc": "Dette stedet er allerede registrert.",
    "campingDetector.couldNotRegister": "Kunne ikke registrere stedet",
    "campingDetector.loginRequired": "Innlogging kreves",
    "campingDetector.loginRequiredDesc": "Logg inn for å bidra med nye steder.",
    "campingDetector.cannotRegister": "Kan ikke registrere",
    "campingDetector.missingPlaceId": "Mangler sted-ID fra Google.",
    "stopType.visit": "Besøk",
    "stopType.overnight": "Overnatting",
    "stopType.destination": "Destinasjon",
    "stopType.nights": "Netter",
    "stopType.stayHours": "Opphold (t)",
    "wizard.suggestedStops": "Campinger langs ruten",
    "wizard.findingCampings": "Finner campinger langs ruten...",
    "wizard.noCampingsNearRoute": "Ingen campinger funnet nær denne ruten",
    "wizard.saveTrip": "Lagre reise",
    "wizard.stepPlanningMode": "Planlegging",
    "wizard.stepDrivingHours": "Kjøring",
    "wizard.stepTravelStyle": "Reisestil",
    "wizard.stepCountryInfo": "Land",
    "wizard.howToPlan": "Hvordan vil du planlegge reisen din?",
    "wizard.howToPlanDesc": "Velg det alternativet som passer deg best.",
    "wizard.planModeKnow": "Jeg vet hvor jeg skal",
    "wizard.planModeKnowDesc": "Jeg har et reisemål klart og vil planlegge ruten dit.",
    "wizard.planModeHelp": "Hjelp meg å finne en reise",
    "wizard.planModeHelpDesc": "Jeg vil ha inspirasjon og AI-forslag til reiser.",
    "wizard.planModeOptimize": "Optimaliser ruten min smart",
    "wizard.planModeOptimizeDesc": "Jeg vet omtrent hvor jeg skal, men vil ha de beste etappene og stoppene.",
    "wizard.stepDetails": "Detaljer",
    "wizard.stepTrip": "Reise",
    "wizard.stepPreferences": "Preferanser",
    "wizard.dailyDrivingTitle": "Hvor lenge vil du kjøre per dag?",
    "wizard.dailyDrivingDesc": "Vi tilpasser etappene etter ditt valg.",
    "wizard.recommended": "Anbefalt",
    "wizard.travelStyleTitle": "Hvordan vil du reise?",
    "wizard.travelStyleDesc": "Vi tilpasser ruten og forslagene etter din reisestil.",
    "wizard.styleFastest": "Raskeste vei",
    "wizard.styleFastestDesc": "Prioriter kortest kjøretid via motorvei.",
    "wizard.styleSafe": "Trygg og rolig reise",
    "wizard.styleSafeDesc": "Roligere veier med rimelig daglig kjøring.",
    "wizard.styleScenic": "Naturskjønn vei",
    "wizard.styleScenicDesc": "Prioriter vakre veier og utsikter.",
    "wizard.styleCamping": "Campingvennlig rute",
    "wizard.styleCampingDesc": "Rute med god tilgang til campingplasser.",
    "wizard.styleBudget": "Budsjettvennlig reise",
    "wizard.styleBudgetDesc": "Unngå bompenger og dyre stopp.",
    "wizard.styleFamily": "Barnevennlig reise",
    "wizard.styleFamilyDesc": "Kortere etapper med barnevennlige stopp.",
    "wizard.selectTravelStyle": "Velg en reisestil",
    "wizard.longDrivingWarning": "Lengre daglig kjøring kan påvirke trafikksikkerheten. Vi anbefaler maks 4–5 timer per dag.",
    "wizard.skipOvernightHere": "Hopp over overnatting – fortsett å kjøre",
    "wizard.resetStages": "Tilbakestill etapper",
    "wizard.passThrough": "Gjennomreise",
    "wizard.noOvernightShort": "Ingen overnatting",
    "wizard.mergedDayTitle": "Du kjører gjennom uten overnatting",
    "wizard.mergedDayDesc": "Denne dagen er slått sammen med neste. Korte stopp for pause og tanking anbefales.",
    "wizard.undoSkipOvernight": "Angre – overnatt her igjen",
    "wizard.selectPlanningMode": "Velg hvordan du vil planlegge",
    "wizard.accommodationTitle": "Overnattingsforslag",
    "wizard.accommodationDescCamping": "Viser bobilplasser og campingplasser langs ruten din.",
    "wizard.accommodationDescCar": "Viser hotell og overnatting nær destinasjonen din.",
    "wizard.campingRules": "Campingregler",
    "wizard.countriesInRouteOrder": "Land vises i ruteorden",
    "wizard.countryInfoDesc": "Praktisk informasjon for hvert land du passerer.",
    "wizard.countryInfoTitle": "Land langs ruten din",
    "wizard.startCountry": "Startland",
    "wizard.transitCountry": "Transittland",
    "wizard.destinationCountry": "Destinasjon",
    "wizard.ferryAlternatives": "Ferjealternativer",
    "wizard.ferryDuration": "Reisetid",
    "wizard.ferryRoute": "Strekning",
    "wizard.ferryRelevance": "Hvorfor relevant",
    "wizard.driving": "Kjøring",
    "wizard.drivingRules": "Kjøreregler",
    "wizard.fromRoute": "Langs ruten",
    "wizard.fuelInfo": "Drivstoff",
    "wizard.importantForMotorhome": "viktig for bobil",
    "wizard.infoItems": "infopunkter",
    "wizard.loadingCountryInfo": "Laster landinformasjon...",
    "wizard.motorhomeMultiCountryTip": "Med bobil kan regler variere kraftig mellom land. Sjekk miljøsoner, vignetter og tillatte mål før avreise.",
    "wizard.noCountryInfo": "Ingen landinformasjon tilgjengelig. Angi rute først.",
    "wizard.noResultsForStage": "Ingen resultater for denne etappen",
    "wizard.oneWay": "Enveis",
    "wizard.optimizeRoute": "Optimaliser stopprekkefølge",
    "wizard.searchingStage": "Søker for etappe",
    "wizard.selectDestForAccommodation": "Velg destinasjon først for overnattingsforslag.",
    "wizard.shortTripNoOvernight": "Kort tur – ingen overnatting nødvendig.",
    "wizard.skipStep": "Hopp over dette steget",
    "wizard.from": "Fra",
    "wizard.to": "Til",
    "wizard.selectedAccommodation": "Valgt overnatting",
    "wizard.changeAccommodation": "Bytt",
    "wizard.selected": "Valgt",
    "wizard.selectAccommodation": "Velg",
    "wizard.noAccommodationYet": "Ingen overnatting valgt ennå. Du kan velge i neste steg.",
    "wizard.viewOnMap": "Vis på kart",
    "common.close": "Lukk",
    "wizard.speedLimits": "Fartsgrenser",
    "wizard.stages": "etapper",
    "wizard.tolls": "Bompenger & vignetter",
    "wizard.wildCamping": "Villcamping",
    "wizard.stepRoute": "Utreise",
    "wizard.stepReturn": "Hjemreise",
    "wizard.stepAccommodation": "Overnatting",
    "wizard.stepVehicle": "Kjøretøy",
    "wizard.stepOverview": "Oversikt",
    "wizard.next": "Neste",
    "wizard.back": "Tilbake",
    "wizard.tripTypeLabel": "Turtype",
    "wizard.accommodationDesc": "Her er overnattingsforslag basert på ruten din og reisemåte. Legg til som stopp eller hopp over.",
    "wizard.showingCampingsAlongRoute": "Viser campingplasser langs ruten din",
    "wizard.showingHotelsNearDest": "Viser hoteller nær destinasjonen din",
    "wizard.showingAccommodationNearDest": "Viser overnatting langs ruten din",
    "wizard.searchingAlongRoute": "Søker langs ruten din...",
    "wizard.expandingSearch": "Utvider søkeområdet...",
    "wizard.tryingAlternatives": "Prøver alternative overnattinger...",
    "wizard.noAccommodationFound": "Ingen overnatting funnet nær ruten din. Du kan hoppe over dette trinnet og legge til stopp manuelt senere.",
    "wizard.returnTripDesc": "Velg hvordan du vil reise tilbake.",
    "wizard.sameRouteDesc": "Kjør tilbake samme vei",
    "wizard.scenicDesc": "Alternativ naturskjønn rute",
    "wizard.fastestDesc": "Raskeste veien hjem",
    "wizard.routeSummary": "Rute",
    "wizard.selectDestFirst": "Velg destinasjon først",
    "wizard.selectStartFirst": "Velg startpunkt først",
    "wizard.fuelType": "Drivstofftype",
    "wizard.estimatedFuelCostLabel": "Estimert drivstoffkostnad",
    "wizard.showPricesIn": "Vis priser i",
    "wizard.perNight": "natt",
    "wizard.finalDay": "Sluttmål",
    "wizard.overnight": "Overnatting",
    "wizard.viewAllAccommodations": "Vis alle",
    "wizard.accommodationNear": "Overnatting nær",
    "wizard.tapToChooseAccommodation": "Trykk for å velge overnatting.",
    "wizard.accommodationMissingHint": "Du har planlagt en overnatting her, men ingen overnatting er valgt.",
    "wizard.accommodationMissingTitle": "Overnatting mangler",
    "wizard.accommodationMissingSubtitle": "Trykk for å velge overnatting for hver dag.",
    "wizard.detectedAccommodationTitle": "Ser ut som overnatting",
    "wizard.detectedAccommodationDesc": "ser ut som en overnatting. Hvordan vil du markere den?",
    "wizard.overnightHint": "Du sover her",
    "wizard.visitHint": "Bare gjennomkjøring eller besøk",
    "wizard.destinationHint": "Reisens sluttmål",
    "wizard.firstTimeHint": "Start med å søke etter reisemålet ditt. Velg deretter kjøretøy, datoer og stopp — ruten beregnes automatisk!",
    "wizard.budgetOptional": "Budsjett (valgfritt)",
    "wizard.budgetPlaceholder": "f.eks. 15000",
    "wizard.fuelAutoAdded": "Estimert drivstoffkostnad lagt til budsjett",
    "wizard.planNewTrip": "Planlegg ny reise",
    "wizard.myTrips": "Mine lagrede reiser",
    "wizard.planTrip": "Planlegg reise",
    "wizard.or": "eller",
    "wizard.popularDestinations": "Populært",
    "wizard.describeTrip": "Beskriv reisen din – vi planlegger alt for deg",
    "wizard.describeSubtext": "Skriv fritt hva du vil gjøre, så bygger vi forslaget",
    "wizard.backToManual": "Tilbake til manuelt søk",
    "wizard.selectSuggestion": "Velg et forslag fra listen for å gå videre.",
    "wizard.analyzingTrip": "Analyserer reisen din…",
    "wizard.createPlan": "Lag reiseplan",
    "wizard.suggestedTrip": "Foreslått reise",
    "wizard.places": "steder",
    "wizard.startOver": "Start på nytt",
    "wizard.noDestFound": "Ingen destinasjoner funnet",
    "wizard.noDestFoundDesc": "Prøv å beskrive reisen mer detaljert.",
    "wizard.couldNotParse": "Kunne ikke tolke reisen",
    "wizard.couldNotParseDesc": "Prøv igjen med en tydeligere beskrivelse.",
    "wizard.tooManyRequests": "For mange forespørsler",
    "wizard.tooManyRequestsDesc": "Vent litt og prøv igjen.",
    "wizard.aiCreditOut": "AI-kreditt brukt opp",
    "wizard.aiCreditOutDesc": "Kontakt support for mer informasjon.",
    "wizard.tripSaved": "Reisen lagret!",
    "wizard.tripSavedDesc": "Gå til Mine reiser for å se den.",
    "wizard.couldNotReachVannie": "Kunne ikke nå Vannie akkurat nå.",
    "wizard.describePlaceholder": "Beskriv reisen din med egne ord — vi lager en komplett reiseplan for deg.",
    "wizard.chars": "tegn",
    "wizard.destinationConfirmed": "Destinasjon valgt",
    "wizard.locating": "Finner koordinater...",
    "wizard.destType": "Mål",
    "wizard.visitType": "Besøk",
    "wizard.aiSuggestTitle": "AI Reiseforslag",
    "wizard.aiSuggestOpen": "Beskriv reisen din – vi planlegger alt for deg",
    "wizard.aiSuggestPlaceholder": "F.eks. 'Jeg vil til stranden i 5 dager' eller 'Koselig fjelltur med fotturer i alpene'",
    "wizard.vannieThinking": "Vannie tenker...",
    "wizard.suggestTrip": "Foreslå reise",
    "wizard.suggestedStopsLabel": "Foreslåtte stopp",
    "wizard.useThisSuggestion": "Bruk dette forslaget",
    "wizard.couldNotGenerate": "Kunne ikke generere forslag",
    "wizard.days": "dager",
    "wizard.perDay": "/dag",
    "wizard.roundtrip": "Tur-retur",
    "wizard.oneway": "Enveis",
    "wizard.departure": "Avreise",
    "wizard.return": "Retur",
    "wizard.selectDate": "Velg dato",
    "wizard.departureDateOptional": "Avreisedato (valgfritt)",
    "wizard.day": "dag",
    "wizard.dayLabel": "Dag {n}",
    "wizard.roundtripLabel": "Tur-retur",
    "wizard.estimatedTravelTime": "Estimert reisetid",
    "wizard.total": "totalt",
    "wizard.stopsLabel": "stopp",
    "wizard.fetchingReturnTips": "Henter tips for hjemreisen…",
    "wizard.returnTips": "Tips for hjemreisen",
    "wizard.roundtripShort": "t/r",
    "wizard.driveTime": "Kjøretid",
    "wizard.chargeCost": "Ladekostnad",
    "wizard.fuelCost": "Drivstoffkostnad",
    "wizard.distance": "Distanse",
    "wizard.chooseRoute": "Velg denne ruten",
    "wizard.ferry": "Ferge",
    "wizard.maxDrivingHours": "Maks kjøretid per dag",
    "wizard.longDriveWarning": "Lang kjøretid",
    "wizard.longDriveDesc": "Vi anbefaler maks 4 timer for trygg og behagelig kjøring.",
    "wizard.drivingDaysCalc": "{hours}t/dag = ~{days} kjøredager",
    "wizard.drivingDaysRoundtrip": "dager tur-retur",
    "wizard.vehicleSettings": "Kjøretøy og drivstoff",
    "wizard.scenic": "Naturskjønn",
    "wizard.fastest": "Raskest",
    "wizard.balanced": "Balansert",
    "wizard.sameRoute": "Samme rute",
    "wizard.returnTrip": "Hjemreise",
    "wizard.homecoming": "Hjemkomst",
    "wizard.optimizationSummary": "Rute optimalisert",
    "wizard.stageOverview": "Dagsetapper",
    "wizard.overnightIn": "Overnatting nær",
    "wizard.arrivalAt": "Ankomst ved",
    "wizard.returnRouteOption": "Hjemreise",
    "wizard.hours": "t",
    "wizard.addedAsOvernight": "lagt til som overnatting!",
    "wizard.addedAsRestStop": "lagt til som rasteplass!",
    "wizard.stopAddedFromMap": "Stopp lagt til fra kartet!",
    "wizard.updatedStop": "Oppdatert stopp",
    "wizard.stopPositionUpdated": "Stoppets posisjon oppdatert",
    "wizard.newStop": "Nytt stopp",
    "wizard.optimizedDesc": "Stoppene omsortert for korteste totalavstand",
    "wizard.newOrder": "Ny rekkefølge",
    "wizard.orderChanged": "Rekkefølgen endret fra original",
    "wizard.routeOptimized": "Ruten optimalisert!",
    "wizard.nameError": "Kunne ikke generere navneforslag",
    "wizard.mapInstructions": "Klikk på kartet for å legge til stopp, dra for å flytte",
    "wizard.nameTrip": "Gi reisen din et navn",
    "wizard.optional": "valgfritt",
    "wizard.namePlaceholder": "f.eks. Sommer i Alpene",
    "wizard.aiSuggest": "AI-forslag",
    "wizard.signupAndSave": "Opprett konto og lagre",
    "wizard.useTrip": "Bruk denne reisen",
    "wizard.dragToReorder": "Dra for å endre rekkefølge",
    "wizard.prefillTip": "Forhåndsutfylt fra et Vantastika-tips",
    "wizard.prefillGuide": "Forhåndsutfylt fra en reiseguide",
    "demo.phase1Eyebrow": "For mange valg",
    "demo.phase1Title": "Se problemet",
    "demo.phase1Text": "Kartet er fullt av steder og det er vanskelig å vite hva som er verdt stoppet.",
    "demo.phase2Eyebrow": "Ren planlegging",
    "demo.phase2Title": "Kun verifiserte steder",
    "demo.phase2Text": "Vantastika filtrerer bort støyen og gjør planleggingen tydelig, rask og trygg.",
    "demo.phase3Eyebrow": "Ferdig roadtrip",
    "demo.phase3Title": "Ruten din, klar til avreise",
    "demo.phase3Text": "Se hele ruten, viktige stopp og en reise som føles ferdig før du starter motoren.",
    "demo.stepStart": "Start reisen",
    "demo.stepVerified": "Verifiserte stopp",
    "demo.stepArrive": "Frem til målet",
    "demo.planSmart": "Planlegg smart",
    "demo.budget": "Budsjett",
    "demo.budgetUsed": "brukt",
    "demo.roadtripPreview": "Forhåndsvisning",
    "service.title": "Service & Nødhjelp",
    "service.workshop": "Verksted",
    "service.gasStation": "Bensinstasjon",
    "service.water": "Vann",
    "service.charging": "Lading",
    "service.waste": "Tømming",
    "service.hospital": "Sykehus",
    "service.police": "Politi",
    "service.grocery": "Matbutikk",
    "service.emergency": "Nødsituasjon",
    "service.exitEmergency": "Lukk SOS",
    "service.emergencyMode": "Nødmodus",
    "service.callEmergency": "Ring",
    "service.emergencyNote": "112 fungerer i alle EU-land",
    "service.locating": "Finner posisjonen din...",
    "service.locationNeeded": "Posisjon trengs",
    "service.enableLocation": "Tillat posisjonstilgang for å finne tjenester i nærheten",
    "service.retry": "Prøv igjen",
    "service.searching": "Søker i nærheten...",
    "service.noResults": "Ingen tjenester funnet i nærheten",
    "service.navigate": "Naviger",
    "service.open": "Åpent",
    "service.closed": "Stengt",
    "service.fetchError": "Kunne ikke laste tjenester",
    "service.useGPS": "Bruk GPS",
    "service.searchPlaceholder": "Søk hva som helst... Tesla, Hymer, campingutstyr, apotek",
    "service.manual": "Manuell",
    "service.searchResultsFor": "Resultater for",
    "why.title": "Hvorfor velge Vantastika?",
    "why.subtitle": "Alt du trenger for roadtripen — helt gratis, uten kort.",
    "why.smartPlanning": "Smart reiseplanlegging",
    "why.smartPlanningDesc": "Lag ruter med stopp, drivstoffberegning og tidsplanlegging i én flyt.",
    "why.aiAssistant": "AI-reiseassistent",
    "why.aiAssistantDesc": "Beskriv drømmereisen din med egne ord — vår AI bygger hele planen for deg.",
    "why.campingsEurope": "Campingplasser i hele Europa",
    "why.campingsEuropeDesc": "Finn, sammenlign og filtrer campingplasser i 21 land.",
    "why.travelGuides": "Inspirerende reiseguider",
    "why.travelGuidesDesc": "Ferdige temaruter med historie, stopp og tips — klare til å kjøre.",
    "why.budget": "Budsjett & utgifter",
    "why.budgetDesc": "Hold oversikt over kostnader, drivstoff og daglig budsjett under reisen.",
    "why.shareFriends": "Del med venner",
    "why.shareFriendsDesc": "Inviter reisekamerater og planlegg sammen i sanntid.",
    "why.checklists": "Smarte sjekklister",
    "why.checklistsDesc": "Tilpassede pakkelister basert på kjøretøytype og reise.",
    "why.sos": "SOS & trygghetspakke",
    "why.sosDesc": "Nøddeling, hendelsesrapport og rask tilgang til nødnumre.",
    "why.badges": "Nivåer & badges",
    "why.badgesDesc": "Samle utmerkelser, klatre i nivåer og utfordre deg selv som reisende.",
    "why.cta": "Opprett gratis konto",
    "why.ctaSub": "Ingen kort nødvendig · Klart på 30 sekunder",
    "games.title": "Roadtrip Games",
    "games.subtitle": "Gjør turen morsommere 🎮",
    "games.played": "Spilt",
    "games.totalPoints": "Totale poeng",
    "games.challengeFriend": "Utfordre en venn",
    "games.loginPrompt": "Logg inn for å lagre spillhistorikk",
    "games.loginPromptSub": "Dine poeng, favoritter og plassering lagres",
    "games.onTrip": "Reise pågår – spill tilpasset for {country}",
    "games.duringTrip": "Under reisen",
    "games.duringTripSub": "Perfekt for passasjerer – {n} spilltyper",
    "games.atStop": "Ved stopp & rast",
    "games.atStopSub": "Når dere tar pause eller utforsker – {n} spilltyper",
    "games.camping": "Camping & kveldsspill",
    "games.campingSub": "Lengre spill for kvelder og campingliv – {n} spilltyper",
    "games.tabs.games": "Spill",
    "games.tabs.community": "Fellesskap",
    "games.tabs.challenges": "Utfordring",
    "games.tabs.badges": "Merker",
    "games.tabs.history": "Historikk",
    "games.favorites": "Favorittspill",
    "games.favoritesEmpty": "Hjertemerk et spill for å lagre det her",
    "games.recommended": "Anbefalt",
    "game.quiz.name": "Roadtrip Quiz",
    "game.quiz.desc": "Test kunnskapen din om land, veier og camping",
    "game.bingo.name": "Roadtrip Bingo",
    "game.bingo.desc": "Se etter ting langs veien og marker bingobrettet ditt",
    "game.guess_place.name": "Gjett Stedet",
    "game.guess_place.desc": "Kan du gjette hvilket sted vi beskriver?",
    "game.would_you_rather.name": "Heller Da",
    "game.would_you_rather.desc": "Velg mellom vanskelige reisedilemmaer",
    "game.trivia_battle.name": "Trivia Battle",
    "game.trivia_battle.desc": "Konkurrer mot andre reisende i sanntid",
    "game.photo_challenge.name": "Fotoutfordring",
    "game.photo_challenge.desc": "Kreative fotooppgaver ved stoppet ditt",
    "game.story.name": "Fortellerspill",
    "game.story.desc": "Lag en reisefortelling sammen",
    "game.checkpoint.name": "Checkpoint",
    "game.checkpoint.desc": "Utforsk området med morsomme oppdrag",
    "game.evening_game.name": "Kveldsspill",
    "game.evening_game.desc": "Koselige gruppespill for kvelden",
    "game.scenario.name": "Scenariospill",
    "game.scenario.desc": "Hva ville du gjort? Reisedilemmaer",
    "game.intro.tagline.quiz": "Test kunnskapen din!",
    "game.intro.tagline.bingo": "Hold utkikk!",
    "game.intro.tagline.guess_place": "Kan du gjette?",
    "game.intro.tagline.would_you_rather": "Ta ditt valg!",
    "game.intro.tagline.trivia_battle": "Utfordringen starter!",
    "game.intro.tagline.photo_challenge": "Fang øyeblikket!",
    "game.intro.tagline.story": "Det var en gang en reise...",
    "game.intro.tagline.checkpoint": "Utforsk & oppdag!",
    "game.intro.tagline.evening_game": "På tide å slappe av!",
    "game.intro.tagline.scenario": "Hva ville du gjort?",
    "game.difficulty": "Velg vanskelighetsgrad",
    "game.difficulty.easy": "Lett",
    "game.difficulty.medium": "Middels",
    "game.difficulty.hard": "Vanskelig",
    "game.difficulty.easyDesc": "Perfekt for nybegynnere",
    "game.difficulty.mediumDesc": "Passe utfordrende",
    "game.difficulty.hardDesc": "For ekte eksperter",
    "game.question": "Spørsmål",
    "game.points": "poeng",
    "game.finished": "Quiz ferdig!",
    "game.score.amazing": "Utrolig! Du er en mester! 🏆",
    "game.score.great": "Fantastisk! 🎉",
    "game.score.good": "Bra jobbet! 👍",
    "game.score.ok": "Ikke verst! Prøv igjen! 💪",
    "game.score.low": "Øvelse gjør mester! 💪",
    "game.bestStreak": "Beste streak: {n} riktige på rad!",
    "game.back": "Tilbake",
    "game.paused": "Spillet er pauset",
    "game.exit.title": "Forlate spillet?",
    "game.exit.description": "Fremgangen din går tapt hvis du avslutter.",
    "game.exit.continue": "Fortsett å spille",
    "game.exit.pause": "Pause spillet",
    "game.exit.quit": "Avslutt spillet",
    "game.story.finished": "Historien ferdig!",
    "game.story.chapters": "kapitler fullført",
    "game.story.chapter": "Kapittel",
    "game.story.new": "Ny historie",
    "game.story.whatDo": "Hva gjør dere?",
    "game.scenario.finished": "Scenariet ferdig!",
    "game.scenario.steps": "steg gjennomført",
    "game.scenario.step": "Steg",
    "game.scenario.new": "Nytt scenario",
    "game.scenario.whatDo": "Hva gjør dere?",
    "game.scenario.intense": "Intenst!",
    "game.newRound": "Ny runde",
    "game.seeResults": "Se resultat",
    "game.nextQuestion": "Neste spørsmål",
    "game.playNow": "Spill nå",
    "game.players": "spillere",
    "game.minutes": "min",
    "game.safety.title": "Sikkerhet først!",
    "game.safety.desc": "Dette spillet er ment for passasjerer, ikke sjåføren. Bekreft at du er passasjer.",
    "game.safety.confirm": "Jeg er passasjer",
    "game.safety.later": "Spill senere",
    "game.version": "{country}-versjon",
    "game.hero.title": "Gjør reisen morsommere",
    "game.hero.game_types": "spilltyper",
    "game.hero.questions": "spørsmål",
    "game.hero.countries": "land",
    "game.challenge_friend": "Utfordre en venn",
    "game.trip_active": "Reise pågår",
    "game.login_prompt": "Logg inn for å lagre spillhistorikk",
    "game.login_prompt_desc": "Poeng, favoritter og plassering lagres",
    "game.tab.games": "Spill",
    "game.tab.challenges": "Utfordringer",
    "game.tab.community": "Community",
    "game.tab.collect": "Samle",
    "game.tab.history": "Historikk",
    "game.section.on_the_road": "Under reisen",
    "game.section.at_stop": "Ved stopp & rast",
    "game.section.camping": "Camping & kveldsspill",
    "game.favorites": "Favorittspill",
    "game.no_favorites": "Hjertemerk et spill for å lagre det her",
    "game.wyr.allDone": "Alle spørsmål ferdige!",
    "game.wyr.questionsAnswered": "spørsmål besvart",
    "game.wyr.title": "Heller Da",
    "game.wyr.wouldYouRather": "Ville du heller...",
    "game.wyr.or": "eller",
    "game.wyr.done": "Ferdig!",
    "game.recommended.title": "Anbefalt for deg",
    "game.weekly.title": "Ukens utfordringer",
    "game.weekly.done": "Ferdig",
    "game.bingo.found": "funnet",
    "game.bingo.subtitle": "Finn ting langs veien",
    "game.bingo.spotted": "Funnet!",
    "game.bingo.instructions": "Trykk på ting du ser langs veien. Bingo = rad, kolonne eller diagonal!",
    "game.guess.finished": "Gjett Stedet ferdig!",
    "game.guess.fewerClues": "Færre ledetråder = flere poeng!",
    "game.guess.title": "Gjett Stedet",
    "game.guess.place": "Sted",
    "game.guess.clues": "Ledetråder",
    "game.guess.showNext": "Vis neste ledetråd",
    "game.guess.placeholder": "Skriv svaret ditt...",
    "game.guess.guess": "Gjett",
    "game.guess.correct": "Riktig!",
    "game.guess.wrong": "Feil!",
    "game.guess.correctAnswer": "Riktig svar",
    "game.guess.nextPlace": "Neste sted",
    "game.playAgain": "Spill igjen",
    "game.evening.finished": "Kveldsspill ferdig!",
    "game.evening.wellDone": "Bra jobbet med hele gruppen!",
    "game.evening.round": "Runde",
    "game.evening.done": "Ferdig!",
    "game.evening.nextRound": "Neste runde",
    "game.evening.trivia": "Trivia",
    "game.evening.creative": "Kreativ",
    "game.evening.debate": "Debatt",
    "game.evening.memory": "Hukommelse",
    "game.evening.drawing": "Tegne",
    "game.checkpoint.finished": "Checkpoint ferdig!",
    "game.checkpoint.challengesDone": "utfordringer fullført",
    "game.checkpoint.newChallenges": "Nye utfordringer",
    "game.checkpoint.subtitle": "Utforsk stedet med utfordringer",
    "game.checkpoint.finish": "Fullfør checkpoint",
    "game.safety.warning": "Trafikksikkerhet kommer alltid først. Kjør aldri og spill samtidig.",
    "game.safety.cancel": "Avbryt",
    "game.leaderboard.title": "Toppliste – denne uken",
    "game.leaderboard.games": "spill",
    "game.leaderboard.you": "Du",
    "game.leaderboard.yourPosition": "Din posisjon",
    "game.history.empty": "Ingen spilte spill ennå",
    "game.history.emptyDesc": "Start ditt første spill og se historikken din her!",
    "game.history.played": "Spilt",
    "game.history.gameTypes": "Spilltyper",
    "game.history.countries": "Land",
    "game.history.avgScore": "Gjennomsnittlig poengsum",
    "game.history.inProgress": "Pågående",
    "game.history.continue": "Fortsett å spille",
    "game.badges.title": "Badges",
    "game.quiz.fast": "Raske",
    "game.quiz.perfect": "Perfekt!",
    "game.audience.label": "Hvem spiller?",
    "game.audience.kids": "Barn",
    "game.audience.kids_desc": "Morsomme og enkle spill for barn",
    "game.audience.family": "Familie",
    "game.audience.family_desc": "Spill for hele familien",
    "game.audience.adult": "Voksne",
    "game.audience.adult_desc": "Spill for voksne",
    "game.audience.couple": "Par",
    "game.audience.couple_desc": "Romantiske spill for to",
    "game.audience.friends": "Venner",
    "game.audience.friends_desc": "Morsomt og konkurransepreget med venner",
    "game.daily.title": "Daglige & ukentlige utfordringer",
    "game.daily.today": "I dag",
    "game.daily.this_week": "Denne uken",
    "game.daily.this_month": "Denne måneden",
    "game.daily.quiz_challenge": "Dagens quiz-utfordring",
    "game.daily.quiz_challenge_desc": "Få 7+ på dagens quiz",
    "game.daily.bingo_quick": "Rask bingo",
    "game.daily.bingo_quick_desc": "Finn 5 ting på bingobrettet",
    "game.daily.guess_master": "Gjettemester",
    "game.daily.guess_master_desc": "Gjett riktig på 3 steder",
    "game.daily.scenario_star": "Scenariostjerne",
    "game.daily.scenario_star_desc": "Fullfør en scenariorunde",
    "game.daily.photo_snap": "Fotoknips",
    "game.daily.photo_snap_desc": "Fullfør 3 fotoutfordringer",
    "game.daily.story_teller": "Fortelleren",
    "game.daily.story_teller_desc": "Fullfør en historie",
    "game.daily.trivia_duel": "Triviaduell",
    "game.daily.trivia_duel_desc": "Få 5+ i trivia battle",
    "game.weekly.quiz_master": "Ukens quizmester",
    "game.weekly.quiz_master_desc": "Få 9+ på en quiz denne uken",
    "game.weekly.bingo_champion": "Bingomester",
    "game.weekly.bingo_champion_desc": "Fyll et helt bingobrett",
    "game.weekly.explorer": "Ukens utforsker",
    "game.weekly.explorer_desc": "Fullfør 4 checkpoint-utfordringer",
    "game.weekly.photo_pro": "Fotoproff",
    "game.weekly.photo_pro_desc": "Fullfør 6 fotoutfordringer",
    "game.weekly.evening_host": "Kveldsvert",
    "game.weekly.evening_host_desc": "Arranger et kveldsspill",
    "game.monthly.country_champion": "Landsmester",
    "game.monthly.country_champion_desc": "Perfekt poengsum på en landsquiz",
    "game.monthly.all_rounder": "Allrounderen",
    "game.monthly.all_rounder_desc": "Spill 5 forskjellige spilltyper",
    "game.monthly.road_legend": "Veilegenden",
    "game.monthly.road_legend_desc": "Fullfør 3 historier",
    "game.season.spring": "Vårens spill",
    "game.season.summer": "Sommerens spill",
    "game.season.autumn": "Høstens spill",
    "game.season.winter": "Vinterens spill",
    "game.seasonal.limited": "Sesong",
    "game.seasonal.spring_awakening": "Vår-quiz",
    "game.seasonal.spring_awakening_desc": "Test vårkunnskap",
    "game.seasonal.easter_trip": "Påske-roadtrip bingo",
    "game.seasonal.easter_trip_desc": "Finn påsken langs veien",
    "game.seasonal.spring_photo": "Vår-fotoutfordring",
    "game.seasonal.spring_photo_desc": "Fang vårens skjønnhet",
    "game.seasonal.summer_roadtrip": "Sommer-roadtrip quiz",
    "game.seasonal.summer_roadtrip_desc": "Sol, strender og sommerveier",
    "game.seasonal.beach_bingo": "Strand & kyst-bingo",
    "game.seasonal.beach_bingo_desc": "Finn sommersaker ved kysten",
    "game.seasonal.midsummer": "Sankthans-utfordring",
    "game.seasonal.midsummer_desc": "Feir sankthans med utfordringer",
    "game.seasonal.camping_nights": "Sommercampingkvelder",
    "game.seasonal.camping_nights_desc": "Perfekt for varme campingkvelder",
    "game.seasonal.autumn_colors": "Høstfarger-foto",
    "game.seasonal.autumn_colors_desc": "Fang høstens gylne farger",
    "game.seasonal.harvest_quiz": "Høsttidsquiz",
    "game.seasonal.harvest_quiz_desc": "Quiz om høst og innhøsting",
    "game.seasonal.fog_mystery": "Tåkelagt mysterium",
    "game.seasonal.fog_mystery_desc": "Et mystisk høsteventyr",
    "game.seasonal.winter_roads": "Vinterveier-quiz",
    "game.seasonal.winter_roads_desc": "Test vinterkjøringskunnskap",
    "game.seasonal.christmas_markets": "Julemarkedsutforskeren",
    "game.seasonal.christmas_markets_desc": "Gjett berømte julemarkeder",
    "game.seasonal.cozy_evening": "Koselig vinterkveld",
    "game.seasonal.cozy_evening_desc": "Perfekt spill for kalde kvelder",
    "game.seasonal.northern_lights": "Nordlys-historie",
    "game.seasonal.northern_lights_desc": "Et eventyr under nordlyset",
    "game.level.label": "Nivå",
    "game.level.beginner": "Nybegynner",
    "game.level.explorer": "Utforsker",
    "game.level.adventurer": "Eventyrer",
    "game.level.traveler": "Reisende",
    "game.level.road_master": "Veimester",
    "game.level.pro": "Roadtrip Pro",
    "game.level.navigator": "Navigator",
    "game.level.euro_champ": "Europamester",
    "game.level.legendary": "Legendarisk",
    "game.level.legend": "Roadtrip Legende",
    "game.level.next": "Neste",
    "game.level.games_played": "spilt",
    "game.collectibles.title": "Landssamling",
    "game.collectibles.countries": "land",
    "country.sweden": "Sverige",
    "country.norway": "Norge",
    "country.germany": "Tyskland",
    "country.italy": "Italia",
    "country.france": "Frankrike",
    "country.spain": "Spania",
    "game.region.norrland": "Norrland",
    "game.region.svealand": "Svealand",
    "game.region.gotaland": "Götaland",
    "game.region.fjords": "Fjordene",
    "game.region.nordnorge": "Nord-Norge",
    "game.region.sorlandet": "Sørlandet",
    "game.region.bayern": "Bayern",
    "game.region.norddeutschland": "Nord-Tyskland",
    "game.region.rheinland": "Rhinlandet",
    "game.region.norditalien": "Nord-Italia",
    "game.region.toscana": "Toscana",
    "game.region.syditalien": "Sør-Italia",
    "game.region.ildefrance": "Île-de-France",
    "game.region.provence": "Provence",
    "game.region.bretagne": "Bretagne",
    "game.region.andalucia": "Andalucía",
    "game.region.catalonia": "Catalonia",
    "game.region.nordspanien": "Nord-Spania",
    "nav.compare": "Sammenlign campingplasser",
    "nav.contact": "Kontakt oss",
    "search.startTyping": "Begynn å skrive for å søke",
    "search.startTypingDesc": "Søk etter stedsnavn, by, kategori eller reisemålstype",
    "search.clickForDetails": "Klikk på et kort for detaljer",
    "search.searchHint": "Søk etter navn, kategori, by eller region. Smartsøk finner rett sted for deg.",
    "search.foundOnGoogle": "ekstra forslag fra Google",
    "search.addHint": "Trykk på et kort for å legge til i Vantastika",
    "search.fromGoogle": "Fra Google",
    "search.addToVantastika": "Legg til i Vantastika",
    "search.googleAdding": "Legger til...",
    "search.googleAddedShort": "Sendt til gjennomgang",
    "search.googleAdded": "Sted sendt inn",
    "search.googleAddedDesc": "Takk! Det blir synlig etter admin-gjennomgang.",
    "search.googleAlreadyExists": "Finnes allerede i Vantastika",
    "search.googleAlreadyExistsDesc": "Vi tar deg til den eksisterende siden.",
    "search.googleAddError": "Kunne ikke legge til stedet. Prøv igjen.",
    "search.nearbyFound": "campinger nær deg",
    "search.within25km": "innen 25 km",
    "search.noNearby": "Ingen campinger i nærheten",
    "search.noNearbyDesc": "Prøv kartet for å utforske lenger unna.",
    "search.openMap": "Åpne kart",
    "search.findingLocation": "Finner posisjonen din...",
    "search.findingLocationDesc": "Vi bruker den for å vise campinger nær deg.",
    "search.locationDeniedHint": "Posisjonstilgang er avslått. Tillat for å se campinger nær deg.",
    "search.enableLocationHint": "Tillat posisjonstilgang for å se nærmeste campinger.",
    "search.allowLocation": "Tillat posisjon",
    "search.topRated": "Topprangerte campinger",
    "hero.valueProps": "Alt på ett sted — ikke mer bytting mellom apper",
    "hero.diffTitle": "Hvorfor Vantastika?",
    "hero.diff1": "Reiseplanlegging, stopp og budsjett i én flyt",
    "hero.diff2": "Skann kvitteringer og spor utgifter automatisk",
    "hero.diff3": "Legg til stopp direkte fra kartet",
    "hero.diff4": "For bobiler, biler og bilturer",
    "features.budget": "Spor budsjettet ditt",
    "features.budgetDesc": "Registrer utgifter, skann kvitteringer og se hvor pengene går",
    "trip.stopType": "Stopptype",
    "trip.stopTypeVisit": "Kort besøk",
    "trip.stopTypeOvernight": "Overnatting",
    "trip.stopTypeDestination": "Endelig reisemål",
    "trip.nightsCount": "Netter",
    "trip.editStop": "Rediger stopp",
    "trip.optimizeRoute": "Optimaliser rute",
    "trip.outboundLeg": "Utgående",
    "trip.returnLeg": "Retur",
    "trip.returnLegDesc": "Returreise hjem",
    "trip.addReturnStop": "Legg til stopp på returen",
    "trip.optimizeReturn": "Optimaliser retur",
    "trip.returnOptimized": "Returruten er optimalisert!",
    "trip.noReturnStops": "Ingen returstopp ennå. Legg til stopp for reisen hjem.",
    "trip.markAsReturn": "Merk som retur",
    "trip.markAsOutbound": "Merk som utgående",
    "trip.routeOptimized": "Ruten er optimalisert!",
    "optimize.title": "Optimaliser rekkefølgen på stoppene",
    "optimize.description": "Sammenlign ulike rutealternativer og velg det som passer best for turen din.",
    "optimize.analyzing": "Analyserer rutealternativer…",
    "optimize.currentOrder": "Nåværende rekkefølge",
    "optimize.currentDesc": "Din nåværende rekkefølge på stoppene som planlagt.",
    "optimize.geographic": "Mest logiske rute",
    "optimize.geographicDesc": "Stopp sortert for naturlig geografisk flyt mot reisemålet.",
    "optimize.shortest": "Korteste rute",
    "optimize.shortestDesc": "Nærmeste-nabo-sortering for å minimere total kjøreavstand.",
    "optimize.dayOptimized": "Dagstur-optimalisert",
    "optimize.dayOptimizedDesc": "Organisert rundt overnattingsstopp for logiske dagsetapper.",
    "optimize.recommended": "Anbefalt",
    "optimize.stopOrder": "Stopprekkefølge",
    "optimize.start": "Start",
    "optimize.end": "Reisemål",
    "optimize.overnights": "overnattinger",
    "optimize.shorter": "kortere",
    "optimize.longer": "lengre",
    "optimize.saves": "besparelser",
    "optimize.useThisRoute": "Bruk denne ruten",
    "optimize.cancel": "Avbryt",
    "tip.routeHighlights": "Tips langs denne ruten",
    "tip.routeHighlightsDesc": "Anbefalte steder og høydepunkter for turen din",
    "tip.openInMaps": "Åpne i kart",
    "tip.saveMoney": "Spar penger",
    "tip.fuelEstimate": "Drivstoffkostnad",
    "tip.campingCost": "Camping",
    "tip.night": "natt",
    "tip.yourDescription": "Din beskrivelse (valgfritt)",
    "tip.yourDescriptionPlaceholder": "Del hva som gjør denne ruten spesiell, tips til andre, eller hvorfor du likte den...",
    "submit.loginRequired": "Innlogging kreves",
    "submit.photos": "Bilder",
    "submit.photosDesc": "Last opp opptil 5 bilder",
    "wizard.whenTravel": "Når reiser du?",
    "wizard.saving": "Lagrer...",
    "budget.title": "Reisebudsjett",
    "budget.tab": "Budsjett",
    "budget.setBudget": "Sett budsjett",
    "budget.totalBudget": "Totalt budsjett (kr)",
    "budget.spent": "Brukt",
    "budget.remaining": "Gjenværende",
    "budget.used": "brukt",
    "budget.topCategory": "Største kategori",
    "budget.addExpense": "Legg til utgift",
    "budget.amount": "Beløp",
    "budget.description": "Beskrivelse (valgfritt)",
    "budget.save": "Lagre",
    "budget.cancel": "Avbryt",
    "budget.expenses": "Utgifter",
    "budget.breakdown": "Kategorifordeling",
    "budget.expenseAdded": "Utgift lagt til!",
    "budget.expenseUpdated": "Utgift oppdatert!",
    "budget.expenseFailed": "Kunne ikke lagre utgift",
    "budget.autoLabel": "Automatisk generert",
    "budget.editFuelCalc": "Rediger",
    "budget.fuelCalcTitle": "Beregning av drivstoffkostnad",
    "budget.fuelPriceLabel": "Pris/liter",
    "budget.fuelConsumptionLabel": "l/100km",
    "budget.fuelDistanceLabel": "Distanse (km)",
    "budget.fuelCalcResult": "Estimert drivstoffkostnad:",
    "budget.fuelSave": "Oppdater drivstoffkostnad",
    "budget.fuelUpdated": "Drivstoffkostnad oppdatert!",
    "budget.budgetSaved": "Budsjett lagret!",
    "budget.getAiTips": "Få budsjettips fra Vannie",
    "budget.aiTipsTitle": "Vannies budsjettips",
    "budget.noTips": "Ingen tips tilgjengelig akkurat nå.",
    "budget.tipsFailed": "Kunne ikke laste inn tips",
    "budget.close": "Lukk",
    "budget.cat.fuel": "Drivstoff",
    "budget.cat.camping": "Camping/Plass",
    "budget.cat.food": "Mat og drikke",
    "budget.cat.tolls": "Bompenge/Ferge",
    "budget.cat.activities": "Aktiviteter",
    "budget.cat.shopping": "Shopping",
    "budget.cat.other": "Annet",
    "budget.budgetLabel": "Budsjett",
    "budget.edit": "Rediger",
    "budget.backToTrip": "Tilbake til tur",
    "budget.estimatedFuel": "Estimert drivstoff",
    "budget.estimateOnly": "Kun estimat",
    "budget.autoCosts": "Automatiske kostnader",
    "budget.manualExpenses": "Dine utgifter",
    "budget.pageDesc": "Administrer budsjettene og utgiftene dine",
    "budget.loginRequired": "Logg inn for å bruke budsjettfunksjonen",
    "budget.subtitle": "Dine budsjetter og utgifter",
    "budget.pageSubtitle": "Planlegg reisebudsjettet ditt, legg til utgifter og følg med på hvor mye du har igjen.",
    "budget.newBudget": "Nytt budsjett",
    "budget.newExpense": "Ny utgift",
    "budget.tabActive": "Aktiv",
    "budget.tabAll": "Alle",
    "budget.tabExpenses": "Ulenkede utgifter",
    "budget.tabUnlinked": "Ulenkede",
    "budget.emptyTitle": "Ingen budsjetter ennå",
    "budget.emptyDesc": "Opprett ditt første budsjett for å få bedre oversikt over turkostnadene dine.",
    "budget.emptyStateTitle": "Ingen budsjett opprettet ennå",
    "budget.emptyStateDesc": "Opprett ditt første reisebudsjett for å få bedre oversikt over kostnader til overnatting, mat, drivstoff og aktiviteter.",
    "budget.createBudget": "Opprett budsjett",
    "budget.createFirstBudget": "Opprett første budsjett",
    "budget.chipAccommodation": "Overnatting",
    "budget.chipFood": "Mat",
    "budget.chipFuel": "Drivstoff",
    "budget.chipActivities": "Aktiviteter",
    "budget.chipShopping": "Shopping",
    "budget.unlinkedLabel": "Ulenkede utgifter",
    "budget.unlinkedHint": "Disse utgiftene er ikke koblet til noe budsjett.",
    "budget.more": "mer",
    "budget.noActiveTitle": "Ingen aktive budsjetter",
    "budget.noActiveDesc": "Opprett et budsjett eller aktiver et eksisterende.",
    "budget.overviewTitle": "Budsjettsoversikt",
    "budget.statusGood": "På sporet",
    "budget.statusWarning": "Nærmer seg grensen",
    "budget.statusOver": "Over budsjett",
    "budget.pctUsed": "brukt",
    "budget.totalBudgetCard": "Totalbudsjett",
    "budget.spentCard": "Brukt",
    "budget.remainingCard": "Igjen",
    "budget.paused": "Pause",
    "budget.completed": "fullført",
    "budget.noExpenses": "Ingen ulenkede utgifter",
    "budget.noExpensesDesc": "Utgifter uten et linket budsjett vil vises her",
    "budget.setBudgetTitle": "Månedlig budsjett",
    "budget.setBudgetBtn": "Sett budsjett",
    "budget.setBudgetSaved": "Budsjettgrense lagret ✓",
    "budget.month": "Måned",
    "budget.budgetAmount": "Budsjett",
    "budget.amountPlaceholder": "f.eks. 15000",
    "budget.saving": "Lagrer...",
    "budget.saveBudgetLimit": "Lagre budsjettgrense",
    "budget.createBudgetTitle": "Opprett budsjett",
    "budget.budgetName": "Budsjettnavn",
    "budget.budgetNamePlaceholder": "f.eks. Sommerferie 2026",
    "budget.budgetTypeLabel": "Budsjett type",
    "budget.totalBudgetLabel": "Totalt budsjett",
    "budget.currencyLabel": "Valuta",
    "budget.startDateLabel": "Startdato",
    "budget.endDateLabel": "Sluttdato",
    "budget.colorLabel": "Farge",
    "budget.selectColor": "Velg farge",
    "budget.descriptionLabel": "Beskrivelse",
    "budget.descriptionPlaceholder": "Valgfri merknad...",
    "budget.linkTrip": "Koble til tur (valgfritt)",
    "budget.noTripSelected": "Ingen tur valgt",
    "budget.noTrip": "Ingen tur",
    "budget.fillNameAndAmount": "Skriv inn navn og gyldig beløp",
    "budget.budgetCreated": "Budsjett opprettet ✓",
    "budget.couldNotSave": "Kunne ikke lagre",
    "budget.creating": "Oppretter...",
    "budget.addExpenseTitle": "Legg til utgift",
    "budget.scanWithAi": "Skann kvittering med AI",
    "budget.amountLabel": "Beløp",
    "budget.dateLabel": "Dato",
    "budget.titleLabel": "Tittel",
    "budget.titlePlaceholder": "Hva kjøpte du?",
    "budget.categoryLabel": "Kategori",
    "budget.paymentMethod": "Betalingsmetode",
    "budget.selectPayment": "Velg...",
    "budget.linkBudget": "Koble til budsjett (valgfritt)",
    "budget.linkBudgetDirect": "Koble til budsjett",
    "budget.noBudgetSelected": "Ingen budsjett valgt",
    "budget.noBudget": "Intet budsjett",
    "budget.noteLabel": "Merknad",
    "budget.notePlaceholder": "Valgfri merknad...",
    "budget.receiptLabel": "Kvittering",
    "budget.receiptRemove": "Fjern",
    "budget.receiptUpload": "Ta bilde eller last opp kvittering",
    "budget.fileTooLarge": "Filen er for stor",
    "budget.fileTooLargeDesc": "Maks 15 MB",
    "budget.fillAmountAndTitle": "Skriv inn et gyldig beløp og tittel",
    "budget.expenseSaved": "Utgift lagret ✓",
    "budget.converted": "Konvertert:",
    "budget.editBudgetTitle": "Rediger budsjett",
    "budget.budgetUpdated": "Budsjett oppdatert ✓",
    "budget.saveChanges": "Lagre endringer",
    "budget.editExpenseTitle": "Rediger utgift",
    "budget.scanTitle": "Smart kvitteringsskanning",
    "budget.scanCapture": "Ta bilde eller last opp kvittering",
    "budget.scanCaptureDesc": "AI analyserer kvitteringen din linje for linje",
    "budget.scanTakePhoto": "Ta bilde",
    "budget.scanUpload": "Last opp",
    "budget.scanAnalyzing": "Analyserer kvittering...",
    "budget.scanAnalyzingDesc": "AI leser varer, beløp og kategorier",
    "budget.scanFailed": "Kunne ikke analysere kvittering",
    "budget.scanConfidenceHigh": "Høy tillit",
    "budget.scanConfidenceMedium": "Middels tillit — gjennomgå felter",
    "budget.scanConfidenceLow": "Lav tillit — sjekk alle felter",
    "budget.scanCandidates": "Mulige beløp:",
    "budget.scanItemsPerCategory": "Varer per kategori",
    "budget.scanMixedCategories": "Blandede kategorier",
    "budget.scanSaveHow": "Hvordan vil du lagre?",
    "budget.scanSingleExpense": "Én utgift",
    "budget.scanSplitExpenses": "Splitt",
    "budget.scanSplitDesc": "Oppretter {n} separate utgifter — én per kategori.",
    "budget.scanMainCategory": "Hovedkategori",
    "budget.scanShowRaw": "Vis rå tekst fra kvittering",
    "budget.scanHideRaw": "Skjul rå tekst fra kvittering",
    "budget.scanCancel": "Avbryt",
    "budget.scanSaveExpense": "Lagre utgift",
    "budget.scanSaveExpenses": "Lagre {n} utgifter",
    "budget.scanSavedMultiple": "{n} utgifter lagret fra kvittering ✓",
    "budget.scanSavedSingle": "Utgift lagret fra kvittering ✓",
    "budget.confidenceMedium": "Middels tillit",
    "budget.confidenceLow": "Usikkert — vennligst sjekk",
    "budget.statusPaused": "Pauset",
    "budget.statusCompleted": "Fullført",
    "budget.budgetLabel2": "Budsjett",
    "budget.spentLabel": "Brukt",
    "budget.remainingLabel": "Gjenstående",
    "budget.overBy": "Over med",
    "budget.usedPercent": "brukt",
    "budget.expenseCount": "utgifter",
    "budget.mixedCurrenciesNote": "Utgifter i {currencies} — konvertert til {target}",
    "budget.deleteConfirm": "Er du sikker på at du vil slette dette budsjettet?",
    "budget.statusActivated": "Budsjett aktivert",
    "budget.statusPausedToast": "Budsjett pauset",
    "budget.statusCompletedToast": "Budsjett fullført",
    "budget.perCategory": "Per kategori",
    "budget.biggestExpense": "Største utgift",
    "budget.expenseListTitle": "Utgifter",
    "budget.noExpensesYet": "Ingen utgifter ennå",
    "budget.noExpensesYetDesc": "Trykk \"Legg til utgift\" for å starte",
    "budget.totalSpent": "Totalt brukt",
    "budget.thisMonth": "Denne måneden",
    "budget.budgeted": "Budsjettert",
    "budget.warnings": "Advarsler",
    "summary.title": "Kostnadssammendrag for tur",
    "summary.titleOngoing": "Kostnadsoversikt",
    "summary.over": "Over budsjett",
    "summary.saved": "Sparret",
    "summary.dailyAvg": "Per dag",
    "summary.overBudget": "Du gikk over budsjettet på denne turen",
    "summary.underBudget": "Du holdt deg innenfor budsjettet — flott jobba!",
    "summary.breakdown": "Forbruksfordeling",
    "summary.categoryDetails": "Kategoridetaljer",
    "fuelLog.title": "Drivstofflogg",
    "fuelLog.addFuel": "Legg til fylling",
    "fuelLog.liters": "Liter",
    "fuelLog.cost": "Kostnad",
    "fuelLog.date": "Dato",
    "fuelLog.odometer": "Kilometerstand (km)",
    "fuelLog.note": "Merknad",
    "fuelLog.optional": "Valgfritt",
    "fuelLog.save": "Lagre",
    "fuelLog.added": "Drivstoffylling logget!",
    "fuelLog.addFailed": "Kunne ikke lagre drivstoffylling",
    "fuelLog.invalidInput": "Skriv inn både liter og kostnad",
    "fuelSummary.title": "Drivstoff-sammendrag",
    "fuelSummary.totalLiters": "Totalt antall liter",
    "fuelSummary.totalCost": "Drivstoffkostnad",
    "fuelSummary.avgPrice": "Gjennomsnittlig pris/liter",
    "fuelSummary.consumption": "Forbruk",
    "fuelSummary.costPerKm": "Kostnad/km",
    "fuelSummary.costPerMil": "Kostnad/mil",
    "fuelSummary.noDistance": "Legg til rutedistanse for å se forbruk og kostnad per km.",
    "fuelSummary.basedOn": "Basert på {n} fylling(er)",
    "checklist.tab": "Sjekkliste",
    "journal.tab": "Dagbok",
    "authGate.ctaSection": "Alt du trenger for turen din",
    "authGate.ctaButton": "Opprett konto og lagre",
    "authGate.saveBudgetMsg": "Lagre budsjettet ditt og spor utgifter",
    "authGate.saveReceiptMsg": "Ta vare på kvitteringene dine på tvers av enheter",
    "authGate.saveFavoriteMsg": "Lagre favorittstedene dine i én liste",
    "authGate.useBudgetMsg": "Spor budsjettet ditt — opprett en gratis konto",
    "authGate.useChecklistMsg": "Bruk sjekklister — opprett en gratis konto",
    "gate.whyJoin": "HVORFOR OPPrette EN KONTO?",
    "gate.benefit1": "Ta vare på turen, stoppene og budsjettet ditt",
    "gate.benefit2": "Fortsett planleggingen på hvilken som helst enhet",
    "gate.benefit3": "Full budssporingskontroll med kvitteringsskanning",
    "gate.benefit4": "AI-reiseassistent inkludert gratis",
    "gate.benefit5": "Tilgang til fellesskapets tips og anmeldelser",
    "gate.benefit6": "Sjekklister tilpasset ditt kjøretøy",
    "welcome.title": "Velkommen til Vantastika!",
    "welcome.titleName": "Velkommen, {name}!",
    "welcome.desc": "Alt du trenger for å planlegge, reise og huske turene dine er her.",
    "welcome.feature1": "Planlegg ruter, legg til stopp og spor budsjett og drivstoff",
    "welcome.feature2": "Vannie, din AI-reiseassistent, hjelper deg med å finne de beste rutene",
    "welcome.feature3": "Lagre favorittsteder, del turer med venner",
    "welcome.cta": "Planlegg min første tur",
    "welcome.dismiss": "Utforsk på egen hånd",
    "welcome.close": "Lukk",
    "gate.noCard": "Gratis å komme i gang. Ingen kredittkort kreves.",
    "gate.createAccount": "Opprett gratis konto",
    "gate.alreadyMember": "Har du allerede en konto? Logg inn",
    "gate.seeMore": "Vil du se mer?",
    "gate.registerToSee": "Opprett en gratis konto for å få tilgang til alle funksjoner og steder.",
    "gate.searchTitle": "Utforsk alle campingplasser",
    "gate.searchDesc": "Registrer deg gratis for å bla gjennom alle verifiserte steder, lagre favorittene dine og lese anmeldelser fra fellesskapet.",
    "gate.servicesTitle": "Service og nødsituasjoner",
    "gate.servicesDesc": "Opprett en konto for å finne verksteder, ladestasjoner, vannpunkter og nødtjenester i nærheten av deg.",
    "gate.tripsTitle": "Turplanlegger",
    "gate.tripsDesc": "Registrer deg gratis for å planlegge ubegrensede turer med smarte ruter, drivstoffkostnader og budssporingskontroll.",
    "gate.submitTitle": "Legg til et sted",
    "gate.submitDesc": "Registrer deg gratis for å dele favorittstedene dine med fellesskapet.",
    "gate.tipsTitle": "Veitips",
    "gate.tipsDesc": "Opprett en konto for å lese og dele reisetips fra ekte reisende.",
    "roadTips.title": "Veitips",
    "roadTips.share": "Del et tips",
    "roadTips.allCountries": "Alle land",
    "roadTips.formTitle": "Del ditt veitips",
    "roadTips.labelTitle": "Tittel *",
    "roadTips.placeholderTitle": "f.eks. Beste rute gjennom Alpene",
    "roadTips.labelType": "Type",
    "roadTips.labelCountry": "Land",
    "roadTips.select": "Velg",
    "roadTips.labelTip": "Ditt tips *",
    "roadTips.placeholderTip": "Del din opplevelse...",
    "roadTips.labelImage": "Bilde-URL (valgfritt)",
    "roadTips.posting": "Publiserer...",
    "roadTips.post": "Publiser tips",
    "roadTips.shared": "Tips delt!",
    "roadTips.loginToShare": "Logg inn for å dele dine egne veitips.",
    "roadTips.noTips": "Ingen veitips ennå. Bli den første til å dele!",
    "roadTips.type.general": "Generelt tips",
    "roadTips.type.route": "Ruteanbefaling",
    "roadTips.type.parking": "Parkeringstips",
    "roadTips.type.food": "Mat & Shopping",
    "roadTips.type.warning": "Advarsel",
    "roadTips.type.service": "Servicepunkt",
    "community.title": "Community",
    "community.subtitle": "Del opplevelser, tips og inspirasjon med andre reisende",
    "community.newPost": "Nytt innlegg",
    "community.createPost": "Opprett nytt innlegg",
    "community.all": "Alle",
    "community.anonymous": "Anonym",
    "community.noPostsYet": "Ingen innlegg ennå",
    "community.beFirst": "Bli den første til å starte en diskusjon!",
    "community.loadMore": "Last flere innlegg",
    "community.loading": "Laster…",
    "community.createFirst": "Opprett det første innlegget",
    "community.back": "Tilbake",
    "community.comments": "Kommentarer",
    "community.noComments": "Ingen kommentarer ennå — bli den første!",
    "community.commentPlaceholder": "Skriv en kommentar...",
    "community.titlePlaceholder": "Tittel",
    "community.contentPlaceholder": "Hva vil du diskutere?",
    "community.addImage": "Legg til bilde (valgfritt)",
    "community.publishing": "Publiserer...",
    "community.publish": "Publiser innlegg",
    "community.toast.postCreated": "Innlegg opprettet!",
    "community.toast.postCreateError": "Kunne ikke opprette innlegget.",
    "community.toast.commentError": "Kunne ikke opprette kommentaren.",
    "community.toast.postDeleted": "Innlegg slettet",
    "community.toast.error": "Feil",
    "community.confirmDelete": "Er du sikker på at du vil slette dette innlegget?",
    "community.cat.general": "Generelt",
    "community.cat.tips": "Tips & Triks",
    "community.cat.routes": "Ruter & Steder",
    "community.cat.gear": "Utstyr",
    "community.cat.help": "Hjelp & Spørsmål",
    "community.cat.photos": "Bilder & Reisehistorier",
    "community.type.question": "Spørsmål",
    "community.type.tip": "Tips",
    "community.type.discussion": "Diskusjon",
    "community.type.routes": "Ruter & steder",
    "community.type.gear": "Utstyr",
    "community.type.photos": "Bilder",
    "community.type.help": "Hjelp",
    "community.type.question.desc": "Still et spørsmål til fellesskapet",
    "community.type.tip.desc": "Del et godt tips",
    "community.type.discussion.desc": "Start en diskusjon",
    "community.type.routes.desc": "Del favorittsteder",
    "community.type.gear.desc": "Utstyr og tilbehør",
    "community.type.photos.desc": "Del reisen din",
    "community.type.help.desc": "Be om hjelp",
    "community.awaitingAnswer": "Venter på svar",
    "community.selectType": "Velg innleggstype",
    "community.createPostTitle": "Opprett innlegg",
    "community.changeType": "Bytt type",
    "community.questionPlaceholder": "Skriv spørsmålet ditt her...",
    "community.questionContentPlaceholder": "Beskriv spørsmålet i detalj slik at andre kan hjelpe deg...",
    "community.communityRules": "Fellesskapsregler",
    "community.communityRulesText": "Du kan ikke publisere nakenbilder, seksuelt innhold, reklame, annonser, salg, spam, trusler eller trakassering. Alle innlegg og bilder kan bli gjennomgått automatisk før publisering.",
    "games.seoTitle": "Roadtrip-spill – Vantastika",
    "games.seoDesc": "Spill quiz, bingo og flere spill under roadtripen",
    "social.places": "Steder",
    "social.tripsPlanned": "Planlagte turer",
    "social.members": "Medlemmer",
    "social.reviews": "Anmeldelser",
    "social.popularRoutes": "Populære ruter fra andre reisende",
    "social.traveler": "Reisende",
    "social.stops": "stopp",
    "error.somethingWrong": "Noe gikk galt",
    "error.autoRepairFailed": "Vi prøvde å reparere automatisk, men det virket ikke. Prøv å laste siden på nytt.",
    "error.tryAgain": "Prøv igjen",
    "error.reloadPage": "Last siden på nytt",
    "a11y.decreaseText": "Reduser tekststørrelse",
    "a11y.increaseText": "Øk tekststørrelse",
    "a11y.decreaseTooltip": "Mindre tekst",
    "a11y.increaseTooltip": "Større tekst",
    "aria.close": "Lukk",
    "aria.openVannie": "Åpne Vannie",
    "aria.previous": "Forrige",
    "aria.next": "Neste",
    "aria.openMenu": "Åpne meny",
    "aria.closeMenu": "Lukk meny",
    "aria.step": "Steg",
    "onboarding.step": "Steg {current} av {total}",
    "onboarding.back": "Tilbake",
    "onboarding.skip": "Hopp over",
    "onboarding.planTrip": "Planlegg turen din",
    "onboarding.planTripDesc": "Fortell oss hvor du vil dra, så hjelper vi deg med å bygge turen din – rute, stopp og budsjett på ett sted.",
    "onboarding.createFirstTrip": "Lag min første tur",
    "onboarding.findPlaces": "Finn steder på kartet",
    "onboarding.findPlacesDesc": "Campingplasser, attraksjoner og restauranter – legg til stopp direkte fra kartet til turen din.",
    "onboarding.searchCampings": "Utforsk kartet",
    "onboarding.budgetChecklist": "Spor budsjett og utgifter",
    "onboarding.budgetChecklistDesc": "Loggfør hva du bruker, skann kvitteringer og hold oversikt over tur-budsjettet ditt – alt på ett sted.",
    "onboarding.createTrip": "Start budsjettet mitt",
    "onboarding.askVannie": "Møt Vannie",
    "onboarding.askVannieDesc": "Din personlige reiseassistent. Få tips, ruteideer og lokale regler – inkludert gratis.",
    "onboarding.doneStart": "La oss dra!",
    "compare.title": "Sammenlign campingplasser",
    "compare.subtitle": "Velg opptil {max} campingplasser for å sammenligne side om side.",
    "compare.add": "Legg til",
    "compare.searchPlaceholder": "Søk camping...",
    "compare.property": "Fasilitet",
    "compare.rating": "Vurdering",
    "compare.pricePerNight": "Pris/natt",
    "compare.free": "Gratis",
    "compare.type": "Type",
    "compare.addMore": "Legg til minst én campingplass til for å sammenligne.",
    "compare.selectCampings": "Velg campingplasser for å sammenligne",
    "compare.clickAdd": "Klikk \"Legg til\" over for å komme i gang.",
    "compare.electricity": "Strøm",
    "compare.freshWater": "Ferskvann",
    "compare.shower": "Dusj",
    "compare.pets": "Kjæledyr",
    "compare.greyWater": "Gråvann",
    "compare.toiletCassette": "Kassettoalett",
    "compare.wc": "WC",
    "compare.openAllYear": "Åpent hele året",
    "compare.familyFriendly": "Familievennlig",
    "compare.largeMotorhomes": "Store bobiler",
    "compare.nearWater": "Nær vann",
    "compare.spaWellness": "Spa/Velvære",
    "friends.nearby": "Reisefølge i nærheten",
    "friends.sharingOn": "Deler lokasjon",
    "friends.sharingOff": "Deler ikke lokasjon",
    "friends.enableSharing": "Aktiver stedsdeling for å se personer du deler turer med",
    "friends.onlyCollaborators": "Bare de du samarbeider med på en tur kan se din posisjon",
    "friends.noOneSharing": "Ingen reisefølge deler posisjonen sin akkurat nå",
    "friends.anonymous": "Anonym",
    "friends.away": "borte",
    "friends.unknownDistance": "Ukjent avstand",
    "spot.loginToNavigate": "Logg inn for å åpne navigasjon",
    "spot.navigateHere": "Naviger hit",
    "incident.title": "Rapporter hendelse",
    "incident.button": "Rapporter hendelse",
    "incident.stepOf": "Steg {current} av {total}",
    "incident.saving": "Lagrer...",
    "incident.step1": "Type hendelse",
    "incident.step2": "Tid og sted",
    "incident.step3": "Kjøretøydetaljer",
    "incident.step4": "Hva skjedde",
    "incident.step5": "Annen part",
    "incident.step6": "Sammendrag og PDF",
    "incident.type.collision": "Kollisjon",
    "incident.type.theft": "Tyveri",
    "incident.type.vandalism": "Vandalisme",
    "incident.type.breakdown": "Sammenbrudd",
    "incident.type.weather": "Værskade",
    "incident.type.other": "Annet",
    "incident.date": "Dato",
    "incident.time": "Tid",
    "incident.location": "Sted",
    "incident.locationPlaceholder": "Gateadresse eller stedsnavn",
    "incident.locationHint": "Vær så spesifikk som mulig – gate, by, land",
    "incident.vehicleType": "Kjøretøytype",
    "incident.vehicleTypePlaceholder": "f.eks. Bobil, Campervan, Bil",
    "incident.registration": "Registreringsnummer",
    "incident.vehicleDesc": "Beskrivelse av kjøretøy",
    "incident.vehicleDescPlaceholder": "Farge, merke, modell, år",
    "incident.whatHappened": "Hva skjedde?",
    "incident.descriptionPlaceholder": "Beskriv hendelsen klart og rolig",
    "incident.additionalNotes": "Tilleggsnokater",
    "incident.notesPlaceholder": "Vitner, vær, veiforhold...",
    "incident.otherPartyHint": "Fyll ut hvis en annen part var involvert. Hopp over hvis ikke aktuelt.",
    "incident.otherName": "Navn",
    "incident.otherPhone": "Telefonnummer",
    "incident.otherVehicle": "Kjøretøy",
    "incident.otherReg": "Registreringsnummer",
    "incident.otherInsurance": "Forsikringsselskap",
    "incident.incidentType": "Hendelsestype",
    "incident.summary": "Sammendrag av rapport",
    "incident.downloadPdf": "Lagre og last ned PDF",
    "incident.saveOnly": "Lagre uten PDF",
    "incident.wizardTitle": "Skadeunderlag",
    "incident.step0.title": "Viktig informasjon",
    "incident.step0.heading": "Dette er et skadeunderlag — ikke en skademelding",
    "incident.step0.body1": "Med dette verktøyet lager du et strukturert underlag som hjelper deg å samle korrekt informasjon ved en hendelse.",
    "incident.step0.body2": "Du er selv ansvarlig for å gjennomgå og sende rapporten til forsikringsselskapet ditt.",
    "incident.step0.gdprTitle": "Personvern (GDPR)",
    "incident.step0.gdprBody1": "All informasjon lagres sikkert i kontoen din. Opplysningene deles ikke automatisk med tredjeparter.",
    "incident.step0.gdprBody2": "Du kan når som helst slette en hendelsesrapport og alle tilhørende data.",
    "incident.step0.accept": "Jeg forstår — start",
    "incident.step1.title": "Type hendelse",
    "incident.step2.title": "Dato, tid og sted",
    "incident.step3.title": "Ditt kjøretøy",
    "incident.step4.title": "Motpartens kjøretøy",
    "incident.step5.title": "Sjåfører og eiere",
    "incident.step6.title": "Forsikring",
    "incident.step7.title": "Skadebeskrivelse",
    "incident.step8.title": "Bilder",
    "incident.step9.title": "Oppsummering",
    "incident.stepOf2": "Trinn {current} av {total}",
    "incident.selectCountry": "Velg land...",
    "incident.searchCountry": "Søk land...",
    "incident.searchInsurance": "Søk eller skriv manuelt...",
    "incident.regNum": "Registreringsnummer",
    "incident.regCountry": "Registreringsland",
    "incident.makeModel": "Merke / Modell",
    "incident.driverIsOwner": "Sjåfør = Eier",
    "incident.yes": "Ja",
    "incident.no": "Nei",
    "incident.ownerName": "Eierens navn",
    "incident.ownerPhone": "Eierens telefon",
    "incident.ownerEmail": "Eierens e-post",
    "incident.otherDriverName": "Sjåførens navn",
    "incident.otherDriverPhone": "Sjåførens telefon",
    "incident.yourInsurance": "Din forsikring",
    "incident.opponentInsurance": "Motpartens forsikring",
    "incident.insuranceCompany": "Forsikringsselskap",
    "incident.policyNum": "Polisenummer",
    "incident.policyNumHint": "Finnes på forsikringsbeviset",
    "incident.damageWhat": "Hva skjedde?",
    "incident.damageWhatPlaceholder": "Beskriv hva som skjedde",
    "incident.damageHow": "Hvordan skjedde det?",
    "incident.damageHowPlaceholder": "Beskriv omstendighetene, hastighet, sikt, osv.",
    "incident.damageParts": "Hvilke skader oppsto?",
    "incident.damagePartsPlaceholder": "Hvilke deler av kjøretøyet ble skadet?",
    "incident.damageOther": "Andre detaljer",
    "incident.damageOtherPlaceholder": "Vitner, vær, veiforhold...",
    "incident.notesLabel": "Notater",
    "incident.notesPlaceholder2": "Andre notater",
    "incident.imagesOptional": "Bilder er valgfrie, men anbefales",
    "incident.imagesHint": "Bilder kan dokumentere skadene og forenkle behandlingen hos forsikringsselskapet.",
    "incident.takePhoto": "Ta bilde",
    "incident.uploadBtn": "Last opp",
    "incident.uploading": "Laster opp...",
    "incident.saveDraftFirst": "Rapporten må lagres før du kan laste opp bilder.",
    "incident.saveDraftBtn": "Lagre utkast først",
    "incident.imagesSecure": "Bilder lagres sikkert i kontoen din og deles ikke automatisk. Du kan slette dem når som helst. Kun JPG og PNG (maks 5 MB).",
    "incident.notFilled": "Ikke utfylt",
    "incident.notSpecified": "Ikke angitt",
    "incident.notDescribed": "Ikke beskrevet",
    "incident.change": "Endre",
    "incident.summaryTitle": "Oppsummering",
    "incident.disclaimerShort": "Du er selv ansvarlig for å gjennomgå og sende rapporten til forsikringsselskapet ditt.",
    "incident.generatePdf": "Genererer PDF...",
    "incident.savePdf": "Lagre og last ned PDF",
    "incident.saveNoPdf": "Lagre uten PDF",
    "incident.reportSaved": "Hendelsesrapport lagret",
    "incident.couldNotSave": "Kunne ikke lagre rapport",
    "incident.invalidFile": "Ugyldig fil",
    "incident.uploadFailed": "Opplasting mislyktes",
    "incident.imagesAttached": "{count} bilde(r) vedlagt",
    "incident.timeAndPlace": "Tid og sted",
    "incident.yourVehicle": "Ditt kjøretøy",
    "incident.opponent": "Motpart",
    "incident.insurance": "Forsikring",
    "incident.damages": "Skader",
    "incident.images": "Bilder",
    "incident.val.selectType": "Velg type hendelse",
    "incident.val.dateRequired": "Dato er påkrevd",
    "incident.val.locationRequired": "Sted er påkrevd",
    "incident.val.regRequired": "Registreringsnummer er påkrevd",
    "incident.val.regCountryRequired": "Registreringsland er påkrevd",
    "incident.val.vehicleTypeRequired": "Kjøretøytype er påkrevd",
    "incident.val.ownerNameRequired": "Eierens navn er påkrevd",
    "incident.val.describeWhat": "Beskriv hva som skjedde",
    "incident.val.describeHow": "Beskriv hvordan det skjedde",
    "incident.val.typeMissing": "Hendelsestype mangler",
    "incident.val.regMissing": "Registreringsnummer mangler",
    "incident.val.damageMissing": "Skadebeskrivelse mangler",
    "incident.opponentVehicle": "Motpartens kjøretøy",
    "incident.yourDriver": "Dine opplysninger",
    "incident.opponentDriver": "Motpartens opplysninger",
    "incident.pdf.title": "Skadeunderlag",
    "incident.pdf.disclaimer": "Dette dokumentet er et skadeunderlag og ikke en offisiell skademelding. Du er selv ansvarlig for å gjennomgå og sende rapporten til forsikringsselskapet ditt.",
    "incident.pdf.generated": "Generert",
    "incident.pdf.trip": "Tur",
    "incident.pdf.type": "Type",
    "incident.pdf.date": "Dato",
    "incident.pdf.time": "Tid",
    "incident.pdf.location": "Sted",
    "incident.pdf.vehicleType": "Kjøretøytype",
    "incident.pdf.regNum": "Registreringsnummer",
    "incident.pdf.regCountry": "Registreringsland",
    "incident.pdf.makeModel": "Merke / Modell",
    "incident.pdf.driverIsOwner": "Sjåfør = Eier",
    "incident.pdf.ownerName": "Eierens navn",
    "incident.pdf.ownerPhone": "Eierens telefon",
    "incident.pdf.ownerEmail": "Eierens e-post",
    "incident.pdf.driver": "Sjåfør",
    "incident.pdf.phone": "Telefon",
    "incident.pdf.insuranceCompany": "Forsikringsselskap",
    "incident.pdf.policyNum": "Polisenummer",
    "incident.pdf.whatHappened": "Hva skjedde",
    "incident.pdf.howHappened": "Hvordan skjedde det",
    "incident.pdf.damagedParts": "Skadede deler",
    "incident.pdf.otherDetails": "Andre detaljer",
    "incident.pdf.notes": "Notater",
    "incident.pdf.images": "Bilder",
    "incident.pdf.imagesNote": "{count} bilde(r) vedlagt. Bildene er tilgjengelige i Vantastika-kontoen din.",
    "incident.pdf.footer": "Dette dokumentet er et skadeunderlag laget med Vantastika og utgjør ikke en offisiell skademelding.",
    "incident.pdf.footerReview": "Du er selv ansvarlig for å kontrollere opplysningene og sende dem til forsikringsselskapet ditt.",
    "incident.pdf.yourVehicle": "Ditt kjøretøy",
    "incident.pdf.opponentVehicle": "Motpartens kjøretøy",
    "incident.pdf.damageDesc": "Skadebeskrivelse",
    "incident.pdf.incident": "Hendelse",
    "notFound.title": "Siden ble ikke funnet",
    "notFound.description": "Siden du lette etter ser ikke ut til å finnes. Den kan ha blitt flyttet eller fjernet.",
    "notFound.home": "Til startsiden",
    "notFound.back": "Gå tilbake",
    "incident.generating": "Genererer PDF...",
    "incident.saved": "Hendelsesrapport lagret",
    "incident.saveFailed": "Kunne ikke lagre rapport",
    "incident.back": "Tilbake",
    "incident.next": "Neste",
    "incident.cancel": "Avbryt",
    "incident.pdfTitle": "Hendelsesrapport",
    "incident.pdfGenerated": "Generert",
    "incident.pdfDisclaimer": "Dette er en personlig protokoll. Det er ikke et offisielt kravskjema for forsikring.",
    "incident.drafts": "Utkast til rapporter",
    "incident.noDrafts": "Ingen utkast til rapporter",
    "incident.continueDraft": "Fortsett",
    "incident.noReports": "Ingen hendelsesrapporter ennå",
    "incident.deleted": "Rapport slettet",
    "incident.draft": "Utkast",
    "incident.completed": "Fullført",
    "incident.myReports": "Mine hendelsesrapporter",
    "incident.myReportsSubtitle": "Dine lagrede utkast og fullførte rapporter",
    "incident.vtype.motorhome": "Bobiler",
    "incident.vtype.campervan": "Campingbiler",
    "incident.vtype.car": "Bil",
    "incident.vtype.motorcycle": "Motorsykkel",
    "incident.vtype.trailer": "Tilhenger",
    "incident.vtype.other": "Annet",
    "quick.hello": "Hei {name}! 👋",
    "quick.whatToDo": "Hva vil du gjøre i dag?",
    "quick.continueTrip": "Fortsett tur",
    "quick.activeTrip": "Aktiv tur",
    "quick.newTrip": "Ny tur",
    "quick.planNext": "Planlegg ditt neste eventyr",
    "quick.upcomingTrip": "Kommende tur",
    "quick.findCamping": "Finn camping",
    "quick.searchFilter": "Søk og filtrer",
    "quick.favorites": "Favoritter",
    "quick.saved": "{count} lagret",
    "quick.community": "Fellesskap",
    "quick.tipsDiscussions": "Tips og diskusjoner",
    "gdpr.title": "Vi bryr oss om personvernet ditt",
    "gdpr.description": "Dette nettstedet bruker kun essensielle og funksjonelle informasjonskapsler (autentisering, språkvalg, landsinnstilling og samtykke til informasjonskapsler). Ingen sporings-, annonserings- eller tredjepartsanalysecookies brukes.",
    "gdpr.readPolicy": "Les personvernreglene våre",
    "gdpr.accept": "Forstått",
    "gdpr.decline": "Forstått",
    "install.title": "Installer Vantastika",
    "install.description": "Legg til på startskjermen for rask tilgang - fungerer som en app!",
    "install.installButton": "Installer app",
    "install.showHow": "Vis hvordan",
    "install.understood": "Forstått",
    "install.iosSteps": "Følg disse trinnene i Safari:",
    "install.iosTapShare": "Trykk på",
    "install.iosShareLocation": "Nederst i Safari",
    "install.iosAddHome": "Velg \"Legg til på startskjermen\"",
    "install.iosScrollDown": "Rull ned i menyen om nødvendig",
    "install.iosTapAdd": "Trykk \"Legg til\"",
    "install.androidSteps": "Følg disse trinnene:",
    "install.androidTapMenu": "Trykk på",
    "install.androidMenuLocation": "Øverst til høyre i nettleseren",
    "install.androidInstallApp": "Velg \"Installer app\"",
    "install.androidOrAddHome": "Eller \"Legg til på startskjermen\"",
    "home.trustFree": "100 % gratis",
    "myTrips.title": "Mine turer",
    "myTrips.loginTitle": "Logg inn for å se turene dine",
    "myTrips.loginDesc": "Dine lagrede turer vil vises her etter innlogging.",
    "myTrips.loginButton": "Logg inn",
    "myTrips.pausedStatus": "Pauset",
    "myTrips.activeStatus": "Aktiv tur",
    "myTrips.pausedSubtitle": "Turen din er pauset",
    "myTrips.activeSubtitle": "Du har en aktiv tur",
    "myTrips.tripsCount": "{count} turer lagret",
    "myTrips.tripCount": "{count} tur lagret",
    "myTrips.newTrip": "Ny tur",
    "myTrips.resumeTrip": "Gjenoppta tur",
    "myTrips.continueTrip": "Fortsett tur",
    "myTrips.resume": "Gjenoppta",
    "myTrips.pause": "Pause",
    "myTrips.endTrip": "Avslutt tur",
    "myTrips.end": "Avslutt",
    "myTrips.progress": "{pct} % fullført",
    "myTrips.stops": "{count} stopp",
    "myTrips.nextStop": "Neste stopp:",
    "myTrips.openActiveTrip": "Åpne aktiv tur",
    "myTrips.noTrips": "Ingen lagrede turer ennå",
    "myTrips.noTripsDesc": "Lag din første tur i planleggeren, så vil den vises her.",
    "myTrips.planFirst": "Planlegg din første tur",
    "myTrips.upcomingTrips": "Kommende turer",
    "myTrips.sharedWithMe": "Delt med meg",
    "myTrips.invitedBy": "Invited by",
    "myTrips.completedTrips": "Gjennomførte turer",
    "myTrips.startTrip": "Start tur",
    "myTrips.budget": "Budsjett",
    "myTrips.checklist": "Sjekkliste",
    "myTrips.stoppedPromptTitle": "Du ser ut til å ha stoppet",
    "myTrips.stoppedPromptDesc": "Vil du fortsette eller ta en pause?",
    "myTrips.arrivedAt": "Ankommet {name}!",
    "myTrips.arrivedDesc": "Destinasjon nådd - godt gjort!",
    "myTrips.stopRemoved": "Stopp fjernet",
    "myTrips.close": "Lukk",
    "myTrips.alreadyActiveTrip": "Du har allerede en aktiv reise",
    "myTrips.endActiveFirst": "Avslutt den først.",
    "myTrips.tripStarted": "Reisen startet!",
    "myTrips.tripStartedDesc": "GPS-sporing og journal er nå aktive.",
    "myTrips.tripPaused": "Reise satt på pause",
    "myTrips.tripPausedDesc": "Du kan fortsette når som helst.",
    "myTrips.tripResumed": "Reisen fortsetter!",
    "myTrips.tripResumedDesc": "GPS-sporing er aktiv igjen.",
    "myTrips.tripEnded": "Reisen avsluttet!",
    "myTrips.tripEndedDesc": "Reisen din er lagret.",
    "myTrips.tripReactivated": "Reisen reaktivert!",
    "myTrips.revertedToPlanned": "Tilbakestilt til planlagt",
    "myTrips.revertedToPlannedDesc": "Reisen er tilbake som planlagt. All data er intakt.",
    "myTrips.missingStops": "Reisen mangler stopp",
    "myTrips.missingStopsDesc": "Legg til minst ett stopp før du starter.",
    "myTrips.missingTitle": "Reisen mangler tittel",
    "myTrips.missingTitleDesc": "Gi reisen et navn før du starter.",
    "trip.stopAddedFromMap": "Stopp lagt til fra kartet",
    "trip.stopPositionUpdated": "Stoppets posisjon oppdatert",
    "trip.routeOptimizedDesc": "Stoppene er sortert for korteste kjøredistanse.",
    "places.verificationThanks": "Takk for bekreftelsen!",
    "places.verificationThanksDesc": "Du hjelper oss å holde informasjonen korrekt.",
    "friends.requestAccepted": "Venneforespørsel godtatt!",
    "friends.requestSent": "Venneforespørsel sendt!",
    "myTrips.checklistReminder": "Ikke glem å gå gjennom sjekklisten før avreise",
    "myTrips.today": "I dag",
    "myTrips.tomorrow": "I morgen",
    "myTrips.inDays": "Om {days} dager",
    "myTrips.nextTrip": "Neste tur",
    "myTrips.drafts": "Utkast",
    "myTrips.readyToStart": "Klar til start",
    "myTrips.continuePlanning": "Fortsett planleggingen",
    "myTrips.editTrip": "Rediger",
    "myTrips.openTrip": "Åpne tur",
    "myTrips.viewSummary": "Vis sammendrag",
    "myTrips.duplicate": "Dupliser",
    "myTrips.archive": "Arkiver",
    "myTrips.activeTrips": "Aktiv tur",
    "myTrips.showCompleted": "Vis fullførte turer",
    "myTrips.hideCompleted": "Skjul fullførte turer",
    "tools.title": "Reiseverktøy",
    "tools.checklist": "Sjekkliste",
    "tools.budget": "Budsjett",
    "tools.incident": "Hendelse",
    "tripSummary.distance": "Distanse",
    "tripSummary.driveTime": "Kjøretid",
    "tripSummary.days": "Dager",
    "tripSummary.stops": "Stopp",
    "tripSummary.fuel": "Drivstoff",
    "tripSummary.budget": "Budsjett",
    "tripSummary.route": "Rute",
    "tripSummary.stop": "Stopp {n}",
    "tripGuideLink.title": "Guide for {country}",
    "tripGuideLink.camping": "Camping",
    "tripGuideLink.driving": "Kjøring",
    "tripGuideLink.safety": "Sikkerhet",
    "arrival.continueToNext": "Fortsett til {name}",
    "stats.trips": "Turer",
    "stats.stops": "Stopp",
    "stats.nights": "Netter",
    "stats.countries": "Land",
    "stats.reviews": "Anmeldelser",
    "stats.friends": "Venner",
    "badge.travel": "Reise",
    "badge.exploration": "Utforskning",
    "badge.community": "Fellesskap",
    "badge.milestone": "Milepæler",
    "badge.creative": "Kreativ",
    "badge.engagement": "Engasjement",
    "badge.special": "Spesiell",
    "checklist.title": "Sjekklister",
    "checklist.subtitle": "Tilkoblede og frittstående utfluktslister",
    "checklist.loginRequired": "Logg inn for å se sjekklistene dine",
    "checklist.loginHint": "Dine sjekklister vil vises her etter innlogging.",
    "checklist.backToAll": "Tilbake til alle sjekklister",
    "checklist.trip": "Tur",
    "checklist.general": "Generell pakkeliste",
    "checklist.generalSection": "Uavhengige sjekklister",
    "checklist.generalPacklist": "Generell pakkeliste",
    "checklist.done": "ferdig",
    "checklist.noChecklistYet": "Ingen sjekkliste ennå - trykk for å opprette",
    "checklist.activeTrips": "Aktive og kommende turer",
    "checklist.completedTrips": "Fullførte turer",
    "checklist.noTrips": "Ingen turer ennå. Opprett en tur for å koble til en sjekkliste.",
    "checklist.planTrip": "Planlegg tur",
    "checklist.allDone": "Alt ferdig! 🎉",
    "checklist.remaining": "{count} elementer igjen",
    "checklist.progress": "{done} av {total} ferdig",
    "checklist.addCustom": "Egendefinert element",
    "checklist.addTemplate": "Legg til mal",
    "checklist.reset": "Tilbakestill",
    "checklist.created": "Sjekkliste opprettet! ✅",
    "checklist.createFailed": "Kunne ikke opprette sjekkliste",
    "checklist.resetDone": "Sjekkliste er tilbakestilt",
    "checklist.resetFailed": "Kunne ikke tilbakestille",
    "checklist.itemAdded": "Element lagt til!",
    "checklist.itemAddFailed": "Kunne ikke legge til element",
    "checklist.inputPlaceholder": "Beskriv hva du vil legge til...",
    "checklist.add": "Legg til",
    "checklist.cancel": "Avbryt",
    "checklist.noChecklist": "Ingen sjekkliste for {type}.",
    "checklist.selectTemplate": "Velg mal:",
    "checklist.or": "eller",
    "checklist.aiPacklist": "AI-tilpasset pakkeliste",
    "checklist.standardChecklist": "Standard sjekkliste",
    "checklist.aiAdapted": "AI-liste er tilpasset for {dest}",
    "checklist.andSeason": " og sesong",
    "checklist.aiCreated": "🤖 AI pakkeliste opprettet!",
    "checklist.allExist": "Alle elementer finnes allerede i listen din",
    "checklist.newItemsAdded": "{count} nye elementer lagt til!",
    "checklist.addExtraPacking": "Legg til ekstra pakking:",
    "float.navigate": "Naviger",
    "float.save": "Lagre",
    "float.saving": "Lagrer...",
    "profile.save": "Lagre endringer",
    "profile.saving": "Lagrer...",
    "shared.saveToMyTrips": "Lagre til mine reiser",
    "shared.saving": "Lagrer...",
    "shared.privacyNote": "Start- og sluttpunkter er fjernet for personvern. Legg til ditt eget startpunkt.",
    "shared.addYourStart": "Legg til ditt startpunkt",
    "shared.templateNote": "Denne reisen deles som inspirasjon. Destinasjoner og stopp er inkludert — legg til dine egne start- og sluttpunkter.",
    "trip.editOutbound": "Utgående reise",
    "trip.editReturn": "Returreise",
    "trip.editMainInfo": "Reisedetaljer",
    "vehicle.motorhome": "Bobil",
    "vehicle.campervan": "Campingvogn",
    "vehicle.caravan": "Campingvogn",
    "vehicle.car": "Bil",
    "vehicle.motorcycle": "Motorsykkel",
    "vehicle.selectType": "Kjøretøytype",
    "vehicle.nameLabel": "Kjøretøynavn (valgfritt)",
    "vehicle.namePlaceholder": "f.eks. Fiat Ducato 2020",
    "vehicle.dimensions": "Kjøretøydimensjoner",
    "vehicle.dimensionsExplain": "Brukes for smartere ruteplanlegging — unngå lave broer, trange passasjer og vektbegrensninger.",
    "vehicle.height": "Høyde",
    "vehicle.width": "Bredde",
    "vehicle.length": "Lengde",
    "vehicle.weight": "Vekt",
    "vehicle.disclaimer": "Rutevarsler basert på kjøretøydimensjoner er veiledende. Verifiser alltid restriksjoner på stedet. Dekningen av fullstendige restriksjonsdata utvides kontinuerlig.",
    "vehicle.profileComplete": "Kjøretøyprofil fullført — klar for smart ruteplanlegging",
    "vehicle.save": "Lagre kjøretøyprofil",
    "vehicle.saving": "Lagrer...",
    "vehicle.saved": "Kjøretøyprofil lagret!",
    "vehicle.saveFailed": "Kunne ikke lagre kjøretøyprofil",
    "vehicle.profileTitle": "Kjøretøyprofil",
    "vehicle.profileSubtitle": "For smartere, tryggere ruteplanlegging",
    "myTrips.viewRoute": "Se rute",
    "myTrips.playTrip": "Spill av reise",
    "myTrips.needMoreStops": "Legg til minst 2 stopp med koordinater for å spille av reiseanimasjonen.",
    "tripReplay.stops": "stopp",
    "tripReplay.days": "dager",
    "tripReplay.completed": "Reisen fullført!",
    "tripReplay.replay": "Spill på nytt",
    "tripReplay.share": "Del",
    "routeOverview.title": "Ruteoversikt",
    "routeOverview.totalDistance": "Total distanse",
    "routeOverview.driveTime": "Kjøretid",
    "routeOverview.stops": "Stopp",
    "routeOverview.overnights": "Netter",
    "routeOverview.fuelEstimate": "Estimert drivstofforbruk",
    "routeOverview.itinerary": "Reiserute",
    "routeOverview.nights": "netter",
    "routeOverview.start": "Start",
    "routeOverview.destination": "Destinasjon",
    "routeOverview.visit": "Besøk",
    "games.selectCountry": "Velg land",
    "nav.bottom.budget": "Budsjett",
    "nav.installTitle": "Installer Vantastika",
    "nav.installIOS1": "Trykk på deleknappen (nederst i Safari)",
    "nav.installIOS2": "Velg \"Legg til på startskjermen\"",
    "nav.installIOS3": "Trykk \"Legg til\"",
    "nav.installAndroid1": "Trykk på menyikonet (⋮ øverst til høyre)",
    "nav.installAndroid2": "Velg \"Installer appen\" eller \"Legg til på startskjermen\"",
    "nav.installUnderstood": "Skjønner",
    "tripStatus.startTitle": "Vil du starte reisen nå?",
    "tripStatus.startDesc": "Reisen blir satt som pågående og GPS-sporing aktiveres.",
    "tripStatus.startConfirm": "Ja, start",
    "tripStatus.endTitle": "Vil du virkelig avslutte reisen?",
    "tripStatus.endDesc": "Du kan fortsette den senere hvis du ombestemmer deg.",
    "tripStatus.endConfirm": "Ja, avslutt",
    "tripStatus.pauseTitle": "Vil du sette reisen på pause?",
    "tripStatus.pauseDesc": "Du kan fortsette reisen når som helst.",
    "tripStatus.pauseConfirm": "Ja, pause",
    "tripStatus.resumeTitle": "Vil du gjenoppta reisen?",
    "tripStatus.resumeDesc": "Reisen fortsetter der du pauset.",
    "tripStatus.resumeConfirm": "Ja, fortsett",
    "tripStatus.revertTitle": "Tilbakestill til planlagt reise?",
    "tripStatus.revertDesc": "All reiseinformasjon beholdes — kun statusen endres tilbake til planlagt.",
    "tripStatus.revertConfirm": "Ja, tilbakestill",
    "tripStatus.reactivateTitle": "Gjenoppta avsluttet reise?",
    "tripStatus.reactivateDesc": "Reisen blir satt som pågående igjen. All data beholdes.",
    "tripStatus.reactivateConfirm": "Ja, gjenoppta",
    "tripStatus.trip": "Reise",
    "tripStatus.cancel": "Avbryt",
    "addToTrip.title": "Legg til i reise",
    "addToTrip.loginPrompt": "Logg inn for å legge til steder i reisene dine.",
    "addToTrip.login": "Logg inn",
    "addToTrip.createAccount": "Opprett konto",
    "addToTrip.howUsePlace": "Hvordan vil du bruke dette stedet?",
    "addToTrip.chooseTrip": "Velg reise",
    "addToTrip.startPoint": "Startpunkt",
    "addToTrip.startPointDesc": "Reisen starter her",
    "addToTrip.destination": "Destinasjon",
    "addToTrip.destinationDesc": "Jeg vil reise hit",
    "addToTrip.stop": "Stopp underveis",
    "addToTrip.stopDesc": "Et stopp under reisen",
    "addToTrip.noTrips": "Du har ingen planlagte reiser ennå",
    "addToTrip.noTripsDesc": "Opprett en ny reise og legg til stedet direkte.",
    "addToTrip.stops": "stopp",
    "addToTrip.unnamed": "Unavngitt reise",
    "addToTrip.addedAs": "er lagt til som",
    "addToTrip.roleStart": "startpunkt",
    "addToTrip.roleDestination": "destinasjon",
    "addToTrip.roleStop": "stopp",
    "addToTrip.couldNotAdd": "Kunne ikke legge til stedet",
    "addToTrip.createNew": "Opprett ny reise med dette stedet",
    "optimize.bannerTitle": "AI fant en kortere rute",
    "optimize.bannerDesc": "Spar ca.",
    "optimize.bannerByReordering": "ved å endre rekkefølgen på stoppene dine.",
    "optimize.viewAlternatives": "Vis alternativer",
    "optimize.dismiss": "Ikke nå",
    "optimize.fuelSaved": "i drivstoff",
    "camping.notFound": "Stedet kunne ikke finnes.",
    "camping.approxPrice": "ca.",
    "camping.perNight": "/ natt (omtrentlig)",
    "camping.free": "Gratis",
    "camping.typeStellplatz": "Stellplatz",
    "camping.typeCamping": "Camping",
    "camping.amenities": "Fasiliteter",
    "camping.checkIn": "Innsjekk",
    "camping.checkOut": "Utsjekk",
    "camping.visitWebsite": "Besøk nettside",
    "camping.addToTrip": "Legg til i reise",
    "camping.navigate": "Naviger",
    "camping.viewPage": "Vis side",
    "camping.amenityElectricity": "Strøm",
    "camping.amenityFreshWater": "Ferskvann",
    "camping.amenityGreyWater": "Gråvann",
    "camping.amenityToilet": "Toalett",
    "camping.amenityShower": "Dusj",
    "camping.amenityWC": "WC",
    "camping.amenityPets": "Kjæledyr tillatt",
    "camping.amenityAllYear": "Åpent hele året",
    "camping.amenityFamily": "Familievennlig",
    "camping.amenityLarge": "Store bobiler",
    "camping.amenityWater": "Ved vann",
    "camping.amenitySpa": "Spa",
    "camping.amenityWifi": "WiFi",
    "camping.amenityLaundry": "Vaskeri",
    "camping.amenityRestaurant": "Restaurant",
    "share.inviteToSharedTrip": "Inviter til delt reise",
    "share.inviteDesc": "Mottakeren blir med på samme reise. Alle endringer synkroniseres i sanntid — budsjett, sjekkliste, stopp og notater.",
    "share.inviteViaApp": "Inviter via app...",
    "share.copyInviteLink": "Kopier invitasjonslenke",
    "share.copied": "Kopiert!",
    "share.shareAsTravelTip": "Del som reisetips",
    "share.tipDesc": "Mottakeren får sin egen kopi som inspirasjon. Endringer påvirker ikke din reise.",
    "share.createAiGuide": "Opprett AI-reisefører",
    "share.generatingAi": "Genererer AI-reisefører... ✨",
    "share.aiGuideCreated": "AI-reisefører opprettet! ✨",
    "share.couldNotCreateAi": "Kunne ikke opprette AI-guide",
    "share.aiGuideActive": "AI-reisefører aktiv",
    "share.tabShare": "Del",
    "share.tabTeam": "Reisegruppe",
    "share.tabComments": "Kommentarer",
    "share.tabActivity": "Aktivitet",
    "share.inviteByUsername": "Inviter med brukernavn eller del en invitasjonslenke:",
    "share.usernamePlaceholder": "Brukernavn å invitere...",
    "share.inviteSent": "Invitasjon sendt!",
    "share.userNotFound": "Brukeren ble ikke funnet. Skriv inn nøyaktig brukernavn.",
    "share.cannotInviteSelf": "Du kan ikke invitere deg selv.",
    "share.alreadyInvited": "Brukeren er allerede invitert.",
    "share.couldNotInvite": "Kunne ikke invitere",
    "share.noMembers": "Ingen gruppemedlemmer ennå. Inviter noen for å planlegge sammen!",
    "share.editor": "Redaktør",
    "share.removedFromGroup": "Fjernet fra reisegruppen",
    "share.noComments": "Ingen kommentarer ennå.",
    "share.unknown": "Ukjent",
    "share.commentPlaceholder": "Skriv en kommentar...",
    "share.send": "Send",
    "share.noActivity": "Ingen aktivitet ennå.",
    "share.actionInvited": "inviterte",
    "share.actionCommented": "kommenterte",
    "share.actionAddedStop": "la til stopp:",
    "share.actionUpdatedStop": "oppdaterte stopp:",
    "share.actionRemovedStop": "fjernet stopp:",
    "share.actionUpdatedTrip": "oppdaterte turen",
    "signup.guestVsMember": "Gjest vs Medlem",
    "signup.seeWhatYouGet": "Se hva du får som medlem",
    "signup.freeNoCard": "100% gratis — ingen kredittkort nødvendig",
    "signup.premiumSoon": "Premium kommer snart med enda flere funksjoner",
    "signup.username": "Brukernavn",
    "signup.usernamePlaceholder": "f.eks. vanansen",
    "signup.displayName": "Visningsnavn",
    "signup.displayNamePlaceholder": "Ditt navn vist til andre",
    "signup.confirmPassword": "Bekreft passord",
    "signup.passwordPlaceholder": "Minst 8 tegn, bokstav + tall",
    "signup.passwordsNoMatch": "Passordene stemmer ikke",
    "signup.selectCountry": "Velg land",
    "signup.whatVehicle": "Hva reiser du med?",
    "signup.ageWarning": "Du må være minst 16 år for å opprette en konto på Vantastika. Er du under 18, kreves samtykke fra en verge.",
    "signup.termsConsent": "Jeg godtar",
    "signup.termsLink": "vilkårene for bruk",
    "signup.andThe": "og",
    "signup.integrationTermsLink": "integrasjonsvilkårene",
    "signup.ageConfirm": "og bekrefter at jeg er minst 16 år.",
    "signup.gdprConsent": "Jeg har lest og godtar",
    "signup.privacyLink": "personvernpolicyen (GDPR)",
    "signup.or": "eller",
    "signup.verificationFailed": "Verifisering feilet. Prøv igjen.",
    "signup.captchaFailed": "CAPTCHA-verifisering feilet. Prøv igjen.",
    "signup.tooManyAttempts": "For mange forsøk. Vent et øyeblikk.",
    "signup.acceptTerms": "Du må godta vilkårene",
    "signup.selectCountryError": "Velg land",
    "signup.enterDisplayName": "Skriv inn et visningsnavn",
    "signup.enterUsername": "Skriv inn et brukernavn",
    "signup.accountCreated": "Konto opprettet!",
    "signup.welcomeMsg": "Velkommen til Vantastika!",
    "signup.checkDetails": "Sjekk detaljene dine og prøv igjen.",
    "signup.showPassword": "Vis passord",
    "signup.hidePassword": "Skjul passord",
    "signupVehicle.motorhome": "🚐 Bobil",
    "signupVehicle.motorhomeDesc": "Jeg reiser med bobil",
    "signupVehicle.car": "🚗 Bil",
    "signupVehicle.carDesc": "Jeg reiser med bil",
    "signupVehicle.motorcycle": "🏍️ Motorsykkel",
    "signupVehicle.motorcycleDesc": "Jeg kjører motorsykkel",
    "signupVehicle.campervan": "🚌 Bobil (mindre)",
    "signupVehicle.campervanDesc": "Jeg har en campervan",
    "signupVehicle.rental": "🔑 Leiekjøretøy",
    "signupVehicle.rentalDesc": "Jeg leier ved behov",
    "signupVehicle.other": "🌍 Annet / Usikker",
    "signupVehicle.otherDesc": "Fleksibel reisende",
    "profile.yourProfile": "Din profil",
    "profile.since": "Siden",
    "profile.quickLinks": "Raske lenker",
    "profile.myTrips": "Mine turer",
    "profile.checklists": "Sjekklister",
    "profile.savedPlaces": "Lagrede steder",
    "profile.planning": "Planlegging",
    "profile.saved": "Lagret",
    "vehicle.projects": "Prosjekter",
    "vehicle.viewAll": "Vis alle",
    "vehicle.noProjects": "Ingen prosjekter koblet til {name} ennå.",
    "vehicle.createProject": "Opprett prosjekt",
    "wishlist.startExploring": "Begynn å utforske",
    "profile.notifications": "Varslinger",
    "profile.travelOverview": "Reiseoversikt",
    "profile.levelAndProfile": "Reiseprofil og nivå",
    "profile.progress": "Fremgang",
    "profile.total": "Totalt",
    "profile.badges": "Merker",
    "profile.challenges": "Utfordringer",
    "profile.activeChallenges": "Aktive utfordringer",
    "profile.viewAllProgress": "Se all fremgang",
    "profile.incidentReports": "Hendelsesrapporter",
    "profile.newReport": "Ny rapport",
    "profile.social": "Sosialt",
    "profile.inbox": "Innboks",
    "profile.mySubmissions": "Mine innlegg",
    "profile.accountSettings": "Konto og innstillinger",
    "profile.accountSettingsDesc": "Profil, tema og passord",
    "profile.email": "E-post",
    "profile.displayName": "Visningsnavn",
    "profile.displayNamePlaceholder": "Ditt navn",
    "profile.username": "Brukernavn",
    "profile.usernamePlaceholder": "@brukernavn",
    "profile.country": "Land",
    "profile.countryPlaceholder": "Norge",
    "profile.appearance": "Utseende",
    "profile.themeLight": "Lys",
    "profile.themeDark": "Mørk",
    "profile.themeSystem": "System",
    "profile.themeLightDesc": "Alltid lys tema",
    "profile.themeDarkDesc": "Alltid mørkt tema",
    "profile.themeSystemDesc": "Følger enheten",
    "profile.changePassword": "Endre passord",
    "profile.currentPassword": "Nåværende passord",
    "profile.newPassword": "Nytt passord",
    "profile.newPasswordPlaceholder": "Minst 8 tegn",
    "profile.confirmPasswordLabel": "Bekreft",
    "profile.changePasswordBtn": "Endre passord",
    "profile.changingPassword": "Endrer...",
    "profile.wrongCurrentPassword": "Feil nåværende passord",
    "profile.passwordChanged": "Passord endret!",
    "profile.passwordMinLength": "Passordet må være minst 8 tegn langt",
    "profile.passwordsMismatch": "Passordene stemmer ikke",
    "profile.deleteAccount": "Slett konto",
    "profile.permanentDeletion": "Permanent sletting",
    "profile.deleteAccountDesc": "Når du sletter kontoen din, fjernes følgende permanent: profil, turer, stopp, favoritter, sjekklister, foruminnlegg, anmeldelser, bilder, venneforespørsler, meldinger, hendelsesrapporter og varslinger.",
    "profile.deleteAccountNote": "Hva som kan forbli: anonymiserte revisjonslogger (ikke knyttet til kontoen din), den delte merkevarekatalogen, og alle data vi er juridisk forpliktet til å beholde (f.eks. regnskapslover, maks 7 år).",
    "profile.deleteMyAccount": "Slett min konto",
    "profile.deleteConfirmTitle": "Slett konto permanent",
    "profile.deleteConfirmDesc": "Er du sikker? Følgende slettes permanent:",
    "profile.deleteConfirmList": "• Profil, innstillinger og avatar\n• Turer, stopp, budsjett og sjekklister\n• Favoritter, samlinger og lagrede steder\n• Hendelsesrapporter og opplastede bilder\n• Foruminnlegg, kommentarer og likes\n• Vennskapsforespørsler og meldinger\n• Varslinger og stedsverifiseringer",
    "profile.deleteConfirmNote": "Anonymiserte revisjonslogger og den delte merkevarekatalogen påvirkes ikke. Data vi er juridisk forpliktet til å beholde kan forbli i opptil 7 år.",
    "profile.cannotUndo": "Kan ikke angres.",
    "profile.deleting": "Sletter...",
    "profile.yesDeleteAccount": "Ja, slett kontoen min",
    "profile.accountDeleted": "Konto er slettet",
    "profile.accountDeletedDesc": "Alle dine data er fjernet.",
    "profile.couldNotDelete": "Kunne ikke slette konto",
    "profile.profileSaved": "Profil lagret!",
    "profile.couldNotSave": "Kunne ikke lagre",
    "profile.profilePicUpdated": "Profilbilde oppdatert!",
    "profile.uploading": "Laster opp...",
    "profile.fileTooLarge": "Filen er for stor",
    "profile.fileTooLargeDesc": "Maks 2 MB",
    "profile.imageRejected": "Bilde avvist",
    "profile.myVehicle": "Mitt kjøretøy",
    "profile.enterCurrentPassword": "Skriv inn ditt nåværende passord",
    "profile.couldNotChangePassword": "Kunne ikke endre passord",
    "profile.vehicleMotorhome": "Bobil",
    "profile.vehicleCar": "Bil",
    "profile.vehicleMotorcycle": "MC",
    "profile.vehicleCampervan": "Campervan",
    "profile.vehicleRental": "Leie",
    "profile.vehicleOther": "Annet",
    "profile.activeNow": "Aktivt nå",
    "profile.today": "I dag!",
    "profile.inDays": "Om {n} dager",
    "profile.newNotification": "nytt varsel",
    "profile.newNotifications": "nye varsler",
    "profile.showAll": "Vis alle",
    "profile.inspection": "EU-kontroll",
    "profile.gasInspection": "Gasskontroll",
    "profile.service": "Service",
    "profile.expired": "Utløpt!",
    "profile.noDateRegistered": "Ingen dato registrert",
    "profile.noServiceRegistered": "Ingen service registrert",
    "profile.missing": "Mangler",
    "profile.insurance": "Forsikring",
    "profile.noInsuranceRegistered": "Ingen forsikring registrert",
    "profile.regNumberMissing": "Registreringsnummer mangler",
    "profile.addForSmartData": "Legg til for smart kjøretøydata",
    "profile.addRegNumber": "Legg til regnr",
    "profile.statusGood": "Bra",
    "profile.statusSoon": "Snart",
    "profile.statusAction": "Tiltak",
    "profile.edit": "Rediger",
    "profile.addService": "Legg til service",
    "profile.addVehicleDesc": "Koble kjøretøyet ditt for smart planlegging, påminnelser og tilpasset innhold",
    "profile.addVehicleBtn": "Legg til kjøretøy",
    "profile.validUntil": "Gyldig til {date}",
    "scan.title": "Smart kvitteringsskanning",
    "scan.takePhoto": "Ta bilde",
    "scan.upload": "Last opp",
    "scan.photoOrUpload": "Ta et bilde eller last opp en kvittering",
    "scan.aiAnalyzes": "AI analyserer kvitteringen linje for linje",
    "scan.analyzing": "Analyserer kvittering...",
    "scan.analyzingDesc": "AI leser varer, beløp og kategorier",
    "scan.fileTooLarge": "Filen er for stor",
    "scan.fileTooLargeMax": "Maks 15 MB",
    "scan.analysisFailed": "Analyse feilet",
    "scan.couldNotAnalyze": "Kunne ikke analysere kvitteringen",
    "scan.amount": "Beløp",
    "scan.currency": "Valuta",
    "scan.possibleAmounts": "Mulige beløp:",
    "scan.converted": "Konvertert:",
    "scan.date": "Dato",
    "scan.titleLabel": "Tittel",
    "scan.itemsPerCategory": "Varer per kategori",
    "scan.mixedCategories": "Blandede kategorier",
    "scan.howToSave": "Hvordan vil du lagre?",
    "scan.oneExpense": "Én utgift",
    "scan.splitUp": "Splitt opp",
    "scan.splitDesc": "Oppretter {n} separate utgifter — én per kategori.",
    "scan.mainCategory": "Hovedkategori",
    "scan.linkToBudget": "Knytt til budsjett",
    "scan.noBudgetSelected": "Ingen budsjett valgt",
    "scan.noBudget": "Intet budsjett",
    "scan.linkToTrip": "Knytt til tur",
    "scan.noTripSelected": "Ingen tur valgt",
    "scan.noTrip": "Ingen tur",
    "scan.note": "Notat",
    "scan.hideRawText": "Skjul rå tekst fra kvittering",
    "scan.showRawText": "Vis rå tekst fra kvittering",
    "scan.cancel": "Avbryt",
    "scan.saving": "Lagrer...",
    "scan.saveExpense": "Lagre utgift",
    "scan.saveExpenses": "Lagre {n} utgifter",
    "scan.expenseSaved": "Utgift lagret fra kvittering ✓",
    "scan.expensesSaved": "{n} utgifter lagret fra kvittering ✓",
    "scan.fillAmountAndTitle": "Skriv inn beløp og tittel",
    "scan.couldNotSave": "Kunne ikke lagre",
    "scan.confidenceHigh": "Høy sikkerhet",
    "scan.confidenceMedium": "Middels sikkerhet — gjennomgå feltene",
    "scan.confidenceLow": "Lav sikkerhet — kontroller alle felter",
    "scan.dotMedium": "Middels sikkerhet",
    "scan.dotLow": "Usikkert — kontroller",
    "scan.close": "Lukk",
    "scan.paid": "Betalt:",
    "scan.vat": "MVA:",
    "scan.receipt": "Kvittering",
    // Camera permissions
    "camera.prePermissionHint": "Kameratilgang er nødvendig for å skanne kvitteringer",
    "camera.deniedError": "Kameratilgang ble nektet. Aktiver den i nettleserens eller telefonens innstillinger.",
    "camera.notFoundError": "Ingen kamera funnet på denne enheten.",
    "camera.inUseError": "Kameraet brukes av en annen app.",
    "camera.genericError": "En uventet feil oppstod under tilgang til kameraet.",
    "camera.openSettings": "Vis instruksjoner",
    "camera.androidSettingsHint": "Gå til Innstillinger → Apper → Nettleseren din → Tillatelser → Kamera",
    "camera.iosSettingsHint": "Gå til Innstillinger → Personvern og sikkerhet → Kamera → Nettleseren din",
    "camera.desktopSettingsHint": "Klikk på låsikonet i adressefeltet og aktiver kameratilgang.",
    // Location permissions
    "location.detecting": "Henter posisjonen din…",
    "location.unsupportedTitle": "Posisjonsdeling støttes ikke",
    "location.unsupportedDesc": "Enheten eller nettleseren din støtter ikke posisjonsdeling. Prøv en annen nettleser.",
    "location.deniedTitle": "Posisjonstilgang nektet",
    "location.deniedDesc": "Du har nektet posisjonstilgang. For å bruke denne funksjonen må du aktivere posisjonsdeling i nettleserens eller telefonens innstillinger.",
    "location.tryAgain": "Prøv igjen",
    "location.openSettings": "Vis instruksjoner",
    "location.androidSettingsHint": "Gå til Innstillinger → Apper → Nettleseren din → Tillatelser → Posisjon",
    "location.iosSettingsHint": "Gå til Innstillinger → Personvern og sikkerhet → Stedstjenester → Nettleseren din",
    "location.desktopSettingsHint": "Klikk på låsikonet i adressefeltet og aktiver posisjonstilgang.",
    "location.promptDesc": "Vi trenger posisjonen din for å vise relevante resultater i nærheten.",
    "location.allowAccess": "Tillat posisjonstilgang",
    "location.fetchErrorTitle": "Kunne ikke hente posisjon",
    "location.timeoutDesc": "Det tok for lang tid å hente posisjonen din. Sjekk at GPS er aktivert.",
    "location.unavailableDesc": "Posisjonen din kunne ikke bestemmes akkurat nå. Prøv igjen om en stund.",
    "location.errorTitle": "Posisjonsfeil",
    "location.errorDesc": "Noe gikk galt under henting av posisjonen din.",
    "season.springGreeting": "Våren er her — tid for planlegging!",
    "season.summer": "Sommer",
    "season.summerGreeting": "Sommeren venter — hvilken vei tar du?",
    "season.autumn": "Høst",
    "season.autumnGreeting": "Høstens farger kaller!",
    "season.winter": "Vinter",
    "season.winterGreeting": "Vinteren er magisk — planlegg en snøtur!",
    "map.searchPlaceholder": "Søk by, camping, adresse...",
    "map.searching": "Søker...",
    "map.searchingSuggestions": "Søker etter forslag…",
    "map.noResults": "Ingen treff funnet",
    "map.noResultsHint": "Prøv by, camping, adresse eller stedsnavn",
    "map.exploreEurope": "Utforsk Europa",
    "map.exploreDesc": "Søk etter et sted for å oppdage campingplasser, rasteplasser og severdigheter",
    "map.exploreDescLong": "Søk etter et sted for å oppdage campingplasser, restauranter, severdigheter og mer",
    "map.resultsNear": "Resultater nær",
    "map.noCampingsFound": "Ingen campingplasser eller rasteplasser funnet i nærheten.",
    "map.mapErrorBanner": "Kartet kunne ikke lastes. Du kan fortsatt søke og utforske steder i listevisning.",
    "map.nearYou": "Steder nær deg",
    "map.places": "steder",
    "map.placesNearYou": "steder nær deg",
    "map.campingsNearby": "Campingplasser og rasteplasser i nærheten",
    "map.explore": "Utforsk",
    "map.closePanel": "Lukk panel",
    "map.showResults": "Vis resultater",
    "map.navigate": "Naviger",
    "map.addToTrip": "Legg til i tur",
    "map.open": "Åpent",
    "map.closed": "Stengt",
    "map.free": "Gratis",
    "map.camping": "Camping",
    "map.stellplatz": "Rasteplass",
    "map.dumpStation": "Tømmestasjon",
    "map.navigateHit": "Naviger hit",
    "map.place": "Sted",
    "map.restaurant": "Restaurant",
    "map.supermarket": "Dagligvare",
    "map.attraction": "Severdighet",
    "map.parking": "Parkering",
    "map.fuelStation": "Bensinstasjon",
    "map.museum": "Museum",
    "map.nature": "Natur",
    "map.sights": "Severdigheter",
    "map.grocery": "Dagligvare",
    "map.perNight": "/natt",
    "submit.addressAutoHint": "Søk adressen først — land, region og by fylles inn automatisk.",
    "submit.autoDetected": "Land, region og by har blitt fylt inn automatisk!",
    "submit.placeExists": "Stedet finnes allerede",
    "submit.placeExistsDesc": "Et sted med navnet \"{name}\" er allerede registrert.",
    "submit.nearbyExists": "Sted i nærheten finnes allerede",
    "submit.nearbyExistsDesc": "\"{name}\" er allerede registrert på samme sted.",
    "submit.imageRejected": "Bilde avvist",
    "submit.mainImage": "Hovedbilde",
    "submit.contactInfo": "Kontaktinformasjon",
    "service.heroDesc": "Finn verksteder, drivstoff, vann, sykehus og mer — uansett hvor du er i Europa",
    "service.close": "Lukk",
    "service.clearSearch": "Tøm",
    "service.search": "Søk",
    "service.positionSharing": "Posisjonsdeling",
    "service.positionSharingDesc": "Del GPS-posisjonen din eller aktiver SOS",
    "service.connectionError": "Sjekk tilkoblingen og prøv igjen",
    "service.tryAgain": "Prøv igjen",
    "service.noResultsHint": "Prøv å søke etter noe annet eller bytt kategori",
    "service.resultsNearYou": "nær deg",
    "budget.noPeriod": "Ingen periode",
    "budget.exceededBy": "Overskredet med",
    "budget.expenseDeleted": "Utgift slettet",
    "budget.undo": "Angre",
    "budget.receipt": "Kvittering",
    "budget.trip": "Tur",
    "whatsNew.title": "Hva er nytt",
    "whatsNew.subtitle": "Siste oppdateringer og funksjoner",
    "whatsNew.seoTitle": "Hva er nytt – Vantastika",
    "whatsNew.seoDesc": "Se de siste oppdateringene og nyhetene i Vantastika.",
    "whatsNew.all": "Alle",
    "whatsNew.noUpdates": "Ingen oppdateringer akkurat nå",
    "whatsNew.type.new_feature": "Ny funksjon",
    "whatsNew.type.improvement": "Forbedring",
    "whatsNew.type.bugfix": "Feilretting",
    "whatsNew.type.important": "Viktig",
    "whatsNew.type.maintenance": "Vedlikehold",
    "whatsNew.readMore": "Les mer",
    "search.benefit1": "Se alle campingplasser og plasser",
    "search.benefit2": "Filtrer etter fasiliteter og pris",
    "search.benefit3": "Les anmeldelser og se vurderinger",
    "search.smartPlaceholder": "Søk steder, campinger, bobilplasser, byer...",
    "search.recommendedFor": "Anbefalt for",
    "search.typeCategory": "Kategori",
    "search.typeRegion": "Område",
    "search.typeCountry": "Land",
    "search.typePlace": "Sted",
    "search.noResultsHint": "Prøv et annet søkeord eller fjern noen filtre",
    "search.browseByCountry": "Bla etter land",
    "travelGuides.badge": "Reiseguider",
    "travelGuides.heroTitle1": "Oppdag Europas beste ",
    "travelGuides.heroTitle2": "reiseruter",
    "travelGuides.heroSubtitle": "Detaljerte guider med historie, høydepunkter og praktisk info. Gjør inspirasjon til en ekte reise med ett klikk.",
    "travelGuides.popular": "Populære reisemål",
    "travelGuides.moreCountries": "Flere land",
    "travelGuides.guidesCount": "{n} guider",
    "travelGuides.guidesSingular": "1 guide",
    "travelGuides.explore": "Utforsk",
    "countryGuide.titlePrefix": "Reiseguide",
    "countryGuide.allGuides": "Alle reiseguider",
    "countryGuide.seoDesc": "Komplett reiseguide for bobil i",
    "countryGuide.overview": "Oversikt",
    "countryGuide.campingRules": "Camping & Overnatting",
    "countryGuide.campingRegulations": "Campingregler",
    "countryGuide.wildCamping": "Villcamping",
    "countryGuide.campingTips": "Campingtips",
    "countryGuide.bestSeason": "Beste sesong",
    "countryGuide.drivingInfo": "Kjøring & Veier",
    "countryGuide.trafficRules": "Trafikkregler",
    "countryGuide.speedLimits": "Fartsgrenser",
    "countryGuide.tollsVignettes": "Bompenger & Vignetter",
    "countryGuide.currency": "Valuta",
    "countryGuide.fuel": "Drivstoff",
    "countryGuide.emergency": "Nødnummer",
    "countryGuide.language": "Språk",
    "countryGuide.bestPlaces": "Beste steder å besøke",
    "countryGuide.safetyTips": "Sikkerhetstips",
    "countryGuide.practicalTips": "Praktiske tips",
    "countryGuide.routeGuides": "Ruteguider",
    "countryGuide.guide": "Guide",
    "countryGuide.readGuide": "Les guide",
    "countryGuide.searchCampings": "Søk campinger",
    "countryGuide.showOnMap": "Vis på kart",
    "countryGuide.planTrip": "Planlegg reise",
    "countryGuide.planTripHere": "Planlegg reise hit",
    "countryGuide.campings": "Campinger",
    "guideDetail.days": "dager",
    "guideDetail.history": "Historie & bakgrunn",
    "guideDetail.routeInfo": "Ruteinformasjon",
    "guideDetail.startPoint": "Startpunkt",
    "guideDetail.endPoint": "Sluttpunkt",
    "guideDetail.distance": "Avstand",
    "guideDetail.direction": "Retning",
    "guideDetail.passesThrough": "Passerer gjennom",
    "guideDetail.overview": "Oversikt",
    "guideDetail.highlights": "Høydepunkter",
    "guideDetail.practical": "Praktisk",
    "guideDetail.bestSeason": "Beste sesong",
    "guideDetail.vehicleType": "Kjøretøytype",
    "guideDetail.motorhome": "Bobil",
    "guideDetail.car": "Bil",
    "guideDetail.motorcycle": "Motorsykkel",
    "guideDetail.campervan": "Campervan",
    "guideDetail.warnings": "Ting å tenke på",
    "guideDetail.practicalTips": "Praktiske tips",
    "guideDetail.whyVisit": "Hvorfor besøke: ",
    "guideDetail.readyToGo": "Klar for å dra?",
    "guideDetail.readyToGoDesc": "Gjør denne guiden til en ekte reise i reiseplanleggeren. Tilpass stopp, antall dager og returreise etter dine ønsker.",
    "guideDetail.planThisTrip": "Planlegg denne reisen",
    "guideDetail.refreshGuide": "Oppdater guide",
    "guideDetail.generating": "Lager detaljert reiseguide med AI...",
    "guideDetail.loading": "Laster reiseguide...",
    "guideDetail.premiumBenefit1": "Les hele reiseguiden med stopp og tips",
    "guideDetail.premiumBenefit2": "Se detaljert ruteinformasjon",
    "guideDetail.premiumBenefit3": "Gjør guiden til en ekte reise",
    "destination.motorhome": "Bobil i",
    "destination.planTrip": "Planlegg bobilreisen til",
    "destination.planNow": "Planlegg reisen nå",
    "destination.viewGuides": "Se reiseguider",
    "destination.whatWeOffer": "Hva Vantastika tilbyr",
    "destination.featureRoutes": "Ruteplanlegging",
    "destination.featureRoutesDesc": "Planlegg ruten med stopp, avstander og kjøretider.",
    "destination.featureCamping": "Campingplasser",
    "destination.featureCampingDesc": "Finn de beste campingplassene og bobilplassene.",
    "destination.featureRules": "Kjøreregler",
    "destination.featureRulesDesc": "Fartsgrenser, bompenger og lokale regler.",
    "destination.featureSafety": "Sikkerhet & SOS",
    "destination.featureSafetyDesc": "Nødnumre, veihjelp og sikkerhetstips.",
    "destination.popularRoutes": "Populære ruter i",
    "destination.readyToGo": "Klar for turen?",
    "destination.ctaDesc": "Opprett en gratis konto og begynn å planlegge road trippen din i dag.",
    "destination.getStarted": "Kom i gang gratis",
    "destination.findCampings": "Finn campingplasser",
    "share.shareOnInstagram": "Del på Instagram",
    "share.shareOnFacebook": "Del på Facebook",
    "share.linkCopied": "Lenke kopiert!",
    "share.linkCopiedDesc": "Lim inn hvor du vil.",
    "ig.headline": "Planlegg reisen smartere med Vantastika",
    "ig.subheadline": "Rute, budsjett og reiseplanlegging på ett sted for roadtrips, bobil og bilferie.",
    "ig.ctaStart": "Kom i gang gratis",
    "ig.ctaOpen": "Åpne Vantastika",
    "ig.ctaDemo": "Se hvordan det fungerer",
    "ig.demoTitle": "Så enkelt planlegger du reisen",
    "ig.benefit1.title": "Planlegg hele reiseruten",
    "ig.benefit1.desc": "Lag ruten din med stopp, avstander og tider.",
    "ig.benefit2.title": "Hold oversikt over reisebudsjettet",
    "ig.benefit2.desc": "Se hva reisen koster — før og underveis.",
    "ig.benefit3.title": "Bedre oversikt",
    "ig.benefit3.desc": "Alt samlet på ett sted, før og under reisen.",
    "ig.free": "Gratis. Ingen kredittkort nødvendig.",
    "projects.title": "Prosjekter",
    "projects.myProjects": "Mine prosjekter",
    "projects.subtitle": "Planlegg og følg opp bygg og renoveringer",
    "projects.create": "Nytt prosjekt",
    "projects.createTitle": "Opprett prosjekt",
    "projects.createFirst": "Opprett ditt første prosjekt",
    "projects.creating": "Oppretter...",
    "projects.emptyTitle": "Ingen prosjekter ennå",
    "projects.emptyDesc": "Opprett et prosjekt for å organisere bygg, renoveringer og oppgraderinger med materiallister, oppgaver og budsjett.",
    "projects.name": "Prosjektnavn",
    "projects.namePlaceholder": "f.eks. Batterikonvertering",
    "projects.description": "Beskrivelse",
    "projects.descPlaceholder": "Hva handler prosjektet om?",
    "projects.categoryLabel": "Kategori",
    "projects.priorityLabel": "Prioritet",
    "projects.startDate": "Startdato",
    "projects.endDate": "Sluttdato",
    "projects.budget": "Budsjett",
    "projects.actualCost": "Faktisk kostnad",
    "projects.notes": "Notater",
    "projects.notesPlaceholder": "Legg til notater...",
    "projects.save": "Lagre",
    "projects.edit": "Rediger",
    "projects.cancel": "Avbryt",
    "projects.add": "Legg til",
    "projects.share": "Del",
    "projects.notFound": "Prosjektet ble ikke funnet",
    "projects.backToProjects": "Tilbake til prosjekter",
    "projects.status.not_started": "Ikke startet",
    "projects.status.in_progress": "Pågår",
    "projects.status.completed": "Fullført",
    "projects.priority.low": "Lav",
    "projects.priority.medium": "Middels",
    "projects.priority.high": "Høy",
    "projects.priority.urgent": "Haster",
    "projects.category.battery": "Batteri",
    "projects.category.solar": "Solpanel",
    "projects.category.bathroom": "Bad",
    "projects.category.kitchen": "Kjøkken",
    "projects.category.water_system": "Vannsystem",
    "projects.category.electrical": "Elektrisk",
    "projects.category.interior": "Interiør",
    "projects.category.exterior": "Eksteriør",
    "projects.category.mechanical": "Mekanisk",
    "projects.category.other": "Annet",
    "projects.tab.overview": "Oversikt",
    "projects.tab.materials": "Materialer",
    "projects.tab.tasks": "Oppgaver",
    "projects.tab.files": "Filer",
    "projects.tab.history": "Historikk",
    "projects.tasksCompleted": "Oppgaver fullført",
    "projects.materialsBought": "Materialer kjøpt",
    "projects.budgetOverview": "Budsjettoversikt",
    "projects.materialsCost": "Materialkostnad",
    "projects.spent": "Brukt",
    "projects.remaining": "Gjenstår",
    "projects.total": "Totalt",
    "projects.bought": "Kjøpt",
    "projects.addMaterial": "Legg til materiale",
    "projects.materialName": "Produktnavn",
    "projects.quantity": "Antall",
    "projects.price": "Pris",
    "projects.storeName": "Butikk",
    "projects.storeUrl": "Lenke (URL)",
    "projects.noMaterials": "Ingen materialer lagt til ennå",
    "projects.addTask": "Legg til oppgave",
    "projects.taskTitle": "Oppgavetittel",
    "projects.taskDesc": "Beskrivelse",
    "projects.noTasks": "Ingen oppgaver lagt til ennå",
    "projects.completedAt": "Fullført",
    "projects.status_label": "Status",
    "projects.filter.all": "Alle",
    "projects.filter.bought": "Kjøpt",
    "projects.filter.not_bought": "Ikke kjøpt",
    "projects.filter.pending": "Gjenstår",
    "projects.filter.done": "Fullført",
    "projects.uploadFile": "Last opp fil",
    "projects.uploading": "Laster opp...",
    "projects.maxFileSize": "Maks 15 MB",
    "projects.fileTooLarge": "Filen er for stor (maks 15 MB)",
    "projects.fileUploaded": "Fil lastet opp",
    "projects.uploadError": "Opplasting mislyktes",
    "projects.noFiles": "Ingen filer lagt til ennå",
    "projects.noHistory": "Ingen aktivitet ennå",
    "projects.shareProject": "Del prosjekt",
    "projects.generateLink": "Opprett delingslenke",
    "projects.linkGenerated": "Delingslenke opprettet",
    "projects.exportPdf": "Eksporter som PDF",
    "projects.pdfFull": "Hele prosjektet",
    "projects.pdfMaterials": "Bare materialer",
    "projects.pdfTasks": "Bare oppgaver",
    "projects.pdfSummary": "Sammendrag",
    "projects.deleteProject": "Slett prosjekt",
    "projects.confirmDeleteTitle": "Slette prosjekt?",
    "projects.confirmDeleteDesc": "Dette vil permanent slette prosjektet og alt materiale, oppgaver, filer og historikk. Handlingen kan ikke angres.",
    "projects.deleted": "Prosjekt slettet",
    "projects.vehicleLabel": "Kjøretøy",
    "projects.linkedVehicle": "Koblet kjøretøy",
    "projects.linkVehicle": "Koble kjøretøy",
    "projects.noVehicle": "Ingen kjøretøy koblet",
    "projects.selectVehicle": "Velg kjøretøy",
    "projects.noVehicleOption": "Ingen",
    "projects.duplicate": "Dupliser",
    "projects.editMaterial": "Rediger materiale",
    "projects.editTask": "Rediger oppgave",
    "projects.saveMaterial": "Lagre materiale",
    "projects.saveTask": "Lagre oppgave",
    "projects.swipeToDelete": "Sveip for å slette",
    "projects.moreActions": "Flere handlinger",
    "projects.editProject": "Rediger prosjekt",
    "upload.validating": "Validerer...",
    "upload.processing": "Behandler bilde...",
    "upload.converting": "Konverterer HEIC...",
    "upload.resizing": "Endrer størrelse...",
    "upload.compressing": "Komprimerer...",
    "upload.thumbnailing": "Lager miniatyrbilde...",
    "upload.almostDone": "Nesten ferdig...",
    "upload.uploading": "Laster opp...",
    "upload.done": "Ferdig!",
    "upload.failed": "Opplasting mislyktes",
    "upload.fileTooLarge": "Filen er for stor",
    "upload.imageRejected": "Bilde avvist",
  },
  de: {
    "offline.message": "Du bist offline — gespeicherte Daten werden aus dem Cache angezeigt",
    "offline.syncing": "Daten werden synchronisiert...",
    "offline.synced": "Alles synchronisiert!",
    "confirm.deleteTitle": "Bist du sicher?",
    "confirm.deleteDesc": "Diese Aktion kann nicht rückgängig gemacht werden.",
    "confirm.cancel": "Abbrechen",
    "confirm.delete": "Löschen",
    "confirm.deleteTrip": "Diese Reise löschen?",
    "confirm.deleteTripDesc": "Die Reise und alle Daten werden dauerhaft gelöscht.",
    "confirm.deletePost": "Diesen Beitrag löschen?",
    "confirm.deletePostDesc": "Der Beitrag wird dauerhaft entfernt.",
    "confirm.deleteBudget": "Dieses Budget löschen?",
    "confirm.deleteBudgetDesc": "Das Budget und alle Ausgaben werden dauerhaft gelöscht.",
    "confirm.deleteNotification": "Diese Benachrichtigung löschen?",
    "confirm.deleteNotificationDesc": "Die Benachrichtigung wird dauerhaft entfernt.",
    "confirm.deleteMaterial": "Dieses Material löschen?",
    "confirm.deleteMaterialDesc": "Das Material wird dauerhaft aus dem Projekt entfernt.",
    "confirm.deleteTask": "Diese Aufgabe löschen?",
    "confirm.deleteTaskDesc": "Die Aufgabe wird dauerhaft aus dem Projekt entfernt.",
    "confirm.deleteFile": "Diese Datei löschen?",
    "confirm.deleteFileDesc": "Die Datei wird dauerhaft entfernt.",
    "confirm.deleteMessage": "Diese Nachricht löschen?",
    "confirm.deleteMessageDesc": "Die Nachricht wird dauerhaft entfernt.",
    "confirm.deleteUpdate": "Dieses Update löschen?",
    "confirm.deleteUpdateDesc": "Das Update wird dauerhaft entfernt.",
    "confirm.deleteCamping": "Diesen Campingplatz dauerhaft löschen?",
    "confirm.deleteCampingDesc": "Der Campingplatz wird dauerhaft gelöscht.",
    "confirm.unpublishTrip": "Diese Reise entveröffentlichen?",
    "confirm.unpublishTripDesc": "Die Reise wird für andere nicht mehr sichtbar sein.",
    "confirm.endTrip": "Diese Reise beenden?",
    "confirm.endTripDesc": "Die Reise wird als abgeschlossen markiert.",
    "nav.explore": "Campingplätze",
    "nav.map": "Entdecken",
    "nav.trips": "Reiseplaner",
    "nav.favorites": "Favoriten",
    "nav.login": "Anmelden",
    "nav.signup": "Registrieren",
    "nav.logout": "Abmelden",
    "nav.bottom.trips": "Neue Reise",
    "nav.bottom.explore": "Camping",
    "nav.bottom.map": "Karte",
    "nav.bottom.saved": "Gespeichert",
    "nav.bottom.profile": "Profil",
    "nav.bottom.login": "Anmelden",
    "nav.bottom.signup": "Registrieren",
    "nav.bottom.home": "Start",
    "nav.bottom.newTrip": "Neue Reise",
    "nav.bottom.myTrips": "Meine Reisen",
    "nav.checklists": "Checklisten",
    "nav.travelGuides": "Reiseführer",
    "nav.menuMine": "Meine",
    "nav.menuCreate": "Erstellen",
    "nav.menuApp": "App",
    "nav.menuOther": "Sonstiges",
    "nav.installApp": "App installieren",
    "nav.settings": "Einstellungen",
    "myTrips.showTrip": "Reise anzeigen",
    "myTrips.liveView": "Live-Ansicht",
    "myTrips.details": "Details",
    "myTrips.revertToPlanned": "Auf geplant zurücksetzen",
    "myTrips.reactivateTrip": "Reise fortsetzen",
    "myTrips.loadingLive": "Live-Ansicht wird geladen...",
    "footer.integrationTerms": "Integrationsbedingungen",
    "dash.greeting.morning": "Guten Morgen",
    "dash.greeting.day": "Hallo",
    "dash.greeting.afternoon": "Guten Nachmittag",
    "dash.greeting.evening": "Guten Abend",
    "dash.greeting.night": "Gute Nacht",
    "dash.subtitle.active": "Deine Reise läuft – hier ist dein Status",
    "dash.subtitle.planning": "Du hast Reisen vorzubereiten",
    "dash.subtitle.completed": "Gut gemacht! Hier ist deine Reiseübersicht",
    "dash.subtitle.empty": "Was möchtest du heute tun?",
    "dash.paused": "Pausiert",
    "dash.ongoing": "Laufende Reise",
    "dash.total": "Gesamt",
    "dash.stops": "Stopps",
    "dash.done": "Fertig",
    "dash.nextDest": "Nächstes Ziel",
    "dash.continueTrip": "Reise fortsetzen",
    "dash.openActive": "Aktive Reise öffnen",
    "dash.journal": "Journal",
    "dash.budget": "Budget",
    "dash.findPlace": "Platz finden",
    "dash.upcoming": "Bevorstehende Reise",
    "dash.today": "Heute!",
    "dash.tomorrow": "Morgen",
    "dash.daysLeft": "{n} Tage übrig",
    "dash.continuePlan": "Weiter planen",
    "dash.start": "Starten",
    "dash.morePlanned": "Weitere geplante Reisen",
    "dash.findCamping": "Camping finden",
    "dash.findCampingSub": "2.800+ Plätze",
    "dash.guides": "Reiseführer",
    "dash.guidesSub": "21+ Länder",
    "dash.checklists": "Checklisten",
    "dash.checklistsSub": "Klug packen",
    "dash.wishlist": "Wunschliste",
    "dash.vehicle": "Fahrzeug",
    "dash.projects": "Projekte",
    "dash.community": "Community",
    "dash.popularDest": "Beliebte Reiseziele",
    "dash.seeAll": "Alle anzeigen",
    "dash.planNew": "Neue Reise planen",
    "dash.planNewSub": "Schritt-für-Schritt Assistent",
    "dash.season.spring": "Frühlingsabenteuer",
    "dash.season.summer": "Sommer unterwegs",
    "dash.season.autumn": "Herbst-Roadtrips",
    "dash.season.winter": "Winterabenteuer",
    "dash.tripDone": "Reise abgeschlossen",
    "dash.visited": "Besucht",
    "dash.seeSummary": "Zusammenfassung",
    "dash.planNewOld": "Neue Reise planen",
    "dash.emptyTitle": "Zeit, deine erste Reise zu planen!",
    "dash.emptyDesc": "Erstelle eine Reise und füge Stopps, Budget und Checklisten hinzu.",
    "dash.createTrip": "Reise erstellen",
    "dash.stopsCount": "{count} Stopps",
    "dash.visitedCount": "{count} besucht",
    "dash.onboarding.step1": "Reise planen",
    "dash.onboarding.step1Desc": "Erstelle deinen ersten Roadtrip mit Stopps und Daten",
    "dash.onboarding.step2": "Traumziele speichern",
    "dash.onboarding.step2Desc": "Baue deine Wunschliste mit Orten zum Besuchen",
    "dash.onboarding.step3": "Campingplätze entdecken",
    "dash.onboarding.step3Desc": "Durchsuche 2.800+ Campingplätze in Europa",
    "dash.onboarding.stepVehicle": "Fahrzeug einrichten",
    "dash.onboarding.stepVehicleDesc": "Füge dein Wohnmobil, Auto oder Campingbus hinzu",
    "dash.onboarding.stepProject": "Projekt starten",
    "dash.onboarding.stepProjectDesc": "Verfolge Renovierungen, Upgrades und Umbauten",
    "dash.emptyDescReturning": "Plane deine erste Reise – deine gespeicherten Daten sind bereit!",
    "dash.recentProjects": "Deine Projekte",
    "dash.popularGuides": "Beliebte Reiseführer",
    "dash.noProjects": "Noch keine Projekte",
    "dash.draft": "Entwurf",
    "dash.continueDraft": "Weiter planen",
    "dash.untitled": "Unbenannte Reise",
    "dash.nextStep": "Nächster Schritt",
    "dash.nextStepStops": "Füge deinen ersten Stopp hinzu",
    "dash.nextStepDate": "Wähle ein Reisedatum",
    "dash.nextStepChecklist": "Checkliste abschließen",
    "dash.prepTitle": "Bereite deine Reise vor",
    "dash.prepBudget": "Lege ein Budget fest, um den Überblick zu behalten",
    "dash.prepStops": "Füge weitere Stopps zur Route hinzu",
    "dash.prepChecklist": "Überprüfe deine Packliste",
    "dash.packed": "gepackt",
    "dash.incompleteTitle": "Planung abschließen",
    "dash.incompleteDate": "Füge ein Abreisedatum hinzu",
    "dash.incompleteStops": "Füge Stopps zu deiner Route hinzu",
    "dash.postTripTitle": "Was kommt als nächstes?",
    "dash.postTripShare": "Teile deine Reise mit Freunden",
    "dash.postTripReview": "Bewerte die Orte, die du besucht hast",
    "dash.postTripJournal": "Schreibe über deine Erlebnisse",
    "dash.postTripMsg": "Du bist {km} km gereist und hast {stops} Orte besucht — tolles Abenteuer!",
    "nav.myTrips": "Meine Reisen",
    "nav.submit": "Platz einreichen",
    "nav.help": "So nutzt du die App",
    "help.title": "So nutzt du die App",
    "help.whatIs.title": "Was ist Vantastika?",
    "help.whatIs.body": "Dein Reisebegleiter für Wohnmobil-, Campervan- und Autoreisen in Europa. Finde Campingplätze, plane Routen und behalte dein Budget im Blick — alles an einem Ort.",
    "help.startTrip.title": "Reise starten",
    "help.startTrip.body": "Tippe auf Reiseplaner, gib deiner Reise einen Namen, füge Stopps und Daten hinzu. Die App berechnet Entfernungen und Kraftstoffkosten für dich.",
    "help.search.title": "Orte suchen",
    "help.search.body": "Nutze Entdecken oder die Suchseite, um Campingplätze und Stellplätze in ganz Europa zu finden.",
    "help.map.title": "Karte verwenden",
    "help.map.body": "Öffne Entdecken, um die Karte zu sehen. Suche nach einer Stadt oder nutze deinen Standort. Tippe auf Markierungen für Details und Wegbeschreibungen.",
    "help.addStop.title": "Stopp hinzufügen",
    "help.addStop.body": "Tippe bei einem Campingplatz auf 'Zur Reise hinzufügen', um ihn in deine Route aufzunehmen.",
    "help.share.title": "Teilen & inspirieren",
    "help.share.body": "Mache deine Reise öffentlich, damit andere deine Route sehen können. Durchstöbere geteilte Reisen der Community.",
    "help.favorites.title": "Favoriten speichern",
    "help.favorites.body": "Tippe auf das Herz bei einem Campingplatz, um ihn zu speichern. Organisiere Favoriten in Sammlungen.",
    "hero.title1": "Plane deine gesamte Reise",
    "hero.title2": "an einem Ort.",
    "hero.subtitle": "Verifizierte Plätze, smarte Routen, Budgetkontrolle, Checklisten und KI-Unterstützung.",
    "hero.searchPlaceholder": "Wohin möchtest du fahren?",
    "hero.search": "Suchen",
    "hero.ctaPlan": "Reise planen",
    "hero.ctaExplore": "Campingplätze finden",
    "hero.ctaSubmit": "Platz vorschlagen",
    "features.find": "Campingplätze finden",
    "features.findDesc": "Echte Plätze mit Preisen, Bewertungen und allem was du wissen musst",
    "features.save": "Favoriten speichern",
    "features.saveDesc": "Behalte deine besten Plätze im Blick",
    "features.plan": "Reisen planen",
    "features.planDesc": "Routen, Stopps und Kraftstoffkosten. Fertig in Sekunden",
    "features.europe": "Ganz Europa",
    "features.europeDesc": "Tausende Plätze denen du vertrauen kannst, in ganz Europa",
    "featured.title": "Beliebte Campingplätze",
    "featured.viewAll": "Alle anzeigen →",
    "search.title": "Campingplätze Entdecken",
    "search.placeholder": "Campingplätze suchen...",
    "search.country": "Land",
    "search.allCountries": "Alle Länder",
    "search.filters": "Filter",
    "search.clearAll": "Alle löschen",
    "search.minPrice": "Min Preis",
    "search.maxPrice": "Max Preis",
    "search.region": "Region",
    "search.regionPlaceholder": "z.B. Bayern",
    "search.noResults": "Keine Campingplätze gefunden",
    "search.adjustFilters": "Versuche die Filter anzupassen",
    "search.found": "Campingplätze gefunden",
    "filter.electricity": "Stromanschluss",
    "filter.freshWater": "Frischwasser",
    "filter.greyWater": "Grauwasserentsorgung",
    "filter.toilet": "Toilettenkassette",
    "filter.shower": "Dusche",
    "filter.wc": "WC",
    "filter.petFriendly": "Haustierfreundlich",
    "filter.openAllYear": "Ganzjährig geöffnet",
    "filter.familyFriendly": "Familienfreundlich",
    "filter.largeMotorhomes": "Große Wohnmobile",
    "filter.nearWater": "Am See/Strand",
    "filter.spaWellness": "Spa/Wellness",
    "filter.wifi": "WLAN",
    "filter.laundry": "Waschmaschine",
    "filter.restaurant": "Restaurant",
    "detail.about": "Über",
    "detail.amenities": "Ausstattung",
    "detail.contact": "Kontakt",
    "detail.website": "Webseite",
    "detail.checkIn": "Check-in",
    "detail.checkOut": "Check-out",
    "detail.location": "Standort",
    "detail.openInMaps": "In Google Maps öffnen →",
    "detail.mapComingSoon": "Karte kommt bald",
    "detail.back": "Zurück",
    "detail.save": "Speichern",
    "detail.saved": "Gespeichert",
    "detail.perNight": "pro Nacht",
    "detail.reviews": "Bewertungen",
    "detail.writeReview": "Bewertung schreiben",
    "detail.noReviews": "Noch keine Bewertungen",
    "detail.submitReview": "Bewertung senden",
    "detail.yourRating": "Deine Bewertung",
    "detail.yourComment": "Dein Kommentar (optional)",
    "detail.loginToReview": "Anmelden um zu bewerten",
    "detail.notFound": "Campingplatz nicht gefunden",
    "detail.backToSearch": "Zurück zur Suche",
    "favorites.title": "Meine Favoriten",
    "favorites.saveYour": "Speichere deine Lieblingscampingplätze",
    "favorites.signUpPrompt": "Registriere dich oder melde dich an.",
    "favorites.empty": "Noch keine Favoriten",
    "favorites.emptyDesc": "Erkunde und speichere Campingplätze die dir gefallen",
    "favorites.explore": "Campingplätze Entdecken",
    "favorites.emptyCollection": "Keine Favoriten in dieser Sammlung",
    "wishlist.title": "Wunschliste",
    "wishlist.save": "Auf Wunschliste speichern",
    "wishlist.saved": "Auf Wunschliste gespeichert!",
    "wishlist.alreadySaved": "Bereits auf der Wunschliste",
    // SaveGateSheet
    "saveGate.wishlist.savedFor": "Wir haben den Ort gespeichert:",
    "saveGate.trip.savedFor": "Wir haben deine Reise gespeichert:",
    "saveGate.place.savedFor": "Wir haben gespeichert:",
    "saveGate.holdsFor24h": "Wir bewahren es 24 Stunden auf — erstelle ein Konto, damit es nie verloren geht.",
    "saveGate.wishlist.benefit1": "Synchronisiere zwischen Handy & Computer",
    "saveGate.wishlist.benefit2": "Benachrichtigung bei Preissenkungen",
    "saveGate.wishlist.benefit3": "Mit Reisepartner teilen",
    "saveGate.trip.benefit1": "Offline-Modus für unterwegs",
    "saveGate.trip.benefit2": "Smarte Navigation, an dein Fahrzeug angepasst",
    "saveGate.trip.benefit3": "Live-Standort mit der Familie teilen",
    "saveGate.place.benefit1": "Speichere deinen Ort dauerhaft",
    "saveGate.place.benefit2": "Erhalte Tipps zu Orten in der Nähe",
    "saveGate.place.benefit3": "Auf allen Geräten verfügbar",
    "saveGate.socialProof": "Reisende in Nordeuropa nutzen Vantastika",
    "saveGate.createAccount15s": "Konto in 15 Sek. erstellen",
    "saveGate.alreadyMember": "Schon Mitglied?",
    "saveGate.logIn": "Anmelden",
    "saveGate.continueGuest": "Als Gast fortfahren — 24 Std. gespeichert",
    "saveGate.noCardNeeded": "Kostenlos. Keine Karte nötig.",
    // GuestRescueSheet
    "guestRescue.title": "Du hast {count} ungesicherte Einträge",
    "guestRescue.expiresIn": "Läuft ab in {time}",
    "guestRescue.expired": "Abgelaufen — Konto erstellen zum Wiederherstellen",
    "guestRescue.trip": "Reise",
    "guestRescue.wishlist": "Wunschliste",
    "guestRescue.rescueCta": "Einträge retten — Konto erstellen",
    "guestRescue.footnote": "Für immer kostenlos. Keine Kreditkarte.",
    "saveGate.hoursShort": "Std.",
    "saveGate.minutesShort": "Min.",
    "guestRescue.toastTitle": "Tipp: Konto erstellen",
    "guestRescue.toastBody": "Speichere auf allen Geräten und verliere nie eine Reise.",
    "guestRescue.toastCta": "Jetzt erstellen",
    "nav.bottom.guestSaves": "{count} gespeichert",
    "wishlist.loginMsg": "Melden Sie sich an, um Orte zu speichern.",
    "wishlist.empty": "Ihre Wunschliste ist leer",
    "wishlist.emptyDesc": "Speichern Sie Traumorte und planen Sie später Reisen daraus.",
    "wishlist.add": "Ort hinzufügen",
    "wishlist.searchPlace": "Nach einem Ort suchen",
    "wishlist.searchPlaceholder": "Stadt, Land, Sehenswürdigkeit, Camping...",
    "wishlist.myNote": "Meine Notiz",
    "wishlist.notePlaceholder": "Notiz zu diesem Ort schreiben...",
    "wishlist.addNote": "Tippen um Notiz hinzuzufügen...",
    "wishlist.saveNote": "Speichern",
    "wishlist.nearby": "In der Nähe",
    "wishlist.nearbyAttractions": "Sehenswürdigkeiten",
    "wishlist.nearbyAccommodation": "Unterkünfte",
    "wishlist.nearbyRestaurants": "Restaurants",
    "wishlist.noNearby": "Noch keine nahegelegenen Orte in unserer Datenbank.",
    "wishlist.planTrip": "Reise hierhin planen",
    "wishlist.planMultiple": "Reise planen",
    "wishlist.deleteTitle": "Von der Wunschliste entfernen?",
    "wishlist.deleteDesc": "Der Ort wird von Ihrer Wunschliste entfernt.",
    "wishlist.selectedCount": "ausgewählt",
    "wishlist.pickFromWishlist": "Aus Wunschliste wählen",
    "wishlist.loading": "Laden...",
    "wishlist.emptyPicker": "Wunschliste ist leer. Speichern Sie zuerst Orte!",
    "wishlist.addSelected": "Ausgewählte hinzufügen",
    "wishlist.fromWishlist": "Aus Wunschliste",
    "wishlist.planWithPlaces": "Reise mit ausgewählten Orten planen",
    "wishlist.planWithPlacesDesc": "Reihenfolge wählen und jedem Ort eine Rolle zuweisen.",
    "wishlist.orderMode": "Routenreihenfolge",
    "wishlist.myOrder": "Meine Reihenfolge beibehalten",
    "wishlist.myOrderDesc": "Genau die gewählte Reihenfolge verwenden",
    "wishlist.smartOrder": "Route optimieren",
    "wishlist.smartOrderDesc": "Smarteste Reihenfolge mit weniger Umwegen finden",
    "wishlist.assignRoles": "Welche Rolle hat jeder Ort?",
    "wishlist.role_destination": "Hauptziel",
    "wishlist.role_stop": "Zwischenstopp",
    "wishlist.role_attraction": "Sehenswürdigkeit",
    "wishlist.role_overnight": "Übernachtung",
    "wishlist.startPlanning": "Planung starten",
    "wishlist.places": "Orte",
    "notifications.emptyTitle": "Noch keine Benachrichtigungen",
    "notifications.emptyDesc": "Wenn etwas passiert, sehen Sie es hier",
    "notifications.title": "Benachrichtigungen",
    "notifications.unreadCount": "ungelesen",
    "notifications.noUnread": "Keine ungelesenen Benachrichtigungen",
    "notifications.markAll": "Alle markieren",
    "notifications.markRead": "Als gelesen markieren",
    "notifications.show": "Anzeigen",
    "notifications.delete": "Löschen",
    "notifications.goToInbox": "Zum Posteingang & Nachrichten",
    "notifications.allRead": "Alle als gelesen markiert",
    "trips.title": "Meine Reisen",
    "trips.plan": "Plane Deine Reisen",
    "trips.loginPrompt": "Melde dich an um Campingreisen zu erstellen.",
    "trips.newTrip": "Neue Reise",
    "trips.createTrip": "Neue Reise Erstellen",
    "trips.tripTitle": "Reisetitel",
    "trips.startDate": "Startdatum",
    "trips.endDate": "Enddatum",
    "trips.notes": "Notizen für die Reise...",
    "trips.create": "Reise Erstellen",
    "trips.empty": "Noch keine Reisen",
    "trips.emptyDesc": "Erstelle deine erste Reise",
    "trips.stops": "Stopps",
    "trips.noStops": "Noch keine Stopps hinzugefügt.",
    "trips.created": "Reise erstellt!",
    "trips.failed": "Reise konnte nicht erstellt werden",
    "trips.partialSave": "Reise teilweise gespeichert",
    "trips.partialDesc": "Einige Teile konnten nicht gespeichert werden. Gehe zu Meine Reisen zum Bearbeiten.",
    "trips.unknownPlace": "Unbekannter Ort",
    "trips.ideaLoaded": "Deine Reiseidee wurde in den Planer unten geladen.",
    "trips.newInspiration": "Neue Inspiration",
    "trips.requireAuthSave": "Du hast Zeit in die Planung dieser Reise investiert. Erstelle ein Konto und alles wird automatisch gespeichert.",
    "trips.daysAbbrev": "Tage",
    "trips.perDay": "/Tag",
    "trips.socialProof": "Reisende planen hier",
    "trips.freeLabel": "100% kostenlos",
    "trips.ideaCreating": "Deine Reiseidee wird erstellt...",
    "trips.ideaBuilding": "Wir erstellen einen kompletten Reiseplan für {destination} mit Stopps, Sehenswürdigkeiten und Campingplätzen.",
    "trips.ideaTitle": "Reiseidee: {title}",
    "trips.campingsNearby": "Campingplätze & Stellplätze in der Nähe",
    "trips.restoreWelcome": "Willkommen zurück!",
    "trips.restoreSaving": "Wir speichern deine Reise \"{title}\" automatisch...",
    "trips.restoreFailed": "Reise konnte nicht wiederhergestellt werden",
    "trips.restoreFailedDesc": "Die gespeicherten Reisedaten waren unvollständig. Plane eine neue Reise.",
    "trips.restoreErrorDesc": "Etwas ist schiefgelaufen. Plane eine neue Reise.",
    "trips.partialStopsFailed": "Reise gespeichert, aber einige Stopps fehlgeschlagen",
    "trips.partialStopsDesc": "Gehe zu Meine Reisen und bearbeite die Reise, um fehlende Stopps hinzuzufügen.",
    "contact.sendFailed": "Konnte nicht gesendet werden",
    "contact.tryAgain": "Versuche es später erneut.",
    "contact.sent": "Nachricht gesendet!",
    "contact.writeMessage": "Schreibe eine Nachricht",
    "contact.rateLimited": "Warte einen Moment, bevor du erneut sendest",
    "weather.fetchError": "Wetterdaten konnten nicht abgerufen werden. Versuche es später erneut.",
    "cost.fetchFailed": "Kostenschätzung konnte nicht abgerufen werden",
    "cost.adults": "Erwachsene",
    "cost.children": "Kinder",
    "cost.person": "Person",
    "cost.persons": "Personen",
    "wizard.nextStepDesc": "Was möchtest du als nächstes tun?",
    "wizard.addBudgetCta": "Budget für die Reise festlegen",
    "wizard.addMoreStopsCta": "Weitere Stopps & Campingplätze hinzufügen",
    "wizard.viewMyTrips": "Meine Reisen ansehen",
    "myTrips.chatUnavailable": "Reisechat ist nicht verfügbar",
    "myTrips.chatClosed": "Der Chat wird geschlossen, wenn die Reise abgeschlossen ist.",
    "myTrips.chatOnlyShared": "Chat ist nur in geteilten Reisen mit Reisebegleitern verfügbar.",
    "contact.subject": "Betreff",
    "contact.subjectPlaceholder": "Beschreibe kurz, worum es geht",
    "contact.message": "Nachricht",
    "contact.messagePlaceholder": "Erzähle uns mehr im Detail...",
    "contact.creating": "Erstelle...",
    "contact.sendTicket": "Anfrage senden",
    "contact.allTickets": "Alle Anfragen",
    "contact.waitingReply": "Vantastika wartet auf deine Antwort",
    "contact.resolvedLabel": "Gelöst",
    "contact.closedNotice": "Diese Anfrage ist geschlossen. Erstelle eine neue Anfrage, wenn du weitere Hilfe benötigst.",
    "contact.you": "Du",
    "contact.replyPlaceholder": "Schreibe deine Antwort...",
    "contact.sending": "Sende...",
    "contact.reply": "Antworten",
    "places.yourPosition": "Deine Position",
    "places.unnamedPlace": "Unbenannter Ort",
    "places.myPosition": "Meine Position",
    "map.listView": "Listenansicht",
    "map.mapView": "Kartenansicht",
    "shared.joinedTrip": "Du bist der Reise beigetreten!",
    "shared.joinedDesc": "Du kannst die Reise jetzt zusammen mit der Reisegruppe sehen und bearbeiten.",
    "shared.couldNotJoin": "Konnte nicht beitreten",
    "shared.tryAgainLater": "Versuche es später erneut.",
    "shared.searchStartPlace": "Suche deinen Startort...",
    "shared.savingCopy": "Speichere...",
    "shared.saveAsCopy": "Als eigene Kopie speichern",
    "camping.blurBenefitDesc": "Vollständige Beschreibung und Ausstattung ansehen",
    "camping.blurBenefitPrice": "Preise und Preisverlauf anzeigen",
    "camping.blurBenefitReviews": "Bewertungen anderer Reisender lesen",
    "camping.blurBenefitMap": "Karte öffnen und direkt navigieren",
    "login.title": "Willkommen zurück",
    "login.subtitle": "Melde dich bei Vantastika an",
    "login.email": "E-Mail",
    "login.password": "Passwort",
    "login.submit": "Anmelden",
    "login.loading": "Anmeldung...",
    "login.failed": "Anmeldung fehlgeschlagen",
    "login.noAccount": "Kein Konto?",
    "login.signUp": "Registrieren",
    "login.forgotPassword": "Passwort vergessen?",
    "login.or": "ODER",
    "login.verificationFailed": "Überprüfung fehlgeschlagen. Versuchen Sie es erneut.",
    "login.captchaFailed": "CAPTCHA-Überprüfung fehlgeschlagen. Versuchen Sie es erneut.",
    "login.tooManyAttempts": "Zu viele Anmeldeversuche. Bitte warten.",
    "login.wrongCredentials": "Falsche E-Mail oder falsches Passwort.",
    "social.continueGoogle": "Weiter mit Google",
    "social.continueApple": "Weiter mit Apple",
    "social.loginFailed": "Anmeldung fehlgeschlagen",
    "social.unknownError": "Unbekannter Fehler",
    "forgot.title": "Passwort vergessen",
    "forgot.checkInbox": "Prüfe deinen Posteingang!",
    "forgot.enterEmail": "Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.",
    "forgot.sentTo": "Wir haben eine E-Mail an",
    "forgot.checkSpam": "mit einem Link zum Zurücksetzen deines Passworts gesendet. Prüfe den Spam-Ordner, falls du sie nicht findest.",
    "forgot.backToLogin": "Zurück zur Anmeldung",
    "forgot.emailLabel": "E-Mail-Adresse",
    "forgot.emailPlaceholder": "deine@email.de",
    "forgot.sending": "Wird gesendet...",
    "forgot.sendLink": "Link zum Zurücksetzen senden",
    "forgot.couldNotSend": "E-Mail konnte nicht gesendet werden",
    "forgot.enterEmailFirst": "Gib deine E-Mail-Adresse ein",
    "reset.title": "Neues Passwort",
    "reset.subtitle": "Wähle ein neues Passwort für dein Konto.",
    "reset.newPassword": "Neues Passwort",
    "reset.confirmPassword": "Passwort bestätigen",
    "reset.placeholder": "Mindestens 8 Zeichen, Buchstabe + Zahl",
    "reset.tooShort": "Das Passwort muss mindestens 8 Zeichen lang sein",
    "reset.noMatch": "Passwörter stimmen nicht überein",
    "reset.updating": "Wird aktualisiert...",
    "reset.update": "Passwort aktualisieren",
    "reset.couldNotUpdate": "Passwort konnte nicht aktualisiert werden",
    "reset.success": "Passwort aktualisiert!",
    "reset.successDesc": "Dein Passwort wurde erfolgreich geändert.",
    "reset.goToProfile": "Zu meinem Profil",
    "reset.invalidLink": "Ungültiger Link",
    "reset.invalidLinkDesc": "Der Link zum Zurücksetzen ist ungültig oder abgelaufen. Fordere einen neuen von der Anmeldeseite an.",
    "signup.title": "Konto erstellen",
    "signup.subtitle": "Campingplätze speichern und Reisen planen",
    "signup.name": "Anzeigename",
    "signup.email": "E-Mail",
    "signup.password": "Passwort",
    "signup.gdpr": "Ich stimme der Datenschutzerklärung zu und willige in die Verarbeitung meiner personenbezogenen Daten gemäß DSGVO ein.",
    "signup.submit": "Registrieren",
    "signup.loading": "Konto wird erstellt...",
    "signup.failed": "Registrierung fehlgeschlagen",
    "signup.success": "Konto erstellt!",
    "signup.checkEmail": "Überprüfe deine E-Mail zur Bestätigung.",
    "signup.acceptPrivacy": "Bitte Datenschutz akzeptieren",
    "signup.hasAccount": "Bereits ein Konto?",
    "signup.logIn": "Anmelden",
    "signup.heroTitle": "Plane deine nächste Reise in 60 Sekunden",
    "signup.heroSubtitle": "Erstelle ein kostenloses Konto und nutze ganz Vantastika – keine Werbung, keine Kreditkarte.",
    "signup.benefit1": "Speichere deine Routen für immer",
    "signup.benefit2": "Campingvorschläge entlang des Weges",
    "signup.benefit3": "Teil der Community werden",
    "signup.benefit4": "Für immer kostenlos – keine Karte nötig",
    "signup.titleShort": "Konto erstellen",
    "signup.subtitleShort": "Dauert 10 Sekunden. Keine Karte nötig.",
    "signup.submitShort": "Kostenlos registrieren",
    "signup.miniConsent": "Ich akzeptiere die",
    "signup.ageMini": "Ich bin mindestens 16 Jahre alt.",
    "signup.trustLine": "🔒 Wir teilen deine E-Mail nie. Jederzeit kündbar.",
    "signup.freeForever": "Für immer kostenlos",
    "signup.confirmPasswordPlaceholder": "Passwort wiederholen",
    "footer.desc": "Plane deine Reise, finde die richtigen Plätze und reise mit voller Kontrolle. Wohnmobil, Van oder Zelt.",
    "footer.explore": "Entdecken",
    "footer.searchCampings": "Campingplätze Suchen",
    "footer.account": "Konto",
    "footer.legal": "Rechtliches",
    "footer.privacy": "Datenschutz",
    "footer.terms": "Nutzungsbedingungen",
    "footer.rights": "Alle Rechte vorbehalten.",
    "footer.followInstagram": "Folge Vantastika auf Instagram",
    "footer.followFacebook": "Folge Vantastika auf Facebook",
    "footer.followUs": "Folge uns",
    "geo.myLocation": "Mein Standort",
    "geo.nearMe": "In der Nähe",
    "geo.permissionDenied": "Standortzugriff verweigert",
    "geo.detecting": "Standort wird ermittelt...",
    "trip.editTrip": "Reise bearbeiten",
    "trip.tripTitle": "Reisetitel",
    "trip.destination": "Ziel (Adresse suchen...)",
    "trip.startDate": "Startdatum",
    "trip.consumption": "Verbrauch (l/100km)",
    "trip.consumptionEv": "Verbrauch (kWh/100km)",
    "trip.fuelPrice": "Dieselpreis",
    "trip.fuelPriceBensin": "Benzinpreis",
    "trip.fuelPriceEv": "Strompreis",
    "trip.fuelPriceUnitEv": "/kWh",
    "trip.vehicleType": "Fahrzeugtyp",
    "trip.vehicleDiesel": "Diesel",
    "trip.vehicleBensin": "Benzin",
    "trip.vehicleEv": "Elektro",
    "trip.travelMode": "Reiseart",
    "trip.travelModeCar": "Auto",
    "trip.travelModeMotorhome": "Wohnmobil",
    "trip.travelModeMotorcycle": "Motorrad",
    "trip.travelModeDesc": "KI passt Routen und Tipps an Ihr Fahrzeug an",
    "trip.priceCurrency": "Preiswährung",
    "trip.saveChanges": "Änderungen speichern",
    "trip.calculatingRoute": "Route wird berechnet...",
    "trip.driveTime": "Fahrzeit",
    "trip.estimatedFuelCost": "Geschätzte Kraftstoffkosten:",
    "trip.estimatedDiesel": "Geschätzte Dieselkosten:",
    "trip.estimatedChargeCost": "Geschätzte Ladekosten:",
    "trip.openGoogleMaps": "In Google Maps öffnen",
    "trip.tankSize": "Tankgröße (l)",
    "trip.batterySize": "Batterie (kWh)",
    "trip.rangeAnalysis": "Reichweitenanalyse",
    "trip.rangePerFill": "Reichweite pro Tankfüllung",
    "trip.chargingStops": "Ladestopps",
    "trip.fuelStops": "Tankstopps",
    "trip.avgLeg": "Durchschn. pro Etappe",
    "trip.costPerCharge": "Kosten pro Ladung",
    "trip.costPerFill": "Kosten pro Tankfüllung",
    "trip.evStopNote": "Sie müssen etwa {stops} Mal laden, ungefähr alle {km} km.",
    "trip.fuelStopNote": "Sie müssen etwa {stops} Mal tanken, ungefähr alle {km} km.",
    "trip.noStopsNeeded": "Ihre Reichweite deckt die gesamte Fahrt ohne Stopp ab!",
    "trip.routeAlternatives": "Routenalternativen",
    "trip.route": "Route",
    "trip.addMin2Stops": "Mindestens 2 Stopps für Kraftstoffberechnung",
    "trip.addStop": "Stopp hinzufügen",
    "trip.newStop": "Neuer Stopp",
    "trip.searchPlace": "Ort oder Adresse suchen...",
    "trip.notesOptional": "Notizen (optional)",
    "trip.saving": "Wird gespeichert...",
    "trip.add": "Hinzufügen",
    "trip.cancel": "Abbrechen",
    "trip.dragToReorder": "Ziehen zum Neuordnen",
    "trip.navigate": "Hierhin navigieren",
    "trip.startNavigation": "Navigation starten",
    "trip.uploading": "Wird hochgeladen...",
    "trip.writeExperience": "Schreibe über deine Erfahrung...",
    "trip.save": "Speichern",
    "trip.tripExperience": "Reiseerfahrung",
    "trip.edit": "Bearbeiten",
    "trip.write": "Schreiben",
    "trip.describeTripExperience": "Beschreibe deine Reise, was du erlebt hast, Tipps für andere...",
    "trip.tripUpdated": "Reise aktualisiert!",
    "trip.linkCopied": "Link kopiert!",
    "trip.shareLinkDesc": "Teile den Link mit deinen Freunden.",
    "trip.couldNotShare": "Konnte nicht teilen",
    "trip.imageUploaded": "Bild hochgeladen!",
    "trip.couldNotUpload": "Bild konnte nicht hochgeladen werden",
    "trip.experienceSaved": "Erfahrung gespeichert!",
    "trip.stopAdded": "Stopp hinzugefügt!",
    "trip.couldNotAddStop": "Stopp konnte nicht hinzugefügt werden",
    "trip.orderUpdated": "Reihenfolge aktualisiert!",
    "trip.fuelCalc": "Kraftstoffberechnung (Diesel)",
    "trip.currency": "Währung",
    "tip.notFound": "Reise nicht gefunden",
    "tip.notFoundDesc": "Sie ist möglicherweise nicht mehr verfügbar.",
    "tip.back": "Zurück",
    "tip.traveler": "Reisende/r",
    "tip.sharedTrip": "Geteilte Reise",
    "tip.planThisTrip": "Diese Reise planen",
    "tip.aboutTrip": "Über diese Reise",
    "tip.aiGenerated": "KI-generierte Reisepräsentation",
    "tip.budget": "Budget",
    "tip.stops": "Stopps",
    "tip.places": "Orte",
    "tip.duration": "Dauer",
    "tip.days": "Tage",
    "tip.consumption": "Verbrauch",
    "tip.goodToKnow": "Gut zu wissen vor der Abreise",
    "tip.bestSeason": "Beste Reisezeit",
    "tip.currency": "Währung",
    "tip.speed": "Geschwindigkeit",
    "tip.emergency": "Notruf",
    "tip.campingRules": "Campingregeln",
    "tip.route": "Die Route",
    "tip.endOfTrip": "Ende der Reise",
    "tip.wantSameRoute": "Möchtest du die gleiche Route fahren?",
    "tip.cloneDesc": "Wir erstellen eine neue Reise für dich mit denselben Stopps. Du kannst sie anpassen — Start ändern, Stopps hinzufügen und Budget planen.",
    "tip.needAccount": "Du brauchst ein Konto, um die Reise zu speichern",
    "tip.tripCreated": "Reise erstellt! 🚐 Jetzt anpassen.",
    "tip.couldNotCreate": "Reise konnte nicht erstellt werden",
    "tip.home": "Start",
    "tip.travelTips": "Reisetipps",
    "tip.startPoint": "Startpunkt",
    "tip.endPoint": "Endpunkt",
    "tip.travelGuide": "Reiseführer",
    "tip.hide": "Ausblenden",
    "tip.sharedThisTrip": "hat diese Reise geteilt",
    "tip.explore": "Entdecken",
    "tip.inspirationTitle": "Inspiration von Reisenden",
    "tip.inspirationDesc": "Entdecke Routen, die von anderen Reisenden geteilt wurden",
    "tip.tipHidden": "Reisetipp von der Startseite ausgeblendet",
    "tip.couldNotHide": "Reisetipp konnte nicht ausgeblendet werden",
    "tip.publishAsTip": "Als Reisetipp veröffentlichen",
    "tip.publishDesc": "Zeige deine Reise auf der Startseite als Inspiration für andere",
    "tip.creatingAi": "KI-Reisepräsentation wird erstellt... ✨",
    "tip.creatingAiDesc": "Dies kann einen Moment dauern.",
    "tip.publishedWithAi": "Reise mit KI-Guide veröffentlicht! 🌍✨",
    "tip.published": "Reise ist jetzt für alle sichtbar! 🌍",
    "tip.unpublished": "Reise ist jetzt privat",
    "tip.publishedNoAi": "Reise veröffentlicht! (KI-Guide konnte nicht erstellt werden)",
    "authGate.title": "Alles freischalten",
    "authGate.description": "Erstelle ein Konto, um Reisen zu planen, Orte zu speichern und Budget zu verwalten.",
    "authGate.signup": "Konto erstellen",
    "authGate.login": "Anmelden",
    "authGate.or": "oder",
    "authGate.hasAccount": "Schon ein Konto?",
    "authGate.ctaPlaces": "Verifizierte Plätze in ganz Europa",
    "authGate.ctaRoutes": "Smarte Routen mit Kraftstoffkosten",
    "authGate.ctaBudget": "Volle Budgetkontrolle",
    "authGate.ctaChecklist": "Packlisten für jeden Reisetyp",
    "authGate.ctaAI": "KI-Assistent der jede Straße kennt",
    "authGate.saveTripMsg": "Reise speichern – Konto erstellen",
    "authGate.planRouteMsg": "Route planen – anmelden",
    "authGate.savePlaceMsg": "Ort speichern – Konto erstellen",
    "gate.freeForever": "Kostenlos starten",
    "submit.loginRequired": "Anmeldung erforderlich",
    "submit.loginDesc": "Du musst angemeldet sein.",
    "submit.title": "Campingplatz einreichen",
    "submit.subtitle": "Teile deinen Lieblingsplatz",
    "submit.reviewNote": "Eingereichte Plätze werden vor der Veröffentlichung geprüft.",
    "submit.basicInfo": "Grundinformationen",
    "submit.name": "Name",
    "submit.type": "Typ",
    "submit.country": "Land",
    "submit.region": "Region",
    "submit.city": "Stadt",
    "submit.description": "Beschreibung",
    "submit.shortDescription": "Kurzbeschreibung",
    "submit.fullDescription": "Vollständige Beschreibung",
    "submit.priceSchedule": "Preis & Zeiten",
    "submit.freeSpot": "Kostenloser Platz",
    "submit.pricePerNight": "Preis pro Nacht (€)",
    "submit.imageContact": "Bild & Kontakt",
    "submit.imageUrl": "Bild-URL",
    "submit.phone": "Telefon",
    "submit.email": "E-Mail",
    "submit.website": "Webseite",
    "submit.address": "Adresse",
    "submit.fullAddress": "Vollständige Adresse",
    "submit.searchAddress": "Adresse suchen...",
    "submit.amenities": "Ausstattung",
    "submit.submit": "Campingplatz einreichen",
    "submit.submitting": "Wird eingereicht...",
    "submit.success": "Eingereicht!",
    "submit.successDesc": "Dein Campingplatz wird von einem Admin geprüft.",
    "submit.missingFields": "Fehlende Felder",
    "submit.missingFieldsDesc": "Name, Land und Region sind erforderlich.",
    "submit.ctaTitle": "Kennst du einen tollen Platz?",
    "submit.ctaDesc": "Hilf der Community und füge einen Campingplatz hinzu",
    "home.featuresTrustTitle": "Plätze, denen du vertrauen kannst",
    "home.featuresTrustDesc": "Echte Campingplätze mit Preisen, Bewertungen und allem, was du wissen musst.",
    "home.featuresRoutesTitle": "Routen, die funktionieren",
    "home.featuresRoutesDesc": "Sieh die ganze Reise auf einen Blick. Stopps, Fahrzeiten und Spritkosten direkt auf der Karte.",
    "home.featuresBudgetTitle": "Volle Kontrolle über dein Geld",
    "home.featuresBudgetDesc": "Setze ein Budget, logge Ausgaben und sieh genau, wohin das Geld geht.",
    "home.featuresChecklistTitle": "Vergiss nie etwas",
    "home.featuresChecklistDesc": "Checklisten passend zu deinem Reisetyp. Gas, Wasser, Pass, Ladegerät. Alles an einem Ort.",
    "home.featuresAITitle": "KI-Assistent",
    "home.featuresAIDesc": "Frag Vannie alles über deine Reise. Routen, Regeln, Tipps und lokales Wissen. Immer verfügbar.",
    "home.sectionTitle": "Alles, was du für die ganze Reise brauchst",
    "home.sectionSubtitle": "Von der ersten Idee bis zur letzten Meile. Vantastika gibt dir die Kontrolle.",
    "home.trustPlaces": "2 400+ Plätze",
    "home.trustEurope": "Ganz Europa",
    "home.videoOverlay1": "Plane deine Route. Finde die besten Plätze. Los geht's.",
    "home.videoOverlay2": "Alles was du für den perfekten Roadtrip brauchst.",
    "home.lifestyleTitle": "Deine Reise. Deine Regeln.",
    "home.lifestyleSubtitle": "Routen, Stopps, Budget und Packliste. Fertig in Sekunden.",
    "home.lifestyleCta": "Jetzt planen",
    "home.vannieTitle": "Triff Vannie. Dein persönlicher Reiseführer.",
    "home.vannieDesc": "Stell eine Frage, bekomm eine klare Antwort. Beste Plätze, lokale Tipps, Routenvorschläge und Regeln pro Land.",
    "home.vannieCta": "Mit Vannie chatten",
    "home.vannieCtaSignup": "Mitmachen und mit Vannie chatten",
    "home.vannieRoutes": "Routenplanung",
    "home.vannieCamping": "Campingtipps",
    "home.vannieRules": "Lokale Regeln",
    "home.vannieBudget": "Budgethilfe",
    "nav.guides": "Länderführer",
    "nav.services": "Service & SOS",
    "nav.games": "Roadtrip-Spiele",
    "nav.vannie": "Vannie",
    "nav.community": "Community",
    "nav.profile": "Mein Profil",
    "card.free": "KOSTENLOS",
    "card.night": "Nacht",
    "card.loginForPrice": "Anmelden für Preis",
    "cat.campings": "Campingplätze",
    "cat.stellplatz": "Stellplatz",
    "cat.freeSpots": "Kostenlose Plätze",
    "map.noStopsToShow": "Keine Stopps mit Koordinaten zum Anzeigen",
    "wizard.whereToGo": "Wohin möchtest du fahren?",
    "wizard.searchDestination": "Ziel suchen (z.B. Sanremo, Italien)...",
    "wizard.startPoint": "Wo startest du?",
    "wizard.finalDay": "Endziel",
    "wizard.overnight": "Übernachtung",
    "wizard.viewAllAccommodations": "Alle anzeigen",
    "wizard.accommodationNear": "Unterkünfte in der Nähe",
    "wizard.tapToChooseAccommodation": "Wähle eine Unterkunft unten.",
    "wizard.accommodationMissingHint": "Du hast hier eine Übernachtung geplant, aber keine Unterkunft gewählt.",
    "wizard.accommodationMissingTitle": "Unterkunft fehlt",
    "wizard.accommodationMissingSubtitle": "Tippe, um für jeden Tag eine Unterkunft zu wählen.",
    "wizard.detectedAccommodationTitle": "Scheint eine Unterkunft zu sein",
    "wizard.detectedAccommodationDesc": "scheint eine Unterkunft zu sein. Wie möchtest du sie markieren?",
    "wizard.overnightHint": "Hier übernachtest du",
    "wizard.visitHint": "Nur Durchfahrt oder Besuch",
    "wizard.destinationHint": "Endziel der Reise",
    "wizard.optimizeOrder": "Reihenfolge optimieren",
    "wizard.myLocation": "Mein Standort",
    "wizard.chooseManually": "Manuell wählen",
    "wizard.searchStartPoint": "Startpunkt suchen...",
    "wizard.locationDetected": "Standort erkannt",
    "wizard.start": "Start",
    "wizard.destination": "Ziel",
    "wizard.yourStops": "Deine Stopps",
    "wizard.stop": "Stopp",
    "wizard.addedAsStop": "als Stopp hinzugefügt!",
    "campingDetector.title": "Sieht aus wie ein Campingplatz!",
    "campingDetector.aiDetected": "KI erkannt",
    "campingDetector.description": "Möchtest du den Ort zu Vantastika hinzufügen, damit andere Reisende ihn finden?",
    "campingDetector.pendingNote": "Der Ort wird vor der Veröffentlichung von einem Admin geprüft.",
    "campingDetector.confidence": "Treffer",
    "campingDetector.typeCamping": "Campingplatz",
    "campingDetector.typeStellplatz": "Stellplatz",
    "campingDetector.typeFreeParking": "Kostenlos parken",
    "campingDetector.typeLodging": "Unterkunft",
    "campingDetector.notNow": "Nicht jetzt",
    "campingDetector.registerCta": "Zu Vantastika hinzufügen",
    "campingDetector.registering": "Wird hinzugefügt…",
    "campingDetector.registeredTitle": "Danke für den Beitrag!",
    "campingDetector.registeredDesc": "Der Ort wurde eingereicht und nach Prüfung veröffentlicht.",
    "campingDetector.alreadyExists": "Bereits in Vantastika",
    "campingDetector.alreadyExistsDesc": "Dieser Ort ist bereits registriert.",
    "campingDetector.couldNotRegister": "Ort konnte nicht registriert werden",
    "campingDetector.loginRequired": "Anmeldung erforderlich",
    "campingDetector.loginRequiredDesc": "Melde dich an, um neue Orte beizutragen.",
    "campingDetector.cannotRegister": "Registrierung nicht möglich",
    "campingDetector.missingPlaceId": "Google-Orts-ID fehlt.",
    "stopType.visit": "Besuch",
    "stopType.overnight": "Übernachtung",
    "stopType.destination": "Ziel",
    "stopType.nights": "Nächte",
    "stopType.stayHours": "Aufenthalt (h)",
    "wizard.suggestedStops": "Campingplätze entlang der Route",
    "wizard.findingCampings": "Finde Campingplätze entlang der Route...",
    "wizard.noCampingsNearRoute": "Keine Campingplätze in der Nähe dieser Route gefunden",
    "wizard.saveTrip": "Reise speichern",
    "wizard.stepPlanningMode": "Planung",
    "wizard.stepDrivingHours": "Fahrzeit",
    "wizard.stepTravelStyle": "Reisestil",
    "wizard.stepCountryInfo": "Länder",
    "wizard.howToPlan": "Wie möchten Sie Ihre Reise planen?",
    "wizard.howToPlanDesc": "Wählen Sie die Option, die am besten zu Ihnen passt.",
    "wizard.planModeKnow": "Ich weiß, wohin ich will",
    "wizard.planModeKnowDesc": "Ich habe ein Ziel und möchte die Route dorthin planen.",
    "wizard.planModeHelp": "Hilf mir, eine Reise zu finden",
    "wizard.planModeHelpDesc": "Ich möchte Inspiration und KI-Vorschläge für Reisen.",
    "wizard.planModeOptimize": "Route smart optimieren",
    "wizard.planModeOptimizeDesc": "Ich weiß ungefähr, wohin ich will, aber möchte die besten Etappen und Stopps.",
    "wizard.stepDetails": "Details",
    "wizard.stepTrip": "Reise",
    "wizard.stepPreferences": "Präferenzen",
    "wizard.dailyDrivingTitle": "Wie lange möchten Sie pro Tag fahren?",
    "wizard.dailyDrivingDesc": "Wir passen die Etappen an Ihre Wahl an.",
    "wizard.recommended": "Empfohlen",
    "wizard.travelStyleTitle": "Wie möchten Sie reisen?",
    "wizard.travelStyleDesc": "Wir passen Route und Vorschläge an Ihren Reisestil an.",
    "wizard.styleFastest": "Schnellste Route",
    "wizard.styleFastestDesc": "Kürzeste Fahrzeit über Autobahnen.",
    "wizard.styleSafe": "Sicher und entspannt",
    "wizard.styleSafeDesc": "Ruhigere Straßen mit angemessener Fahrzeit.",
    "wizard.styleScenic": "Landschaftlich schön",
    "wizard.styleScenicDesc": "Schöne Straßen und Aussichten bevorzugen.",
    "wizard.styleCamping": "Campingfreundliche Route",
    "wizard.styleCampingDesc": "Route mit gutem Zugang zu Campingplätzen.",
    "wizard.styleBudget": "Budgetfreundliche Reise",
    "wizard.styleBudgetDesc": "Mautstraßen und teure Stopps vermeiden.",
    "wizard.styleFamily": "Familienfreundliche Reise",
    "wizard.styleFamilyDesc": "Kürzere Etappen mit kinderfreundlichen Stopps.",
    "wizard.selectTravelStyle": "Wählen Sie einen Reisestil",
    "wizard.longDrivingWarning": "Längeres tägliches Fahren kann die Verkehrssicherheit beeinträchtigen. Wir empfehlen max. 4–5 Stunden pro Tag.",
    "wizard.skipOvernightHere": "Übernachtung überspringen – weiterfahren",
    "wizard.resetStages": "Etappen zurücksetzen",
    "wizard.passThrough": "Durchreise",
    "wizard.noOvernightShort": "Keine Übernachtung",
    "wizard.mergedDayTitle": "Sie fahren ohne Übernachtung durch",
    "wizard.mergedDayDesc": "Dieser Tag ist mit dem nächsten zusammengelegt. Kurze Pausen zum Ausruhen und Tanken werden empfohlen.",
    "wizard.undoSkipOvernight": "Rückgängig – hier übernachten",
    "wizard.selectPlanningMode": "Wählen Sie, wie Sie planen möchten",
    "wizard.accommodationTitle": "Unterkunftsvorschläge",
    "wizard.accommodationDescCamping": "Zeigt Wohnmobilstellplätze und Campingplätze entlang Ihrer Route.",
    "wizard.accommodationDescCar": "Zeigt Hotels und Unterkünfte in der Nähe Ihres Ziels.",
    "wizard.campingRules": "Campingregeln",
    "wizard.countriesInRouteOrder": "Länder in Routenreihenfolge",
    "wizard.countryInfoDesc": "Praktische Informationen für jedes Land, das Sie durchqueren.",
    "wizard.countryInfoTitle": "Länder entlang Ihrer Route",
    "wizard.startCountry": "Startland",
    "wizard.transitCountry": "Transitland",
    "wizard.destinationCountry": "Ziel",
    "wizard.ferryAlternatives": "Fähralternativen",
    "wizard.ferryDuration": "Fahrzeit",
    "wizard.ferryRoute": "Strecke",
    "wizard.ferryRelevance": "Warum relevant",
    "wizard.driving": "Fahren",
    "wizard.drivingRules": "Fahrregeln",
    "wizard.fromRoute": "Entlang der Route",
    "wizard.fuelInfo": "Kraftstoff",
    "wizard.importantForMotorhome": "wichtig für Wohnmobil",
    "wizard.infoItems": "Infopunkte",
    "wizard.loadingCountryInfo": "Lade Länderinformationen...",
    "wizard.motorhomeMultiCountryTip": "Mit dem Wohnmobil können die Regeln zwischen Ländern stark variieren. Prüfen Sie Umweltzonen, Vignetten und erlaubte Maße vor der Abreise.",
    "wizard.noCountryInfo": "Keine Länderinformationen verfügbar. Geben Sie zuerst eine Route ein.",
    "wizard.noResultsForStage": "Keine Ergebnisse für diese Etappe",
    "wizard.oneWay": "Einfache Fahrt",
    "wizard.optimizeRoute": "Stoppreihenfolge optimieren",
    "wizard.searchingStage": "Suche für Etappe",
    "wizard.selectDestForAccommodation": "Wählen Sie zuerst ein Ziel für Unterkunftsvorschläge.",
    "wizard.shortTripNoOvernight": "Kurze Reise – keine Übernachtung nötig.",
    "wizard.skipStep": "Diesen Schritt überspringen",
    "wizard.from": "Von",
    "wizard.to": "Nach",
    "wizard.selectedAccommodation": "Gewählte Unterkunft",
    "wizard.changeAccommodation": "Ändern",
    "wizard.selected": "Gewählt",
    "wizard.selectAccommodation": "Wählen",
    "wizard.noAccommodationYet": "Noch keine Unterkunft gewählt. Sie können im nächsten Schritt wählen.",
    "wizard.viewOnMap": "Auf Karte anzeigen",
    "common.close": "Schließen",
    "wizard.speedLimits": "Geschwindigkeitsbegrenzungen",
    "wizard.stages": "Etappen",
    "wizard.tolls": "Maut & Vignetten",
    "wizard.wildCamping": "Wildcamping",
    "wizard.stepRoute": "Hinreise",
    "wizard.stepReturn": "Rückreise",
    "wizard.stepAccommodation": "Unterkunft",
    "wizard.stepVehicle": "Fahrzeug",
    "wizard.stepOverview": "Übersicht",
    "wizard.next": "Weiter",
    "wizard.back": "Zurück",
    "wizard.tripTypeLabel": "Reiseart",
    "wizard.accommodationDesc": "Hier sind Unterkunftsvorschläge basierend auf deiner Route und Reiseart. Füge sie als Stopp hinzu oder überspringe.",
    "wizard.showingCampingsAlongRoute": "Zeige Campingplätze entlang deiner Route",
    "wizard.showingHotelsNearDest": "Zeige Hotels in der Nähe deines Ziels",
    "wizard.showingAccommodationNearDest": "Zeige Unterkünfte entlang deiner Route",
    "wizard.searchingAlongRoute": "Suche entlang deiner Route...",
    "wizard.expandingSearch": "Erweitere Suchgebiet...",
    "wizard.tryingAlternatives": "Probiere alternative Unterkünfte...",
    "wizard.noAccommodationFound": "Keine Unterkünfte in der Nähe deiner Route gefunden. Du kannst diesen Schritt überspringen und später Stopps hinzufügen.",
    "wizard.returnTripDesc": "Wähle, wie du zurückreisen möchtest.",
    "wizard.sameRouteDesc": "Gleichen Weg zurück",
    "wizard.scenicDesc": "Alternative landschaftliche Route",
    "wizard.fastestDesc": "Schnellster Weg zurück",
    "wizard.routeSummary": "Route",
    "wizard.selectDestFirst": "Wähle zuerst ein Reiseziel",
    "wizard.selectStartFirst": "Wähle zuerst einen Startpunkt",
    "wizard.fuelType": "Kraftstoffart",
    "wizard.estimatedFuelCostLabel": "Geschätzte Kraftstoffkosten",
    "wizard.showPricesIn": "Preise anzeigen in",
    "wizard.perNight": "Nacht",
    "wizard.moreDetails": "Mehr Details",
    "wizard.firstTimeHint": "Suche zuerst nach deinem Reiseziel. Wähle dann Fahrzeug, Daten und Stopps — die Route wird automatisch berechnet!",
    "wizard.budgetOptional": "Budget (optional)",
    "wizard.budgetPlaceholder": "z.B. 15000",
    "wizard.fuelAutoAdded": "Geschätzte Kraftstoffkosten zum Budget hinzugefügt",
    "wizard.planNewTrip": "Neue Reise planen",
    "wizard.myTrips": "Meine gespeicherten Reisen",
    "wizard.planTrip": "Reise planen",
    "wizard.or": "oder",
    "wizard.popularDestinations": "Beliebt",
    "wizard.describeTrip": "Beschreibe deine Reise – wir planen alles für dich",
    "wizard.describeSubtext": "Schreibe frei, was du machen möchtest, und wir erstellen den Vorschlag",
    "wizard.backToManual": "Zurück zur manuellen Suche",
    "wizard.selectSuggestion": "Wähle einen Vorschlag aus der Liste, um fortzufahren.",
    "wizard.analyzingTrip": "Analysiere deine Reise…",
    "wizard.createPlan": "Reiseplan erstellen",
    "wizard.suggestedTrip": "Vorgeschlagene Reise",
    "wizard.places": "Orte",
    "wizard.startOver": "Neu beginnen",
    "wizard.noDestFound": "Keine Ziele gefunden",
    "wizard.noDestFoundDesc": "Versuche, deine Reise detaillierter zu beschreiben.",
    "wizard.couldNotParse": "Reise konnte nicht interpretiert werden",
    "wizard.couldNotParseDesc": "Versuche es erneut mit einer klareren Beschreibung.",
    "wizard.tooManyRequests": "Zu viele Anfragen",
    "wizard.tooManyRequestsDesc": "Warte einen Moment und versuche es erneut.",
    "wizard.aiCreditOut": "AI-Guthaben aufgebraucht",
    "wizard.aiCreditOutDesc": "Kontaktiere den Support für weitere Informationen.",
    "wizard.tripSaved": "Reise gespeichert!",
    "wizard.tripSavedDesc": "Gehe zu Meine Reisen, um sie zu sehen.",
    "wizard.couldNotReachVannie": "Vannie ist gerade nicht erreichbar.",
    "wizard.describePlaceholder": "Beschreibe deine Reise in eigenen Worten — wir erstellen einen kompletten Reiseplan für dich.",
    "wizard.chars": "Zeichen",
    "wizard.destinationConfirmed": "Ziel ausgewählt",
    "wizard.locating": "Koordinaten werden gesucht...",
    "wizard.destType": "Ziel",
    "wizard.visitType": "Besuch",
    "wizard.aiSuggestTitle": "AI Reisevorschlag",
    "wizard.aiSuggestOpen": "Beschreibe deine Reise – wir planen alles für dich",
    "wizard.aiSuggestPlaceholder": "Z.B. 'Ich möchte 5 Tage an den Strand' oder 'Gemütliche Bergtour mit Wanderungen in den Alpen'",
    "wizard.vannieThinking": "Vannie denkt nach...",
    "wizard.suggestTrip": "Reise vorschlagen",
    "wizard.suggestedStopsLabel": "Vorgeschlagene Stopps",
    "wizard.useThisSuggestion": "Diesen Vorschlag verwenden",
    "wizard.couldNotGenerate": "Vorschlag konnte nicht generiert werden",
    "wizard.days": "Tage",
    "wizard.perDay": "/Tag",
    "wizard.roundtrip": "Hin & zurück",
    "wizard.oneway": "Einfache Fahrt",
    "wizard.departure": "Abfahrt",
    "wizard.return": "Rückfahrt",
    "wizard.selectDate": "Datum wählen",
    "wizard.departureDateOptional": "Abfahrtsdatum (optional)",
    "wizard.day": "Tag",
    "wizard.dayLabel": "Tag {n}",
    "wizard.roundtripLabel": "Hin & zurück",
    "wizard.estimatedTravelTime": "Geschätzte Reisezeit",
    "wizard.total": "gesamt",
    "wizard.stopsLabel": "Stopps",
    "wizard.fetchingReturnTips": "Tipps für die Rückfahrt werden geladen…",
    "wizard.returnTips": "Tipps für die Rückfahrt",
    "wizard.roundtripShort": "h&z",
    "wizard.driveTime": "Fahrzeit",
    "wizard.chargeCost": "Ladekosten",
    "wizard.fuelCost": "Kraftstoffkosten",
    "wizard.distance": "Strecke",
    "wizard.chooseRoute": "Diese Route wählen",
    "wizard.ferry": "Fähre",
    "wizard.maxDrivingHours": "Max. Fahrzeit pro Tag",
    "wizard.longDriveWarning": "Lange Fahrzeit",
    "wizard.longDriveDesc": "Wir empfehlen max. 4 Stunden für sicheres und angenehmes Fahren.",
    "wizard.drivingDaysCalc": "{hours}h/Tag = ~{days} Fahrtage",
    "wizard.drivingDaysRoundtrip": "Tage hin & zurück",
    "wizard.vehicleSettings": "Fahrzeug & Kraftstoff",
    "wizard.scenic": "Landschaftlich",
    "wizard.fastest": "Schnellste",
    "wizard.balanced": "Ausgewogen",
    "wizard.sameRoute": "Gleiche Route",
    "wizard.returnTrip": "Rückfahrt",
    "wizard.homecoming": "Ankunft zuhause",
    "wizard.optimizationSummary": "Route optimiert",
    "wizard.stageOverview": "Tagesetappen",
    "wizard.overnightIn": "Übernachtung bei",
    "wizard.arrivalAt": "Ankunft in",
    "wizard.returnRouteOption": "Rückfahrt",
    "wizard.hours": "Std",
    "wizard.addedAsOvernight": "als Übernachtung hinzugefügt!",
    "wizard.addedAsRestStop": "als Rastplatz hinzugefügt!",
    "wizard.stopAddedFromMap": "Stopp von der Karte hinzugefügt!",
    "wizard.updatedStop": "Stopp aktualisiert",
    "wizard.stopPositionUpdated": "Stopp-Position aktualisiert",
    "wizard.newStop": "Neuer Stopp",
    "wizard.optimizedDesc": "Stopps nach kürzester Gesamtdistanz sortiert",
    "wizard.newOrder": "Neue Reihenfolge",
    "wizard.orderChanged": "Reihenfolge vom Original geändert",
    "wizard.routeOptimized": "Route optimiert!",
    "wizard.nameError": "Namensvorschläge konnten nicht generiert werden",
    "wizard.mapInstructions": "Klicken Sie auf die Karte um Stopps hinzuzufügen, ziehen zum Verschieben",
    "wizard.nameTrip": "Reise benennen",
    "wizard.optional": "optional",
    "wizard.namePlaceholder": "z.B. Sommer in den Alpen",
    "wizard.aiSuggest": "KI-Vorschlag",
    "wizard.signupAndSave": "Registrieren & speichern",
    "wizard.useTrip": "Diese Reise verwenden",
    "wizard.dragToReorder": "Ziehen zum Umordnen",
    "wizard.prefillTip": "Vorausgefüllt von einem Vantastika-Tipp",
    "wizard.prefillGuide": "Vorausgefüllt von einem Reiseführer",
    "demo.phase1Eyebrow": "Zu viele Optionen",
    "demo.phase1Title": "Das Problem erkennen",
    "demo.phase1Text": "Die Karte ist voller Orte und es ist schwer zu wissen, welcher Halt sich wirklich lohnt.",
    "demo.phase2Eyebrow": "Klare Planung",
    "demo.phase2Title": "Nur verifizierte Plätze",
    "demo.phase2Text": "Vantastika filtert das Rauschen und macht die Planung klar, schnell und sicher.",
    "demo.phase3Eyebrow": "Fertiger Roadtrip",
    "demo.phase3Title": "Deine Route, startklar",
    "demo.phase3Text": "Sieh die gesamte Route, wichtige Stopps und eine Reise, die sich fertig anfühlt, bevor du den Motor startest.",
    "demo.stepStart": "Reise starten",
    "demo.stepVerified": "Verifizierte Stopps",
    "demo.stepArrive": "Am Ziel ankommen",
    "demo.planSmart": "Clever planen",
    "demo.budget": "Budget",
    "demo.budgetUsed": "verbraucht",
    "demo.roadtripPreview": "Routenvorschau",
    "service.title": "Service & Notfall",
    "service.workshop": "Werkstatt",
    "service.gasStation": "Tankstelle",
    "service.water": "Wasser",
    "service.charging": "Laden",
    "service.waste": "Entsorgung",
    "service.hospital": "Krankenhaus",
    "service.police": "Polizei",
    "service.grocery": "Supermarkt",
    "service.emergency": "Notfall",
    "service.exitEmergency": "SOS beenden",
    "service.emergencyMode": "Notfallmodus",
    "service.callEmergency": "Anrufen",
    "service.emergencyNote": "112 funktioniert in allen EU-Ländern",
    "service.locating": "Standort wird ermittelt...",
    "service.locationNeeded": "Standort erforderlich",
    "service.enableLocation": "Standortzugriff erlauben, um Dienste in der Nähe zu finden",
    "service.retry": "Erneut versuchen",
    "service.searching": "Suche in der Nähe...",
    "service.noResults": "Keine Dienste in der Nähe gefunden",
    "service.navigate": "Navigieren",
    "service.open": "Geöffnet",
    "service.closed": "Geschlossen",
    "service.fetchError": "Dienste konnten nicht geladen werden",
    "service.useGPS": "GPS verwenden",
    "service.searchPlaceholder": "Suche alles... Tesla, Hymer, Campingausrüstung, Apotheke",
    "service.manual": "Manuell",
    "service.searchResultsFor": "Ergebnisse für",
    "why.title": "Warum Vantastika wählen?",
    "why.subtitle": "Alles für deinen Roadtrip — komplett kostenlos, ohne Kreditkarte.",
    "why.smartPlanning": "Intelligente Reiseplanung",
    "why.smartPlanningDesc": "Erstelle Routen mit Stopps, Kraftstoffberechnung und Zeitplanung in einem Fluss.",
    "why.aiAssistant": "KI-Reiseassistent",
    "why.aiAssistantDesc": "Beschreibe deine Traumreise in eigenen Worten — unsere KI erstellt den gesamten Plan.",
    "why.campingsEurope": "Campingplätze in ganz Europa",
    "why.campingsEuropeDesc": "Finde, vergleiche und filtere Campingplätze und Stellplätze in 21 Ländern.",
    "why.travelGuides": "Inspirierende Reiseführer",
    "why.travelGuidesDesc": "Fertige Themenrouten mit Geschichte, Stopps und Tipps — fahrbereit.",
    "why.budget": "Budget & Ausgaben",
    "why.budgetDesc": "Behalte Kosten, Kraftstoff und Tagesbudget während der Reise im Blick.",
    "why.shareFriends": "Mit Freunden teilen",
    "why.shareFriendsDesc": "Lade Reisekameraden ein und plant gemeinsam in Echtzeit.",
    "why.checklists": "Smarte Checklisten",
    "why.checklistsDesc": "Angepasste Packlisten basierend auf Fahrzeugtyp und Reise.",
    "why.sos": "SOS & Sicherheitspaket",
    "why.sosDesc": "Notfall-Teilen, Unfallberichte und schneller Zugang zu Notrufnummern.",
    "why.badges": "Level & Abzeichen",
    "why.badgesDesc": "Sammle Auszeichnungen, steige im Level auf und fordere dich als Reisender heraus.",
    "why.cta": "Kostenloses Konto erstellen",
    "why.ctaSub": "Keine Karte nötig · In 30 Sekunden fertig",
    "games.title": "Roadtrip Games",
    "games.subtitle": "Mach die Reise spaßiger 🎮",
    "games.played": "Gespielt",
    "games.totalPoints": "Gesamtpunkte",
    "games.challengeFriend": "Fordere einen Freund heraus",
    "games.loginPrompt": "Anmelden um Spielverlauf zu speichern",
    "games.loginPromptSub": "Deine Punkte, Favoriten und Platzierung werden gespeichert",
    "games.onTrip": "Reise läuft – Spiele angepasst für {country}",
    "games.duringTrip": "Unterwegs",
    "games.duringTripSub": "Perfekt für Beifahrer – {n} Spieltypen",
    "games.atStop": "Bei Stopps & Pausen",
    "games.atStopSub": "Wenn ihr pausiert oder erkundet – {n} Spieltypen",
    "games.camping": "Camping & Abendspiele",
    "games.campingSub": "Längere Spiele für Abende und Campingleben – {n} Spieltypen",
    "games.tabs.games": "Spiele",
    "games.tabs.community": "Community",
    "games.tabs.challenges": "Herausforderung",
    "games.tabs.badges": "Abzeichen",
    "games.tabs.history": "Verlauf",
    "games.favorites": "Lieblingsspiele",
    "games.favoritesEmpty": "Markiere ein Spiel mit Herz um es hier zu speichern",
    "games.recommended": "Empfohlen",
    "game.quiz.name": "Roadtrip Quiz",
    "game.quiz.desc": "Teste dein Wissen über Länder, Straßen und Camping",
    "game.bingo.name": "Roadtrip Bingo",
    "game.bingo.desc": "Entdecke Dinge unterwegs und markiere deine Bingokarte",
    "game.guess_place.name": "Errate den Ort",
    "game.guess_place.desc": "Kannst du erraten, welchen Ort wir beschreiben?",
    "game.would_you_rather.name": "Was wäre dir lieber",
    "game.would_you_rather.desc": "Wähle zwischen kniffligen Reisedilemmas",
    "game.trivia_battle.name": "Trivia Battle",
    "game.trivia_battle.desc": "Tritt in Echtzeit gegen andere Reisende an",
    "game.photo_challenge.name": "Foto-Challenge",
    "game.photo_challenge.desc": "Kreative Fotoaufgaben an deinem Stopp",
    "game.story.name": "Geschichtenspiel",
    "game.story.desc": "Erstellt zusammen eine Reisegeschichte",
    "game.checkpoint.name": "Checkpoint",
    "game.checkpoint.desc": "Erkunde die Gegend mit lustigen Aufgaben",
    "game.evening_game.name": "Abendspiel",
    "game.evening_game.desc": "Gemütliche Gruppenspiele für den Abend",
    "game.scenario.name": "Szenariospiel",
    "game.scenario.desc": "Was würdest du tun? Reisedilemmas",
    "game.intro.tagline.quiz": "Teste dein Wissen!",
    "game.intro.tagline.bingo": "Augen auf!",
    "game.intro.tagline.guess_place": "Kannst du es erraten?",
    "game.intro.tagline.would_you_rather": "Triff deine Wahl!",
    "game.intro.tagline.trivia_battle": "Die Herausforderung beginnt!",
    "game.intro.tagline.photo_challenge": "Fang den Moment ein!",
    "game.intro.tagline.story": "Es war einmal eine Reise...",
    "game.intro.tagline.checkpoint": "Entdecke & erkunde!",
    "game.intro.tagline.evening_game": "Zeit zum Entspannen!",
    "game.intro.tagline.scenario": "Was würdest du tun?",
    "game.difficulty": "Schwierigkeitsgrad wählen",
    "game.difficulty.easy": "Leicht",
    "game.difficulty.medium": "Mittel",
    "game.difficulty.hard": "Schwer",
    "game.difficulty.easyDesc": "Perfekt für Anfänger",
    "game.difficulty.mediumDesc": "Eine faire Herausforderung",
    "game.difficulty.hardDesc": "Für echte Experten",
    "game.question": "Frage",
    "game.points": "Punkte",
    "game.finished": "Quiz geschafft!",
    "game.score.amazing": "Unglaublich! Du bist ein Meister! 🏆",
    "game.score.great": "Fantastisch! 🎉",
    "game.score.good": "Gut gemacht! 👍",
    "game.score.ok": "Nicht schlecht! Versuch es nochmal! 💪",
    "game.score.low": "Übung macht den Meister! 💪",
    "game.bestStreak": "Beste Serie: {n} richtige in Folge!",
    "game.back": "Zurück",
    "game.paused": "Spiel pausiert",
    "game.exit.title": "Spiel verlassen?",
    "game.exit.description": "Dein Fortschritt geht verloren, wenn du aufhörst.",
    "game.exit.continue": "Weiterspielen",
    "game.exit.pause": "Spiel pausieren",
    "game.exit.quit": "Spiel beenden",
    "game.story.finished": "Geschichte abgeschlossen!",
    "game.story.chapters": "Kapitel abgeschlossen",
    "game.story.chapter": "Kapitel",
    "game.story.new": "Neue Geschichte",
    "game.story.whatDo": "Was macht ihr?",
    "game.scenario.finished": "Szenario abgeschlossen!",
    "game.scenario.steps": "Schritte durchgeführt",
    "game.scenario.step": "Schritt",
    "game.scenario.new": "Neues Szenario",
    "game.scenario.whatDo": "Was macht ihr?",
    "game.scenario.intense": "Intensiv!",
    "game.newRound": "Neue Runde",
    "game.seeResults": "Ergebnis ansehen",
    "game.nextQuestion": "Nächste Frage",
    "game.playNow": "Jetzt spielen",
    "game.players": "Spieler",
    "game.minutes": "Min",
    "game.safety.title": "Sicherheit geht vor!",
    "game.safety.desc": "Dieses Spiel ist für Beifahrer gedacht, nicht für den Fahrer. Bestätige, dass du Beifahrer bist.",
    "game.safety.confirm": "Ich bin Beifahrer",
    "game.safety.later": "Später spielen",
    "game.version": "{country}-Version",
    "game.hero.title": "Mach die Reise spaßiger",
    "game.hero.game_types": "Spieltypen",
    "game.hero.questions": "Fragen",
    "game.hero.countries": "Länder",
    "game.challenge_friend": "Fordere einen Freund heraus",
    "game.trip_active": "Reise läuft",
    "game.login_prompt": "Anmelden um Spielverlauf zu speichern",
    "game.login_prompt_desc": "Punkte, Favoriten und Bestenliste werden gespeichert",
    "game.tab.games": "Spiele",
    "game.tab.challenges": "Aufgaben",
    "game.tab.community": "Community",
    "game.tab.collect": "Sammeln",
    "game.tab.history": "Verlauf",
    "game.section.on_the_road": "Unterwegs",
    "game.section.at_stop": "Bei Stopps & Pausen",
    "game.section.camping": "Camping & Abendspiele",
    "game.favorites": "Lieblingsspiele",
    "game.no_favorites": "Markiere ein Spiel als Favorit",
    "game.wyr.allDone": "Alle Fragen fertig!",
    "game.wyr.questionsAnswered": "Fragen beantwortet",
    "game.wyr.title": "Lieber Das",
    "game.wyr.wouldYouRather": "Würdest du lieber...",
    "game.wyr.or": "oder",
    "game.wyr.done": "Fertig!",
    "game.recommended.title": "Für dich empfohlen",
    "game.weekly.title": "Wöchentliche Herausforderungen",
    "game.weekly.done": "Erledigt",
    "game.bingo.found": "gefunden",
    "game.bingo.subtitle": "Finde Dinge unterwegs",
    "game.bingo.spotted": "Entdeckt!",
    "game.bingo.instructions": "Tippe auf Dinge die du unterwegs siehst. Bingo = Reihe, Spalte oder Diagonale!",
    "game.guess.finished": "Ort erraten fertig!",
    "game.guess.fewerClues": "Weniger Hinweise = mehr Punkte!",
    "game.guess.title": "Errate den Ort",
    "game.guess.place": "Ort",
    "game.guess.clues": "Hinweise",
    "game.guess.showNext": "Nächsten Hinweis zeigen",
    "game.guess.placeholder": "Antwort eingeben...",
    "game.guess.guess": "Raten",
    "game.guess.correct": "Richtig!",
    "game.guess.wrong": "Falsch!",
    "game.guess.correctAnswer": "Richtige Antwort",
    "game.guess.nextPlace": "Nächster Ort",
    "game.playAgain": "Nochmal spielen",
    "game.evening.finished": "Abendspiel fertig!",
    "game.evening.wellDone": "Toll gemacht mit der ganzen Gruppe!",
    "game.evening.round": "Runde",
    "game.evening.done": "Fertig!",
    "game.evening.nextRound": "Nächste Runde",
    "game.evening.trivia": "Trivia",
    "game.evening.creative": "Kreativ",
    "game.evening.debate": "Debatte",
    "game.evening.memory": "Gedächtnis",
    "game.evening.drawing": "Zeichnen",
    "game.checkpoint.finished": "Checkpoint geschafft!",
    "game.checkpoint.challengesDone": "Aufgaben erledigt",
    "game.checkpoint.newChallenges": "Neue Aufgaben",
    "game.checkpoint.subtitle": "Erkunde den Ort mit Aufgaben",
    "game.checkpoint.finish": "Checkpoint beenden",
    "game.safety.warning": "Verkehrssicherheit geht immer vor. Niemals gleichzeitig fahren und spielen.",
    "game.safety.cancel": "Abbrechen",
    "game.leaderboard.title": "Bestenliste – diese Woche",
    "game.leaderboard.games": "Spiele",
    "game.leaderboard.you": "Du",
    "game.leaderboard.yourPosition": "Deine Position",
    "game.history.empty": "Noch keine Spiele gespielt",
    "game.history.emptyDesc": "Starte dein erstes Spiel und sieh hier deine Geschichte!",
    "game.history.played": "Gespielt",
    "game.history.gameTypes": "Spieltypen",
    "game.history.countries": "Länder",
    "game.history.avgScore": "Durchschnittliche Punktzahl",
    "game.history.inProgress": "Laufend",
    "game.history.continue": "Weiterspielen",
    "game.badges.title": "Abzeichen",
    "game.quiz.fast": "Schnell",
    "game.quiz.perfect": "Perfekt!",
    "game.audience.label": "Wer spielt?",
    "game.audience.kids": "Kinder",
    "game.audience.kids_desc": "Lustige und einfache Spiele für Kinder",
    "game.audience.family": "Familie",
    "game.audience.family_desc": "Spiele für die ganze Familie",
    "game.audience.adult": "Erwachsene",
    "game.audience.adult_desc": "Spiele für Erwachsene",
    "game.audience.couple": "Paar",
    "game.audience.couple_desc": "Romantische Spiele zu zweit",
    "game.audience.friends": "Freunde",
    "game.audience.friends_desc": "Spaß und Wettbewerb mit Freunden",
    "game.daily.title": "Tägliche & wöchentliche Aufgaben",
    "game.daily.today": "Heute",
    "game.daily.this_week": "Diese Woche",
    "game.daily.this_month": "Dieser Monat",
    "game.daily.quiz_challenge": "Tägliche Quiz-Aufgabe",
    "game.daily.quiz_challenge_desc": "7+ beim heutigen Quiz",
    "game.daily.bingo_quick": "Schnell-Bingo",
    "game.daily.bingo_quick_desc": "Finde 5 Dinge auf dem Bingobrett",
    "game.daily.guess_master": "Ratemeister",
    "game.daily.guess_master_desc": "3 Orte richtig erraten",
    "game.daily.scenario_star": "Szenario-Star",
    "game.daily.scenario_star_desc": "Eine Szenario-Runde abschließen",
    "game.daily.photo_snap": "Foto-Schnappschuss",
    "game.daily.photo_snap_desc": "3 Foto-Aufgaben erledigen",
    "game.daily.story_teller": "Geschichtenerzähler",
    "game.daily.story_teller_desc": "Eine Geschichte abschließen",
    "game.daily.trivia_duel": "Trivia-Duell",
    "game.daily.trivia_duel_desc": "5+ im Trivia Battle",
    "game.weekly.quiz_master": "Wöchentlicher Quizmeister",
    "game.weekly.quiz_master_desc": "9+ bei einem Quiz diese Woche",
    "game.weekly.bingo_champion": "Bingo-Champion",
    "game.weekly.bingo_champion_desc": "Ein volles Bingobrett",
    "game.weekly.explorer": "Wöchentlicher Entdecker",
    "game.weekly.explorer_desc": "4 Checkpoint-Aufgaben abschließen",
    "game.weekly.photo_pro": "Foto-Profi",
    "game.weekly.photo_pro_desc": "6 Foto-Aufgaben erledigen",
    "game.weekly.evening_host": "Abend-Gastgeber",
    "game.weekly.evening_host_desc": "Ein Abendspiel veranstalten",
    "game.monthly.country_champion": "Landes-Champion",
    "game.monthly.country_champion_desc": "Perfekte Punktzahl bei einem Länder-Quiz",
    "game.monthly.all_rounder": "Allrounder",
    "game.monthly.all_rounder_desc": "5 verschiedene Spieltypen spielen",
    "game.monthly.road_legend": "Straßen-Legende",
    "game.monthly.road_legend_desc": "3 Geschichten abschließen",
    "game.season.spring": "Frühlingsspiele",
    "game.season.summer": "Sommerspiele",
    "game.season.autumn": "Herbstspiele",
    "game.season.winter": "Winterspiele",
    "game.seasonal.limited": "Saison",
    "game.seasonal.spring_awakening": "Frühlings-Quiz",
    "game.seasonal.spring_awakening_desc": "Teste dein Frühlingswissen",
    "game.seasonal.easter_trip": "Oster-Roadtrip Bingo",
    "game.seasonal.easter_trip_desc": "Finde Ostern unterwegs",
    "game.seasonal.spring_photo": "Frühlings-Foto-Aufgabe",
    "game.seasonal.spring_photo_desc": "Fange die Schönheit des Frühlings ein",
    "game.seasonal.summer_roadtrip": "Sommer-Roadtrip Quiz",
    "game.seasonal.summer_roadtrip_desc": "Sonne, Strände und Sommerstraßen",
    "game.seasonal.beach_bingo": "Strand & Küsten-Bingo",
    "game.seasonal.beach_bingo_desc": "Finde Sommerdinge an der Küste",
    "game.seasonal.midsummer": "Mittsommer-Aufgabe",
    "game.seasonal.midsummer_desc": "Feier Mittsommer mit Aufgaben",
    "game.seasonal.camping_nights": "Sommer-Campingabende",
    "game.seasonal.camping_nights_desc": "Perfekt für warme Campingabende",
    "game.seasonal.autumn_colors": "Herbstfarben-Foto",
    "game.seasonal.autumn_colors_desc": "Fange die goldenen Herbstfarben ein",
    "game.seasonal.harvest_quiz": "Erntezeit-Quiz",
    "game.seasonal.harvest_quiz_desc": "Quiz über Herbst und Ernte",
    "game.seasonal.fog_mystery": "Nebel-Mysterium",
    "game.seasonal.fog_mystery_desc": "Ein mysteriöses Herbstabenteuer",
    "game.seasonal.winter_roads": "Winterstraßen-Quiz",
    "game.seasonal.winter_roads_desc": "Teste dein Winterfahrwissen",
    "game.seasonal.christmas_markets": "Weihnachtsmarkt-Entdecker",
    "game.seasonal.christmas_markets_desc": "Errate berühmte Weihnachtsmärkte",
    "game.seasonal.cozy_evening": "Gemütlicher Winterabend",
    "game.seasonal.cozy_evening_desc": "Perfektes Spiel für kalte Abende",
    "game.seasonal.northern_lights": "Nordlicht-Geschichte",
    "game.seasonal.northern_lights_desc": "Ein Abenteuer unter dem Nordlicht",
    "game.level.label": "Stufe",
    "game.level.beginner": "Anfänger",
    "game.level.explorer": "Entdecker",
    "game.level.adventurer": "Abenteurer",
    "game.level.traveler": "Reisender",
    "game.level.road_master": "Straßenmeister",
    "game.level.pro": "Roadtrip Pro",
    "game.level.navigator": "Navigator",
    "game.level.euro_champ": "Europa-Champion",
    "game.level.legendary": "Legendär",
    "game.level.legend": "Roadtrip Legende",
    "game.level.next": "Nächste",
    "game.level.games_played": "gespielt",
    "game.collectibles.title": "Ländersammlung",
    "game.collectibles.countries": "Länder",
    "country.sweden": "Schweden",
    "country.norway": "Norwegen",
    "country.germany": "Deutschland",
    "country.italy": "Italien",
    "country.france": "Frankreich",
    "country.spain": "Spanien",
    "game.region.norrland": "Norrland",
    "game.region.svealand": "Svealand",
    "game.region.gotaland": "Götaland",
    "game.region.fjords": "Fjorde",
    "game.region.nordnorge": "Nordnorwegen",
    "game.region.sorlandet": "Südnorwegen",
    "game.region.bayern": "Bayern",
    "game.region.norddeutschland": "Norddeutschland",
    "game.region.rheinland": "Rheinland",
    "game.region.norditalien": "Norditalien",
    "game.region.toscana": "Toskana",
    "game.region.syditalien": "Süditalien",
    "game.region.ildefrance": "Île-de-France",
    "game.region.provence": "Provence",
    "game.region.bretagne": "Bretagne",
    "game.region.andalucia": "Andalusien",
    "game.region.catalonia": "Katalonien",
    "game.region.nordspanien": "Nordspanien",
    "nav.compare": "Campingplätze vergleichen",
    "nav.contact": "Kontakt",
    "search.startTyping": "Tippen Sie, um zu suchen",
    "search.startTypingDesc": "Suchen Sie nach Ortsnamen, Städten, Kategorien oder Reisezielen",
    "search.clickForDetails": "Klicken Sie auf eine Karte für Details",
    "search.searchHint": "Suche nach Name, Kategorie, Stadt oder Region. Smarte Suche findet den richtigen Ort für Sie.",
    "search.foundOnGoogle": "weitere Vorschläge von Google",
    "search.addHint": "Tippen Sie auf eine Karte, um sie zu Vantastika hinzuzufügen",
    "search.fromGoogle": "Von Google",
    "search.addToVantastika": "Zu Vantastika hinzufügen",
    "search.googleAdding": "Wird hinzugefügt...",
    "search.googleAddedShort": "Zur Prüfung eingereicht",
    "search.googleAdded": "Ort eingereicht",
    "search.googleAddedDesc": "Danke! Er wird nach Admin-Prüfung sichtbar.",
    "search.googleAlreadyExists": "Bereits in Vantastika",
    "search.googleAlreadyExistsDesc": "Wir leiten Sie zur bestehenden Seite weiter.",
    "search.googleAddError": "Ort konnte nicht hinzugefügt werden. Erneut versuchen.",
    "search.nearbyFound": "Campingplätze in der Nähe",
    "search.within25km": "innerhalb 25 km",
    "search.noNearby": "Keine Campingplätze in der Nähe",
    "search.noNearbyDesc": "Probiere die Karte, um weiter weg zu suchen.",
    "search.openMap": "Karte öffnen",
    "search.findingLocation": "Standort wird ermittelt...",
    "search.findingLocationDesc": "Wir nutzen ihn, um Campingplätze in der Nähe anzuzeigen.",
    "search.locationDeniedHint": "Standortzugriff verweigert. Erlauben Sie ihn, um Campingplätze nah zu sehen.",
    "search.enableLocationHint": "Standortzugriff erlauben, um nächste Campingplätze zu sehen.",
    "search.allowLocation": "Standort erlauben",
    "search.topRated": "Beste Campingplätze",
    "hero.valueProps": "Alles an einem Ort – kein Wechseln zwischen Apps mehr",
    "hero.diffTitle": "Warum Vantastika?",
    "hero.diff1": "Reiseplanung, Stopps und Budget in einem Ablauf",
    "hero.diff2": "Belege scannen und Ausgaben automatisch verfolgen",
    "hero.diff3": "Stopps direkt von der Karte hinzufügen",
    "hero.diff4": "Für Wohnmobile, Autos und Roadtrips",
    "features.budget": "Budget verfolgen",
    "features.budgetDesc": "Ausgaben protokollieren, Belege scannen und sehen, wohin das Geld fließt",
    "trip.stopType": "Stopp-Typ",
    "trip.stopTypeVisit": "Kurzer Besuch",
    "trip.stopTypeOvernight": "Übernachtung",
    "trip.stopTypeDestination": "Endziel",
    "trip.nightsCount": "Nächte",
    "trip.editStop": "Stopp bearbeiten",
    "trip.optimizeRoute": "Route optimieren",
    "trip.outboundLeg": "Hinfahrt",
    "trip.returnLeg": "Rückfahrt",
    "trip.returnLegDesc": "Rückreise nach Hause",
    "trip.addReturnStop": "Stopp auf der Rückfahrt hinzufügen",
    "trip.optimizeReturn": "Rückfahrt optimieren",
    "trip.returnOptimized": "Rückfahrt optimiert!",
    "trip.noReturnStops": "Noch keine Rückfahrt-Stopps. Fügen Sie Stopps für Ihre Heimreise hinzu.",
    "trip.markAsReturn": "Als Rückfahrt markieren",
    "trip.markAsOutbound": "Als Hinfahrt markieren",
    "trip.routeOptimized": "Route optimiert!",
    "optimize.title": "Reihenfolge der Route optimieren",
    "optimize.description": "Vergleichen Sie verschiedene Routenalternativen und wählen Sie diejenige, die am besten zu Ihrer Reise passt.",
    "optimize.analyzing": "Analysiere Routenalternativen...",
    "optimize.currentOrder": "Aktuelle Reihenfolge",
    "optimize.currentDesc": "Ihre aktuelle Stopp-Reihenfolge wie geplant.",
    "optimize.geographic": "Logischste Route",
    "optimize.geographicDesc": "Stopps sortiert für einen natürlichen geografischen Fluss zum Ziel.",
    "optimize.shortest": "Kürzeste Route",
    "optimize.shortestDesc": "Nächstgelegene Sortierung zur Minimierung der gesamten Fahrstrecke.",
    "optimize.dayOptimized": "Tagesausflug-optimiert",
    "optimize.dayOptimizedDesc": "Organisiert um Übernachtungsstopps für logische Tagesetappen.",
    "optimize.recommended": "Empfohlen",
    "optimize.stopOrder": "Stopp-Reihenfolge",
    "optimize.start": "Start",
    "optimize.end": "Ziel",
    "optimize.overnights": "Übernachtungen",
    "optimize.shorter": "kürzer",
    "optimize.longer": "länger",
    "optimize.saves": "spart",
    "optimize.useThisRoute": "Diese Route verwenden",
    "optimize.cancel": "Abbrechen",
    "tip.routeHighlights": "Tipps entlang dieser Route",
    "tip.routeHighlightsDesc": "Empfohlene Orte und Highlights für Ihre Reise",
    "tip.openInMaps": "In Karten öffnen",
    "tip.saveMoney": "Geld sparen",
    "tip.fuelEstimate": "Kraftstoffkosten",
    "tip.campingCost": "Camping",
    "tip.night": "Nacht",
    "tip.yourDescription": "Ihre Beschreibung (optional)",
    "tip.yourDescriptionPlaceholder": "Teilen Sie mit, was diese Route besonders macht, Tipps für andere oder warum sie Ihnen gefallen hat...",
    "submit.photos": "Fotos",
    "submit.photosDesc": "Laden Sie bis zu 5 Fotos hoch",
    "wizard.whenTravel": "Wann reisen Sie?",
    "wizard.saving": "Speichere...",
    "budget.title": "Reisebudget",
    "budget.tab": "Budget",
    "budget.setBudget": "Budget festlegen",
    "budget.totalBudget": "Gesamtbudget (kr)",
    "budget.spent": "Ausgegeben",
    "budget.remaining": "Verbleibend",
    "budget.used": "verwendet",
    "budget.topCategory": "Größte Kategorie",
    "budget.addExpense": "Ausgabe hinzufügen",
    "budget.amount": "Betrag",
    "budget.description": "Beschreibung (optional)",
    "budget.save": "Speichern",
    "budget.cancel": "Abbrechen",
    "budget.expenses": "Ausgaben",
    "budget.breakdown": "Kategorieaufschlüsselung",
    "budget.expenseAdded": "Ausgabe hinzugefügt!",
    "budget.expenseUpdated": "Ausgabe aktualisiert!",
    "budget.expenseFailed": "Konnte Ausgabe nicht speichern",
    "budget.autoLabel": "Automatisch generiert",
    "budget.editFuelCalc": "Bearbeiten",
    "budget.fuelCalcTitle": "Kraftstoffkostenberechnung",
    "budget.fuelPriceLabel": "Preis/Liter",
    "budget.fuelConsumptionLabel": "l/100km",
    "budget.fuelDistanceLabel": "Distanz (km)",
    "budget.fuelCalcResult": "Geschätzte Kraftstoffkosten:",
    "budget.fuelSave": "Kraftstoffkosten aktualisieren",
    "budget.fuelUpdated": "Kraftstoffkosten aktualisiert!",
    "budget.budgetSaved": "Budget gespeichert!",
    "budget.getAiTips": "Holen Sie sich Budget-Tipps von Vannie",
    "budget.aiTipsTitle": "Vannies Budget-Tipps",
    "budget.noTips": "Im Moment sind keine Tipps verfügbar.",
    "budget.tipsFailed": "Konnte Tipps nicht laden",
    "budget.close": "Schließen",
    "budget.cat.fuel": "Kraftstoff",
    "budget.cat.camping": "Camping/Stellplatz",
    "budget.cat.food": "Essen & Trinken",
    "budget.cat.tolls": "Maut/Fähren",
    "budget.cat.activities": "Aktivitäten",
    "budget.cat.shopping": "Einkaufen",
    "budget.cat.other": "Sonstiges",
    "budget.budgetLabel": "Budget",
    "budget.edit": "Bearbeiten",
    "budget.backToTrip": "Zurück zur Reise",
    "budget.estimatedFuel": "Geschätzter Kraftstoff",
    "budget.estimateOnly": "Nur Schätzung",
    "budget.autoCosts": "Automatische Kosten",
    "budget.manualExpenses": "Ihre Ausgaben",
    "budget.pageDesc": "Verwalten Sie Ihre Budgets und Ausgaben",
    "budget.loginRequired": "Melden Sie sich an, um die Budget-Funktion zu nutzen",
    "budget.subtitle": "Ihre Budgets und Ausgaben",
    "budget.pageSubtitle": "Planen Sie Ihr Reisebudget, fügen Sie Ausgaben hinzu und verfolgen Sie, wie viel übrig ist.",
    "budget.newBudget": "Neues Budget",
    "budget.newExpense": "Neue Ausgabe",
    "budget.tabActive": "Aktiv",
    "budget.tabAll": "Alle",
    "budget.tabExpenses": "Nicht verknüpfte Ausgaben",
    "budget.tabUnlinked": "Nicht verknüpft",
    "budget.emptyTitle": "Noch keine Budgets",
    "budget.emptyDesc": "Erstellen Sie Ihr erstes Budget, um eine bessere Übersicht über Ihre Reisekosten zu erhalten.",
    "budget.emptyStateTitle": "Noch kein Budget erstellt",
    "budget.emptyStateDesc": "Erstellen Sie Ihr erstes Reisebudget, um Kosten für Unterkunft, Essen, Kraftstoff und Aktivitäten besser im Blick zu behalten.",
    "budget.createBudget": "Budget erstellen",
    "budget.createFirstBudget": "Erstes Budget erstellen",
    "budget.chipAccommodation": "Unterkunft",
    "budget.chipFood": "Essen",
    "budget.chipFuel": "Kraftstoff",
    "budget.chipActivities": "Aktivitäten",
    "budget.chipShopping": "Einkaufen",
    "budget.unlinkedLabel": "Nicht verknüpfte Ausgaben",
    "budget.unlinkedHint": "Diese Ausgaben sind mit keinem Budget verknüpft.",
    "budget.more": "mehr",
    "budget.noActiveTitle": "Keine aktiven Budgets",
    "budget.noActiveDesc": "Erstellen Sie ein Budget oder aktivieren Sie ein bestehendes.",
    "budget.overviewTitle": "Budgetübersicht",
    "budget.statusGood": "Im Plan",
    "budget.statusWarning": "Nähert sich dem Limit",
    "budget.statusOver": "Über Budget",
    "budget.pctUsed": "verwendet",
    "budget.totalBudgetCard": "Gesamtbudget",
    "budget.spentCard": "Ausgegeben",
    "budget.remainingCard": "Übrig",
    "budget.paused": "Pausiert",
    "budget.completed": "abgeschlossen",
    "budget.noExpenses": "Keine nicht verknüpften Ausgaben",
    "budget.noExpensesDesc": "Ausgaben ohne verknüpftes Budget werden hier angezeigt",
    "budget.setBudgetTitle": "Monatliches Budget",
    "budget.setBudgetBtn": "Budget festlegen",
    "budget.setBudgetSaved": "Budgetlimit gespeichert ✓",
    "budget.month": "Monat",
    "budget.budgetAmount": "Budget",
    "budget.amountPlaceholder": "z.B. 15000",
    "budget.saving": "Speichere...",
    "budget.saveBudgetLimit": "Budgetlimit speichern",
    "budget.createBudgetTitle": "Budget erstellen",
    "budget.budgetName": "Budgetname",
    "budget.budgetNamePlaceholder": "z.B. Sommerurlaub 2026",
    "budget.budgetTypeLabel": "Budgettyp",
    "budget.totalBudgetLabel": "Gesamtbudget",
    "budget.currencyLabel": "Währung",
    "budget.startDateLabel": "Startdatum",
    "budget.endDateLabel": "Enddatum",
    "budget.colorLabel": "Farbe",
    "budget.selectColor": "Farbe auswählen",
    "budget.descriptionLabel": "Beschreibung",
    "budget.descriptionPlaceholder": "Optionale Notiz...",
    "budget.linkTrip": "Mit Reise verknüpfen (optional)",
    "budget.noTripSelected": "Keine Reise ausgewählt",
    "budget.noTrip": "Keine Reise",
    "budget.fillNameAndAmount": "Name und gültigen Betrag eingeben",
    "budget.budgetCreated": "Budget erstellt ✓",
    "budget.couldNotSave": "Konnte nicht gespeichert werden",
    "budget.creating": "Erstelle...",
    "budget.addExpenseTitle": "Ausgabe hinzufügen",
    "budget.scanWithAi": "Beleg mit KI scannen",
    "budget.amountLabel": "Betrag",
    "budget.dateLabel": "Datum",
    "budget.titleLabel": "Titel",
    "budget.titlePlaceholder": "Was hast du gekauft?",
    "budget.categoryLabel": "Kategorie",
    "budget.paymentMethod": "Zahlungsmethode",
    "budget.selectPayment": "Auswählen...",
    "budget.linkBudget": "Mit Budget verknüpfen (optional)",
    "budget.linkBudgetDirect": "Zu Budget verknüpfen",
    "budget.noBudgetSelected": "Kein Budget ausgewählt",
    "budget.noBudget": "Kein Budget",
    "budget.noteLabel": "Notiz",
    "budget.notePlaceholder": "Optionale Notiz...",
    "budget.receiptLabel": "Beleg",
    "budget.receiptRemove": "Entfernen",
    "budget.receiptUpload": "Foto machen oder Beleg hochladen",
    "budget.fileTooLarge": "Datei zu groß",
    "budget.fileTooLargeDesc": "Max. 15 MB",
    "budget.fillAmountAndTitle": "Gültigen Betrag und Titel eingeben",
    "budget.expenseSaved": "Ausgabe gespeichert ✓",
    "budget.converted": "Konvertiert:",
    "budget.editBudgetTitle": "Budget bearbeiten",
    "budget.budgetUpdated": "Budget aktualisiert ✓",
    "budget.saveChanges": "Änderungen speichern",
    "budget.editExpenseTitle": "Ausgabe bearbeiten",
    "budget.scanTitle": "Intelligenter Belegscan",
    "budget.scanCapture": "Foto machen oder Beleg hochladen",
    "budget.scanCaptureDesc": "KI analysiert deinen Beleg Zeile für Zeile",
    "budget.scanTakePhoto": "Foto machen",
    "budget.scanUpload": "Hochladen",
    "budget.scanAnalyzing": "Analysiere Beleg...",
    "budget.scanAnalyzingDesc": "KI liest Artikel, Beträge und Kategorien",
    "budget.scanFailed": "Beleg konnte nicht analysiert werden",
    "budget.scanConfidenceHigh": "Hohe Sicherheit",
    "budget.scanConfidenceMedium": "Mittlere Sicherheit — Felder prüfen",
    "budget.scanConfidenceLow": "Niedrige Sicherheit — alle Felder prüfen",
    "budget.scanCandidates": "Mögliche Beträge:",
    "budget.scanItemsPerCategory": "Artikel pro Kategorie",
    "budget.scanMixedCategories": "Gemischte Kategorien",
    "budget.scanSaveHow": "Wie möchtest du speichern?",
    "budget.scanSingleExpense": "Eine Ausgabe",
    "budget.scanSplitExpenses": "Aufteilen",
    "budget.scanSplitDesc": "Erstellt {n} separate Ausgaben — eine pro Kategorie.",
    "budget.scanMainCategory": "Hauptkategorie",
    "budget.scanShowRaw": "Rohtext des Belegs anzeigen",
    "budget.scanHideRaw": "Rohtext des Belegs ausblenden",
    "budget.scanCancel": "Abbrechen",
    "budget.scanSaveExpense": "Ausgabe speichern",
    "budget.scanSaveExpenses": "{n} Ausgaben speichern",
    "budget.scanSavedMultiple": "{n} Ausgaben vom Beleg gespeichert ✓",
    "budget.scanSavedSingle": "Ausgabe vom Beleg gespeichert ✓",
    "budget.confidenceMedium": "Mittlere Sicherheit",
    "budget.confidenceLow": "Unsicher — bitte prüfen",
    "budget.statusPaused": "Pausiert",
    "budget.statusCompleted": "Abgeschlossen",
    "budget.budgetLabel2": "Budget",
    "budget.spentLabel": "Ausgegeben",
    "budget.remainingLabel": "Übrig",
    "budget.overBy": "Über budget um",
    "budget.usedPercent": "genutzt",
    "budget.expenseCount": "Ausgaben",
    "budget.mixedCurrenciesNote": "Ausgaben in {currencies} — konvertiert zu {target}",
    "budget.deleteConfirm": "Möchtest du dieses Budget wirklich löschen?",
    "budget.statusActivated": "Budget aktiviert",
    "budget.statusPausedToast": "Budget pausiert",
    "budget.statusCompletedToast": "Budget abgeschlossen",
    "budget.perCategory": "Pro Kategorie",
    "budget.biggestExpense": "Größte Ausgabe",
    "budget.expenseListTitle": "Ausgaben",
    "budget.noExpensesYet": "Noch keine Ausgaben",
    "budget.noExpensesYetDesc": "Tippe auf „Ausgabe hinzufügen“, um loszulegen",
    "budget.totalSpent": "Gesamtausgaben",
    "budget.thisMonth": "Diesen Monat",
    "budget.budgeted": "Budgetiert",
    "budget.warnings": "Warnungen",
    "summary.title": "Reisekostenübersicht",
    "summary.titleOngoing": "Kostenübersicht",
    "summary.over": "Budget überschritten",
    "summary.saved": "Gespart",
    "summary.dailyAvg": "Pro Tag",
    "summary.overBudget": "Du hast das Budget für diese Reise überschritten",
    "summary.underBudget": "Du bist im Budget geblieben — super gemacht!",
    "summary.breakdown": "Ausgabenaufschlüsselung",
    "summary.categoryDetails": "Kategorie-Details",
    "fuelLog.title": "Kraftstoffprotokoll",
    "fuelLog.addFuel": "Betankung hinzufügen",
    "fuelLog.liters": "Liter",
    "fuelLog.cost": "Kosten",
    "fuelLog.date": "Datum",
    "fuelLog.odometer": "Kilometerstand (km)",
    "fuelLog.note": "Notiz",
    "fuelLog.optional": "Optional",
    "fuelLog.save": "Speichern",
    "fuelLog.added": "Betankung protokolliert!",
    "fuelLog.addFailed": "Betankung konnte nicht gespeichert werden",
    "fuelLog.invalidInput": "Bitte Liter und Kosten eingeben",
    "fuelSummary.title": "Kraftstoffzusammenfassung",
    "fuelSummary.totalLiters": "Gesamtliter",
    "fuelSummary.totalCost": "Kraftstoffkosten",
    "fuelSummary.avgPrice": "Durchschnittspreis/Liter",
    "fuelSummary.consumption": "Verbrauch",
    "fuelSummary.costPerKm": "Kosten/km",
    "fuelSummary.costPerMil": "Kosten/Meile",
    "fuelSummary.noDistance": "Füge eine Routenentfernung hinzu, um Verbrauch und Kosten pro km anzuzeigen.",
    "fuelSummary.basedOn": "Basierend auf {n} Betankung(en)",
    "checklist.tab": "Checkliste",
    "journal.tab": "Tagebuch",
    "authGate.ctaSection": "Alles, was du für deine Reise brauchst",
    "authGate.ctaButton": "Konto erstellen und speichern",
    "authGate.saveBudgetMsg": "Speichere dein Budget und verfolge Ausgaben",
    "authGate.saveReceiptMsg": "Bewahre deine Belege auf allen Geräten auf",
    "authGate.saveFavoriteMsg": "Speichere deine Lieblingsorte in einer Liste",
    "authGate.useBudgetMsg": "Verfolge dein Budget — erstelle ein kostenloses Konto",
    "authGate.useChecklistMsg": "Nutze Checklisten — erstelle ein kostenloses Konto",
    "gate.whyJoin": "WARUM EIN KONTO ERSTELLEN?",
    "gate.benefit1": "Speichere deine Reise, Stopps und dein Budget",
    "gate.benefit2": "Plane von jedem Gerät aus weiter",
    "gate.benefit3": "Vollständige Budgetverfolgung mit Belegscan",
    "gate.benefit4": "KI-Reiseassistent kostenlos inklusive",
    "gate.benefit5": "Zugriff auf Community-Tipps und Bewertungen",
    "gate.benefit6": "Checklisten, die auf dein Fahrzeug zugeschnitten sind",
    "welcome.title": "Willkommen bei Vantastika!",
    "welcome.titleName": "Willkommen, {name}!",
    "welcome.desc": "Alles, was du zum Planen, Reisen und Erinnern deiner Reisen brauchst, ist genau hier.",
    "welcome.feature1": "Plane Routen, füge Stopps hinzu und verfolge Budget und Kraftstoff",
    "welcome.feature2": "Vannie, dein KI-Reiseassistent, hilft dir, die besten Routen zu finden",
    "welcome.feature3": "Speichere Lieblingsorte, teile Reisen mit Freunden",
    "welcome.cta": "Plane meine erste Reise",
    "welcome.dismiss": "Alleine erkunden",
    "welcome.close": "Schließen",
    "gate.noCard": "Kostenlos starten. Keine Kreditkarte erforderlich.",
    "gate.createAccount": "Kostenloses Konto erstellen",
    "gate.alreadyMember": "Hast du schon ein Konto? Anmelden",
    "gate.seeMore": "Mehr sehen?",
    "gate.registerToSee": "Erstelle ein kostenloses Konto, um auf alle Funktionen und Orte zuzugreifen.",
    "gate.searchTitle": "Alle Campingplätze entdecken",
    "gate.searchDesc": "Registriere dich kostenlos, um alle geprüften Orte zu durchsuchen, deine Favoriten zu speichern und Bewertungen aus der Community zu lesen.",
    "gate.servicesTitle": "Service & Notfall",
    "gate.servicesDesc": "Erstelle ein Konto, um Werkstätten, Ladestationen, Wasserstellen und Notdienste in deiner Nähe zu finden.",
    "gate.tripsTitle": "Reiseplaner",
    "gate.tripsDesc": "Registriere dich kostenlos, um unbegrenzte Reisen mit intelligenten Routen, Kraftstoffkosten und Budgetverfolgung zu planen.",
    "gate.submitTitle": "Ort hinzufügen",
    "gate.submitDesc": "Registriere dich kostenlos, um deine Lieblingsorte mit der Community zu teilen.",
    "gate.tipsTitle": "Reisetipps",
    "gate.tipsDesc": "Erstelle ein Konto, um Reise-Tipps von echten Reisenden zu lesen und zu teilen.",
    "roadTips.title": "Straßen-Tipps",
    "roadTips.share": "Tipp teilen",
    "roadTips.allCountries": "Alle Länder",
    "roadTips.formTitle": "Teile deinen Reisetipp",
    "roadTips.labelTitle": "Titel *",
    "roadTips.placeholderTitle": "z.B. Beste Route durch die Alpen",
    "roadTips.labelType": "Typ",
    "roadTips.labelCountry": "Land",
    "roadTips.select": "Auswählen",
    "roadTips.labelTip": "Dein Tipp *",
    "roadTips.placeholderTip": "Teile deine Erfahrung...",
    "roadTips.labelImage": "Bild-URL (optional)",
    "roadTips.posting": "Wird gepostet...",
    "roadTips.post": "Tipp posten",
    "roadTips.shared": "Tipp geteilt!",
    "roadTips.loginToShare": "Melde dich an, um eigene Reisetipps zu teilen.",
    "roadTips.noTips": "Noch keine Tipps. Sei der Erste!",
    "roadTips.type.general": "Allgemeiner Tipp",
    "roadTips.type.route": "Routenempfehlung",
    "roadTips.type.parking": "Parktipp",
    "roadTips.type.food": "Essen & Shopping",
    "roadTips.type.warning": "Warnung / Achtung",
    "roadTips.type.service": "Servicepunkt",
    "community.title": "Community",
    "community.subtitle": "Teile Erlebnisse, Tipps und Inspiration mit anderen Reisenden",
    "community.newPost": "Neuer Beitrag",
    "community.createPost": "Neuen Beitrag erstellen",
    "community.all": "Alle",
    "community.anonymous": "Anonym",
    "community.noPostsYet": "Noch keine Beiträge",
    "community.beFirst": "Sei der Erste, der eine Diskussion startet!",
    "community.loadMore": "Mehr Beiträge laden",
    "community.loading": "Wird geladen…",
    "community.createFirst": "Ersten Beitrag erstellen",
    "community.back": "Zurück",
    "community.comments": "Kommentare",
    "community.noComments": "Noch keine Kommentare — sei der Erste!",
    "community.commentPlaceholder": "Schreibe einen Kommentar...",
    "community.titlePlaceholder": "Titel",
    "community.contentPlaceholder": "Worüber möchtest du diskutieren?",
    "community.addImage": "Bild hinzufügen (optional)",
    "community.publishing": "Wird veröffentlicht...",
    "community.publish": "Beitrag veröffentlichen",
    "community.toast.postCreated": "Beitrag erstellt!",
    "community.toast.postPublished": "Dein Beitrag wurde veröffentlicht.",
    "community.toast.commentError": "Kommentar konnte nicht erstellt werden.",
    "community.toast.postDeleted": "Beitrag gelöscht",
    "community.toast.error": "Fehler",
    "community.confirmDelete": "Bist du sicher, dass du diesen Beitrag löschen möchtest?",
    "community.cat.general": "Allgemein",
    "community.cat.tips": "Tipps & Tricks",
    "community.cat.routes": "Routen & Orte",
    "community.cat.gear": "Ausrüstung",
    "community.cat.help": "Hilfe & Fragen",
    "community.cat.photos": "Fotos & Reiseberichte",
    "community.type.question": "Frage",
    "community.type.tip": "Tipp",
    "community.type.discussion": "Diskussion",
    "community.type.routes": "Routen & Orte",
    "community.type.gear": "Ausrüstung",
    "community.type.photos": "Fotos",
    "community.type.help": "Hilfe",
    "community.type.question.desc": "Stelle der Community eine Frage",
    "community.type.tip.desc": "Teile einen guten Tipp",
    "community.type.discussion.desc": "Starte eine Diskussion",
    "community.type.routes.desc": "Teile Lieblingsorte",
    "community.type.gear.desc": "Ausrüstung & Zubehör",
    "community.type.photos.desc": "Teile deine Reise",
    "community.type.help.desc": "Bitte um Hilfe",
    "community.awaitingAnswer": "Wartet auf Antwort",
    "community.selectType": "Beitragstyp wählen",
    "community.createPostTitle": "Beitrag erstellen",
    "community.changeType": "Typ ändern",
    "community.questionPlaceholder": "Schreibe deine Frage hier...",
    "community.questionContentPlaceholder": "Beschreibe deine Frage ausführlich, damit andere dir helfen können...",
    "community.communityRules": "Community-Regeln",
    "community.communityRulesText": "Du darfst keine Nacktbilder, sexuelle Inhalte, Werbung, Anzeigen, Verkauf, Spam, Drohungen oder Belästigungen veröffentlichen. Alle Beiträge und Bilder können vor der Veröffentlichung automatisch überprüft werden.",
    "games.seoTitle": "Roadtrip-Spiele – Vantastika",
    "games.seoDesc": "Spiele Quiz, Bingo und mehr während deines Roadtrips",
    "social.places": "Orte",
    "social.tripsPlanned": "Geplante Reisen",
    "social.members": "Mitglieder",
    "social.reviews": "Bewertungen",
    "social.popularRoutes": "Beliebte Routen von anderen Reisenden",
    "social.traveler": "Reisender",
    "social.stops": "Stopps",
    "error.somethingWrong": "Etwas ist schiefgelaufen",
    "error.autoRepairFailed": "Wir haben versucht, es automatisch zu reparieren, aber es hat nicht funktioniert. Versuche, die Seite neu zu laden.",
    "error.tryAgain": "Erneut versuchen",
    "error.reloadPage": "Seite neu laden",
    "a11y.decreaseText": "Textgröße verkleinern",
    "a11y.increaseText": "Textgröße vergrößern",
    "a11y.decreaseTooltip": "Kleinerer Text",
    "a11y.increaseTooltip": "Größerer Text",
    "aria.close": "Schließen",
    "aria.openVannie": "Vannie öffnen",
    "aria.previous": "Zurück",
    "aria.next": "Weiter",
    "aria.openMenu": "Menü öffnen",
    "aria.closeMenu": "Menü schließen",
    "aria.step": "Schritt",
    "onboarding.step": "Schritt {current} von {total}",
    "onboarding.back": "Zurück",
    "onboarding.skip": "Überspringen",
    "onboarding.planTrip": "Plane deine Reise",
    "onboarding.planTripDesc": "Sag uns, wohin du reisen möchtest, und wir helfen dir, deine Reise zu planen – Route, Stopps und Budget an einem Ort.",
    "onboarding.createFirstTrip": "Meine erste Reise erstellen",
    "onboarding.findPlaces": "Orte auf der Karte finden",
    "onboarding.findPlacesDesc": "Campingplätze, Sehenswürdigkeiten und Restaurants – füge Stopps direkt von der Karte zu deiner Reise hinzu.",
    "onboarding.searchCampings": "Karte erkunden",
    "onboarding.budgetChecklist": "Budget und Ausgaben verfolgen",
    "onboarding.budgetChecklistDesc": "Erfasse deine Ausgaben, scanne Belege und behalte deine Reiseausgaben im Auge – alles an einem Ort.",
    "onboarding.createTrip": "Mein Budget beginnen",
    "onboarding.askVannie": "Lerne Vannie kennen",
    "onboarding.askVannieDesc": "Dein persönlicher Reiseassistent. Erhalte Tipps, Routenvorschläge und lokale Regeln – kostenlos enthalten.",
    "onboarding.doneStart": "Los geht's!",
    "compare.title": "Campingplätze vergleichen",
    "compare.subtitle": "Wähle bis zu {max} Campingplätze für einen Seitenvergleich aus.",
    "compare.add": "Hinzufügen",
    "compare.searchPlaceholder": "Campingplatz suchen...",
    "compare.property": "Eigenschaft",
    "compare.rating": "Bewertung",
    "compare.pricePerNight": "Preis/Nacht",
    "compare.free": "Kostenlos",
    "compare.type": "Typ",
    "compare.addMore": "Füge mindestens einen weiteren Campingplatz hinzu, um zu vergleichen.",
    "compare.selectCampings": "Campingplätze zum Vergleichen auswählen",
    "compare.clickAdd": "Klicke auf \"Hinzufügen\", um zu beginnen.",
    "compare.electricity": "Strom",
    "compare.freshWater": "Frischwasser",
    "compare.shower": "Dusche",
    "compare.pets": "Haustiere",
    "compare.greyWater": "Grauwasser",
    "compare.toiletCassette": "Toiletten-Kassette",
    "compare.wc": "WC",
    "compare.openAllYear": "Ganzjährig geöffnet",
    "compare.familyFriendly": "Familienfreundlich",
    "compare.largeMotorhomes": "Große Wohnmobile",
    "compare.nearWater": "In Wassernähe",
    "compare.spaWellness": "Spa/Wellness",
    "friends.nearby": "Reisegefährten in der Nähe",
    "friends.sharingOn": "Teilen aktiviert",
    "friends.sharingOff": "Teilen deaktiviert",
    "friends.enableSharing": "Aktiviere die Standortfreigabe, um Personen zu sehen, mit denen du Reisen teilst",
    "friends.onlyCollaborators": "Nur diejenigen, mit denen du an einer Reise zusammenarbeitest, können deine Position sehen",
    "friends.noOneSharing": "Derzeit teilt niemand seinen Standort mit Reisepartnern",
    "friends.anonymous": "Anonym",
    "friends.away": "entfernt",
    "friends.unknownDistance": "Unbekannte Entfernung",
    "spot.loginToNavigate": "Melde dich an, um die Navigation zu öffnen",
    "spot.navigateHere": "Hierhin navigieren",
    "incident.title": "Vorfall melden",
    "incident.button": "Vorfall melden",
    "incident.stepOf": "Schritt {current} von {total}",
    "incident.saving": "Speichere...",
    "incident.step1": "Art des Vorfalls",
    "incident.step2": "Zeit und Ort",
    "incident.step3": "Fahrzeugdetails",
    "incident.step4": "Was ist passiert",
    "incident.step5": "Andere Partei",
    "incident.step6": "Zusammenfassung & PDF",
    "incident.type.collision": "Kollision",
    "incident.type.theft": "Diebstahl",
    "incident.type.vandalism": "Vandalismus",
    "incident.type.breakdown": "Panne",
    "incident.type.weather": "Wetterschaden",
    "incident.type.other": "Sonstiges",
    "incident.date": "Datum",
    "incident.time": "Uhrzeit",
    "incident.location": "Ort",
    "incident.locationPlaceholder": "Adresse oder Ortsname",
    "incident.locationHint": "Sei so spezifisch wie möglich – Straße, Stadt, Land",
    "incident.vehicleType": "Fahrzeugtyp",
    "incident.vehicleTypePlaceholder": "z. B. Wohnmobil, Campervan, Auto",
    "incident.registration": "Kennzeichen",
    "incident.vehicleDesc": "Fahrzeugbeschreibung",
    "incident.vehicleDescPlaceholder": "Farbe, Marke, Modell, Baujahr",
    "incident.whatHappened": "Was ist passiert?",
    "incident.descriptionPlaceholder": "Beschreibe den Vorfall klar und ruhig",
    "incident.additionalNotes": "Zusätzliche Notizen",
    "incident.notesPlaceholder": "Zeugen, Wetter, Straßenverhältnisse...",
    "incident.otherPartyHint": "Ausfüllen, wenn eine andere Partei beteiligt war. Überspringen, wenn nicht zutreffend.",
    "incident.otherName": "Name",
    "incident.otherPhone": "Telefonnummer",
    "incident.otherVehicle": "Fahrzeug",
    "incident.otherReg": "Kennzeichen",
    "incident.otherInsurance": "Versicherungsgesellschaft",
    "incident.incidentType": "Art des Vorfalls",
    "incident.summary": "Zusammenfassung des Berichts",
    "incident.downloadPdf": "Speichern & PDF herunterladen",
    "incident.saveOnly": "Ohne PDF speichern",
    "incident.wizardTitle": "Schadensbericht",
    "incident.step0.title": "Wichtige Informationen",
    "incident.step0.heading": "Dies ist ein Schadensbericht — keine Schadenmeldung",
    "incident.step0.body1": "Mit diesem Werkzeug erstellst du einen strukturierten Bericht, der dir hilft, korrekte Informationen bei einem Vorfall zu sammeln.",
    "incident.step0.body2": "Du bist selbst dafür verantwortlich, den Bericht zu prüfen und an deine Versicherung zu senden.",
    "incident.step0.gdprTitle": "Datenschutz (DSGVO)",
    "incident.step0.gdprBody1": "Alle Informationen werden sicher in deinem Konto gespeichert. Die Daten werden nicht automatisch an Dritte weitergegeben.",
    "incident.step0.gdprBody2": "Du kannst jederzeit einen Vorfallbericht und alle zugehörigen Daten löschen.",
    "incident.step0.accept": "Ich verstehe — starten",
    "incident.step1.title": "Art des Vorfalls",
    "incident.step2.title": "Datum, Uhrzeit & Ort",
    "incident.step3.title": "Dein Fahrzeug",
    "incident.step4.title": "Fahrzeug der Gegenseite",
    "incident.step5.title": "Fahrer & Eigentümer",
    "incident.step6.title": "Versicherung",
    "incident.step7.title": "Schadensbeschreibung",
    "incident.step8.title": "Bilder",
    "incident.step9.title": "Zusammenfassung",
    "incident.stepOf2": "Schritt {current} von {total}",
    "incident.selectCountry": "Land wählen...",
    "incident.searchCountry": "Land suchen...",
    "incident.searchInsurance": "Suchen oder manuell eingeben...",
    "incident.regNum": "Kennzeichen",
    "incident.regCountry": "Zulassungsland",
    "incident.makeModel": "Marke / Modell",
    "incident.driverIsOwner": "Fahrer = Eigentümer",
    "incident.yes": "Ja",
    "incident.no": "Nein",
    "incident.ownerName": "Name des Eigentümers",
    "incident.ownerPhone": "Telefon des Eigentümers",
    "incident.ownerEmail": "E-Mail des Eigentümers",
    "incident.otherDriverName": "Name des Fahrers",
    "incident.otherDriverPhone": "Telefon des Fahrers",
    "incident.yourInsurance": "Deine Versicherung",
    "incident.opponentInsurance": "Versicherung der Gegenseite",
    "incident.insuranceCompany": "Versicherungsgesellschaft",
    "incident.policyNum": "Versicherungsnummer",
    "incident.policyNumHint": "Auf dem Versicherungsschein angegeben",
    "incident.damageWhat": "Was ist passiert?",
    "incident.damageWhatPlaceholder": "Beschreibe, was passiert ist",
    "incident.damageHow": "Wie ist es passiert?",
    "incident.damageHowPlaceholder": "Beschreibe die Umstände, Geschwindigkeit, Sicht usw.",
    "incident.damageParts": "Welche Schäden sind entstanden?",
    "incident.damagePartsPlaceholder": "Welche Teile des Fahrzeugs wurden beschädigt?",
    "incident.damageOther": "Weitere Details",
    "incident.damageOtherPlaceholder": "Zeugen, Wetter, Straßenverhältnisse...",
    "incident.notesLabel": "Notizen",
    "incident.notesPlaceholder2": "Weitere Notizen",
    "incident.imagesOptional": "Bilder sind optional, aber empfohlen",
    "incident.imagesHint": "Bilder können die Schäden belegen und die Bearbeitung bei der Versicherung erleichtern.",
    "incident.takePhoto": "Foto aufnehmen",
    "incident.uploadBtn": "Hochladen",
    "incident.uploading": "Wird hochgeladen...",
    "incident.saveDraftFirst": "Der Bericht muss gespeichert werden, bevor du Bilder hochladen kannst.",
    "incident.saveDraftBtn": "Entwurf zuerst speichern",
    "incident.imagesSecure": "Bilder werden sicher in deinem Konto gespeichert und nicht automatisch geteilt. Du kannst sie jederzeit löschen. Nur JPG und PNG (max. 5 MB).",
    "incident.notFilled": "Nicht ausgefüllt",
    "incident.notSpecified": "Nicht angegeben",
    "incident.notDescribed": "Nicht beschrieben",
    "incident.change": "Ändern",
    "incident.summaryTitle": "Zusammenfassung",
    "incident.disclaimerShort": "Du bist selbst dafür verantwortlich, den Bericht zu prüfen und an deine Versicherung zu senden.",
    "incident.generatePdf": "PDF wird erstellt...",
    "incident.savePdf": "Speichern & PDF herunterladen",
    "incident.saveNoPdf": "Ohne PDF speichern",
    "incident.reportSaved": "Vorfallbericht gespeichert",
    "incident.couldNotSave": "Bericht konnte nicht gespeichert werden",
    "incident.invalidFile": "Ungültige Datei",
    "incident.uploadFailed": "Hochladen fehlgeschlagen",
    "incident.imagesAttached": "{count} Bild(er) angehängt",
    "incident.timeAndPlace": "Zeit & Ort",
    "incident.yourVehicle": "Dein Fahrzeug",
    "incident.opponent": "Gegenseite",
    "incident.insurance": "Versicherung",
    "incident.damages": "Schäden",
    "incident.images": "Bilder",
    "incident.val.selectType": "Vorfallart wählen",
    "incident.val.dateRequired": "Datum erforderlich",
    "incident.val.locationRequired": "Ort erforderlich",
    "incident.val.regRequired": "Kennzeichen erforderlich",
    "incident.val.regCountryRequired": "Zulassungsland erforderlich",
    "incident.val.vehicleTypeRequired": "Fahrzeugtyp erforderlich",
    "incident.val.ownerNameRequired": "Name des Eigentümers erforderlich",
    "incident.val.describeWhat": "Beschreibe, was passiert ist",
    "incident.val.describeHow": "Beschreibe, wie es passiert ist",
    "incident.val.typeMissing": "Vorfallart fehlt",
    "incident.val.regMissing": "Kennzeichen fehlt",
    "incident.val.damageMissing": "Schadensbeschreibung fehlt",
    "incident.opponentVehicle": "Fahrzeug der Gegenseite",
    "incident.yourDriver": "Deine Angaben",
    "incident.opponentDriver": "Angaben der Gegenseite",
    "incident.pdf.title": "Schadensbericht",
    "incident.pdf.disclaimer": "Dieses Dokument ist ein Schadensbericht und keine offizielle Schadenmeldung. Du bist selbst dafür verantwortlich, den Bericht zu prüfen und an deine Versicherung zu senden.",
    "incident.pdf.generated": "Erstellt",
    "incident.pdf.trip": "Reise",
    "incident.pdf.type": "Typ",
    "incident.pdf.date": "Datum",
    "incident.pdf.time": "Uhrzeit",
    "incident.pdf.location": "Ort",
    "incident.pdf.vehicleType": "Fahrzeugtyp",
    "incident.pdf.regNum": "Kennzeichen",
    "incident.pdf.regCountry": "Zulassungsland",
    "incident.pdf.makeModel": "Marke / Modell",
    "incident.pdf.driverIsOwner": "Fahrer = Eigentümer",
    "incident.pdf.ownerName": "Name des Eigentümers",
    "incident.pdf.ownerPhone": "Telefon des Eigentümers",
    "incident.pdf.ownerEmail": "E-Mail des Eigentümers",
    "incident.pdf.driver": "Fahrer",
    "incident.pdf.phone": "Telefon",
    "incident.pdf.insuranceCompany": "Versicherungsgesellschaft",
    "incident.pdf.policyNum": "Versicherungsnummer",
    "incident.pdf.whatHappened": "Was ist passiert",
    "incident.pdf.howHappened": "Wie ist es passiert",
    "incident.pdf.damagedParts": "Beschädigte Teile",
    "incident.pdf.otherDetails": "Weitere Details",
    "incident.pdf.notes": "Notizen",
    "incident.pdf.images": "Bilder",
    "incident.pdf.imagesNote": "{count} Bild(er) angehängt. Die Bilder sind in deinem Vantastika-Konto verfügbar.",
    "incident.pdf.footer": "Dieses Dokument ist ein Schadensbericht, erstellt mit Vantastika, und stellt keine offizielle Schadenmeldung dar.",
    "incident.pdf.footerReview": "Du bist selbst dafür verantwortlich, die Angaben zu überprüfen und an deine Versicherung zu senden.",
    "incident.pdf.yourVehicle": "Dein Fahrzeug",
    "incident.pdf.opponentVehicle": "Fahrzeug der Gegenseite",
    "incident.pdf.damageDesc": "Schadensbeschreibung",
    "incident.pdf.incident": "Vorfall",
    "notFound.title": "Seite nicht gefunden",
    "notFound.description": "Die Seite, die du suchst, scheint nicht zu existieren. Sie wurde möglicherweise verschoben oder entfernt.",
    "notFound.home": "Zur Startseite",
    "notFound.back": "Zurück",
    "incident.generating": "PDF wird generiert...",
    "incident.saved": "Vorfallbericht gespeichert",
    "incident.saveFailed": "Bericht konnte nicht gespeichert werden",
    "incident.back": "Zurück",
    "incident.next": "Weiter",
    "incident.cancel": "Abbrechen",
    "incident.pdfTitle": "Vorfallbericht",
    "incident.pdfGenerated": "Generiert",
    "incident.pdfDisclaimer": "Dies ist eine persönliche Aufzeichnung. Es ist kein offizielles Versicherungsformular.",
    "incident.drafts": "Entwürfe von Berichten",
    "incident.noDrafts": "Keine Entwürfe vorhanden",
    "incident.continueDraft": "Fortsetzen",
    "incident.noReports": "Noch keine Vorfallberichte",
    "incident.deleted": "Bericht gelöscht",
    "incident.draft": "Entwurf",
    "incident.completed": "Abgeschlossen",
    "incident.myReports": "Meine Vorfallberichte",
    "incident.myReportsSubtitle": "Deine gespeicherten und Entwurfsberichte",
    "incident.vtype.motorhome": "Wohnmobil",
    "incident.vtype.campervan": "Campervan",
    "incident.vtype.car": "Auto",
    "incident.vtype.motorcycle": "Motorrad",
    "incident.vtype.trailer": "Anhänger",
    "incident.vtype.other": "Sonstiges",
    "quick.hello": "Hallo {name}! 👋",
    "quick.whatToDo": "Was möchtest du heute tun?",
    "quick.continueTrip": "Reise fortsetzen",
    "quick.activeTrip": "Aktive Reise",
    "quick.newTrip": "Neue Reise",
    "quick.planNext": "Plane dein nächstes Abenteuer",
    "quick.upcomingTrip": "Bevorstehende Reise",
    "quick.findCamping": "Campingplatz finden",
    "quick.searchFilter": "Suchen & filtern",
    "quick.favorites": "Favoriten",
    "quick.saved": "{count} gespeichert",
    "quick.community": "Community",
    "quick.tipsDiscussions": "Tipps & Diskussionen",
    "gdpr.title": "Wir achten auf Ihre Privatsphäre",
    "gdpr.description": "Diese Seite verwendet nur essenzielle und funktionale Cookies (Authentifizierung, Spracheinstellung, Ländereinstellung und Einverständniserklärung zu Cookies). Es werden keine Tracking-, Werbe- oder Analyse-Cookies von Drittanbietern verwendet.",
    "gdpr.readPolicy": "Lesen Sie unsere Datenschutzrichtlinie",
    "gdpr.accept": "Verstanden",
    "gdpr.decline": "Verstanden",
    "install.title": "Vantastika installieren",
    "install.description": "Auf dem Homescreen ablegen für schnellen Zugriff – funktioniert wie eine App!",
    "install.installButton": "App installieren",
    "install.showHow": "So geht's",
    "install.understood": "Verstanden",
    "install.iosSteps": "Folgen Sie diesen Schritten in Safari:",
    "install.iosTapShare": "Tippen Sie auf das",
    "install.iosShareLocation": "Unten in Safari",
    "install.iosAddHome": "Wählen Sie \"Zum Home-Bildschirm\"",
    "install.iosScrollDown": "Scrollen Sie bei Bedarf im Menü nach unten",
    "install.iosTapAdd": "Tippen Sie auf \"Hinzufügen\"",
    "install.androidSteps": "Folgen Sie diesen Schritten:",
    "install.androidTapMenu": "Tippen Sie auf das",
    "install.androidMenuLocation": "Oben rechts im Browser",
    "install.androidInstallApp": "Wählen Sie \"App installieren\"",
    "install.androidOrAddHome": "Oder \"Zum Startbildschirm hinzufügen\"",
    "home.trustFree": "100% kostenlos",
    "myTrips.title": "Meine Reisen",
    "myTrips.loginTitle": "Einloggen, um Ihre Reisen zu sehen",
    "myTrips.loginDesc": "Ihre gespeicherten Reisen erscheinen hier nach dem Einloggen.",
    "myTrips.loginButton": "Einloggen",
    "myTrips.pausedStatus": "Pausiert",
    "myTrips.activeStatus": "Aktive Reise",
    "myTrips.pausedSubtitle": "Ihre Reise ist pausiert",
    "myTrips.activeSubtitle": "Sie haben eine aktive Reise",
    "myTrips.tripsCount": "{count} Reisen gespeichert",
    "myTrips.tripCount": "{count} Reise gespeichert",
    "myTrips.newTrip": "Neue Reise",
    "myTrips.resumeTrip": "Reise fortsetzen",
    "myTrips.continueTrip": "Reise fortsetzen",
    "myTrips.resume": "Fortsetzen",
    "myTrips.pause": "Pausieren",
    "myTrips.endTrip": "Reise beenden",
    "myTrips.end": "Beenden",
    "myTrips.progress": "{pct}% erledigt",
    "myTrips.stops": "{count} Stopps",
    "myTrips.nextStop": "Nächster Stopp:",
    "myTrips.openActiveTrip": "Aktive Reise öffnen",
    "myTrips.noTrips": "Noch keine Reisen gespeichert",
    "myTrips.noTripsDesc": "Erstellen Sie Ihre erste Reise im Planer und sie wird hier angezeigt.",
    "myTrips.planFirst": "Planen Sie Ihre erste Reise",
    "myTrips.upcomingTrips": "Bevorstehende Reisen",
    "myTrips.sharedWithMe": "Mit mir geteilt",
    "myTrips.invitedBy": "Eingeladen von",
    "myTrips.completedTrips": "Abgeschlossene Reisen",
    "myTrips.startTrip": "Reise starten",
    "myTrips.budget": "Budget",
    "myTrips.checklist": "Checkliste",
    "myTrips.stoppedPromptTitle": "Sie scheinen angehalten zu haben",
    "myTrips.stoppedPromptDesc": "Möchten Sie fortfahren oder eine Pause machen?",
    "myTrips.arrivedAt": "Angekommen in {name}!",
    "myTrips.arrivedDesc": "Ziel erreicht – gut gemacht!",
    "myTrips.stopRemoved": "Stopp entfernt",
    "myTrips.close": "Schließen",
    "myTrips.alreadyActiveTrip": "Sie haben bereits eine aktive Reise",
    "myTrips.endActiveFirst": "Beenden Sie diese zuerst.",
    "myTrips.tripStarted": "Reise gestartet!",
    "myTrips.tripStartedDesc": "GPS-Tracking und Journal sind jetzt aktiv.",
    "myTrips.tripPaused": "Reise pausiert",
    "myTrips.tripPausedDesc": "Sie können jederzeit fortfahren.",
    "myTrips.tripResumed": "Reise fortgesetzt!",
    "myTrips.tripResumedDesc": "GPS-Tracking ist wieder aktiv.",
    "myTrips.tripEnded": "Reise beendet!",
    "myTrips.tripEndedDesc": "Ihre Reise wurde gespeichert.",
    "myTrips.tripReactivated": "Reise reaktiviert!",
    "myTrips.revertedToPlanned": "Auf geplant zurückgesetzt",
    "myTrips.revertedToPlannedDesc": "Die Reise ist wieder als geplant markiert. Alle Daten sind erhalten.",
    "myTrips.missingStops": "Reise hat keine Stopps",
    "myTrips.missingStopsDesc": "Fügen Sie mindestens einen Stopp hinzu, bevor Sie starten.",
    "myTrips.missingTitle": "Reise hat keinen Titel",
    "myTrips.missingTitleDesc": "Geben Sie der Reise einen Namen, bevor Sie starten.",
    "trip.stopAddedFromMap": "Stopp von der Karte hinzugefügt",
    "trip.stopPositionUpdated": "Stoppposition aktualisiert",
    "trip.routeOptimizedDesc": "Stopps wurden nach kürzester Fahrstrecke sortiert.",
    "places.verificationThanks": "Danke für die Bestätigung!",
    "places.verificationThanksDesc": "Sie helfen uns, die Informationen korrekt zu halten.",
    "friends.requestAccepted": "Freundschaftsanfrage akzeptiert!",
    "friends.requestSent": "Freundschaftsanfrage gesendet!",
    "myTrips.checklistReminder": "Vergessen Sie nicht, die Checkliste vor Abfahrt durchzugehen",
    "myTrips.today": "Heute",
    "myTrips.tomorrow": "Morgen",
    "myTrips.inDays": "In {days} Tagen",
    "myTrips.nextTrip": "Nächste Reise",
    "myTrips.drafts": "Entwürfe",
    "myTrips.readyToStart": "Bereit zum Starten",
    "myTrips.continuePlanning": "Weiterplanen",
    "myTrips.editTrip": "Bearbeiten",
    "myTrips.openTrip": "Reise öffnen",
    "myTrips.viewSummary": "Zusammenfassung anzeigen",
    "myTrips.duplicate": "Duplizieren",
    "myTrips.archive": "Archivieren",
    "myTrips.activeTrips": "Aktive Reise",
    "myTrips.showCompleted": "Abgeschlossene Reisen anzeigen",
    "myTrips.hideCompleted": "Abgeschlossene Reisen ausblenden",
    "tools.title": "Reise-Tools",
    "tools.checklist": "Checkliste",
    "tools.budget": "Budget",
    "tools.incident": "Vorfallsbericht",
    "tripSummary.distance": "Distanz",
    "tripSummary.driveTime": "Fahrzeit",
    "tripSummary.days": "Tage",
    "tripSummary.stops": "Stopps",
    "tripSummary.fuel": "Treibstoff",
    "tripSummary.budget": "Budget",
    "tripSummary.route": "Route",
    "tripSummary.stop": "Stopp {n}",
    "tripGuideLink.title": "Reiseführer für {country}",
    "tripGuideLink.camping": "Camping",
    "tripGuideLink.driving": "Fahren",
    "tripGuideLink.safety": "Sicherheit",
    "arrival.continueToNext": "Weiter zu {name}",
    "stats.trips": "Reisen",
    "stats.stops": "Stopps",
    "stats.nights": "Nächte",
    "stats.countries": "Länder",
    "stats.reviews": "Bewertungen",
    "stats.friends": "Freunde",
    "badge.travel": "Reisen",
    "badge.exploration": "Erkundung",
    "badge.community": "Community",
    "badge.milestone": "Meilensteine",
    "badge.creative": "Kreativ",
    "badge.engagement": "Engagement",
    "badge.special": "Spezial",
    "checklist.title": "Checklisten",
    "checklist.subtitle": "Reisebezogene und eigenständige Checklisten",
    "checklist.loginRequired": "Einloggen, um Ihre Checklisten zu sehen",
    "checklist.loginHint": "Ihre Checklisten erscheinen hier nach dem Einloggen.",
    "checklist.backToAll": "Zurück zu allen Checklisten",
    "checklist.trip": "Reise",
    "checklist.general": "Allgemeine Packliste",
    "checklist.generalSection": "Eigenständige Checklisten",
    "checklist.generalPacklist": "Allgemeine Packliste",
    "checklist.done": "erledigt",
    "checklist.noChecklistYet": "Noch keine Checkliste – tippen Sie zum Erstellen",
    "checklist.activeTrips": "Aktive & bevorstehende Reisen",
    "checklist.completedTrips": "Abgeschlossene Reisen",
    "checklist.noTrips": "Noch keine Reisen. Erstellen Sie eine Reise, um eine Checkliste zu verknüpfen.",
    "checklist.planTrip": "Reise planen",
    "checklist.allDone": "Alles erledigt! 🎉",
    "checklist.remaining": "{count} Punkte übrig",
    "checklist.progress": "{done} von {total} erledigt",
    "checklist.addCustom": "Benutzerdefinierter Eintrag",
    "checklist.addTemplate": "Vorlage hinzufügen",
    "checklist.reset": "Zurücksetzen",
    "checklist.created": "Checkliste erstellt! ✅",
    "checklist.createFailed": "Checkliste konnte nicht erstellt werden",
    "checklist.resetDone": "Checkliste wurde zurückgesetzt",
    "checklist.resetFailed": "Zurücksetzen fehlgeschlagen",
    "checklist.itemAdded": "Eintrag hinzugefügt! ✅",
    "checklist.itemAddFailed": "Eintrag konnte nicht hinzugefügt werden",
    "checklist.inputPlaceholder": "Beschreiben Sie, was Sie hinzufügen möchten...",
    "checklist.add": "Hinzufügen",
    "checklist.cancel": "Abbrechen",
    "checklist.noChecklist": "Keine Checkliste für {type} vorhanden.",
    "checklist.selectTemplate": "Vorlage auswählen:",
    "checklist.or": "oder",
    "checklist.aiPacklist": "KI-angepasste Packliste",
    "checklist.standardChecklist": "Standard-Checkliste",
    "checklist.aiAdapted": "KI-Liste ist angepasst für {dest}",
    "checklist.andSeason": " und Saison",
    "checklist.aiCreated": "🤖 KI-Packliste erstellt!",
    "checklist.allExist": "Alle Einträge sind bereits in Ihrer Liste vorhanden",
    "checklist.newItemsAdded": "{count} neue Einträge hinzugefügt!",
    "checklist.addExtraPacking": "Zusätzliches Packen hinzufügen:",
    "float.navigate": "Navigieren",
    "float.save": "Speichern",
    "float.saving": "Speichere...",
    "profile.save": "Änderungen speichern",
    "profile.saving": "Speichere...",
    "shared.saveToMyTrips": "Zu meinen Reisen speichern",
    "shared.saving": "Speichere...",
    "shared.privacyNote": "Start- und Endpunkte wurden aus Datenschutzgründen entfernt. Füge deinen eigenen Startpunkt hinzu.",
    "shared.addYourStart": "Füge deinen Startpunkt hinzu",
    "shared.templateNote": "Diese Reise wird als Inspiration geteilt. Ziele und Stopps sind enthalten – füge deine eigenen Start- und Endpunkte hinzu.",
    "trip.editOutbound": "Hinfahrt",
    "trip.editReturn": "Rückfahrt",
    "trip.editMainInfo": "Reisedetails",
    "vehicle.motorhome": "Wohnmobil",
    "vehicle.campervan": "Wohnkastenwagen",
    "vehicle.caravan": "Wohnwagen",
    "vehicle.car": "Auto",
    "vehicle.motorcycle": "Motorrad",
    "vehicle.selectType": "Fahrzeugtyp",
    "vehicle.nameLabel": "Fahrzeugname (optional)",
    "vehicle.namePlaceholder": "z.B. Fiat Ducato 2020",
    "vehicle.dimensions": "Fahrzeugabmessungen",
    "vehicle.dimensionsExplain": "Wird für eine intelligentere Routenplanung verwendet – vermeide niedrige Brücken, enge Passagen und Gewichtsbeschränkungen.",
    "vehicle.height": "Höhe",
    "vehicle.width": "Breite",
    "vehicle.length": "Länge",
    "vehicle.weight": "Gewicht",
    "vehicle.disclaimer": "Routenwarnungen basierend auf Fahrzeugabmessungen sind eine ratsame Orientierungshilfe. Überprüfe immer die Beschilderung vor Ort. Die Abdeckung umfassender Beschränkungsdaten wird erweitert.",
    "vehicle.profileComplete": "Fahrzeugprofil vollständig – bereit für intelligente Routenplanung",
    "vehicle.save": "Fahrzeugprofil speichern",
    "vehicle.saving": "Speichere...",
    "vehicle.saved": "Fahrzeugprofil gespeichert!",
    "vehicle.saveFailed": "Fahrzeugprofil konnte nicht gespeichert werden",
    "vehicle.profileTitle": "Fahrzeugprofil",
    "vehicle.profileSubtitle": "Für eine intelligentere, sicherere Routenplanung",
    "myTrips.viewRoute": "Route ansehen",
    "myTrips.playTrip": "Reise abspielen",
    "myTrips.needMoreStops": "Füge mindestens 2 Stopps mit Koordinaten hinzu, um die Reiseanimation abzuspielen.",
    "tripReplay.stops": "Stopps",
    "tripReplay.days": "Tage",
    "tripReplay.completed": "Reise abgeschlossen!",
    "tripReplay.replay": "Erneut abspielen",
    "tripReplay.share": "Teilen",
    "routeOverview.title": "Routenübersicht",
    "routeOverview.totalDistance": "Gesamtdistanz",
    "routeOverview.driveTime": "Fahrzeit",
    "routeOverview.stops": "Stopps",
    "routeOverview.overnights": "Übernachtungen",
    "routeOverview.fuelEstimate": "Geschätzter Kraftstoffverbrauch",
    "routeOverview.itinerary": "Reiseplan",
    "routeOverview.nights": "Nächte",
    "routeOverview.start": "Start",
    "routeOverview.destination": "Ziel",
    "routeOverview.visit": "Besuch",
    "games.selectCountry": "Land auswählen",
    "nav.bottom.budget": "Budget",
    "nav.installTitle": "Vantastika installieren",
    "nav.installIOS1": "Tippe auf die Teilen-Schaltfläche (unten in Safari)",
    "nav.installIOS2": "Wähle \"Zum Home-Bildschirm\"",
    "nav.installIOS3": "Tippe auf \"Hinzufügen\"",
    "nav.installAndroid1": "Tippe auf das Menü-Symbol (⋮ oben rechts)",
    "nav.installAndroid2": "Wähle \"App installieren\" oder \"Zum Startbildschirm hinzufügen\"",
    "nav.installUnderstood": "Verstanden",
    "tripStatus.startTitle": "Möchtest du die Reise jetzt beginnen?",
    "tripStatus.startDesc": "Die Reise wird als laufend eingestellt und die GPS-Ortung aktiviert.",
    "tripStatus.startConfirm": "Ja, starten",
    "tripStatus.endTitle": "Möchtest du die Reise wirklich beenden?",
    "tripStatus.endDesc": "Du kannst sie später fortsetzen, wenn du deine Meinung änderst.",
    "tripStatus.endConfirm": "Ja, beenden",
    "tripStatus.pauseTitle": "Möchtest du die Reise pausieren?",
    "tripStatus.pauseDesc": "Du kannst die Reise jederzeit fortsetzen.",
    "tripStatus.pauseConfirm": "Ja, pausieren",
    "tripStatus.resumeTitle": "Möchtest du die Reise fortsetzen?",
    "tripStatus.resumeDesc": "Die Reise wird dort fortgesetzt, wo du pausiert hast.",
    "tripStatus.resumeConfirm": "Ja, fortsetzen",
    "tripStatus.revertTitle": "Zur geplanten Reise zurückkehren?",
    "tripStatus.revertDesc": "Alle Reiseinformationen bleiben erhalten – nur der Status ändert sich zurück zu \"geplant\".",
    "tripStatus.revertConfirm": "Ja, zurückkehren",
    "tripStatus.reactivateTitle": "Die beendete Reise fortsetzen?",
    "tripStatus.reactivateDesc": "Die Reise wird wieder als laufend eingestellt. Alle Daten bleiben erhalten.",
    "tripStatus.reactivateConfirm": "Ja, fortsetzen",
    "tripStatus.trip": "Reise",
    "tripStatus.cancel": "Abbrechen",
    "addToTrip.title": "Zur Reise hinzufügen",
    "addToTrip.loginPrompt": "Melde dich an, um Orte zu deinen Reisen hinzuzufügen.",
    "addToTrip.login": "Anmelden",
    "addToTrip.createAccount": "Konto erstellen",
    "addToTrip.howUsePlace": "Wie möchtest du diesen Ort nutzen?",
    "addToTrip.chooseTrip": "Reise auswählen",
    "addToTrip.startPoint": "Startpunkt",
    "addToTrip.startPointDesc": "Die Reise beginnt hier",
    "addToTrip.destination": "Ziel",
    "addToTrip.destinationDesc": "Ich möchte hierhin reisen",
    "addToTrip.stop": "Stopp unterwegs",
    "addToTrip.stopDesc": "Ein Stopp während der Reise",
    "addToTrip.noTrips": "Du hast noch keine geplanten Reisen",
    "addToTrip.noTripsDesc": "Erstelle eine neue Reise und füge den Ort direkt hinzu.",
    "addToTrip.stops": "Stopps",
    "addToTrip.unnamed": "Unbenannte Reise",
    "addToTrip.addedAs": "wurde hinzugefügt als",
    "addToTrip.roleStart": "Startpunkt",
    "addToTrip.roleDestination": "Ziel",
    "addToTrip.roleStop": "Stopp",
    "addToTrip.couldNotAdd": "Ort konnte nicht hinzugefügt werden",
    "addToTrip.createNew": "Neue Reise mit diesem Ort erstellen",
    "optimize.bannerTitle": "KI hat eine kürzere Route gefunden",
    "optimize.bannerDesc": "Spare ca.",
    "optimize.bannerByReordering": "indem du die Reihenfolge deiner Stopps änderst.",
    "optimize.viewAlternatives": "Alternativen anzeigen",
    "optimize.dismiss": "Nicht jetzt",
    "optimize.fuelSaved": "an Kraftstoff",
    "camping.notFound": "Ort konnte nicht gefunden werden.",
    "camping.approxPrice": "ca.",
    "camping.perNight": "/ Nacht (ca.)",
    "camping.free": "Kostenlos",
    "camping.typeStellplatz": "Stellplatz",
    "camping.typeCamping": "Campingplatz",
    "camping.amenities": "Ausstattung",
    "camping.checkIn": "Anreise",
    "camping.checkOut": "Abreise",
    "camping.visitWebsite": "Webseite besuchen",
    "camping.addToTrip": "Zur Reise hinzufügen",
    "camping.navigate": "Navigieren",
    "camping.viewPage": "Seite ansehen",
    "camping.amenityElectricity": "Strom",
    "camping.amenityFreshWater": "Frischwasser",
    "camping.amenityGreyWater": "Grauwasser",
    "camping.amenityToilet": "Toilette",
    "camping.amenityShower": "Dusche",
    "camping.amenityWC": "WC",
    "camping.amenityPets": "Haustiere",
    "camping.amenityAllYear": "Ganzjährig geöffnet",
    "camping.amenityFamily": "Familienfreundlich",
    "camping.amenityLarge": "Große Wohnmobile",
    "camping.amenityWater": "Am Wasser gelegen",
    "camping.amenitySpa": "Wellness",
    "camping.amenityWifi": "WLAN",
    "camping.amenityLaundry": "Waschküche",
    "camping.amenityRestaurant": "Restaurant",
    "share.inviteToSharedTrip": "Zur geteilten Reise einladen",
    "share.inviteDesc": "Der Empfänger nimmt an derselben Reise teil. Alle Änderungen synchronisieren sich in Echtzeit – Budget, Checkliste, Stopps und Notizen.",
    "share.inviteViaApp": "Per App einladen...",
    "share.copyInviteLink": "Einladungslink kopieren",
    "share.copied": "Kopiert!",
    "share.shareAsTip": "Als Reisetipp teilen",
    "share.tipDesc": "Der Empfänger erhält seine eigene Kopie als Inspiration. Änderungen wirken sich nicht auf deine Reise aus.",
    "share.createAiGuide": "KI-Reiseführer erstellen",
    "share.generatingAi": "Erstelle KI-Reiseführer... ✨",
    "share.aiGuideCreated": "KI-Reiseführer erstellt! ✨",
    "share.couldNotCreateAi": "KI-Guide konnte nicht erstellt werden",
    "share.aiGuideActive": "KI-Reiseführer aktiv",
    "share.tabShare": "Teilen",
    "share.tabTeam": "Reisegruppe",
    "share.tabComments": "Kommentare",
    "share.tabActivity": "Aktivität",
    "share.inviteByUsername": "Einladen per Benutzername oder Einladungslink teilen:",
    "share.usernamePlaceholder": "Zu invite Benutzername...",
    "share.inviteSent": "Einladung gesendet!",
    "share.userNotFound": "Benutzer nicht gefunden. Exakten Benutzernamen eingeben.",
    "share.cannotInviteSelf": "Du kannst dich nicht selbst einladen.",
    "share.alreadyInvited": "Benutzer wurde bereits eingeladen.",
    "share.couldNotInvite": "Einladung konnte nicht gesendet werden",
    "share.noMembers": "Noch keine Gruppenmitglieder. Lade jemanden ein, gemeinsam zu planen!",
    "share.editor": "Bearbeiter",
    "share.removedFromGroup": "Aus der Reisegruppe entfernt",
    "share.noComments": "Noch keine Kommentare.",
    "share.unknown": "Unbekannt",
    "share.commentPlaceholder": "Schreibe einen Kommentar...",
    "share.send": "Senden",
    "share.noActivity": "Noch keine Aktivität.",
    "share.actionInvited": "eingeladen",
    "share.actionCommented": "kommentiert",
    "share.actionAddedStop": "Stopp hinzugefügt:",
    "share.actionUpdatedStop": "Stopp aktualisiert:",
    "share.actionRemovedStop": "Stopp entfernt:",
    "share.actionUpdatedTrip": "die Reise aktualisiert",
    "signup.guestVsMember": "Gast vs Mitglied",
    "signup.seeWhatYouGet": "Siehe, was du als Mitglied bekommst",
    "signup.freeNoCard": "100 % kostenlos – keine Kreditkarte erforderlich",
    "signup.premiumSoon": "Premium kommt bald mit noch mehr Funktionen",
    "signup.username": "Benutzername",
    "signup.usernamePlaceholder": "z.B. vanansen",
    "signup.displayName": "Anzeigename",
    "signup.displayNamePlaceholder": "Dein Name, der anderen angezeigt wird",
    "signup.confirmPassword": "Passwort bestätigen",
    "signup.passwordPlaceholder": "Mindestens 8 Zeichen, Buchstabe + Zahl",
    "signup.passwordsNoMatch": "Passwörter stimmen nicht überein",
    "signup.selectCountry": "Wähle dein Land",
    "signup.whatVehicle": "Womit reist du?",
    "signup.ageWarning": "Du musst mindestens 16 Jahre alt sein, um ein Konto bei Vantastika zu erstellen. Wenn du unter 18 bist, ist die Zustimmung eines Erziehungsberechtigten erforderlich.",
    "signup.termsConsent": "Ich akzeptiere die",
    "signup.termsLink": "Nutzungsbedingungen",
    "signup.andThe": "und die",
    "signup.integrationTermsLink": "Integrationsbedingungen",
    "signup.ageConfirm": "und bestätige, dass ich mindestens 16 Jahre alt bin.",
    "signup.gdprConsent": "Ich habe die gelesen und akzeptiere die",
    "signup.privacyLink": "Datenschutzrichtlinie (DSGVO)",
    "signup.or": "oder",
    "signup.verificationFailed": "Verifizierung fehlgeschlagen. Versuche es erneut.",
    "signup.captchaFailed": "CAPTCHA-Verifizierung fehlgeschlagen. Versuche es erneut.",
    "signup.tooManyAttempts": "Zu viele Versuche. Warte einen Moment.",
    "signup.acceptTerms": "Du musst die Bedingungen akzeptieren",
    "signup.selectCountryError": "Wähle dein Land",
    "signup.enterDisplayName": "Gib einen Anzeigenamen ein",
    "signup.enterUsername": "Gib einen Benutzernamen ein",
    "signup.accountCreated": "Konto erstellt!",
    "signup.welcomeMsg": "Willkommen bei Vantastika!",
    "signup.checkDetails": "Überprüfe deine Daten und versuche es erneut.",
    "signup.showPassword": "Passwort anzeigen",
    "signup.hidePassword": "Passwort ausblenden",
    "signupVehicle.motorhome": "🚐 Wohnmobil",
    "signupVehicle.motorhomeDesc": "Ich reise mit dem Wohnmobil",
    "signupVehicle.car": "🚗 Auto",
    "signupVehicle.carDesc": "Ich reise mit dem Auto",
    "signupVehicle.motorcycle": "🏍️ Motorrad",
    "signupVehicle.motorcycleDesc": "Ich fahre Motorrad",
    "signupVehicle.campervan": "🚌 Campervan",
    "signupVehicle.campervanDesc": "Ich habe einen Campervan",
    "signupVehicle.rental": "🔑 Mietfahrzeug",
    "signupVehicle.rentalDesc": "Ich miete bei Bedarf",
    "signupVehicle.other": "🌍 Andere / Unsicher",
    "signupVehicle.otherDesc": "Flexibler Reisender",
    "profile.yourProfile": "Dein Profil",
    "profile.since": "Seit",
    "profile.quickLinks": "Schnellzugriffe",
    "profile.myTrips": "Meine Reisen",
    "profile.checklists": "Checklisten",
    "profile.savedPlaces": "Gespeicherte Orte",
    "profile.planning": "Planung",
    "profile.saved": "Gespeichert",
    "vehicle.projects": "Projekte",
    "vehicle.viewAll": "Alle anzeigen",
    "vehicle.noProjects": "Noch keine Projekte mit {name} verknüpft.",
    "vehicle.createProject": "Projekt erstellen",
    "wishlist.startExploring": "Entdecken starten",
    "profile.notifications": "Benachrichtigungen",
    "profile.travelOverview": "Reiseübersicht",
    "profile.levelAndProfile": "Reiseprofil & Level",
    "profile.progress": "Fortschritt",
    "profile.total": "Gesamt",
    "profile.badges": "Abzeichen",
    "profile.challenges": "Herausforderungen",
    "profile.activeChallenges": "Aktive Herausforderungen",
    "profile.viewAllProgress": "Gesamten Fortschritt anzeigen",
    "profile.incidentReports": "Vorfallberichte",
    "profile.newReport": "Neuer Bericht",
    "profile.social": "Soziales",
    "profile.inbox": "Posteingang",
    "profile.mySubmissions": "Meine Einreichungen",
    "profile.accountSettings": "Konto & Einstellungen",
    "profile.accountSettingsDesc": "Profil, Design und Passwort",
    "profile.email": "E-Mail",
    "profile.displayName": "Anzeigename",
    "profile.displayNamePlaceholder": "Dein Name",
    "profile.username": "Benutzername",
    "profile.usernamePlaceholder": "@benutzername",
    "profile.country": "Land",
    "profile.countryPlaceholder": "Deutschland",
    "profile.appearance": "Erscheinungsbild",
    "profile.themeLight": "Hell",
    "profile.themeDark": "Dunkel",
    "profile.themeSystem": "System",
    "profile.themeLightDesc": "Immer helles Design",
    "profile.themeDarkDesc": "Immer dunkles Design",
    "profile.themeSystemDesc": "Folgt dem Gerät",
    "profile.changePassword": "Passwort ändern",
    "profile.currentPassword": "Aktuelles Passwort",
    "profile.newPassword": "Neues Passwort",
    "profile.newPasswordPlaceholder": "Mindestens 8 Zeichen",
    "profile.confirmPasswordLabel": "Bestätigen",
    "profile.changePasswordBtn": "Passwort ändern",
    "profile.changingPassword": "Ändere...",
    "profile.wrongCurrentPassword": "Falsches aktuelles Passwort",
    "profile.passwordChanged": "Passwort geändert!",
    "profile.passwordMinLength": "Das Passwort muss mindestens 8 Zeichen lang sein",
    "profile.passwordsMismatch": "Passwörter stimmen nicht überein",
    "profile.deleteAccount": "Konto löschen",
    "profile.permanentDeletion": "Dauerhafte Löschung",
    "profile.deleteAccountDesc": "Beim Löschen deines Kontos werden folgende Daten dauerhaft entfernt: Profil, Reisen, Stopps, Favoriten, Checklisten, Forenbeiträge, Bewertungen, Bilder, Freundschaftsanfragen, Nachrichten, Vorfallberichte und Benachrichtigungen.",
    "profile.deleteAccountNote": "Was übrig bleiben kann: Anonymisierte Audit-Logs (nicht mit deinem Konto verknüpft), der gemeinsame Abzeichen-Katalog und Daten, zu deren Aufbewahrung wir gesetzlich verpflichtet sind (z.B. Buchhaltungsgesetze, max. 7 Jahre).",
    "profile.deleteMyAccount": "Mein Konto löschen",
    "profile.deleteConfirmTitle": "Konto dauerhaft löschen",
    "profile.deleteConfirmDesc": "Bist du sicher? Folgendes wird dauerhaft gelöscht:",
    "profile.deleteConfirmList": "• Profil, Einstellungen und Avatar\n• Reisen, Stopps, Budget und Checklisten\n• Favoriten, Sammlungen und gespeicherte Orte\n• Vorfallberichte und hochgeladene Bilder\n• Forenbeiträge, Kommentare und Likes\n• Freundschaftsanfragen und Nachrichten\n• Benachrichtigungen und Ortsverifizierungen",
    "profile.deleteConfirmNote": "Anonymisierte Audit-Logs und der gemeinsame Abzeichen-Katalog sind nicht betroffen. Daten, zu deren Aufbewahrung wir gesetzlich verpflichtet sind, können bis zu 7 Jahre aufbewahrt werden.",
    "profile.cannotUndo": "Kann nicht rückgängig gemacht werden.",
    "profile.deleting": "Lösche...",
    "profile.yesDeleteAccount": "Ja, mein Konto löschen",
    "profile.accountDeleted": "Konto wurde gelöscht",
    "profile.accountDeletedDesc": "Alle deine Daten wurden gelöscht.",
    "profile.couldNotDelete": "Konto konnte nicht gelöscht werden",
    "profile.profileSaved": "Profil gespeichert!",
    "profile.couldNotSave": "Konnte nicht gespeichert werden",
    "profile.profilePicUpdated": "Profilbild aktualisiert!",
    "profile.uploading": "Lade hoch...",
    "profile.fileTooLarge": "Datei zu groß",
    "profile.fileTooLargeDesc": "Max. 2 MB",
    "profile.imageRejected": "Bild abgelehnt",
    "profile.myVehicle": "Mein Fahrzeug",
    "profile.enterCurrentPassword": "Gib dein aktuelles Passwort ein",
    "profile.couldNotChangePassword": "Passwort konnte nicht geändert werden",
    "profile.vehicleMotorhome": "Wohnmobil",
    "profile.vehicleCar": "Auto",
    "profile.vehicleMotorcycle": "Motorrad",
    "profile.vehicleCampervan": "Campervan",
    "profile.vehicleRental": "Miete",
    "profile.vehicleOther": "Andere",
    "profile.activeNow": "Aktuell aktiv",
    "profile.today": "Heute!",
    "profile.inDays": "In {n} Tagen",
    "profile.newNotification": "neue Benachrichtigung",
    "profile.newNotifications": "neue Benachrichtigungen",
    "profile.showAll": "Alle anzeigen",
    "profile.inspection": "TÜV/HU",
    "profile.gasInspection": "Gasprüfung",
    "profile.service": "Service",
    "profile.expired": "Abgelaufen!",
    "profile.noDateRegistered": "Kein Datum erfasst",
    "profile.noServiceRegistered": "Kein Service erfasst",
    "profile.missing": "Fehlt",
    "profile.insurance": "Versicherung",
    "profile.noInsuranceRegistered": "Keine Versicherung erfasst",
    "profile.regNumberMissing": "Kennzeichen fehlt",
    "profile.addForSmartData": "Für smarte Fahrzeugdaten hinzufügen",
    "profile.addRegNumber": "Kennzeichen hinzufügen",
    "profile.statusGood": "Gut",
    "profile.statusSoon": "Bald",
    "profile.statusAction": "Aktion",
    "profile.edit": "Bearbeiten",
    "profile.addService": "Service hinzufügen",
    "profile.addVehicleDesc": "Verbinde dein Fahrzeug für smarte Planung, Erinnerungen und personalisierte Inhalte",
    "profile.addVehicleBtn": "Fahrzeug hinzufügen",
    "profile.validUntil": "Gültig bis {date}",
    "scan.title": "Intelligenter Beleg-Scan",
    "scan.takePhoto": "Foto machen",
    "scan.upload": "Hochladen",
    "scan.photoOrUpload": "Mach ein Foto oder lade einen Beleg hoch",
    "scan.aiAnalyzes": "KI analysiert den Beleg Zeile für Zeile",
    "scan.analyzing": "Analysiere Beleg...",
    "scan.analyzingDesc": "KI liest Artikel, Beträge und Kategorien",
    "scan.fileTooLarge": "Datei zu groß",
    "scan.fileTooLargeMax": "Max. 15 MB",
    "scan.analysisFailed": "Analyse fehlgeschlagen",
    "scan.couldNotAnalyze": "Beleg konnte nicht analysiert werden",
    "scan.amount": "Betrag",
    "scan.currency": "Währung",
    "scan.possibleAmounts": "Mögliche Beträge:",
    "scan.converted": "Konvertiert:",
    "scan.date": "Datum",
    "scan.titleLabel": "Titel",
    "scan.itemsPerCategory": "Artikel pro Kategorie",
    "scan.mixedCategories": "Gemischte Kategorien",
    "scan.howToSave": "Wie möchten Sie speichern?",
    "scan.oneExpense": "Eine Ausgabe",
    "scan.splitUp": "Aufteilen",
    "scan.splitDesc": "Erstellt {n} separate Ausgaben — eine pro Kategorie.",
    "scan.mainCategory": "Hauptkategorie",
    "scan.linkToBudget": "Mit Budget verknüpfen",
    "scan.noBudgetSelected": "Kein Budget ausgewählt",
    "scan.noBudget": "Kein Budget",
    "scan.linkToTrip": "Mit Reise verknüpfen",
    "scan.noTripSelected": "Keine Reise ausgewählt",
    "scan.noTrip": "Keine Reise",
    "scan.note": "Notiz",
    "scan.hideRawText": "Rohtext des Belegs ausblenden",
    "scan.showRawText": "Rohtext des Belegs anzeigen",
    "scan.cancel": "Abbrechen",
    "scan.saving": "Speichere...",
    "scan.saveExpense": "Ausgabe speichern",
    "scan.saveExpenses": "{n} Ausgaben speichern",
    "scan.expenseSaved": "Ausgabe vom Beleg gespeichert ✓",
    "scan.expensesSaved": "{n} Ausgaben vom Beleg gespeichert ✓",
    "scan.fillAmountAndTitle": "Betrag und Titel eingeben",
    "scan.couldNotSave": "Konnte nicht gespeichert werden",
    "scan.confidenceHigh": "Hohe Konfidenz",
    "scan.confidenceMedium": "Mittlere Konfidenz — Felder prüfen",
    "scan.confidenceLow": "Geringe Konfidenz — alle Felder prüfen",
    "scan.dotMedium": "Mittlere Konfidenz",
    "scan.dotLow": "Unsicher – prüfen",
    "scan.close": "Schließen",
    "scan.paid": "Bezahlt:",
    "scan.vat": "MwSt.:",
    "scan.receipt": "Beleg",
    // Camera permissions
    "camera.prePermissionHint": "Kamerazugriff wird zum Scannen von Belegen benötigt",
    "camera.deniedError": "Kamerazugriff wurde verweigert. Aktivieren Sie ihn in den Browser- oder Telefoneinstellungen.",
    "camera.notFoundError": "Keine Kamera auf diesem Gerät gefunden.",
    "camera.inUseError": "Die Kamera wird von einer anderen App verwendet.",
    "camera.genericError": "Ein unerwarteter Fehler beim Zugriff auf die Kamera ist aufgetreten.",
    "camera.openSettings": "Anweisungen anzeigen",
    "camera.androidSettingsHint": "Gehen Sie zu Einstellungen → Apps → Ihr Browser → Berechtigungen → Kamera",
    "camera.iosSettingsHint": "Gehen Sie zu Einstellungen → Datenschutz & Sicherheit → Kamera → Ihr Browser",
    "camera.desktopSettingsHint": "Klicken Sie auf das Schloss-Symbol in der Adressleiste und aktivieren Sie den Kamerazugriff.",
    // Location permissions
    "location.detecting": "Ihren Standort abrufen…",
    "location.unsupportedTitle": "Standortfreigabe nicht unterstützt",
    "location.unsupportedDesc": "Ihr Gerät oder Browser unterstützt keine Standortfreigabe. Versuchen Sie einen anderen Browser.",
    "location.deniedTitle": "Standortzugriff verweigert",
    "location.deniedDesc": "Sie haben den Standortzugriff verweigert. Um diese Funktion zu nutzen, aktivieren Sie die Standortfreigabe in Ihren Browser- oder Telefoneinstellungen.",
    "location.tryAgain": "Erneut versuchen",
    "location.openSettings": "Anweisungen anzeigen",
    "location.androidSettingsHint": "Gehen Sie zu Einstellungen → Apps → Ihr Browser → Berechtigungen → Standort",
    "location.iosSettingsHint": "Gehen Sie zu Einstellungen → Datenschutz & Sicherheit → Standortdienste → Ihr Browser",
    "location.desktopSettingsHint": "Klicken Sie auf das Schloss-Symbol in der Adressleiste und aktivieren Sie den Standortzugriff.",
    "location.promptDesc": "Wir benötigen Ihren Standort, um relevante Ergebnisse in der Nähe anzuzeigen.",
    "location.allowAccess": "Standortzugriff erlauben",
    "location.fetchErrorTitle": "Standort konnte nicht abgerufen werden",
    "location.timeoutDesc": "Der Abruf Ihres Standorts dauerte zu lange. Stellen Sie sicher, dass GPS aktiviert ist.",
    "location.unavailableDesc": "Ihr Standort konnte gerade nicht bestimmt werden. Versuchen Sie es gleich erneut.",
    "location.errorTitle": "Standortfehler",
    "location.errorDesc": "Beim Abrufen Ihres Standorts ist etwas schiefgelaufen.",
    "season.springGreeting": "Der Frühling ist da – Zeit zu planen!",
    "season.summer": "Sommer",
    "season.summerGreeting": "Der Sommer wartet – welche Route schlägst du ein?",
    "season.autumn": "Herbst",
    "season.autumnGreeting": "Die Herbstfarben rufen!",
    "season.winter": "Winter",
    "season.winterGreeting": "Der Winter ist magisch – planen Sie einen Skiurlaub!",
    "map.searchPlaceholder": "Stadt, Camping, Adresse suchen...",
    "map.searching": "Suche...",
    "map.searchingSuggestions": "Suche nach Vorschlägen…",
    "map.noResults": "Keine Ergebnisse gefunden",
    "map.noResultsHint": "Versuchen Sie Stadt, Camping, Adresse oder Ortsname",
    "map.exploreEurope": "Europa erkunden",
    "map.exploreDesc": "Suchen Sie nach einem Ort, um Campingplätze, Stellplätze und Sehenswürdigkeiten zu entdecken",
    "map.exploreDescLong": "Suchen Sie nach einem Ort, um Campingplätze, Restaurants, Sehenswürdigkeiten und mehr zu entdecken",
    "map.resultsNear": "Ergebnisse in der Nähe von",
    "map.noCampingsFound": "Keine Campingplätze oder Stellplätze in der Nähe gefunden.",
    "map.mapErrorBanner": "Die Karte konnte nicht geladen werden. Sie können weiterhin Orte in der Listenansicht suchen.",
    "map.nearYou": "Orte in Ihrer Nähe",
    "map.places": "Orte",
    "map.placesNearYou": "Orte in der Nähe",
    "map.campingsNearby": "Campingplätze & Stellplätze in der Nähe",
    "map.explore": "Erkunden",
    "map.closePanel": "Panel schließen",
    "map.showResults": "Ergebnisse anzeigen",
    "map.navigate": "Navigieren",
    "map.addToTrip": "Zur Reise hinzufügen",
    "map.open": "Geöffnet",
    "map.closed": "Geschlossen",
    "map.free": "Kostenlos",
    "map.camping": "Camping",
    "map.stellplatz": "Stellplatz",
    "map.dumpStation": "Entsorgungsstation",
    "map.navigateHit": "Hierhin navigieren",
    "map.place": "Ort",
    "map.restaurant": "Restaurant",
    "map.supermarket": "Supermarkt",
    "map.attraction": "Sehenswürdigkeit",
    "map.parking": "Parkplatz",
    "map.fuelStation": "Tankstelle",
    "map.museum": "Museum",
    "map.nature": "Natur",
    "map.sights": "Sehenswürdigkeiten",
    "map.grocery": "Supermarkt",
    "map.perNight": "/Nacht",
    "submit.addressAutoHint": "Suchen Sie zuerst die Adresse — Land, Region und Stadt werden automatisch ausgefüllt.",
    "submit.autoDetected": "Land, Region und Stadt wurden automatisch ausgefüllt!",
    "submit.placeExists": "Ort existiert bereits",
    "submit.placeExistsDesc": "Ein Ort namens \"{name}\" ist bereits registriert.",
    "submit.nearbyExists": "Ort in der Nähe existiert bereits",
    "submit.nearbyExistsDesc": "\"{name}\" ist bereits am selben Standort registriert.",
    "submit.imageRejected": "Bild abgelehnt",
    "submit.mainImage": "Hauptbild",
    "submit.contactInfo": "Kontaktdaten",
    "service.heroDesc": "Finden Sie Werkstätten, Tankstellen, Wasser, Krankenhäuser und mehr — überall in Europa",
    "service.close": "Schließen",
    "service.clearSearch": "Löschen",
    "service.search": "Suchen",
    "service.positionSharing": "Positionsfreigabe",
    "service.positionSharingDesc": "Teilen Sie Ihre GPS-Position oder aktivieren Sie SOS",
    "service.connectionError": "Überprüfen Sie Ihre Verbindung und versuchen Sie es erneut",
    "service.tryAgain": "Erneut versuchen",
    "service.noResultsHint": "Versuchen Sie etwas anderes zu suchen oder wechseln Sie die Kategorie",
    "service.resultsNearYou": "in der Nähe",
    "budget.noPeriod": "Kein Zeitraum",
    "budget.exceededBy": "Überschritten um",
    "budget.expenseDeleted": "Ausgabe gelöscht",
    "budget.undo": "Rückgängig",
    "budget.receipt": "Beleg",
    "budget.trip": "Reise",
    "whatsNew.title": "Was gibt's Neues",
    "whatsNew.subtitle": "Neueste Updates und Funktionen",
    "whatsNew.seoTitle": "Was gibt's Neues – Vantastika",
    "whatsNew.seoDesc": "Sehen Sie die neuesten Updates und Neuigkeiten in Vantastika.",
    "whatsNew.all": "Alle",
    "whatsNew.noUpdates": "Keine Updates im Moment",
    "whatsNew.type.new_feature": "Neue Funktion",
    "whatsNew.type.improvement": "Verbesserung",
    "whatsNew.type.bugfix": "Fehlerbehebung",
    "whatsNew.type.important": "Wichtig",
    "whatsNew.type.maintenance": "Wartung",
    "whatsNew.readMore": "Mehr lesen",
    "search.benefit1": "Alle Campingplätze und Stellplätze sehen",
    "search.benefit2": "Nach Ausstattung und Preis filtern",
    "search.benefit3": "Bewertungen lesen und Sterne sehen",
    "search.smartPlaceholder": "Orte, Campingplätze, Stellplätze, Städte suchen...",
    "search.recommendedFor": "Empfohlen für",
    "search.typeCategory": "Kategorie",
    "search.typeRegion": "Gebiet",
    "search.typeCountry": "Land",
    "search.typePlace": "Ort",
    "search.noResultsHint": "Versuche einen anderen Suchbegriff oder entferne einige Filter",
    "search.browseByCountry": "Nach Land durchsuchen",
    "travelGuides.badge": "Reiseführer",
    "travelGuides.heroTitle1": "Entdecke Europas beste ",
    "travelGuides.heroTitle2": "Reiserouten",
    "travelGuides.heroSubtitle": "Detaillierte Guides mit Geschichte, Highlights und praktischen Infos. Verwandle Inspiration in eine echte Reise mit einem Klick.",
    "travelGuides.popular": "Beliebte Reiseziele",
    "travelGuides.moreCountries": "Weitere Länder",
    "travelGuides.guidesCount": "{n} Guides",
    "travelGuides.guidesSingular": "1 Guide",
    "travelGuides.explore": "Entdecken",
    "countryGuide.titlePrefix": "Reiseführer",
    "countryGuide.allGuides": "Alle Reiseführer",
    "countryGuide.seoDesc": "Kompletter Wohnmobil-Reiseführer für",
    "countryGuide.overview": "Überblick",
    "countryGuide.campingRules": "Camping & Übernachtung",
    "countryGuide.campingRegulations": "Campingregeln",
    "countryGuide.wildCamping": "Wildcamping",
    "countryGuide.campingTips": "Campingtipps",
    "countryGuide.bestSeason": "Beste Reisezeit",
    "countryGuide.drivingInfo": "Fahren & Straßen",
    "countryGuide.trafficRules": "Verkehrsregeln",
    "countryGuide.speedLimits": "Geschwindigkeitsbegrenzungen",
    "countryGuide.tollsVignettes": "Maut & Vignetten",
    "countryGuide.currency": "Währung",
    "countryGuide.fuel": "Kraftstoff",
    "countryGuide.emergency": "Notruf",
    "countryGuide.language": "Sprache",
    "countryGuide.bestPlaces": "Beste Orte zu besuchen",
    "countryGuide.safetyTips": "Sicherheitstipps",
    "countryGuide.practicalTips": "Praktische Tipps",
    "countryGuide.routeGuides": "Routenführer",
    "countryGuide.guide": "Guide",
    "countryGuide.readGuide": "Guide lesen",
    "countryGuide.searchCampings": "Campingplätze suchen",
    "countryGuide.showOnMap": "Auf Karte zeigen",
    "countryGuide.planTrip": "Reise planen",
    "countryGuide.planTripHere": "Reise hierhin planen",
    "countryGuide.campings": "Campingplätze",
    "guideDetail.days": "Tage",
    "guideDetail.history": "Geschichte & Hintergrund",
    "guideDetail.routeInfo": "Routeninformation",
    "guideDetail.startPoint": "Startpunkt",
    "guideDetail.endPoint": "Endpunkt",
    "guideDetail.distance": "Entfernung",
    "guideDetail.direction": "Richtung",
    "guideDetail.passesThrough": "Führt durch",
    "guideDetail.overview": "Überblick",
    "guideDetail.highlights": "Highlights",
    "guideDetail.practical": "Praktisch",
    "guideDetail.bestSeason": "Beste Reisezeit",
    "guideDetail.vehicleType": "Fahrzeugtyp",
    "guideDetail.motorhome": "Wohnmobil",
    "guideDetail.car": "Auto",
    "guideDetail.motorcycle": "Motorrad",
    "guideDetail.campervan": "Campervan",
    "guideDetail.warnings": "Zu beachten",
    "guideDetail.practicalTips": "Praktische Tipps",
    "guideDetail.whyVisit": "Warum besuchen: ",
    "guideDetail.readyToGo": "Bereit loszufahren?",
    "guideDetail.readyToGoDesc": "Verwandeln Sie diesen Guide in eine echte Reise im Reiseplaner. Passen Sie Stopps, Anzahl der Tage und Rückreise nach Ihren Wünschen an.",
    "guideDetail.planThisTrip": "Diese Reise planen",
    "guideDetail.refreshGuide": "Guide aktualisieren",
    "guideDetail.generating": "Erstelle detaillierten Reiseführer mit KI...",
    "guideDetail.loading": "Lade Reiseführer...",
    "guideDetail.premiumBenefit1": "Lesen Sie den kompletten Reiseführer mit Stopps und Tipps",
    "guideDetail.premiumBenefit2": "Detaillierte Routeninformationen ansehen",
    "guideDetail.premiumBenefit3": "Den Guide in eine echte Reise verwandeln",
    "destination.motorhome": "Wohnmobil in",
    "destination.planTrip": "Planen Sie Ihre Wohnmobilreise nach",
    "destination.planNow": "Reise jetzt planen",
    "destination.viewGuides": "Reiseführer ansehen",
    "destination.whatWeOffer": "Was Vantastika bietet",
    "destination.featureRoutes": "Routenplanung",
    "destination.featureRoutesDesc": "Planen Sie Ihre Route mit Stopps, Entfernungen und Fahrzeiten.",
    "destination.featureCamping": "Campingplätze & Stellplätze",
    "destination.featureCampingDesc": "Finden Sie die besten Campingplätze und Wohnmobilstellplätze.",
    "destination.featureRules": "Verkehrsregeln",
    "destination.featureRulesDesc": "Geschwindigkeitsbegrenzungen, Maut und lokale Vorschriften.",
    "destination.featureSafety": "Sicherheit & SOS",
    "destination.featureSafetyDesc": "Notrufnummern, Pannenhilfe und Sicherheitstipps.",
    "destination.popularRoutes": "Beliebte Routen in",
    "destination.readyToGo": "Bereit für die Reise?",
    "destination.ctaDesc": "Erstellen Sie ein kostenloses Konto und planen Sie Ihren Roadtrip noch heute.",
    "destination.getStarted": "Kostenlos starten",
    "destination.findCampings": "Campingplätze finden",
    "share.shareOnInstagram": "Auf Instagram teilen",
    "share.shareOnFacebook": "Auf Facebook teilen",
    "share.linkCopied": "Link kopiert!",
    "share.linkCopiedDesc": "Einfügen wo Sie möchten.",
    "ig.headline": "Plane deine Reise smarter mit Vantastika",
    "ig.subheadline": "Route, Budget und Reiseplanung an einem Ort für Roadtrips, Wohnmobil und Autourlaub.",
    "ig.ctaStart": "Kostenlos starten",
    "ig.ctaOpen": "Vantastika öffnen",
    "ig.ctaDemo": "So funktioniert's",
    "ig.demoTitle": "So einfach planst du deine Reise",
    "ig.benefit1.title": "Plane deine gesamte Route",
    "ig.benefit1.desc": "Erstelle deine Route mit Stopps, Entfernungen und Zeiten.",
    "ig.benefit2.title": "Behalte das Reisebudget im Blick",
    "ig.benefit2.desc": "Sieh, was die Reise kostet — vorher und unterwegs.",
    "ig.benefit3.title": "Bessere Übersicht",
    "ig.benefit3.desc": "Alles an einem Ort, vor und während der Reise.",
    "ig.free": "Kostenlos. Keine Kreditkarte erforderlich.",
    "projects.title": "Projekte",
    "projects.myProjects": "Meine Projekte",
    "projects.subtitle": "Plane und verfolge deine Bau- und Renovierungsprojekte",
    "projects.create": "Neues Projekt",
    "projects.createTitle": "Projekt erstellen",
    "projects.createFirst": "Erstelle dein erstes Projekt",
    "projects.creating": "Wird erstellt...",
    "projects.emptyTitle": "Noch keine Projekte",
    "projects.emptyDesc": "Erstelle ein Projekt, um deine Bauprojekte, Renovierungen und Upgrades mit Materiallisten, Aufgaben und Budget zu organisieren.",
    "projects.name": "Projektname",
    "projects.namePlaceholder": "z.B. Batterieumbau",
    "projects.description": "Beschreibung",
    "projects.descPlaceholder": "Worum geht es in diesem Projekt?",
    "projects.categoryLabel": "Kategorie",
    "projects.priorityLabel": "Priorität",
    "projects.startDate": "Startdatum",
    "projects.endDate": "Enddatum",
    "projects.budget": "Budget",
    "projects.actualCost": "Tatsächliche Kosten",
    "projects.notes": "Notizen",
    "projects.notesPlaceholder": "Notizen hinzufügen...",
    "projects.save": "Speichern",
    "projects.edit": "Bearbeiten",
    "projects.cancel": "Abbrechen",
    "projects.add": "Hinzufügen",
    "projects.share": "Teilen",
    "projects.notFound": "Projekt nicht gefunden",
    "projects.backToProjects": "Zurück zu Projekten",
    "projects.status.not_started": "Nicht begonnen",
    "projects.status.in_progress": "In Arbeit",
    "projects.status.completed": "Abgeschlossen",
    "projects.priority.low": "Niedrig",
    "projects.priority.medium": "Mittel",
    "projects.priority.high": "Hoch",
    "projects.priority.urgent": "Dringend",
    "projects.category.battery": "Batterie",
    "projects.category.solar": "Solarpanel",
    "projects.category.bathroom": "Badezimmer",
    "projects.category.kitchen": "Küche",
    "projects.category.water_system": "Wassersystem",
    "projects.category.electrical": "Elektrik",
    "projects.category.interior": "Innenraum",
    "projects.category.exterior": "Außenbereich",
    "projects.category.mechanical": "Mechanik",
    "projects.category.other": "Sonstiges",
    "projects.tab.overview": "Übersicht",
    "projects.tab.materials": "Materialien",
    "projects.tab.tasks": "Aufgaben",
    "projects.tab.files": "Dateien",
    "projects.tab.history": "Verlauf",
    "projects.tasksCompleted": "Aufgaben erledigt",
    "projects.materialsBought": "Materialien gekauft",
    "projects.budgetOverview": "Budgetübersicht",
    "projects.materialsCost": "Materialkosten",
    "projects.spent": "Ausgegeben",
    "projects.remaining": "Verbleibend",
    "projects.total": "Gesamt",
    "projects.bought": "Gekauft",
    "projects.addMaterial": "Material hinzufügen",
    "projects.materialName": "Produktname",
    "projects.quantity": "Menge",
    "projects.price": "Preis",
    "projects.storeName": "Geschäft",
    "projects.storeUrl": "Link (URL)",
    "projects.noMaterials": "Noch keine Materialien hinzugefügt",
    "projects.addTask": "Aufgabe hinzufügen",
    "projects.taskTitle": "Aufgabentitel",
    "projects.taskDesc": "Beschreibung",
    "projects.noTasks": "Noch keine Aufgaben hinzugefügt",
    "projects.completedAt": "Abgeschlossen",
    "projects.status_label": "Status",
    "projects.filter.all": "Alle",
    "projects.filter.bought": "Gekauft",
    "projects.filter.not_bought": "Nicht gekauft",
    "projects.filter.pending": "Ausstehend",
    "projects.filter.done": "Erledigt",
    "projects.uploadFile": "Datei hochladen",
    "projects.uploading": "Wird hochgeladen...",
    "projects.maxFileSize": "Max. 15 MB",
    "projects.fileTooLarge": "Datei ist zu groß (max. 15 MB)",
    "projects.fileUploaded": "Datei hochgeladen",
    "projects.uploadError": "Upload fehlgeschlagen",
    "projects.noFiles": "Noch keine Dateien hinzugefügt",
    "projects.noHistory": "Noch keine Aktivität",
    "projects.shareProject": "Projekt teilen",
    "projects.generateLink": "Freigabelink erstellen",
    "projects.linkGenerated": "Freigabelink erstellt",
    "projects.exportPdf": "Als PDF exportieren",
    "projects.pdfFull": "Gesamtes Projekt",
    "projects.pdfMaterials": "Nur Materialien",
    "projects.pdfTasks": "Nur Aufgaben",
    "projects.pdfSummary": "Zusammenfassung",
    "projects.deleteProject": "Projekt löschen",
    "projects.confirmDeleteTitle": "Projekt löschen?",
    "projects.confirmDeleteDesc": "Dies löscht das Projekt und alle Materialien, Aufgaben, Dateien und den Verlauf dauerhaft. Diese Aktion kann nicht rückgängig gemacht werden.",
    "projects.deleted": "Projekt gelöscht",
    "projects.vehicleLabel": "Fahrzeug",
    "projects.linkedVehicle": "Verknüpftes Fahrzeug",
    "projects.linkVehicle": "Fahrzeug verknüpfen",
    "projects.noVehicle": "Kein Fahrzeug verknüpft",
    "projects.selectVehicle": "Fahrzeug auswählen",
    "projects.noVehicleOption": "Keins",
    "projects.duplicate": "Duplizieren",
    "projects.editMaterial": "Material bearbeiten",
    "projects.editTask": "Aufgabe bearbeiten",
    "projects.saveMaterial": "Material speichern",
    "projects.saveTask": "Aufgabe speichern",
    "projects.swipeToDelete": "Wischen zum Löschen",
    "projects.moreActions": "Mehr Aktionen",
    "projects.editProject": "Projekt bearbeiten",
    "upload.validating": "Validierung...",
    "upload.processing": "Bild wird verarbeitet...",
    "upload.converting": "HEIC wird konvertiert...",
    "upload.resizing": "Größe wird geändert...",
    "upload.compressing": "Wird komprimiert...",
    "upload.thumbnailing": "Vorschaubild wird erstellt...",
    "upload.almostDone": "Fast fertig...",
    "upload.uploading": "Wird hochgeladen...",
    "upload.done": "Fertig!",
    "upload.failed": "Upload fehlgeschlagen",
    "upload.fileTooLarge": "Datei ist zu groß",
    "upload.imageRejected": "Bild abgelehnt",
  },
  da: {
    "offline.message": "Du er offline — gemte data vises fra cache",
    "offline.syncing": "Synkroniserer data...",
    "offline.synced": "Alt synkroniseret!",
    "confirm.deleteTitle": "Er du sikker?",
    "confirm.deleteDesc": "Denne handling kan ikke fortrydes.",
    "confirm.cancel": "Annuller",
    "confirm.delete": "Slet",
    "confirm.deleteTrip": "Slet denne tur?",
    "confirm.deleteTripDesc": "Turen og alle data slettes permanent.",
    "confirm.deletePost": "Slet dette opslag?",
    "confirm.deletePostDesc": "Opslaget slettes permanent.",
    "confirm.deleteBudget": "Slet dette budget?",
    "confirm.deleteBudgetDesc": "Budgettet og alle udgifter slettes permanent.",
    "confirm.deleteNotification": "Slet denne notifikation?",
    "confirm.deleteNotificationDesc": "Notifikationen slettes permanent.",
    "confirm.deleteMaterial": "Slet dette materiale?",
    "confirm.deleteMaterialDesc": "Materialet slettes permanent fra projektet.",
    "confirm.deleteTask": "Slet denne opgave?",
    "confirm.deleteTaskDesc": "Opgaven slettes permanent fra projektet.",
    "confirm.deleteFile": "Slet denne fil?",
    "confirm.deleteFileDesc": "Filen slettes permanent.",
    "confirm.deleteMessage": "Slet denne besked?",
    "confirm.deleteMessageDesc": "Beskeden slettes permanent.",
    "confirm.deleteUpdate": "Slet denne opdatering?",
    "confirm.deleteUpdateDesc": "Opdateringen slettes permanent.",
    "confirm.deleteCamping": "Slet denne campingplads permanent?",
    "confirm.deleteCampingDesc": "Campingpladsen slettes permanent.",
    "confirm.unpublishTrip": "Afpublicer denne tur?",
    "confirm.unpublishTripDesc": "Turen vil ikke længere være synlig for andre.",
    "confirm.endTrip": "Afslut denne tur?",
    "confirm.endTripDesc": "Turen markeres som afsluttet.",
    "nav.explore": "Udforsk Campingpladser",
    "nav.map": "Kort",
    "nav.trips": "Planlæg en tur",
    "nav.favorites": "Gemte steder",
    "nav.login": "Log ind",
    "nav.signup": "Kom gratis i gang",
    "nav.logout": "Log ud",
    "nav.bottom.trips": "Ny tur",
    "nav.bottom.explore": "Udforsk",
    "nav.bottom.map": "Kort",
    "nav.bottom.saved": "Gemte",
    "nav.bottom.profile": "Profil",
    "nav.bottom.login": "Log ind",
    "nav.bottom.signup": "Tilmeld",
    "nav.bottom.home": "Hjem",
    "nav.bottom.newTrip": "Ny tur",
    "nav.bottom.myTrips": "Mine ture",
    "nav.checklists": "Tjeklister",
    "nav.travelGuides": "Rejseguider",
    "nav.menuMine": "Mig",
    "nav.menuCreate": "Opret",
    "nav.settings": "Indstillinger",
    "footer.integrationTerms": "Integrationvilkår",
    "dash.greeting.morning": "Godmorgen",
    "dash.greeting.day": "Hej",
    "dash.greeting.afternoon": "God eftermiddag",
    "dash.greeting.evening": "Godaften",
    "dash.greeting.night": "Godnat",
    "dash.subtitle.active": "Din tur er i gang – her er din status",
    "dash.subtitle.planning": "Du har ture, der skal forberedes",
    "dash.subtitle.completed": "Godt klaret! Her er din tur-oversigt",
    "dash.subtitle.empty": "Hvad vil du lave i dag?",
    "dash.paused": "Pauset",
    "dash.ongoing": "Tur i gang",
    "dash.total": "Total",
    "dash.stops": "Stop",
    "dash.done": "Færdig",
    "dash.nextDest": "Næste destination",
    "dash.continueTrip": "Fortsæt tur",
    "dash.openActive": "Åbn aktiv tur",
    "dash.journal": "Dagbog",
    "dash.budget": "Budget",
    "dash.findPlace": "Find sted",
    "dash.upcoming": "Kommende tur",
    "dash.today": "I dag!",
    "dash.tomorrow": "I morgen",
    "dash.daysLeft": "{n} dage tilbage",
    "dash.continuePlan": "Fortsæt planlægning",
    "dash.start": "Start",
    "dash.morePlanned": "Flere planlagte ture",
    "dash.findCamping": "Find camping",
    "dash.findCampingSub": "2.800+ pladser",
    "dash.guides": "Rejseguider",
    "dash.guidesSub": "21+ lande",
    "dash.checklists": "Tjeklister",
    "dash.checklistsSub": "Pak smart",
    "dash.wishlist": "Ønskeliste",
    "dash.vehicle": "Køretøj",
    "dash.projects": "Projekter",
    "dash.community": "Fællesskab",
    "dash.popularDest": "Populære destinationer",
    "dash.seeAll": "Se alle",
    "dash.planNew": "Planlæg ny rejse",
    "dash.planNewSub": "Trin-for-trin guide",
    "dash.season.spring": "Forårseventyr",
    "dash.season.summer": "Sommer på vejen",
    "dash.season.autumn": "Efterårets roadtrips",
    "dash.season.winter": "Vintereventyr",
    "dash.tripDone": "Tur afsluttet",
    "dash.visited": "Besøgt",
    "dash.seeSummary": "Se oversigt",
    "dash.planNewOld": "Planlæg ny tur",
    "dash.emptyTitle": "Tid til at planlægge din første tur!",
    "dash.emptyDesc": "Opret en tur og tilføj stop, budget og tjeklister.",
    "dash.createTrip": "Opret tur",
    "dash.stopsCount": "{count} stop",
    "dash.visitedCount": "{count} besøgt",
    "dash.onboarding.step1": "Planlæg en tur",
    "dash.onboarding.step1Desc": "Opret din første roadtrip med stop og datoer",
    "dash.onboarding.step2": "Gem drømmedestinationer",
    "dash.onboarding.step2Desc": "Byg din ønskeliste med steder at besøge",
    "dash.onboarding.step3": "Udforsk campingpladser",
    "dash.onboarding.step3Desc": "Gennemse 2.800+ campingpladser i Europa",
    "dash.onboarding.stepVehicle": "Opsæt dit køretøj",
    "dash.onboarding.stepVehicleDesc": "Tilføj din autocamper, bil eller campingbus",
    "dash.onboarding.stepProject": "Start et projekt",
    "dash.onboarding.stepProjectDesc": "Følg renoveringer, opgraderinger og byggerier",
    "dash.emptyDescReturning": "Planlæg din første tur – dine gemte data er klar til brug!",
    "dash.recentProjects": "Dine projekter",
    "dash.popularGuides": "Populære rejseguider",
    "dash.noProjects": "Ingen projekter endnu",
    "dash.draft": "Kladde",
    "dash.continueDraft": "Fortsæt planlægning",
    "dash.untitled": "Unavngivet tur",
    "dash.nextStep": "Næste trin",
    "dash.nextStepStops": "Tilføj dit første stop",
    "dash.nextStepDate": "Vælg rejsedato",
    "dash.nextStepChecklist": "Færdiggør tjeklisten",
    "dash.prepTitle": "Forbered din tur",
    "dash.prepBudget": "Sæt et budget for at holde styr på udgifterne",
    "dash.prepStops": "Tilføj flere stop til din rute",
    "dash.prepChecklist": "Gennemgå din pakkeliste",
    "dash.packed": "pakket",
    "dash.incompleteTitle": "Afslut planlægning",
    "dash.incompleteDate": "Tilføj en afrejsedato",
    "dash.incompleteStops": "Tilføj stop til din rute",
    "dash.postTripTitle": "Hvad nu?",
    "dash.postTripShare": "Del din tur med venner",
    "dash.postTripReview": "Bedøm de steder du besøgte",
    "dash.postTripJournal": "Skriv om din oplevelse",
    "dash.postTripMsg": "Du kørte {km} km og besøgte {stops} steder — et stort eventyr!",
    "nav.myTrips": "Mine ture",
    "nav.submit": "Tilføj et sted",
    "nav.help": "Sådan bruger du appen",
    "nav.compare": "Sammenlign campingpladser",
    "nav.guides": "Landeguider",
    "nav.contact": "Kontakt os",
    "help.title": "Sådan bruger du appen",
    "search.startTyping": "Begynd at skrive for at søge",
    "search.startTypingDesc": "Søg på stednavn, by, kategori eller destinationstype",
    "search.clickForDetails": "Klik på et kort for detaljer",
    "search.searchHint": "Søg på navn, kategori, by eller region. Smart søgning finder det rigtige sted for dig.",
    "search.foundOnGoogle": "ekstra forslag fra Google",
    "search.addHint": "Tryk på et kort for at tilføje til Vantastika",
    "search.fromGoogle": "Fra Google",
    "search.addToVantastika": "Tilføj til Vantastika",
    "search.googleAdding": "Tilføjer...",
    "search.googleAddedShort": "Sendt til gennemgang",
    "search.googleAdded": "Sted indsendt",
    "search.googleAddedDesc": "Tak! Det bliver synligt efter admin-gennemgang.",
    "search.googleAlreadyExists": "Findes allerede i Vantastika",
    "search.googleAlreadyExistsDesc": "Vi sender dig til den eksisterende side.",
    "search.googleAddError": "Kunne ikke tilføje stedet. Prøv igen.",
    "search.nearbyFound": "campingpladser nær dig",
    "search.within25km": "inden for 25 km",
    "search.noNearby": "Ingen campingpladser i nærheden",
    "search.noNearbyDesc": "Prøv kortet for at udforske længere væk.",
    "search.openMap": "Åbn kort",
    "search.findingLocation": "Finder din placering...",
    "search.findingLocationDesc": "Vi bruger den til at vise campingpladser nær dig.",
    "search.locationDeniedHint": "Placeringsadgang er nægtet. Tillad det for at se campingpladser nær dig.",
    "search.enableLocationHint": "Tillad placeringsadgang for at se nærmeste campingpladser.",
    "search.allowLocation": "Tillad placering",
    "search.topRated": "Bedst bedømte campingpladser",
    "help.whatIs.title": "Hvad er Vantastika?",
    "help.whatIs.body": "Din rejseledsager til autocamper-, campervan- og bilture i hele Europa. Find campingpladser, planlæg ruter og hold styr på dit budget – alt på ét sted.",
    "help.startTrip.title": "Start en tur",
    "help.startTrip.body": "Tryk på \"Trip Planner\", giv din tur et navn, tilføj dine stop og datoer. Appen beregner afstande og brændstofomkostninger for dig.",
    "help.search.title": "Søg efter steder",
    "help.search.body": "Brug \"Udforsk\" eller søgesiden til at finde campingpladser, pladser og servicestationer nær enhver lokation i Europa.",
    "help.map.title": "Brug kortet",
    "help.map.body": "Åbn \"Udforsk\" for at se kortet. Søg efter en by eller brug din nuværende placering. Tryk på markører for detaljer og rutevejledning.",
    "help.addStop.title": "Tilføj et stop til din tur",
    "help.addStop.body": "Når du ser en campingplads, skal du trykke på \"Tilføj til tur\" for at inkludere den i din rute. Du kan omarrangere og redigere stop når som helst.",
    "help.share.title": "Del & bliv inspireret",
    "help.share.body": "Gør din tur offentlig, så andre kan se din rute. Gennemse delte ture fra fællesskabet for idéer.",
    "help.favorites.title": "Gem favoritter",
    "help.favorites.body": "Tryk på hjerteikonet på enhver campingplads for at gemme den. Organiser favoritter i samlinger for nem adgang senere.",
    "hero.title1": "Planlæg hele din tur",
    "hero.title2": "på ét sted.",
    "hero.subtitle": "Planlæg din tur, tilføj stop, find steder på kortet og hold styr på dit budget — uden at skifte mellem apps.",
    "hero.searchPlaceholder": "Hvor vil du hen?",
    "hero.search": "Søg",
    "hero.ctaPlan": "Planlæg min tur — gratis",
    "hero.ctaExplore": "Find Campingpladser",
    "hero.ctaSubmit": "Kender du et godt sted?",
    "hero.valueProps": "Alt på ét sted — ikke mere at skifte mellem apps",
    "hero.diffTitle": "Hvorfor Vantastika?",
    "hero.diff1": "Turplanlægning, stop og budget i ét flow",
    "hero.diff2": "Scan kvitteringer og spor udgifter automatisk",
    "hero.diff3": "Tilføj stop direkte fra kortet",
    "hero.diff4": "Til autocampere, biler og roadtrips",
    "features.find": "Find steder på kortet",
    "features.findDesc": "Campingpladser, attraktioner, restauranter og flere stop — direkte ind i din plan",
    "features.save": "Gem favoritter",
    "features.saveDesc": "Hold styr på dine steder, du skal besøge",
    "features.plan": "Planlæg din tur",
    "features.planDesc": "Opret en rute, tilføj stop og byg din tur trin for trin",
    "features.budget": "Spor dit budget",
    "features.budgetDesc": "Log udgifter, scan kvitteringer og se, hvor pengene bliver af",
    "features.europe": "Hele Europa",
    "features.europeDesc": "Tusindvis af verificerede steder i hele Europa",
    "featured.title": "Populære Campingpladser",
    "featured.viewAll": "Vis alle →",
    "search.title": "Udforsk Campingpladser",
    "search.placeholder": "Søg campingpladser...",
    "search.country": "Land",
    "search.allCountries": "Alle lande",
    "search.filters": "Filtre",
    "search.clearAll": "Ryd alle",
    "search.minPrice": "Min pris",
    "search.maxPrice": "Max pris",
    "search.region": "Region",
    "search.regionPlaceholder": "f.eks. Dalarna",
    "search.noResults": "Ingen campingpladser fundet",
    "search.adjustFilters": "Prøv at justere dine filtre",
    "search.found": "campingpladser fundet",
    "filter.electricity": "El-tilslutning",
    "filter.freshWater": "Ferskvand",
    "filter.greyWater": "Spildevandsudledning",
    "filter.toilet": "Toiletkassette-udledning",
    "filter.shower": "Bad",
    "filter.wc": "Toilet",
    "filter.petFriendly": "Kæledyrsvenlig",
    "filter.openAllYear": "Åben hele året",
    "filter.familyFriendly": "Familievenlig",
    "filter.largeMotorhomes": "Store autocampere",
    "filter.nearWater": "Nær vand/strand",
    "filter.spaWellness": "Spa/wellness i nærheden",
    "filter.wifi": "WiFi",
    "filter.laundry": "Vaskeri",
    "filter.restaurant": "Restaurant",
    "detail.about": "Om",
    "detail.amenities": "Faciliteter",
    "detail.contact": "Kontakt",
    "detail.website": "Hjemmeside",
    "detail.checkIn": "Check-in",
    "detail.checkOut": "Check-ud",
    "detail.location": "Lokation",
    "detail.openInMaps": "Åbn i Google Maps →",
    "detail.mapComingSoon": "Kort kommer snart",
    "detail.back": "Tilbage",
    "detail.save": "Gem",
    "detail.saved": "Gemt",
    "detail.perNight": "per nat",
    "detail.reviews": "Anmeldelser",
    "detail.writeReview": "Skriv en anmeldelse",
    "detail.noReviews": "Ingen anmeldelser endnu",
    "detail.submitReview": "Send anmeldelse",
    "detail.yourRating": "Din bedømmelse",
    "detail.yourComment": "Din kommentar (valgfrit)",
    "detail.loginToReview": "Log ind for at skrive en anmeldelse",
    "detail.notFound": "Campingplads ikke fundet",
    "detail.backToSearch": "Tilbage til søgning",
    "favorites.title": "Mine favoritter",
    "favorites.saveYour": "Gem dine yndlings campingpladser",
    "favorites.signUpPrompt": "Tilmeld dig eller log ind for at gemme campingpladser i dine favoritter.",
    "favorites.empty": "Ingen favoritter endnu",
    "favorites.emptyDesc": "Begynd at udforske og gem campingpladser, du elsker",
    "favorites.explore": "Udforsk campingpladser",
    "favorites.emptyCollection": "Ingen favoritter i denne samling",
    "wishlist.title": "Ønskeliste",
    "wishlist.save": "Gem i ønskeliste",
    "wishlist.saved": "Gemt i ønskelisten!",
    "wishlist.alreadySaved": "Allerede i ønskelisten",
    // SaveGateSheet
    "saveGate.wishlist.savedFor": "Vi har gemt stedet",
    "saveGate.trip.savedFor": "Vi har gemt rejsen",
    "saveGate.place.savedFor": "Vi har gemt",
    "saveGate.holdsFor24h": "Vi gemmer det i 24 timer — opret en konto, så det aldrig forsvinder.",
    "saveGate.wishlist.benefit1": "Synkroniser mellem telefon og computer",
    "saveGate.wishlist.benefit2": "Få besked når prisen falder",
    "saveGate.wishlist.benefit3": "Del med rejsekammeraten",
    "saveGate.trip.benefit1": "Aktivér offline-tilstand på vejen",
    "saveGate.trip.benefit2": "Smart navigation tilpasset dit køretøj",
    "saveGate.trip.benefit3": "Del live-position med familien",
    "saveGate.place.benefit1": "Gem dit sted permanent",
    "saveGate.place.benefit2": "Få anbefalinger til steder i nærheden",
    "saveGate.place.benefit3": "Tilgængeligt på alle enheder",
    "saveGate.socialProof": "rejsende i Norden bruger Vantastika",
    "saveGate.createAccount15s": "Opret konto på 15 sek",
    "saveGate.alreadyMember": "Allerede medlem?",
    "saveGate.logIn": "Log ind",
    "saveGate.continueGuest": "Fortsæt som gæst — gemmes 24 t",
    "saveGate.noCardNeeded": "Gratis. Intet kort kræves.",
    // GuestRescueSheet
    "guestRescue.title": "Du har {count} gemte ting i fare",
    "guestRescue.expiresIn": "Udløber om {time}",
    "guestRescue.expired": "Udløbet — opret konto for at gendanne",
    "guestRescue.trip": "Rejse",
    "guestRescue.wishlist": "Ønskeliste",
    "guestRescue.rescueCta": "Red mine ting — opret konto",
    "guestRescue.footnote": "Gratis for evigt. Intet betalingskort.",
    "saveGate.hoursShort": "t",
    "saveGate.minutesShort": "min",
    "guestRescue.toastTitle": "Tip: Opret en konto",
    "guestRescue.toastBody": "Gem på alle enheder, så du aldrig mister dine rejser.",
    "guestRescue.toastCta": "Gør det nu",
    "nav.bottom.guestSaves": "{count} gemt",
    "wishlist.loginMsg": "Log ind for at gemme steder, du vil besøge.",
    "wishlist.empty": "Din ønskeliste er tom",
    "wishlist.emptyDesc": "Gem steder du drømmer om at besøge og planlæg ture derfra senere.",
    "wishlist.add": "Tilføj sted",
    "wishlist.searchPlace": "Søg efter et sted at gemme",
    "wishlist.searchPlaceholder": "By, land, attraktion, camping...",
    "wishlist.myNote": "Min note",
    "wishlist.notePlaceholder": "Skriv en note om dette sted...",
    "wishlist.addNote": "Tryk for at tilføje note...",
    "wishlist.saveNote": "Gem",
    "wishlist.nearby": "I nærheden",
    "wishlist.nearbyAttractions": "Attraktioner",
    "wishlist.nearbyAccommodation": "Overnatning",
    "wishlist.nearbyRestaurants": "Restauranter",
    "wishlist.noNearby": "Ingen nærliggende steder fundet i vores database endnu.",
    "wishlist.planTrip": "Planlæg tur hertil",
    "wishlist.planMultiple": "Planlæg tur",
    "wishlist.deleteTitle": "Fjern fra ønskeliste?",
    "wishlist.deleteDesc": "Stedet fjernes fra din ønskeliste.",
    "wishlist.selectedCount": "valgt",
    "wishlist.pickFromWishlist": "Vælg fra ønskeliste",
    "wishlist.loading": "Indlæser...",
    "wishlist.emptyPicker": "Ønskelisten er tom. Gem steder først!",
    "wishlist.addSelected": "Tilføj valgte",
    "wishlist.fromWishlist": "Fra ønskeliste",
    "wishlist.planWithPlaces": "Planlæg rejse med valgte steder",
    "wishlist.planWithPlacesDesc": "Vælg rækkefølge og tildel hver plads en rolle.",
    "wishlist.orderMode": "Ruterækkefølge",
    "wishlist.myOrder": "Behold min rækkefølge",
    "wishlist.myOrderDesc": "Brug præcis den rækkefølge du valgte",
    "wishlist.smartOrder": "Optimer ruten",
    "wishlist.smartOrderDesc": "Find den smarteste rækkefølge med mindre omvej",
    "wishlist.assignRoles": "Hvilken rolle har hvert sted?",
    "wishlist.role_destination": "Destination",
    "wishlist.role_stop": "Stop",
    "wishlist.role_attraction": "Attraktion",
    "wishlist.role_overnight": "Overnatning",
    "wishlist.startPlanning": "Start planlægning",
    "wishlist.places": "steder",
    "notifications.emptyTitle": "Ingen notifikationer endnu",
    "notifications.emptyDesc": "Når noget sker, ser du det her",
    "notifications.title": "Notifikationer",
    "notifications.unreadCount": "ulæst",
    "notifications.noUnread": "Ingen ulæste notifikationer",
    "notifications.markAll": "Marker alle",
    "notifications.markRead": "Marker som læst",
    "notifications.show": "Vis",
    "notifications.delete": "Slet",
    "notifications.goToInbox": "Gå til indbakke & beskeder",
    "notifications.allRead": "Alt markeret som læst",
    "trips.title": "Mine ture",
    "trips.plan": "Planlæg dine ture",
    "trips.loginPrompt": "Log ind for at oprette og administrere dine campingture.",
    "trips.newTrip": "Ny tur",
    "trips.createTrip": "Opret en ny tur",
    "trips.tripTitle": "Tur titel",
    "trips.startDate": "Startdato",
    "trips.endDate": "Slutdato",
    "trips.notes": "Noter til turen...",
    "trips.create": "Opret tur",
    "trips.empty": "Ingen ture endnu",
    "trips.emptyDesc": "Opret din første tur og begynd at tilføje campingstop",
    "trips.stops": "Stop",
    "trips.noStops": "Ingen stop tilføjet endnu. Gem campingpladser i dine favoritter, og tilføj dem derefter her.",
    "trips.created": "Tur oprettet!",
    "trips.failed": "Kunne ikke oprette tur",
    "trips.partialSave": "Turen blev delvist gemt",
    "trips.partialDesc": "Nogle dele kunne ikke gemmes. Gå til Mine ture for at redigere.",
    "trips.unknownPlace": "Ukendt sted",
    "trips.ideaLoaded": "Din rejseidé er indlæst i planlæggeren nedenfor.",
    "trips.newInspiration": "Ny inspiration",
    "trips.requireAuthSave": "Du har brugt tid på at planlægge denne tur. Opret en konto, så gemmes alt automatisk.",
    "trips.daysAbbrev": "dage",
    "trips.perDay": "/dag",
    "trips.socialProof": "rejsende planlægger her",
    "trips.freeLabel": "100% gratis",
    "trips.ideaCreating": "Opretter din rejseidé...",
    "trips.ideaBuilding": "Vi bygger en komplet rejseplan for {destination} med stop, attraktioner og campingpladser.",
    "trips.ideaTitle": "Rejseidé: {title}",
    "trips.campingsNearby": "Campingpladser og pladser i nærheden",
    "trips.restoreWelcome": "Velkommen tilbage!",
    "trips.restoreSaving": "Vi gemmer din tur \"{title}\" automatisk...",
    "trips.restoreFailed": "Kunne ikke gendanne turen",
    "trips.restoreFailedDesc": "De gemte turdata var ufuldstændige. Planlæg en ny tur.",
    "trips.restoreErrorDesc": "Noget gik galt. Planlæg en ny tur.",
    "trips.partialStopsFailed": "Turen blev gemt, men nogle stop mislykkedes",
    "trips.partialStopsDesc": "Gå til Mine ture og rediger turen for at tilføje manglende stop.",
    "contact.sendFailed": "Kunne ikke sende",
    "contact.tryAgain": "Prøv igen senere.",
    "contact.sent": "Besked sendt!",
    "contact.writeMessage": "Skriv en besked",
    "contact.rateLimited": "Vent et øjeblik, før du sender igen",
    "weather.fetchError": "Kunne ikke hente vejrdata. Prøv igen senere.",
    "cost.fetchFailed": "Kunne ikke hente omkostningsestimat",
    "cost.adults": "voksne",
    "cost.children": "børn",
    "cost.person": "person",
    "cost.persons": "personer",
    "wizard.nextStepDesc": "Hvad vil du gerne gøre nu?",
    "wizard.addBudgetCta": "Sæt et budget for turen",
    "wizard.addMoreStopsCta": "Tilføj flere stop & campingpladser",
    "wizard.viewMyTrips": "Se mine ture",
    "myTrips.chatUnavailable": "Rejsechatten er ikke tilgængelig",
    "myTrips.chatClosed": "Chatten lukkes, når rejsen er afsluttet.",
    "myTrips.chatOnlyShared": "Chat er kun tilgængelig i delte rejser med rejsefæller.",
    "contact.subject": "Emne",
    "contact.subjectPlaceholder": "Beskriv kort, hvad sagen handler om",
    "contact.message": "Besked",
    "contact.messagePlaceholder": "Fortæl os mere i detaljer...",
    "contact.creating": "Opretter...",
    "contact.sendTicket": "Send henvendelse",
    "contact.allTickets": "Alle henvendelser",
    "contact.waitingReply": "Vantastika venter på dit svar",
    "contact.resolvedLabel": "Løst",
    "contact.closedNotice": "Denne sag er lukket. Opret en ny henvendelse, hvis du har brug for mere hjælp.",
    "contact.you": "Du",
    "contact.replyPlaceholder": "Skriv dit svar...",
    "contact.sending": "Sender...",
    "contact.reply": "Svar",
    "places.yourPosition": "Din position",
    "places.unnamedPlace": "Unavngivet sted",
    "places.myPosition": "Min position",
    "map.listView": "Listevisning",
    "map.mapView": "Kortvisning",
    "shared.joinedTrip": "Du er kommet med på turen!",
    "shared.joinedDesc": "Du kan nu se og redigere turen sammen med rejseselskabet.",
    "shared.couldNotJoin": "Kunne ikke deltage",
    "shared.tryAgainLater": "Prøv igen senere.",
    "shared.searchStartPlace": "Søg efter dit startsted...",
    "shared.savingCopy": "Gemmer...",
    "shared.saveAsCopy": "Gem som egen kopi",
    "camping.blurBenefitDesc": "Se fuld beskrivelse og faciliteter",
    "camping.blurBenefitPrice": "Vis priser og prishistorik",
    "camping.blurBenefitReviews": "Læs anmeldelser fra andre rejsende",
    "camping.blurBenefitMap": "Åbn kort og navigér direkte",
    "login.title": "Velkommen tilbage",
    "login.subtitle": "Log ind på din Vantastika-konto",
    "login.email": "E-mail",
    "login.password": "Adgangskode",
    "login.submit": "Log ind",
    "login.loading": "Logger ind...",
    "login.failed": "Login mislykkedes",
    "login.noAccount": "Har du ingen konto?",
    "login.signUp": "Tilmeld dig",
    "login.forgotPassword": "Glemt adgangskode?",
    "login.or": "ELLER",
    "login.verificationFailed": "Verificeringen mislykkedes. Prøv igen.",
    "login.captchaFailed": "CAPTCHA-verificering mislykkedes. Prøv igen.",
    "login.tooManyAttempts": "For mange loginforsøg. Vent et øjeblik.",
    "login.wrongCredentials": "Forkert e-mail eller adgangskode.",
    "social.continueGoogle": "Fortsæt med Google",
    "social.continueApple": "Fortsæt med Apple",
    "social.loginFailed": "Login mislykkedes",
    "social.unknownError": "Ukendt fejl",
    "forgot.title": "Glemt adgangskode",
    "forgot.checkInbox": "Tjek din indbakke!",
    "forgot.enterEmail": "Indtast din e-mailadresse, så sender vi et link til nulstilling.",
    "forgot.sentTo": "Vi har sendt en e-mail til",
    "forgot.checkSpam": "med et link til at nulstille din adgangskode. Tjek spam, hvis du ikke finder det.",
    "forgot.backToLogin": "Tilbage til login",
    "forgot.emailLabel": "E-mailadresse",
    "forgot.emailPlaceholder": "din@email.dk",
    "forgot.sending": "Sender...",
    "forgot.sendLink": "Send nulstillingslink",
    "forgot.couldNotSend": "Kunne ikke sende nulstillings-e-mail",
    "forgot.enterEmailFirst": "Indtast din e-mailadresse",
    "reset.title": "Ny adgangskode",
    "reset.subtitle": "Vælg en ny adgangskode til din konto.",
    "reset.newPassword": "Ny adgangskode",
    "reset.confirmPassword": "Bekræft adgangskode",
    "reset.placeholder": "Mindst 8 tegn, bogstav + tal",
    "reset.tooShort": "Adgangskoden skal være mindst 8 tegn",
    "reset.noMatch": "Adgangskoderne matcher ikke",
    "reset.updating": "Opdaterer...",
    "reset.update": "Opdater adgangskode",
    "reset.couldNotUpdate": "Kunne ikke opdatere adgangskode",
    "reset.success": "Adgangskode opdateret!",
    "reset.successDesc": "Din adgangskode er ændret.",
    "reset.goToProfile": "Gå til min profil",
    "reset.invalidLink": "Ugyldigt link",
    "reset.invalidLinkDesc": "Nulstillingslinket er ugyldigt eller udløbet. Anmod om et nyt fra loginsiden.",
    "signup.title": "Opret en konto",
    "signup.subtitle": "Gem campingpladser og begynd at planlægge ture gratis",
    "signup.name": "Visningsnavn",
    "signup.email": "E-mail",
    "signup.password": "Adgangskode",
    "signup.gdpr": "Jeg accepterer privatlivspolitikken og samtykker til behandlingen af mine personlige data i overensstemmelse med GDPR.",
    "signup.submit": "Tilmeld dig",
    "signup.loading": "Opretter konto...",
    "signup.failed": "Oprettelse mislykkedes",
    "signup.success": "Konto oprettet!",
    "signup.checkEmail": "Tjek din e-mail for at bekræfte din konto.",
    "signup.acceptPrivacy": "Accepter venligst privatlivspolitikken",
    "signup.hasAccount": "Har du allerede en konto?",
    "signup.logIn": "Log ind",
    "signup.heroTitle": "Planlæg din næste tur på 60 sekunder",
    "signup.heroSubtitle": "Opret en gratis konto og få adgang til hele Vantastika – ingen reklamer, intet kort.",
    "signup.benefit1": "Gem dine ruter for altid",
    "signup.benefit2": "Campingforslag undervejs",
    "signup.benefit3": "Bliv en del af fællesskabet",
    "signup.benefit4": "Helt gratis – kort er ikke nødvendigt",
    "signup.titleShort": "Opret din konto",
    "signup.subtitleShort": "Tager 10 sekunder. Intet kort nødvendigt.",
    "signup.submitShort": "Opret gratis konto",
    "signup.miniConsent": "Jeg accepterer",
    "signup.ageMini": "Jeg er mindst 16 år.",
    "signup.trustLine": "🔒 Vi deler aldrig din e-mail. Afmeld når som helst.",
    "signup.freeForever": "Gratis for altid",
    "signup.confirmPasswordPlaceholder": "Gentag din adgangskode",
    "footer.desc": "Planlæg din tur, find de rigtige steder og rejs med fuld kontrol. Autocamper, varevogn eller telt.",
    "footer.explore": "Udforsk",
    "footer.searchCampings": "Søg campingpladser",
    "footer.account": "Konto",
    "footer.legal": "Juridisk",
    "footer.privacy": "Privatlivspolitik",
    "footer.terms": "Brugsbetingelser",
    "footer.rights": "Alle rettigheder forbeholdes.",
    "footer.followInstagram": "Følg Vantastika på Instagram",
    "footer.followFacebook": "Følg Vantastika på Facebook",
    "footer.followUs": "Følg os",
    "geo.myLocation": "Min lokation",
    "geo.nearMe": "Nær mig",
    "geo.permissionDenied": "Lokationstilladelse nægtet",
    "geo.detecting": "Detekterer lokation...",
    "trip.editTrip": "Rediger tur",
    "trip.tripTitle": "Tur titel",
    "trip.destination": "Destination (søg efter adresse...)",
    "trip.startDate": "Startdato",
    "trip.consumption": "Forbrug (l/100km)",
    "trip.consumptionEv": "Forbrug (kWh/100km)",
    "trip.fuelPrice": "Dieselpris",
    "trip.fuelPriceBensin": "Benzinpris",
    "trip.fuelPriceEv": "Elpris",
    "trip.fuelPriceUnitEv": "/kWh",
    "trip.vehicleType": "Køretøjstype",
    "trip.vehicleDiesel": "Diesel",
    "trip.vehicleBensin": "Benzin",
    "trip.vehicleEv": "Elektrisk",
    "trip.travelMode": "Rejsetilstand",
    "trip.travelModeCar": "Bil",
    "trip.travelModeMotorhome": "Autocamper",
    "trip.travelModeMotorcycle": "Motorcykel",
    "trip.travelModeDesc": "AI tilpasser ruter og tips baseret på dit køretøj",
    "trip.priceCurrency": "Prisvaluta (den valuta du angiver prisen i)",
    "trip.saveChanges": "Gem ændringer",
    "trip.calculatingRoute": "Beregner rute...",
    "trip.driveTime": "køretid",
    "trip.estimatedFuelCost": "Anslået brændstofomkostning:",
    "trip.estimatedDiesel": "Anslået dieselomkostning:",
    "trip.estimatedChargeCost": "Anslået opladningsomkostning:",
    "trip.openGoogleMaps": "Åbn i Google Maps",
    "trip.tankSize": "Tankstørrelse (l)",
    "trip.batterySize": "Batteri (kWh)",
    "trip.rangeAnalysis": "Rækkeviddeanalyse",
    "trip.rangePerFill": "Rækkevidde per tankning",
    "trip.chargingStops": "Opladningsstop",
    "trip.fuelStops": "Tankningsstop",
    "trip.avgLeg": "Gns. per strækning",
    "trip.costPerCharge": "Omkostning per opladning",
    "trip.costPerFill": "Omkostning per tankning",
    "trip.evStopNote": "Du skal oplade ca. {stops} gange, cirka hver {km} km.",
    "trip.fuelStopNote": "Du skal tanke ca. {stops} gange, cirka hver {km} km.",
    "trip.noStopsNeeded": "Din rækkevidde dækker hele turen uden stop!",
    "trip.routeAlternatives": "Rutealternativer",
    "trip.route": "Rute",
    "trip.addMin2Stops": "Tilføj mindst 2 stop for brændstofberegning",
    "trip.addStop": "Tilføj stop",
    "trip.newStop": "Nyt stop",
    "trip.searchPlace": "Søg sted eller adresse...",
    "trip.notesOptional": "Noter (valgfrit)",
    "trip.saving": "Gemmer...",
    "trip.add": "Tilføj",
    "trip.cancel": "Annuller",
    "trip.dragToReorder": "Træk for at omarrangere",
    "trip.navigate": "Naviger hertil",
    "trip.startNavigation": "Start navigation",
    "trip.uploading": "Overfører...",
    "trip.writeExperience": "Skriv om din oplevelse her...",
    "trip.save": "Gem",
    "trip.tripExperience": "Tur oplevelse",
    "trip.edit": "Rediger",
    "trip.write": "Skriv",
    "trip.describeTripExperience": "Beskriv din tur, hvad du oplevede, tips til andre...",
    "trip.tripUpdated": "Tur opdateret!",
    "trip.linkCopied": "Link kopieret!",
    "trip.shareLinkDesc": "Del linket med dine venner.",
    "trip.couldNotShare": "Kunne ikke dele",
    "trip.imageUploaded": "Billede uploadet!",
    "trip.couldNotUpload": "Kunne ikke uploade billede",
    "trip.experienceSaved": "Oplevelse gemt!",
    "trip.stopAdded": "Stop tilføjet!",
    "trip.couldNotAddStop": "Kunne ikke tilføje stop",
    "trip.orderUpdated": "Bestilling opdateret!",
    "trip.fuelCalc": "Brændstofberegning (diesel)",
    "trip.currency": "Valuta",
    "trip.stopType": "Stoptype",
    "trip.stopTypeVisit": "Kort besøg",
    "trip.stopTypeOvernight": "Overnatning",
    "trip.stopTypeDestination": "Endelig destination",
    "trip.nightsCount": "Nætter",
    "trip.editStop": "Rediger stop",
    "trip.optimizeRoute": "Optimer rute",
    "trip.outboundLeg": "Udgående",
    "trip.returnLeg": "Returrejse",
    "trip.returnLegDesc": "Rejsen hjem",
    "trip.addReturnStop": "Tilføj stop på returrejse",
    "trip.optimizeReturn": "Optimer returrejse",
    "trip.returnOptimized": "Returrute optimeret!",
    "trip.noReturnStops": "Ingen returstop endnu. Tilføj stop til din rejse hjem.",
    "trip.markAsReturn": "Marker som returrejse",
    "trip.markAsOutbound": "Marker som udgående",
    "trip.routeOptimized": "Rute optimeret!",
    "optimize.title": "Optimer rækkefølge af stop",
    "optimize.description": "Sammenlign forskellige rutealternativer og vælg den, der passer bedst til din tur.",
    "optimize.analyzing": "Analyserer rutealternativer...",
    "optimize.currentOrder": "Nuværende rækkefølge",
    "optimize.currentDesc": "Din nuværende rækkefølge af stop som planlagt.",
    "optimize.geographic": "Mest logiske rute",
    "optimize.geographicDesc": "Stop sorteret efter naturlig geografisk flow mod destinationen.",
    "optimize.shortest": "Korteste rute",
    "optimize.shortestDesc": "Sortering efter nærmeste nabo for at minimere den samlede kørselsafstand.",
    "optimize.dayOptimized": "Optimeret til dagsture",
    "optimize.dayOptimizedDesc": "Organiseret omkring overnatningsstop for logiske dagsetaper.",
    "optimize.recommended": "Anbefalet",
    "optimize.stopOrder": "Stoprækkefølge",
    "optimize.start": "Start",
    "optimize.end": "Destination",
    "optimize.overnights": "overnatninger",
    "optimize.shorter": "kortere",
    "optimize.longer": "længere",
    "optimize.saves": "besparelser",
    "optimize.useThisRoute": "Brug denne rute",
    "optimize.cancel": "Annuller",
    "tip.notFound": "Tur ikke fundet",
    "tip.notFoundDesc": "Den er muligvis ikke længere tilgængelig.",
    "tip.back": "Tilbage",
    "tip.traveler": "Rejsende",
    "tip.sharedTrip": "Delt tur",
    "tip.planThisTrip": "Planlæg denne tur",
    "tip.aboutTrip": "Om denne tur",
    "tip.aiGenerated": "AI-genereret turpræsentation",
    "tip.budget": "Budget",
    "tip.stops": "Stop",
    "tip.places": "steder",
    "tip.duration": "Varighed",
    "tip.days": "dage",
    "tip.consumption": "Forbrug",
    "tip.goodToKnow": "Godt at vide, inden du tager afsted",
    "tip.bestSeason": "Bedste sæson",
    "tip.currency": "Valuta",
    "tip.speed": "Hastighed",
    "tip.emergency": "Nødopkald",
    "tip.campingRules": "Campingregler",
    "tip.route": "Ruten",
    "tip.endOfTrip": "Slutningen af turen",
    "tip.wantSameRoute": "Vil du tage den samme rute?",
    "tip.cloneDesc": "Vi opretter en helt ny tur til dig med de samme stop. Du kan derefter tilpasse den — skift start, tilføj stop og planlæg dit budget.",
    "tip.needAccount": "Du skal være logget ind for at gemme turen",
    "tip.tripCreated": "Tur oprettet! 🚐 Nu kan du tilpasse den.",
    "tip.couldNotCreate": "Kunne ikke oprette tur",
    "tip.home": "Hjem",
    "tip.travelTips": "Rejsetips",
    "tip.startPoint": "Startpunkt",
    "tip.endPoint": "Slutpunkt",
    "tip.travelGuide": "Rejseguide",
    "tip.hide": "Skjul",
    "tip.sharedThisTrip": "delte denne tur",
    "tip.explore": "Udforsk",
    "tip.inspirationTitle": "Inspiration fra rejsende",
    "tip.inspirationDesc": "Opdag ruter delt af andre rejsende",
    "tip.tipHidden": "Rejsetip skjult fra forsiden",
    "tip.couldNotHide": "Kunne ikke skjule rejsetip",
    "tip.publishAsTip": "Udgiv som rejsetip",
    "tip.publishDesc": "Vis din tur på forsiden som inspiration for andre",
    "tip.creatingAi": "Opretter AI-turpræsentation... ✨",
    "tip.creatingAiDesc": "Dette kan tage et øjeblik.",
    "tip.publishedWithAi": "Tur udgivet med AI-guide! 🌍✨",
    "tip.published": "Tur er nu synlig for alle! 🌍",
    "tip.unpublished": "Tur er nu privat",
    "tip.publishedNoAi": "Tur udgivet! (AI-guide kunne ikke oprettes)",
    "tip.routeHighlights": "Tips langs denne rute",
    "tip.routeHighlightsDesc": "Anbefalede steder og højdepunkter til din tur",
    "tip.openInMaps": "Åbn i kort",
    "tip.saveMoney": "Spar penge",
    "tip.fuelEstimate": "Brændstofomkostninger",
    "tip.campingCost": "Camping",
    "tip.night": "nat",
    "tip.yourDescription": "Din beskrivelse (valgfri)",
    "tip.yourDescriptionPlaceholder": "Del hvad der gør denne rute speciel, tips til andre, eller hvorfor du kunne lide den...",
    "submit.loginRequired": "Login kræves",
    "submit.loginDesc": "Du skal være logget ind for at registrere et sted.",
    "submit.title": "Registrer et sted",
    "submit.subtitle": "Del din yndlings campingplads med fællesskabet",
    "submit.reviewNote": "Registrerede steder vil blive gennemgået, før de udgives.",
    "submit.basicInfo": "Grundlæggende oplysninger",
    "submit.name": "Navn",
    "submit.type": "Type",
    "submit.country": "Land",
    "submit.region": "Region",
    "submit.city": "By",
    "submit.description": "Beskrivelse",
    "submit.shortDescription": "Kort beskrivelse",
    "submit.fullDescription": "Fuld beskrivelse",
    "submit.priceSchedule": "Pris & Tidsplan",
    "submit.freeSpot": "Dette er en gratis plads",
    "submit.pricePerNight": "Pris per nat (€)",
    "submit.imageContact": "Billeder & Kontakt",
    "submit.imageUrl": "Billede URL",
    "submit.phone": "Telefon",
    "submit.email": "E-mail",
    "submit.website": "Website",
    "submit.address": "Adresse",
    "submit.fullAddress": "Fuld adresse",
    "submit.searchAddress": "Søg adresse...",
    "submit.amenities": "Faciliteter",
    "submit.photos": "Billeder",
    "submit.photosDesc": "Upload op til 5 billeder",
    "submit.submit": "Registrer sted",
    "submit.submitting": "Registrerer...",
    "submit.success": "Registreret!",
    "submit.successDesc": "Dit sted vil blive gennemgået af en administrator.",
    "submit.missingFields": "Manglende felter",
    "submit.missingFieldsDesc": "Navn, land og region er påkrævet.",
    "submit.ctaTitle": "Kender du et godt sted?",
    "submit.ctaDesc": "Registrer en campingplads eller stellplatz",
    "home.featuresTrustTitle": "Verificerede steder",
    "home.featuresTrustDesc": "Ægte campingpladser og pladser med priser, anmeldelser og alt hvad du behøver, inden du tager afsted.",
    "home.featuresRoutesTitle": "Smarte ruter",
    "home.featuresRoutesDesc": "Se hele turen i én visning. Stop, køretider, brændstofomkostninger og alternative veje lige på kortet.",
    "home.featuresBudgetTitle": "Rejsebudget",
    "home.featuresBudgetDesc": "Sæt et budget, log dine udgifter og se præcis, hvor pengene går hen. Kategori for kategori.",
    "home.featuresChecklistTitle": "Tjekliste",
    "home.featuresChecklistDesc": "Tjeklister skræddersyet til din rejsestil. Gas, vand, pas, oplader. Alt på ét sted.",
    "home.featuresAITitle": "AI-assistent",
    "home.featuresAIDesc": "Spørg Vannie om alt vedrørende din rejse. Ruter, regler, tips og lokal viden. Altid tilgængelig.",
    "home.sectionTitle": "Se hvordan det virker",
    "home.sectionSubtitle": "Fra den første idé til din rejse på vejen. Alt på ét sted.",
    "home.trustPlaces": "2.400+ steder",
    "home.trustEurope": "Hele Europa",
    "home.videoOverlay1": "Planlæg din rute. Find de bedste steder. Kør afsted.",
    "home.videoOverlay2": "Alt hvad du behøver til din roadtrip.",
    "home.lifestyleTitle": "Din rejse. Din måde.",
    "home.lifestyleSubtitle": "Ruter, stop, budget og pakkeliste. Klar på sekunder.",
    "home.lifestyleCta": "Begynd at planlægge",
    "home.vannieTitle": "Mød Vannie. Din personlige rejseguide.",
    "home.vannieDesc": "Stil et spørgsmål, få et direkte svar. Bedste steder, lokale tips, rutesforslag og regler pr. land. Vannie ved det. Og det koster intet.",
    "home.vannieCta": "Chat med Vannie",
    "home.vannieCtaSignup": "Tilmeld dig og chat med Vannie",
    "home.vannieRoutes": "Ruteplanlægning",
    "home.vannieCamping": "Campingtips",
    "home.vannieRules": "Lokale regler",
    "home.vannieBudget": "Budgethjælp",
    "nav.services": "Service & SOS",
    "nav.games": "Roadtrip-spil",
    "nav.vannie": "Vannie",
    "nav.community": "Fællesskab",
    "nav.profile": "Min profil",
    "card.free": "GRATIS",
    "card.night": "nat",
    "card.loginForPrice": "Log ind for pris",
    "cat.campings": "Campingpladser",
    "cat.stellplatz": "Stellplatz",
    "cat.freeSpots": "Gratis pladser",
    "map.noStopsToShow": "Ingen stop med koordinater at vise",
    "wizard.whereToGo": "Hvor vil du hen?",
    "wizard.searchDestination": "Søg destination (f.eks. Sanremo, Italien)...",
    "wizard.startPoint": "Hvor starter du fra?",
    "wizard.whenTravel": "Hvornår rejser du?",
    "wizard.myLocation": "Min lokation",
    "wizard.chooseManually": "Vælg manuelt",
    "wizard.searchStartPoint": "Søg startsted...",
    "wizard.locationDetected": "Lokation registreret",
    "wizard.start": "Start",
    "wizard.destination": "Destination",
    "wizard.yourStops": "Dine stop",
    "wizard.stop": "Stop",
    "wizard.addedAsStop": "tilføjet som stop!",
    "campingDetector.title": "Det ligner en campingplads!",
    "campingDetector.aiDetected": "AI fandt",
    "campingDetector.description": "Vil du tilføje stedet til Vantastika, så andre rejsende kan finde det?",
    "campingDetector.pendingNote": "Stedet gennemgås af en admin, før det bliver offentligt.",
    "campingDetector.confidence": "match",
    "campingDetector.typeCamping": "Campingplads",
    "campingDetector.typeStellplatz": "Autocamper-plads",
    "campingDetector.typeFreeParking": "Gratis parkering",
    "campingDetector.typeLodging": "Overnatning",
    "campingDetector.notNow": "Ikke nu",
    "campingDetector.registerCta": "Tilføj til Vantastika",
    "campingDetector.registering": "Tilføjer…",
    "campingDetector.registeredTitle": "Tak for bidraget!",
    "campingDetector.registeredDesc": "Stedet er sendt og udgives efter gennemgang.",
    "campingDetector.alreadyExists": "Findes allerede i Vantastika",
    "campingDetector.alreadyExistsDesc": "Dette sted er allerede registreret.",
    "campingDetector.couldNotRegister": "Kunne ikke registrere stedet",
    "campingDetector.loginRequired": "Login påkrævet",
    "campingDetector.loginRequiredDesc": "Log ind for at bidrage med nye steder.",
    "campingDetector.cannotRegister": "Kan ikke registrere",
    "campingDetector.missingPlaceId": "Mangler steds-ID fra Google.",
    "stopType.visit": "Besøg",
    "stopType.overnight": "Overnatning",
    "stopType.destination": "Destination",
    "stopType.nights": "Nætter",
    "stopType.stayHours": "Ophold (t)",
    "wizard.suggestedStops": "Campingpladser langs ruten",
    "wizard.findingCampings": "Søger campingpladser langs ruten...",
    "wizard.noCampingsNearRoute": "Ingen campingpladser fundet nær denne rute",
    "wizard.saveTrip": "Gem min rejse",
    "wizard.stepPlanningMode": "Planlægning",
    "wizard.stepDrivingHours": "Kørsel",
    "wizard.stepTravelStyle": "Rejsestil",
    "wizard.stepCountryInfo": "Lande",
    "wizard.howToPlan": "Hvordan vil du planlægge din rejse?",
    "wizard.howToPlanDesc": "Vælg den mulighed, der passer dig bedst.",
    "wizard.planModeKnow": "Jeg ved, hvor jeg skal hen",
    "wizard.planModeKnowDesc": "Jeg har en destination klar og vil planlægge ruten derhen.",
    "wizard.planModeHelp": "Hjælp mig med at finde en rejse",
    "wizard.planModeHelpDesc": "Jeg vil have inspiration og AI-forslag til rejser.",
    "wizard.planModeOptimize": "Optimer min rute smart",
    "wizard.planModeOptimizeDesc": "Jeg ved nogenlunde, hvor jeg skal, men vil have de bedste etaper og stop.",
    "wizard.stepDetails": "Detaljer",
    "wizard.stepTrip": "Rejse",
    "wizard.stepPreferences": "Præferencer",
    "wizard.dailyDrivingTitle": "Hvor længe vil du køre per dag?",
    "wizard.dailyDrivingDesc": "Vi tilpasser etaperne efter dit valg.",
    "wizard.recommended": "Anbefalet",
    "wizard.travelStyleTitle": "Hvordan vil du rejse?",
    "wizard.travelStyleDesc": "Vi tilpasser ruten og forslagene efter din rejsestil.",
    "wizard.styleFastest": "Hurtigste rute",
    "wizard.styleFastestDesc": "Prioriter kortest køretid via motorvej.",
    "wizard.styleSafe": "Tryg og rolig rejse",
    "wizard.styleSafeDesc": "Roligere veje med rimelig daglig kørsel.",
    "wizard.styleScenic": "Naturskøn rute",
    "wizard.styleScenicDesc": "Prioriter smukke veje og udsigter.",
    "wizard.styleCamping": "Campingvenlig rute",
    "wizard.styleCampingDesc": "Rute med god adgang til campingpladser.",
    "wizard.styleBudget": "Budgetvenlig rejse",
    "wizard.styleBudgetDesc": "Undgå vejafgifter og dyre stop.",
    "wizard.styleFamily": "Børnevenlig rejse",
    "wizard.styleFamilyDesc": "Kortere etapper med børnevenlige stop.",
    "wizard.selectTravelStyle": "Vælg en rejsestil",
    "wizard.longDrivingWarning": "Længere daglig kørsel kan påvirke trafiksikkerheden. Vi anbefaler maks 4–5 timer per dag.",
    "wizard.skipOvernightHere": "Spring overnatning over – fortsæt med at køre",
    "wizard.resetStages": "Nulstil etaper",
    "wizard.passThrough": "Gennemkørsel",
    "wizard.noOvernightShort": "Ingen overnatning",
    "wizard.mergedDayTitle": "Du kører igennem uden overnatning",
    "wizard.mergedDayDesc": "Denne dag er lagt sammen med den næste. Korte stop til pause og tankning anbefales.",
    "wizard.undoSkipOvernight": "Fortryd – overnat her igen",
    "wizard.selectPlanningMode": "Vælg hvordan du vil planlægge",
    "wizard.accommodationTitle": "Overnatningsforslag",
    "wizard.accommodationDescCamping": "Viser autocamper- og campingpladser langs din rute.",
    "wizard.accommodationDescCar": "Viser hoteller og overnatning nær din destination.",
    "wizard.campingRules": "Campingregler",
    "wizard.countriesInRouteOrder": "Lande vises i ruteorden",
    "wizard.countryInfoDesc": "Praktisk information for hvert land du passerer.",
    "wizard.countryInfoTitle": "Lande langs din rute",
    "wizard.startCountry": "Startland",
    "wizard.transitCountry": "Transitland",
    "wizard.destinationCountry": "Destination",
    "wizard.ferryAlternatives": "Færgealternativer",
    "wizard.ferryDuration": "Rejsetid",
    "wizard.ferryRoute": "Strækning",
    "wizard.ferryRelevance": "Hvorfor relevant",
    "wizard.driving": "Kørsel",
    "wizard.drivingRules": "Køreregler",
    "wizard.fromRoute": "Langs ruten",
    "wizard.fuelInfo": "Brændstof",
    "wizard.importantForMotorhome": "vigtigt for autocamper",
    "wizard.infoItems": "infopunkter",
    "wizard.loadingCountryInfo": "Indlæser landeinformation...",
    "wizard.motorhomeMultiCountryTip": "Med autocamper kan regler variere meget mellem lande. Tjek miljøzoner, vignetter og tilladte mål før afrejse.",
    "wizard.noCountryInfo": "Ingen landeinformation tilgængelig. Angiv rute først.",
    "wizard.noResultsForStage": "Ingen resultater for denne etape",
    "wizard.oneWay": "Enkeltrejse",
    "wizard.optimizeRoute": "Optimer stoprækkefølge",
    "wizard.searchingStage": "Søger for etape",
    "wizard.selectDestForAccommodation": "Vælg destination først for overnatningsforslag.",
    "wizard.shortTripNoOvernight": "Kort tur – ingen overnatning nødvendig.",
    "wizard.skipStep": "Spring dette trin over",
    "wizard.from": "Fra",
    "wizard.to": "Til",
    "wizard.selectedAccommodation": "Valgt overnatning",
    "wizard.changeAccommodation": "Skift",
    "wizard.selected": "Valgt",
    "wizard.selectAccommodation": "Vælg",
    "wizard.noAccommodationYet": "Ingen overnatning valgt endnu. Du kan vælge i næste trin.",
    "wizard.viewOnMap": "Vis på kort",
    "common.close": "Luk",
    "wizard.speedLimits": "Hastighedsgrænser",
    "wizard.stages": "etaper",
    "wizard.tolls": "Vejafgifter & vignetter",
    "wizard.wildCamping": "Vildcamping",
    "wizard.stepRoute": "Udrejse",
    "wizard.stepReturn": "Hjemrejse",
    "wizard.stepAccommodation": "Overnatning",
    "wizard.stepVehicle": "Køretøj",
    "wizard.stepOverview": "Oversigt",
    "wizard.next": "Næste",
    "wizard.back": "Tilbage",
    "wizard.tripTypeLabel": "Turtype",
    "wizard.accommodationDesc": "Her er overnatningsforslag baseret på din rute og rejsemåde. Tilføj som stop eller spring over.",
    "wizard.showingCampingsAlongRoute": "Viser campingpladser langs din rute",
    "wizard.showingHotelsNearDest": "Viser hoteller nær din destination",
    "wizard.showingAccommodationNearDest": "Viser overnatning langs din rute",
    "wizard.searchingAlongRoute": "Søger langs din rute...",
    "wizard.expandingSearch": "Udvider søgeområdet...",
    "wizard.tryingAlternatives": "Prøver alternative overnatninger...",
    "wizard.noAccommodationFound": "Ingen overnatning fundet nær din rute. Du kan springe over dette trin og tilføje stop manuelt senere.",
    "wizard.returnTripDesc": "Vælg, hvordan du vil rejse hjem.",
    "wizard.sameRouteDesc": "Kør tilbage samme vej",
    "wizard.scenicDesc": "Alternativ naturskøn rute",
    "wizard.fastestDesc": "Hurtigste vej hjem",
    "wizard.routeSummary": "Rute",
    "wizard.selectDestFirst": "Vælg destination først",
    "wizard.selectStartFirst": "Vælg startpunkt først",
    "wizard.fuelType": "Brændstoftype",
    "wizard.estimatedFuelCostLabel": "Estimeret brændstofomkostning",
    "wizard.showPricesIn": "Vis priser i",
    "wizard.perNight": "nat",
    "wizard.finalDay": "Slutmål",
    "wizard.overnight": "Overnatning",
    "wizard.viewAllAccommodations": "Vis alle",
    "wizard.accommodationNear": "Overnatning nær",
    "wizard.tapToChooseAccommodation": "Tryk for at vælge overnatning.",
    "wizard.accommodationMissingHint": "Du har planlagt en overnatning her, men ingen overnatning er valgt.",
    "wizard.accommodationMissingTitle": "Overnatning mangler",
    "wizard.accommodationMissingSubtitle": "Tryk for at vælge overnatning for hver dag.",
    "wizard.detectedAccommodationTitle": "Ligner overnatning",
    "wizard.detectedAccommodationDesc": "ligner en overnatning. Hvordan vil du markere det?",
    "wizard.overnightHint": "Du sover her",
    "wizard.visitHint": "Bare gennemkørsel eller besøg",
    "wizard.destinationHint": "Rejsens slutmål",
    "wizard.moreDetails": "Flere detaljer",
    "wizard.firstTimeHint": "Start med at søge efter din destination. Vælg derefter køretøj, datoer og stop — ruten beregnes automatisk!",
    "wizard.saving": "Gemmer...",
    "wizard.budgetOptional": "Angiv et budget (valgfrit)",
    "wizard.budgetPlaceholder": "f.eks. 15000",
    "wizard.fuelAutoAdded": "Estimeret brændstofomkostning tilføjet til budgettet",
    "wizard.planNewTrip": "Planlæg ny rejse",
    "wizard.myTrips": "Mine gemte rejser",
    "wizard.planTrip": "Planlæg rejse",
    "wizard.or": "eller",
    "wizard.popularDestinations": "Populært",
    "wizard.describeTrip": "Beskriv din rejse – vi planlægger det hele for dig",
    "wizard.describeSubtext": "Skriv frit hvad du vil lave, og vi bygger forslaget",
    "wizard.backToManual": "Tilbage til manuel søgning",
    "wizard.selectSuggestion": "Vælg et forslag fra listen for at fortsætte.",
    "wizard.analyzingTrip": "Analyserer din rejse...",
    "wizard.createPlan": "Opret rejseplan",
    "wizard.suggestedTrip": "Foreslået rejse",
    "wizard.places": "steder",
    "wizard.startOver": "Start forfra",
    "wizard.noDestFound": "Ingen destinationer fundet",
    "wizard.noDestFoundDesc": "Prøv at beskrive din rejse mere detaljeret.",
    "wizard.couldNotParse": "Kunne ikke fortolke rejsen",
    "wizard.couldNotParseDesc": "Prøv igen med en tydeligere beskrivelse.",
    "wizard.tooManyRequests": "For mange anmodninger",
    "wizard.tooManyRequestsDesc": "Vent et øjeblik og prøv igen.",
    "wizard.aiCreditOut": "AI-kreditter opbrugt",
    "wizard.aiCreditOutDesc": "Kontakt support for mere information.",
    "wizard.tripSaved": "Rejsen gemt!",
    "wizard.tripSavedDesc": "Gå til Mine Rejser for at se den.",
    "wizard.couldNotReachVannie": "Kunne ikke nå Vannie lige nu.",
    "wizard.describePlaceholder": "Beskriv din rejse med dine egne ord — vi laver en komplet rejseplan for dig.",
    "wizard.chars": "tegn",
    "wizard.destinationConfirmed": "Destination valgt",
    "wizard.locating": "Finder koordinater...",
    "wizard.destType": "Destination",
    "wizard.visitType": "Besøg",
    "wizard.aiSuggestTitle": "AI Rejseforslag",
    "wizard.aiSuggestOpen": "Beskriv din rejse – vi planlægger det hele for dig",
    "wizard.aiSuggestPlaceholder": "F.eks. 'Jeg vil til stranden i 5 dage' eller 'Hyggelig bjergferie med vandreture i Alperne'",
    "wizard.vannieThinking": "Vannie tænker...",
    "wizard.suggestTrip": "Foreslå rejse",
    "wizard.suggestedStopsLabel": "Foreslåede stop",
    "wizard.useThisSuggestion": "Brug dette forslag",
    "wizard.couldNotGenerate": "Kunne ikke generere forslag",
    "wizard.days": "dage",
    "wizard.perDay": "/dag",
    "wizard.roundtrip": "Returrejse",
    "wizard.oneway": "Enkeltrejse",
    "wizard.departure": "Afgang",
    "wizard.return": "Retur",
    "wizard.selectDate": "Vælg dato",
    "wizard.departureDateOptional": "Afgangsdato (valgfrit)",
    "wizard.day": "dag",
    "wizard.dayLabel": "Dag {n}",
    "wizard.roundtripLabel": "Returrejse",
    "wizard.estimatedTravelTime": "Anslået rejsetid",
    "wizard.total": "total",
    "wizard.stopsLabel": "stop",
    "wizard.fetchingReturnTips": "Henter tips til returrejse...",
    "wizard.returnTips": "Returrejsetips",
    "wizard.roundtripShort": "returrejse",
    "wizard.driveTime": "Køretid",
    "wizard.chargeCost": "Opladningsomkostning",
    "wizard.fuelCost": "Brændstofomkostning",
    "wizard.distance": "Afstand",
    "wizard.chooseRoute": "Vælg denne rute",
    "wizard.ferry": "Færge",
    "wizard.maxDrivingHours": "Maks. køretimer pr. dag",
    "wizard.longDriveWarning": "Lang køretid",
    "wizard.longDriveDesc": "Vi anbefaler maks. 4 timer for sikker og behagelig kørsel.",
    "wizard.drivingDaysCalc": "{hours}t/dag = ~{days} køredage",
    "wizard.drivingDaysRoundtrip": "dage returrejse",
    "wizard.vehicleSettings": "Køretøj & brændstofindstillinger",
    "wizard.scenic": "Naturskønt",
    "wizard.fastest": "Hurtigst",
    "wizard.balanced": "Balanceret",
    "wizard.sameRoute": "Samme rute",
    "wizard.returnTrip": "Returrejse",
    "wizard.homecoming": "Ankomst hjem",
    "wizard.optimizationSummary": "Rute optimeret",
    "wizard.stageOverview": "Dagsetapper",
    "wizard.overnightIn": "Overnatning nær",
    "wizard.arrivalAt": "Ankomst ved",
    "wizard.returnRouteOption": "Returrejse",
    "wizard.hours": "t",
    "wizard.addedAsOvernight": "tilføjet som overnatningsstop!",
    "wizard.addedAsRestStop": "tilføjet som hvilestop!",
    "wizard.stopAddedFromMap": "Stop tilføjet fra kort!",
    "wizard.updatedStop": "Opdateret stop",
    "wizard.stopPositionUpdated": "Stop position opdateret",
    "wizard.newStop": "Nyt stop",
    "wizard.optimizedDesc": "Stop omorganiseret for kortest mulig samlet distance",
    "wizard.newOrder": "Ny rækkefølge",
    "wizard.orderChanged": "Rækkefølgen er ændret fra originalen",
    "wizard.routeOptimized": "Ruten er optimeret!",
    "wizard.nameError": "Kunne ikke generere navneforslag",
    "wizard.mapInstructions": "Klik på kortet for at tilføje stop, træk for at flytte dem",
    "wizard.nameTrip": "Navngiv din rejse",
    "wizard.optional": "valgfrit",
    "wizard.namePlaceholder": "f.eks. Sommer i Alperne",
    "wizard.aiSuggest": "AI foreslå",
    "wizard.signupAndSave": "Tilmeld & gem rejse",
    "wizard.useTrip": "Brug denne rejse",
    "wizard.dragToReorder": "Træk for at omarrangere",
    "wizard.prefillTip": "Forudfyldt fra et Vantastika-tip",
    "wizard.prefillGuide": "Forudfyldt fra en rejseguide",
    "budget.title": "Rejsebudget",
    "budget.tab": "Budget",
    "budget.setBudget": "Angiv budget",
    "budget.totalBudget": "Samlet budget (kr)",
    "budget.spent": "Brugt",
    "budget.remaining": "Resterende",
    "budget.used": "brugt",
    "budget.topCategory": "Største kategori",
    "budget.addExpense": "Tilføj udgift",
    "budget.amount": "Beløb",
    "budget.description": "Beskrivelse (valgfri)",
    "budget.save": "Gem",
    "budget.cancel": "Annuller",
    "budget.expenses": "Udgifter",
    "budget.breakdown": "Kategoriopdeling",
    "budget.expenseAdded": "Udgift tilføjet!",
    "budget.expenseUpdated": "Udgift opdateret!",
    "budget.expenseFailed": "Kunne ikke gemme udgift",
    "budget.autoLabel": "Auto-genereret",
    "budget.editFuelCalc": "Rediger",
    "budget.fuelCalcTitle": "Brændstofomkostningsberegning",
    "budget.fuelPriceLabel": "Pris/liter",
    "budget.fuelConsumptionLabel": "l/100km",
    "budget.fuelDistanceLabel": "Afstand (km)",
    "budget.fuelCalcResult": "Anslået brændstofomkostning:",
    "budget.fuelSave": "Opdater brændstofomkostning",
    "budget.fuelUpdated": "Brændstofomkostning opdateret!",
    "budget.budgetSaved": "Budget gemt!",
    "budget.getAiTips": "Få budgettips fra Vannie",
    "budget.aiTipsTitle": "Vannies budgettips",
    "budget.noTips": "Ingen tips tilgængelige lige nu.",
    "budget.tipsFailed": "Kunne ikke indlæse tips",
    "budget.close": "Luk",
    "budget.cat.fuel": "Brændstof",
    "budget.cat.camping": "Camping/Plads",
    "budget.cat.food": "Mad & Drikke",
    "budget.cat.tolls": "Vejafgifter/Færger",
    "budget.cat.activities": "Aktiviteter",
    "budget.cat.shopping": "Shopping",
    "budget.cat.other": "Andet",
    "budget.budgetLabel": "Budget",
    "budget.edit": "Rediger",
    "budget.backToTrip": "Tilbage til tur",
    "budget.estimatedFuel": "Anslået brændstof",
    "budget.estimateOnly": "Kun estimat",
    "budget.autoCosts": "Automatiske omkostninger",
    "budget.manualExpenses": "Dine udgifter",
    "budget.pageDesc": "Administrer dine budgetter og udgifter",
    "budget.loginRequired": "Log ind for at bruge budgetfunktionen",
    "budget.subtitle": "Dine budgetter og udgifter",
    "budget.pageSubtitle": "Planlæg dit rejsebudget, tilføj udgifter og følg med i, hvor meget du har tilbage.",
    "budget.newBudget": "Nyt budget",
    "budget.newExpense": "Ny udgift",
    "budget.tabActive": "Aktiv",
    "budget.tabAll": "Alle",
    "budget.tabExpenses": "Uforbundne udgifter",
    "budget.tabUnlinked": "Uforbundne",
    "budget.emptyTitle": "Ingen budgetter endnu",
    "budget.emptyDesc": "Opret dit første budget for at få et bedre overblik over dine turudgifter.",
    "budget.emptyStateTitle": "Intet budget oprettet endnu",
    "budget.emptyStateDesc": "Opret dit første rejsebudget for at få bedre overblik over udgifter til overnatning, mad, brændstof og aktiviteter.",
    "budget.createBudget": "Opret budget",
    "budget.createFirstBudget": "Opret første budget",
    "budget.chipAccommodation": "Overnatning",
    "budget.chipFood": "Mad",
    "budget.chipFuel": "Brændstof",
    "budget.chipActivities": "Aktiviteter",
    "budget.chipShopping": "Shopping",
    "budget.unlinkedLabel": "Uforbundne udgifter",
    "budget.unlinkedHint": "Disse udgifter er ikke knyttet til noget budget.",
    "budget.more": "mere",
    "budget.noActiveTitle": "Ingen aktive budgetter",
    "budget.noActiveDesc": "Opret et budget eller aktiver et eksisterende.",
    "budget.overviewTitle": "Budgetoversigt",
    "budget.statusGood": "På sporet",
    "budget.statusWarning": "Nærmer sig grænsen",
    "budget.statusOver": "Over budget",
    "budget.pctUsed": "brugt",
    "budget.totalBudgetCard": "Samlet budget",
    "budget.spentCard": "Brugt",
    "budget.remainingCard": "Tilbage",
    "budget.paused": "Pauset",
    "budget.completed": "afsluttet",
    "budget.noExpenses": "Ingen uforbundne udgifter",
    "budget.noExpensesDesc": "Udgifter uden et tilknyttet budget vil blive vist her",
    "budget.setBudgetTitle": "Månedligt budget",
    "budget.setBudgetBtn": "Indstil budget",
    "budget.setBudgetSaved": "Budgetgrænse gemt ✓",
    "budget.month": "Måned",
    "budget.budgetAmount": "Budget",
    "budget.amountPlaceholder": "f.eks. 15000",
    "budget.saving": "Gemmer...",
    "budget.saveBudgetLimit": "Gem budgetgrænse",
    "budget.createBudgetTitle": "Opret budget",
    "budget.budgetName": "Budgetnavn",
    "budget.budgetNamePlaceholder": "f.eks. Sommerferie 2026",
    "budget.budgetTypeLabel": "Budgettype",
    "budget.totalBudgetLabel": "Samlet budget",
    "budget.currencyLabel": "Valuta",
    "budget.startDateLabel": "Startdato",
    "budget.endDateLabel": "Slutdato",
    "budget.colorLabel": "Farve",
    "budget.selectColor": "Vælg farve",
    "budget.descriptionLabel": "Beskrivelse",
    "budget.descriptionPlaceholder": "Valgfri note...",
    "budget.linkTrip": "Tilknyt tur (valgfri)",
    "budget.noTripSelected": "Ingen tur valgt",
    "budget.noTrip": "Ingen tur",
    "budget.fillNameAndAmount": "Indtast et navn og et gyldigt beløb",
    "budget.budgetCreated": "Budget oprettet ✓",
    "budget.couldNotSave": "Kunne ikke gemme",
    "budget.creating": "Opretter...",
    "budget.addExpenseTitle": "Tilføj udgift",
    "budget.scanWithAi": "Scan kvittering med AI",
    "budget.amountLabel": "Beløb",
    "budget.dateLabel": "Dato",
    "budget.titleLabel": "Titel",
    "budget.titlePlaceholder": "Hvad købte du?",
    "budget.categoryLabel": "Kategori",
    "budget.paymentMethod": "Betalingsmetode",
    "budget.selectPayment": "Vælg...",
    "budget.linkBudget": "Tilknyt budget (valgfri)",
    "budget.linkBudgetDirect": "Tilknyt budget",
    "budget.noBudgetSelected": "Intet budget valgt",
    "budget.noBudget": "Intet budget",
    "budget.noteLabel": "Note",
    "budget.notePlaceholder": "Valgfri note...",
    "budget.receiptLabel": "Kvittering",
    "budget.receiptRemove": "Fjern",
    "budget.receiptUpload": "Tag billede eller upload kvittering",
    "budget.fileTooLarge": "Filen er for stor",
    "budget.fileTooLargeDesc": "Max 15 MB",
    "budget.fillAmountAndTitle": "Indtast et gyldigt beløb og titel",
    "budget.expenseSaved": "Udgift gemt ✓",
    "budget.converted": "Konverteret:",
    "budget.editBudgetTitle": "Rediger budget",
    "budget.budgetUpdated": "Budget opdateret ✓",
    "budget.saveChanges": "Gem ændringer",
    "budget.editExpenseTitle": "Rediger udgift",
    "budget.scanTitle": "Smart kvitteringsscan",
    "budget.scanCapture": "Tag billede eller upload kvittering",
    "budget.scanCaptureDesc": "AI analyserer din kvittering linje for linje",
    "budget.scanTakePhoto": "Tag billede",
    "budget.scanUpload": "Upload",
    "budget.scanAnalyzing": "Analyserer kvittering...",
    "budget.scanAnalyzingDesc": "AI læser varer, beløb og kategorier",
    "budget.scanFailed": "Kunne ikke analysere kvittering",
    "budget.scanConfidenceHigh": "Høj sikkerhed",
    "budget.scanConfidenceMedium": "Medium sikkerhed — gennemgå felter",
    "budget.scanConfidenceLow": "Lav sikkerhed — tjek alle felter",
    "budget.scanCandidates": "Mulige beløb:",
    "budget.scanItemsPerCategory": "Varer pr. kategori",
    "budget.scanMixedCategories": "Blandede kategorier",
    "budget.scanSaveHow": "Hvordan vil du gemme?",
    "budget.scanSingleExpense": "Én udgift",
    "budget.scanSplitExpenses": "Opdel",
    "budget.scanSplitDesc": "Opretter {n} separate udgifter — én pr. kategori.",
    "budget.scanMainCategory": "Hovedkategori",
    "budget.scanShowRaw": "Vis rå kvitteringstekst",
    "budget.scanHideRaw": "Skjul rå kvitteringstekst",
    "budget.scanCancel": "Annuller",
    "budget.scanSaveExpense": "Gem udgift",
    "budget.scanSaveExpenses": "Gem {n} udgifter",
    "budget.scanSavedMultiple": "{n} udgifter gemt fra kvittering ✓",
    "budget.scanSavedSingle": "Udgift gemt fra kvittering ✓",
    "budget.confidenceMedium": "Medium sikkerhed",
    "budget.confidenceLow": "Usikker — tjek venligst",
    "budget.statusPaused": "Pauset",
    "budget.statusCompleted": "Afsluttet",
    "budget.budgetLabel2": "Budget",
    "budget.spentLabel": "Brugt",
    "budget.remainingLabel": "Resterende",
    "budget.overBy": "Over med",
    "budget.usedPercent": "brugt",
    "budget.expenseCount": "udgifter",
    "budget.mixedCurrenciesNote": "Udgifter i {currencies} — konverteret til {target}",
    "budget.deleteConfirm": "Er du sikker på, at du vil slette dette budget?",
    "budget.statusActivated": "Budget aktiveret",
    "budget.statusPausedToast": "Budget sat på pause",
    "budget.statusCompletedToast": "Budget afsluttet",
    "budget.perCategory": "Pr. kategori",
    "budget.biggestExpense": "Største udgift",
    "budget.expenseListTitle": "Udgifter",
    "budget.noExpensesYet": "Ingen udgifter endnu",
    "budget.noExpensesYetDesc": "Tryk på \"Tilføj udgift\" for at komme i gang",
    "budget.totalSpent": "I alt brugt",
    "budget.thisMonth": "Denne måned",
    "budget.budgeted": "Budgetteret",
    "budget.warnings": "Advarsler",
    "summary.title": "Opsummering af rejseudgifter",
    "summary.titleOngoing": "Udgiftsoversigt",
    "summary.over": "Over budget",
    "summary.saved": "Sikret",
    "summary.dailyAvg": "Pr. dag",
    "summary.overBudget": "Du overskred budgettet på denne rejse",
    "summary.underBudget": "Du holdt dig inden for budgettet — godt gået!",
    "summary.breakdown": "Fordeling af udgifter",
    "summary.categoryDetails": "Kategoridetaljer",
    "fuelLog.title": "Brændstoflog",
    "fuelLog.addFuel": "Tilføj optankning",
    "fuelLog.liters": "Liter",
    "fuelLog.cost": "Pris",
    "fuelLog.date": "Dato",
    "fuelLog.odometer": "Kilometertæller (km)",
    "fuelLog.note": "Bemærkning",
    "fuelLog.optional": "Valgfrit",
    "fuelLog.save": "Gem",
    "fuelLog.added": "Optankning logget!",
    "fuelLog.addFailed": "Kunne ikke gemme optankning",
    "fuelLog.invalidInput": "Indtast både liter og pris",
    "fuelSummary.title": "Brændstofopsummering",
    "fuelSummary.totalLiters": "I alt liter",
    "fuelSummary.totalCost": "Brændstofpris",
    "fuelSummary.avgPrice": "Gennemsnitspris/liter",
    "fuelSummary.consumption": "Forbrug",
    "fuelSummary.costPerKm": "Pris/km",
    "fuelSummary.costPerMil": "Pris/mil",
    "fuelSummary.noDistance": "Tilføj ruteafstand for at se forbrug og pris pr. km.",
    "fuelSummary.basedOn": "Baseret på {n} optankning(er)",
    "checklist.tab": "Tjekliste",
    "journal.tab": "Dagbog",
    "authGate.title": "Gem din rejse og fortsæt, når du vil",
    "authGate.description": "Opret en gratis konto for at gemme din rejse, dine stop og dit budget — så du kan fortsætte, hvor du slap.",
    "authGate.signup": "Opret konto og gem",
    "authGate.login": "Log ind",
    "authGate.ctaSection": "Alt hvad du behøver til din rejse",
    "authGate.ctaPlaces": "Dine stop og rute — gemt og synkroniseret",
    "authGate.ctaRoutes": "Fortsæt planlægningen på enhver enhed",
    "authGate.ctaBudget": "Budget og kvitteringer — altid tilgængelige",
    "authGate.ctaChecklist": "Tjeklister der følger din rejse",
    "authGate.ctaAI": "AI-assistent der kender enhver vej",
    "authGate.ctaButton": "Opret konto og gem",
    "authGate.saveTripMsg": "Gem din rejse og fortsæt senere",
    "authGate.saveBudgetMsg": "Gem dit budget og spor udgifter",
    "authGate.saveReceiptMsg": "Gem dine kvitteringer på tværs af enheder",
    "authGate.saveFavoriteMsg": "Gem dine yndlingssteder i én liste",
    "authGate.planRouteMsg": "Fortsæt hvor du slap",
    "authGate.savePlaceMsg": "Gem dette sted i din samling",
    "authGate.useBudgetMsg": "Spor dit budget — opret en gratis konto",
    "authGate.useChecklistMsg": "Brug tjeklister — opret en gratis konto",
    "authGate.or": "eller",
    "authGate.hasAccount": "Har du allerede en konto?",
    "gate.whyJoin": "HVORFOR OPRETTE EN KONTO?",
    "gate.benefit1": "Gem din rejse, stop og budget sikkert",
    "gate.benefit2": "Fortsæt planlægningen på enhver enhed",
    "gate.benefit3": "Fuld budgetsporing med kvitteringsscanning",
    "gate.benefit4": "AI-rejseassistent inkluderet gratis",
    "gate.benefit5": "Få adgang til community-tips og anmeldelser",
    "gate.benefit6": "Tjeklister skræddersyet til dit køretøj",
    "gate.freeForever": "Gratis at komme i gang",
    "welcome.title": "Velkommen til Vantastika!",
    "welcome.titleName": "Velkommen, {name}!",
    "welcome.desc": "Alt hvad du behøver for at planlægge, rejse og huske dine rejser er lige her.",
    "welcome.feature1": "Planlæg ruter, tilføj stop og spor budget og brændstof",
    "welcome.feature2": "Vannie, din AI-rejseassistent, hjælper dig med at finde de bedste ruter",
    "welcome.feature3": "Gem yndlingssteder, del rejser med venner",
    "welcome.cta": "Planlæg min første rejse",
    "welcome.dismiss": "Udforsk på egen hånd",
    "welcome.close": "Luk",
    "gate.noCard": "Gratis at komme i gang. Intet kreditkort kræves.",
    "gate.createAccount": "Opret gratis konto",
    "gate.alreadyMember": "Har du allerede en konto? Log ind",
    "gate.seeMore": "Vil du se mere?",
    "gate.registerToSee": "Opret en gratis konto for at få adgang til alle funktioner og steder.",
    "gate.searchTitle": "Udforsk alle campingpladser",
    "gate.searchDesc": "Tilmeld dig gratis for at gennemse alle verificerede steder, gem dine favoritter og læs anmeldelser fra fællesskabet.",
    "gate.servicesTitle": "Service & Nød",
    "gate.servicesDesc": "Opret en konto for at finde værksteder, ladestationer, vandpunkter og nødtjenester i nærheden af dig.",
    "gate.tripsTitle": "Rejseplanlægger",
    "gate.tripsDesc": "Tilmeld dig gratis for at planlægge ubegrænsede rejser med smarte ruter, brændstofomkostninger og budgetsporing.",
    "gate.submitTitle": "Tilføj et sted",
    "gate.submitDesc": "Tilmeld dig gratis for at dele dine yndlingssteder med fællesskabet.",
    "gate.tipsTitle": "Vejtips",
    "gate.tipsDesc": "Opret en konto for at læse og dele rejsetips fra rigtige rejsende.",
    "roadTips.title": "Vejtips",
    "roadTips.share": "Del et tip",
    "roadTips.allCountries": "Alle lande",
    "roadTips.formTitle": "Del dit vejtip",
    "roadTips.labelTitle": "Titel *",
    "roadTips.placeholderTitle": "f.eks. Bedste rute gennem Alperne",
    "roadTips.labelType": "Type",
    "roadTips.labelCountry": "Land",
    "roadTips.select": "Vælg",
    "roadTips.labelTip": "Dit tip *",
    "roadTips.placeholderTip": "Del din oplevelse...",
    "roadTips.labelImage": "Billed-URL (valgfrit)",
    "roadTips.posting": "Udgiver...",
    "roadTips.post": "Del tip",
    "roadTips.shared": "Tip delt!",
    "roadTips.loginToShare": "Log ind for at dele dine egne vejtips.",
    "roadTips.noTips": "Ingen vejtips endnu. Vær den første til at dele!",
    "roadTips.type.general": "Generelt tip",
    "roadTips.type.route": "Ruteanbefaling",
    "roadTips.type.parking": "Parkeringstip",
    "roadTips.type.food": "Mad & Shopping",
    "roadTips.type.warning": "Advarsel",
    "roadTips.type.service": "Servicepunkt",
    "community.title": "Community",
    "community.subtitle": "Del oplevelser, tips og inspiration med andre rejsende",
    "community.newPost": "Nyt opslag",
    "community.createPost": "Opret nyt opslag",
    "community.all": "Alle",
    "community.anonymous": "Anonym",
    "community.noPostsYet": "Ingen opslag endnu",
    "community.beFirst": "Vær den første til at starte en diskussion!",
    "community.loadMore": "Indlæs flere indlæg",
    "community.loading": "Indlæser…",
    "community.createFirst": "Opret det første opslag",
    "community.back": "Tilbage",
    "community.comments": "Kommentarer",
    "community.noComments": "Ingen kommentarer endnu — vær den første!",
    "community.commentPlaceholder": "Skriv en kommentar...",
    "community.titlePlaceholder": "Titel",
    "community.contentPlaceholder": "Hvad vil du diskutere?",
    "community.addImage": "Tilføj billede (valgfrit)",
    "community.publishing": "Udgiver...",
    "community.publish": "Udgiv opslag",
    "community.toast.postCreated": "Opslag oprettet!",
    "community.toast.postPublished": "Dit opslag er blevet udgivet.",
    "community.toast.postCreateError": "Kunne ikke oprette opslaget.",
    "community.toast.postDeleted": "Opslag slettet",
    "community.toast.error": "Fejl",
    "community.confirmDelete": "Er du sikker på, at du vil slette dette opslag?",
    "community.cat.general": "Generelt",
    "community.cat.tips": "Tips & Tricks",
    "community.cat.routes": "Ruter & Steder",
    "community.cat.gear": "Udstyr",
    "community.cat.help": "Hjælp & Spørgsmål",
    "community.cat.photos": "Billeder & Rejsehistorier",
    "community.type.question": "Spørgsmål",
    "community.type.tip": "Tips",
    "community.type.discussion": "Diskussion",
    "community.type.routes": "Ruter & steder",
    "community.type.gear": "Udstyr",
    "community.type.photos": "Billeder",
    "community.type.help": "Hjælp",
    "community.type.question.desc": "Stil fællesskabet et spørgsmål",
    "community.type.tip.desc": "Del et godt tip",
    "community.type.discussion.desc": "Start en diskussion",
    "community.type.routes.desc": "Del favoritsteder",
    "community.type.gear.desc": "Udstyr og tilbehør",
    "community.type.photos.desc": "Del din rejse",
    "community.type.help.desc": "Bed om hjælp",
    "community.awaitingAnswer": "Venter på svar",
    "community.selectType": "Vælg indlægstype",
    "community.createPostTitle": "Opret indlæg",
    "community.changeType": "Skift type",
    "community.questionPlaceholder": "Skriv dit spørgsmål her...",
    "community.questionContentPlaceholder": "Beskriv dit spørgsmål i detalje, så andre kan hjælpe dig...",
    "community.communityRules": "Fællesskabsregler",
    "community.communityRulesText": "Du må ikke offentliggøre nøgenbilleder, seksuelt indhold, reklame, annoncer, salg, spam, trusler eller chikane. Alle indlæg og billeder kan gennemgås automatisk før offentliggørelse.",
    "games.seoTitle": "Roadtrip-spil – Vantastika",
    "games.seoDesc": "Spil quiz, bingo og flere spil under din roadtrip",
    "demo.phase1Eyebrow": "For mange valgmuligheder",
    "demo.phase1Title": "Se problemet",
    "demo.phase1Text": "Kortet er fuld af steder, og det er svært at vide, hvad der rent faktisk er værd at stoppe ved.",
    "demo.phase2Eyebrow": "Ren planlægning",
    "demo.phase2Title": "Kun verificerede steder",
    "demo.phase2Text": "Vantastika filtrerer støjen fra og gør planlægningen klar, hurtig og sikker.",
    "demo.phase3Eyebrow": "Komplet roadtrip",
    "demo.phase3Title": "Din rute, klar til afgang",
    "demo.phase3Text": "Se hele ruten, vigtige stop og en tur, der føles færdig, før du starter motoren.",
    "demo.stepStart": "Start din rejse",
    "demo.stepVerified": "Verificerede stop",
    "demo.stepArrive": "Ankom til destination",
    "demo.planSmart": "Planlæg smart",
    "demo.budget": "Budget",
    "demo.budgetUsed": "brugt",
    "demo.roadtripPreview": "Roadtrip-forhåndsvisning",
    "service.title": "Service & Nød",
    "service.workshop": "Værksted",
    "service.gasStation": "Tankstation",
    "service.water": "Vand",
    "service.charging": "Opladning",
    "service.waste": "Affald",
    "service.hospital": "Hospital",
    "service.police": "Politi",
    "service.grocery": "Madvarebutik",
    "service.emergency": "Nød",
    "service.exitEmergency": "Afslut SOS",
    "service.emergencyMode": "Nødtilstand",
    "service.callEmergency": "Ring",
    "service.emergencyNote": "112 virker i alle EU-lande",
    "service.locating": "Finder din placering...",
    "service.locationNeeded": "Placering nødvendig",
    "service.enableLocation": "Tillad adgang til placering for at finde ydelser i nærheden",
    "service.retry": "Prøv igen",
    "service.searching": "Søger i nærheden...",
    "service.noResults": "Ingen ydelser fundet i nærheden",
    "service.navigate": "Naviger",
    "service.open": "Åben",
    "service.closed": "Lukket",
    "service.fetchError": "Kunne ikke indlæse ydelser",
    "service.useGPS": "Brug GPS",
    "service.searchPlaceholder": "Søg efter alt... Tesla, Hymer, campingudstyr, apotek",
    "service.manual": "Manuel",
    "service.searchResultsFor": "Resultater for",
    "social.places": "Steder",
    "social.tripsPlanned": "Rejser planlagt",
    "social.members": "Medlemmer",
    "social.reviews": "Anmeldelser",
    "social.popularRoutes": "Populære ruter fra andre rejsende",
    "social.traveler": "Rejsende",
    "social.stops": "stopsteder",
    "error.somethingWrong": "Noget gik galt",
    "error.autoRepairFailed": "Vi forsøgte at reparere automatisk, men det virkede ikke. Prøv at genindlæse siden.",
    "error.tryAgain": "Prøv igen",
    "error.reloadPage": "Genindlæs side",
    "a11y.decreaseText": "Formindsk tekststørrelse",
    "a11y.increaseText": "Forstør tekststørrelse",
    "a11y.decreaseTooltip": "Mindre tekst",
    "a11y.increaseTooltip": "Større tekst",
    "aria.close": "Luk",
    "aria.openVannie": "Åbn Vannie",
    "aria.previous": "Forrige",
    "aria.next": "Næste",
    "aria.openMenu": "Åbn menu",
    "aria.closeMenu": "Luk menu",
    "aria.step": "Trin",
    "onboarding.step": "Trin {current} af {total}",
    "onboarding.back": "Tilbage",
    "onboarding.skip": "Spring over",
    "onboarding.planTrip": "Planlæg din rejse",
    "onboarding.planTripDesc": "Fortæl os, hvor du vil hen, og vi hjælper dig med at opbygge din rejse — rute, stop og budget samlet ét sted.",
    "onboarding.createFirstTrip": "Opret min første rejse",
    "onboarding.findPlaces": "Find steder på kortet",
    "onboarding.findPlacesDesc": "Campingpladser, seværdigheder og restauranter — tilføj stop direkte fra kortet til din rejse.",
    "onboarding.searchCampings": "Udforsk kortet",
    "onboarding.budgetChecklist": "Spor budget og udgifter",
    "onboarding.budgetChecklistDesc": "Registrer dine udgifter, scan kvitteringer og hold styr på dit rejsebudget — alt samlet ét sted.",
    "onboarding.createTrip": "Start mit budget",
    "onboarding.askVannie": "Mød Vannie",
    "onboarding.askVannieDesc": "Din personlige rejseassistent. Få tips, ruteidéer og lokale regler — inkluderet gratis.",
    "onboarding.doneStart": "Lad os komme i gang!",
    "compare.title": "Sammenlign campingpladser",
    "compare.subtitle": "Vælg op til {max} campingpladser til at sammenligne side om side.",
    "compare.add": "Tilføj",
    "compare.searchPlaceholder": "Søg campingplads...",
    "compare.property": "Ejendom",
    "compare.rating": "Vurdering",
    "compare.pricePerNight": "Pris/nat",
    "compare.free": "Gratis",
    "compare.type": "Type",
    "compare.addMore": "Tilføj mindst én campingplads mere for at sammenligne.",
    "compare.selectCampings": "Vælg campingpladser til sammenligning",
    "compare.clickAdd": "Klik på \"Tilføj\" ovenfor for at komme i gang.",
    "compare.electricity": "El",
    "compare.freshWater": "Ferskvand",
    "compare.shower": "Bruser",
    "compare.pets": "Kæledyr",
    "compare.greyWater": "Gråvand",
    "compare.toiletCassette": "Toiletkassette",
    "compare.wc": "Toilet",
    "compare.openAllYear": "Åbent hele året",
    "compare.familyFriendly": "Familievenlig",
    "compare.largeMotorhomes": "Store autocampere",
    "compare.nearWater": "Nær vand",
    "compare.spaWellness": "Spa/Wellness",
    "friends.nearby": "Rejsefæller i nærheden",
    "friends.sharingOn": "Deling slået til",
    "friends.sharingOff": "Deling slået fra",
    "friends.enableSharing": "Aktivér lokationsdeling for at se personer, du deler rejser med",
    "friends.onlyCollaborators": "Kun dem, du samarbejder med på en rejse, kan se din position",
    "friends.noOneSharing": "Ingen rejsefæller deler deres lokation lige nu",
    "friends.anonymous": "Anonym",
    "friends.away": "væk",
    "friends.unknownDistance": "Ukendt afstand",
    "spot.loginToNavigate": "Log ind for at åbne navigation",
    "spot.navigateHere": "Naviger hertil",
    "incident.title": "Anmeld hændelse",
    "incident.button": "Anmeld hændelse",
    "incident.stepOf": "Trin {current} af {total}",
    "incident.saving": "Gemmer...",
    "incident.step1": "Type af hændelse",
    "incident.step2": "Tid og sted",
    "incident.step3": "Oplysninger om køretøj",
    "incident.step4": "Hvad skete der",
    "incident.step5": "Modpart",
    "incident.step6": "Oversigt & PDF",
    "incident.type.collision": "Ulykke",
    "incident.type.theft": "Tyveri",
    "incident.type.vandalism": "Vandalisme",
    "incident.type.breakdown": "Nedbrud",
    "incident.type.weather": "Vejrskade",
    "incident.type.other": "Andet",
    "incident.date": "Dato",
    "incident.time": "Tid",
    "incident.location": "Sted",
    "incident.locationPlaceholder": "Adresse eller stednavn",
    "incident.locationHint": "Vær så specifik som muligt — gade, by, land",
    "incident.vehicleType": "Køretøjstype",
    "incident.vehicleTypePlaceholder": "f.eks. Autocamper, Campervan, Bil",
    "incident.registration": "Registreringsnummer",
    "incident.vehicleDesc": "Beskrivelse af køretøj",
    "incident.vehicleDescPlaceholder": "Farve, mærke, model, årgang",
    "incident.whatHappened": "Hvad skete der?",
    "incident.descriptionPlaceholder": "Beskriv hændelsen klart og roligt",
    "incident.additionalNotes": "Yderligere noter",
    "incident.notesPlaceholder": "Vidner, vejr, vejforhold...",
    "incident.otherPartyHint": "Udfyld hvis en anden part var involveret. Spring over hvis det ikke er relevant.",
    "incident.otherName": "Navn",
    "incident.otherPhone": "Telefonnummer",
    "incident.otherVehicle": "Køretøj",
    "incident.otherReg": "Registreringsnummer",
    "incident.otherInsurance": "Forsikringsselskab",
    "incident.incidentType": "Hændelsestype",
    "incident.summary": "Oversigt over hændelse",
    "incident.downloadPdf": "Gem & download PDF",
    "incident.saveOnly": "Gem uden PDF",
    "incident.wizardTitle": "Skaderapport",
    "incident.step0.title": "Vigtig information",
    "incident.step0.heading": "Dette er en skaderapport — ikke en skadeanmeldelse",
    "incident.step0.body1": "Med dette værktøj opretter du en struktureret rapport, der hjælper dig med at indsamle korrekte oplysninger ved en hændelse.",
    "incident.step0.body2": "Du er selv ansvarlig for at gennemgå og sende rapporten til dit forsikringsselskab.",
    "incident.step0.gdprTitle": "Databeskyttelse (GDPR)",
    "incident.step0.gdprBody1": "Alle oplysninger gemmes sikkert på din konto. Data deles ikke automatisk med tredjeparter.",
    "incident.step0.gdprBody2": "Du kan til enhver tid slette en hændelsesrapport og alle tilhørende data.",
    "incident.step0.accept": "Jeg forstår — start",
    "incident.step1.title": "Type hændelse",
    "incident.step2.title": "Dato, tid og sted",
    "incident.step3.title": "Dit køretøj",
    "incident.step4.title": "Modpartens køretøj",
    "incident.step5.title": "Chauffører og ejere",
    "incident.step6.title": "Forsikring",
    "incident.step7.title": "Skadesbeskrivelse",
    "incident.step8.title": "Billeder",
    "incident.step9.title": "Opsummering",
    "incident.stepOf2": "Trin {current} af {total}",
    "incident.selectCountry": "Vælg land...",
    "incident.searchCountry": "Søg land...",
    "incident.searchInsurance": "Søg eller skriv manuelt...",
    "incident.regNum": "Registreringsnummer",
    "incident.regCountry": "Registreringsland",
    "incident.makeModel": "Mærke / Model",
    "incident.driverIsOwner": "Chauffør = Ejer",
    "incident.yes": "Ja",
    "incident.no": "Nej",
    "incident.ownerName": "Ejerens navn",
    "incident.ownerPhone": "Ejerens telefon",
    "incident.ownerEmail": "Ejerens e-mail",
    "incident.otherDriverName": "Chaufførens navn",
    "incident.otherDriverPhone": "Chaufførens telefon",
    "incident.yourInsurance": "Din forsikring",
    "incident.opponentInsurance": "Modpartens forsikring",
    "incident.insuranceCompany": "Forsikringsselskab",
    "incident.policyNum": "Policenummer",
    "incident.policyNumHint": "Findes på forsikringsbeviset",
    "incident.damageWhat": "Hvad skete der?",
    "incident.damageWhatPlaceholder": "Beskriv hvad der skete",
    "incident.damageHow": "Hvordan skete det?",
    "incident.damageHowPlaceholder": "Beskriv omstændighederne, hastighed, sigt osv.",
    "incident.damageParts": "Hvilke skader opstod?",
    "incident.damagePartsPlaceholder": "Hvilke dele af køretøjet blev beskadiget?",
    "incident.damageOther": "Andre detaljer",
    "incident.damageOtherPlaceholder": "Vidner, vejr, vejforhold...",
    "incident.notesLabel": "Noter",
    "incident.notesPlaceholder2": "Andre noter",
    "incident.imagesOptional": "Billeder er valgfrie, men anbefales",
    "incident.imagesHint": "Billeder kan dokumentere skaderne og forenkle behandlingen hos forsikringsselskabet.",
    "incident.takePhoto": "Tag billede",
    "incident.uploadBtn": "Upload",
    "incident.uploading": "Uploader...",
    "incident.saveDraftFirst": "Rapporten skal gemmes, før du kan uploade billeder.",
    "incident.saveDraftBtn": "Gem kladde først",
    "incident.imagesSecure": "Billeder gemmes sikkert på din konto og deles ikke automatisk. Du kan slette dem når som helst. Kun JPG og PNG (maks 5 MB).",
    "incident.notFilled": "Ikke udfyldt",
    "incident.notSpecified": "Ikke angivet",
    "incident.notDescribed": "Ikke beskrevet",
    "incident.change": "Rediger",
    "incident.summaryTitle": "Opsummering",
    "incident.disclaimerShort": "Du er selv ansvarlig for at gennemgå og sende rapporten til dit forsikringsselskab.",
    "incident.generatePdf": "Genererer PDF...",
    "incident.savePdf": "Gem og download PDF",
    "incident.saveNoPdf": "Gem uden PDF",
    "incident.reportSaved": "Hændelsesrapport gemt",
    "incident.couldNotSave": "Kunne ikke gemme rapport",
    "incident.invalidFile": "Ugyldig fil",
    "incident.uploadFailed": "Upload mislykkedes",
    "incident.imagesAttached": "{count} billede(r) vedhæftet",
    "incident.timeAndPlace": "Tid og sted",
    "incident.yourVehicle": "Dit køretøj",
    "incident.opponent": "Modpart",
    "incident.insurance": "Forsikring",
    "incident.damages": "Skader",
    "incident.images": "Billeder",
    "incident.val.selectType": "Vælg type hændelse",
    "incident.val.dateRequired": "Dato er påkrævet",
    "incident.val.locationRequired": "Sted er påkrævet",
    "incident.val.regRequired": "Registreringsnummer er påkrævet",
    "incident.val.regCountryRequired": "Registreringsland er påkrævet",
    "incident.val.vehicleTypeRequired": "Køretøjstype er påkrævet",
    "incident.val.ownerNameRequired": "Ejerens navn er påkrævet",
    "incident.val.describeWhat": "Beskriv hvad der skete",
    "incident.val.describeHow": "Beskriv hvordan det skete",
    "incident.val.typeMissing": "Hændelsestype mangler",
    "incident.val.regMissing": "Registreringsnummer mangler",
    "incident.val.damageMissing": "Skadesbeskrivelse mangler",
    "incident.opponentVehicle": "Modpartens køretøj",
    "incident.yourDriver": "Dine oplysninger",
    "incident.opponentDriver": "Modpartens oplysninger",
    "incident.pdf.title": "Skaderapport",
    "incident.pdf.disclaimer": "Dette dokument er en skaderapport og ikke en officiel skadeanmeldelse. Du er selv ansvarlig for at gennemgå og sende rapporten til dit forsikringsselskab.",
    "incident.pdf.generated": "Genereret",
    "incident.pdf.trip": "Tur",
    "incident.pdf.type": "Type",
    "incident.pdf.date": "Dato",
    "incident.pdf.time": "Tid",
    "incident.pdf.location": "Sted",
    "incident.pdf.vehicleType": "Køretøjstype",
    "incident.pdf.regNum": "Registreringsnummer",
    "incident.pdf.regCountry": "Registreringsland",
    "incident.pdf.makeModel": "Mærke / Model",
    "incident.pdf.driverIsOwner": "Chauffør = Ejer",
    "incident.pdf.ownerName": "Ejerens navn",
    "incident.pdf.ownerPhone": "Ejerens telefon",
    "incident.pdf.ownerEmail": "Ejerens e-mail",
    "incident.pdf.driver": "Chauffør",
    "incident.pdf.phone": "Telefon",
    "incident.pdf.insuranceCompany": "Forsikringsselskab",
    "incident.pdf.policyNum": "Policenummer",
    "incident.pdf.whatHappened": "Hvad skete der",
    "incident.pdf.howHappened": "Hvordan skete det",
    "incident.pdf.damagedParts": "Beskadigede dele",
    "incident.pdf.otherDetails": "Andre detaljer",
    "incident.pdf.notes": "Noter",
    "incident.pdf.images": "Billeder",
    "incident.pdf.imagesNote": "{count} billede(r) vedhæftet. Billederne er tilgængelige i din Vantastika-konto.",
    "incident.pdf.footer": "Dette dokument er en skaderapport udarbejdet med Vantastika og udgør ikke en officiel skadeanmeldelse.",
    "incident.pdf.footerReview": "Du er selv ansvarlig for at kontrollere oplysningerne og sende dem til dit forsikringsselskab.",
    "incident.pdf.yourVehicle": "Dit køretøj",
    "incident.pdf.opponentVehicle": "Modpartens køretøj",
    "incident.pdf.damageDesc": "Skadesbeskrivelse",
    "incident.pdf.incident": "Hændelse",
    "notFound.title": "Siden blev ikke fundet",
    "notFound.description": "Siden du ledte efter ser ikke ud til at eksistere. Den kan være flyttet eller fjernet.",
    "notFound.home": "Til startsiden",
    "notFound.back": "Gå tilbage",
    "incident.generating": "Genererer PDF...",
    "incident.saved": "Hændelsesrapport gemt",
    "incident.saveFailed": "Kunne ikke gemme rapport",
    "incident.back": "Tilbage",
    "incident.next": "Næste",
    "incident.cancel": "Annuller",
    "incident.pdfTitle": "Hændelsesrapport",
    "incident.pdfGenerated": "Genereret",
    "incident.pdfDisclaimer": "Dette er en personlig registrering. Det er ikke en officiel forsikringskravsformular.",
    "incident.drafts": "Udkast til rapporter",
    "incident.noDrafts": "Ingen udkast til rapporter",
    "incident.continueDraft": "Fortsæt",
    "incident.noReports": "Ingen hændelsesrapporter endnu",
    "incident.deleted": "Rapport slettet",
    "incident.draft": "Udkast",
    "incident.completed": "Fuldført",
    "incident.myReports": "Mine hændelsesrapporter",
    "incident.myReportsSubtitle": "Dine gemte og udkast til rapporter",
    "incident.vtype.motorhome": "Autocamper",
    "incident.vtype.campervan": "Campervan",
    "incident.vtype.car": "Bil",
    "incident.vtype.motorcycle": "Motorcykel",
    "incident.vtype.trailer": "Trailer",
    "incident.vtype.other": "Andet",
    "quick.hello": "Hej {name}! 👋",
    "quick.whatToDo": "Hvad vil du gerne lave i dag?",
    "quick.continueTrip": "Fortsæt rejse",
    "quick.activeTrip": "Aktiv rejse",
    "quick.newTrip": "Ny rejse",
    "quick.planNext": "Planlæg dit næste eventyr",
    "quick.upcomingTrip": "Kommende rejse",
    "quick.findCamping": "Find camping",
    "quick.searchFilter": "Søg & filtrer",
    "quick.favorites": "Favoritter",
    "quick.saved": "{count} gemt",
    "quick.community": "Fællesskab",
    "quick.tipsDiscussions": "Tips & diskussioner",
    "gdpr.title": "Vi tager dit privatliv alvorligt",
    "gdpr.description": "Dette websted bruger kun essentielle og funktionelle cookies (godkendelse, sprogvalg, landeindstilling og cookie-samtykke). Der bruges ingen tracking-, reklame- eller tredjepartsanalyse-cookies.",
    "gdpr.readPolicy": "Læs vores privatlivspolitik",
    "gdpr.accept": "Forstået",
    "gdpr.decline": "Forstået",
    "install.title": "Installer Vantastika",
    "install.description": "Tilføj til din startskærm for hurtig adgang — fungerer som en app!",
    "install.installButton": "Installer app",
    "install.showHow": "Vis hvordan",
    "install.understood": "Forstået",
    "install.iosSteps": "Følg disse trin i Safari:",
    "install.iosTapShare": "Tryk på",
    "install.iosShareLocation": "Nederst i Safari",
    "install.iosAddHome": "Vælg \"Tilføj til hjemmeskærm\"",
    "install.iosScrollDown": "Rul ned i menuen, hvis nødvendigt",
    "install.iosTapAdd": "Tryk på \"Tilføj\"",
    "install.androidSteps": "Følg disse trin:",
    "install.androidTapMenu": "Tryk på",
    "install.androidMenuLocation": "Øverst til højre i browseren",
    "install.androidInstallApp": "Vælg \"Installer app\"",
    "install.androidOrAddHome": "Eller \"Tilføj til hjemmeskærm\"",
    "home.trustFree": "100% gratis",
    "myTrips.title": "Mine ture",
    "myTrips.loginTitle": "Log ind for at se dine ture",
    "myTrips.loginDesc": "Dine gemte ture vil blive vist her efter login.",
    "myTrips.loginButton": "Log ind",
    "myTrips.pausedStatus": "Pauset",
    "myTrips.activeStatus": "Aktiv tur",
    "myTrips.pausedSubtitle": "Din tur er pauset",
    "myTrips.activeSubtitle": "Du har en aktiv tur",
    "myTrips.tripsCount": "{count} ture gemt",
    "myTrips.tripCount": "{count} tur gemt",
    "myTrips.newTrip": "Ny tur",
    "myTrips.resumeTrip": "Genoptag tur",
    "myTrips.continueTrip": "Fortsæt tur",
    "myTrips.resume": "Genoptag",
    "myTrips.pause": "Pause",
    "myTrips.endTrip": "Afslut tur",
    "myTrips.end": "Afslut",
    "myTrips.progress": "{pct}% fuldført",
    "myTrips.stops": "{count} stop",
    "myTrips.nextStop": "Næste stop:",
    "myTrips.openActiveTrip": "Åbn aktiv tur",
    "myTrips.noTrips": "Ingen gemte ture endnu",
    "myTrips.noTripsDesc": "Opret din første tur i planlæggeren, og den vil blive vist her.",
    "myTrips.planFirst": "Planlæg din første tur",
    "myTrips.upcomingTrips": "Kommende ture",
    "myTrips.sharedWithMe": "Delt med mig",
    "myTrips.invitedBy": "Inviteret af",
    "myTrips.completedTrips": "Afsluttede ture",
    "myTrips.startTrip": "Start tur",
    "myTrips.budget": "Budget",
    "myTrips.checklist": "Tjekliste",
    "myTrips.stoppedPromptTitle": "Du ser ud til at være stoppet",
    "myTrips.stoppedPromptDesc": "Vil du fortsætte eller tage en pause?",
    "myTrips.arrivedAt": "Ankommet til {name}!",
    "myTrips.arrivedDesc": "Destination nået - godt klaret!",
    "myTrips.stopRemoved": "Stop fjernet",
    "myTrips.close": "Luk",
    "myTrips.alreadyActiveTrip": "Du har allerede en aktiv rejse",
    "myTrips.endActiveFirst": "Afslut den først.",
    "myTrips.tripStarted": "Rejsen startet!",
    "myTrips.tripStartedDesc": "GPS-sporing og journal er nu aktive.",
    "myTrips.tripPaused": "Rejse sat på pause",
    "myTrips.tripPausedDesc": "Du kan fortsætte når som helst.",
    "myTrips.tripResumed": "Rejsen fortsætter!",
    "myTrips.tripResumedDesc": "GPS-sporing er aktiv igen.",
    "myTrips.tripEnded": "Rejsen afsluttet!",
    "myTrips.tripEndedDesc": "Din rejse er gemt.",
    "myTrips.tripReactivated": "Rejsen genaktiveret!",
    "myTrips.revertedToPlanned": "Nulstillet til planlagt",
    "myTrips.revertedToPlannedDesc": "Rejsen er tilbage som planlagt. Alle data er intakte.",
    "myTrips.missingStops": "Rejsen mangler stop",
    "myTrips.missingStopsDesc": "Tilføj mindst ét stop, før du starter.",
    "myTrips.missingTitle": "Rejsen mangler titel",
    "myTrips.missingTitleDesc": "Giv rejsen et navn, før du starter.",
    "trip.stopAddedFromMap": "Stop tilføjet fra kortet",
    "trip.stopPositionUpdated": "Stopposition opdateret",
    "trip.routeOptimizedDesc": "Stoppene er sorteret efter korteste køredistance.",
    "places.verificationThanks": "Tak for bekræftelsen!",
    "places.verificationThanksDesc": "Du hjælper os med at holde informationen korrekt.",
    "friends.requestAccepted": "Venneanmodning accepteret!",
    "friends.requestSent": "Venneanmodning sendt!",
    "myTrips.checklistReminder": "Glem ikke at gennemgå tjeklisten før afgang",
    "myTrips.today": "I dag",
    "myTrips.tomorrow": "I morgen",
    "myTrips.inDays": "Om {days} dage",
    "myTrips.nextTrip": "Næste tur",
    "myTrips.drafts": "Udkast",
    "myTrips.readyToStart": "Klar til start",
    "myTrips.continuePlanning": "Fortsæt planlægning",
    "myTrips.editTrip": "Rediger",
    "myTrips.openTrip": "Åbn tur",
    "myTrips.viewSummary": "Vis oversigt",
    "myTrips.duplicate": "Dupliker",
    "myTrips.archive": "Arkiver",
    "myTrips.activeTrips": "Aktiv tur",
    "myTrips.showTrip": "Vis tur",
    "myTrips.liveView": "Live visning",
    "myTrips.details": "Detaljer",
    "myTrips.revertToPlanned": "Tilbage til planlagt",
    "myTrips.reactivateTrip": "Genaktiver tur",
    "myTrips.loadingLive": "Indlæser live-visning...",
    "myTrips.showCompleted": "Vis afsluttede ture",
    "myTrips.hideCompleted": "Skjul afsluttede ture",
    "nav.menuApp": "App",
    "nav.menuOther": "Andet",
    "nav.installApp": "Installer app",
    "tools.title": "Rejseværktøjer",
    "tools.checklist": "Tjekliste",
    "tools.budget": "Budget",
    "tools.incident": "Hændelse",
    "tripSummary.distance": "Afstand",
    "tripSummary.driveTime": "Køretid",
    "tripSummary.days": "Dage",
    "tripSummary.stops": "Stop",
    "tripSummary.fuel": "Brændstof",
    "tripSummary.budget": "Budget",
    "tripSummary.route": "Rute",
    "tripSummary.stop": "Stop {n}",
    "tripGuideLink.title": "Guide til {country}",
    "tripGuideLink.camping": "Camping",
    "tripGuideLink.driving": "Kørsel",
    "tripGuideLink.safety": "Sikkerhed",
    "arrival.continueToNext": "Fortsæt til {name}",
    "stats.trips": "Ture",
    "stats.stops": "Stop",
    "stats.nights": "Nætter",
    "stats.countries": "Lande",
    "stats.reviews": "Anmeldelser",
    "stats.friends": "Venner",
    "badge.travel": "Rejse",
    "badge.exploration": "Udforskning",
    "badge.community": "Fællesskab",
    "badge.milestone": "Milepæl",
    "badge.creative": "Kreativ",
    "badge.engagement": "Engagement",
    "badge.special": "Speciel",
    "checklist.title": "Tjeklister",
    "checklist.subtitle": "Tilknyttede og selvstændige tjeklister",
    "checklist.loginRequired": "Log ind for at se dine tjeklister",
    "checklist.loginHint": "Dine tjeklister vil blive vist her efter login.",
    "checklist.backToAll": "Tilbage til alle tjeklister",
    "checklist.trip": "Tur",
    "checklist.general": "Generel pakkeliste",
    "checklist.generalSection": "Selvstændige tjeklister",
    "checklist.generalPacklist": "Generel pakkeliste",
    "checklist.done": "færdig",
    "checklist.noChecklistYet": "Ingen tjekliste endnu - tryk for at oprette",
    "checklist.activeTrips": "Aktive & kommende ture",
    "checklist.completedTrips": "Afsluttede ture",
    "checklist.noTrips": "Ingen ture endnu. Opret en tur for at tilknytte en tjekliste.",
    "checklist.planTrip": "Planlæg tur",
    "checklist.allDone": "Alt er klar! 🎉",
    "checklist.remaining": "{count} emner tilbage",
    "checklist.progress": "{done} af {total} udført",
    "checklist.addCustom": "Tilføj brugerdefineret emne",
    "checklist.addTemplate": "Tilføj skabelon",
    "checklist.reset": "Nulstil",
    "checklist.created": "Tjekliste oprettet! ✅",
    "checklist.createFailed": "Kunne ikke oprette tjekliste",
    "checklist.resetDone": "Tjekliste er nulstillet",
    "checklist.resetFailed": "Kunne ikke nulstille",
    "checklist.itemAdded": "Emne tilføjet!",
    "checklist.itemAddFailed": "Kunne ikke tilføje emne",
    "checklist.inputPlaceholder": "Beskriv hvad du vil tilføje...",
    "checklist.add": "Tilføj",
    "checklist.cancel": "Annuller",
    "checklist.noChecklist": "Ingen tjekliste endnu for {type}.",
    "checklist.selectTemplate": "Vælg skabelon:",
    "checklist.or": "eller",
    "checklist.aiPacklist": "AI-tilpasset pakkeliste",
    "checklist.standardChecklist": "Standard tjekliste",
    "checklist.aiAdapted": "AI-listen er tilpasset til {dest}",
    "checklist.andSeason": " og sæson",
    "checklist.aiCreated": "🤖 AI pakkeliste oprettet!",
    "checklist.allExist": "Alle emner eksisterer allerede på din liste",
    "checklist.newItemsAdded": "{count} nye emner tilføjet!",
    "checklist.addExtraPacking": "Tilføj ekstra pakning:",
    "float.navigate": "Naviger",
    "float.save": "Gem",
    "float.saving": "Gemmer...",
    "profile.save": "Gem ændringer",
    "profile.saving": "Gemmer...",
    "shared.saveToMyTrips": "Gem til mine ture",
    "shared.saving": "Gemmer...",
    "shared.privacyNote": "Start- og slutpunkter er fjernet af hensyn til privatlivets fred. Tilføj din egen startlokation.",
    "shared.addYourStart": "Tilføj din startlokation",
    "shared.templateNote": "Denne tur deles som inspiration. Destinationer og stop er inkluderet — tilføj dine egne start- og slutpunkter.",
    "trip.editOutbound": "Ud-tur",
    "trip.editReturn": "Returrejse",
    "trip.editMainInfo": "Turens detaljer",
    "vehicle.motorhome": "Autocamper",
    "vehicle.campervan": "Campingvogn",
    "vehicle.caravan": "Campingvogn",
    "vehicle.car": "Bil",
    "vehicle.motorcycle": "Motorcykel",
    "vehicle.selectType": "Køretøjstype",
    "vehicle.nameLabel": "Køretøjsnavn (valgfrit)",
    "vehicle.namePlaceholder": "f.eks. Fiat Ducato 2020",
    "vehicle.dimensions": "Køretøjets dimensioner",
    "vehicle.dimensionsExplain": "Bruges til smartere ruteplanlægning — undgå lave broer, smalle passager og vægtbegrænsninger.",
    "vehicle.height": "Højde",
    "vehicle.width": "Bredde",
    "vehicle.length": "Længde",
    "vehicle.weight": "Vægt",
    "vehicle.disclaimer": "Ruteadvarsler baseret på køretøjets dimensioner er vejledende. Verificer altid begrænsninger på stedet. Dækningen af fulde restriktionsdata udvides løbende.",
    "vehicle.profileComplete": "Køretøjsprofil fuldført — klar til smart routing",
    "vehicle.save": "Gem køretøjsprofil",
    "vehicle.saving": "Gemmer...",
    "vehicle.saved": "Køretøjsprofil gemt!",
    "vehicle.saveFailed": "Kunne ikke gemme køretøjsprofil",
    "vehicle.profileTitle": "Køretøjsprofil",
    "vehicle.profileSubtitle": "Til smartere, sikrere ruteplanlægning",
    "myTrips.viewRoute": "Se rute",
    "myTrips.playTrip": "Afspil rejse",
    "myTrips.needMoreStops": "Tilføj mindst 2 stop med koordinater for at afspille rejseanimationen.",
    "tripReplay.stops": "stop",
    "tripReplay.days": "dage",
    "tripReplay.completed": "Rejse afsluttet!",
    "tripReplay.replay": "Afspil igen",
    "tripReplay.share": "Del",
    "routeOverview.title": "Ruteoversigt",
    "routeOverview.totalDistance": "Samlet afstand",
    "routeOverview.driveTime": "Køretid",
    "routeOverview.stops": "Stop",
    "routeOverview.overnights": "Overnatninger",
    "routeOverview.fuelEstimate": "Anslået brændstof",
    "routeOverview.itinerary": "Rejseplan",
    "routeOverview.nights": "nætter",
    "routeOverview.start": "Start",
    "routeOverview.destination": "Destination",
    "routeOverview.visit": "Besøg",
    "why.title": "Hvorfor vælge Vantastika?",
    "why.subtitle": "Alt hvad du behøver til din roadtrip — helt gratis, intet kort nødvendigt.",
    "why.smartPlanning": "Smart turplanlægning",
    "why.smartPlanningDesc": "Opret ruter med stop, brændstofberegning og tidsplanlægning i ét flow.",
    "why.aiAssistant": "AI rejseassistent",
    "why.aiAssistantDesc": "Beskriv din drømmerejse med dine egne ord — vores AI bygger hele planen for dig.",
    "why.campingsEurope": "Campingpladser i Europa",
    "why.campingsEuropeDesc": "Find, sammenlign og filtrer campingpladser og standpladser i 21 lande.",
    "why.travelGuides": "Inspirerende rejseguider",
    "why.travelGuidesDesc": "Færdiglavede tematiserede ruter med historie, stop og tips — klar til at køre.",
    "why.budget": "Budget & udgifter",
    "why.budgetDesc": "Hold styr på omkostninger, brændstof og dagligt budget under din rejse.",
    "why.shareFriends": "Del med venner",
    "why.shareFriendsDesc": "Inviter rejsefæller og planlæg sammen i realtid.",
    "why.checklists": "Smarte tjeklister",
    "why.checklistsDesc": "Tilpassede pakkelister baseret på din køretøjstype og tur.",
    "why.sos": "SOS & sikkerhedssæt",
    "why.sosDesc": "Nøddelingsfunktion, hændelsesrapporter og hurtig adgang til hjælpenumre.",
    "why.badges": "Niveauer & badges",
    "why.badgesDesc": "Saml præstationer, climb niveauer og udfordr dig selv som rejsende.",
    "why.cta": "Opret gratis konto",
    "why.ctaSub": "Intet kort nødvendigt · Klar på 30 sekunder",
    "games.title": "Roadtrip Spil",
    "games.subtitle": "Gør turen sjovere 🎮",
    "games.played": "Spillet",
    "games.totalPoints": "Samlet point",
    "games.challengeFriend": "Udfordr en ven",
    "games.selectCountry": "Vælg land",
    "games.loginPrompt": "Log ind for at gemme spilhistorik",
    "games.loginPromptSub": "Dine scores, favoritter og placering på ranglisten gemmes",
    "games.onTrip": "Tur i gang – spil tilpasset {country}",
    "games.duringTrip": "Undervejs på turen",
    "games.duringTripSub": "Perfekt for passagerer – {n} spiltyper",
    "games.atStop": "Ved stop & pauser",
    "games.atStopSub": "Når du holder pause eller udforsker – {n} spiltyper",
    "games.camping": "Camping & aften-spil",
    "games.campingSub": "Længere spil til aftener og campingliv – {n} spiltyper",
    "games.tabs.games": "Spil",
    "games.tabs.community": "Fællesskab",
    "games.tabs.challenges": "Udfordringer",
    "games.tabs.badges": "Badges",
    "games.tabs.history": "Historik",
    "games.favorites": "Favoritspil",
    "games.favoritesEmpty": "Hjerte et spil for at gemme det her",
    "games.recommended": "Anbefalede",
    "game.quiz.name": "Roadtrip Quiz",
    "game.quiz.desc": "Test din viden om lande, veje og camping",
    "game.bingo.name": "Roadtrip Bingo",
    "game.bingo.desc": "Spot ting langs vejen og marker din bingoplade",
    "game.guess_place.name": "Gæt Stedet",
    "game.guess_place.desc": "Kan du gætte hvilket sted vi beskriver?",
    "game.would_you_rather.name": "Ville du hellere",
    "game.would_you_rather.desc": "Vælg mellem svære rejsedilemmaer",
    "game.trivia_battle.name": "Trivia Kamp",
    "game.trivia_battle.desc": "Dyst mod andre rejsende i realtid",
    "game.photo_challenge.name": "Foto Udfordring",
    "game.photo_challenge.desc": "Kreative fotograferings opgaver ved dit stop",
    "game.story.name": "Historie Spil",
    "game.story.desc": "Skab en rejsehistorie sammen",
    "game.checkpoint.name": "Tjekpoint",
    "game.checkpoint.desc": "Udforsk området med sjove missioner",
    "game.evening_game.name": "Aften Spil",
    "game.evening_game.desc": "Hyggelige gruppespil til aftenen",
    "game.scenario.name": "Scenarie Spil",
    "game.scenario.desc": "Hvad ville du gøre? Rejsedilemmaer",
    "game.intro.tagline.quiz": "Test din viden!",
    "game.intro.tagline.bingo": "Hold øje på vejen!",
    "game.intro.tagline.guess_place": "Kan du gætte det?",
    "game.intro.tagline.would_you_rather": "Tag dit valg!",
    "game.intro.tagline.trivia_battle": "Udfordring accepteret!",
    "game.intro.tagline.photo_challenge": "Fang øjeblikket!",
    "game.intro.tagline.story": "Engang på en rejse...",
    "game.intro.tagline.checkpoint": "Udforsk & opdag!",
    "game.intro.tagline.evening_game": "Tid til at slappe af!",
    "game.intro.tagline.scenario": "Hvad ville du gøre?",
    "game.difficulty": "Vælg sværhedsgrad",
    "game.difficulty.easy": "Let",
    "game.difficulty.medium": "Medium",
    "game.difficulty.hard": "Svær",
    "game.difficulty.easyDesc": "Perfekt for begyndere",
    "game.difficulty.mediumDesc": "En fair udfordring",
    "game.difficulty.hardDesc": "For ægte eksperter",
    "game.question": "Spørgsmål",
    "game.points": "point",
    "game.finished": "Spillet er slut!",
    "game.score.amazing": "Utroligt! Du er en mester! 🏆",
    "game.score.great": "Fantastisk! 🎉",
    "game.score.good": "Godt klaret! 👍",
    "game.score.ok": "Ikke dårligt! Prøv igen! 💪",
    "game.score.low": "Øvelse gør mester! 💪",
    "game.bestStreak": "Bedste stime: {n} i træk!",
    "game.back": "Tilbage",
    "game.paused": "Spillet er sat på pause",
    "game.exit.title": "Forlad spillet?",
    "game.exit.description": "Dine fremskridt vil gå tabt, hvis du afslutter.",
    "game.exit.continue": "Fortsæt med at spille",
    "game.exit.pause": "Sæt spillet på pause",
    "game.exit.quit": "Afslut spillet",
    "game.story.finished": "Historie afsluttet!",
    "game.story.chapters": "kapitler gennemført",
    "game.story.chapter": "Kapitel",
    "game.story.new": "Ny historie",
    "game.story.whatDo": "Hvad gør du?",
    "game.scenario.finished": "Scenarie afsluttet!",
    "game.scenario.steps": "trin taget",
    "game.scenario.step": "Trin",
    "game.scenario.new": "Nyt scenarie",
    "game.scenario.whatDo": "Hvad gør du?",
    "game.scenario.intense": "Intenst!",
    "game.newRound": "Ny runde",
    "game.seeResults": "Se resultater",
    "game.nextQuestion": "Næste spørgsmål",
    "game.playNow": "Spil nu",
    "game.players": "spillere",
    "game.minutes": "min",
    "game.safety.title": "Sikkerhed først!",
    "game.safety.desc": "Dette spil er beregnet til passagerer, ikke chaufføren. Bekræft, at du er passager.",
    "game.safety.confirm": "Jeg er passager",
    "game.safety.later": "Spil senere",
    "game.version": "{country}-version",
    "game.hero.title": "Gør turen sjov",
    "game.hero.game_types": "spiltyper",
    "game.hero.questions": "spørgsmål",
    "game.hero.countries": "lande",
    "game.challenge_friend": "Udfordr en ven",
    "game.trip_active": "Tur i gang",
    "game.login_prompt": "Log ind for at gemme spilhistorik",
    "game.login_prompt_desc": "Dine resultater, favoritter og placering på ranglisten gemmes",
    "game.tab.games": "Spil",
    "game.tab.challenges": "Udfordringer",
    "game.tab.community": "Fællesskab",
    "game.tab.collect": "Saml",
    "game.tab.history": "Historik",
    "game.section.on_the_road": "På vejen",
    "game.section.at_stop": "Ved stop & rastepladser",
    "game.section.camping": "Camping & aften",
    "game.favorites": "Favoritspil",
    "game.no_favorites": "Sæt et hjerte på et spil for at gemme det her",
    "game.wyr.allDone": "Alle spørgsmål færdige!",
    "game.wyr.questionsAnswered": "spørgsmål besvaret",
    "game.wyr.title": "Hellere Da",
    "game.wyr.wouldYouRather": "Ville du hellere...",
    "game.wyr.or": "eller",
    "game.wyr.done": "Færdig!",
    "game.recommended.title": "Anbefalet til dig",
    "game.weekly.title": "Ugens udfordringer",
    "game.weekly.done": "Færdig",
    "game.bingo.found": "fundet",
    "game.bingo.subtitle": "Find ting langs vejen",
    "game.bingo.spotted": "Fundet!",
    "game.bingo.instructions": "Tryk på ting du ser langs vejen. Bingo = række, kolonne eller diagonal!",
    "game.guess.finished": "Gæt Stedet færdig!",
    "game.guess.fewerClues": "Færre ledetråde = flere point!",
    "game.guess.title": "Gæt Stedet",
    "game.guess.place": "Sted",
    "game.guess.clues": "Ledetråde",
    "game.guess.showNext": "Vis næste ledetråd",
    "game.guess.placeholder": "Skriv dit svar...",
    "game.guess.guess": "Gæt",
    "game.guess.correct": "Rigtigt!",
    "game.guess.wrong": "Forkert!",
    "game.guess.correctAnswer": "Rigtigt svar",
    "game.guess.nextPlace": "Næste sted",
    "game.playAgain": "Spil igen",
    "game.evening.finished": "Aftenspil færdigt!",
    "game.evening.wellDone": "Godt klaret med hele gruppen!",
    "game.evening.round": "Runde",
    "game.evening.done": "Færdig!",
    "game.evening.nextRound": "Næste runde",
    "game.evening.trivia": "Trivia",
    "game.evening.creative": "Kreativ",
    "game.evening.debate": "Debat",
    "game.evening.memory": "Hukommelse",
    "game.evening.drawing": "Tegne",
    "game.checkpoint.finished": "Checkpoint færdig!",
    "game.checkpoint.challengesDone": "udfordringer gennemført",
    "game.checkpoint.newChallenges": "Nye udfordringer",
    "game.checkpoint.subtitle": "Udforsk stedet med udfordringer",
    "game.checkpoint.finish": "Afslut checkpoint",
    "game.safety.warning": "Trafiksikkerhed kommer altid først. Kør aldrig og spil samtidig.",
    "game.safety.cancel": "Annuller",
    "game.leaderboard.title": "Topliste – denne uge",
    "game.leaderboard.games": "spil",
    "game.leaderboard.you": "Dig",
    "game.leaderboard.yourPosition": "Din position",
    "game.history.empty": "Ingen spillede spil endnu",
    "game.history.emptyDesc": "Start dit første spil og se din historik her!",
    "game.history.played": "Spillet",
    "game.history.gameTypes": "Spiltyper",
    "game.history.countries": "Lande",
    "game.history.avgScore": "Gennemsnitlig score",
    "game.history.inProgress": "I gang",
    "game.history.continue": "Fortsæt med at spille",
    "game.badges.title": "Badges",
    "game.quiz.fast": "Hurtige",
    "game.quiz.perfect": "Perfekt!",
    "game.audience.label": "Hvem spiller?",
    "game.audience.kids": "Børn",
    "game.audience.kids_desc": "Sjove og nemme spil for børn",
    "game.audience.family": "Familie",
    "game.audience.family_desc": "Spil for hele familien",
    "game.audience.adult": "Voksne",
    "game.audience.adult_desc": "Spil for voksne",
    "game.audience.couple": "Par",
    "game.audience.couple_desc": "Romantiske spil for to",
    "game.audience.friends": "Venner",
    "game.audience.friends_desc": "Sjovt og konkurrencepræget med venner",
    "game.daily.title": "Daglige & ugentlige udfordringer",
    "game.daily.today": "I dag",
    "game.daily.this_week": "Denne uge",
    "game.daily.this_month": "Denne måned",
    "game.daily.quiz_challenge": "Dagens quizudfordring",
    "game.daily.quiz_challenge_desc": "Få 7+ i dagens quiz",
    "game.daily.bingo_quick": "Hurtig bingo",
    "game.daily.bingo_quick_desc": "Find 5 ting på bingopladen",
    "game.daily.guess_master": "Gæt master",
    "game.daily.guess_master_desc": "Gæt 3 steder korrekt",
    "game.daily.scenario_star": "Scenariestjerne",
    "game.daily.scenario_star_desc": "Gennemfør en scenarierunde",
    "game.daily.photo_snap": "Fotoudfordring",
    "game.daily.photo_snap_desc": "Gennemfør 3 fotoudfordringer",
    "game.daily.story_teller": "Historiefortæller",
    "game.daily.story_teller_desc": "Gennemfør et historieeventyr",
    "game.daily.trivia_duel": "Trivia-duel",
    "game.daily.trivia_duel_desc": "Få 5+ i en trivia-kamp",
    "game.weekly.quiz_master": "Ugentlig quizmester",
    "game.weekly.quiz_master_desc": "Få 9+ i en hvilken som helst quiz denne uge",
    "game.weekly.bingo_champion": "Blingo-mester",
    "game.weekly.bingo_champion_desc": "Gennemfør en fuld bingoplade",
    "game.weekly.explorer": "Ugentlig opdagelsesrejsende",
    "game.weekly.explorer_desc": "Gennemfør 4 checkpoint-udfordringer",
    "game.weekly.photo_pro": "Foto-pro",
    "game.weekly.photo_pro_desc": "Gennemfør 6 fotoudfordringer",
    "game.weekly.evening_host": "Aften vært",
    "game.weekly.evening_host_desc": "Vært et spilsession om aftenen",
    "game.monthly.country_champion": "Landets mester",
    "game.monthly.country_champion_desc": "Perfekt score i en landekvizz",
    "game.monthly.all_rounder": "Alt-mulig-mand",
    "game.monthly.all_rounder_desc": "Spil 5 forskellige spiltyper",
    "game.monthly.road_legend": "Vejlegende",
    "game.monthly.road_legend_desc": "Gennemfør 3 historieeventyr",
    "game.season.spring": "Forårsspil",
    "game.season.summer": "Sommerspil",
    "game.season.autumn": "Efterårsspil",
    "game.season.winter": "Vinterspil",
    "game.seasonal.limited": "Sæsonbestemt",
    "game.seasonal.spring_awakening": "Forårsopvågning quiz",
    "game.seasonal.spring_awakening_desc": "Test din viden om foråret",
    "game.seasonal.easter_trip": "Påske roadtrip bingo",
    "game.seasonal.easter_trip_desc": "Spot påske på vejen",
    "game.seasonal.spring_photo": "Forårsfotoudfordring",
    "game.seasonal.spring_photo_desc": "Fang forårets skønhed",
    "game.seasonal.summer_roadtrip": "Sommer roadtrip quiz",
    "game.seasonal.summer_roadtrip_desc": "Sol, strande og sommerveje",
    "game.seasonal.beach_bingo": "Strand & kyst bingo",
    "game.seasonal.beach_bingo_desc": "Find sommerting langs kysten",
    "game.seasonal.midsummer": "Sankthans udfordring",
    "game.seasonal.midsummer_desc": "Fejr sankthans med udfordringer",
    "game.seasonal.camping_nights": "Sommercamping nætter",
    "game.seasonal.camping_nights_desc": "Perfekt til varme campingaftener",
    "game.seasonal.autumn_colors": "Efterårsfarver foto",
    "game.seasonal.autumn_colors_desc": "Fang efterårets gyldne nuancer",
    "game.seasonal.harvest_quiz": "Høsttid quiz",
    "game.seasonal.harvest_quiz_desc": "Quiz om efterår og høst",
    "game.seasonal.fog_mystery": "Tåget mysterium historie",
    "game.seasonal.fog_mystery_desc": "Et mystisk efterårs eventyr",
    "game.seasonal.winter_roads": "Vinterveje quiz",
    "game.seasonal.winter_roads_desc": "Test din viden om vinterkørsel",
    "game.seasonal.christmas_markets": "Julemarked opdagelses",
    "game.seasonal.christmas_markets_desc": "Gæt de berømte julemarkeder",
    "game.seasonal.cozy_evening": "Hyggelig vinteraften",
    "game.seasonal.cozy_evening_desc": "Perfekt spil til kolde aftener",
    "game.seasonal.northern_lights": "Nordlys historie",
    "game.seasonal.northern_lights_desc": "Et eventyr under nordlyset",
    "game.level.label": "Niveau",
    "game.level.beginner": "Begynder",
    "game.level.explorer": "Opdagelsesrejsende",
    "game.level.adventurer": "Eventyrer",
    "game.level.traveler": "Rejsende",
    "game.level.road_master": "Vejmester",
    "game.level.pro": "Roadtrip Pro",
    "game.level.navigator": "Navigator",
    "game.level.euro_champ": "Euro Mester",
    "game.level.legendary": "Legendarisk",
    "game.level.legend": "Roadtrip Legende",
    "game.level.next": "Næste",
    "game.level.games_played": "spillet",
    "game.collectibles.title": "Land Indsamlinger",
    "game.collectibles.countries": "lande",
    "country.sweden": "Sverige",
    "country.norway": "Norge",
    "country.germany": "Tyskland",
    "country.italy": "Italien",
    "country.france": "Frankrig",
    "country.spain": "Spanien",
    "game.region.norrland": "Norrland",
    "game.region.svealand": "Svealand",
    "game.region.gotaland": "Götaland",
    "game.region.fjords": "Fjorde",
    "game.region.nordnorge": "Nordnorge",
    "game.region.sorlandet": "Sørlandet",
    "game.region.bayern": "Bayern",
    "game.region.norddeutschland": "Nordtyskland",
    "game.region.rheinland": "Rhinland",
    "game.region.norditalien": "Norditalien",
    "game.region.toscana": "Toscana",
    "game.region.syditalien": "Syditalien",
    "game.region.ildefrance": "Île-de-France",
    "game.region.provence": "Provence",
    "game.region.bretagne": "Bretagne",
    "game.region.andalucia": "Andalusien",
    "game.region.catalonia": "Catalonien",
    "game.region.nordspanien": "Nordspanien",
    "nav.bottom.budget": "Budget",
    "nav.installTitle": "Installer Vantastika",
    "nav.installIOS1": "Tryk på Del-knappen (nederst i Safari)",
    "nav.installIOS2": "Vælg \"Tilføj på startskærm\"",
    "nav.installIOS3": "Tryk på \"Tilføj\"",
    "nav.installAndroid1": "Tryk på menuikonet (⋮ øverst til højre)",
    "nav.installAndroid2": "Vælg \"Installer app\" eller \"Tilføj til startskærm\"",
    "nav.installUnderstood": "Forstået",
    "tripStatus.startTitle": "Vil du starte turen nu?",
    "tripStatus.startDesc": "Turen sættes som igangværende, og GPS-tracking aktiveres.",
    "tripStatus.startConfirm": "Ja, start",
    "tripStatus.endTitle": "Vil du virkelig afslutte turen?",
    "tripStatus.endDesc": "Du kan genoptage den senere, hvis du ombestemmer dig.",
    "tripStatus.endConfirm": "Ja, afslut",
    "tripStatus.pauseTitle": "Vil du sætte turen på pause?",
    "tripStatus.pauseDesc": "Du kan fortsætte turen når som helst.",
    "tripStatus.pauseConfirm": "Ja, sæt på pause",
    "tripStatus.resumeTitle": "Vil du genoptage turen?",
    "tripStatus.resumeDesc": "Turen fortsætter, hvor du satte den på pause.",
    "tripStatus.resumeConfirm": "Ja, fortsæt",
    "tripStatus.revertTitle": "Tilbage til planlagt tur?",
    "tripStatus.revertDesc": "Alle turens oplysninger beholdes – kun status ændres tilbage til planlagt.",
    "tripStatus.revertConfirm": "Ja, tilbagefør",
    "tripStatus.reactivateTitle": "Genoptag den afsluttede tur?",
    "tripStatus.reactivateDesc": "Turen sættes som igangværende igen. Alle data beholdes.",
    "tripStatus.reactivateConfirm": "Ja, genoptag",
    "tripStatus.trip": "Tur",
    "tripStatus.cancel": "Annuller",
    "addToTrip.title": "Tilføj til tur",
    "addToTrip.loginPrompt": "Log ind for at tilføje steder til dine ture.",
    "addToTrip.login": "Log ind",
    "addToTrip.createAccount": "Opret konto",
    "addToTrip.howUsePlace": "Hvordan vil du bruge dette sted?",
    "addToTrip.chooseTrip": "Vælg tur",
    "addToTrip.startPoint": "Startpunkt",
    "addToTrip.startPointDesc": "Turen starter her",
    "addToTrip.destination": "Destination",
    "addToTrip.destinationDesc": "Jeg vil rejse hertil",
    "addToTrip.stop": "Stop undervejs",
    "addToTrip.stopDesc": "Et stop under turen",
    "addToTrip.noTrips": "Du har ingen planlagte ture endnu",
    "addToTrip.noTripsDesc": "Opret en ny tur, og tilføj stedet direkte.",
    "addToTrip.stops": "stop",
    "addToTrip.unnamed": "Unavngiven tur",
    "addToTrip.addedAs": "er blevet tilføjet som",
    "addToTrip.roleStart": "startpunkt",
    "addToTrip.roleDestination": "destination",
    "addToTrip.roleStop": "stop",
    "addToTrip.couldNotAdd": "Kunne ikke tilføje stedet",
    "addToTrip.createNew": "Opret ny tur med dette sted",
    "optimize.bannerTitle": "AI fandt en kortere rute",
    "optimize.bannerDesc": "Spar ca.",
    "optimize.bannerByReordering": "ved at ændre rækkefølgen af dine stop.",
    "optimize.viewAlternatives": "Vis alternativer",
    "optimize.dismiss": "Ikke nu",
    "optimize.fuelSaved": "i brændstof",
    "camping.notFound": "Stedet kunne ikke findes.",
    "camping.approxPrice": "ca.",
    "camping.perNight": "/ nat (ca.)",
    "camping.free": "Gratis",
    "camping.typeStellplatz": "Stellplatz",
    "camping.typeCamping": "Camping",
    "camping.amenities": "Faciliteter",
    "camping.checkIn": "Indtjek",
    "camping.checkOut": "Udtjek",
    "camping.visitWebsite": "Besøg hjemmeside",
    "camping.addToTrip": "Tilføj til tur",
    "camping.navigate": "Naviger",
    "camping.viewPage": "Se side",
    "camping.amenityElectricity": "Strøm",
    "camping.amenityFreshWater": "Ferskvand",
    "camping.amenityGreyWater": "Gråvand",
    "camping.amenityToilet": "Toilet",
    "camping.amenityShower": "Bruser",
    "camping.amenityWC": "WC",
    "camping.amenityPets": "Kæledyr",
    "camping.amenityAllYear": "Åbent hele året",
    "camping.amenityFamily": "Familievenligt",
    "camping.amenityLarge": "Store autocampere",
    "camping.amenityWater": "Nær vand",
    "camping.amenitySpa": "Spa",
    "camping.amenityWifi": "WiFi",
    "camping.amenityLaundry": "Vaskeri",
    "camping.amenityRestaurant": "Restaurant",
    "share.inviteToSharedTrip": "Inviter til delt tur",
    "share.inviteDesc": "Modtageren deltager i samme tur. Alle ændringer synkroniseres i realtid — budget, tjekliste, stop og noter.",
    "share.inviteViaApp": "Inviter via app...",
    "share.copyInviteLink": "Kopiér invitation link",
    "share.copied": "Kopieret!",
    "share.shareAsTip": "Del som rejsetip",
    "share.tipDesc": "Modtageren får sin egen kopi som inspiration. Ændringer påvirker ikke din tur.",
    "share.createAiGuide": "Opret AI rejseguide",
    "share.generatingAi": "Genererer AI rejseguide... ✨",
    "share.aiGuideCreated": "AI rejseguide oprettet! ✨",
    "share.couldNotCreateAi": "Kunne ikke oprette AI guide",
    "share.aiGuideActive": "AI rejseguide aktiv",
    "share.tabShare": "Del",
    "share.tabTeam": "Rejsegruppe",
    "share.tabComments": "Kommentarer",
    "share.tabActivity": "Aktivitet",
    "share.inviteByUsername": "Inviter med brugernavn eller del et invitation link:",
    "share.usernamePlaceholder": "Brugernavn til at invitere...",
    "share.inviteSent": "Invitation sendt!",
    "share.userNotFound": "Bruger ikke fundet. Indtast præcist brugernavn.",
    "share.cannotInviteSelf": "Du kan ikke invitere dig selv.",
    "share.alreadyInvited": "Bruger er allerede inviteret.",
    "share.couldNotInvite": "Kunne ikke invitere",
    "share.noMembers": "Ingen gruppemedlemmer endnu. Inviter nogen til at planlægge sammen!",
    "share.editor": "Redaktør",
    "share.removedFromGroup": "Fjernet fra rejsegruppe",
    "share.noComments": "Ingen kommentarer endnu.",
    "share.unknown": "Ukendt",
    "share.commentPlaceholder": "Skriv en kommentar...",
    "share.send": "Send",
    "share.noActivity": "Ingen aktivitet endnu.",
    "share.actionInvited": "inviterede",
    "share.actionCommented": "kommenterede",
    "share.actionAddedStop": "tilføjede stop:",
    "share.actionUpdatedStop": "opdaterede stop:",
    "share.actionRemovedStop": "fjernede stop:",
    "share.actionUpdatedTrip": "opdaterede turen",
    "signup.guestVsMember": "Gæst vs Medlem",
    "signup.seeWhatYouGet": "Se hvad du får som medlem",
    "signup.freeNoCard": "100% gratis — intet kreditkort nødvendigt",
    "signup.premiumSoon": "Premium kommer snart med endnu flere funktioner",
    "signup.username": "Brugernavn",
    "signup.usernamePlaceholder": "f.eks. vanansen",
    "signup.displayName": "Visningsnavn",
    "signup.displayNamePlaceholder": "Dit navn vist til andre",
    "signup.confirmPassword": "Bekræft adgangskode",
    "signup.passwordPlaceholder": "Minimum 8 tegn, bogstav + tal",
    "signup.passwordsNoMatch": "Adgangskoder stemmer ikke overens",
    "signup.selectCountry": "Vælg dit land",
    "signup.whatVehicle": "Hvad rejser du med?",
    "signup.ageWarning": "Du skal være mindst 16 år gammel for at oprette en konto på Vantastika. Hvis du er under 18, kræves samtykke fra en værge.",
    "signup.termsConsent": "Jeg accepterer",
    "signup.termsLink": "servicevilkårene",
    "signup.andThe": "og",
    "signup.integrationTermsLink": "integrationsvilkårene",
    "signup.ageConfirm": "og bekræfter, at jeg er mindst 16 år gammel.",
    "signup.gdprConsent": "Jeg har læst og accepterer",
    "signup.privacyLink": "privatlivspolitikken (GDPR)",
    "signup.or": "eller",
    "signup.verificationFailed": "Verifikation mislykkedes. Prøv igen.",
    "signup.captchaFailed": "CAPTCHA-verifikation mislykkedes. Prøv igen.",
    "signup.tooManyAttempts": "For mange forsøg. Vent et øjeblik.",
    "signup.acceptTerms": "Du skal acceptere betingelserne",
    "signup.selectCountryError": "Vælg dit land",
    "signup.enterDisplayName": "Indtast et visningsnavn",
    "signup.enterUsername": "Indtast et brugernavn",
    "signup.accountCreated": "Konto oprettet!",
    "signup.welcomeMsg": "Velkommen til Vantastika!",
    "signup.checkDetails": "Tjek dine oplysninger og prøv igen.",
    "signup.showPassword": "Vis adgangskode",
    "signup.hidePassword": "Skjul adgangskode",
    "signupVehicle.motorhome": "🚐 Autocamper",
    "signupVehicle.motorhomeDesc": "Jeg rejser med autocamper",
    "signupVehicle.car": "🚗 Bil",
    "signupVehicle.carDesc": "Jeg rejser med bil",
    "signupVehicle.motorcycle": "🏍️ Motorcykel",
    "signupVehicle.motorcycleDesc": "Jeg kører motorcykel",
    "signupVehicle.campervan": "🚌 Autocamper",
    "signupVehicle.campervanDesc": "Jeg har en autocamper",
    "signupVehicle.rental": "🔑 Lejet køretøj",
    "signupVehicle.rentalDesc": "Jeg lejer ved behov",
    "signupVehicle.other": "🌍 Andet / Usikker",
    "signupVehicle.otherDesc": "Fleksibel rejsende",
    "profile.yourProfile": "Din profil",
    "profile.since": "Siden",
    "profile.quickLinks": "Genveje",
    "profile.myTrips": "Mine ture",
    "profile.checklists": "Tjeklister",
    "profile.savedPlaces": "Gemte steder",
    "profile.planning": "Planlægning",
    "profile.saved": "Gemt",
    "vehicle.projects": "Projekter",
    "vehicle.viewAll": "Vis alle",
    "vehicle.noProjects": "Ingen projekter koblet til {name} endnu.",
    "vehicle.createProject": "Opret projekt",
    "wishlist.startExploring": "Begynd at udforske",
    "projects.vehicleLabel": "Køretøj",
    "projects.linkedVehicle": "Tilknyttet køretøj",
    "projects.linkVehicle": "Tilknyt køretøj",
    "projects.noVehicle": "Intet køretøj tilknyttet",
    "projects.selectVehicle": "Vælg køretøj",
    "projects.noVehicleOption": "Ingen",
    "profile.notifications": "Notifikationer",
    "profile.travelOverview": "Rejseoversigt",
    "profile.levelAndProfile": "Rejseprofil & niveau",
    "profile.progress": "Fremgang",
    "profile.total": "Total",
    "profile.badges": "Badges",
    "profile.challenges": "Udfordringer",
    "profile.activeChallenges": "Aktive udfordringer",
    "profile.viewAllProgress": "Se al fremgang",
    "profile.incidentReports": "Hændelsesrapporter",
    "profile.newReport": "Ny rapport",
    "profile.social": "Socialt",
    "profile.inbox": "Indbakke",
    "profile.mySubmissions": "Mine indsendelser",
    "profile.accountSettings": "Konto & indstillinger",
    "profile.accountSettingsDesc": "Profil, tema og adgangskode",
    "profile.email": "E-mail",
    "profile.displayName": "Visningsnavn",
    "profile.displayNamePlaceholder": "Dit navn",
    "profile.username": "Brugernavn",
    "profile.usernamePlaceholder": "@brugernavn",
    "profile.country": "Land",
    "profile.countryPlaceholder": "Sverige",
    "profile.appearance": "Udseende",
    "profile.themeLight": "Lys",
    "profile.themeDark": "Mørk",
    "profile.themeSystem": "System",
    "profile.themeLightDesc": "Altid lys tema",
    "profile.themeDarkDesc": "Altid mørkt tema",
    "profile.themeSystemDesc": "Følger enhed",
    "profile.changePassword": "Skift adgangskode",
    "profile.currentPassword": "Nuværende adgangskode",
    "profile.newPassword": "Ny adgangskode",
    "profile.newPasswordPlaceholder": "Mindst 8 tegn",
    "profile.confirmPasswordLabel": "Bekræft",
    "profile.changePasswordBtn": "Skift adgangskode",
    "profile.changingPassword": "Ændrer...",
    "profile.wrongCurrentPassword": "Forkert nuværende adgangskode",
    "profile.passwordChanged": "Adgangskode ændret!",
    "profile.passwordMinLength": "Adgangskode skal være mindst 8 tegn lang",
    "profile.passwordsMismatch": "Adgangskoder stemmer ikke overens",
    "profile.deleteAccount": "Slet konto",
    "profile.permanentDeletion": "Permanent sletning",
    "profile.deleteAccountDesc": "Når du sletter din konto, fjernes følgende permanent: profil, ture, stop, favoritter, tjeklister, forumindlæg, anmeldelser, billeder, venneanmodninger, beskeder, hændelsesrapporter og notifikationer.",
    "profile.deleteAccountNote": "Hvad der kan forblive: Anonymiserede revisionslogfiler (ikke knyttet til din konto), den delte badge-katalog og enhver data, vi er juridisk forpligtet til at beholde (f.eks. regnskabslove, max 7 år).",
    "profile.deleteMyAccount": "Slet min konto",
    "profile.deleteConfirmTitle": "Slet konto permanent",
    "profile.deleteConfirmDesc": "Er du sikker? Følgende slettes permanent:",
    "profile.deleteConfirmList": "• Profil, indstillinger og avatar\n• Ture, stop, budget og tjeklister\n• Favoritter, samlinger og gemte steder\n• Hændelsesrapporter og uploadede billeder\n• Forumindlæg, kommentarer og likes\n• Venneanmodninger og beskeder\n• Notifikationer og stedverifikationer",
    "profile.deleteConfirmNote": "Anonymiserede revisionslogfiler og den delte badge-katalog påvirkes ikke. Data, vi er juridisk forpligtet til at beholde, kan forblive op til 7 år.",
    "profile.cannotUndo": "Kan ikke fortrydes.",
    "profile.deleting": "Sletter...",
    "profile.yesDeleteAccount": "Ja, slet min konto",
    "profile.accountDeleted": "Konto er slettet",
    "profile.accountDeletedDesc": "Alle dine data er fjernet.",
    "profile.couldNotDelete": "Kunne ikke slette konto",
    "profile.profileSaved": "Profil gemt!",
    "profile.couldNotSave": "Kunne ikke gemme",
    "profile.profilePicUpdated": "Profilbillede opdateret!",
    "profile.uploading": "Uploader...",
    "profile.fileTooLarge": "Filen er for stor",
    "profile.fileTooLargeDesc": "Max 2 MB",
    "profile.imageRejected": "Billede afvist",
    "profile.myVehicle": "Mit køretøj",
    "profile.enterCurrentPassword": "Indtast din nuværende adgangskode",
    "profile.couldNotChangePassword": "Kunne ikke ændre adgangskode",
    "profile.vehicleMotorhome": "Campingvogn",
    "profile.vehicleCar": "Bil",
    "profile.vehicleMotorcycle": "MC",
    "profile.vehicleCampervan": "Autocamper",
    "profile.vehicleRental": "Leje",
    "profile.vehicleOther": "Andet",
    "profile.activeNow": "Aktivt lige nu",
    "profile.today": "I dag!",
    "profile.inDays": "Om {n} dage",
    "profile.newNotification": "ny notifikation",
    "profile.newNotifications": "nye notifikationer",
    "profile.showAll": "Vis alle",
    "profile.inspection": "Syn",
    "profile.gasInspection": "Gaskontrol",
    "profile.service": "Service",
    "profile.expired": "Udløbet!",
    "profile.noDateRegistered": "Ingen dato registreret",
    "profile.noServiceRegistered": "Ingen service registreret",
    "profile.missing": "Mangler",
    "profile.insurance": "Forsikring",
    "profile.noInsuranceRegistered": "Ingen forsikring registreret",
    "profile.regNumberMissing": "Registreringsnummer mangler",
    "profile.addForSmartData": "Tilføj for smart køretøjsdata",
    "profile.addRegNumber": "Tilføj regnr",
    "profile.statusGood": "God",
    "profile.statusSoon": "Snart",
    "profile.statusAction": "Handling",
    "profile.edit": "Rediger",
    "profile.addService": "Tilføj service",
    "profile.addVehicleDesc": "Tilslut dit køretøj for smart planlægning, påmindelser og tilpasset indhold",
    "profile.addVehicleBtn": "Tilføj køretøj",
    "profile.validUntil": "Gyldig til {date}",
    "scan.title": "Smart kvitteringsscanning",
    "scan.takePhoto": "Tag foto",
    "scan.upload": "Upload",
    "scan.photoOrUpload": "Tag et foto eller upload en kvittering",
    "scan.aiAnalyzes": "AI analyserer kvitteringen linje for linje",
    "scan.analyzing": "Analyserer kvittering...",
    "scan.analyzingDesc": "AI læser varer, beløb og kategorier",
    "scan.fileTooLarge": "Filen er for stor",
    "scan.fileTooLargeMax": "Max 15 MB",
    "scan.analysisFailed": "Analyse mislykkedes",
    "scan.couldNotAnalyze": "Kunne ikke analysere kvitteringen",
    "scan.amount": "Beløb",
    "scan.currency": "Valuta",
    "scan.possibleAmounts": "Mulige beløb:",
    "scan.converted": "Konverteret:",
    "scan.date": "Dato",
    "scan.titleLabel": "Titel",
    "scan.itemsPerCategory": "Varer pr. kategori",
    "scan.mixedCategories": "Blandede kategorier",
    "scan.howToSave": "Hvordan vil du gemme?",
    "scan.oneExpense": "Én udgift",
    "scan.splitUp": "Opdel",
    "scan.splitDesc": "Opretter {n} separate udgifter — én pr. kategori.",
    "scan.mainCategory": "Hovedkategori",
    "scan.linkToBudget": "Link til budget",
    "scan.noBudgetSelected": "Intet budget valgt",
    "scan.noBudget": "Intet budget",
    "scan.linkToTrip": "Link til tur",
    "scan.noTripSelected": "Ingen tur valgt",
    "scan.noTrip": "Ingen tur",
    "scan.note": "Note",
    "scan.hideRawText": "Skjul rå kvitteringstekst",
    "scan.showRawText": "Vis rå kvitteringstekst",
    "scan.cancel": "Annuller",
    "scan.saving": "Gemmer...",
    "scan.saveExpense": "Gem udgift",
    "scan.saveExpenses": "Gem {n} udgifter",
    "scan.expenseSaved": "Udgift gemt fra kvittering ✓",
    "scan.expensesSaved": "{n} udgifter gemt fra kvittering ✓",
    "scan.fillAmountAndTitle": "Indtast beløb og titel",
    "scan.couldNotSave": "Kunne ikke gemme",
    "scan.confidenceHigh": "Høj sikkerhed",
    "scan.confidenceMedium": "Medium sikkerhed — gennemgå felter",
    "scan.confidenceLow": "Lav sikkerhed — tjek alle felter",
    "scan.dotMedium": "Medium sikkerhed",
    "scan.dotLow": "Usikker — tjek",
    "scan.close": "Luk",
    "scan.paid": "Betalt:",
    "scan.vat": "Moms:",
    "scan.receipt": "Kvittering",
    // Camera permissions
    "camera.prePermissionHint": "Kameraadgang er nødvendig for at scanne kvitteringer",
    "camera.deniedError": "Kameraadgang blev nægtet. Aktiver den i browserens eller telefonens indstillinger.",
    "camera.notFoundError": "Intet kamera fundet på denne enhed.",
    "camera.inUseError": "Kameraet bruges af en anden app.",
    "camera.genericError": "En uventet fejl opstod under adgang til kameraet.",
    "camera.openSettings": "Vis instruktioner",
    "camera.androidSettingsHint": "Gå til Indstillinger → Apps → Din browser → Tilladelser → Kamera",
    "camera.iosSettingsHint": "Gå til Indstillinger → Anonymitet og sikkerhed → Kamera → Din browser",
    "camera.desktopSettingsHint": "Klik på låseikonet i adresselinjen og aktiver kameraadgang.",
    // Location permissions
    "location.detecting": "Henter din position…",
    "location.unsupportedTitle": "Positionsdeling understøttes ikke",
    "location.unsupportedDesc": "Din enhed eller browser understøtter ikke positionsdeling. Prøv en anden browser.",
    "location.deniedTitle": "Positionsadgang nægtet",
    "location.deniedDesc": "Du har nægtet positionsadgang. For at bruge denne funktion skal du aktivere positionsdeling i browserens eller telefonens indstillinger.",
    "location.tryAgain": "Prøv igen",
    "location.openSettings": "Vis instruktioner",
    "location.androidSettingsHint": "Gå til Indstillinger → Apps → Din browser → Tilladelser → Position",
    "location.iosSettingsHint": "Gå til Indstillinger → Anonymitet og sikkerhed → Stedtjenester → Din browser",
    "location.desktopSettingsHint": "Klik på låseikonet i adresselinjen og aktiver positionsadgang.",
    "location.promptDesc": "Vi har brug for din position for at vise relevante resultater i nærheden.",
    "location.allowAccess": "Tillad positionsadgang",
    "location.fetchErrorTitle": "Kunne ikke hente position",
    "location.timeoutDesc": "Det tog for lang tid at hente din position. Kontroller at GPS er aktiveret.",
    "location.unavailableDesc": "Din position kunne ikke bestemmes lige nu. Prøv igen om et øjeblik.",
    "location.errorTitle": "Positionsfejl",
    "location.errorDesc": "Noget gik galt under hentning af din position.",
    "season.springGreeting": "Foråret er her — tid til planlægning!",
    "season.summer": "Sommer",
    "season.summerGreeting": "Sommeren venter — hvilken vej vil du tage?",
    "season.autumn": "Efterår",
    "season.autumnGreeting": "Efterårsfarverne kalder!",
    "season.winter": "Vinter",
    "season.winterGreeting": "Vinteren er magisk — planlæg en snetur!",
    "map.searchPlaceholder": "Søg by, camping, adresse...",
    "map.searching": "Søger...",
    "map.searchingSuggestions": "Søger efter forslag…",
    "map.noResults": "Ingen resultater fundet",
    "map.noResultsHint": "Prøv by, camping, adresse eller stedsnavn",
    "map.exploreEurope": "Udforsk Europa",
    "map.exploreDesc": "Søg efter et sted for at opdage campingpladser, rastepladser og seværdigheder",
    "map.exploreDescLong": "Søg efter et sted for at opdage campingpladser, restauranter, seværdigheder og mere",
    "map.resultsNear": "Resultater nær",
    "map.noCampingsFound": "Ingen campingpladser eller rastepladser fundet i nærheden.",
    "map.mapErrorBanner": "Kortet kunne ikke indlæses. Du kan stadig søge og udforske steder i listevisning.",
    "map.nearYou": "Steder nær dig",
    "map.places": "steder",
    "map.placesNearYou": "steder nær dig",
    "map.campingsNearby": "Campingpladser og rastepladser i nærheden",
    "map.explore": "Udforsk",
    "map.closePanel": "Luk panel",
    "map.showResults": "Vis resultater",
    "map.navigate": "Naviger",
    "map.addToTrip": "Tilføj til tur",
    "map.open": "Åbent",
    "map.closed": "Lukket",
    "map.free": "Gratis",
    "map.camping": "Camping",
    "map.stellplatz": "Rasteplads",
    "map.dumpStation": "Tømningsstation",
    "map.navigateHit": "Naviger hertil",
    "map.place": "Sted",
    "map.restaurant": "Restaurant",
    "map.supermarket": "Dagligvare",
    "map.attraction": "Seværdighed",
    "map.parking": "Parkering",
    "map.fuelStation": "Tankstation",
    "map.museum": "Museum",
    "map.nature": "Natur",
    "map.sights": "Seværdigheder",
    "map.grocery": "Dagligvare",
    "map.perNight": "/nat",
    "submit.addressAutoHint": "Søg adressen først — land, region og by udfyldes automatisk.",
    "submit.autoDetected": "Land, region og by er blevet udfyldt automatisk!",
    "submit.placeExists": "Stedet findes allerede",
    "submit.placeExistsDesc": "Et sted med navnet \"{name}\" er allerede registreret.",
    "submit.nearbyExists": "Sted i nærheden findes allerede",
    "submit.nearbyExistsDesc": "\"{name}\" er allerede registreret på samme sted.",
    "submit.imageRejected": "Billede afvist",
    "submit.mainImage": "Hovedbillede",
    "submit.contactInfo": "Kontaktoplysninger",
    "service.heroDesc": "Find værksteder, brændstof, vand, hospitaler og mere — uanset hvor du er i Europa",
    "service.close": "Luk",
    "service.clearSearch": "Ryd",
    "service.search": "Søg",
    "service.positionSharing": "Positionsdeling",
    "service.positionSharingDesc": "Del din GPS-position eller aktiver SOS",
    "service.connectionError": "Tjek din forbindelse og prøv igen",
    "service.tryAgain": "Prøv igen",
    "service.noResultsHint": "Prøv at søge efter noget andet eller skift kategori",
    "service.resultsNearYou": "nær dig",
    "budget.noPeriod": "Ingen periode",
    "budget.exceededBy": "Overskredet med",
    "budget.expenseDeleted": "Udgift slettet",
    "budget.undo": "Fortryd",
    "budget.receipt": "Kvittering",
    "budget.trip": "Tur",
    "whatsNew.title": "Hvad er nyt",
    "whatsNew.subtitle": "Seneste opdateringer og funktioner",
    "whatsNew.seoTitle": "Hvad er nyt – Vantastika",
    "whatsNew.seoDesc": "Se de seneste opdateringer og nyheder i Vantastika.",
    "whatsNew.all": "Alle",
    "whatsNew.noUpdates": "Ingen opdateringer lige nu",
    "whatsNew.type.new_feature": "Ny funktion",
    "whatsNew.type.improvement": "Forbedring",
    "whatsNew.type.bugfix": "Fejlrettelse",
    "whatsNew.type.important": "Vigtigt",
    "whatsNew.type.maintenance": "Vedligeholdelse",
    "whatsNew.readMore": "Læs mere",
    "search.benefit1": "Se alle campingpladser og pladser",
    "search.benefit2": "Filtrer efter faciliteter og pris",
    "search.benefit3": "Læs anmeldelser og se vurderinger",
    "search.smartPlaceholder": "Søg steder, campingpladser, stellepladser, byer...",
    "search.recommendedFor": "Anbefalet til",
    "search.typeCategory": "Kategori",
    "search.typeRegion": "Område",
    "search.typeCountry": "Land",
    "search.typePlace": "Sted",
    "search.noResultsHint": "Prøv et andet søgeord eller fjern nogle filtre",
    "search.browseByCountry": "Gennemse efter land",
    "travelGuides.badge": "Rejseguider",
    "travelGuides.heroTitle1": "Opdag Europas bedste ",
    "travelGuides.heroTitle2": "rejseruter",
    "travelGuides.heroSubtitle": "Detaljerede guides med historie, højdepunkter og praktisk info. Forvandl inspiration til en rigtig rejse med ét klik.",
    "travelGuides.popular": "Populære rejsemål",
    "travelGuides.moreCountries": "Flere lande",
    "travelGuides.guidesCount": "{n} guider",
    "travelGuides.guidesSingular": "1 guide",
    "travelGuides.explore": "Udforsk",
    "countryGuide.titlePrefix": "Rejseguide",
    "countryGuide.allGuides": "Alle rejseguider",
    "countryGuide.seoDesc": "Komplet rejseguide for autocamper i",
    "countryGuide.overview": "Overblik",
    "countryGuide.campingRules": "Camping & Overnatning",
    "countryGuide.campingRegulations": "Campingregler",
    "countryGuide.wildCamping": "Vild camping",
    "countryGuide.campingTips": "Campingtips",
    "countryGuide.bestSeason": "Bedste sæson",
    "countryGuide.drivingInfo": "Kørsel & Veje",
    "countryGuide.trafficRules": "Trafikregler",
    "countryGuide.speedLimits": "Hastighedsgrænser",
    "countryGuide.tollsVignettes": "Vejafgifter & Vignetter",
    "countryGuide.currency": "Valuta",
    "countryGuide.fuel": "Brændstof",
    "countryGuide.emergency": "Nødnummer",
    "countryGuide.language": "Sprog",
    "countryGuide.bestPlaces": "Bedste steder at besøge",
    "countryGuide.safetyTips": "Sikkerhedstips",
    "countryGuide.practicalTips": "Praktiske tips",
    "countryGuide.routeGuides": "Ruteguider",
    "countryGuide.guide": "Guide",
    "countryGuide.readGuide": "Læs guide",
    "countryGuide.searchCampings": "Søg campingpladser",
    "countryGuide.showOnMap": "Vis på kort",
    "countryGuide.planTrip": "Planlæg rejse",
    "countryGuide.planTripHere": "Planlæg rejse hertil",
    "countryGuide.campings": "Campingpladser",
    "guideDetail.days": "dage",
    "guideDetail.history": "Historie & baggrund",
    "guideDetail.routeInfo": "Ruteinformation",
    "guideDetail.startPoint": "Startpunkt",
    "guideDetail.endPoint": "Slutpunkt",
    "guideDetail.distance": "Afstand",
    "guideDetail.direction": "Retning",
    "guideDetail.passesThrough": "Passerer igennem",
    "guideDetail.overview": "Overblik",
    "guideDetail.highlights": "Højdepunkter",
    "guideDetail.practical": "Praktisk",
    "guideDetail.bestSeason": "Bedste sæson",
    "guideDetail.vehicleType": "Køretøjstype",
    "guideDetail.motorhome": "Autocamper",
    "guideDetail.car": "Bil",
    "guideDetail.motorcycle": "Motorcykel",
    "guideDetail.campervan": "Campervan",
    "guideDetail.warnings": "Ting at tænke over",
    "guideDetail.practicalTips": "Praktiske tips",
    "guideDetail.whyVisit": "Hvorfor besøge: ",
    "guideDetail.readyToGo": "Klar til at tage af sted?",
    "guideDetail.readyToGoDesc": "Forvandl denne guide til en rigtig rejse i rejseplanlæggeren. Tilpas stop, antal dage og returrejse efter dine ønsker.",
    "guideDetail.planThisTrip": "Planlæg denne rejse",
    "guideDetail.refreshGuide": "Opdater guide",
    "guideDetail.generating": "Opretter detaljeret rejseguide med AI...",
    "guideDetail.loading": "Indlæser rejseguide...",
    "guideDetail.premiumBenefit1": "Læs hele rejseguiden med stop og tips",
    "guideDetail.premiumBenefit2": "Se detaljeret ruteinformation",
    "guideDetail.premiumBenefit3": "Forvandl guiden til en rigtig rejse",
    "destination.motorhome": "Autocamper i",
    "destination.planTrip": "Planlæg din autocamperrejse til",
    "destination.planNow": "Planlæg din rejse nu",
    "destination.viewGuides": "Se rejseguider",
    "destination.whatWeOffer": "Hvad Vantastika tilbyder",
    "destination.featureRoutes": "Ruteplanlægning",
    "destination.featureRoutesDesc": "Planlæg din rute med stop, afstande og køretider.",
    "destination.featureCamping": "Campingpladser",
    "destination.featureCampingDesc": "Find de bedste campingpladser og autocamperpladser.",
    "destination.featureRules": "Kørselsregler",
    "destination.featureRulesDesc": "Hastighedsgrænser, vejafgifter og lokale regler.",
    "destination.featureSafety": "Sikkerhed & SOS",
    "destination.featureSafetyDesc": "Nødnumre, vejhjælp og sikkerhedstips.",
    "destination.popularRoutes": "Populære ruter i",
    "destination.readyToGo": "Klar til turen?",
    "destination.ctaDesc": "Opret en gratis konto og begynd at planlægge din roadtrip i dag.",
    "destination.getStarted": "Kom i gang gratis",
    "destination.findCampings": "Find campingpladser",
    "share.shareOnInstagram": "Del på Instagram",
    "share.shareOnFacebook": "Del på Facebook",
    "share.linkCopied": "Link kopieret!",
    "share.linkCopiedDesc": "Indsæt hvor du vil.",
    "ig.headline": "Planlæg din rejse smartere med Vantastika",
    "ig.subheadline": "Rute, budget og rejseplanlægning ét sted til roadtrips, autocamper og bilferie.",
    "ig.ctaStart": "Kom i gang gratis",
    "ig.ctaOpen": "Åbn Vantastika",
    "ig.ctaDemo": "Se hvordan det virker",
    "ig.demoTitle": "Så nemt planlægger du din rejse",
    "ig.benefit1.title": "Planlæg hele ruten",
    "ig.benefit1.desc": "Opret din rute med stop, afstande og tider.",
    "ig.benefit2.title": "Hold styr på rejsebudgettet",
    "ig.benefit2.desc": "Se hvad rejsen koster — før og undervejs.",
    "ig.benefit3.title": "Bedre overblik",
    "ig.benefit3.desc": "Alt samlet ét sted, før og under rejsen.",
    "ig.free": "Gratis. Intet kreditkort påkrævet.",
    "upload.validating": "Validerer...",
    "upload.processing": "Behandler billede...",
    "upload.converting": "Konverterer HEIC...",
    "upload.resizing": "Ændrer størrelse...",
    "upload.compressing": "Komprimerer...",
    "upload.thumbnailing": "Opretter miniature...",
    "upload.almostDone": "Næsten færdig...",
    "upload.uploading": "Uploader...",
    "upload.done": "Færdig!",
    "upload.failed": "Upload mislykkedes",
    "upload.fileTooLarge": "Filen er for stor",
    "upload.imageRejected": "Billede afvist",
  },
  fi: {
    "offline.message": "Olet offline — tallennetut tiedot näytetään välimuistista",
    "offline.syncing": "Synkronoidaan tietoja...",
    "offline.synced": "Kaikki synkronoitu!",
    "confirm.deleteTitle": "Oletko varma?",
    "confirm.deleteDesc": "Tätä toimintoa ei voi kumota.",
    "confirm.cancel": "Peruuta",
    "confirm.delete": "Poista",
    "confirm.deleteTrip": "Poista tämä matka?",
    "confirm.deleteTripDesc": "Matka ja kaikki sen tiedot poistetaan pysyvästi.",
    "confirm.deletePost": "Poista tämä viesti?",
    "confirm.deletePostDesc": "Viesti poistetaan pysyvästi.",
    "confirm.deleteBudget": "Poista tämä budjetti?",
    "confirm.deleteBudgetDesc": "Budjetti ja kaikki kulut poistetaan pysyvästi.",
    "confirm.deleteNotification": "Poista tämä ilmoitus?",
    "confirm.deleteNotificationDesc": "Ilmoitus poistetaan pysyvästi.",
    "confirm.deleteMaterial": "Poista tämä materiaali?",
    "confirm.deleteMaterialDesc": "Materiaali poistetaan pysyvästi projektista.",
    "confirm.deleteTask": "Poista tämä tehtävä?",
    "confirm.deleteTaskDesc": "Tehtävä poistetaan pysyvästi projektista.",
    "confirm.deleteFile": "Poista tämä tiedosto?",
    "confirm.deleteFileDesc": "Tiedosto poistetaan pysyvästi.",
    "confirm.deleteMessage": "Poista tämä viesti?",
    "confirm.deleteMessageDesc": "Viesti poistetaan pysyvästi.",
    "confirm.deleteUpdate": "Poista tämä päivitys?",
    "confirm.deleteUpdateDesc": "Päivitys poistetaan pysyvästi.",
    "confirm.deleteCamping": "Poista tämä leirintäalue pysyvästi?",
    "confirm.deleteCampingDesc": "Leirintäalue poistetaan pysyvästi.",
    "confirm.unpublishTrip": "Poista tämä matka julkaisusta?",
    "confirm.unpublishTripDesc": "Matka ei ole enää näkyvissä muille.",
    "confirm.endTrip": "Lopeta tämä matka?",
    "confirm.endTripDesc": "Matka merkitään päättyneeksi.",
    "nav.explore": "Tutustu leirintäalueisiin",
    "nav.map": "Tutki karttaa",
    "nav.trips": "Suunnittele matka",
    "nav.favorites": "Tallennetut paikat",
    "nav.login": "Kirjaudu sisään",
    "nav.signup": "Aloita ilmaiseksi",
    "nav.logout": "Kirjaudu ulos",
    "nav.bottom.trips": "Uusi matka",
    "nav.bottom.explore": "Tutustu",
    "nav.bottom.map": "Kartta",
    "nav.bottom.saved": "Tallennetut",
    "nav.bottom.profile": "Profiili",
    "nav.bottom.login": "Kirjaudu sisään",
    "nav.bottom.signup": "Rekisteröidy",
    "nav.bottom.home": "Koti",
    "nav.bottom.newTrip": "Uusi matka",
    "nav.bottom.myTrips": "Omat matkani",
    "nav.checklists": "Tarkistuslistat",
    "nav.travelGuides": "Matkaoppaat",
    "nav.menuMine": "Oma",
    "nav.menuCreate": "Luo",
    "nav.settings": "Asetukset",
    "footer.integrationTerms": "Integrointiehdot",
    "dash.greeting.morning": "Hyvää huomenta",
    "dash.greeting.day": "Hei",
    "dash.greeting.afternoon": "Hyvää iltapäivää",
    "dash.greeting.evening": "Hyvää iltaa",
    "dash.greeting.night": "Hyvää yötä",
    "dash.subtitle.active": "Matkasi on käynnissä – tässä tilanteesi",
    "dash.subtitle.planning": "Sinulla on matkoja suunniteltavana",
    "dash.subtitle.completed": "Hienoa! Tässä matkasi yhteenveto",
    "dash.subtitle.empty": "Mitä haluaisit tehdä tänään?",
    "dash.paused": "Tauolla",
    "dash.ongoing": "Matka käynnissä",
    "dash.total": "Yhteensä",
    "dash.stops": "Pysähdyspaikat",
    "dash.done": "Valmis",
    "dash.nextDest": "Seuraava kohde",
    "dash.continueTrip": "Jatka matkaa",
    "dash.openActive": "Avaa aktiivinen matka",
    "dash.journal": "Päiväkirja",
    "dash.budget": "Budjetti",
    "dash.findPlace": "Etsi paikka",
    "dash.upcoming": "Tuleva matka",
    "dash.today": "Tänään!",
    "dash.tomorrow": "Huomenna",
    "dash.daysLeft": "{n} päivää jäljellä",
    "dash.continuePlan": "Jatka suunnittelua",
    "dash.start": "Aloita",
    "dash.morePlanned": "Lisää suunniteltuja matkoja",
    "dash.findCamping": "Etsi leirintäalueita",
    "dash.findCampingSub": "2 800+ paikkaa",
    "dash.guides": "Matkaoppaat",
    "dash.guidesSub": "21+ maata",
    "dash.checklists": "Tarkistuslistat",
    "dash.checklistsSub": "Pakkaa viisaasti",
    "dash.wishlist": "Toivelista",
    "dash.vehicle": "Ajoneuvo",
    "dash.projects": "Projektit",
    "dash.community": "Yhteisö",
    "dash.popularDest": "Suositut kohteet",
    "dash.seeAll": "Näytä kaikki",
    "dash.planNew": "Suunnittele uusi matka",
    "dash.planNewSub": "Vaihe vaiheelta",
    "dash.season.spring": "Kevätseikkailut",
    "dash.season.summer": "Kesä tiellä",
    "dash.season.autumn": "Syksyn roadtripit",
    "dash.season.winter": "Talviseikkailut",
    "dash.tripDone": "Matka valmis",
    "dash.visited": "Vieraillut",
    "dash.seeSummary": "Katso yhteenveto",
    "dash.planNewOld": "Suunnittele uusi matka",
    "dash.emptyTitle": "Aika suunnitella ensimmäinen matkasi!",
    "dash.emptyDesc": "Luo matka ja lisää pysähdyspaikat, budjetti ja tarkistuslistat.",
    "dash.createTrip": "Luo matka",
    "dash.stopsCount": "{count} pysähdyspaikkaa",
    "dash.visitedCount": "{count} vierailtu",
    "dash.onboarding.step1": "Suunnittele matka",
    "dash.onboarding.step1Desc": "Luo ensimmäinen matkasi pysähdyspaikkojen ja päivämäärien kanssa",
    "dash.onboarding.step2": "Tallenna unelmakohteet",
    "dash.onboarding.step2Desc": "Luo toivelista paikoista, joissa haluat vierailla",
    "dash.onboarding.step3": "Tutustu leirintäalueisiin",
    "dash.onboarding.step3Desc": "Selaa yli 2 800 leirintäaluetta Euroopassa",
    "dash.onboarding.stepVehicle": "Määritä ajoneuvosi",
    "dash.onboarding.stepVehicleDesc": "Lisää matkailuautosi, autosi tai retkeilyautosi",
    "dash.onboarding.stepProject": "Aloita projekti",
    "dash.onboarding.stepProjectDesc": "Seuraa kunnostuksia, päivityksiä ja rakennusprojekteja",
    "dash.emptyDescReturning": "Suunnittele ensimmäinen matkasi – tallennetut tietosi ovat valmiina!",
    "dash.recentProjects": "Projektisi",
    "dash.popularGuides": "Suositut oppaat",
    "dash.noProjects": "Ei projekteja vielä",
    "dash.draft": "Luonnos",
    "dash.continueDraft": "Jatka suunnittelua",
    "dash.untitled": "Nimetön matka",
    "dash.nextStep": "Seuraava vaihe",
    "dash.nextStepStops": "Lisää ensimmäinen pysähdyspaikka",
    "dash.nextStepDate": "Valitse matkustuspäivä",
    "dash.nextStepChecklist": "Täytä tarkistuslista",
    "dash.prepTitle": "Valmistaudu matkaasi",
    "dash.prepBudget": "Aseta budjetti pysyäksesi aikataulussa",
    "dash.prepStops": "Lisää pysähdyspaikkoja reitillesi",
    "dash.prepChecklist": "Tarkista pakkauslistasi",
    "dash.packed": "pakattu",
    "dash.incompleteTitle": "Viimeistele suunnittelu",
    "dash.incompleteDate": "Lisää lähtöpäivä",
    "dash.incompleteStops": "Lisää pysähdyspaikkoja reitillesi",
    "dash.postTripTitle": "Mitä seuraavaksi?",
    "dash.postTripShare": "Jaa matkasi ystäviesi kanssa",
    "dash.postTripReview": "Arvioi vierailemasi paikat",
    "dash.postTripJournal": "Kirjoita kokemuksistasi",
    "dash.postTripMsg": "Matkustit {km} km ja vierailit {stops} paikassa – mahtava seikkailu!",
    "nav.myTrips": "Omat matkani",
    "nav.submit": "Lisää paikka",
    "nav.help": "Ohjeet sovelluksen käyttöön",
    "nav.compare": "Vertaa leirintäalueita",
    "nav.guides": "Maakohtaiset oppaat",
    "nav.contact": "Ota yhteyttä",
    "help.title": "Ohjeet sovelluksen käyttöön",
    "search.startTyping": "Aloita kirjoittaminen hakeaksesi",
    "search.startTypingDesc": "Hae paikan, kaupungin, kategorian tai matkakohteen tyypin perusteella",
    "search.clickForDetails": "Katso tiedot napsauttamalla korttia",
    "search.searchHint": "Hae nimen, kategorian, kaupungin tai alueen perusteella. Älykäs haku löytää oikean paikan sinulle.",
    "search.foundOnGoogle": "lisäehdotuksia Googlesta",
    "search.addHint": "Napauta korttia lisätäksesi Vantastikaan",
    "search.fromGoogle": "Googlesta",
    "search.addToVantastika": "Lisää Vantastikaan",
    "search.googleAdding": "Lisätään...",
    "search.googleAddedShort": "Lähetetty tarkistettavaksi",
    "search.googleAdded": "Paikka lähetetty",
    "search.googleAddedDesc": "Kiitos! Se näkyy ylläpitäjän tarkistuksen jälkeen.",
    "search.googleAlreadyExists": "Jo Vantastikassa",
    "search.googleAlreadyExistsDesc": "Viemme sinut olemassa olevalle sivulle.",
    "search.googleAddError": "Paikkaa ei voitu lisätä. Yritä uudelleen.",
    "search.nearbyFound": "leirintäaluetta lähelläsi",
    "search.within25km": "25 km sisällä",
    "search.noNearby": "Ei leirintäalueita lähistöllä",
    "search.noNearbyDesc": "Kokeile karttaa tutkiaksesi kauempaa.",
    "search.openMap": "Avaa kartta",
    "search.findingLocation": "Etsitään sijaintiasi...",
    "search.findingLocationDesc": "Käytämme sitä lähellä olevien leirintäalueiden näyttämiseen.",
    "search.locationDeniedHint": "Sijaintipääsy on evätty. Salli se nähdäksesi leirintäalueita lähelläsi.",
    "search.enableLocationHint": "Salli sijaintipääsy nähdäksesi lähimmät leirintäalueet.",
    "search.allowLocation": "Salli sijainti",
    "search.topRated": "Parhaiten arvostetut leirintäalueet",
    "help.whatIs.title": "Mikä on Vantastika?",
    "help.whatIs.body": "Matkakumppanisi matkailuauto-, retkeilyauto- ja automatkoille ympäri Eurooppaa. Löydä leirintäalueita, suunnittele reittejä ja seuraa budjettiasi – kaikki yhdessä paikassa.",
    "help.startTrip.title": "Aloita matka",
    "help.startTrip.body": "Napauta Matkasuunnittelijaa, anna matkallesi nimi, lisää pysähdyspaikat ja päivämäärät. Sovellus laskee etäisyydet ja polttoainekustannukset puolestasi.",
    "help.search.title": "Hae paikkoja",
    "help.search.body": "Käytä Tutki-ominaisuutta tai hakusivua löytääksesi leirintäalueita, pysäköintipaikkoja ja huoltoasemia lähellä mitä tahansa paikkaa Euroopassa.",
    "help.map.title": "Käytä karttaa",
    "help.map.body": "Avaa Tutki nähdäksesi kartan. Hae kaupunkia tai käytä nykyistä sijaintiasi. Napauta merkkejä nähdäksesi tiedot ja reittiohjeet.",
    "help.addStop.title": "Lisää pysähdyspaikka matkaasi",
    "help.addStop.body": "Kun tarkastelet leirintäaluetta, napauta 'Lisää matkaan' sisällyttääksesi sen reitillesi. Voit järjestellä ja muokata pysähdyspaikkoja milloin tahansa.",
    "help.share.title": "Jaa & inspiroidu",
    "help.share.body": "Tee matkastasi julkinen, jotta muutkin voivat nähdä reittisi. Selaa yhteisön jakamia matkoja saadaksesi ideoita.",
    "help.favorites.title": "Tallenna suosikit",
    "help.favorites.body": "Napauta minkä tahansa leirintäalueen sydänkuvaketta tallentaaksesi sen. Järjestä suosikit kokoelmiin helppoa käyttöä varten myöhemmin.",
    "hero.title1": "Suunnittele koko matkasi",
    "hero.title2": "yhdessä paikassa.",
    "hero.subtitle": "Suunnittele matkasi, lisää pysähdyspaikat, löydä paikkoja kartalta ja seuraa budjettiasi – ilman siirtymistä sovellusten välillä.",
    "hero.searchPlaceholder": "Minne haluat mennä?",
    "hero.search": "Hae",
    "hero.ctaPlan": "Suunnittele matkani – ilmaiseksi",
    "hero.ctaExplore": "Etsi leirintäalueita",
    "hero.ctaSubmit": "Tiedätkö hyvän paikan?",
    "hero.valueProps": "Kaikki yhdessä paikassa – ei enää siirtymistä sovellusten välillä",
    "hero.diffTitle": "Miksi Vantastika?",
    "hero.diff1": "Matkasuunnittelu, pysähdyspaikat ja budjetti yhdessä prosessissa",
    "hero.diff2": "Skannaa kuitit ja seuraa kulut automaattisesti",
    "hero.diff3": "Lisää pysähdyspaikkoja suoraan kartalta",
    "hero.diff4": "Matkailuautoille, autoille ja road tripeille",
    "features.find": "Löydä paikkoja kartalta",
    "features.findDesc": "Leirintäalueet, nähtävyydet, ravintolat ja muut pysähdyspaikat – suoraan suunnitelmaasi",
    "features.save": "Tallenna suosikit",
    "features.saveDesc": "Pidä kirjaa vierailun arvoisista paikoistasi",
    "features.plan": "Suunnittele matkasi",
    "features.planDesc": "Luo reitti, lisää pysähdyspaikat ja rakenna matkasi askel askeleelta",
    "features.budget": "Seuraa budjettiasi",
    "features.budgetDesc": "Kirjaa kulut, skannaa kuitit ja näe, mihin rahat menevät",
    "features.europe": "Kaikki Euroopan alueelta",
    "features.europeDesc": "Tuhansia vahvistettuja paikkoja ympäri Eurooppaa",
    "featured.title": "Suositut leirintäalueet",
    "featured.viewAll": "Katso kaikki →",
    "search.title": "Tutustu leirintäalueisiin",
    "search.placeholder": "Etsi paikkoja, matkoja, käyttäjiä...",
    "search.country": "Maa",
    "search.allCountries": "Kaikki maat",
    "search.filters": "Suodattimet",
    "search.clearAll": "Tyhjennä kaikki",
    "search.minPrice": "Minimihinta",
    "search.maxPrice": "Maksimihinta",
    "search.region": "Alue",
    "search.regionPlaceholder": "esim. Dalarna",
    "search.noResults": "Ei tuloksia haulle: {query}",
    "search.adjustFilters": "Kokeile säätää suodattimia",
    "search.found": "leirintäaluetta löytyi",
    "filter.electricity": "Sähköliitäntä",
    "filter.freshWater": "Makea vesi",
    "filter.greyWater": "Harmaan veden poisto",
    "filter.toilet": "WC-kasettien tyhjennys",
    "filter.shower": "Suihku",
    "filter.wc": "WC",
    "filter.petFriendly": "Lemmikkiystävällinen",
    "filter.openAllYear": "Avoinna ympäri vuoden",
    "filter.familyFriendly": "Perheystävällinen",
    "filter.largeMotorhomes": "Suuret matkailuautot",
    "filter.nearWater": "Lähellä järveä/rantaa",
    "filter.spaWellness": "Kylpylä/wellness lähellä",
    "filter.wifi": "WiFi",
    "filter.laundry": "Pyykinpesu",
    "filter.restaurant": "Ravintola",
    "detail.about": "Tietoja",
    "detail.amenities": "Palvelut",
    "detail.contact": "Yhteystiedot",
    "detail.website": "Verkkosivusto",
    "detail.checkIn": "Sisäänkirjautuminen",
    "detail.checkOut": "Uloskirjautuminen",
    "detail.location": "Sijainti",
    "detail.openInMaps": "Avaa Google Mapsissa →",
    "detail.mapComingSoon": "Kartta tulossa pian",
    "detail.back": "Takaisin",
    "detail.save": "Tallenna",
    "detail.saved": "Tallennettu",
    "detail.perNight": "per yö",
    "detail.reviews": "Arvostelut",
    "detail.writeReview": "Kirjoita arvostelu",
    "detail.noReviews": "Ei vielä arvosteluja",
    "detail.submitReview": "Lähetä arvostelu",
    "detail.yourRating": "Arvostelusi",
    "detail.yourComment": "Kommenttisi (valinnainen)",
    "detail.loginToReview": "Kirjaudu sisään kirjoittaaksesi arvostelun",
    "detail.notFound": "Leirintäaluetta ei löydy",
    "detail.backToSearch": "Takaisin hakuun",
    "favorites.title": "Omat suosikit",
    "favorites.saveYour": "Tallenna suosikkileirintäalueesi",
    "favorites.signUpPrompt": "Rekisteröidy tai kirjaudu sisään tallentaaksesi leirintäalueita suosikkeihisi.",
    "favorites.empty": "Ei vielä suosikkeja",
    "favorites.emptyDesc": "Aloita tutkiminen ja tallenna leirintäalueita, joista pidät",
    "favorites.explore": "Tutustu leirintäalueisiin",
    "favorites.emptyCollection": "Ei suosikkeja tässä kokoelmassa",
    "wishlist.title": "Toivelista",
    "wishlist.save": "Tallenna toivelistalle",
    "wishlist.saved": "Tallennettu toivelistalle!",
    "wishlist.alreadySaved": "Jo toivelistalla",
    // SaveGateSheet
    "saveGate.wishlist.savedFor": "Tallensimme paikan",
    "saveGate.trip.savedFor": "Tallensimme matkan",
    "saveGate.place.savedFor": "Tallensimme",
    "saveGate.holdsFor24h": "Säilytämme sen 24 tuntia — luo tili, ettei se katoa.",
    "saveGate.wishlist.benefit1": "Synkronoi puhelimen ja tietokoneen välillä",
    "saveGate.wishlist.benefit2": "Saa ilmoituksen, kun hinta laskee",
    "saveGate.wishlist.benefit3": "Jaa matkakumppanin kanssa",
    "saveGate.trip.benefit1": "Aktivoi offline-tila tielle",
    "saveGate.trip.benefit2": "Älykäs ajoneuvoon mukautuva navigointi",
    "saveGate.trip.benefit3": "Jaa sijainti reaaliajassa perheen kanssa",
    "saveGate.place.benefit1": "Tallenna paikkasi pysyvästi",
    "saveGate.place.benefit2": "Saa vinkkejä lähellä olevista paikoista",
    "saveGate.place.benefit3": "Käytä missä tahansa laitteella",
    "saveGate.socialProof": "matkailijaa Pohjoismaissa käyttää Vantastikaa",
    "saveGate.createAccount15s": "Luo tili 15 sekunnissa",
    "saveGate.alreadyMember": "Jo jäsen?",
    "saveGate.logIn": "Kirjaudu sisään",
    "saveGate.continueGuest": "Jatka vieraana — säilyy 24 t",
    "saveGate.noCardNeeded": "Ilmaista. Ei korttia tarvita.",
    // GuestRescueSheet
    "guestRescue.title": "Sinulla on {count} tallennusta vaarassa",
    "guestRescue.expiresIn": "Vanhenee {time}",
    "guestRescue.expired": "Vanhentunut — luo tili palauttaaksesi",
    "guestRescue.trip": "Matka",
    "guestRescue.wishlist": "Toivelista",
    "guestRescue.rescueCta": "Pelasta tallennukseni — luo tili",
    "guestRescue.footnote": "Ilmaista ikuisesti. Ei pankkikorttia.",
    "saveGate.hoursShort": "t",
    "saveGate.minutesShort": "min",
    "guestRescue.toastTitle": "Vinkki: Luo tili",
    "guestRescue.toastBody": "Tallenna kaikilla laitteilla ja älä menetä matkojasi.",
    "guestRescue.toastCta": "Tee se nyt",
    "nav.bottom.guestSaves": "{count} tallennettu",
    "wishlist.loginMsg": "Kirjaudu sisään tallentaaksesi paikkoja.",
    "wishlist.empty": "Toivelistasi on tyhjä",
    "wishlist.emptyDesc": "Tallenna unelmapaikkoja ja suunnittele matkoja niiden pohjalta myöhemmin.",
    "wishlist.add": "Lisää paikka",
    "wishlist.searchPlace": "Etsi tallennettava paikka",
    "wishlist.searchPlaceholder": "Kaupunki, maa, nähtävyys, leirintäalue...",
    "wishlist.myNote": "Oma muistiinpano",
    "wishlist.notePlaceholder": "Kirjoita muistiinpano tästä paikasta...",
    "wishlist.addNote": "Napauta lisätäksesi muistiinpano...",
    "wishlist.saveNote": "Tallenna",
    "wishlist.nearby": "Lähellä",
    "wishlist.nearbyAttractions": "Nähtävyydet",
    "wishlist.nearbyAccommodation": "Majoitus",
    "wishlist.nearbyRestaurants": "Ravintolat",
    "wishlist.noNearby": "Lähellä ei löydy paikkoja tietokannastamme vielä.",
    "wishlist.planTrip": "Suunnittele matka tänne",
    "wishlist.planMultiple": "Suunnittele matka",
    "wishlist.deleteTitle": "Poista toivelistalta?",
    "wishlist.deleteDesc": "Paikka poistetaan toivelistaltasi.",
    "wishlist.selectedCount": "valittu",
    "wishlist.pickFromWishlist": "Valitse toivelistalta",
    "wishlist.loading": "Ladataan...",
    "wishlist.emptyPicker": "Toivelista on tyhjä. Tallenna paikkoja ensin!",
    "wishlist.addSelected": "Lisää valitut",
    "wishlist.fromWishlist": "Toivelistalta",
    "wishlist.planWithPlaces": "Suunnittele matka valituista paikoista",
    "wishlist.planWithPlacesDesc": "Valitse järjestys ja anna jokaiselle paikalle rooli.",
    "wishlist.orderMode": "Reittijärjestys",
    "wishlist.myOrder": "Pidä järjestykseni",
    "wishlist.myOrderDesc": "Käytä täsmälleen valitsemaasi järjestystä",
    "wishlist.smartOrder": "Optimoi reitti",
    "wishlist.smartOrderDesc": "Löydä älykkäin järjestys vähemmällä kiertotiellä",
    "wishlist.assignRoles": "Mikä rooli jokaisella paikalla on?",
    "wishlist.role_destination": "Pääkohde",
    "wishlist.role_stop": "Pysähdys",
    "wishlist.role_attraction": "Nähtävyys",
    "wishlist.role_overnight": "Yöpyminen",
    "wishlist.startPlanning": "Aloita suunnittelu",
    "wishlist.places": "paikkaa",
    "notifications.emptyTitle": "Ei ilmoituksia vielä",
    "notifications.emptyDesc": "Kun jotain tapahtuu, näet sen täällä",
    "notifications.title": "Ilmoitukset",
    "notifications.unreadCount": "lukematon",
    "notifications.noUnread": "Ei lukemattomia ilmoituksia",
    "notifications.markAll": "Merkitse kaikki",
    "notifications.markRead": "Merkitse luetuksi",
    "notifications.show": "Näytä",
    "notifications.delete": "Poista",
    "notifications.goToInbox": "Siirry postilaatikkoon ja viesteihin",
    "notifications.allRead": "Kaikki merkitty luetuksi",
    "trips.title": "Omat matkat",
    "trips.plan": "Suunnittele matkasi",
    "trips.loginPrompt": "Kirjaudu sisään luodaksesi ja hallitaksesi leirintämatkojasi.",
    "trips.newTrip": "Uusi matka",
    "trips.createTrip": "Luo uusi matka",
    "trips.tripTitle": "Matkan nimi",
    "trips.startDate": "Alkamispäivä",
    "trips.endDate": "Päättymispäivä",
    "trips.notes": "Muistiinpanoja matkalle...",
    "trips.create": "Luo matka",
    "trips.empty": "Ei matkoja vielä",
    "trips.emptyDesc": "Luo ensimmäinen matkasi ja ala lisätä leirintäpysähdyksiä",
    "trips.stops": "Pysähdykset",
    "trips.noStops": "Ei vielä lisättyjä pysähdyksiä. Tallenna leirintäalueita suosikkeihisi ja lisää ne sitten tähän.",
    "trips.created": "Matka luotu!",
    "trips.failed": "Matkan luonti epäonnistui",
    "trips.partialSave": "Matka tallennettiin osittain",
    "trips.partialDesc": "Kaikkia osia ei voitu tallentaa. Siirry Omat matkat -osioon muokataksesi.",
    "trips.unknownPlace": "Tuntematon paikka",
    "trips.ideaLoaded": "Matkaidea on ladattu suunnittelijan alle.",
    "trips.newInspiration": "Uusi inspiraatio",
    "trips.requireAuthSave": "Olet käyttänyt aikaa tämän matkan suunnitteluun. Luo tili, niin kaikki tallennetaan automaattisesti.",
    "trips.daysAbbrev": "päivää",
    "trips.perDay": "/päivä",
    "trips.socialProof": "matkailijaa suunnittelee täällä",
    "trips.freeLabel": "100% ilmainen",
    "trips.ideaCreating": "Luodaan matkaideaasi...",
    "trips.ideaBuilding": "Rakennamme kattavan matkasuunnitelman kohteeseen {destination} pysähdyksien, nähtävyyksien ja leirintäalueiden kanssa.",
    "trips.ideaTitle": "Matkaidea: {title}",
    "trips.campingsNearby": "Leirintäalueet ja paikat lähistöllä",
    "trips.restoreWelcome": "Tervetuloa takaisin!",
    "trips.restoreSaving": "Tallennamme matkasi \"{title}\" automaattisesti...",
    "trips.restoreFailed": "Matkaa ei voitu palauttaa",
    "trips.restoreFailedDesc": "Tallennetut matkatiedot olivat puutteelliset. Suunnittele uusi matka.",
    "trips.restoreErrorDesc": "Jokin meni pieleen. Suunnittele uusi matka.",
    "trips.partialStopsFailed": "Matka tallennettu, mutta jotkut pysähdykset epäonnistuivat",
    "trips.partialStopsDesc": "Siirry Omat matkat -osioon ja muokkaa matkaa lisätäksesi puuttuvat pysähdykset.",
    "contact.sendFailed": "Lähetys epäonnistui",
    "contact.tryAgain": "Yritä myöhemmin uudelleen.",
    "contact.sent": "Viesti lähetetty!",
    "contact.writeMessage": "Kirjoita viesti",
    "contact.rateLimited": "Odota hetki ennen kuin lähetät uudelleen",
    "weather.fetchError": "Säätietoja ei voitu hakea. Yritä myöhemmin uudelleen.",
    "cost.fetchFailed": "Kustannusarviota ei voitu hakea",
    "cost.adults": "aikuista",
    "cost.children": "lasta",
    "cost.person": "henkilö",
    "cost.persons": "henkilöä",
    "wizard.nextStepDesc": "Mitä haluat tehdä seuraavaksi?",
    "wizard.addBudgetCta": "Aseta matkan budjetti",
    "wizard.addMoreStopsCta": "Lisää pysähdyksiä ja leirintäalueita",
    "wizard.viewMyTrips": "Näytä omat matkat",
    "myTrips.chatUnavailable": "Matkachat ei ole käytettävissä",
    "myTrips.chatClosed": "Chat sulkeutuu, kun matka on päättynyt.",
    "myTrips.chatOnlyShared": "Chat on käytettävissä vain jaetuissa matkoissa matkaseuran kanssa.",
    "contact.subject": "Aihe",
    "contact.subjectPlaceholder": "Kuvaile lyhyesti, mistä on kyse",
    "contact.message": "Viesti",
    "contact.messagePlaceholder": "Kerro meille lisää yksityiskohtia...",
    "contact.creating": "Luodaan...",
    "contact.sendTicket": "Lähetä yhteydenotto",
    "contact.allTickets": "Kaikki yhteydenotot",
    "contact.waitingReply": "Vantastika odottaa vastaustasi",
    "contact.resolvedLabel": "Ratkaistu",
    "contact.closedNotice": "Tämä yhteydenotto on suljettu. Luo uusi yhteydenotto, jos tarvitset lisää apua.",
    "contact.you": "Sinä",
    "contact.replyPlaceholder": "Kirjoita vastauksesi...",
    "contact.sending": "Lähetetään...",
    "contact.reply": "Vastaa",
    "places.yourPosition": "Sijaintisi",
    "places.unnamedPlace": "Nimetön paikka",
    "places.myPosition": "Oma sijainti",
    "map.listView": "Listanäkymä",
    "map.mapView": "Karttanäkymä",
    "shared.joinedTrip": "Olet liittynyt matkaan!",
    "shared.joinedDesc": "Voit nyt katsella ja muokata matkaa yhdessä matkaseuran kanssa.",
    "shared.couldNotJoin": "Liittyminen epäonnistui",
    "shared.tryAgainLater": "Yritä myöhemmin uudelleen.",
    "shared.searchStartPlace": "Hae lähtöpaikkaasi...",
    "shared.savingCopy": "Tallennetaan...",
    "shared.saveAsCopy": "Tallenna omaksi kopioksi",
    "camping.blurBenefitDesc": "Katso koko kuvaus ja mukavuudet",
    "camping.blurBenefitPrice": "Näytä hinnat ja hintahistoria",
    "camping.blurBenefitReviews": "Lue muiden matkailijoiden arvostelut",
    "camping.blurBenefitMap": "Avaa kartta ja navigoi suoraan",
    "login.title": "Tervetuloa takaisin",
    "login.subtitle": "Kirjaudu sisään Vantastika-tilillesi",
    "login.email": "Sähköposti",
    "login.password": "Salasana",
    "login.submit": "Kirjaudu sisään",
    "login.loading": "Kirjaudutaan sisään...",
    "login.failed": "Kirjautuminen epäonnistui",
    "login.noAccount": "Eikö sinulla ole tiliä?",
    "login.signUp": "Rekisteröidy",
    "login.forgotPassword": "Unohtuiko salasana?",
    "login.or": "TAI",
    "login.verificationFailed": "Vahvistus epäonnistui. Yritä uudelleen.",
    "login.captchaFailed": "CAPTCHA-vahvistus epäonnistui. Yritä uudelleen.",
    "login.tooManyAttempts": "Liian monta kirjautumisyritystä. Odota hetki.",
    "login.wrongCredentials": "Väärä sähköposti tai salasana.",
    "social.continueGoogle": "Jatka Googlella",
    "social.continueApple": "Jatka Applella",
    "social.loginFailed": "Kirjautuminen epäonnistui",
    "social.unknownError": "Tuntematon virhe",
    "forgot.title": "Unohtuiko salasana",
    "forgot.checkInbox": "Tarkista postilaatikkosi!",
    "forgot.enterEmail": "Anna sähköpostiosoitteesi niin lähetämme palautuslinkin.",
    "forgot.sentTo": "Lähetimme sähköpostin osoitteeseen",
    "forgot.checkSpam": "jossa on linkki salasanan palauttamiseksi. Tarkista roskaposti, jos et löydä sitä.",
    "forgot.backToLogin": "Takaisin kirjautumiseen",
    "forgot.emailLabel": "Sähköpostiosoite",
    "forgot.emailPlaceholder": "sinun@email.fi",
    "forgot.sending": "Lähetetään...",
    "forgot.sendLink": "Lähetä palautuslinkki",
    "forgot.couldNotSend": "Palautussähköpostia ei voitu lähettää",
    "forgot.enterEmailFirst": "Anna sähköpostiosoitteesi",
    "reset.title": "Uusi salasana",
    "reset.subtitle": "Valitse uusi salasana tilillesi.",
    "reset.newPassword": "Uusi salasana",
    "reset.confirmPassword": "Vahvista salasana",
    "reset.placeholder": "Vähintään 8 merkkiä, kirjain + numero",
    "reset.tooShort": "Salasanan on oltava vähintään 8 merkkiä",
    "reset.noMatch": "Salasanat eivät täsmää",
    "reset.updating": "Päivitetään...",
    "reset.update": "Päivitä salasana",
    "reset.couldNotUpdate": "Salasanaa ei voitu päivittää",
    "reset.success": "Salasana päivitetty!",
    "reset.successDesc": "Salasanasi on vaihdettu onnistuneesti.",
    "reset.goToProfile": "Siirry profiiliini",
    "reset.invalidLink": "Virheellinen linkki",
    "reset.invalidLinkDesc": "Palautuslinkki on virheellinen tai vanhentunut. Pyydä uusi kirjautumissivulta.",
    "signup.title": "Luo tili",
    "signup.subtitle": "Tallenna leirintäalueita ja aloita matkojen suunnittelu ilmaiseksi",
    "signup.name": "Näyttönimi",
    "signup.email": "Sähköposti",
    "signup.password": "Salasana",
    "signup.gdpr": "Hyväksyn tietosuojaselosteen ja suostun henkilötietojeni käsittelyyn GDPR:n mukaisesti.",
    "signup.submit": "Rekisteröidy",
    "signup.loading": "Luodaan tiliä...",
    "signup.failed": "Rekisteröityminen epäonnistui",
    "signup.success": "Tili luotu!",
    "signup.checkEmail": "Tarkista sähköpostisi vahvistaaksesi tilisi.",
    "signup.acceptPrivacy": "Hyväksy tietosuojaseloste",
    "signup.hasAccount": "Onko sinulla jo tili?",
    "signup.logIn": "Kirjaudu sisään",
    "signup.heroTitle": "Suunnittele seuraava matkasi 60 sekunnissa",
    "signup.heroSubtitle": "Luo ilmainen tili ja käytä koko Vantastikaa – ei mainoksia, ei korttia.",
    "signup.benefit1": "Tallenna reittisi ikuisesti",
    "signup.benefit2": "Leirintäalue-ehdotukset matkan varrella",
    "signup.benefit3": "Liity yhteisöön",
    "signup.benefit4": "Aina ilmainen – ei korttia tarvita",
    "signup.titleShort": "Luo tilisi",
    "signup.subtitleShort": "Vie 10 sekuntia. Korttia ei tarvita.",
    "signup.submitShort": "Luo ilmainen tili",
    "signup.miniConsent": "Hyväksyn",
    "signup.ageMini": "Olen vähintään 16-vuotias.",
    "signup.trustLine": "🔒 Emme jaa sähköpostiasi. Peruuta milloin tahansa.",
    "signup.freeForever": "Aina ilmainen",
    "signup.confirmPasswordPlaceholder": "Toista salasanasi",
    "footer.desc": "Suunnittele matkasi, löydä oikeat paikat ja matkusta täydellisessä hallinnassa. Matkailuauto, pakettiauto tai teltta.",
    "footer.explore": "Tutustu",
    "footer.searchCampings": "Etsi leirintäalueita",
    "footer.account": "Tili",
    "footer.legal": "Lainsäädäntö",
    "footer.privacy": "Tietosuojaseloste",
    "footer.terms": "Käyttöehdot",
    "footer.rights": "Kaikki oikeudet pidätetään.",
    "footer.followInstagram": "Seuraa Vantastikaa Instagramissa",
    "footer.followFacebook": "Seuraa Vantastikaa Facebookissa",
    "footer.followUs": "Seuraa meitä",
    "geo.myLocation": "Oma sijaintini",
    "geo.nearMe": "Lähelläni",
    "geo.permissionDenied": "Sijaintiluvan evätty",
    "geo.detecting": "Paikannetaan sijaintia...",
    "trip.editTrip": "Muokkaa matkaa",
    "trip.tripTitle": "Matkan nimi",
    "trip.destination": "Määränpää (etsi osoitetta...)",
    "trip.startDate": "Aloituspäivämäärä",
    "trip.consumption": "Kulutus (l/100km)",
    "trip.consumptionEv": "Kulutus (kWh/100km)",
    "trip.fuelPrice": "Diesel-hinta",
    "trip.fuelPriceBensin": "Bensa-hinta",
    "trip.fuelPriceEv": "Sähkön hinta",
    "trip.fuelPriceUnitEv": "/kWh",
    "trip.vehicleType": "Ajoneuvotyyppi",
    "trip.vehicleDiesel": "Diesel",
    "trip.vehicleBensin": "Bensiini",
    "trip.vehicleEv": "Sähköauto",
    "trip.travelMode": "Matkustustapa",
    "trip.travelModeCar": "Auto",
    "trip.travelModeMotorhome": "Matkailuauto",
    "trip.travelModeMotorcycle": "Moottoripyörä",
    "trip.travelModeDesc": "Tekoäly mukauttaa reittejä ja vinkkejä ajoneuvosi mukaan",
    "trip.priceCurrency": "Hintavaluutta (valuutta, jossa syötät hinnan)",
    "trip.saveChanges": "Tallenna muutokset",
    "trip.calculatingRoute": "Lasketaan reittiä...",
    "trip.driveTime": "ajoaika",
    "trip.estimatedFuelCost": "Arvioidut polttoainekustannukset:",
    "trip.estimatedDiesel": "Arvioidut diesel-kustannukset:",
    "trip.estimatedChargeCost": "Arvioidut latauskustannukset:",
    "trip.openGoogleMaps": "Avaa Google Mapsissa",
    "trip.tankSize": "Tankin koko (l)",
    "trip.batterySize": "Akku (kWh)",
    "trip.rangeAnalysis": "Toimintasäteen analyysi",
    "trip.rangePerFill": "Toimintasäde per tankkaus",
    "trip.chargingStops": "Latauspysähdykset",
    "trip.fuelStops": "Tankkauspysähdykset",
    "trip.avgLeg": "Keskim. osuus",
    "trip.costPerCharge": "Kustannus per lataus",
    "trip.costPerFill": "Kustannus per tankkaus",
    "trip.evStopNote": "Sinun täytyy ladata noin {stops} kertaa, suunnilleen {km} km:n välein.",
    "trip.fuelStopNote": "Sinun täytyy tankata noin {stops} kertaa, suunnilleen {km} km:n välein.",
    "trip.noStopsNeeded": "Toimintasäteesi kattaa koko matkan pysähtymättä!",
    "trip.routeAlternatives": "Reittivaihtoehdot",
    "trip.route": "Reitti",
    "trip.addMin2Stops": "Lisää vähintään 2 pysähdystä polttoainelaskentaa varten",
    "trip.addStop": "Lisää pysähdys",
    "trip.newStop": "Uusi pysähdys",
    "trip.searchPlace": "Etsi paikkaa tai osoitetta...",
    "trip.notesOptional": "Muistiinpanot (valinnainen)",
    "trip.saving": "Tallennetaan...",
    "trip.add": "Lisää",
    "trip.cancel": "Peruuta",
    "trip.dragToReorder": "Vedä ja pudota järjestelyä varten",
    "trip.navigate": "Navigoi tähän",
    "trip.startNavigation": "Aloita navigointi",
    "trip.uploading": "Ladataan...",
    "trip.writeExperience": "Kirjoita kokemuksistasi tähän...",
    "trip.save": "Tallenna",
    "trip.tripExperience": "Matkakokemus",
    "trip.edit": "Muokkaa matkaa",
    "trip.write": "Kirjoita",
    "trip.describeTripExperience": "Kuvaile matkaasi, kokemuksiasi, vinkkejä muille...",
    "trip.tripUpdated": "Matka päivitetty!",
    "trip.linkCopied": "Linkki kopioitu!",
    "trip.shareLinkDesc": "Jaa linkki ystäviesi kanssa.",
    "trip.couldNotShare": "Jakaminen epäonnistui",
    "trip.imageUploaded": "Kuva ladattu!",
    "trip.couldNotUpload": "Kuvan lataus epäonnistui",
    "trip.experienceSaved": "Kokemus tallennettu!",
    "trip.stopAdded": "Pysähdys lisätty!",
    "trip.couldNotAddStop": "Pysähdyksen lisäys epäonnistui",
    "trip.orderUpdated": "Järjestys päivitetty!",
    "trip.fuelCalc": "Polttoainelaskelma (diesel)",
    "trip.currency": "Valuutta",
    "trip.stopType": "Pysähdyksen tyyppi",
    "trip.stopTypeVisit": "Lyhyt vierailu",
    "trip.stopTypeOvernight": "Yöpyminen",
    "trip.stopTypeDestination": "Päämäärä",
    "trip.nightsCount": "Yöt",
    "trip.editStop": "Muokkaa pysähdystä",
    "trip.optimizeRoute": "Optimoi reitti",
    "trip.outboundLeg": "Meno",
    "trip.returnLeg": "Paluu",
    "trip.returnLegDesc": "Matka takaisin kotiin",
    "trip.addReturnStop": "Lisää pysähdys paluumatkalle",
    "trip.optimizeReturn": "Optimoi paluu",
    "trip.returnOptimized": "Paluumatka optimoitu!",
    "trip.noReturnStops": "Ei vielä pysähdyksiä paluumatkalle. Lisää pysähdyksiä kotimatkaasi varten.",
    "trip.markAsReturn": "Merkitse paluumatkaksi",
    "trip.markAsOutbound": "Merkitse menomatkaksi",
    "trip.routeOptimized": "Reitti optimoitu!",
    "optimize.title": "Optimoi reittijärjestys",
    "optimize.description": "Vertaa eri reittivaihtoehtoja ja valitse matkaasi parhaiten sopiva.",
    "optimize.analyzing": "Analysoidaan reittivaihtoehtoja...",
    "optimize.currentOrder": "Nykyinen järjestys",
    "optimize.currentDesc": "Nykyinen pysähdysjärjestyksesi suunnitelman mukaan.",
    "optimize.geographic": "Loogisin reitti",
    "optimize.geographicDesc": "Pysähdykset lajiteltu luonnollisen maantieteellisen virtauksen mukaan kohti päämäärää.",
    "optimize.shortest": "Lyhin reitti",
    "optimize.shortestDesc": "Lähimmän naapurin lajittelu kokonaisajoajan minimoimiseksi.",
    "optimize.dayOptimized": "Päiväretki optimoitu",
    "optimize.dayOptimizedDesc": "Järjestetty yöpymisten ympärille loogisia päiväetappeja varten.",
    "optimize.recommended": "Suositeltu",
    "optimize.stopOrder": "Pysähdysjärjestys",
    "optimize.start": "Alku",
    "optimize.end": "Määränpää",
    "optimize.overnights": "yöpymiset",
    "optimize.shorter": "lyhyempi",
    "optimize.longer": "pidempi",
    "optimize.saves": "säästää",
    "optimize.useThisRoute": "Käytä tätä reittiä",
    "optimize.cancel": "Peruuta",
    "tip.notFound": "Matkaa ei löytynyt",
    "tip.notFoundDesc": "Se ei ehkä ole enää saatavilla.",
    "tip.back": "Takaisin",
    "tip.traveler": "Matkailija",
    "tip.sharedTrip": "Jaettu matka",
    "tip.planThisTrip": "Suunnittele tämä matka",
    "tip.aboutTrip": "Tietoja tästä matkasta",
    "tip.aiGenerated": "Tekoälyn luoma matkaesittely",
    "tip.budget": "Budjetti",
    "tip.stops": "Pysähdykset",
    "tip.places": "paikat",
    "tip.duration": "Kesto",
    "tip.days": "päivää",
    "tip.consumption": "Kulutus",
    "tip.goodToKnow": "Hyvä tietää ennen lähtöä",
    "tip.bestSeason": "Paras vuodenaika",
    "tip.currency": "Valuutta",
    "tip.speed": "Nopeus",
    "tip.emergency": "Hätätilanne",
    "tip.campingRules": "Leirintäsäännöt",
    "tip.route": "Reitti",
    "tip.endOfTrip": "Matkan loppu",
    "tip.wantSameRoute": "Haluatko saman reitin?",
    "tip.cloneDesc": "Luomme sinulle täysin uuden matkan samoilla pysähdyksillä. Voit sitten muokata sitä – muuttaa alkupistettä, lisätä pysähdyksiä ja suunnitella budjettisi.",
    "tip.needAccount": "Tarvitset tilin matkan tallentamiseksi",
    "tip.tripCreated": "Matka luotu! 🚐 Voit nyt muokata sitä.",
    "tip.couldNotCreate": "Matkan luonti epäonnistui",
    "tip.home": "Koti",
    "tip.travelTips": "Matkavinkkejä",
    "tip.startPoint": "Lähtöpiste",
    "tip.endPoint": "Päätepiste",
    "tip.travelGuide": "Matkaopas",
    "tip.hide": "Piilota",
    "tip.sharedThisTrip": "jakoivat tämän matkan",
    "tip.explore": "Tutustu",
    "tip.inspirationTitle": "Inspiraatiota matkailijoilta",
    "tip.inspirationDesc": "Löydä muiden matkailijoiden jakamia reittejä",
    "tip.tipHidden": "Matkavinkki piilotettu etusivulta",
    "tip.couldNotHide": "Matkavinkin piilottaminen epäonnistui",
    "tip.publishAsTip": "Julkaise matkavinkkinä",
    "tip.publishDesc": "Näytä matkasi etusivulla inspiraationa muille",
    "tip.creatingAi": "Luodaan tekoälymatkaesittelyä... ✨",
    "tip.creatingAiDesc": "Tämä voi kestää hetken.",
    "tip.publishedWithAi": "Matka julkaistu tekoälyoppaan kanssa! 🌍✨",
    "tip.published": "Matka on nyt kaikkien nähtävissä! 🌍",
    "tip.unpublished": "Matka on nyt yksityinen",
    "tip.publishedNoAi": "Matka julkaistu! (Tekoälyoppaan luonti epäonnistui)",
    "tip.routeHighlights": "Reitin kohokohdat",
    "tip.routeHighlightsDesc": "Suositellut paikat ja kohokohdat matkaasi varten",
    "tip.openInMaps": "Avaa kartoissa",
    "tip.saveMoney": "Säästä rahaa",
    "tip.fuelEstimate": "Polttoainekustannukset",
    "tip.campingCost": "Leirintä",
    "tip.night": "yö",
    "tip.yourDescription": "Oma kuvauksesi (valinnainen)",
    "tip.yourDescriptionPlaceholder": "Jaa, mikä tekee tästä reitistä erityisen, vinkkejä muille tai miksi pidit siitä...",
    "submit.loginRequired": "Kirjautuminen vaaditaan",
    "submit.loginDesc": "Sinun on oltava sisäänkirjautunut rekisteröidäksesi paikan.",
    "submit.title": "Rekisteröi paikka",
    "submit.subtitle": "Jaa suosikki leirintäpaikkasi yhteisön kanssa",
    "submit.reviewNote": "Rekisteröidyt paikat tarkistetaan ennen julkaisua.",
    "submit.basicInfo": "Perustiedot",
    "submit.name": "Nimi",
    "submit.type": "Tyyppi",
    "submit.country": "Maa",
    "submit.region": "Alue",
    "submit.city": "Kaupunki",
    "submit.description": "Kuvaus",
    "submit.shortDescription": "Lyhyt kuvaus",
    "submit.fullDescription": "Täysi kuvaus",
    "submit.priceSchedule": "Hinta ja aikataulu",
    "submit.freeSpot": "Tämä on ilmainen paikka",
    "submit.pricePerNight": "Hinta per yö (€)",
    "submit.imageContact": "Kuvat ja yhteystiedot",
    "submit.imageUrl": "Kuvan URL",
    "submit.phone": "Puhelin",
    "submit.email": "Sähköposti",
    "submit.website": "Verkkosivusto",
    "submit.address": "Osoite",
    "submit.fullAddress": "Täysi osoite",
    "submit.searchAddress": "Etsi osoitetta...",
    "submit.amenities": "Palvelut",
    "submit.photos": "Valokuvat",
    "submit.photosDesc": "Lataa enintään 5 kuvaa",
    "submit.submit": "Rekisteröi paikka",
    "submit.submitting": "Rekisteröidään...",
    "submit.success": "Rekisteröity!",
    "submit.successDesc": "Paikkasi tarkistetaan järjestelmänvalvojan toimesta.",
    "submit.missingFields": "Puuttuvat kentät",
    "submit.missingFieldsDesc": "Nimi, maa ja alue ovat pakollisia.",
    "submit.ctaTitle": "Tiedätkö hyvän paikan?",
    "submit.ctaDesc": "Rekisteröi leirintäalue tai stellplatz",
    "home.featuresTrustTitle": "Vahvistetut paikat",
    "home.featuresTrustDesc": "Todellisia leirintäalueita ja paikkoja hinnoilla, arvosteluilla ja kaikella, mitä tarvitset ennen lähtöä.",
    "home.featuresRoutesTitle": "Älykkäät reitit",
    "home.featuresRoutesDesc": "Näe koko matka yhdellä kertaa. Pysähdykset, ajoajat, polttoainekustannukset ja vaihtoehtoiset reitit suoraan kartalla.",
    "home.featuresBudgetTitle": "Matkabudjetti",
    "home.featuresBudgetDesc": "Aseta budjetti, kirjaa kulutuksesi ja näe tarkalleen, mihin rahat menevät. Kategoria kerrallaan.",
    "home.featuresChecklistTitle": "Tarkistuslista",
    "home.featuresChecklistDesc": "Matkatyyppiisi räätälöidyt tarkistuslistat. Kaasu, vesi, passi, laturi. Kaikki yhdessä paikassa.",
    "home.featuresAITitle": "AI-avustaja",
    "home.featuresAIDesc": "Kysy Vannie'lta matkastasi. Reitit, säännöt, vinkit ja paikallistuntemus. Aina käytettävissä.",
    "home.sectionTitle": "Katso miten se toimii",
    "home.sectionSubtitle": "Ensimmäisestä ideasta matkaan tien päällä. Kaikki yhdessä paikassa.",
    "home.trustPlaces": "2 400+ paikkaa",
    "home.trustEurope": "Koko Eurooppa",
    "home.videoOverlay1": "Suunnittele reittisi. Löydä parhaat paikat. Lähde tien päälle.",
    "home.videoOverlay2": "Kaikki mitä tarvitset matkallesi.",
    "home.lifestyleTitle": "Matkasi. Omalla tavallasi.",
    "home.lifestyleSubtitle": "Reitit, pysähdykset, budjetti ja pakkauslista. Valmiina sekunneissa.",
    "home.lifestyleCta": "Aloita suunnittelu",
    "home.vannieTitle": "Tapaa Vannie. Henkilökohtainen matkaoppaasi.",
    "home.vannieDesc": "Esitä kysymys, saat suoran vastauksen. Parhaat paikat, paikalliset vinkit, reittiehdotukset ja maiden säännöt. Vannie tietää. Ja se on ilmaista.",
    "home.vannieCta": "Keskustele Vannie'n kanssa",
    "home.vannieCtaSignup": "Liity ja keskustele Vannie'n kanssa",
    "home.vannieRoutes": "Reittisuunnittelu",
    "home.vannieCamping": "Leirintävinkit",
    "home.vannieRules": "Paikalliset säännöt",
    "home.vannieBudget": "Budjettiapu",
    "nav.services": "Palvelut & SOS",
    "nav.games": "Roadtrip-pelit",
    "nav.vannie": "Vannie",
    "nav.community": "Yhteisö",
    "nav.profile": "Oma profiili",
    "card.free": "ILMAINEN",
    "card.night": "yö",
    "card.loginForPrice": "Kirjaudu nähdäksesi hinnan",
    "cat.campings": "Leirintäalueet",
    "cat.stellplatz": "Pysäköintialueet",
    "cat.freeSpots": "Ilmaiset paikat",
    "map.noStopsToShow": "Ei pysähdyksiä koordinaateilla näytettäväksi",
    "wizard.whereToGo": "Minne haluat mennä?",
    "wizard.searchDestination": "Etsi kohde (esim. Sanremo, Italia)...",
    "wizard.startPoint": "Mistä aloitat?",
    "wizard.finalDay": "Päämäärä",
    "wizard.overnight": "Yöpyminen",
    "wizard.viewAllAccommodations": "Näytä kaikki",
    "wizard.accommodationNear": "Majoitus lähellä",
    "wizard.tapToChooseAccommodation": "Valitse majoitus alta.",
    "wizard.accommodationMissingHint": "Olet suunnitellut yöpymisen tänne, mutta majoitusta ei ole valittu.",
    "wizard.accommodationMissingTitle": "Majoitus puuttuu",
    "wizard.accommodationMissingSubtitle": "Napauta valitaksesi majoituksen jokaiselle päivälle.",
    "wizard.detectedAccommodationTitle": "Vaikuttaa majoitukselta",
    "wizard.detectedAccommodationDesc": "vaikuttaa majoitukselta. Miten haluat merkitä sen?",
    "wizard.overnightHint": "Nukut täällä",
    "wizard.visitHint": "Vain läpikulku tai vierailu",
    "wizard.destinationHint": "Matkan päämäärä",
    "wizard.optimizeOrder": "Optimoi järjestys",
    "wizard.whenTravel": "Milloin matkustat?",
    "wizard.myLocation": "Oma sijaintini",
    "wizard.chooseManually": "Valitse manuaalisesti",
    "wizard.searchStartPoint": "Etsi lähtöpiste...",
    "wizard.locationDetected": "Sijainti tunnistettu",
    "wizard.start": "Aloita",
    "wizard.destination": "Kohde",
    "wizard.yourStops": "Pysähdyksesi",
    "wizard.stop": "Pysähdys",
    "wizard.addedAsStop": "lisätty pysähdykseksi!",
    "campingDetector.title": "Tämä näyttää leirintäalueelta!",
    "campingDetector.aiDetected": "Tekoäly havaitsi",
    "campingDetector.description": "Haluatko lisätä paikan Vantastikaan, jotta muut matkailijat löytävät sen?",
    "campingDetector.pendingNote": "Ylläpitäjä tarkistaa paikan ennen julkaisua.",
    "campingDetector.confidence": "osuvuus",
    "campingDetector.typeCamping": "Leirintäalue",
    "campingDetector.typeStellplatz": "Matkailuautopaikka",
    "campingDetector.typeFreeParking": "Ilmainen pysäköinti",
    "campingDetector.typeLodging": "Majoitus",
    "campingDetector.notNow": "Ei nyt",
    "campingDetector.registerCta": "Lisää Vantastikaan",
    "campingDetector.registering": "Lisätään…",
    "campingDetector.registeredTitle": "Kiitos panoksesta!",
    "campingDetector.registeredDesc": "Paikka on lähetetty ja julkaistaan tarkistuksen jälkeen.",
    "campingDetector.alreadyExists": "On jo Vantastikassa",
    "campingDetector.alreadyExistsDesc": "Tämä paikka on jo rekisteröity.",
    "campingDetector.couldNotRegister": "Paikan rekisteröinti epäonnistui",
    "campingDetector.loginRequired": "Kirjautuminen vaaditaan",
    "campingDetector.loginRequiredDesc": "Kirjaudu sisään lisätäksesi uusia paikkoja.",
    "campingDetector.cannotRegister": "Rekisteröinti ei onnistu",
    "campingDetector.missingPlaceId": "Googlen paikkatunnus puuttuu.",
    "stopType.visit": "Vierailu",
    "stopType.overnight": "Yöpyminen",
    "stopType.destination": "Kohde",
    "stopType.nights": "Yöt",
    "stopType.stayHours": "Oleskelu (h)",
    "wizard.suggestedStops": "Leirintäalueet reitin varrella",
    "wizard.findingCampings": "Etsitään leirintäalueita reitin varrelta...",
    "wizard.noCampingsNearRoute": "Ei leirintäalueita löytynyt reitin läheltä",
    "wizard.saveTrip": "Tallenna matkani",
    "wizard.stepPlanningMode": "Suunnittelu",
    "wizard.stepDrivingHours": "Ajaminen",
    "wizard.stepTravelStyle": "Matkustustyyli",
    "wizard.stepCountryInfo": "Maat",
    "wizard.howToPlan": "Miten haluat suunnitella matkasi?",
    "wizard.howToPlanDesc": "Valitse sinulle sopivin vaihtoehto.",
    "wizard.planModeKnow": "Tiedän minne olen menossa",
    "wizard.planModeKnowDesc": "Minulla on kohde selvillä ja haluan suunnitella reitin sinne.",
    "wizard.planModeHelp": "Auta minua löytämään matka",
    "wizard.planModeHelpDesc": "Haluan inspiraatiota ja AI-ehdotuksia matkoille.",
    "wizard.planModeOptimize": "Optimoi reittini älykkäästi",
    "wizard.planModeOptimizeDesc": "Tiedän suunnilleen minne olen menossa, mutta haluan parhaat etapit ja pysähdykset.",
    "wizard.stepDetails": "Tiedot",
    "wizard.stepTrip": "Matka",
    "wizard.stepPreferences": "Asetukset",
    "wizard.dailyDrivingTitle": "Kuinka kauan haluat ajaa päivässä?",
    "wizard.dailyDrivingDesc": "Mukautamme etapit valintasi mukaan.",
    "wizard.recommended": "Suositeltu",
    "wizard.travelStyleTitle": "Miten haluat matkustaa?",
    "wizard.travelStyleDesc": "Mukautamme reitin ja ehdotukset matkustustyylisi mukaan.",
    "wizard.styleFastest": "Nopein reitti",
    "wizard.styleFastestDesc": "Lyhin ajoaika moottoritietä pitkin.",
    "wizard.styleSafe": "Turvallinen ja rauhallinen",
    "wizard.styleSafeDesc": "Rauhallisempia teitä kohtuullisella päiväajolla.",
    "wizard.styleScenic": "Maisemareitti",
    "wizard.styleScenicDesc": "Kauniita teitä ja näköaloja.",
    "wizard.styleCamping": "Leirintäystävällinen reitti",
    "wizard.styleCampingDesc": "Reitti hyvällä pääsyllä leirintäalueille.",
    "wizard.styleBudget": "Budjettiystävällinen matka",
    "wizard.styleBudgetDesc": "Vältä tietulleja ja kalliita pysähdyksiä.",
    "wizard.styleFamily": "Lapsiystävällinen matka",
    "wizard.styleFamilyDesc": "Lyhyemmät etapit lapsiystävällisillä pysähdyksillä.",
    "wizard.selectTravelStyle": "Valitse matkustustyyli",
    "wizard.longDrivingWarning": "Pidempi päivittäinen ajaminen voi vaikuttaa liikenneturvallisuuteen. Suosittelemme max 4–5 tuntia päivässä.",
    "wizard.skipOvernightHere": "Ohita yöpyminen – jatka ajamista",
    "wizard.resetStages": "Nollaa etapit",
    "wizard.passThrough": "Läpiajo",
    "wizard.noOvernightShort": "Ei yöpymistä",
    "wizard.mergedDayTitle": "Ajat läpi ilman yöpymistä",
    "wizard.mergedDayDesc": "Tämä päivä on yhdistetty seuraavaan. Lyhyitä taukoja lepoon ja tankkaukseen suositellaan.",
    "wizard.undoSkipOvernight": "Kumoa – yövy tässä",
    "wizard.selectPlanningMode": "Valitse miten haluat suunnitella",
    "wizard.accommodationTitle": "Majoitusehdotukset",
    "wizard.accommodationDescCamping": "Näyttää matkailuauto- ja leirintäpaikkoja reitilläsi.",
    "wizard.accommodationDescCar": "Näyttää hotelleja ja majoituksia kohteesi lähellä.",
    "wizard.campingRules": "Leirintäsäännöt",
    "wizard.countriesInRouteOrder": "Maat reittijärjestyksessä",
    "wizard.countryInfoDesc": "Käytännön tietoa jokaisesta maasta, jonka läpi kuljet.",
    "wizard.countryInfoTitle": "Maat reitilläsi",
    "wizard.startCountry": "Lähtömaa",
    "wizard.transitCountry": "Kauttakulkumaa",
    "wizard.destinationCountry": "Määränpää",
    "wizard.ferryAlternatives": "Lauttvaihtoehdot",
    "wizard.ferryDuration": "Matka-aika",
    "wizard.ferryRoute": "Reitti",
    "wizard.ferryRelevance": "Miksi tärkeä",
    "wizard.driving": "Ajaminen",
    "wizard.drivingRules": "Ajosäännöt",
    "wizard.fromRoute": "Reitin varrella",
    "wizard.fuelInfo": "Polttoaine",
    "wizard.importantForMotorhome": "tärkeää matkailuautolle",
    "wizard.infoItems": "tietopistettä",
    "wizard.loadingCountryInfo": "Ladataan maatietoja...",
    "wizard.motorhomeMultiCountryTip": "Matkailuautolla säännöt voivat vaihdella merkittävästi maiden välillä. Tarkista ympäristövyöhykkeet, vinjetit ja sallitut mitat ennen lähtöä.",
    "wizard.noCountryInfo": "Ei maatietoja saatavilla. Anna reitti ensin.",
    "wizard.noResultsForStage": "Ei tuloksia tälle etapille",
    "wizard.oneWay": "Yhdensuuntainen",
    "wizard.optimizeRoute": "Optimoi pysähdysjärjestys",
    "wizard.searchingStage": "Haetaan etappiin",
    "wizard.selectDestForAccommodation": "Valitse kohde ensin majoitusehdotuksia varten.",
    "wizard.shortTripNoOvernight": "Lyhyt matka – ei yöpymistä tarvita.",
    "wizard.skipStep": "Ohita tämä vaihe",
    "wizard.from": "Lähtö",
    "wizard.to": "Kohde",
    "wizard.selectedAccommodation": "Valittu majoitus",
    "wizard.changeAccommodation": "Vaihda",
    "wizard.selected": "Valittu",
    "wizard.selectAccommodation": "Valitse",
    "wizard.noAccommodationYet": "Ei majoitusta valittu vielä. Voit valita seuraavassa vaiheessa.",
    "wizard.viewOnMap": "Näytä kartalla",
    "common.close": "Sulje",
    "wizard.speedLimits": "Nopeusrajoitukset",
    "wizard.stages": "etapit",
    "wizard.tolls": "Tietullit & vinjetit",
    "wizard.wildCamping": "Villiretkeily",
    "wizard.stepRoute": "Menomatka",
    "wizard.stepReturn": "Paluumatka",
    "wizard.stepAccommodation": "Majoitus",
    "wizard.stepVehicle": "Ajoneuvo",
    "wizard.stepOverview": "Yhteenveto",
    "wizard.next": "Seuraava",
    "wizard.back": "Takaisin",
    "wizard.tripTypeLabel": "Matkatyyppi",
    "wizard.accommodationDesc": "Tässä on majoitusehdotuksia reitin ja matkustustavan perusteella. Lisää pysähdykseksi tai ohita.",
    "wizard.showingCampingsAlongRoute": "Näytetään leirintäalueita reitin varrella",
    "wizard.showingHotelsNearDest": "Näytetään hotelleja kohteen lähellä",
    "wizard.showingAccommodationNearDest": "Näytetään majoituksia reitin varrella",
    "wizard.searchingAlongRoute": "Haetaan reitin varrelta...",
    "wizard.expandingSearch": "Laajennetaan hakualuetta...",
    "wizard.tryingAlternatives": "Kokeillaan vaihtoehtoisia majoituksia...",
    "wizard.noAccommodationFound": "Majoituksia ei löytynyt reitin läheltä. Voit ohittaa tämän vaiheen ja lisätä pysähdyksiä myöhemmin.",
    "wizard.returnTripDesc": "Valitse, miten haluat matkustaa takaisin.",
    "wizard.sameRouteDesc": "Aja takaisin samaa reittiä",
    "wizard.scenicDesc": "Vaihtoehtoinen maisemareitti",
    "wizard.fastestDesc": "Nopein tie kotiin",
    "wizard.routeSummary": "Reitti",
    "wizard.selectDestFirst": "Valitse kohde ensin",
    "wizard.selectStartFirst": "Valitse lähtöpiste ensin",
    "wizard.fuelType": "Polttoainetyyppi",
    "wizard.estimatedFuelCostLabel": "Arvioitu polttoainekustannus",
    "wizard.showPricesIn": "Näytä hinnat valuutassa",
    "wizard.perNight": "yö",
    "wizard.moreDetails": "Lisätiedot",
    "wizard.firstTimeHint": "Aloita hakemalla kohdettasi. Valitse sitten ajoneuvo, päivämäärät ja pysähdykset – reitti lasketaan automaattisesti!",
    "wizard.saving": "Tallennetaan...",
    "wizard.budgetOptional": "Aseta budjetti (valinnainen)",
    "wizard.budgetPlaceholder": "esim. 15000",
    "wizard.fuelAutoAdded": "Arvioitu polttoainekustannus lisätty budjettiin",
    "wizard.planNewTrip": "Suunnittele uusi matka",
    "wizard.myTrips": "Tallentamani matkat",
    "wizard.planTrip": "Suunnittele matka",
    "wizard.or": "tai",
    "wizard.popularDestinations": "Suosittua",
    "wizard.describeTrip": "Kuvaile matkaasi - me suunnittelemme kaiken puolestasi",
    "wizard.describeSubtext": "Kirjoita vapaasti, mitä haluat tehdä, niin me rakennamme ehdotuksen",
    "wizard.backToManual": "Takaisin manuaaliseen hakuun",
    "wizard.selectSuggestion": "Valitse ehdotus listalta jatkaaksesi.",
    "wizard.analyzingTrip": "Analysoidaan matkaasi…",
    "wizard.createPlan": "Luo matkasuunnitelma",
    "wizard.suggestedTrip": "Ehdotettu matka",
    "wizard.places": "paikkaa",
    "wizard.startOver": "Aloita alusta",
    "wizard.noDestFound": "Kohteita ei löytynyt",
    "wizard.noDestFoundDesc": "Yritä kuvailla matkaasi yksityiskohtaisemmin.",
    "wizard.couldNotParse": "Ei voitu tulkita matkaa",
    "wizard.couldNotParseDesc": "Yritä uudelleen selkeämmällä kuvauksella.",
    "wizard.tooManyRequests": "Liian monta pyyntöä",
    "wizard.tooManyRequestsDesc": "Odota hetki ja yritä uudelleen.",
    "wizard.aiCreditOut": "AI-krediitit loppu",
    "wizard.aiCreditOutDesc": "Ota yhteyttä tukeen saadaksesi lisätietoja.",
    "wizard.tripSaved": "Matka tallennettu!",
    "wizard.tripSavedDesc": "Siirry Omat matkat nähdäksesi sen.",
    "wizard.couldNotReachVannie": "Vannie'en ei saada yhteyttä juuri nyt.",
    "wizard.describePlaceholder": "Kuvaile matkaasi omin sanoin – luomme täydellisen matkasuunnitelman puolestasi.",
    "wizard.chars": "merkkiä",
    "wizard.destinationConfirmed": "Kohde valittu",
    "wizard.locating": "Etsitään koordinaatteja...",
    "wizard.destType": "Kohde",
    "wizard.visitType": "Vierailu",
    "wizard.aiSuggestTitle": "AI Matkaehdotus",
    "wizard.aiSuggestOpen": "Kuvaile matkaasi – me suunnittelemme kaiken puolestasi",
    "wizard.aiSuggestPlaceholder": "Esim. 'Haluan mennä rannalle 5 päiväksi' tai 'Mukava vuoristomatka vaelluksella Alpeilla'",
    "wizard.vannieThinking": "Vannie miettii...",
    "wizard.suggestTrip": "Ehdottaa matkaa",
    "wizard.suggestedStopsLabel": "Ehdotetut pysähdykset",
    "wizard.useThisSuggestion": "Käytä tätä ehdotusta",
    "wizard.couldNotGenerate": "Ehdotusta ei voitu luoda",
    "wizard.days": "päivää",
    "wizard.perDay": "/päivä",
    "wizard.roundtrip": "Meno-paluu",
    "wizard.oneway": "Yksisuuntainen",
    "wizard.departure": "Lähtö",
    "wizard.return": "Paluu",
    "wizard.selectDate": "Valitse päivämäärä",
    "wizard.departureDateOptional": "Lähtöpäivämäärä (valinnainen)",
    "wizard.day": "päivä",
    "wizard.dayLabel": "Päivä {n}",
    "wizard.roundtripLabel": "Meno-paluu",
    "wizard.estimatedTravelTime": "Arvioitu matka-aika",
    "wizard.total": "yhteensä",
    "wizard.stopsLabel": "pysähdystä",
    "wizard.fetchingReturnTips": "Haetaan paluumatkan vinkkejä...",
    "wizard.returnTips": "Paluumatkan vinkit",
    "wizard.roundtripShort": "meno-paluu",
    "wizard.driveTime": "Ajoaika",
    "wizard.chargeCost": "Latauskustannus",
    "wizard.fuelCost": "Polttoainekustannus",
    "wizard.distance": "Matka",
    "wizard.chooseRoute": "Valitse tämä reitti",
    "wizard.ferry": "Lautta",
    "wizard.maxDrivingHours": "Max ajoaika päivässä",
    "wizard.longDriveWarning": "Pitkä ajoaika",
    "wizard.longDriveDesc": "Suosittelemme enintään 4 tuntia turvallista ja nautinnollista ajoa varten.",
    "wizard.drivingDaysCalc": "{hours}h/päivä = ~{days} ajo-päivää",
    "wizard.drivingDaysRoundtrip": "päivää meno-paluu",
    "wizard.vehicleSettings": "Ajoneuvo- ja polttoainetiedot",
    "wizard.scenic": "Maisemareitti",
    "wizard.fastest": "Nopein",
    "wizard.balanced": "Tasapainoinen",
    "wizard.sameRoute": "Sama reitti",
    "wizard.returnTrip": "Paluumatka",
    "wizard.homecoming": "Kotimatka",
    "wizard.optimizationSummary": "Reitti optimoitu",
    "wizard.stageOverview": "Päiväetapit",
    "wizard.overnightIn": "Yöpyminen lähellä",
    "wizard.arrivalAt": "Saapuminen kohteeseen",
    "wizard.returnRouteOption": "Paluumatka",
    "wizard.hours": "t",
    "wizard.addedAsOvernight": "lisätty yöpymiskohteeksi!",
    "wizard.addedAsRestStop": "lisätty levähdyspysähdykseksi!",
    "wizard.stopAddedFromMap": "Pysähdys lisätty kartalta!",
    "wizard.updatedStop": "Päivitetty pysähdys",
    "wizard.stopPositionUpdated": "Pysähdyksen sijainti päivitetty",
    "wizard.newStop": "Uusi pysähdys",
    "wizard.optimizedDesc": "Pysähdykset järjestetty uudelleen lyhimmän kokonaismatkan saavuttamiseksi",
    "wizard.newOrder": "Uusi järjestys",
    "wizard.orderChanged": "Järjestys muuttunut alkuperäisestä",
    "wizard.routeOptimized": "Reitti optimoitu!",
    "wizard.nameError": "N Ehdotusten luominen epäonnistui",
    "wizard.mapInstructions": "Klikkaa karttaa lisätäksesi pysähdyksiä, vedä siirtääksesi niitä",
    "wizard.nameTrip": "Nimeä matkasi",
    "wizard.optional": "valinnainen",
    "wizard.namePlaceholder": "esim. Kesä Alpeilla",
    "wizard.aiSuggest": "AI Ehdotus",
    "wizard.signupAndSave": "Rekisteröidy ja tallenna matka",
    "wizard.useTrip": "Käytä tätä matkaa",
    "wizard.dragToReorder": "Vedä uudelleentilaamiseksi",
    "wizard.prefillTip": "Esitäytetty Vantastika-vinkistä",
    "wizard.prefillGuide": "Esitäytetty matkaoppaasta",
    "budget.title": "Matkabudjetti",
    "budget.tab": "Budjetti",
    "budget.setBudget": "Aseta budjetti",
    "budget.totalBudget": "Kokonaissumma",
    "budget.spent": "Käytetty",
    "budget.remaining": "Jäljellä",
    "budget.used": "käytetty",
    "budget.topCategory": "Suurin kategoria",
    "budget.addExpense": "Lisää kuluerä",
    "budget.amount": "Summa",
    "budget.description": "Kuvaus (valinnainen)",
    "budget.save": "Tallenna",
    "budget.cancel": "Peruuta",
    "budget.expenses": "Kuluerät",
    "budget.breakdown": "Kategorioittain",
    "budget.expenseAdded": "Meno lisätty!",
    "budget.expenseUpdated": "Meno päivitetty!",
    "budget.expenseFailed": "Menon tallennus epäonnistui",
    "budget.autoLabel": "Automaattisesti luotu",
    "budget.editFuelCalc": "Muokkaa",
    "budget.fuelCalcTitle": "Polttoainekulujen laskenta",
    "budget.fuelPriceLabel": "Hinta/litra",
    "budget.fuelConsumptionLabel": "l/100km",
    "budget.fuelDistanceLabel": "Matka (km)",
    "budget.fuelCalcResult": "Arvioidut polttoainekulut:",
    "budget.fuelSave": "Päivitä polttoainekulut",
    "budget.fuelUpdated": "Polttoainekulut päivitetty!",
    "budget.budgetSaved": "Budjetti tallennettu!",
    "budget.getAiTips": "Hanki budjettivinkkejä Vannielta",
    "budget.aiTipsTitle": "Vannien budjettivinkit",
    "budget.noTips": "Ei vinkkejä tällä hetkellä.",
    "budget.tipsFailed": "Vinkkien lataus epäonnistui",
    "budget.close": "Sulje",
    "budget.cat.fuel": "Polttoaine",
    "budget.cat.camping": "Leirintä/Paikka",
    "budget.cat.food": "Ruoka & Juoma",
    "budget.cat.tolls": "Tietullit/Lautat",
    "budget.cat.activities": "Aktiviteetit",
    "budget.cat.shopping": "Ostokset",
    "budget.cat.other": "Muu",
    "budget.budgetLabel": "Budjetti",
    "budget.edit": "Muokkaa budjettia",
    "budget.backToTrip": "Takaisin matkalle",
    "budget.estimatedFuel": "Arvioidut polttoainekulut",
    "budget.estimateOnly": "Vain arvio",
    "budget.autoCosts": "Automaattiset kulut",
    "budget.manualExpenses": "Sinun menosi",
    "budget.pageDesc": "Hallinnoi budjettejasi ja menojasi",
    "budget.loginRequired": "Kirjaudu sisään käyttääksesi budjettitoimintoa",
    "budget.subtitle": "Budjettisi ja menosi",
    "budget.pageSubtitle": "Suunnittele matkabudjettisi, lisää menoja ja seuraa, paljonko rahaa on jäljellä.",
    "budget.newBudget": "Uusi budjetti",
    "budget.newExpense": "Uusi meno",
    "budget.tabActive": "Aktiivinen",
    "budget.tabAll": "Kaikki",
    "budget.tabExpenses": "Linkittämättömät menot",
    "budget.tabUnlinked": "Linkittämättömät",
    "budget.emptyTitle": "Ei budjetteja vielä",
    "budget.emptyDesc": "Luo ensimmäinen budjettisi saadaksesi paremman yleiskuvan matkakuluistasi.",
    "budget.emptyStateTitle": "Budjettia ei ole vielä luotu",
    "budget.emptyStateDesc": "Luo ensimmäinen matkabudjettisi saadaksesi paremman käsityksen majoitus-, ruoka-, polttoaine- ja aktiviteettikustannuksista.",
    "budget.createBudget": "Luo budjetti",
    "budget.createFirstBudget": "Luo ensimmäinen budjetti",
    "budget.chipAccommodation": "Majoitus",
    "budget.chipFood": "Ruoka",
    "budget.chipFuel": "Polttoaine",
    "budget.chipActivities": "Aktiviteetit",
    "budget.chipShopping": "Ostokset",
    "budget.unlinkedLabel": "Linkittämättömät menot",
    "budget.unlinkedHint": "Nämä menot eivät ole yhdistetty mihinkään budjettiin.",
    "budget.more": "lisää",
    "budget.noActiveTitle": "Ei aktiivisia budjetteja",
    "budget.noActiveDesc": "Luo budjetti tai aktivoi olemassa oleva.",
    "budget.overviewTitle": "Budjettikatsaus",
    "budget.statusGood": "Hyvä tilanne",
    "budget.statusWarning": "Lähestyy rajaa",
    "budget.statusOver": "Yli budjetin",
    "budget.pctUsed": "käytetty",
    "budget.totalBudgetCard": "Kokonaisbudjetti",
    "budget.spentCard": "Käytetty",
    "budget.remainingCard": "Jäljellä",
    "budget.paused": "Keskeytetty",
    "budget.completed": "valmis",
    "budget.noExpenses": "Ei linkittämättömiä menoja",
    "budget.noExpensesDesc": "Menot ilman linkitettyä budjettia näkyvät täällä",
    "budget.setBudgetTitle": "Kuukausibudjetti",
    "budget.setBudgetBtn": "Aseta budjetti",
    "budget.setBudgetSaved": "Budjettiraja tallennettu ✓",
    "budget.month": "Kuukausi",
    "budget.budgetAmount": "Budjetti",
    "budget.amountPlaceholder": "esim. 15000",
    "budget.saving": "Tallennetaan...",
    "budget.saveBudgetLimit": "Tallenna budjettiraja",
    "budget.createBudgetTitle": "Luo budjetti",
    "budget.budgetName": "Budjetin nimi",
    "budget.budgetNamePlaceholder": "esim. Kesäloma 2026",
    "budget.budgetTypeLabel": "Budjetin tyyppi",
    "budget.totalBudgetLabel": "Kokonaisbudjetti",
    "budget.currencyLabel": "Valuutta",
    "budget.startDateLabel": "Alkamispäivä",
    "budget.endDateLabel": "Päättymispäivä",
    "budget.colorLabel": "Väri",
    "budget.selectColor": "Valitse väri",
    "budget.descriptionLabel": "Kuvaus",
    "budget.descriptionPlaceholder": "Valinnainen huomautus...",
    "budget.linkTrip": "Linkitä matkaan (valinnainen)",
    "budget.noTripSelected": "Matkaa ei valittu",
    "budget.noTrip": "Ei matkaa",
    "budget.fillNameAndAmount": "Syötä nimi ja kelvollinen summa",
    "budget.budgetCreated": "Budjetti luotu ✓",
    "budget.couldNotSave": "Ei voitu tallentaa",
    "budget.creating": "Luodaan...",
    "budget.addExpenseTitle": "Lisää meno",
    "budget.scanWithAi": "Skannaa kuitti tekoälyllä",
    "budget.amountLabel": "Määrä",
    "budget.dateLabel": "Päivämäärä",
    "budget.titleLabel": "Otsikko",
    "budget.titlePlaceholder": "Mitä ostit?",
    "budget.categoryLabel": "Kategoria",
    "budget.paymentMethod": "Maksutapa",
    "budget.selectPayment": "Valitse...",
    "budget.linkBudget": "Linkitä budjettiin (valinnainen)",
    "budget.linkBudgetDirect": "Linkitä budjettiin",
    "budget.noBudgetSelected": "Budjettia ei valittu",
    "budget.noBudget": "Ei budjettia",
    "budget.noteLabel": "Muistiinpano",
    "budget.notePlaceholder": "Valinnainen huomautus...",
    "budget.receiptLabel": "Kuitti",
    "budget.receiptRemove": "Poista",
    "budget.receiptUpload": "Ota kuva tai lataa kuitti",
    "budget.fileTooLarge": "Tiedosto on liian suuri",
    "budget.fileTooLargeDesc": "Max 15 Mt",
    "budget.fillAmountAndTitle": "Syötä kelvollinen summa ja otsikko",
    "budget.expenseSaved": "Meno tallennettu ✓",
    "budget.converted": "Muunnettu:",
    "budget.editBudgetTitle": "Muokkaa budjettia",
    "budget.budgetUpdated": "Budjetti päivitetty ✓",
    "budget.saveChanges": "Tallenna muutokset",
    "budget.editExpenseTitle": "Muokkaa menoa",
    "budget.scanTitle": "Älykäs kuittiskannaus",
    "budget.scanCapture": "Ota kuva tai lataa kuitti",
    "budget.scanCaptureDesc": "Tekoäly analysoi kuitin rivi riviltä",
    "budget.scanTakePhoto": "Ota kuva",
    "budget.scanUpload": "Lataa",
    "budget.scanAnalyzing": "Analysoidaan kuittia...",
    "budget.scanAnalyzingDesc": "Tekoäly lukee tuotteita, määriä ja kategorioita",
    "budget.scanFailed": "Kuitin analysointi epäonnistui",
    "budget.scanConfidenceHigh": "Korkea luottamus",
    "budget.scanConfidenceMedium": "Keskitason luottamus — tarkista kentät",
    "budget.scanConfidenceLow": "Matala luottamus — tarkista kaikki kentät",
    "budget.scanCandidates": "Mahdolliset summat:",
    "budget.scanItemsPerCategory": "Tuotteet per kategoria",
    "budget.scanMixedCategories": "Sekalaiset kategoriat",
    "budget.scanSaveHow": "Miten haluat tallentaa?",
    "budget.scanSingleExpense": "Yksi meno",
    "budget.scanSplitExpenses": "Jaa",
    "budget.scanSplitDesc": "Luo {n} erillistä menoa – yksi per kategoria.",
    "budget.scanMainCategory": "Pääkategoria",
    "budget.scanShowRaw": "Näytä raaka kuittiteksti",
    "budget.scanHideRaw": "Piilota raaka kuittiteksti",
    "budget.scanCancel": "Peruuta",
    "budget.scanSaveExpense": "Tallenna meno",
    "budget.scanSaveExpenses": "Tallenna {n} menoa",
    "budget.scanSavedMultiple": "{n} menoa tallennettu kuitista ✓",
    "budget.scanSavedSingle": "Meno tallennettu kuitista ✓",
    "budget.confidenceMedium": "Keskitason luottamus",
    "budget.confidenceLow": "Epävarma — tarkista",
    "budget.statusPaused": "Keskeytetty",
    "budget.statusCompleted": "Valmis",
    "budget.budgetLabel2": "Budjetti",
    "budget.spentLabel": "Käytetty",
    "budget.remainingLabel": "Jäljellä",
    "budget.overBy": "Yli",
    "budget.usedPercent": "käytetty",
    "budget.expenseCount": "menoa",
    "budget.mixedCurrenciesNote": "Menot valuutoissa {currencies} — muunnettu valuuttaan {target}",
    "budget.deleteConfirm": "Oletko varma, että haluat poistaa tämän budjetin?",
    "budget.statusActivated": "Budjetti aktivoitu",
    "budget.statusPausedToast": "Budjetti keskeytetty",
    "budget.statusCompletedToast": "Budjetti suoritettu",
    "budget.perCategory": "Per kategoria",
    "budget.biggestExpense": "Suurin meno",
    "budget.expenseListTitle": "Menot",
    "budget.noExpensesYet": "Ei vielä menoja",
    "budget.noExpensesYetDesc": "Napauta \"Lisää meno\" aloittaaksesi",
    "budget.totalSpent": "Yhteensä kulutettu",
    "budget.thisMonth": "Tässä kuussa",
    "budget.budgeted": "Budjetoitu",
    "budget.warnings": "Varoitukset",
    "summary.title": "Matkan kustannusyhteenveto",
    "summary.titleOngoing": "Kustannusten yleiskatsaus",
    "summary.over": "Yli budjetin",
    "summary.saved": "Säästetty",
    "summary.dailyAvg": "Päivässä",
    "summary.overBudget": "Ylitit budjetin tällä matkalla",
    "summary.underBudget": "Pysyit budjetissa — hienoa työtä!",
    "summary.breakdown": "Kulutuksen erittely",
    "summary.categoryDetails": "Kategorian tiedot",
    "fuelLog.title": "Polttoaineloki",
    "fuelLog.addFuel": "Lisää tankkaus",
    "fuelLog.liters": "Litrat",
    "fuelLog.cost": "Hinta",
    "fuelLog.date": "Päivämäärä",
    "fuelLog.odometer": "Odametri (km)",
    "fuelLog.note": "Huomautus",
    "fuelLog.optional": "Valinnainen",
    "fuelLog.save": "Tallenna",
    "fuelLog.added": "Tankkaus kirjattu!",
    "fuelLog.addFailed": "Tankkausta ei voitu tallentaa",
    "fuelLog.invalidInput": "Syötä sekä litrat että hinta",
    "fuelSummary.title": "Polttoaineyhteenveto",
    "fuelSummary.totalLiters": "Litrat yhteensä",
    "fuelSummary.totalCost": "Polttoainekustannukset",
    "fuelSummary.avgPrice": "Keskim. hinta/litra",
    "fuelSummary.consumption": "Kulutus",
    "fuelSummary.costPerKm": "Kustannus/km",
    "fuelSummary.costPerMil": "Kustannus/mi",
    "fuelSummary.noDistance": "Lisää reitin etäisyys nähdäksesi kulutuksen ja kustannukset per km.",
    "fuelSummary.basedOn": "Perustuu {n} tankkaukseen",
    "checklist.tab": "Tarkistuslista",
    "journal.tab": "Päiväkirja",
    "authGate.title": "Tallenna matkasi ja jatka milloin haluat",
    "authGate.description": "Luo ilmainen tili pitääksesi matkasi, pysähdyksesi ja budjettisi — niin voit jatkaa siitä, mihin jäit.",
    "authGate.signup": "Luo tili ja tallenna",
    "authGate.login": "Kirjaudu sisään",
    "authGate.ctaSection": "Kaikki mitä tarvitset matkallesi",
    "authGate.ctaPlaces": "Pysähdyksesi ja reittisi — tallennettu ja synkronoitu",
    "authGate.ctaRoutes": "Jatka suunnittelua millä tahansa laitteella",
    "authGate.ctaBudget": "Budjetti ja kuitit — aina käytettävissä",
    "authGate.ctaChecklist": "Tarkistuslistat, jotka seuraavat matkaasi",
    "authGate.ctaAI": "AI-avustaja, joka tuntee jokaisen tien",
    "authGate.ctaButton": "Luo tili ja tallenna",
    "authGate.saveTripMsg": "Tallenna matkasi ja jatka myöhemmin",
    "authGate.saveBudgetMsg": "Tallenna budjettisi ja seuraa menoja",
    "authGate.saveReceiptMsg": "Säilytä kuittisi laitteiden välillä",
    "authGate.saveFavoriteMsg": "Tallenna suosikkipaikkasi yhdelle listalle",
    "authGate.planRouteMsg": "Jatka siitä, mihin jäit",
    "authGate.savePlaceMsg": "Tallenna tämä paikka kokoelmaasi",
    "authGate.useBudgetMsg": "Seuraa budjettiasi — luo ilmainen tili",
    "authGate.useChecklistMsg": "Käytä tarkistuslistoja — luo ilmainen tili",
    "authGate.or": "tai",
    "authGate.hasAccount": "Onko sinulla jo tili?",
    "gate.whyJoin": "MIKSI LUODA TILI?",
    "gate.benefit1": "Pidä matkasi, pysähdyksesi ja budjettisi turvassa",
    "gate.benefit2": "Jatka suunnittelua millä tahansa laitteella",
    "gate.benefit3": "Täysi budjetin seuranta kuittien skannauksella",
    "gate.benefit4": "Ilmainen AI-matka-avustaja sisältyy hintaan",
    "gate.benefit5": "Pääsy yhteisön vinkkeihin ja arvosteluihin",
    "gate.benefit6": "Ajoneuvollesi räätälöidyt tarkistuslistat",
    "gate.freeForever": "Ilmainen aloittaa",
    "welcome.title": "Tervetuloa Vantastikaan!",
    "welcome.titleName": "Tervetuloa, {name}!",
    "welcome.desc": "Kaikki mitä tarvitset suunnitellaksesi, matkataksesi ja muistaaksesi matkasi on täällä.",
    "welcome.feature1": "Suunnittele reittejä, lisää pysähdyspaikkoja ja seuraa budjettia ja polttoainetta",
    "welcome.feature2": "Vannie, AI-matka-avustajasi, auttaa sinua löytämään parhaat reitit",
    "welcome.feature3": "Tallenna suosikkipaikkasi, jaa matkoja ystävien kanssa",
    "welcome.cta": "Suunnittele ensimmäinen matkani",
    "welcome.dismiss": "Tutki itsenäisesti",
    "welcome.close": "Sulje",
    "gate.noCard": "Ilmainen aloittaa. Luottokorttia ei tarvita.",
    "gate.createAccount": "Luo ilmainen tili",
    "gate.alreadyMember": "Onko sinulla jo tili? Kirjaudu sisään",
    "gate.seeMore": "Haluatko nähdä enemmän?",
    "gate.registerToSee": "Luo ilmainen tili päästäksesi kaikkiin ominaisuuksiin ja paikkoihin.",
    "gate.searchTitle": "Tutustu kaikkiin leirintäalueisiin",
    "gate.searchDesc": "Rekisteröidy ilmaiseksi selataksesi kaikkia vahvistettuja paikkoja, tallentaaksesi suosikkisi ja lukeaksesi yhteisön arvosteluja.",
    "gate.servicesTitle": "Huolto & Hätätilanne",
    "gate.servicesDesc": "Luo tili löytääksesi työpajoja, latausasemia, vesipisteitä ja hätäpalveluita läheltäsi.",
    "gate.tripsTitle": "Matkasuunnittelija",
    "gate.tripsDesc": "Rekisteröidy ilmaiseksi suunnitellaksesi rajattomasti matkoja älykkäillä reiteillä, polttoainekustannuksilla ja budjetin seurannalla.",
    "gate.submitTitle": "Lisää paikka",
    "gate.submitDesc": "Rekisteröidy ilmaiseksi jakaaksesi suosikkipaikkasi yhteisön kanssa.",
    "gate.tipsTitle": "Matkavinkit",
    "gate.tipsDesc": "Luo tili lukeaksesi ja jakaaksesi matkavinkkejä oikeilta matkailijoilta.",
    "roadTips.title": "Matkavinkit",
    "roadTips.share": "Jaa vinkki",
    "roadTips.allCountries": "Kaikki maat",
    "roadTips.formTitle": "Jaa matkavinkki",
    "roadTips.labelTitle": "Otsikko *",
    "roadTips.placeholderTitle": "esim. Paras reitti Alppien läpi",
    "roadTips.labelType": "Tyyppi",
    "roadTips.labelCountry": "Maa",
    "roadTips.select": "Valitse",
    "roadTips.labelTip": "Vinkkisi *",
    "roadTips.placeholderTip": "Jaa kokemuksesi...",
    "roadTips.labelImage": "Kuva-URL (valinnainen)",
    "roadTips.posting": "Julkaistaan...",
    "roadTips.post": "Julkaise vinkki",
    "roadTips.shared": "Vinkki jaettu!",
    "roadTips.loginToShare": "Kirjaudu sisään jakaaksesi omia matkavinkkejäsi.",
    "roadTips.noTips": "Ei matkavinkkejä vielä. Ole ensimmäinen!",
    "roadTips.type.general": "Yleinen vinkki",
    "roadTips.type.route": "Reittisuositus",
    "roadTips.type.parking": "Pysäköintivinkki",
    "roadTips.type.food": "Ruoka & Ostokset",
    "roadTips.type.warning": "Varoitus",
    "roadTips.type.service": "Palvelupiste",
    "community.title": "Yhteisö",
    "community.subtitle": "Jaa kokemuksia, vinkkejä ja inspiraatiota muiden matkailijoiden kanssa",
    "community.newPost": "Uusi viesti",
    "community.createPost": "Luo uusi viesti",
    "community.all": "Kaikki",
    "community.anonymous": "Nimetön",
    "community.noPostsYet": "Ei viestejä vielä",
    "community.beFirst": "Ole ensimmäinen, joka aloittaa keskustelun!",
    "community.loadMore": "Lataa lisää julkaisuja",
    "community.loading": "Ladataan…",
    "community.createFirst": "Luo ensimmäinen viesti",
    "community.back": "Takaisin",
    "community.comments": "Kommentit",
    "community.noComments": "Ei kommentteja vielä — ole ensimmäinen!",
    "community.commentPlaceholder": "Kirjoita kommentti...",
    "community.titlePlaceholder": "Otsikko",
    "community.contentPlaceholder": "Mistä haluaisit keskustella?",
    "community.addImage": "Lisää kuva (valinnainen)",
    "community.publishing": "Julkaistaan...",
    "community.publish": "Julkaise viesti",
    "community.toast.postCreated": "Viesti luotu!",
    "community.toast.postPublished": "Viestisi on julkaistu.",
    "community.toast.postCreateError": "Viestiä ei voitu luoda.",
    "community.toast.commentError": "Kommenttia ei voitu luoda.",
    "community.toast.error": "Virhe",
    "community.confirmDelete": "Haluatko varmasti poistaa tämän viestin?",
    "community.cat.general": "Yleinen",
    "community.cat.tips": "Vinkit & Niksit",
    "community.cat.routes": "Reitit & Paikat",
    "community.cat.gear": "Varusteet",
    "community.cat.help": "Apu & Kysymykset",
    "community.cat.photos": "Kuvat & Matkatarinat",
    "community.type.question": "Kysymys",
    "community.type.tip": "Vinkki",
    "community.type.discussion": "Keskustelu",
    "community.type.routes": "Reitit & paikat",
    "community.type.gear": "Varusteet",
    "community.type.photos": "Kuvat",
    "community.type.help": "Apu",
    "community.type.question.desc": "Kysy yhteisöltä kysymys",
    "community.type.tip.desc": "Jaa hyvä vinkki",
    "community.type.discussion.desc": "Aloita keskustelu",
    "community.type.routes.desc": "Jaa suosikkipaikkoja",
    "community.type.gear.desc": "Varusteet ja tarvikkeet",
    "community.type.photos.desc": "Jaa matkasi",
    "community.type.help.desc": "Pyydä apua",
    "community.awaitingAnswer": "Odottaa vastausta",
    "community.selectType": "Valitse julkaisutyyppi",
    "community.createPostTitle": "Luo julkaisu",
    "community.changeType": "Vaihda tyyppi",
    "community.questionPlaceholder": "Kirjoita kysymyksesi tähän...",
    "community.questionContentPlaceholder": "Kuvaile kysymyksesi yksityiskohtaisesti, jotta muut voivat auttaa sinua...",
    "community.communityRules": "Yhteisösäännöt",
    "community.communityRulesText": "Et saa julkaista alastonkuvia, seksuaalista sisältöä, mainoksia, ilmoituksia, myyntiä, roskapostia, uhkauksia tai häirintää. Kaikki julkaisut ja kuvat voidaan tarkistaa automaattisesti ennen julkaisua.",
    "games.seoTitle": "Roadtrip-pelit – Vantastika",
    "games.seoDesc": "Pelaa tietovisoja, bingoa ja muita pelejä roadtripillä",
    "demo.phase1Eyebrow": "Liian monta vaihtoehtoa",
    "demo.phase1Title": "Näe ongelma",
    "demo.phase1Text": "Kartta on täynnä paikkoja, ja on vaikea tietää, mikä todella on pysähdyksen arvoinen.",
    "demo.phase2Eyebrow": "Selkeä suunnittelu",
    "demo.phase2Title": "Vain vahvistetut paikat",
    "demo.phase2Text": "Vantastika suodattaa kohinan ja tekee suunnittelusta selkeää, nopeaa ja turvallista.",
    "demo.phase3Eyebrow": "Täydellinen roadtrip",
    "demo.phase3Title": "Reittisi, valmiina",
    "demo.phase3Text": "Näe koko reitti, tärkeimmät pysähdyspaikat ja matka, joka tuntuu valmiilta jo ennen moottorin käynnistämistä.",
    "demo.stepStart": "Aloita matkasi",
    "demo.stepVerified": "Vahvistetut pysähdyspaikat",
    "demo.stepArrive": "Saavu määränpäähän",
    "demo.planSmart": "Suunnittele älykkäästi",
    "demo.budget": "Budjetti",
    "demo.budgetUsed": "käytetty",
    "demo.roadtripPreview": "Roadtrip-esikatselu",
    "service.title": "Huolto & Hätätilanne",
    "service.workshop": "Työpaja",
    "service.gasStation": "Bensa-asema",
    "service.water": "Vesi",
    "service.charging": "Lataus",
    "service.waste": "Jäte",
    "service.hospital": "Sairaala",
    "service.police": "Poliisi",
    "service.grocery": "Ruokakauppa",
    "service.emergency": "Hätätilanne",
    "service.exitEmergency": "Poistu SOS",
    "service.emergencyMode": "Hätätilanne-tila",
    "service.callEmergency": "Soita",
    "service.emergencyNote": "112 toimii kaikissa EU-maissa",
    "service.locating": "Paikannetaan sijaintiasi...",
    "service.locationNeeded": "Sijainti tarvitaan",
    "service.enableLocation": "Salli sijainnin käyttö oikeuksien antaminen löytääksesi palveluita läheltäsi",
    "service.retry": "Yritä uudelleen",
    "service.searching": "Etsitään läheltä...",
    "service.noResults": "Lähistöllä ei löytynyt palveluita",
    "service.navigate": "Navigoi",
    "service.open": "Avoinna",
    "service.closed": "Suljettu",
    "service.fetchError": "Palveluita ei voitu ladata",
    "service.useGPS": "Käytä GPS:ää",
    "service.searchPlaceholder": "Etsi mitä tahansa... Tesla, Hymer, retkeilyvarusteet, apteekki",
    "service.manual": "Manuaalinen",
    "service.searchResultsFor": "Tulokset kohteelle",
    "social.places": "Paikat",
    "social.tripsPlanned": "Suunnitellut matkat",
    "social.members": "Jäsenet",
    "social.reviews": "Arvostelut",
    "social.popularRoutes": "Suositut reitit muilta matkailijoilta",
    "social.traveler": "Matkailija",
    "social.stops": "pysähdystä",
    "error.somethingWrong": "Jotain meni vikaan",
    "error.autoRepairFailed": "Yritimme korjata automaattisesti, mutta se ei onnistunut. Yritä lataamalla sivu uudelleen.",
    "error.tryAgain": "Yritä uudelleen",
    "error.reloadPage": "Lataa sivu uudelleen",
    "a11y.decreaseText": "Pienennä tekstin kokoa",
    "a11y.increaseText": "Suurenna tekstin kokoa",
    "a11y.decreaseTooltip": "Pienempi teksti",
    "a11y.increaseTooltip": "Suurempi teksti",
    "aria.close": "Sulje",
    "aria.openVannie": "Avaa Vannie",
    "aria.previous": "Edellinen",
    "aria.next": "Seuraava",
    "aria.openMenu": "Avaa valikko",
    "aria.closeMenu": "Sulje valikko",
    "aria.step": "Vaihe",
    "onboarding.step": "Vaihe {current} / {total}",
    "onboarding.back": "Takaisin",
    "onboarding.skip": "Ohita",
    "onboarding.planTrip": "Suunnittele matkasi",
    "onboarding.planTripDesc": "Kerro meille minne haluat mennä, niin autamme sinua rakentamaan matkasi - reitin, pysähdykset ja budjetin yhdessä paikassa.",
    "onboarding.createFirstTrip": "Rakenna ensimmäinen matkani",
    "onboarding.findPlaces": "Etsi paikkoja kartalta",
    "onboarding.findPlacesDesc": "Leirintäalueet, nähtävyydet ja ravintolat - lisää pysähdyksiä suoraan kartalta matkallesi.",
    "onboarding.searchCampings": "Tutki karttaa",
    "onboarding.budgetChecklist": "Seuraa budjettia ja kuluja",
    "onboarding.budgetChecklistDesc": "Kirjaa kulutuksesi, skannaa kuitit ja pidä kirjaa matkabudjetistasi - kaikki yhdessä paikassa.",
    "onboarding.createTrip": "Aloita budjettini",
    "onboarding.askVannie": "Tapaa Vannie",
    "onboarding.askVannieDesc": "Henkilökohtainen matka-apulaisesi. Saat vinkkejä, reittiehdotuksia ja paikallisia sääntöjä - ilmaiseksi.",
    "onboarding.doneStart": "Mennään!",
    "compare.title": "Vertaa leirintäalueita",
    "compare.subtitle": "Valitse korkeintaan {max} leirintäaluetta vertailtavaksi rinnakkain.",
    "compare.add": "Lisää",
    "compare.searchPlaceholder": "Etsi leirintäaluetta...",
    "compare.property": "Ominaisuus",
    "compare.rating": "Arvosana",
    "compare.pricePerNight": "Hinta/yö",
    "compare.free": "Ilmainen",
    "compare.type": "Tyyppi",
    "compare.addMore": "Lisää vähintään yksi leirintäalue vertailtavaksi.",
    "compare.selectCampings": "Valitse leirintäalueet vertailtavaksi",
    "compare.clickAdd": "Napsauta \"Lisää\" yllä aloittaaksesi.",
    "compare.electricity": "Sähkö",
    "compare.freshWater": "Makea vesi",
    "compare.shower": "Suihku",
    "compare.pets": "Lemmikit",
    "compare.greyWater": "Harmaavesi",
    "compare.toiletCassette": "WC-kasetti",
    "compare.wc": "WC",
    "compare.openAllYear": "Avoinna ympäri vuoden",
    "compare.familyFriendly": "Perheystävällinen",
    "compare.largeMotorhomes": "Isot matkailuautot",
    "compare.nearWater": "Veden lähellä",
    "compare.spaWellness": "Kylpylä/Wellness",
    "friends.nearby": "Matkaseuralaisia lähellä",
    "friends.sharingOn": "Jako päällä",
    "friends.sharingOff": "Jako pois päältä",
    "friends.enableSharing": "Ota sijainnin jakaminen käyttöön nähdäksesi ihmiset, joiden kanssa jaat matkoja",
    "friends.onlyCollaborators": "Vain ne, joiden kanssa teet yhteistyötä matkalla, näkevät sijaintisi",
    "friends.noOneSharing": "Tällä hetkellä kukaan matkaseuralaisistasi ei jaa sijaintiaan",
    "friends.anonymous": "Anonyymi",
    "friends.away": "poissa",
    "friends.unknownDistance": "Etäisyys tuntematon",
    "spot.loginToNavigate": "Kirjaudu sisään navigoidaksesi",
    "spot.navigateHere": "Navigoi tänne",
    "incident.title": "Ilmoita tapaturmasta",
    "incident.button": "Ilmoita tapaturmasta",
    "incident.stepOf": "Vaihe {current} / {total}",
    "incident.saving": "Tallennetaan...",
    "incident.step1": "Tapaturman tyyppi",
    "incident.step2": "Aika ja sijainti",
    "incident.step3": "Ajoneuvon tiedot",
    "incident.step4": "Mitä tapahtui",
    "incident.step5": "Toinen osapuoli",
    "incident.step6": "Yhteenveto & PDF",
    "incident.type.collision": "Törmäys",
    "incident.type.theft": "Varkaus",
    "incident.type.vandalism": "Vandalismi",
    "incident.type.breakdown": "Rikkoontuminen",
    "incident.type.weather": "Säävaurio",
    "incident.type.other": "Muu",
    "incident.date": "Päivämäärä",
    "incident.time": "Aika",
    "incident.location": "Sijainti",
    "incident.locationPlaceholder": "Osoite tai paikan nimi",
    "incident.locationHint": "Ole mahdollisimman tarkka – katu, kaupunki, maa",
    "incident.vehicleType": "Ajoneuvon tyyppi",
    "incident.vehicleTypePlaceholder": "esim. Matkailuauto, Campervan, Auto",
    "incident.registration": "Rekisterinumero",
    "incident.vehicleDesc": "Ajoneuvon kuvaus",
    "incident.vehicleDescPlaceholder": "Väri, merkki, malli, vuosi",
    "incident.whatHappened": "Mitä tapahtui?",
    "incident.descriptionPlaceholder": "Kuvaile tapaturma selkeästi ja rauhallisesti",
    "incident.additionalNotes": "Lisähuomautuksia",
    "incident.notesPlaceholder": "Silminnäkijät, sää, tieolosuhteet...",
    "incident.otherPartyHint": "Täytä, jos toinen osapuoli oli mukana. Ohita, jos ei koske.",
    "incident.otherName": "Nimi",
    "incident.otherPhone": "Puhelinnumero",
    "incident.otherVehicle": "Ajoneuvo",
    "incident.otherReg": "Rekisterinumero",
    "incident.otherInsurance": "Vakuutusyhtiö",
    "incident.incidentType": "Tapaturman tyyppi",
    "incident.summary": "Tapaturmaraportin yhteenveto",
    "incident.downloadPdf": "Tallenna & Lataa PDF",
    "incident.saveOnly": "Tallenna ilman PDF:ää",
    "incident.wizardTitle": "Vahinkoraportti",
    "incident.step0.title": "Tärkeää tietoa",
    "incident.step0.heading": "Tämä on vahinkoraportti — ei vakuutusilmoitus",
    "incident.step0.body1": "Tällä työkalulla luot strukturoidun raportin, joka auttaa sinua keräämään oikeat tiedot tapaturman yhteydessä.",
    "incident.step0.body2": "Olet itse vastuussa raportin tarkistamisesta ja lähettämisestä vakuutusyhtiöllesi.",
    "incident.step0.gdprTitle": "Tietosuoja (GDPR)",
    "incident.step0.gdprBody1": "Kaikki tiedot tallennetaan turvallisesti tilillesi. Tietoja ei jaeta automaattisesti kolmansille osapuolille.",
    "incident.step0.gdprBody2": "Voit milloin tahansa poistaa tapahtumaraportin ja kaikki siihen liittyvät tiedot.",
    "incident.step0.accept": "Ymmärrän — aloita",
    "incident.step1.title": "Tapahtuman tyyppi",
    "incident.step2.title": "Päivämäärä, aika ja paikka",
    "incident.step3.title": "Ajoneuvosi",
    "incident.step4.title": "Vastapuolen ajoneuvo",
    "incident.step5.title": "Kuljettajat ja omistajat",
    "incident.step6.title": "Vakuutus",
    "incident.step7.title": "Vahingon kuvaus",
    "incident.step8.title": "Kuvat",
    "incident.step9.title": "Yhteenveto",
    "incident.stepOf2": "Vaihe {current}/{total}",
    "incident.selectCountry": "Valitse maa...",
    "incident.searchCountry": "Hae maata...",
    "incident.searchInsurance": "Hae tai kirjoita manuaalisesti...",
    "incident.regNum": "Rekisterinumero",
    "incident.regCountry": "Rekisteröintimaa",
    "incident.makeModel": "Merkki / Malli",
    "incident.driverIsOwner": "Kuljettaja = Omistaja",
    "incident.yes": "Kyllä",
    "incident.no": "Ei",
    "incident.ownerName": "Omistajan nimi",
    "incident.ownerPhone": "Omistajan puhelin",
    "incident.ownerEmail": "Omistajan sähköposti",
    "incident.otherDriverName": "Kuljettajan nimi",
    "incident.otherDriverPhone": "Kuljettajan puhelin",
    "incident.yourInsurance": "Vakuutuksesi",
    "incident.opponentInsurance": "Vastapuolen vakuutus",
    "incident.insuranceCompany": "Vakuutusyhtiö",
    "incident.policyNum": "Vakuutusnumero",
    "incident.policyNumHint": "Löytyy vakuutuskirjasta",
    "incident.damageWhat": "Mitä tapahtui?",
    "incident.damageWhatPlaceholder": "Kuvaile mitä tapahtui",
    "incident.damageHow": "Miten se tapahtui?",
    "incident.damageHowPlaceholder": "Kuvaile olosuhteet, nopeus, näkyvyys jne.",
    "incident.damageParts": "Mitkä vahingot syntyivät?",
    "incident.damagePartsPlaceholder": "Mitkä ajoneuvon osat vaurioituivat?",
    "incident.damageOther": "Muut yksityiskohdat",
    "incident.damageOtherPlaceholder": "Todistajat, sää, tieolosuhteet...",
    "incident.notesLabel": "Muistiinpanot",
    "incident.notesPlaceholder2": "Muut muistiinpanot",
    "incident.imagesOptional": "Kuvat ovat valinnaisia mutta suositeltavia",
    "incident.imagesHint": "Kuvat voivat dokumentoida vahingot ja helpottaa käsittelyä vakuutusyhtiössä.",
    "incident.takePhoto": "Ota kuva",
    "incident.uploadBtn": "Lataa",
    "incident.uploading": "Ladataan...",
    "incident.saveDraftFirst": "Raportti on tallennettava ennen kuin voit ladata kuvia.",
    "incident.saveDraftBtn": "Tallenna luonnos ensin",
    "incident.imagesSecure": "Kuvat tallennetaan turvallisesti tilillesi eikä niitä jaeta automaattisesti. Voit poistaa ne milloin tahansa. Vain JPG ja PNG (maks. 5 MB).",
    "incident.notFilled": "Ei täytetty",
    "incident.notSpecified": "Ei määritelty",
    "incident.notDescribed": "Ei kuvattu",
    "incident.change": "Muokkaa",
    "incident.summaryTitle": "Yhteenveto",
    "incident.disclaimerShort": "Olet itse vastuussa raportin tarkistamisesta ja lähettämisestä vakuutusyhtiöllesi.",
    "incident.generatePdf": "Luodaan PDF...",
    "incident.savePdf": "Tallenna ja lataa PDF",
    "incident.saveNoPdf": "Tallenna ilman PDF:ää",
    "incident.reportSaved": "Tapahtumaraportti tallennettu",
    "incident.couldNotSave": "Raporttia ei voitu tallentaa",
    "incident.invalidFile": "Virheellinen tiedosto",
    "incident.uploadFailed": "Lataus epäonnistui",
    "incident.imagesAttached": "{count} kuva(a) liitetty",
    "incident.timeAndPlace": "Aika ja paikka",
    "incident.yourVehicle": "Ajoneuvosi",
    "incident.opponent": "Vastapuoli",
    "incident.insurance": "Vakuutus",
    "incident.damages": "Vahingot",
    "incident.images": "Kuvat",
    "incident.val.selectType": "Valitse tapahtuman tyyppi",
    "incident.val.dateRequired": "Päivämäärä vaaditaan",
    "incident.val.locationRequired": "Paikka vaaditaan",
    "incident.val.regRequired": "Rekisterinumero vaaditaan",
    "incident.val.regCountryRequired": "Rekisteröintimaa vaaditaan",
    "incident.val.vehicleTypeRequired": "Ajoneuvotyyppi vaaditaan",
    "incident.val.ownerNameRequired": "Omistajan nimi vaaditaan",
    "incident.val.describeWhat": "Kuvaile mitä tapahtui",
    "incident.val.describeHow": "Kuvaile miten se tapahtui",
    "incident.val.typeMissing": "Tapahtuman tyyppi puuttuu",
    "incident.val.regMissing": "Rekisterinumero puuttuu",
    "incident.val.damageMissing": "Vahingon kuvaus puuttuu",
    "incident.opponentVehicle": "Vastapuolen ajoneuvo",
    "incident.yourDriver": "Omat tietosi",
    "incident.opponentDriver": "Vastapuolen tiedot",
    "incident.pdf.title": "Vahinkoraportti",
    "incident.pdf.disclaimer": "Tämä asiakirja on vahinkoraportti eikä virallinen vakuutusilmoitus. Olet itse vastuussa raportin tarkistamisesta ja lähettämisestä vakuutusyhtiöllesi.",
    "incident.pdf.generated": "Luotu",
    "incident.pdf.trip": "Matka",
    "incident.pdf.type": "Tyyppi",
    "incident.pdf.date": "Päivämäärä",
    "incident.pdf.time": "Aika",
    "incident.pdf.location": "Paikka",
    "incident.pdf.vehicleType": "Ajoneuvotyyppi",
    "incident.pdf.regNum": "Rekisterinumero",
    "incident.pdf.regCountry": "Rekisteröintimaa",
    "incident.pdf.makeModel": "Merkki / Malli",
    "incident.pdf.driverIsOwner": "Kuljettaja = Omistaja",
    "incident.pdf.ownerName": "Omistajan nimi",
    "incident.pdf.ownerPhone": "Omistajan puhelin",
    "incident.pdf.ownerEmail": "Omistajan sähköposti",
    "incident.pdf.driver": "Kuljettaja",
    "incident.pdf.phone": "Puhelin",
    "incident.pdf.insuranceCompany": "Vakuutusyhtiö",
    "incident.pdf.policyNum": "Vakuutusnumero",
    "incident.pdf.whatHappened": "Mitä tapahtui",
    "incident.pdf.howHappened": "Miten se tapahtui",
    "incident.pdf.damagedParts": "Vaurioituneet osat",
    "incident.pdf.otherDetails": "Muut yksityiskohdat",
    "incident.pdf.notes": "Muistiinpanot",
    "incident.pdf.images": "Kuvat",
    "incident.pdf.imagesNote": "{count} kuva(a) liitetty. Kuvat ovat saatavilla Vantastika-tililläsi.",
    "incident.pdf.footer": "Tämä asiakirja on vahinkoraportti, joka on luotu Vantastikalla, eikä se ole virallinen vakuutusilmoitus.",
    "incident.pdf.footerReview": "Olet itse vastuussa tietojen tarkistamisesta ja niiden lähettämisestä vakuutusyhtiöllesi.",
    "incident.pdf.yourVehicle": "Ajoneuvosi",
    "incident.pdf.opponentVehicle": "Vastapuolen ajoneuvo",
    "incident.pdf.damageDesc": "Vahingon kuvaus",
    "incident.pdf.incident": "Tapahtuma",
    "notFound.title": "Sivua ei löytynyt",
    "notFound.description": "Etsimääsi sivua ei näytä olevan olemassa. Se on ehkä siirretty tai poistettu.",
    "notFound.home": "Etusivulle",
    "notFound.back": "Takaisin",
    "incident.generating": "Luodaan PDF...",
    "incident.saved": "Tapaturmaraportti tallennettu",
    "incident.saveFailed": "Raportin tallennus epäonnistui",
    "incident.back": "Takaisin",
    "incident.next": "Seuraava",
    "incident.cancel": "Peruuta",
    "incident.pdfTitle": "Tapaturmaraportti",
    "incident.pdfGenerated": "Luotu",
    "incident.pdfDisclaimer": "Tämä on henkilökohtainen tallenne. Se ei ole virallinen vakuutusvaatimuslomake.",
    "incident.drafts": "Luonnosraportit",
    "incident.noDrafts": "Ei luonnosraportteja",
    "incident.continueDraft": "Jatka",
    "incident.noReports": "Ei tapaturmaraportteja vielä",
    "incident.deleted": "Raportti poistettu",
    "incident.draft": "Luonnos",
    "incident.completed": "Valmis",
    "incident.myReports": "Omat tapaturmaraportit",
    "incident.myReportsSubtitle": "Tallennetut ja luonnosraporttisi",
    "incident.vtype.motorhome": "Matkailuauto",
    "incident.vtype.campervan": "Campervan",
    "incident.vtype.car": "Auto",
    "incident.vtype.motorcycle": "Moottoripyörä",
    "incident.vtype.trailer": "Peräkärry",
    "incident.vtype.other": "Muu",
    "quick.hello": "Hei {name}! 👋",
    "quick.whatToDo": "Mitä haluaisit tehdä tänään?",
    "quick.continueTrip": "Jatka matkaa",
    "quick.activeTrip": "Aktiivinen matka",
    "quick.newTrip": "Uusi matka",
    "quick.planNext": "Suunnittele seuraava seikkailusi",
    "quick.upcomingTrip": "Tuleva matka",
    "quick.findCamping": "Etsi leirintäaluetta",
    "quick.searchFilter": "Hae & suodata",
    "quick.favorites": "Suosikit",
    "quick.saved": "{count} tallennettu",
    "quick.community": "Yhteisö",
    "quick.tipsDiscussions": "Vinkit & keskustelut",
    "gdpr.title": "Yksityisyytesi on meille tärkeä",
    "gdpr.description": "Tämä sivusto käyttää vain välttämättömiä ja toiminnallisia evästeitä (tunnistautuminen, kieliasetus, maan asetus ja evästeiden hyväksyntä). Seurant-, mainonta- tai kolmannen osapuolen analytiikkaevästeitä ei käytetä.",
    "gdpr.readPolicy": "Lue tietosuojakäytäntömme",
    "gdpr.accept": "Selvä",
    "gdpr.decline": "Selvä",
    "install.title": "Asenna Vantastika",
    "install.description": "Lisää aloitusnäyttöön nopeaa käyttöä varten – toimii kuin sovellus!",
    "install.installButton": "Asenna sovellus",
    "install.showHow": "Näytä miten",
    "install.understood": "Selvä",
    "install.iosSteps": "Noudata näitä ohjeita Safarissa:",
    "install.iosTapShare": "Napauta",
    "install.iosShareLocation": "Safarissa alareunassa",
    "install.iosAddHome": "Valitse \"Lisää Home Screeniin\"",
    "install.iosScrollDown": "Selaa alaspäin valikossa tarvittaessa",
    "install.iosTapAdd": "Napauta \"Lisää\"",
    "install.androidSteps": "Noudata näitä ohjeita:",
    "install.androidTapMenu": "Napauta",
    "install.androidMenuLocation": "Oikeassa yläkulmassa selaimessa",
    "install.androidInstallApp": "Valitse \"Asenna sovellus\"",
    "install.androidOrAddHome": "Tai \"Lisää Home Screeniin\"",
    "home.trustFree": "100% ilmainen",
    "myTrips.title": "Omat matkani",
    "myTrips.loginTitle": "Kirjaudu nähdäksesi matkasi",
    "myTrips.loginDesc": "Tallennat matkasi ilmestyvät tänne kirjautumisen jälkeen.",
    "myTrips.loginButton": "Kirjaudu sisään",
    "myTrips.pausedStatus": "Keskeytetty",
    "myTrips.activeStatus": "Aktiivinen matka",
    "myTrips.pausedSubtitle": "Matkasi on keskeytetty",
    "myTrips.activeSubtitle": "Sinulla on aktiivinen matka",
    "myTrips.tripsCount": "{count} matkaa tallennettu",
    "myTrips.tripCount": "{count} matka tallennettu",
    "myTrips.newTrip": "Uusi matka",
    "myTrips.resumeTrip": "Jatka matkaa",
    "myTrips.continueTrip": "Jatka matkaa",
    "myTrips.resume": "Jatka",
    "myTrips.pause": "Keskeytä",
    "myTrips.endTrip": "Päätä matka",
    "myTrips.end": "Lopeta",
    "myTrips.progress": "{pct}% valmis",
    "myTrips.stops": "{count} pysäkkiä",
    "myTrips.nextStop": "Seuraava pysäkki:",
    "myTrips.openActiveTrip": "Avaa aktiivinen matka",
    "myTrips.noTrips": "Ei tallennettuja matkoja vielä",
    "myTrips.noTripsDesc": "Luo ensimmäinen matkasi suunnittelijassa, niin se ilmestyy tänne.",
    "myTrips.planFirst": "Suunnittele ensimmäinen matkasi",
    "myTrips.upcomingTrips": "Tulevat matkat",
    "myTrips.sharedWithMe": "Jaettu kanssani",
    "myTrips.invitedBy": "Kutsunut",
    "myTrips.completedTrips": "Valmistuneet matkat",
    "myTrips.startTrip": "Aloita matka",
    "myTrips.budget": "Budjetti",
    "myTrips.checklist": "Tarkistuslista",
    "myTrips.stoppedPromptTitle": "Näytät pysähtyneen",
    "myTrips.stoppedPromptDesc": "Haluatko jatkaa vai pitää tauon?",
    "myTrips.arrivedAt": "Olet perillä kohteessa {name}!",
    "myTrips.arrivedDesc": "Kohde saavutettu — hyvin tehty!",
    "myTrips.stopRemoved": "Pysäkki poistettu",
    "myTrips.close": "Sulje",
    "myTrips.alreadyActiveTrip": "Sinulla on jo aktiivinen matka",
    "myTrips.endActiveFirst": "Lopeta se ensin.",
    "myTrips.tripStarted": "Matka aloitettu!",
    "myTrips.tripStartedDesc": "GPS-seuranta ja päiväkirja ovat nyt aktiivisia.",
    "myTrips.tripPaused": "Matka keskeytetty",
    "myTrips.tripPausedDesc": "Voit jatkaa milloin tahansa.",
    "myTrips.tripResumed": "Matka jatkuu!",
    "myTrips.tripResumedDesc": "GPS-seuranta on jälleen aktiivinen.",
    "myTrips.tripEnded": "Matka päättynyt!",
    "myTrips.tripEndedDesc": "Matkasi on tallennettu.",
    "myTrips.tripReactivated": "Matka uudelleenaktivoitu!",
    "myTrips.revertedToPlanned": "Palautettu suunnitelluksi",
    "myTrips.revertedToPlannedDesc": "Matka on takaisin suunniteltuna. Kaikki tiedot ovat tallessa.",
    "myTrips.missingStops": "Matkalta puuttuu pysäkkejä",
    "myTrips.missingStopsDesc": "Lisää vähintään yksi pysäkki ennen aloittamista.",
    "myTrips.missingTitle": "Matkalta puuttuu otsikko",
    "myTrips.missingTitleDesc": "Anna matkalle nimi ennen aloittamista.",
    "trip.stopAddedFromMap": "Pysäkki lisätty kartalta",
    "trip.stopPositionUpdated": "Pysäkin sijainti päivitetty",
    "trip.routeOptimizedDesc": "Pysäkit on järjestetty lyhimmän ajomatkan mukaan.",
    "places.verificationThanks": "Kiitos vahvistuksesta!",
    "places.verificationThanksDesc": "Autat meitä pitämään tiedot oikeina.",
    "friends.requestAccepted": "Kaveripyyntö hyväksytty!",
    "friends.requestSent": "Kaveripyyntö lähetetty!",
    "myTrips.checklistReminder": "Muista käydä tarkistuslista läpi ennen lähtöä",
    "myTrips.today": "Tänään",
    "myTrips.tomorrow": "Huomenna",
    "myTrips.inDays": "{days} päivän kuluttua",
    "myTrips.nextTrip": "Seuraava matka",
    "myTrips.drafts": "Luonnokset",
    "myTrips.readyToStart": "Valmis aloitettavaksi",
    "myTrips.continuePlanning": "Jatka suunnittelu",
    "myTrips.editTrip": "Muokkaa",
    "myTrips.openTrip": "Avaa matka",
    "myTrips.viewSummary": "Näytä yhteenveto",
    "myTrips.duplicate": "Monista",
    "myTrips.archive": "Arkistoi",
    "myTrips.activeTrips": "Aktiivinen matka",
    "myTrips.showTrip": "Näytä matka",
    "myTrips.liveView": "Live-näkymä",
    "myTrips.details": "Tiedot",
    "myTrips.revertToPlanned": "Palauta suunniteltuun",
    "myTrips.reactivateTrip": "Aktivoi matka uudelleen",
    "myTrips.loadingLive": "Ladataan live-näkymää...",
    "myTrips.showCompleted": "Näytä valmistuneet matkat",
    "myTrips.hideCompleted": "Piilota valmistuneet matkat",
    "nav.menuApp": "Sovellus",
    "nav.menuOther": "Muu",
    "nav.installApp": "Asenna sovellus",
    "tools.title": "Matkatyökalut",
    "tools.checklist": "Tarkistuslista",
    "tools.budget": "Budjetti",
    "tools.incident": "Tapahtuma",
    "tripSummary.distance": "Matka",
    "tripSummary.driveTime": "Ajoaika",
    "tripSummary.days": "Päivät",
    "tripSummary.stops": "Pysäkit",
    "tripSummary.fuel": "Polttoaine",
    "tripSummary.budget": "Budjetti",
    "tripSummary.route": "Reitti",
    "tripSummary.stop": "Pysäkki {n}",
    "tripGuideLink.title": "Opas: {country}",
    "tripGuideLink.camping": "Leirintä",
    "tripGuideLink.driving": "Ajaminen",
    "tripGuideLink.safety": "Turvallisuus",
    "arrival.continueToNext": "Jatka kohteeseen {name}",
    "stats.trips": "Matkat",
    "stats.stops": "Pysäkit",
    "stats.nights": "Yöt",
    "stats.countries": "Maat",
    "stats.reviews": "Arvostelut",
    "stats.friends": "Ystävät",
    "badge.travel": "Matkailu",
    "badge.exploration": "Tutkiminen",
    "badge.community": "Yhteisö",
    "badge.milestone": "Virstanpylväät",
    "badge.creative": "Luova",
    "badge.engagement": "Sitoutuminen",
    "badge.special": "Erityinen",
    "checklist.title": "Tarkistuslistat",
    "checklist.subtitle": "Matkaan liittyvät ja erilliset tarkistuslistat",
    "checklist.loginRequired": "Kirjaudu sisään nähdäksesi tarkistuslistasi",
    "checklist.loginHint": "Tarkistuslistasi ilmestyvät tänne kirjautumisen jälkeen.",
    "checklist.backToAll": "Takaisin kaikkiin tarkistuslistoihin",
    "checklist.trip": "Matka",
    "checklist.general": "Yleinen pakkauslista",
    "checklist.generalSection": "Erilliset tarkistuslistat",
    "checklist.generalPacklist": "Yleinen pakkauslista",
    "checklist.done": "valmis",
    "checklist.noChecklistYet": "Ei tarkistuslistaa vielä – napauta luodaksesi",
    "checklist.activeTrips": "Aktiiviset ja tulevat matkat",
    "checklist.completedTrips": "Valmistuneet matkat",
    "checklist.noTrips": "Ei matkoja vielä. Luo matka linkittääksesi tarkistuslistan.",
    "checklist.planTrip": "Suunnittele matka",
    "checklist.allDone": "Kaikki valmiina",
    "checklist.remaining": "{count} kohdetta jäljellä",
    "checklist.progress": "{done} / {total} valmis",
    "checklist.addCustom": "Mukautettu kohde",
    "checklist.addTemplate": "Lisää malli",
    "checklist.reset": "Nollaa",
    "checklist.created": "Tarkistuslista luotu! ✅",
    "checklist.createFailed": "Tarkistuslistan luonti epäonnistui",
    "checklist.resetDone": "Tarkistuslista on nollattu",
    "checklist.resetFailed": "Nollaus epäonnistui",
    "checklist.itemAdded": "Kohde lisätty!",
    "checklist.itemAddFailed": "Kohteen lisääminen epäonnistui",
    "checklist.inputPlaceholder": "Kuvaa mitä haluat lisätä...",
    "checklist.add": "Lisää",
    "checklist.cancel": "Peruuta",
    "checklist.noChecklist": "Ei tarkistuslistaa kohteelle {type}.",
    "checklist.selectTemplate": "Valitse malli:",
    "checklist.or": "tai",
    "checklist.aiPacklist": "AI-räätälöity pakkauslista",
    "checklist.standardChecklist": "Standardi tarkistuslista",
    "checklist.aiAdapted": "AI-lista on mukautettu kohteelle {dest}",
    "checklist.andSeason": " ja kausi",
    "checklist.aiCreated": "🤖 AI -pakkauslista luotu!",
    "checklist.allExist": "Kaikki kohteet ovat jo luettelossasi",
    "checklist.newItemsAdded": "{count} uutta kohdetta lisätty!",
    "checklist.addExtraPacking": "Lisää ylimääräisiä pakattavia:",
    "float.navigate": "Navigoi",
    "float.save": "Tallenna",
    "float.saving": "Tallennetaan...",
    "profile.save": "Tallenna",
    "profile.saving": "Tallennetaan...",
    "shared.saveToMyTrips": "Tallenna omiin matkoihini",
    "shared.saving": "Tallennetaan...",
    "shared.privacyNote": "Aloitus- ja päätepisteet on poistettu yksityisyyden suojaamiseksi. Lisää oma aloitussijaintisi.",
    "shared.addYourStart": "Lisää aloitussijaintisi",
    "shared.templateNote": "Tämä matka jaetaan inspiraatioksi. Kohteet ja pysähdykset sisältyvät — lisää omat alku- ja loppupisteesi.",
    "trip.editOutbound": "Meno",
    "trip.editReturn": "Paluu",
    "trip.editMainInfo": "Matkan tiedot",
    "vehicle.motorhome": "Matkailuauto",
    "vehicle.campervan": "Matkailuperävaunu",
    "vehicle.caravan": "Asuntovaunu",
    "vehicle.car": "Auto",
    "vehicle.motorcycle": "Moottoripyörä",
    "vehicle.selectType": "Ajoneuvon tyyppi",
    "vehicle.nameLabel": "Ajoneuvon nimi (valinnainen)",
    "vehicle.namePlaceholder": "esim. Fiat Ducato 2020",
    "vehicle.dimensions": "Ajoneuvon mitat",
    "vehicle.dimensionsExplain": "Käytetään älykkäämpään reittisuunnitteluun — vältä matalia siltoja, kapeita väyliä ja painorajoituksia.",
    "vehicle.height": "Korkeus",
    "vehicle.width": "Leveys",
    "vehicle.length": "Pituus",
    "vehicle.weight": "Paino",
    "vehicle.disclaimer": "Ajoneuvon mittoihin perustuvat reittivaroitukset ovat neuvoa-antavia. Tarkista rajoitukset aina paikan päällä. Rajoitustietojen kattavuutta laajennetaan jatkuvasti.",
    "vehicle.profileComplete": "Ajoneuvoprofiili valmis — valmis älykkääseen reititykseen",
    "vehicle.save": "Tallenna ajoneuvoprofiili",
    "vehicle.saving": "Tallennetaan...",
    "vehicle.saved": "Ajoneuvoprofiili tallennettu!",
    "vehicle.saveFailed": "Ajoneuvoprofiilin tallentaminen epäonnistui",
    "vehicle.profileTitle": "Ajoneuvoprofiili",
    "vehicle.profileSubtitle": "Älykkäämpään ja turvallisempaan reittisuunnitteluun",
    "myTrips.viewRoute": "Näytä reitti",
    "myTrips.playTrip": "Toista matka",
    "myTrips.needMoreStops": "Lisää vähintään 2 pysähdystä koordinaateilla toistaaksesi matka-animaation.",
    "tripReplay.stops": "pysähdystä",
    "tripReplay.days": "päivää",
    "tripReplay.completed": "Matka valmis!",
    "tripReplay.replay": "Toista uudelleen",
    "tripReplay.share": "Jaa",
    "routeOverview.title": "Reittikatsaus",
    "routeOverview.totalDistance": "Kokonaismatka",
    "routeOverview.driveTime": "Ajoaika",
    "routeOverview.stops": "Pysähdykset",
    "routeOverview.overnights": "Yöpymiset",
    "routeOverview.fuelEstimate": "Arvioitu polttoaineenkulutus",
    "routeOverview.itinerary": "Matkasuunnitelma",
    "routeOverview.nights": "yötä",
    "routeOverview.start": "Alku",
    "routeOverview.destination": "Kohde",
    "routeOverview.visit": "Vierailu",
    "why.title": "Miksi valita Vantastika?",
    "why.subtitle": "Kaikki mitä tarvitset matkallesi — täysin ilmaiseksi, ei korttia vaadita.",
    "why.smartPlanning": "Älykäs matkan suunnittelu",
    "why.smartPlanningDesc": "Luo reittejä pysähdyksineen, polttoainelaskelmineen ja aikataulutuksineen yhdessä prosessissa.",
    "why.aiAssistant": "Tekoälymatkaopas",
    "why.aiAssistantDesc": "Kuvaile unelmiesi matka omin sanoin — tekoälymme rakentaa koko suunnitelman puolestasi.",
    "why.campingsEurope": "Leirintäalueet Euroopassa",
    "why.campingsEuropeDesc": "Löydä, vertaa ja suodata leirintäalueita ja paikkoja 21 maassa.",
    "why.travelGuides": "Inspiroivat matkaoppaat",
    "why.travelGuidesDesc": "Valmiit teemareitit historian, pysähdysten ja vinkkien kera — valmiina ajettaviksi.",
    "why.budget": "Budjetti & kulut",
    "why.budgetDesc": "Seuraa kustannuksia, polttoainetta ja päiväbudjettia matkan aikana.",
    "why.shareFriends": "Jaa ystävien kanssa",
    "why.shareFriendsDesc": "Kutsu matkakumppaneita ja suunnitelkaa yhdessä reaaliajassa.",
    "why.checklists": "Älykkäät tarkistuslistat",
    "why.checklistsDesc": "Mukautetut pakkauslistat ajoneuvotyypin ja matkan mukaan.",
    "why.sos": "SOS- ja turvapakkaus",
    "why.sosDesc": "Hätätilanteen jakaminen, tapausraportit ja nopea pääsy hätänumeroihin.",
    "why.badges": "Tasot & merkit",
    "why.badgesDesc": "Kerää saavutuksia, nouse tasoilla ja haasta itsesi matkailijana.",
    "why.cta": "Luo ilmainen tili",
    "why.ctaSub": "Ei korttia tarvita · Valmis 30 sekunnissa",
    "games.title": "Roadtrip-pelit",
    "games.subtitle": "Tee matkasta hauskempi 🎮",
    "games.played": "Pelattu",
    "games.totalPoints": "Pisteitä yhteensä",
    "games.challengeFriend": "Haasta kaveri",
    "games.selectCountry": "Valitse maa",
    "games.loginPrompt": "Kirjaudu sisään tallentaaksesi pelihistorian",
    "games.loginPromptSub": "Pistetuloksesi, suosikkisi ja sijoituksesi tulostaulussa tallennetaan",
    "games.onTrip": "Matka käynnissä – pelit sovitettu maahan {country}",
    "games.duringTrip": "Matkan aikana",
    "games.duringTripSub": "Täydellinen matkustajille – {n} pelityyppiä",
    "games.atStop": "Pysähdyksillä & tauoilla",
    "games.atStopSub": "Kun pysähdyt tai tutustut – {n} pelityyppiä",
    "games.camping": "Leirintä & iltapelit",
    "games.campingSub": "Pidempikestoiset pelit iltoihin ja leirielämään – {n} pelityyppiä",
    "games.tabs.games": "Pelit",
    "games.tabs.community": "Yhteisö",
    "games.tabs.challenges": "Haasteet",
    "games.tabs.badges": "Merkit",
    "games.tabs.history": "Historia",
    "games.favorites": "Suosikkipelit",
    "games.favoritesEmpty": "Lisää peli suosikkeihin sydämellä ♥️",
    "games.recommended": "Suositellut",
    "game.quiz.name": "Roadtrip-tietovisa",
    "game.quiz.desc": "Testaa tietämyksesi maista, teistä ja leirintäelämästä",
    "game.bingo.name": "Roadtrip-bingo",
    "game.bingo.desc": "Etsi asioita tien varrelta ja rastita bingoruutusi",
    "game.guess_place.name": "Arvaa paikka",
    "game.guess_place.desc": "Arvaatko paikan kuvauksen perusteella?",
    "game.would_you_rather.name": "Kumpi mieluummin",
    "game.would_you_rather.desc": "Valitse hankalien matkadilemmajen välillä",
    "game.trivia_battle.name": "Tietovisa-taisto",
    "game.trivia_battle.desc": "Kilpaile muita matkailijoita vastaan reaaliajassa",
    "game.photo_challenge.name": "Valokuvaushaaste",
    "game.photo_challenge.desc": "Luovia valokuvatehtäviä pysähdyspaikallasi",
    "game.story.name": "Tarina-peli",
    "game.story.desc": "Luokaa yhdessä matkatarina",
    "game.checkpoint.name": "Tarkistuspiste",
    "game.checkpoint.desc": "Tutki aluetta hauskoilla tehtävillä",
    "game.evening_game.name": "Iltapeli",
    "game.evening_game.desc": "Rentoja ryhmäpelejä iltaan",
    "game.scenario.name": "Skenaariopeli",
    "game.scenario.desc": "Mitä tekisit? Matkadilemma",
    "game.intro.tagline.quiz": "Testaa tietosi!",
    "game.intro.tagline.bingo": "Silmät tien päällä!",
    "game.intro.tagline.guess_place": "Arvaatko sen?",
    "game.intro.tagline.would_you_rather": "Tee valintasi!",
    "game.intro.tagline.trivia_battle": "Haaste vastaanotettu!",
    "game.intro.tagline.photo_challenge": "Taltioi hetki!",
    "game.intro.tagline.story": "Olipa kerran matka...",
    "game.intro.tagline.checkpoint": "Tutki ja löydä!",
    "game.intro.tagline.evening_game": "On aika rentoutua!",
    "game.intro.tagline.scenario": "Mitä tekisit?",
    "game.difficulty": "Valitse vaikeustaso",
    "game.difficulty.easy": "Helppo",
    "game.difficulty.medium": "Keskivaikea",
    "game.difficulty.hard": "Vaikea",
    "game.difficulty.easyDesc": "Täydellinen aloittelijoille",
    "game.difficulty.mediumDesc": "Kohtuullinen haaste",
    "game.difficulty.hardDesc": "Todellisille asiantuntijoille",
    "game.question": "Kysymys",
    "game.points": "pistettä",
    "game.finished": "Peli ohi!",
    "game.score.amazing": "Uskomaton! Olet mestari! 🏆",
    "game.score.great": "Fantastista! 🎉",
    "game.score.good": "Hyvin tehty! 👍",
    "game.score.ok": "Ei hassumpi! Yritä uudelleen! 💪",
    "game.score.low": "Harjoitus tekee mestarin! 💪",
    "game.bestStreak": "Paras putki: {n} peräkkäin!",
    "game.back": "Takaisin",
    "game.paused": "Peli tauolla",
    "game.exit.title": "Poistu pelistä?",
    "game.exit.description": "Edistymisesi menetetään, jos poistut.",
    "game.exit.continue": "Jatka pelaamista",
    "game.exit.pause": "Tauota peli",
    "game.exit.quit": "Poistu pelistä",
    "game.story.finished": "Tarina valmis!",
    "game.story.chapters": "luvut suoritettu",
    "game.story.chapter": "Luku",
    "game.story.new": "Uusi tarina",
    "game.story.whatDo": "Mitä teet?",
    "game.scenario.finished": "Skenaario valmis!",
    "game.scenario.steps": "askelta otettu",
    "game.scenario.step": "Askel",
    "game.scenario.new": "Uusi skenaario",
    "game.scenario.whatDo": "Mitä teet?",
    "game.scenario.intense": "Intensiivinen!",
    "game.newRound": "Uusi kierros",
    "game.seeResults": "Näytä tulokset",
    "game.nextQuestion": "Seuraava kysymys",
    "game.playNow": "Pelaa nyt",
    "game.players": "pelaajaa",
    "game.minutes": "min",
    "game.safety.title": "Turvallisuus ensin!",
    "game.safety.desc": "Tämä peli on tarkoitettu matkustajille, ei kuljettajalle. Vahvista, että olet matkustaja.",
    "game.safety.confirm": "Olen matkustaja",
    "game.safety.later": "Pelaa myöhemmin",
    "game.version": "{country}-versio",
    "game.hero.title": "Tee matkasta hauska",
    "game.hero.game_types": "pelityypit",
    "game.hero.questions": "kysymystä",
    "game.hero.countries": "maata",
    "game.challenge_friend": "Haasta ystävä",
    "game.trip_active": "Matka käynnissä",
    "game.login_prompt": "Kirjaudu sisään tallentaaksesi pelihistorian",
    "game.login_prompt_desc": "Pisteesi, suosikkisi ja sijoituksesi tulostaulukossa tallennetaan",
    "game.tab.games": "Pelit",
    "game.tab.challenges": "Haasteet",
    "game.tab.community": "Yhteisö",
    "game.tab.collect": "Kerää",
    "game.tab.history": "Historia",
    "game.section.on_the_road": "Matkalla",
    "game.section.at_stop": "Pysähdyksillä ja tauoilla",
    "game.section.camping": "Telttailu & ilta",
    "game.favorites": "Suosikkipelit",
    "game.no_favorites": "Tykkää pelistä tallentaaksesi sen tänne",
    "game.wyr.allDone": "Kaikki kysymykset valmiit!",
    "game.wyr.questionsAnswered": "kysymystä vastattu",
    "game.wyr.title": "Kumman Valitsisit",
    "game.wyr.wouldYouRather": "Valitsisitko mieluummin...",
    "game.wyr.or": "vai",
    "game.wyr.done": "Valmis!",
    "game.recommended.title": "Suositeltu sinulle",
    "game.weekly.title": "Viikon haasteet",
    "game.weekly.done": "Valmis",
    "game.bingo.found": "löydetty",
    "game.bingo.subtitle": "Etsi asioita tien varrelta",
    "game.bingo.spotted": "Löydetty!",
    "game.bingo.instructions": "Napauta asioita joita näet tien varrella. Bingo = rivi, sarake tai diagonaali!",
    "game.guess.finished": "Arvaa Paikka valmis!",
    "game.guess.fewerClues": "Vähemmän vihjeitä = enemmän pisteitä!",
    "game.guess.title": "Arvaa Paikka",
    "game.guess.place": "Paikka",
    "game.guess.clues": "Vihjeet",
    "game.guess.showNext": "Näytä seuraava vihje",
    "game.guess.placeholder": "Kirjoita vastauksesi...",
    "game.guess.guess": "Arvaa",
    "game.guess.correct": "Oikein!",
    "game.guess.wrong": "Väärin!",
    "game.guess.correctAnswer": "Oikea vastaus",
    "game.guess.nextPlace": "Seuraava paikka",
    "game.playAgain": "Pelaa uudelleen",
    "game.evening.finished": "Iltapeli valmis!",
    "game.evening.wellDone": "Hienoa työtä koko porukan kanssa!",
    "game.evening.round": "Kierros",
    "game.evening.done": "Valmis!",
    "game.evening.nextRound": "Seuraava kierros",
    "game.evening.trivia": "Trivia",
    "game.evening.creative": "Luova",
    "game.evening.debate": "Väittely",
    "game.evening.memory": "Muisti",
    "game.evening.drawing": "Piirtäminen",
    "game.checkpoint.finished": "Checkpoint valmis!",
    "game.checkpoint.challengesDone": "haastetta suoritettu",
    "game.checkpoint.newChallenges": "Uudet haasteet",
    "game.checkpoint.subtitle": "Tutustu paikkaan haasteiden avulla",
    "game.checkpoint.finish": "Lopeta checkpoint",
    "game.safety.warning": "Liikenneturvallisuus tulee aina ensin. Älä koskaan aja ja pelaa samanaikaisesti.",
    "game.safety.cancel": "Peruuta",
    "game.leaderboard.title": "Tulostaulukko – tämä viikko",
    "game.leaderboard.games": "peliä",
    "game.leaderboard.you": "Sinä",
    "game.leaderboard.yourPosition": "Sijoituksesi",
    "game.history.empty": "Ei pelattuja pelejä vielä",
    "game.history.emptyDesc": "Aloita ensimmäinen pelisi ja näe historiasi täällä!",
    "game.history.played": "Pelattu",
    "game.history.gameTypes": "Pelityypit",
    "game.history.countries": "Maat",
    "game.history.avgScore": "Keskimääräinen pistemäärä",
    "game.history.inProgress": "Käynnissä",
    "game.history.continue": "Jatka pelaamista",
    "game.badges.title": "Merkit",
    "game.quiz.fast": "Nopeat",
    "game.quiz.perfect": "Täydellinen!",
    "game.audience.label": "Kuka pelaa?",
    "game.audience.kids": "Lapset",
    "game.audience.kids_desc": "Hauskoja ja helppoja pelejä lapsille",
    "game.audience.family": "Perhe",
    "game.audience.family_desc": "Pelet koko perheelle",
    "game.audience.adult": "Aikuiset",
    "game.audience.adult_desc": "Pelet aikuisille",
    "game.audience.couple": "Pariskunta",
    "game.audience.couple_desc": "Romanttisia pelejä kahdelle",
    "game.audience.friends": "Ystävät",
    "game.audience.friends_desc": "Hauskaa ja kilpailullista ystävien kesken",
    "game.daily.title": "Päivittäiset & viikoittaiset haasteet",
    "game.daily.today": "Tänään",
    "game.daily.this_week": "Tällä viikolla",
    "game.daily.this_month": "Tässä kuussa",
    "game.daily.quiz_challenge": "Päivittäinen tietovisa-haaste",
    "game.daily.quiz_challenge_desc": "Pisteitä 7+ tämänpäiväisessä tietovisassa",
    "game.daily.bingo_quick": "Pika-bingo",
    "game.daily.bingo_quick_desc": "Etsi 5 asiaa bingotaululta",
    "game.daily.guess_master": "Arvausmestari",
    "game.daily.guess_master_desc": "Arvaa 3 paikkaa oikein",
    "game.daily.scenario_star": "Skenaariotähti",
    "game.daily.scenario_star_desc": "Suorita skenaariokierros",
    "game.daily.photo_snap": "Valokuvahaaste",
    "game.daily.photo_snap_desc": "Suorita 3 valokuvaushaastetta",
    "game.daily.story_teller": "Tarinankertoja",
    "game.daily.story_teller_desc": "Suorita tarinaseikkailu",
    "game.daily.trivia_duel": "Triviapeli",
    "game.daily.trivia_duel_desc": "Pisteitä 5+ triviataistelussa",
    "game.weekly.quiz_master": "Viikon tietovisamestari",
    "game.weekly.quiz_master_desc": "Pisteitä 9+ mistä tahansa tietovisasta tällä viikolla",
    "game.weekly.bingo_champion": "Bingomestari",
    "game.weekly.bingo_champion_desc": "Suorita täysi bingotaulu",
    "game.weekly.explorer": "Viikon tutkimusmatkailija",
    "game.weekly.explorer_desc": "Suorita 4 tarkistuspistehaastetta",
    "game.weekly.photo_pro": "Valokuvausammattilainen",
    "game.weekly.photo_pro_desc": "Suorita 6 valokuvaushaastetta",
    "game.weekly.evening_host": "Illan isäntä",
    "game.weekly.evening_host_desc": "Järjestä iltainen pelisessio",
    "game.monthly.country_champion": "Maan mestari",
    "game.monthly.country_champion_desc": "Täydet pisteet maatietovisasta",
    "game.monthly.all_rounder": "Monipuolinen pelaaja",
    "game.monthly.all_rounder_desc": "Pelaa 5 eri pelityyppiä",
    "game.monthly.road_legend": "Matkan legenda",
    "game.monthly.road_legend_desc": "Suorita 3 tarinaseikkailua",
    "game.season.spring": "Kevätpelit",
    "game.season.summer": "Kesäpelit",
    "game.season.autumn": "Syyspelit",
    "game.season.winter": "Talvipelit",
    "game.seasonal.limited": "Kausiluonteinen",
    "game.seasonal.spring_awakening": "Kevään herääminen -tietovisa",
    "game.seasonal.spring_awakening_desc": "Testaa kevättietämystäsi",
    "game.seasonal.easter_trip": "Pääsiäisroadtrip-bingo",
    "game.seasonal.easter_trip_desc": "Etsi pääsiäistä tien päältä",
    "game.seasonal.spring_photo": "Kevään valokuvaushaaste",
    "game.seasonal.spring_photo_desc": "Taltioi kevään kauneus",
    "game.seasonal.summer_roadtrip": "Kesäroadtrip-tietovisa",
    "game.seasonal.summer_roadtrip_desc": "Aurinko, rannat ja kesätiet",
    "game.seasonal.beach_bingo": "Ranta & rannikko -bingo",
    "game.seasonal.beach_bingo_desc": "Etsi kesäisiä asioita rannikolta",
    "game.seasonal.midsummer": "Juhannushaaste",
    "game.seasonal.midsummer_desc": "Juhli juhannusta haasteilla",
    "game.seasonal.camping_nights": "Kesän leiri-illat",
    "game.seasonal.camping_nights_desc": "Täydellinen lämpimiin leiri-iltoihin",
    "game.seasonal.autumn_colors": "Syksyn värit -valokuvaus",
    "game.seasonal.autumn_colors_desc": "Taltioi syksyn kultaiset sävyt",
    "game.seasonal.harvest_quiz": "Sadonkorjuuaika -tietovisa",
    "game.seasonal.harvest_quiz_desc": "Tietovisa syksystä ja sadonkorjuusta",
    "game.seasonal.fog_mystery": "Sumuinen mysteeritarina",
    "game.seasonal.fog_mystery_desc": "Salaperäinen syksyinen seikkailu",
    "game.seasonal.winter_roads": "Talviteiden tietovisa",
    "game.seasonal.winter_roads_desc": "Testaa talviajotaidot",
    "game.seasonal.christmas_markets": "Joulutorien etsijä",
    "game.seasonal.christmas_markets_desc": "Arvaa kuuluisat joulutorit",
    "game.seasonal.cozy_evening": "Kodikas talvi-ilta",
    "game.seasonal.cozy_evening_desc": "Täydellinen peli kylmiin iltoihin",
    "game.seasonal.northern_lights": "Revontulitarina",
    "game.seasonal.northern_lights_desc": "Seikkailu revontulien alla",
    "game.level.label": "Taso",
    "game.level.beginner": "Aloittelija",
    "game.level.explorer": "Utelias",
    "game.level.adventurer": "Seikkailija",
    "game.level.traveler": "Matkustaja",
    "game.level.road_master": "Matkamestari",
    "game.level.pro": "Roadtrip Pro",
    "game.level.navigator": "Navigoija",
    "game.level.euro_champ": "Euroopan mestari",
    "game.level.legendary": "Legendaarinen",
    "game.level.legend": "Roadtrip-legenda",
    "game.level.next": "Seuraava",
    "game.level.games_played": "pelattu",
    "game.collectibles.title": "Maakokoelmat",
    "game.collectibles.countries": "maata",
    "country.sweden": "Ruotsi",
    "country.norway": "Norja",
    "country.germany": "Saksa",
    "country.italy": "Italia",
    "country.france": "Ranska",
    "country.spain": "Espanja",
    "game.region.norrland": "Norrlanti",
    "game.region.svealand": "Svealanti",
    "game.region.gotaland": "Götalanti",
    "game.region.fjords": "Vuonot",
    "game.region.nordnorge": "Pohjois-Norja",
    "game.region.sorlandet": "Etelä-Norja",
    "game.region.bayern": "Baijeri",
    "game.region.norddeutschland": "Pohjois-Saksa",
    "game.region.rheinland": "Reininmaa",
    "game.region.norditalien": "Pohjois-Italia",
    "game.region.toscana": "Toscana",
    "game.region.syditalien": "Etelä-Italia",
    "game.region.ildefrance": "Île-de-France",
    "game.region.provence": "Provence",
    "game.region.bretagne": "Bretagne",
    "game.region.andalucia": "Andalusia",
    "game.region.catalonia": "Katalonia",
    "game.region.nordspanien": "Pohjois-Espanja",
    "nav.bottom.budget": "Budjetti",
    "nav.installTitle": "Asenna Vantastika",
    "nav.installIOS1": "Napauta Jaa-painiketta (iPhonen Safari)",
    "nav.installIOS2": "Valitse \"Lisää aloitusnäyttöön\"",
    "nav.installIOS3": "Napauta \"Lisää\"",
    "nav.installAndroid1": "Napauta valikkokuvaketta (⋮ oikeassa yläkulmassa)",
    "nav.installAndroid2": "Valitse \"Asenna sovellus\" tai \"Lisää aloitusnäyttöön\"",
    "nav.installUnderstood": "Selvä",
    "tripStatus.startTitle": "Haluatko aloittaa matkan nyt?",
    "tripStatus.startDesc": "Matka asetetaan käynnissä olevaksi ja GPS-seuranta aktivoidaan.",
    "tripStatus.startConfirm": "Kyllä, aloita",
    "tripStatus.endTitle": "Haluatko todella lopettaa matkan?",
    "tripStatus.endDesc": "Voit jatkaa sitä myöhemmin, jos muutat mielesi.",
    "tripStatus.endConfirm": "Kyllä, lopeta",
    "tripStatus.pauseTitle": "Haluatko keskeyttää matkan?",
    "tripStatus.pauseDesc": "Voit jatkaa matkaa milloin tahansa.",
    "tripStatus.pauseConfirm": "Kyllä, keskeytä",
    "tripStatus.resumeTitle": "Haluatko jatkaa matkaa?",
    "tripStatus.resumeDesc": "Matka jatkuu siitä, mihin sen keskeytit.",
    "tripStatus.resumeConfirm": "Kyllä, jatka",
    "tripStatus.revertTitle": "Palauta suunnitelluksi matkaksi?",
    "tripStatus.revertDesc": "Kaikki matkatiedot säilytetään – vain tila muuttuu takaisin suunnitelluksi.",
    "tripStatus.revertConfirm": "Kyllä, palauta",
    "tripStatus.reactivateTitle": "Jatka lopetettua matkaa?",
    "tripStatus.reactivateDesc": "Matka asetetaan uudelleen käynnissä olevaksi. Kaikki tiedot säilytetään.",
    "tripStatus.reactivateConfirm": "Kyllä, jatka",
    "tripStatus.trip": "Matka",
    "tripStatus.cancel": "Peruuta",
    "addToTrip.title": "Lisää matkaan",
    "addToTrip.loginPrompt": "Kirjaudu sisään lisätäksesi paikkoja matkoihisi.",
    "addToTrip.login": "Kirjaudu sisään",
    "addToTrip.createAccount": "Luo tili",
    "addToTrip.howUsePlace": "Miten haluat käyttää tätä paikkaa?",
    "addToTrip.chooseTrip": "Valitse matka",
    "addToTrip.startPoint": "Lähtöpiste",
    "addToTrip.startPointDesc": "Matka alkaa tästä",
    "addToTrip.destination": "Määränpää",
    "addToTrip.destinationDesc": "Haluan matkustaa tänne",
    "addToTrip.stop": "Pysähdys matkan varrella",
    "addToTrip.stopDesc": "Pysähdys matkan aikana",
    "addToTrip.noTrips": "Sinulla ei ole vielä suunniteltuja matkoja",
    "addToTrip.noTripsDesc": "Luo uusi matka ja lisää paikka suoraan.",
    "addToTrip.stops": "pysähdys(tä)",
    "addToTrip.unnamed": "Nimetön matka",
    "addToTrip.addedAs": "on lisätty niin kuin",
    "addToTrip.roleStart": "lähtöpiste",
    "addToTrip.roleDestination": "määränpää",
    "addToTrip.roleStop": "pysähdys",
    "addToTrip.couldNotAdd": "Paikkaa ei voitu lisätä",
    "addToTrip.createNew": "Luo uusi matka tällä paikalla",
    "optimize.bannerTitle": "Tekoäly löysi lyhyemmän reitin",
    "optimize.bannerDesc": "Säästä noin",
    "optimize.bannerByReordering": "muuttamalla pysähdystesi järjestystä.",
    "optimize.viewAlternatives": "Näytä vaihtoehdot",
    "optimize.dismiss": "Ei nyt",
    "optimize.fuelSaved": "polttoaineessa",
    "camping.notFound": "Paikkaa ei löytynyt.",
    "camping.approxPrice": "n.",
    "camping.perNight": "/ yö (arvio)",
    "camping.free": "Ilmainen",
    "camping.typeStellplatz": "Pysäköintialue",
    "camping.typeCamping": "Leirintäalue",
    "camping.amenities": "Palvelut",
    "camping.checkIn": "Sisään",
    "camping.checkOut": "Ulos",
    "camping.visitWebsite": "Käy verkkosivuilla",
    "camping.addToTrip": "Lisää matkaan",
    "camping.navigate": "Navigoi",
    "camping.viewPage": "Näytä sivu",
    "camping.amenityElectricity": "Sähkö",
    "camping.amenityFreshWater": "Makea vesi",
    "camping.amenityGreyWater": "Harmaavesi",
    "camping.amenityToilet": "WC",
    "camping.amenityShower": "Suihku",
    "camping.amenityWC": "WC",
    "camping.amenityPets": "Lemmikit",
    "camping.amenityAllYear": "Avoinna ympäri vuoden",
    "camping.amenityFamily": "Perheystävällinen",
    "camping.amenityLarge": "Suuret matkailuautot",
    "camping.amenityWater": "Veden lähellä",
    "camping.amenitySpa": "Kylpylä",
    "camping.amenityWifi": "WiFi",
    "camping.amenityLaundry": "Pesula",
    "camping.amenityRestaurant": "Ravintola",
    "share.inviteToSharedTrip": "Kutsu jaettuun matkaan",
    "share.inviteDesc": "Vastaanottaja liittyy samaan matkaan. Kaikki muutokset synkronoidaan reaaliaikaisesti – budjetti, tarkistuslista, pysähdykset ja muistiinpanot.",
    "share.inviteViaApp": "Kutsu sovelluksen kautta...",
    "share.copyLink": "Kopioi kutsulinkki",
    "share.copied": "Kopioitu!",
    "share.shareAsTip": "Jaa matkavinkkinä",
    "share.tipDesc": "Vastaanottaja saa oman kopionsa inspiraatioksi. Muutokset eivät vaikuta matkaasi.",
    "share.createAiGuide": "Luo tekoälymatkaopas",
    "share.generatingAi": "Luodaan tekoälymatkaopasta... ✨",
    "share.aiGuideCreated": "Tekoälymatkaopas luotu! ✨",
    "share.couldNotCreateAi": "Tekoälyopasta ei voitu luoda",
    "share.aiGuideActive": "Tekoälymatkaopas aktiivinen",
    "share.tabShare": "Jaa",
    "share.tabTeam": "Matkaryhmä",
    "share.tabComments": "Kommentit",
    "share.tabActivity": "Aktiviteetti",
    "share.inviteByUsername": "Kutsu käyttäjänimellä tai jaa kutsulinkki:",
    "share.usernamePlaceholder": "Käyttäjänimi kutsuttavaksi...",
    "share.inviteSent": "Kkutsu lähetetty!",
    "share.userNotFound": "Käyttäjää ei löytynyt. Anna tarkka käyttäjänimi.",
    "share.cannotInviteSelf": "Et voi kutsua itseäsi.",
    "share.alreadyInvited": "Käyttäjä on jo kutsuttu.",
    "share.couldNotInvite": "Ei voitu kutsua",
    "share.noMembers": "Ei jäseniä vielä. Kutsu joku suunnittelemaan yhdessä!",
    "share.editor": "Muokkaaja",
    "share.removedFromGroup": "Poistettu matkaryhmästä",
    "share.noComments": "Ei kommentteja vielä.",
    "share.unknown": "Tuntematon",
    "share.commentPlaceholder": "Kirjoita kommentti...",
    "share.send": "Lähetä",
    "share.noActivity": "Ei aktiviteettia vielä",
    "share.actionInvited": "kutsui",
    "share.actionCommented": "kommentoi",
    "share.actionAddedStop": "lisäsi pysähdyksen:",
    "share.actionUpdatedStop": "päivitti pysähdyksen:",
    "share.actionRemovedStop": "poisti pysähdyksen:",
    "share.actionUpdatedTrip": "päivitti matkan",
    "signup.guestVsMember": "Vieras vs jäsen",
    "signup.seeWhatYouGet": "Katso, mitä saat jäsenenä",
    "signup.freeNoCard": "100 % ilmainen – luottokorttia ei tarvita",
    "signup.premiumSoon": "Premium pian vielä useammilla ominaisuuksilla",
    "signup.username": "Käyttäjänimi",
    "signup.usernamePlaceholder": "esim. vanansen",
    "signup.displayName": "Näyttönimi",
    "signup.displayNamePlaceholder": "Nimesi muille näkyvä",
    "signup.confirmPassword": "Vahvista salasana",
    "signup.passwordPlaceholder": "Vähintään 8 merkkiä, kirjain + numero",
    "signup.passwordsNoMatch": "Salasanat eivät vastaa toisiaan",
    "signup.selectCountry": "Valitse maasi",
    "signup.whatVehicle": "Millä matkustat?",
    "signup.ageWarning": "Sinun on oltava vähintään 16-vuotias luodaksesi tilin Vantastikassa. Jos olet alle 18-vuotias, tarvitaan huoltajan suostumus.",
    "signup.termsConsent": "Hyväksyn",
    "signup.termsLink": "käyttöehdot",
    "signup.andThe": "ja",
    "signup.integrationTermsLink": "integraation ehdot",
    "signup.ageConfirm": "ja vahvistan, että olen vähintään 16-vuotias.",
    "signup.gdprConsent": "Olen lukenut ja hyväksyn",
    "signup.privacyLink": "tietosuojakäytännön (GDPR)",
    "signup.or": "tai",
    "signup.verificationFailed": "Vahvistus epäonnistui. Yritä uudelleen.",
    "signup.captchaFailed": "CAPTCHA-vahvistus epäonnistui. Yritä uudelleen.",
    "signup.tooManyAttempts": "Liian monta yritystä. Odota hetki.",
    "signup.acceptTerms": "Sinun on hyväksyttävä ehdot",
    "signup.selectCountryError": "Valitse maasi",
    "signup.enterDisplayName": "Anna näyttönimi",
    "signup.enterUsername": "Anna käyttäjänimi",
    "signup.accountCreated": "Tili luotu!",
    "signup.welcomeMsg": "Tervetuloa Vantastikaan!",
    "signup.checkDetails": "Tarkista tietosi ja yritä uudelleen.",
    "signup.showPassword": "Näytä salasana",
    "signup.hidePassword": "Piilota salasana",
    "signupVehicle.motorhome": "🚐 Motorhome",
    "signupVehicle.motorhomeDesc": "I have a motorhome",
    "signupVehicle.car": "🚗 Auto",
    "signupVehicle.carDesc": "I have a car",
    "signupVehicle.motorcycle": "🏍️ Motorcycle",
    "signupVehicle.motorcycleDesc": "Ajan moottoripyörällä",
    "signupVehicle.campervan": "🚌 Matkailuauto",
    "signupVehicle.campervanDesc": "Minulla on matkailuauto",
    "signupVehicle.rental": "🔑 Vuokra-ajoneuvo",
    "signupVehicle.rentalDesc": "Vuokraan tarvittaessa",
    "signupVehicle.other": "🌍 Muu / Epävarma",
    "signupVehicle.otherDesc": "Joustava matkustaja",
    "profile.yourProfile": "Profiilisi",
    "profile.since": "Vuodesta",
    "profile.quickLinks": "Pikalinkit",
    "profile.myTrips": "Matkani",
    "profile.checklists": "Tarkistuslistat",
    "profile.savedPlaces": "Tallennetut paikat",
    "profile.planning": "Suunnittelu",
    "profile.saved": "Tallennettu",
    "vehicle.projects": "Projektit",
    "vehicle.viewAll": "Näytä kaikki",
    "vehicle.noProjects": "Ei projekteja yhdistetty kohteeseen {name} vielä.",
    "vehicle.createProject": "Luo projekti",
    "wishlist.startExploring": "Aloita tutkiminen",
    "projects.vehicleLabel": "Ajoneuvo",
    "projects.linkedVehicle": "Liitetty ajoneuvo",
    "projects.linkVehicle": "Liitä ajoneuvo",
    "projects.noVehicle": "Ei liitettyä ajoneuvoa",
    "projects.selectVehicle": "Valitse ajoneuvo",
    "projects.noVehicleOption": "Ei mitään",
    "profile.notifications": "Ilmoitukset",
    "profile.travelOverview": "Matkakooste",
    "profile.levelAndProfile": "Matkustusprofiili & taso",
    "profile.progress": "Edistyminen",
    "profile.total": "Yhteensä",
    "profile.badges": "Merkit",
    "profile.challenges": "Haasteet",
    "profile.activeChallenges": "Aktiiviset haasteet",
    "profile.viewAllProgress": "Näytä kaikki edistyminen",
    "profile.incidentReports": "Tapahtumaraportit",
    "profile.newReport": "Uusi raportti",
    "profile.social": "Sosiaalinen",
    "profile.inbox": "Saapuneet",
    "profile.mySubmissions": "Omat lähetykset",
    "profile.accountSettings": "Tili & asetukset",
    "profile.accountSettingsDesc": "Profiili, teema ja salasana",
    "profile.email": "Sähköposti",
    "profile.displayName": "Näyttönimi",
    "profile.displayNamePlaceholder": "Nimesi",
    "profile.username": "Käyttäjänimi",
    "profile.usernamePlaceholder": "@käyttäjänimi",
    "profile.country": "Maa",
    "profile.countryPlaceholder": "Ruotsi",
    "profile.appearance": "Ulkoasu",
    "profile.themeLight": "Vaalea",
    "profile.themeDark": "Tumma",
    "profile.themeSystem": "Järjestelmä",
    "profile.themeLightDesc": "Aina vaalea teema",
    "profile.themeDarkDesc": "Aina tumma teema",
    "profile.themeSystemDesc": "Noudattaa laitettasi",
    "profile.changePassword": "Vaihda salasana",
    "profile.currentPassword": "Nykyinen salasana",
    "profile.newPassword": "Uusi salasana",
    "profile.newPasswordPlaceholder": "Vähintään 8 merkkiä",
    "profile.confirmPasswordLabel": "Vahvista",
    "profile.changePasswordBtn": "Vaihda salasana",
    "profile.changingPassword": "Vaihdan...",
    "profile.wrongCurrentPassword": "Väärä nykyinen salasana",
    "profile.passwordChanged": "Salasana vaihdettu!",
    "profile.passwordMinLength": "Salasanan on oltava vähintään 8 merkkiä",
    "profile.passwordsMismatch": "Salasanat eivät täsmää",
    "profile.deleteAccount": "Poista tili",
    "profile.permanentDeletion": "Pysyvä poistaminen",
    "profile.deleteAccountDesc": "Tilisi poistamisen yhteydessä seuraavat tiedot poistetaan pysyvästi: profiili, matkat, pysähdykset, suosikit, tarkistuslistat, foorumiviestit, arvostelut, kuvat, ystäväpyynnöt, viestit, tapahtumaraportit ja ilmoitukset.",
    "profile.deleteAccountNote": "Mitä saattaa jäädä jäljelle: Anonymisoidut lokitiedot (ei linkitetty tiliisi), jaetut merkkikuvakehys, sekä tiedot, joita meidän on lain mukaan säilytettävä (esim. kirjanpitolait, enintään 7 vuotta).",
    "profile.deleteMyAccount": "Poista minun tilini",
    "profile.deleteConfirmTitle": "Poista tili pysyvästi",
    "profile.deleteConfirmDesc": "Oletko varma? Seuraavat tiedot poistetaan pysyvästi:",
    "profile.deleteConfirmList": "• Profiili, asetukset ja avatar\n• Matkat, pysähdykset, budjetti ja tarkistuslistat\n• Suosikit, kokoelmat ja tallennetut paikat\n• Tapahtumaraportit ja ladatut kuvat\n• Foorumiviestit, kommentit ja tykkäykset\n• Ystäväpyynnöt ja viestit\n• Ilmoitukset ja paikan vahvistukset",
    "profile.deleteConfirmNote": "Anonymisoidut lokitiedot ja jaetut merkkikuvakehys eivät vaikuta. Tiedot, joita meidän on lain mukaan säilytettävä, voivat jäädä jäljelle enintään 7 vuotta.",
    "profile.cannotUndo": "Ei voi peruuttaa.",
    "profile.deleting": "Poistan...",
    "profile.yesDeleteAccount": "Kyllä, poista tilini",
    "profile.accountDeleted": "Tili poistettu",
    "profile.accountDeletedDesc": "Kaikki tietosi on poistettu.",
    "profile.couldNotDelete": "Tiliä ei voitu poistaa",
    "profile.profileSaved": "Profiili tallennettu!",
    "profile.couldNotSave": "Ei voitu tallentaa",
    "profile.profilePicUpdated": "Profiilikuva päivitetty!",
    "profile.uploading": "Lataan...",
    "profile.fileTooLarge": "Tiedosto liian suuri",
    "profile.fileTooLargeDesc": "Max 2 MB",
    "profile.imageRejected": "Kuvaa ei hyväksytty",
    "profile.myVehicle": "Oma ajoneuvoni",
    "profile.enterCurrentPassword": "Syötä nykyinen salasanasi",
    "profile.couldNotChangePassword": "Salasanaa ei voitu vaihtaa",
    "profile.vehicleMotorhome": "Matkailuauto",
    "profile.vehicleCar": "Auto",
    "profile.vehicleMotorcycle": "MC",
    "profile.vehicleCampervan": "Matkailuauto",
    "profile.vehicleRental": "Vuokra",
    "profile.vehicleOther": "Muu",
    "profile.activeNow": "Aktiivista nyt",
    "profile.today": "Tänään!",
    "profile.inDays": "{n} päivän päästä",
    "profile.newNotification": "uusi ilmoitus",
    "profile.newNotifications": "uutta ilmoitusta",
    "profile.showAll": "Näytä kaikki",
    "profile.inspection": "Katsastus",
    "profile.gasInspection": "Kaasutarkastus",
    "profile.service": "Huolto",
    "profile.expired": "Erääntynyt!",
    "profile.noDateRegistered": "Päivämäärää ei kirjattu",
    "profile.noServiceRegistered": "Huoltoa ei kirjattu",
    "profile.missing": "Puuttuu",
    "profile.insurance": "Vakuutus",
    "profile.noInsuranceRegistered": "Vakuutusta ei kirjattu",
    "profile.regNumberMissing": "Rekisterinumero puuttuu",
    "profile.addForSmartData": "Lisää älykkäitä ajoneuvotietoja varten",
    "profile.addRegNumber": "Lisää reknro",
    "profile.statusGood": "Hyvä",
    "profile.statusSoon": "Pian",
    "profile.statusAction": "Toimenpide",
    "profile.edit": "Muokkaa",
    "profile.addService": "Lisää huolto",
    "profile.addVehicleDesc": "Yhdistä ajoneuvosi älykkääseen suunnitteluun, muistutuksiin ja räätälöityyn sisältöön",
    "profile.addVehicleBtn": "Lisää ajoneuvo",
    "profile.validUntil": "Voimassa {date} asti",
    "scan.title": "Älykäs kuittiskannaus",
    "scan.takePhoto": "Ota kuva",
    "scan.upload": "Lataa",
    "scan.photoOrUpload": "Ota valokuva tai lataa kuitti",
    "scan.aiAnalyzes": "Tekoäly analysoi kuitin rivi riviltä",
    "scan.analyzing": "Analysoidaan kuittia...",
    "scan.analyzingDesc": "Tekoäly lukee tuotteet, summat ja kategoriat",
    "scan.fileTooLarge": "Tiedosto liian suuri",
    "scan.fileTooLargeMax": "Max 15 MB",
    "scan.analysisFailed": "Analyysi epäonnistui",
    "scan.couldNotAnalyze": "Kuittia ei voitu analysoida",
    "scan.amount": "Summa",
    "scan.currency": "Valuutta",
    "scan.possibleAmounts": "Mahdolliset summat:",
    "scan.converted": "Muunnettu:",
    "scan.date": "Päivämäärä",
    "scan.titleLabel": "Otsikko",
    "scan.itemsPerCategory": "Kohteita per kategoria",
    "scan.mixedCategories": "Sekalaiset kategoriat",
    "scan.howToSave": "Miten haluat tallentaa?",
    "scan.oneExpense": "Yksi kuluerä",
    "scan.splitUp": "Jaa",
    "scan.splitDesc": "Luo {n} erillistä kuluerää – yksi per kategoria.",
    "scan.mainCategory": "Pääkategoria",
    "scan.linkToBudget": "Linkitä budjettiin",
    "scan.noBudgetSelected": "Ei valittua budjettia",
    "scan.noBudget": "Ei budjettia",
    "scan.linkToTrip": "Linkitä matkaan",
    "scan.noTripSelected": "Ei valittua matkaa",
    "scan.noTrip": "Ei matkaa",
    "scan.note": "Huomautus",
    "scan.hideRawText": "Piilota raakateksti",
    "scan.showRawText": "Näytä raakateksti",
    "scan.cancel": "Peruuta",
    "scan.saving": "Tallennetaan...",
    "scan.saveExpense": "Tallenna kuluerä",
    "scan.saveExpenses": "Tallenna {n} kuluerää",
    "scan.expenseSaved": "Kuluerä tallennettu kuitista ✓",
    "scan.expensesSaved": "{n} kuluerää tallennettu kuitista ✓",
    "scan.fillAmountAndTitle": "Syötä summa ja otsikko",
    "scan.couldNotSave": "Ei voitu tallentaa",
    "scan.confidenceHigh": "Korkea luottamus",
    "scan.confidenceMedium": "Keskitason luottamus – tarkista kentät",
    "scan.confidenceLow": "Matala luottamus – tarkista kaikki kentät",
    "scan.dotMedium": "Keskitason luottamus",
    "scan.dotLow": "Epävarma – tarkista",
    "scan.close": "Sulje",
    "scan.paid": "Maksettu:",
    "scan.vat": "ALV:",
    "scan.receipt": "Kuitti",
    // Camera permissions
    "camera.prePermissionHint": "Kameran käyttöoikeus tarvitaan kuittien skannaukseen",
    "camera.deniedError": "Kameran käyttö estettiin. Ota se käyttöön selaimen tai puhelimen asetuksista.",
    "camera.notFoundError": "Kameraa ei löytynyt tältä laitteelta.",
    "camera.inUseError": "Kameraa käyttää toinen sovellus.",
    "camera.genericError": "Kameran käytössä tapahtui odottamaton virhe.",
    "camera.openSettings": "Näytä ohjeet",
    "camera.androidSettingsHint": "Siirry kohtaan Asetukset → Sovellukset → Selaimesi → Käyttöoikeudet → Kamera",
    "camera.iosSettingsHint": "Siirry kohtaan Asetukset → Tietosuoja ja turvallisuus → Kamera → Selaimesi",
    "camera.desktopSettingsHint": "Napsauta lukkokuvaketta osoitepalkissa ja ota kameran käyttö käyttöön.",
    // Location permissions
    "location.detecting": "Haetaan sijaintiasi…",
    "location.unsupportedTitle": "Sijainninjakoa ei tueta",
    "location.unsupportedDesc": "Laitteesi tai selaimesi ei tue sijainninjakoa. Kokeile toista selainta.",
    "location.deniedTitle": "Sijainnin käyttö estetty",
    "location.deniedDesc": "Olet estänyt sijainnin käytön. Ota sijainninjakaminen käyttöön selaimen tai puhelimen asetuksista käyttääksesi tätä ominaisuutta.",
    "location.tryAgain": "Yritä uudelleen",
    "location.openSettings": "Näytä ohjeet",
    "location.androidSettingsHint": "Siirry kohtaan Asetukset → Sovellukset → Selaimesi → Käyttöoikeudet → Sijainti",
    "location.iosSettingsHint": "Siirry kohtaan Asetukset → Tietosuoja ja turvallisuus → Sijaintipalvelut → Selaimesi",
    "location.desktopSettingsHint": "Napsauta lukkokuvaketta osoitepalkissa ja ota sijainnin käyttö käyttöön.",
    "location.promptDesc": "Tarvitsemme sijaintisi näyttääksemme lähistön tuloksia.",
    "location.allowAccess": "Salli sijainnin käyttö",
    "location.fetchErrorTitle": "Sijaintia ei voitu hakea",
    "location.timeoutDesc": "Sijainnin haku kesti liian kauan. Varmista, että GPS on käytössä.",
    "location.unavailableDesc": "Sijaintiasi ei voitu määrittää juuri nyt. Yritä hetken kuluttua uudelleen.",
    "location.errorTitle": "Sijaintivirhe",
    "location.errorDesc": "Jokin meni vikaan sijaintia haettaessa.",
    "season.springGreeting": "Kevät on täällä – aika suunnitella!",
    "season.summer": "Kesä",
    "season.summerGreeting": "Kesä odottaa – mille tielle lähdet?",
    "season.autumn": "Syksy",
    "season.autumnGreeting": "Syksyn värit kutsuvat!",
    "season.winter": "Talvi",
    "season.winterGreeting": "Talvi on maaginen – suunnittele lumimatka!",
    "map.searchPlaceholder": "Hae kaupunki, leirintäalue, osoite...",
    "map.searching": "Haetaan...",
    "map.searchingSuggestions": "Haetaan ehdotuksia…",
    "map.noResults": "Ei tuloksia",
    "map.noResultsHint": "Kokeile kaupunkia, leirintäaluetta, osoitetta tai paikkaa",
    "map.exploreEurope": "Tutustu Eurooppaan",
    "map.exploreDesc": "Etsi paikka löytääksesi leirintäalueita, levähdyspaikkoja ja nähtävyyksiä",
    "map.exploreDescLong": "Etsi paikka löytääksesi leirintäalueita, ravintoloita, nähtävyyksiä ja paljon muuta",
    "map.resultsNear": "Tulokset lähellä",
    "map.noCampingsFound": "Leirintäalueita tai levähdyspaikkoja ei löytynyt lähistöltä.",
    "map.mapErrorBanner": "Karttaa ei voitu ladata. Voit silti hakea ja selata paikkoja luettelonäkymässä.",
    "map.nearYou": "Paikkoja lähellä",
    "map.places": "paikkaa",
    "map.placesNearYou": "paikkaa lähellä",
    "map.campingsNearby": "Leirintäalueet ja levähdyspaikat lähistöllä",
    "map.explore": "Tutustu",
    "map.closePanel": "Sulje paneeli",
    "map.showResults": "Näytä tulokset",
    "map.navigate": "Navigoi",
    "map.addToTrip": "Lisää matkaan",
    "map.open": "Avoinna",
    "map.closed": "Suljettu",
    "map.free": "Ilmainen",
    "map.camping": "Leirintäalue",
    "map.stellplatz": "Levähdyspaikka",
    "map.dumpStation": "Tyhjennysasema",
    "map.navigateHit": "Navigoi tänne",
    "map.place": "Paikka",
    "map.restaurant": "Ravintola",
    "map.supermarket": "Ruokakauppa",
    "map.attraction": "Nähtävyys",
    "map.parking": "Pysäköinti",
    "map.fuelStation": "Huoltoasema",
    "map.museum": "Museo",
    "map.nature": "Luonto",
    "map.sights": "Nähtävyydet",
    "map.grocery": "Ruokakauppa",
    "map.perNight": "/yö",
    "submit.addressAutoHint": "Hae osoite ensin — maa, alue ja kaupunki täytetään automaattisesti.",
    "submit.autoDetected": "Maa, alue ja kaupunki on täytetty automaattisesti!",
    "submit.placeExists": "Paikka on jo olemassa",
    "submit.placeExistsDesc": "Paikka nimeltä \"{name}\" on jo rekisteröity.",
    "submit.nearbyExists": "Lähistöllä on jo paikka",
    "submit.nearbyExistsDesc": "\"{name}\" on jo rekisteröity samaan paikkaan.",
    "submit.imageRejected": "Kuva hylätty",
    "submit.mainImage": "Pääkuva",
    "submit.contactInfo": "Yhteystiedot",
    "service.heroDesc": "Löydä korjaamoja, polttoainetta, vettä, sairaaloita ja muuta — missä tahansa Euroopassa",
    "service.close": "Sulje",
    "service.clearSearch": "Tyhjennä",
    "service.search": "Hae",
    "service.positionSharing": "Sijainnin jakaminen",
    "service.positionSharingDesc": "Jaa GPS-sijaintisi tai aktivoi SOS",
    "service.connectionError": "Tarkista yhteys ja yritä uudelleen",
    "service.tryAgain": "Yritä uudelleen",
    "service.noResultsHint": "Kokeile hakea jotain muuta tai vaihda kategoriaa",
    "service.resultsNearYou": "lähellä",
    "budget.noPeriod": "Ei ajanjaksoa",
    "budget.exceededBy": "Ylitetty",
    "budget.expenseDeleted": "Kulu poistettu",
    "budget.undo": "Kumoa",
    "budget.receipt": "Kuitti",
    "budget.trip": "Matka",
    "whatsNew.title": "Mitä uutta",
    "whatsNew.subtitle": "Viimeisimmät päivitykset ja ominaisuudet",
    "whatsNew.seoTitle": "Mitä uutta – Vantastika",
    "whatsNew.seoDesc": "Katso viimeisimmät päivitykset ja uutiset Vantastikasta.",
    "whatsNew.all": "Kaikki",
    "whatsNew.noUpdates": "Ei päivityksiä juuri nyt",
    "whatsNew.type.new_feature": "Uusi ominaisuus",
    "whatsNew.type.improvement": "Parannus",
    "whatsNew.type.bugfix": "Virhekorjaus",
    "whatsNew.type.important": "Tärkeä",
    "whatsNew.type.maintenance": "Ylläpito",
    "whatsNew.readMore": "Lue lisää",
    "search.benefit1": "Katso kaikki leirintäalueet ja paikat",
    "search.benefit2": "Suodata varusteiden ja hinnan mukaan",
    "search.benefit3": "Lue arvosteluja ja katso arvioita",
    "search.smartPlaceholder": "Hae paikkoja, leirintäalueita, pysäköintipaikkoja, kaupunkeja...",
    "search.recommendedFor": "Suositeltu kohteelle",
    "search.typeCategory": "Kategoria",
    "search.typeRegion": "Alue",
    "search.typeCountry": "Maa",
    "search.typePlace": "Paikka",
    "search.noResultsHint": "Kokeile toista hakusanaa tai poista joitain suodattimia",
    "search.browseByCountry": "Selaa maittain",
    "travelGuides.badge": "Matkaoppaat",
    "travelGuides.heroTitle1": "Löydä Euroopan parhaat ",
    "travelGuides.heroTitle2": "matkareitit",
    "travelGuides.heroSubtitle": "Yksityiskohtaiset oppaat historioineen, kohokohtineen ja käytännön tiedoin. Muuta inspiraatio todelliseksi matkaksi yhdellä klikkauksella.",
    "travelGuides.popular": "Suositut kohteet",
    "travelGuides.moreCountries": "Lisää maita",
    "travelGuides.guidesCount": "{n} opasta",
    "travelGuides.guidesSingular": "1 opas",
    "travelGuides.explore": "Tutustu",
    "countryGuide.titlePrefix": "Matkaopas",
    "countryGuide.allGuides": "Kaikki matkaoppaat",
    "countryGuide.seoDesc": "Täydellinen matkailuauto-matkaopas kohteeseen",
    "countryGuide.overview": "Yleiskatsaus",
    "countryGuide.campingRules": "Camping & Yöpyminen",
    "countryGuide.campingRegulations": "Campingsäännöt",
    "countryGuide.wildCamping": "Villikämppäily",
    "countryGuide.campingTips": "Campingvinkit",
    "countryGuide.bestSeason": "Paras kausi",
    "countryGuide.drivingInfo": "Ajaminen & Tiet",
    "countryGuide.trafficRules": "Liikennesäännöt",
    "countryGuide.speedLimits": "Nopeusrajoitukset",
    "countryGuide.tollsVignettes": "Tietullit & Vinjetit",
    "countryGuide.currency": "Valuutta",
    "countryGuide.fuel": "Polttoaine",
    "countryGuide.emergency": "Hätänumero",
    "countryGuide.language": "Kieli",
    "countryGuide.bestPlaces": "Parhaat paikat vierailla",
    "countryGuide.safetyTips": "Turvallisuusvinkit",
    "countryGuide.practicalTips": "Käytännön vinkit",
    "countryGuide.routeGuides": "Reittioppaat",
    "countryGuide.guide": "Opas",
    "countryGuide.readGuide": "Lue opas",
    "countryGuide.searchCampings": "Hae leirintäalueita",
    "countryGuide.showOnMap": "Näytä kartalla",
    "countryGuide.planTrip": "Suunnittele matka",
    "countryGuide.planTripHere": "Suunnittele matka tänne",
    "countryGuide.campings": "Leirintäalueet",
    "guideDetail.days": "päivää",
    "guideDetail.history": "Historia & tausta",
    "guideDetail.routeInfo": "Reittitiedot",
    "guideDetail.startPoint": "Lähtöpiste",
    "guideDetail.endPoint": "Päätepiste",
    "guideDetail.distance": "Etäisyys",
    "guideDetail.direction": "Suunta",
    "guideDetail.passesThrough": "Kulkee läpi",
    "guideDetail.overview": "Yleiskatsaus",
    "guideDetail.highlights": "Kohokohdat",
    "guideDetail.practical": "Käytännön tietoa",
    "guideDetail.bestSeason": "Paras kausi",
    "guideDetail.vehicleType": "Ajoneuvotyyppi",
    "guideDetail.motorhome": "Matkailuauto",
    "guideDetail.car": "Auto",
    "guideDetail.motorcycle": "Moottoripyörä",
    "guideDetail.campervan": "Retkeilyauto",
    "guideDetail.warnings": "Huomioitavaa",
    "guideDetail.practicalTips": "Käytännön vinkit",
    "guideDetail.whyVisit": "Miksi vierailla: ",
    "guideDetail.readyToGo": "Valmis lähtemään?",
    "guideDetail.readyToGoDesc": "Muuta tämä opas todelliseksi matkaksi matkasuunnittelijassa. Mukauta pysähdyksiä, päivien määrää ja paluumatkaa toiveidesi mukaan.",
    "guideDetail.planThisTrip": "Suunnittele tämä matka",
    "guideDetail.refreshGuide": "Päivitä opas",
    "guideDetail.generating": "Luodaan yksityiskohtaista matkaopasta tekoälyllä...",
    "guideDetail.loading": "Ladataan matkaopasta...",
    "guideDetail.premiumBenefit1": "Lue koko matkaopas pysähdyksineen ja vinkkeineen",
    "guideDetail.premiumBenefit2": "Katso yksityiskohtaiset reittitiedot",
    "guideDetail.premiumBenefit3": "Muuta opas todelliseksi matkaksi",
    "destination.motorhome": "Matkailuauto",
    "destination.planTrip": "Suunnittele matkailuautomatka kohteeseen",
    "destination.planNow": "Suunnittele matkasi nyt",
    "destination.viewGuides": "Katso matkaoppaita",
    "destination.whatWeOffer": "Mitä Vantastika tarjoaa",
    "destination.featureRoutes": "Reittisuunnittelu",
    "destination.featureRoutesDesc": "Suunnittele reittisi pysähdyksin, etäisyyksin ja ajoaikoin.",
    "destination.featureCamping": "Leirintäalueet",
    "destination.featureCampingDesc": "Löydä parhaat leirintäalueet ja matkailuautopaikat.",
    "destination.featureRules": "Ajosäännöt",
    "destination.featureRulesDesc": "Nopeusrajoitukset, tietullit ja paikalliset säännöt.",
    "destination.featureSafety": "Turvallisuus & SOS",
    "destination.featureSafetyDesc": "Hätänumerot, tiepalvelu ja turvallisuusvinkit.",
    "destination.popularRoutes": "Suosittuja reittejä:",
    "destination.readyToGo": "Valmis matkaan?",
    "destination.ctaDesc": "Luo ilmainen tili ja aloita road tripin suunnittelu tänään.",
    "destination.getStarted": "Aloita ilmaiseksi",
    "destination.findCampings": "Etsi leirintäalueita",
    "share.shareOnInstagram": "Jaa Instagramissa",
    "share.shareOnFacebook": "Jaa Facebookissa",
    "share.linkCopied": "Linkki kopioitu!",
    "share.linkCopiedDesc": "Liitä minne haluat.",
    "ig.headline": "Suunnittele matkasi fiksummin Vantastikalla",
    "ig.subheadline": "Reitti, budjetti ja matkasuunnittelu yhdessä paikassa road tripeille, matkailuautoille ja automatkoille.",
    "ig.ctaStart": "Aloita ilmaiseksi",
    "ig.ctaOpen": "Avaa Vantastika",
    "ig.ctaDemo": "Katso miten se toimii",
    "ig.demoTitle": "Näin helposti suunnittelet matkasi",
    "ig.benefit1.title": "Suunnittele koko reitti",
    "ig.benefit1.desc": "Luo reittisi pysähdyksillä, etäisyyksillä ja ajoilla.",
    "ig.benefit2.title": "Pidä matkabudjetti hallinnassa",
    "ig.benefit2.desc": "Katso mitä matka maksaa — ennen ja matkan aikana.",
    "ig.benefit3.title": "Parempi yleiskuva",
    "ig.benefit3.desc": "Kaikki yhdessä paikassa, ennen ja matkan aikana.",
    "ig.free": "Ilmainen. Luottokorttia ei tarvita.",
    "upload.validating": "Vahvistetaan...",
    "upload.processing": "Käsitellään kuvaa...",
    "upload.converting": "Muunnetaan HEIC...",
    "upload.resizing": "Muutetaan kokoa...",
    "upload.compressing": "Pakataan...",
    "upload.thumbnailing": "Luodaan pikkukuvaa...",
    "upload.almostDone": "Melkein valmis...",
    "upload.uploading": "Ladataan...",
    "upload.done": "Valmis!",
    "upload.failed": "Lataus epäonnistui",
    "upload.fileTooLarge": "Tiedosto liian suuri",
    "upload.imageRejected": "Kuvaa ei hyväksytty",
  },
};

// Fill missing translations with English fallback
for (const locale of Object.keys(translations) as Locale[]) {
  if (locale === "en") continue;
  for (const key of Object.keys(translations.en)) {
    if (!translations[locale][key]) {
      translations[locale][key] = translations.en[key];
    }
  }
}

const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  sv: "Svenska",
  no: "Norsk",
  de: "Deutsch",
  da: "Dansk",
  fi: "Suomi",
};

const LOCALE_FLAGS: Record<Locale, string> = {
  en: "🇬🇧",
  sv: "🇸🇪",
  no: "🇳🇴",
  de: "🇩🇪",
  da: "🇩🇰",
  fi: "🇫🇮",
};

// Map browser language codes to our locales
const BROWSER_LANG_MAP: Record<string, Locale> = {
  en: "en", sv: "sv", nb: "no", nn: "no", no: "no",
  de: "de", da: "da", fi: "fi",
};

// Map country codes to locales for IP-based detection
const COUNTRY_LOCALE_MAP: Record<string, Locale> = {
  SE: "sv", NO: "no", DK: "da", FI: "fi",
  DE: "de", AT: "de", CH: "de",
  FR: "en", BE: "en",
  NL: "en",
  IT: "en",
  ES: "en", PT: "en",
  GB: "en", IE: "en", US: "en", CA: "en", AU: "en",
};

function detectLocale(): Locale {
  const saved = localStorage.getItem("vantastika-locale");
  if (saved && saved in translations) return saved as Locale;

  const browserLang = navigator.language?.split("-")[0]?.toLowerCase();
  if (browserLang && BROWSER_LANG_MAP[browserLang]) {
    return BROWSER_LANG_MAP[browserLang];
  }
  return "en";
}

interface I18nContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string) => string;
  locales: { code: Locale; name: string; flag: string }[];
}

const I18nContext = createContext<I18nContextType | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    localStorage.setItem("vantastika-locale", l);
    document.documentElement.lang = l;
  };

  const humanizeMissingKey = (key: string) => {
    const lastSegment = key.split(".").pop() || key;
    return lastSegment
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (char) => char.toUpperCase());
  };

  useEffect(() => {
    document.documentElement.lang = locale;
    // If no saved locale, try IP-based detection
    const saved = localStorage.getItem("vantastika-locale");
    if (!saved) {
      fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) })
        .then((r) => r.json())
        .then((data) => {
          const countryCode = data?.country_code;
          if (countryCode && COUNTRY_LOCALE_MAP[countryCode]) {
            const detected = COUNTRY_LOCALE_MAP[countryCode];
            setLocaleState(detected);
            localStorage.setItem("vantastika-locale", detected);
            document.documentElement.lang = detected;
          }
        })
        .catch(() => {});
    }
  }, []);

  const t = (key: string) => translations[locale]?.[key] || translations.en[key] || humanizeMissingKey(key);

  const locales = (Object.keys(LOCALE_NAMES) as Locale[]).map((code) => ({
    code,
    name: LOCALE_NAMES[code],
    flag: LOCALE_FLAGS[code],
  }));

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, locales }}>
      {children}
    </I18nContext.Provider>
  );
}

const fallbackT = (key: string) => translations.en[key] || key;
const fallbackLocales = (Object.keys(LOCALE_NAMES) as Locale[]).map((code) => ({
  code,
  name: LOCALE_NAMES[code],
  flag: LOCALE_FLAGS[code],
}));
const fallbackCtx: I18nContextType = {
  locale: "en",
  setLocale: () => {},
  t: fallbackT,
  locales: fallbackLocales,
};

export function useI18n() {
  const ctx = useContext(I18nContext);
  return ctx || fallbackCtx;
}

/** Standalone t() for use outside React components (e.g. mutation callbacks) */
export function tStandalone(key: string): string {
  const saved = localStorage.getItem("vantastika-locale");
  const locale: Locale = (saved && saved in translations) ? saved as Locale : "en";
  return translations[locale]?.[key] || translations.en[key] || key;
}
